import React, { useState, useEffect, useRef, createContext, useContext, useCallback } from "react";
import "./App.css";
import axios from "axios";
import { WhatsAppPublishModal } from './components/WhatsApp';
import WhatsAppSettingsV2 from './components/whatsapp/WhatsAppSettingsV2';

// Gallery Components
import { MyGallery, RecentCreations } from './components/gallery';

// Dashboard V2 - Modern Redesign
import { DashboardV2 } from './components/dashboard';

// Ad Creator Preview Panel
import { AdPreviewPanel } from './components/adcreator/AdPreviewPanel';
import BrandAssetsCard from './components/adcreator/BrandAssetsCard';
import CampaignReferencesPanel from './components/adcreator/CampaignReferencesPanel';
import VideoPlatformPicker from './components/adcreator/VideoPlatformPicker';
import { getSuggestedPresetForPlatform, getPlatformLabel } from './lib/videoPresets';

// Content Studio V2 - Modular Redesign
import { ContentStudioV2 } from './components/contentstudio';

// AI Assistant V2 - Modular Redesign
import { AIAssistantV2 } from './components/aiassistant';

// Analytics V2 - Modular Redesign
import { AnalyticsV2 } from './components/analytics';

// Achievements Page V2
import { AchievementsPageV2 } from './components/achievements/AchievementsPageV2';

// Credits Page V2
import CreditsPageV2 from './components/credits/CreditsPageV2';

// Sidebar V2
import SidebarV2 from './components/sidebar/SidebarV2';
import { CampaignStrategy } from './components/common/CampaignStrategy';

// Achievement System Components
import { 
  AchievementTimeline, 
  AchievementBadgeGallery, 
  AchievementNotification, 
  AchievementDashboardSummary,
  AchievementUnlockAnimation,
  ShareableAchievementCard,
  AchievementRewards,
  EnhancedLeaderboard,
  SuccessCelebrationModal,
  URLCopyModal,
  FunctionalAlertModal
} from './components/achievements';

// AI Feedback Components
import AIFeedbackSimulation from './components/feedback/AIFeedbackSimulation';
import SavedFeedbackModal from './components/feedback/SavedFeedbackModal';
import RegenerateWithFeedbackModal from './components/feedback/RegenerateWithFeedbackModal';
import { FeedbackInput } from './components/feedback/FeedbackInput';
import PublishValidationSettings from './components/admin/PublishValidationSettings';

// Admin Components
import WaitlistManagement from './components/admin/WaitlistManagement';
import ThemeToggle from './components/admin/ThemeToggle';
import CreditManagement from './components/admin/CreditManagement';
import SubscriptionPlanManager from './components/admin/SubscriptionPlanManager';
import EmailCampaignManager from './components/admin/EmailCampaignManager';
import { SubscriptionSuccess } from './components/subscriptions';
import CreditBalance from './components/CreditBalance';
import CreditPurchaseModal from './components/CreditPurchaseModal';
import CreditCostPreview from './components/CreditCostPreview';
import { previewRegenerateCost, previewCampaignRegenerateCost, formatCreditInfo, fetchFullCampaignData, regenerateAd } from './services/regenerationService';
import ConfirmationModal from './components/ui/ConfirmationModal';
import PromptModal from './components/ui/PromptModal';
import AssetLibraryPage from './components/asset-library/AssetLibraryPage';
import AssetLibraryPickerModal from './components/asset-library/AssetLibraryPickerModal';
import VoiceLibraryPage from './components/voice-library/VoiceLibraryPage';
import PublicReelPage from './components/reel/PublicReelPage';
import PublicReelView from './components/reel/PublicReelView';

// Geography and Language Selector
import GeographyLanguageSelector from './components/common/GeographyLanguageSelector';

// Hero Section Mockups for Landing Page Preview
import HeroMockups from './components/HeroMockups';
import CalmStyleLandingPage from './components/CalmStyleLandingPage';
import CalmHeroSection from './components/landing/CalmHeroSection';

// Auth Components
import GoogleAuthCallback from './components/auth/GoogleAuthCallback';
import LoginModal from './components/auth/LoginModal';

// Blog Components
import BlogList from './components/blog/BlogList';
import BlogPost from './components/blog/BlogPost';

// Product Hunt Launch Page
import ProductHuntLaunchPage from './components/ProductHuntLaunchPage';

// Import contexts
import { ColorThemeProvider, useColorTheme } from './context/ColorThemeContext';

// Poster Builder
import { PosterBuilder } from './components/poster-builder';

// Brand Icons for platform ad types
import { BrandIcon, TEMPLATE_ICONS } from './components/contentstudio/BrandIcons';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Fetch a completed campaign with retry + empty-array detection.
 *
 * Why this exists: after generation, the polling endpoint reports
 * `status: 'completed'` slightly before `GET /api/campaigns/{id}` has the
 * final `generated_ads` array fully replicated/committed. Auth blips, brief
 * network failures, and short replica lag can also cause the very first GET
 * to return an empty array. A naive single-attempt fetch falls back to the
 * stub response (which has generated_ads: []) and renders the user a
 * "Generated Content" page with zero ads — a classic silent failure.
 *
 * This helper:
 *   - Retries up to `maxAttempts` (default 3) with linear backoff.
 *   - Treats a 200 response with `generated_ads.length === 0` as a "soft fail"
 *     and retries — except when `generation_status === 'failed'`, in which
 *     case it returns immediately so callers can show the failure state.
 *   - Returns `{ ok: true, campaign }` or `{ ok: false, reason, lastError, campaign? }`.
 *
 * Callers decide whether to render the partial campaign, retry the whole
 * generation, or show an error.
 */
async function fetchCampaignWithRetry(campaignId, sessionToken, { maxAttempts = 3, baseDelayMs = 1000 } = {}) {
  let lastCampaign = null;
  let lastError = null;
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    if (attempt > 1) {
      await new Promise(r => setTimeout(r, baseDelayMs * attempt));
    }
    try {
      const resp = await axios.get(`${API}/campaigns/${campaignId}`, {
        headers: { Authorization: `Bearer ${sessionToken}` },
      });
      const data = resp.data || {};
      const ads = Array.isArray(data.generated_ads) ? data.generated_ads : [];
      lastCampaign = data;
      if (ads.length > 0) {
        console.log(`[fetchCampaignWithRetry] attempt ${attempt}/${maxAttempts} ✓ got ${ads.length} ads`);
        return { ok: true, campaign: data };
      }
      if (data.generation_status === 'failed') {
        console.warn(`[fetchCampaignWithRetry] attempt ${attempt} — campaign FAILED upstream`);
        return { ok: false, reason: 'campaign_failed', campaign: data };
      }
      console.warn(`[fetchCampaignWithRetry] attempt ${attempt}/${maxAttempts} — got 0 ads (status=${data.generation_status}), retrying`);
    } catch (err) {
      lastError = err;
      console.error(`[fetchCampaignWithRetry] attempt ${attempt}/${maxAttempts} threw:`, err?.message || err);
    }
  }
  return {
    ok: false,
    reason: lastError ? 'fetch_threw_all_attempts' : 'empty_ads_all_attempts',
    lastError,
    campaign: lastCampaign,
  };
}

// Hash password using SHA-256 before sending to server
async function hashPassword(password) {
  const encoder = new TextEncoder();
  const data = encoder.encode(password);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  return hashHex;
}

// Check if we should show the full dashboard (for testing purposes)
// Note: SHOW_DASHBOARD is now handled reactively in the main component

// Theme Context
const ThemeContext = React.createContext();

// Simple Error Boundary for React errors
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('React Error Boundary caught error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Something went wrong</h2>
            <p className="text-gray-600 mb-4">Please refresh the page to continue.</p>
            <button 
              onClick={() => window.location.reload()} 
              className="px-4 py-2 bg-emerald-500 text-white rounded hover:bg-orange-600"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const ThemeProvider = ({ children }) => {
  const [isDark, setIsDark] = useState(false);
  
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      setIsDark(savedTheme === 'dark');
    }
  }, []);
  
  const toggleTheme = () => {
    const newTheme = !isDark;
    setIsDark(newTheme);
    localStorage.setItem('theme', newTheme ? 'dark' : 'light');
  };
  
  return (
    <ThemeContext.Provider value={{ isDark, toggleTheme }}>
      <div className={isDark ? 'dark' : ''}>
        {children}
      </div>
    </ThemeContext.Provider>
  );
};

const useTheme = () => {
  const context = React.useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// FAQ Accordion Item Component
const FAQItem = ({ question, answer, colorTheme }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  const getAccentColor = () => {
    switch(colorTheme) {
      case 'purple': return 'from-purple-500 to-purple-600';
      case 'blue': return 'from-blue-500 to-blue-600';
      case 'green': return 'from-green-500 to-green-600';
      default: return 'from-purple-500 to-purple-600';
    }
  };

  const getBorderColor = () => {
    switch(colorTheme) {
      case 'purple': return 'hover:border-purple-300';
      case 'blue': return 'hover:border-blue-300';
      case 'green': return 'hover:border-green-300';
      default: return 'hover:border-purple-300';
    }
  };

  return (
    <div 
      className={`bg-white rounded-2xl border-2 border-gray-100 ${getBorderColor()} transition-all duration-300 overflow-hidden shadow-sm hover:shadow-md`}
      data-testid={`faq-item-${question.toLowerCase().replace(/[^a-z0-9]/g, '-').slice(0, 30)}`}
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none"
        data-testid="faq-toggle-btn"
      >
        <span className="text-lg font-semibold text-gray-900 pr-4">{question}</span>
        <div className={`flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-r ${getAccentColor()} flex items-center justify-center transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}>
          <svg 
            className="w-4 h-4 text-white" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>
      <div 
        className={`transition-all duration-300 ease-in-out ${isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}
      >
        <div className="px-6 pb-5">
          <p className="text-gray-600 leading-relaxed">{answer}</p>
        </div>
      </div>
    </div>
  );
};

// Enterprise Contact Form Component
const EnterpriseContactForm = ({ colorTheme, currentColors }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    needs: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/contact/enterprise`, formData);
      if (response.data.success) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', company: '', needs: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error submitting enterprise contact form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getButtonGradient = () => {
    switch(colorTheme) {
      case 'purple': return 'from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700';
      case 'blue': return 'from-blue-600 to-sky-600 hover:from-blue-700 hover:to-sky-700';
      case 'green': return 'from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700';
      default: return 'from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700';
    }
  };

  const getFocusRing = () => {
    switch(colorTheme) {
      case 'purple': return 'focus:ring-purple-500 focus:border-purple-500';
      case 'blue': return 'focus:ring-blue-500 focus:border-blue-500';
      case 'green': return 'focus:ring-green-500 focus:border-green-500';
      default: return 'focus:ring-purple-500 focus:border-purple-500';
    }
  };

  if (submitStatus === 'success') {
    return (
      <div className="text-center py-8" data-testid="enterprise-form-success">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h4 className="text-xl font-bold text-gray-900 mb-2">Thank You!</h4>
        <p className="text-gray-600">Our sales team will contact you within 24 hours.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="enterprise-contact-form">
      <div>
        <input
          type="text"
          name="name"
          placeholder="Your Name"
          value={formData.name}
          onChange={handleChange}
          required
          className={`w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 ${getFocusRing()} transition`}
          data-testid="enterprise-name-input"
        />
      </div>
      <div>
        <input
          type="email"
          name="email"
          placeholder="Work Email"
          value={formData.email}
          onChange={handleChange}
          required
          className={`w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 ${getFocusRing()} transition`}
          data-testid="enterprise-email-input"
        />
      </div>
      <div>
        <input
          type="text"
          name="company"
          placeholder="Company Name"
          value={formData.company}
          onChange={handleChange}
          required
          className={`w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 ${getFocusRing()} transition`}
          data-testid="enterprise-company-input"
        />
      </div>
      <div>
        <textarea
          name="needs"
          placeholder="Tell us about your needs..."
          value={formData.needs}
          onChange={handleChange}
          rows={4}
          className={`w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 ${getFocusRing()} transition resize-none`}
          data-testid="enterprise-needs-input"
        />
      </div>
      
      {submitStatus === 'error' && (
        <div className="text-red-600 text-sm bg-red-50 px-4 py-2 rounded-lg">
          Failed to submit. Please try again or email us directly at contact@3stech.ai
        </div>
      )}
      
      <button
        type="submit"
        disabled={isSubmitting}
        className={`w-full py-4 bg-gradient-to-r ${getButtonGradient()} text-white rounded-xl font-bold text-lg transition transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none`}
        data-testid="enterprise-submit-btn"
      >
        {isSubmitting ? (
          <span className="flex items-center justify-center">
            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Sending...
          </span>
        ) : (
          'Contact Sales for Custom Pricing'
        )}
      </button>
    </form>
  );
};

// Auth Context
const AuthContext = createContext();

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  // Loading states
  const [initialLoading, setInitialLoading] = useState(true); // For first page load
  const [gridLoading, setGridLoading] = useState(false); // For user grid updates only
  const [sessionToken, setSessionToken] = useState(null);
  // Auth error state for Google Sign-In
  const [authError, setAuthError] = useState(null);
  const [showAuthErrorModal, setShowAuthErrorModal] = useState(false);

  useEffect(() => {
    // Check for existing session
    const token = localStorage.getItem('session_token');
    if (token) {
      setSessionToken(token);
      // Verify token with backend
      verifySession(token);
    } else {
      setInitialLoading(false);
    }

    // Check for auth redirect
    const urlParams = new URLSearchParams(window.location.hash.substring(1));
    const sessionId = urlParams.get('session_id');
    
    if (sessionId) {
      handleAuthCallback(sessionId);
    }
  }, []);

  const verifySession = async (token) => {
    try {
      console.log('🔍 Verifying session token...', token.substring(0, 20) + '...');
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('✅ Session verified successfully:', response.data);
      setUser(response.data);
      setInitialLoading(false);
    } catch (error) {
      console.error('❌ Session verification failed:', error.response?.status, error.response?.data || error.message);
      // Remove invalid session
      localStorage.removeItem('session_token');
      setSessionToken(null);
      setUser(null);
      setInitialLoading(false);
    }
  };

  const handleAuthCallback = async (sessionId) => {
    try {
      const response = await axios.post(`${API}/auth/login`, {
        session_id: sessionId
      });
      
      const { user, session_token } = response.data;
      setUser(user);
      setSessionToken(session_token);
      localStorage.setItem('session_token', session_token);
      
      // Clear URL params
      window.history.replaceState({}, document.title, window.location.pathname);
    } catch (error) {
      console.error('Auth callback error:', error);
      
      // Clear URL params
      window.history.replaceState({}, document.title, window.location.pathname);
      
      // Check for waitlist registration (202 status)
      if (error.response?.status === 202 && error.response?.data?.detail === 'waitlist_registered') {
        setAuthError('waitlist_success');
        setShowAuthErrorModal(true);
      }
      // Check for pending approval (already registered but not approved)
      else if (error.response?.status === 403 && error.response?.data?.detail?.includes('pending approval')) {
        setAuthError(error.response?.data?.detail);
        setShowAuthErrorModal(true);
      }
      // Other errors
      else if (error.response?.status === 403) {
        setAuthError(error.response?.data?.detail || 'Access denied');
        setShowAuthErrorModal(true);
      } else {
        setAuthError('Authentication failed. Please try again or contact support.');
        setShowAuthErrorModal(true);
      }
    } finally {
      setInitialLoading(false);
    }
  };

  const login = () => {
    const redirectUrl = encodeURIComponent(window.location.origin);
    const authUrl = process.env.REACT_APP_AUTH_URL || 'https://auth.emergentagent.com';
    window.location.href = `${authUrl}/?redirect=${redirectUrl}`;
  };

  const logout = async () => {
    try {
      if (sessionToken) {
        await axios.post(`${API}/auth/logout`, {}, {
          headers: { Authorization: `Bearer ${sessionToken}` }
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('session_token');
      setUser(null);
      setSessionToken(null);
      // Reset URL to root and reload to show login screen
      window.history.replaceState({}, document.title, '/');
      window.location.reload();
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      isLoading: initialLoading,
      sessionToken,
      login,
      logout,
      isAuthenticated: !!(user || sessionToken)
    }}>
      {children}
      
      {/* Auth Error Modal for Google Sign-In */}
      {showAuthErrorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full p-8 transform transition-all">
            
            {/* Waitlist Success - New user registered and waiting for approval */}
            {authError === 'waitlist_success' ? (
              <>
                <div className="text-center mb-6">
                  <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full mb-4">
                    <span className="text-4xl">🎉</span>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                    You're on the Waitlist!
                  </h3>
                </div>
                
                <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 rounded-lg p-4 mb-6">
                  <div className="flex items-start gap-3">
                    <span className="text-xl">✅</span>
                    <div>
                      <p className="text-sm font-medium text-green-800 dark:text-green-300">
                        Registration Successful
                      </p>
                      <p className="text-xs text-green-700 dark:text-green-400 mt-1">
                        Your account has been created and is pending admin approval. You'll be able to sign in once an administrator approves your request.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg p-4 mb-6">
                  <div className="flex items-start gap-3">
                    <span className="text-xl">💡</span>
                    <div>
                      <p className="text-sm font-medium text-blue-800 dark:text-blue-300">
                        What's Next?
                      </p>
                      <p className="text-xs text-blue-700 dark:text-blue-400 mt-1">
                        The administrator will review your request and approve your account. You'll be notified once your access is granted.
                      </p>
                    </div>
                  </div>
                </div>
                
                <button
                  onClick={() => {
                    setShowAuthErrorModal(false);
                    setAuthError(null);
                  }}
                  className="w-full py-3 px-6 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-semibold rounded-lg transition-all"
                >
                  Got it!
                </button>
              </>
            ) : authError?.includes('pending approval') ? (
              <>
                {/* Registered but Not Approved */}
                <div className="text-center mb-6">
                  <div className="inline-flex items-center justify-center w-20 h-20 bg-yellow-100 dark:bg-yellow-900/30 rounded-full mb-4">
                    <span className="text-4xl">⏳</span>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                    Pending Approval
                  </h3>
                </div>
                
                <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4 mb-6">
                  <div className="flex items-start gap-3">
                    <span className="text-xl">⏳</span>
                    <div>
                      <p className="text-sm font-medium text-yellow-800 dark:text-yellow-300">
                        Almost There!
                      </p>
                      <p className="text-xs text-yellow-700 dark:text-yellow-400 mt-1">
                        Your account is registered and waiting for admin approval. Please be patient or contact your administrator to expedite the process.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg p-4 mb-6">
                  <div className="flex items-start gap-3">
                    <span className="text-xl">💡</span>
                    <div>
                      <p className="text-sm font-medium text-blue-800 dark:text-blue-300">
                        Need Faster Access?
                      </p>
                      <p className="text-xs text-blue-700 dark:text-blue-400 mt-1">
                        Contact your administrator and ask them to approve your account from Admin Panel → User Management.
                      </p>
                    </div>
                  </div>
                </div>
                
                <button
                  onClick={() => {
                    setShowAuthErrorModal(false);
                    setAuthError(null);
                  }}
                  className="w-full py-3 px-6 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 font-semibold rounded-lg transition-all"
                >
                  Close
                </button>
              </>
            ) : (
              <>
                {/* Generic Error */}
                <div className="text-center mb-6">
                  <div className="inline-flex items-center justify-center w-20 h-20 bg-red-100 dark:bg-red-900/30 rounded-full mb-4">
                    <span className="text-4xl">⚠️</span>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                    Authentication Failed
                  </h3>
                </div>
                
                <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg p-4 mb-6">
                  <div className="flex items-start gap-3">
                    <span className="text-xl">⚠️</span>
                    <div>
                      <p className="text-sm font-medium text-red-800 dark:text-red-300">
                        Something Went Wrong
                      </p>
                      <p className="text-xs text-red-700 dark:text-red-400 mt-1">
                        {authError || 'Please try again or contact support if the problem persists.'}
                      </p>
                    </div>
                  </div>
                </div>
                
                <button
                  onClick={() => {
                    setShowAuthErrorModal(false);
                    setAuthError(null);
                  }}
                  className="w-full py-3 px-6 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 font-semibold rounded-lg transition-all"
                >
                  Close
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
};

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Components
// BACKUP: Old Landing Page (keep for rollback if needed)
const OldLandingPage = () => {
  const { login } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showSignupModal, setShowSignupModal] = useState(false);

  const features = [
    {
      icon: "🎯",
      title: "AI-Powered Content Generation",
      description: "Generate compelling marketing content using advanced Claude AI technology for social media, Google Ads, and email campaigns."
    },
    {
      icon: "📱",
      title: "Multi-Format Marketing",
      description: "Create content for multiple platforms simultaneously - social media posts, Google Ads, product descriptions, and email marketing."
    },
    {
      icon: "🎬",
      title: "Video Content Creation",
      description: "Generate promotional videos with AI using Replicate, Kie AI Veo3, and Google Veo3 models. Create scripts and videos with synthetic scenes and voiceover."
    },
    {
      icon: "🎨",
      title: "Brand Voice Matching",
      description: "Maintain consistent brand voice across all your marketing materials with customizable tone settings and demographic targeting."
    },
    {
      icon: "📊",
      title: "Campaign Analytics",
      description: "Track performance with detailed analytics, campaign insights, and comprehensive reporting dashboards."
    },
    {
      icon: "⚡",
      title: "Instant Results",
      description: "Generate professional marketing copy and videos in seconds, not hours. Accelerate your lead generation process."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <header className="relative z-10 bg-white/80 dark:bg-gray-900/80 backdrop-blur-md border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <div className="text-3xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                AdElevate
              </div>
              <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">AI Marketing Platform</span>
            </div>
            <div className="flex items-center space-x-4">
              <button onClick={toggleTheme} className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors">
                {isDark ? '☀️' : '🌙'}
              </button>
              <button onClick={() => setShowLoginModal(true)} className="px-4 py-2 text-green-600 dark:text-yellow-400 hover:bg-yellow-50 dark:hover:bg-yellow-900/20 rounded-lg font-medium transition-colors">
                Sign In
              </button>
              <button onClick={() => setShowSignupModal(true)} className="px-6 py-2 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white rounded-lg font-medium transition-all transform hover:scale-105">
                Get Started Free
              </button>
            </div>
          </div>
        </div>
      </header>
      <section className="relative py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl lg:text-7xl font-bold text-gray-900 dark:text-white mb-6">
              Transform Products into
              <span className="block bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">Compelling Leads</span>
            </h1>
            <p className="text-xl lg:text-2xl text-gray-600 dark:text-gray-300 mb-8 max-w-4xl mx-auto">
              AdElevate's AI-powered platform creates persuasive marketing content and videos that convert prospects into customers.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <button onClick={() => setShowSignupModal(true)} className="px-8 py-4 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-bold rounded-xl text-lg transition-all transform hover:scale-105 shadow-lg hover:shadow-xl">
                🚀 Start Creating Content
              </button>
            </div>
          </div>
        </div>
      </section>
      <section className="py-20 bg-white/50 dark:bg-gray-900/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all transform hover:-translate-y-2 border border-gray-100 dark:border-gray-700">
                <div className="text-4xl mb-4">{feature.icon}</div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">{feature.title}</h3>
                <p className="text-gray-600 dark:text-gray-300 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <LoginModal isOpen={showLoginModal} onClose={() => setShowLoginModal(false)} />
      <SignupModal isOpen={showSignupModal} onClose={() => setShowSignupModal(false)} />
    </div>
  );
};

// Showcase Section Component - Displays AI-generated content gallery
const ShowcaseSection = ({ colorTheme, currentColors }) => {
  const scrollRef = useRef(null);
  const videoRefs = useRef({});
  const [isAutoScrollPaused, setIsAutoScrollPaused] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [lightboxVideo, setLightboxVideo] = useState(null);
  const [lightboxText, setLightboxText] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [isMuted, setIsMuted] = useState(true);

  // Helper function to simplify model names
  const getSimpleModelName = (model, type) => {
    const modelLower = model.toLowerCase();
    if (type === 'video') {
      if (modelLower.includes('ultra')) return 'Ultra Video';
      if (modelLower.includes('premium')) return 'Premium Video';
      return 'Video';
    } else if (type === 'text') {
      if (modelLower.includes('gpt') || modelLower.includes('4o')) return 'GPT-4o';
      if (modelLower.includes('claude')) return 'Claude';
      return 'AI Text';
    } else {
      if (modelLower.includes('ultra')) return 'Ultra Image';
      if (modelLower.includes('pro 2') || modelLower.includes('pro2')) return 'Ultra Image';
      if (modelLower.includes('premium') || modelLower.includes('pro')) return 'Premium Image';
      return 'Image';
    }
  };

  // Showcase items with permanent S3 URLs - User provided content first
  const showcaseItems = [
    // === USER PROVIDED VIDEOS (6) ===
    {
      id: 1,
      type: 'video',
      title: 'Maha Shivaratri Celebration',
      model: 'Sora 2 Ultra',
      url: 'https://adelevate-dev.s3.ap-southeast-2.amazonaws.com/users/0c285ccc-25a5-46c3-a901-054ecd8b9382/videos/2026/01/MahaShivaratriCelebr_c489e8238_20260124_040020_91c7842c.mp4',
      thumbnail: 'https://adelevate-dev.s3.ap-southeast-2.amazonaws.com/users/0c285ccc-25a5-46c3-a901-054ecd8b9382/images/2026/01/MahaShivaratriCelebr_c489e8238_20260124_035211_161a9018.png',
      category: 'Festival'
    },
    {
      id: 2,
      type: 'video',
      title: 'AdElevate - AI Marketing',
      model: 'Veo 3.1 Premium',
      url: 'https://adelevate.s3.ap-southeast-2.amazonaws.com/users/332d1751-0ee9-4a72-b296-c1d640108f10/videos/2026/01/AdElevate_c9612aab1_20260121_043345_50413936.mp4',
      thumbnail: 'https://adelevate.s3.ap-southeast-2.amazonaws.com/users/332d1751-0ee9-4a72-b296-c1d640108f10/images/2026/01/AdElevate_c9612aab1_20260121_043220_feeb268f.jpg',
      category: 'Marketing'
    },
    {
      id: 3,
      type: 'video',
      title: 'Puranmal - Restaurant',
      model: 'Sora 2 Premium',
      url: 'https://adelevate-dev.s3.ap-southeast-2.amazonaws.com/users/0c285ccc-25a5-46c3-a901-054ecd8b9382/videos/2026/01/Puranmal_cdbbc1b68_20260124_054832_d99bdd7c.mp4',
      thumbnail: 'https://adelevate-dev.s3.ap-southeast-2.amazonaws.com/users/0c285ccc-25a5-46c3-a901-054ecd8b9382/images/2026/01/Puranmal_cdbbc1b68_20260124_054039_df604fb3.png',
      category: 'Restaurant'
    },
    {
      id: 4,
      type: 'video',
      title: 'ICRIT Infotech - IT Services',
      model: 'Veo 3.1 Ultra',
      url: 'https://adelevate-dev.s3.ap-southeast-2.amazonaws.com/users/0c285ccc-25a5-46c3-a901-054ecd8b9382/videos/2026/02/ICRITInfotech_cea630e02_20260209_104103_20f86e10.mp4',
      thumbnail: 'https://adelevate-dev.s3.ap-southeast-2.amazonaws.com/users/0c285ccc-25a5-46c3-a901-054ecd8b9382/images/2026/02/ICRITInfotech_cea630e02_20260209_102944_278ed696.png',
      category: 'Technology'
    },
    {
      id: 5,
      type: 'video',
      title: 'AdElevate - Platform Demo',
      model: 'Sora 2 Ultra',
      url: 'https://adelevate-dev.s3.ap-southeast-2.amazonaws.com/users/0c285ccc-25a5-46c3-a901-054ecd8b9382/videos/2026/02/AdElevate_c3e732029_20260207_103250_f6bcf3ae.mp4',
      thumbnail: 'https://adelevate-dev.s3.ap-southeast-2.amazonaws.com/users/0c285ccc-25a5-46c3-a901-054ecd8b9382/images/2026/02/AdElevate_c3e732029_20260207_102433_2b1c8af3.png',
      category: 'Marketing'
    },
    {
      id: 6,
      type: 'video',
      title: 'eSankalpam - Digital Services',
      model: 'Veo 3.1 Premium',
      url: 'https://adelevate-dev.s3.ap-southeast-2.amazonaws.com/users/0c285ccc-25a5-46c3-a901-054ecd8b9382/videos/2026/01/eSankalpam_cf0f30f01_20260121_001429_11d261d7.mp4',
      thumbnail: 'https://adelevate-dev.s3.ap-southeast-2.amazonaws.com/users/0c285ccc-25a5-46c3-a901-054ecd8b9382/images/2026/01/eSankalpam_c4ceb161d_20260120_082124_576ce602.png',
      category: 'Technology'
    },
    // === USER PROVIDED IMAGES (6) ===
    {
      id: 7,
      type: 'image',
      title: 'Maha Shivaratri Celebration',
      model: 'Nano Banana Pro',
      url: 'https://adelevate-dev.s3.ap-southeast-2.amazonaws.com/users/0c285ccc-25a5-46c3-a901-054ecd8b9382/images/2026/01/MahaShivaratriCelebr_c489e8238_20260124_035211_161a9018.png',
      category: 'Festival'
    },
    {
      id: 8,
      type: 'image',
      title: 'Puranmal - Restaurant',
      model: 'GPT-4o Ultra',
      url: 'https://adelevate-dev.s3.ap-southeast-2.amazonaws.com/users/0c285ccc-25a5-46c3-a901-054ecd8b9382/images/2026/01/Puranmal_cdbbc1b68_20260124_054039_df604fb3.png',
      category: 'Restaurant'
    },
    {
      id: 9,
      type: 'image',
      title: 'AdElevate - Platform Demo',
      model: 'Nano Banana Pro 2',
      url: 'https://adelevate-dev.s3.ap-southeast-2.amazonaws.com/users/0c285ccc-25a5-46c3-a901-054ecd8b9382/images/2026/02/AdElevate_c3e732029_20260207_102433_2b1c8af3.png',
      category: 'Marketing'
    },
    {
      id: 10,
      type: 'image',
      title: 'ICRIT Infotech - IT Services',
      model: 'GPT-4o Premium',
      url: 'https://adelevate-dev.s3.ap-southeast-2.amazonaws.com/users/0c285ccc-25a5-46c3-a901-054ecd8b9382/images/2026/02/ICRITInfotech_cea630e02_20260209_102944_278ed696.png',
      category: 'Technology'
    },
    {
      id: 11,
      type: 'image',
      title: 'eSankalpam - Digital Services',
      model: 'Nano Banana Pro',
      url: 'https://adelevate-dev.s3.ap-southeast-2.amazonaws.com/users/0c285ccc-25a5-46c3-a901-054ecd8b9382/images/2026/01/eSankalpam_c4ceb161d_20260120_082124_576ce602.png',
      category: 'Technology'
    },
    {
      id: 12,
      type: 'image',
      title: 'AdElevate - AI Marketing',
      model: 'GPT-4o Ultra',
      url: 'https://adelevate.s3.ap-southeast-2.amazonaws.com/users/332d1751-0ee9-4a72-b296-c1d640108f10/images/2026/01/AdElevate_c9612aab1_20260121_043220_feeb268f.jpg',
      category: 'Marketing'
    },
    // === EXISTING ITEMS ===
    {
      id: 13,
      type: 'image',
      title: 'Tandoor Palace - Restaurant',
      model: 'Ultra Quality',
      url: 'https://adelevate-dev.s3.ap-southeast-2.amazonaws.com/users/showcase/images/2026/02/tandoorpalace1_clanding__20260210_050331_6c3e14c1.png',
      category: 'Restaurant'
    },
    {
      id: 14,
      type: 'image',
      title: 'MindfulMe - Wellness App',
      model: 'Ultra Quality',
      url: 'https://adelevate-dev.s3.ap-southeast-2.amazonaws.com/users/showcase/images/2026/02/mindfulme1_clanding__20260210_050334_6e80acbf.png',
      category: 'Mobile App'
    },
    {
      id: 15,
      type: 'image',
      title: 'CloudScale Solutions',
      model: 'Premium Quality',
      url: 'https://adelevate-dev.s3.ap-southeast-2.amazonaws.com/users/showcase/images/2026/02/cloudscale_clanding__20260210_050335_e77eed37.jpeg',
      category: 'Service'
    },
    {
      id: 16,
      type: 'image',
      title: 'ShopifyX Pro - E-commerce',
      model: 'Ultra Quality',
      url: 'https://adelevate-dev.s3.ap-southeast-2.amazonaws.com/users/showcase/images/2026/02/shopifyx1_clanding__20260210_050336_ed18bd2e.png',
      category: 'E-commerce'
    },
    {
      id: 17,
      type: 'video',
      title: 'AdElevate - Marketing Platform',
      model: 'Sora 2 Ultra',
      url: 'https://adelevate-dev.s3.ap-southeast-2.amazonaws.com/users/0c285ccc-25a5-46c3-a901-054ecd8b9382/videos/2026/02/AdElevate_c527a3585_20260208_012227_33e28740.mp4',
      thumbnail: 'https://adelevate-dev.s3.ap-southeast-2.amazonaws.com/users/showcase/images/2026/02/tandoorpalace1_clanding__20260210_050331_6c3e14c1.png',
      category: 'Marketing'
    },
    {
      id: 18,
      type: 'image',
      title: 'FitPro 360 - Fitness',
      model: 'Ultra Quality',
      url: 'https://adelevate-dev.s3.ap-southeast-2.amazonaws.com/users/showcase/images/2026/02/fitpro_clanding__20260210_050337_734bdf2b.png',
      category: 'Mobile App'
    },
    // === TEXT ADS (3) - Real examples from user screenshots ===
    {
      id: 19,
      type: 'text',
      title: 'Digital-Savvy Entrepreneur Priya',
      model: 'GPT-4o',
      category: 'Fashion/Beauty',
      persona: 'Digital-Savvy Entrepreneur Priya',
      headline: 'Hey Priya! Struggling with ad creatives that actually convert?',
      content: `Hey there! 👋 I know how exhausting it gets creating fresh ads for your brand every week... especially when you're juggling everything from inventory to customer service! 😅 What if I told you there's an AI tool that creates professional ads in Hindi, Telugu, AND English - all in under 60 seconds? 🤯 Meet AdElevate ✨ • Instagram stories that pop 📸 • Facebook ads that convert 💰 • Google ads in your customers' local language 🎯 • Video content that actually stops the scroll 🎬 The best part? No monthly subscriptions! You only pay for what you use 💪 Remember when we used to spend hours on Canva and still weren't happy with the results? This literally creates ads faster than ordering chai online! ☕ Perfect for us entrepreneurs who need quality content but don't have a marketing team budget 🚀`,
      cta: `Want to see how it works for fashion/beauty brands like yours? Let's hop on a quick 10-min demo call this week! Just reply "SHOW ME" and I'll send you some sample ads it created for brands in Mumbai and Bangalore 📱`,
      ctaColor: 'text-blue-500'
    },
    {
      id: 20,
      type: 'text',
      title: 'Marketing Manager Suresh',
      model: 'GPT-4o',
      category: 'SME Marketing',
      persona: 'Marketing Manager Suresh',
      headline: 'Suresh ji, SME marketing mein AI ka जादू देखा है?',
      content: `Namaste Suresh ji! 👋 Aap daily kitne ads banvane mein time waste करते हैं? Client meetings, creative briefs, designers का wait... फिर revisions! 😅 AdElevate के साथ यह सब बस 60 seconds में ho जाता है! 🎯 **Real scenario:** Manufacturing client को Facebook ad चाहिए Hindi में? Done in 1 minute! 📱 Telugu audience के लिए Instagram video ad? AI तुरंत बना देगा! 🖥️ Google ads English में चाहिए retail business के लिए? Sorted! **Kyun perfect है SMEs के लिए:** ✅ No monthly subscription headache - credit जब चाहिए तब buy करो ✅ 50+ languages support (Hindi, Telugu, English sab covered!) ✅ Facebook, Insta, Google, TikTok - सब platforms ready ✅ Team को train करने की tension नहीं Pune के ek manufacturing SME ने last month 200+ ads बनाए sirf 2 घंटे में! 🔥 Subscription नहीं, bas credits खरीदो जब जरूरत हो. Perfect for budget-conscious SME marketing, right?`,
      cta: `Want to see demo specifically for your business vertical? मैं personally 15-min demo दे सकता हूं - आपके industry के examples के साथ! Reply "DEMO" and I'll schedule it at your convenience 📅`,
      ctaColor: 'text-red-500'
    },
    {
      id: 21,
      type: 'text',
      title: 'Strategic Marketing Manager Elena',
      model: 'Claude',
      category: 'Spanish Marketing',
      persona: 'Strategic Marketing Manager Elena',
      headline: 'De Presupuesto Limitado a Resultados Excepcionales: La Historia de Éxito de Elena en Marketing',
      content: `¡Esto me encanta! 😍 ¡MMDF (My Million Dollar Funnels) es increíble! ✨ ¡Perfecto para Elena, Gerente de Marketing Estratégico!`,
      cta: `¡Descúbrelo ahora! Enlace en la bio 👇`,
      ctaColor: 'text-yellow-500'
    },
    {
      id: 22,
      type: 'text',
      title: 'Career-Growth Focused Priya (Tamil)',
      model: 'GPT-4o',
      category: 'Career/Education',
      persona: 'Career-Growth Focused Priya',
      headline: '🚀 ஏய் ப்ரியா! உங்கள் வாழ்க்கையின் உச்சத்தை தொட தயாரா?',
      content: `வணக்கம்! 👋 உங்கள் தற்போதைய வேலையில் நீங்கள் சிறப்பாக செயல்பட்டு வருகிறீர்கள் என்று எனக்குத் தெரியும், ஆனால் உண்மையைச் சொல்வதென்றால் - நீங்கள் இன்னும் பெரிய ஒன்றுக்கு தயாரா? நீங்கள் கண்ணில் கண்ட அந்த பதவி உயர்வு? உங்கள் உண்மையான திறமையைப் பிரதிபலிக்கும் சம்பள உயர்வு? எல்லாம் உங்கள் கையில்தான் உள்ளது, ஆனால் இன்றைய போட்டி நிறைந்த சந்தையில் தனித்து நிற்க உங்களுக்கு சரியான திறமைகள் தேவை.

தொழில்துறையில் இப்போது நான் பார்க்கும் விஷயங்கள்: 📊 நிறுவனங்கள் BI & Data Science நிபுணத்துவம் கொண்ட வல்லுநர்களை தீவிரமாக தேடி வருகின்றன 💡 Self-Service BI தளங்கள்தான் எதிர்காலம் - பெரும்பாலான மக்களுக்கு அவற்றை எப்படி பயன்படுத்துவது என்றே தெரியாது 💼 Full-stack development திறமைகள் = உடனடி வாழ்க்கை மேம்பாடு

ICRIT Infotech-இல், நாங்கள் வேறு ஏதாவது பயிற்சி நிறுவனம் அல்ல. நாங்கள் ஆஸ்திரேலியாவின் சொந்த IT பவர்ஹவுஸ், உங்களைப் போன்ற எண்ணற்ற வல்லுநர்கள் "நல்ல ஊழியர்" என்பதிலிருந்து "அபூர்வமான சொத்து" ஆக மாற உதவியுள்ளோம்.

எங்கள் ரகசிய மந்திரம்? 🪄 ✅ நிஜ உலக Business Intelligence development பயிற்சி ✅ மேம்பட்ட Data Science & Analytics (எல்லோரும் விரும்பும் திறமைகள்!) ✅ முழுமையான App & Web development கோர்ஸ்கள் ✅ 100% வேலை வாய்ப்பு உதவி (ஆம், நாங்கள் உண்மையில் உங்கள் கனவு வேலையைப் பெற உதவுகிறோம்!) ✅ உங்களை வேலைக்கு அமர்த்தும் நேர்காணல் தயாரிப்பு

சிறந்த விஷயம்? நாங்கள் Microsoft/Google/Zoho/Adobe அங்கீகரிக்கப்பட்ட விற்பனையாளர்கள், எனவே நீங்கள் பெரிய நிறுவனங்கள் பயன்படுத்தும் அதே தளங்களில் கற்கிறீர்கள்.`,
      cta: `"என்ன செய்யலாம்" என்று யோசிப்பதை நிறுத்திவிட்டு, அதை நிஜமாக்க தயாரா? உங்கள் வாழ்க்கை இலக்குகளைப் பற்றி பேசலாமா → +61-470298065`,
      ctaColor: 'text-green-500'
    },
    {
      id: 23,
      type: 'text',
      title: 'Growth-Focused Marketing Manager Fatima (Arabic)',
      model: 'GPT-4o',
      category: 'Arabic Marketing',
      persona: 'Growth-Focused Marketing Manager Fatima',
      headline: 'وسّع فريق التسويق الخاص بك بالذكاء الاصطناعي',
      content: `سئمت من إبطاء المحتوى اليدوي لحملاتك التسويقية؟ يقوم الذكاء الاصطناعي من 3S Tech بإنتاج محتوى تسويقي عالي الجودة فورياً، مما يساعد فرق التسويق في دبي على المنافسة بميزانيات الشركات الكبرى. اكتشف كيف يمكن لحلول 3S Tech المبتكرة أن تحول استراتيجيتك التسويقية وتضعك في المقدمة.`,
      cta: `ابدأ تجربتك المجانية اليوم`,
      ctaColor: 'text-blue-500'
    }
  ];

  // Filter items based on selected filter
  const filteredItems = selectedFilter === 'all' 
    ? showcaseItems 
    : showcaseItems.filter(item => item.type === selectedFilter);

  // Toggle mute only for hero video
  const toggleMute = () => {
    const newMutedState = !isMuted;
    setIsMuted(newMutedState);
    // Only affect the hero video
    const heroVideoEl = videoRefs.current['hero'];
    if (heroVideoEl) {
      heroVideoEl.muted = newMutedState;
    }
  };

  // Auto-scroll effect
  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (scrollContainer && !isAutoScrollPaused) {
      let scrollAmount = 0;
      const scrollSpeed = 0.8;
      
      const autoScroll = setInterval(() => {
        scrollAmount += scrollSpeed;
        if (scrollAmount >= scrollContainer.scrollWidth - scrollContainer.clientWidth) {
          scrollAmount = 0;
        }
        scrollContainer.scrollLeft = scrollAmount;
      }, 30);

      return () => clearInterval(autoScroll);
    }
  }, [isAutoScrollPaused]);

  // Video auto-play on scroll using Intersection Observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (entry.isIntersecting) {
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });
      },
      { threshold: 0.5 }
    );

    Object.values(videoRefs.current).forEach((video) => {
      if (video) observer.observe(video);
    });

    return () => observer.disconnect();
  }, []);

  const getGradientClasses = () => {
    switch(colorTheme) {
      case 'purple': return 'from-purple-900 via-purple-800 to-gray-900';
      case 'blue': return 'from-blue-900 via-blue-800 to-gray-900';
      case 'green': return 'from-green-900 via-green-800 to-gray-900';
      default: return 'from-purple-900 via-purple-800 to-gray-900';
    }
  };

  const getAccentColor = () => {
    switch(colorTheme) {
      case 'purple': return 'text-purple-400';
      case 'blue': return 'text-blue-400';
      case 'green': return 'text-green-400';
      default: return 'text-purple-400';
    }
  };

  const getBadgeBg = () => {
    switch(colorTheme) {
      case 'purple': return 'bg-purple-500/20 text-purple-400';
      case 'blue': return 'bg-blue-500/20 text-blue-400';
      case 'green': return 'bg-green-500/20 text-green-400';
      default: return 'bg-purple-500/20 text-purple-400';
    }
  };

  const heroVideo = showcaseItems.find(i => i.type === 'video');

  return (
    <>
      <section id="showcase" className={`py-20 px-6 bg-gradient-to-br ${getGradientClasses()} relative overflow-hidden`} data-testid="showcase-section">
        <div className="absolute inset-0 opacity-10">
          <div className={`absolute top-20 left-20 w-96 h-96 ${colorTheme === 'purple' ? 'bg-purple-600' : colorTheme === 'blue' ? 'bg-blue-600' : 'bg-green-600'} rounded-full blur-3xl`}></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-yellow-500 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          {/* Section Header */}
          <div className="text-center mb-12 reveal">
            <span className={`inline-block px-4 py-1.5 ${getBadgeBg()} rounded-full text-sm font-medium mb-4`}>
              AI-GENERATED CONTENT
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              See What Our AI Creates
            </h2>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Real examples generated in seconds — images, videos, and ads for any business
            </p>
          </div>

          {/* Featured Hero - Large Video or Image */}
          <div className="mb-10 reveal">
            {heroVideo ? (
              <div className="relative rounded-3xl overflow-hidden group shadow-2xl" data-testid="showcase-hero-video">
                <video
                  ref={(el) => (videoRefs.current['hero'] = el)}
                  src={heroVideo.url}
                  className="w-full h-64 md:h-80 lg:h-96 object-cover"
                  autoPlay
                  muted={isMuted}
                  loop
                  playsInline
                  poster={heroVideo.thumbnail}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent pointer-events-none">
                  <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                    <span className="inline-block px-3 py-1 bg-red-500 text-white text-xs font-bold rounded-full mb-3 animate-pulse">
                      LIVE VIDEO
                    </span>
                    <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">
                      {heroVideo.title}
                    </h3>
                    <p className="text-gray-300">Generated with {getSimpleModelName(heroVideo.model, heroVideo.type)} — Auto-playing</p>
                  </div>
                </div>
                {/* Mute/Unmute Button - Only for Hero Video */}
                <button
                  onClick={toggleMute}
                  className="absolute top-4 right-4 flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium bg-black/60 text-white hover:bg-black/80 transition-all backdrop-blur-sm pointer-events-auto"
                  data-testid="hero-mute-btn"
                >
                  {isMuted ? (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                      </svg>
                      Unmute
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                      </svg>
                      Mute
                    </>
                  )}
                </button>
              </div>
            ) : (
              <div 
                onClick={() => setLightboxImage(showcaseItems[0].url)}
                className="relative rounded-3xl overflow-hidden cursor-pointer group shadow-2xl"
                data-testid="showcase-hero-image"
              >
                <img 
                  src={showcaseItems[0].url} 
                  alt="Featured" 
                  className="w-full h-64 md:h-80 lg:h-96 object-cover transform transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent">
                  <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                    <span className="inline-block px-3 py-1 bg-yellow-500 text-black text-xs font-bold rounded-full mb-3">
                      FEATURED
                    </span>
                    <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">{showcaseItems[0].title}</h3>
                    <p className="text-gray-300">Generated with {getSimpleModelName(showcaseItems[0].model, showcaseItems[0].type)} in seconds</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Auto-Scrolling Gallery */}
          <div className="mb-10 reveal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white flex items-center gap-2">
                <span className="text-yellow-400">✨</span> Browse Our AI Creations
              </h3>
              <div className="flex items-center gap-4">
                {/* Filter Buttons */}
                <div className="flex gap-2">
                  {[
                    { key: 'all', label: 'All' },
                    { key: 'image', label: 'Images' },
                    { key: 'video', label: 'Videos' },
                    { key: 'text', label: 'Text Ads' }
                  ].map(filter => (
                    <button
                      key={filter.key}
                      onClick={() => setSelectedFilter(filter.key)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                        selectedFilter === filter.key
                          ? 'bg-white text-gray-900 shadow-md'
                          : 'bg-white/10 text-gray-300 hover:bg-white/20'
                      }`}
                      data-testid={`filter-${filter.key}`}
                    >
                      {filter.label}
                    </button>
                  ))}
                </div>
                {/* Auto-scroll Status */}
                <span className="text-xs text-gray-400 flex items-center gap-2">
                  {isAutoScrollPaused ? (
                    <>
                      <span className="w-2 h-2 bg-yellow-500 rounded-full"></span>
                      Paused
                    </>
                  ) : (
                    <>
                      <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                      Auto-scroll
                    </>
                  )}
                </span>
              </div>
            </div>
            
            <div 
              ref={scrollRef}
              className="flex gap-4 overflow-x-auto pb-4"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
              onMouseEnter={() => setIsAutoScrollPaused(true)}
              onMouseLeave={() => setIsAutoScrollPaused(false)}
              data-testid="showcase-scroll-container"
            >
              <style>{`.showcase-scroll::-webkit-scrollbar { display: none; }`}</style>
              {[...filteredItems, ...filteredItems].map((item, index) => (
                <div
                  key={`showcase-${item.id}-${index}`}
                  onClick={() => {
                    if (item.type === 'video') setLightboxVideo(item.url);
                    else if (item.type === 'text') setLightboxText(item);
                    else setLightboxImage(item.url);
                  }}
                  className={`flex-shrink-0 ${item.type === 'text' ? 'w-80' : 'w-64'} cursor-pointer group`}
                  data-testid={`showcase-item-${item.id}`}
                >
                  <div className={`relative rounded-xl overflow-hidden transform transition-all duration-300 group-hover:scale-105 shadow-lg group-hover:shadow-${colorTheme === 'purple' ? 'purple' : colorTheme === 'blue' ? 'blue' : 'green'}-500/30 ${item.type === 'text' ? 'h-48' : ''}`}>
                    {item.type === 'video' ? (
                      <>
                        <video
                          ref={(el) => (videoRefs.current[`scroll-${item.id}-${index}`] = el)}
                          src={item.url}
                          className="w-full h-40 object-cover"
                          autoPlay
                          muted
                          loop
                          playsInline
                          poster={item.thumbnail}
                        />
                        <div className="absolute top-2 right-2">
                          <span className="px-2 py-1 bg-red-500/90 text-white text-xs font-bold rounded-full flex items-center gap-1">
                            <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></span>
                            VIDEO
                          </span>
                        </div>
                      </>
                    ) : item.type === 'text' ? (
                      <>
                        {/* Text Ad Card */}
                        <div className={`h-48 bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800 p-4 flex flex-col ${
                          item.persona.includes('Fatima') ? 'text-right' : ''
                        }`} dir={item.persona.includes('Fatima') ? 'rtl' : 'ltr'}>
                          {/* Header with persona badge */}
                          <div className="flex items-center gap-2 mb-2">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                              item.persona.includes('Priya') ? 'bg-green-500/20' : 
                              item.persona.includes('Suresh') ? 'bg-blue-500/20' : 
                              item.persona.includes('Fatima') ? 'bg-amber-500/20' :
                              item.persona.includes('Elena') ? 'bg-red-500/20' : 'bg-purple-500/20'
                            }`}>
                              <svg className={`w-4 h-4 ${
                                item.persona.includes('Priya') ? 'text-green-400' : 
                                item.persona.includes('Suresh') ? 'text-blue-400' : 
                                item.persona.includes('Fatima') ? 'text-amber-400' :
                                item.persona.includes('Elena') ? 'text-red-400' : 'text-purple-400'
                              }`} fill="currentColor" viewBox="0 0 24 24">
                                <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                              </svg>
                            </div>
                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                              item.persona.includes('Priya') ? 'bg-green-500/20 text-green-400' : 
                              item.persona.includes('Suresh') ? 'bg-blue-500/20 text-blue-400' : 
                              item.persona.includes('Fatima') ? 'bg-amber-500/20 text-amber-400' :
                              item.persona.includes('Elena') ? 'bg-red-500/20 text-red-400' : 'bg-purple-500/20 text-purple-400'
                            }`}>
                              {item.persona.split(' ').slice(-1)[0].replace('(Tamil)', '').replace('(Arabic)', '').trim()}
                            </span>
                            <span className={`${item.persona.includes('Fatima') ? 'mr-auto' : 'ml-auto'} px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs font-bold rounded-full`}>
                              TEXT
                            </span>
                          </div>
                          {/* Headline */}
                          <h4 className="text-white font-semibold text-sm mb-2 line-clamp-2">{item.headline}</h4>
                          {/* Content preview */}
                          <p className="text-gray-400 text-xs line-clamp-3 flex-1">{item.content.substring(0, 150)}...</p>
                          {/* Footer - Category only, no model name */}
                          <div className="flex items-center justify-between mt-2 pt-2 border-t border-white/10">
                            <span className="text-xs text-gray-500">{item.category}</span>
                          </div>
                        </div>
                      </>
                    ) : (
                      <img 
                        src={item.url} 
                        alt={item.title}
                        className="w-full h-40 object-cover"
                      />
                    )}
                    {item.type !== 'text' && (
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3">
                        <p className="text-sm text-white font-medium truncate">{item.title}</p>
                        <p className="text-xs text-gray-400">{getSimpleModelName(item.model, item.type)}</p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <p className="text-center text-gray-500 text-sm mt-3">← Auto-scrolling • Hover to pause • Click to expand →</p>
          </div>

          {/* Stats Bar */}
          <div className={`bg-gradient-to-r from-yellow-500/10 to-orange-500/10 rounded-2xl p-6 border border-yellow-500/20 reveal`}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div>
                <p className="text-3xl font-bold text-yellow-400">10K+</p>
                <p className="text-sm text-gray-400">Images Generated</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-orange-400">5K+</p>
                <p className="text-sm text-gray-400">Videos Created</p>
              </div>
              <div>
                <p className={`text-3xl font-bold ${getAccentColor()}`}>50K+</p>
                <p className="text-sm text-gray-400">Text Ads Written</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-green-400">99%</p>
                <p className="text-sm text-gray-400">Cost Savings</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox for Images */}
      {lightboxImage && (
        <div 
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightboxImage(null)}
          data-testid="showcase-lightbox-image"
        >
          <button 
            className="absolute top-4 right-4 text-white/80 hover:text-white z-10"
            onClick={() => setLightboxImage(null)}
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <img 
            src={lightboxImage} 
            alt="Full size" 
            className="max-w-full max-h-[90vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Lightbox for Videos */}
      {lightboxVideo && (
        <div 
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4"
          onClick={() => setLightboxVideo(null)}
          data-testid="showcase-lightbox-video"
        >
          <button 
            className="absolute top-4 right-4 text-white/80 hover:text-white z-10"
            onClick={() => setLightboxVideo(null)}
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <video 
            src={lightboxVideo} 
            className="max-w-full max-h-[90vh] rounded-lg"
            controls
            autoPlay
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Lightbox for Text Ads */}
      {lightboxText && (
        <div 
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4"
          onClick={() => setLightboxText(null)}
          data-testid="showcase-lightbox-text"
        >
          <button 
            className="absolute top-4 right-4 text-white/80 hover:text-white z-10"
            onClick={() => setLightboxText(null)}
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <div 
            className="max-w-2xl w-full max-h-[90vh] overflow-y-auto bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800 rounded-2xl p-6 shadow-2xl border border-white/10"
            onClick={(e) => e.stopPropagation()}
            dir={lightboxText.persona.includes('Fatima') ? 'rtl' : 'ltr'}
          >
            {/* Persona Header */}
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                lightboxText.persona.includes('Priya') ? 'bg-green-500/20' : 
                lightboxText.persona.includes('Suresh') ? 'bg-blue-500/20' : 
                lightboxText.persona.includes('Fatima') ? 'bg-amber-500/20' :
                lightboxText.persona.includes('Elena') ? 'bg-red-500/20' : 'bg-purple-500/20'
              }`}>
                <svg className={`w-5 h-5 ${
                  lightboxText.persona.includes('Priya') ? 'text-green-400' : 
                  lightboxText.persona.includes('Suresh') ? 'text-blue-400' : 
                  lightboxText.persona.includes('Fatima') ? 'text-amber-400' :
                  lightboxText.persona.includes('Elena') ? 'text-red-400' : 'text-purple-400'
                }`} fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                </svg>
              </div>
              <div>
                <span className={`text-sm font-semibold px-3 py-1 rounded-full ${
                  lightboxText.persona.includes('Priya') ? 'bg-green-500/20 text-green-400' : 
                  lightboxText.persona.includes('Suresh') ? 'bg-blue-500/20 text-blue-400' : 
                  lightboxText.persona.includes('Fatima') ? 'bg-amber-500/20 text-amber-400' :
                  lightboxText.persona.includes('Elena') ? 'bg-red-500/20 text-red-400' : 'bg-purple-500/20 text-purple-400'
                }`}>
                  {lightboxText.persona}
                </span>
              </div>
              <span className={`${lightboxText.persona.includes('Fatima') ? 'mr-auto' : 'ml-auto'} flex items-center gap-1 text-xs text-gray-400`}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                AI Generated
              </span>
            </div>

            {/* Title */}
            <div className="mb-4">
              <span className="text-gray-500 text-xs uppercase tracking-wider">Title</span>
              <h3 className="text-xl font-bold text-white mt-1">{lightboxText.headline}</h3>
            </div>

            {/* Content */}
            <div className="mb-4">
              <span className="text-gray-500 text-xs uppercase tracking-wider">Content</span>
              <p className="text-gray-300 mt-2 leading-relaxed whitespace-pre-line">{lightboxText.content}</p>
            </div>

            {/* Call to Action */}
            <div className="mb-4">
              <span className="text-gray-500 text-xs uppercase tracking-wider">Call to Action</span>
              <p className={`mt-2 font-medium ${lightboxText.ctaColor || 'text-blue-400'}`}>{lightboxText.cta}</p>
            </div>

            {/* Footer Meta - Category only, no model name */}
            <div className="flex items-center justify-center pt-4 border-t border-white/10">
              <span className="text-sm text-gray-500">{lightboxText.category}</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// NEW Enhanced Landing Page - Option 4
const ModernLandingPage = () => {
  const { isDark, toggleTheme } = useTheme();
  const { currentColors, colorTheme } = useColorTheme();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [showWaitlistModal, setShowWaitlistModal] = useState(false);
  const [waitlistName, setWaitlistName] = useState('');
  const [waitlistEmail, setWaitlistEmail] = useState('');
  const [waitlistCompany, setWaitlistCompany] = useState('');
  const [waitlistSuccess, setWaitlistSuccess] = useState(false);

  const handleWaitlistSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/waitlist/join`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: waitlistName,
          email: waitlistEmail,
          company: waitlistCompany
        })
      });
      
      if (response.ok) {
        setWaitlistSuccess(true);
      } else {
        console.error('Failed to join waitlist');
        alert('Failed to join waitlist. Please try again.');
      }
    } catch (error) {
      console.error('Error joining waitlist:', error);
      alert('An error occurred. Please try again.');
    }
  };

  const closeWaitlistModal = () => {
    setShowWaitlistModal(false);
    setTimeout(() => {
      setWaitlistName('');
      setWaitlistEmail('');
      setWaitlistCompany('');
      setWaitlistSuccess(false);
    }, 300);
  };

  useEffect(() => {
    // Scroll reveal animation
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('active-reveal');
        }
      });
    }, observerOptions);

    document.querySelectorAll('.reveal').forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <div className="bg-white">
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .fade-in-up { animation: fadeInUp 0.8s ease-out forwards; }
        
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-15px); }
        }
        .float { animation: float 4s ease-in-out infinite; }
        
        @keyframes slideInLeft {
          from { opacity: 0; transform: translateX(-50px); }
          to { opacity: 1; transform: translateX(0); }
        }
        .slide-in-left { animation: slideInLeft 0.8s ease-out forwards; }
        
        @keyframes slideInRight {
          from { opacity: 0; transform: translateX(50px); }
          to { opacity: 1; transform: translateX(0); }
        }
        .slide-in-right { animation: slideInRight 0.8s ease-out forwards; }
        
        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.9); }
          to { opacity: 1; transform: scale(1); }
        }
        .scale-in { animation: scaleIn 0.6s ease-out forwards; }
        
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        .pulse-slow { animation: pulse 3s ease-in-out infinite; }
        
        @keyframes shimmer {
          0% { background-position: -1000px 0; }
          100% { background-position: 1000px 0; }
        }
        .shimmer {
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
          background-size: 1000px 100%;
          animation: shimmer 3s infinite;
        }
        
        .reveal {
          opacity: 0;
          transform: translateY(30px);
          transition: all 0.8s ease-out;
        }
        .reveal.active-reveal {
          opacity: 1;
          transform: translateY(0);
        }
      `}</style>

      {/* Header - Transparent on Hero (Calm Style) */}
      <header className="fixed w-full z-50 top-0">
        <div className="max-w-7xl mx-auto px-8 py-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <img 
                src="/logo192.png" 
                alt="AdElevate" 
                className="w-16 h-16 md:w-20 md:h-20 rounded-2xl shadow-xl"
              />
            </div>
            <nav className="hidden md:flex items-center space-x-10 text-lg font-medium">
              <a href="#showcase" className="hover:opacity-80 transition" style={{ color: '#E5E7EB', textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>Showcase</a>
              <a href="#how-it-works" className="hover:opacity-80 transition" style={{ color: '#E5E7EB', textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>How It Works</a>
              <a href="#features" className="hover:opacity-80 transition" style={{ color: '#E5E7EB', textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>Features</a>
              <a href="#faq" className="hover:opacity-80 transition" style={{ color: '#E5E7EB', textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>FAQ</a>
              <a href="#testimonials" className="hover:opacity-80 transition" style={{ color: '#E5E7EB', textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>Testimonials</a>
              <a href="/blog" className="hover:opacity-80 transition" style={{ color: '#E5E7EB', textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>Blog</a>
            </nav>
            <div className="flex items-center space-x-2 md:space-x-6">
              <button onClick={() => setShowLoginModal(true)} className="hover:opacity-80 font-medium text-sm md:text-lg transition whitespace-nowrap" style={{ color: '#E5E7EB', textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>Log In</button>
              <button onClick={() => setShowSignupModal(true)} className="px-3 md:px-8 py-1.5 md:py-3 bg-white text-gray-800 rounded-full text-[10px] md:text-lg font-semibold hover:shadow-xl transition transform hover:scale-105 whitespace-nowrap">Try AdElevate for Free</button>
            </div>
          </div>
        </div>
      </header>


      {/* Hero Section - Calm Style */}
      <CalmHeroSection 
        colorTheme={colorTheme}
        onGetStarted={() => setShowSignupModal(true)}
        showcaseVideoUrl="https://adelevate-dev.s3.ap-southeast-2.amazonaws.com/users/0c285ccc-25a5-46c3-a901-054ecd8b9382/videos/2026/02/AdElevate_cc81436e5_20260211_105744_b0d954eb.mp4"
      />

      {/* Showcase Section - AI Generated Content Gallery */}
      <ShowcaseSection colorTheme={colorTheme} currentColors={currentColors} />

      {/* How It Works */}
      <section id="how-it-works" className="py-20 px-6 bg-gray-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-20 w-96 h-96 bg-purple-600 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-blue-600 rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-16 reveal">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">How AdElevate Works</h2>
            <p className="text-xl text-gray-300">Three simple steps to marketing magic</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center reveal">
              <div className="relative inline-block mb-6 float">
                <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center text-3xl font-bold text-white shadow-2xl rotate-3 hover:rotate-6 transition">1</div>
                <div className="absolute -top-2 -right-2 w-6 h-6 bg-yellow-400 rounded-full pulse-slow"></div>
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">Describe Product</h3>
              <p className="text-gray-400 leading-relaxed">Tell AdElevate about your product, target audience, and goals in plain English</p>
            </div>
            <div className="text-center reveal">
              <div className="relative inline-block mb-6 float">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center text-3xl font-bold text-white shadow-2xl -rotate-3 hover:-rotate-6 transition">2</div>
                <div className="absolute -top-2 -right-2 w-6 h-6 bg-green-400 rounded-full pulse-slow"></div>
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">AI Creates Magic</h3>
              <p className="text-gray-400 leading-relaxed">Watch as AI generates content for all platforms—ads, videos, emails, social posts</p>
            </div>
            <div className="text-center reveal">
              <div className="relative inline-block mb-6 float">
                <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-emerald-500 rounded-2xl flex items-center justify-center text-3xl font-bold text-white shadow-2xl rotate-3 hover:rotate-6 transition">3</div>
                <div className="absolute -top-2 -right-2 w-6 h-6 bg-orange-400 rounded-full pulse-slow"></div>
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">Launch Everywhere</h3>
              <p className="text-gray-400 leading-relaxed">Review, edit, and publish to all your marketing channels with one click</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 reveal">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Everything You Need</h2>
            <p className="text-xl text-gray-600">Powerful features for modern marketing teams</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="md:col-span-2 bg-gradient-to-br from-purple-100 to-pink-100 rounded-3xl p-12 hover:shadow-2xl transition transform hover:scale-105 reveal">
              <div className="text-5xl mb-4">🎯</div>
              <h3 className="text-3xl font-bold text-gray-900 mb-4">AI-Powered Content Generation</h3>
              <p className="text-gray-700 text-lg mb-6">Multiple AI models working together to create perfect marketing copy for any platform</p>
            </div>
            <div className="bg-gradient-to-br from-blue-100 to-cyan-100 rounded-3xl p-8 hover:shadow-2xl transition transform hover:scale-105 reveal">
              <div className="text-5xl mb-4">🎬</div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">AI Video Creation</h3>
              <p className="text-gray-700">Professional videos with voiceover in minutes</p>
            </div>
            <div className={`bg-gradient-to-br ${currentColors.bgGradientFrom} ${currentColors.bgGradientTo} rounded-3xl p-8 hover:shadow-2xl transition transform hover:scale-105 reveal`}>
              <div className="text-5xl mb-4">📱</div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Multi-Platform</h3>
              <p className="text-gray-700">Deploy to all channels with one click</p>
            </div>
            <div className="md:col-span-2 bg-gradient-to-br from-orange-100 to-yellow-100 rounded-3xl p-12 hover:shadow-2xl transition transform hover:scale-105 reveal">
              <div className="text-5xl mb-4">📊</div>
              <h3 className="text-3xl font-bold text-gray-900 mb-4">Real-Time Analytics</h3>
              <p className="text-gray-700 text-lg">Track performance, measure ROI, and optimize campaigns with detailed insights</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 px-6 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16 reveal">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Common Questions</h2>
            <p className="text-xl text-gray-600">Everything you need to know about AdElevate</p>
          </div>
          <div className="space-y-4 reveal">
            <FAQItem 
              question="What are credits and how do they work?"
              answer="Credits are our usage currency. Each ad type costs different credits: Text ads (3), Image ads (15), Video ads (50-75). Credits never expire and you only pay for what you use."
              colorTheme={colorTheme}
            />
            <FAQItem 
              question="Can I switch between subscription plans?"
              answer="Absolutely! Upgrade or downgrade anytime. Unused credits roll over to your next billing cycle."
              colorTheme={colorTheme}
            />
            <FAQItem 
              question="What's included in the free trial?"
              answer="New users get 50 free credits—enough to create multiple text ads, image ads, or test our video generator. No credit card required."
              colorTheme={colorTheme}
            />
            <FAQItem 
              question="Do credits expire?"
              answer="No! All credits (one-time or subscription) remain in your account until you use them."
              colorTheme={colorTheme}
            />
            <FAQItem 
              question="Can I cancel my subscription?"
              answer="Yes, cancel anytime from your account dashboard. You'll keep access until the end of your current billing period."
              colorTheme={colorTheme}
            />
            <FAQItem 
              question="What if I run out of credits mid-campaign?"
              answer="Simply purchase a one-time credit pack or upgrade your subscription. Credits are added to your account instantly."
              colorTheme={colorTheme}
            />
            <FAQItem 
              question="Do you offer refunds?"
              answer="We offer a 7-day satisfaction guarantee on your first purchase. If AdElevate isn't right for you, we'll refund unused credits."
              colorTheme={colorTheme}
            />
            <FAQItem 
              question="What languages do you support?"
              answer="50+ languages including English, Spanish, French, German, Chinese, Japanese, Hindi, and more."
              colorTheme={colorTheme}
            />
            <FAQItem 
              question="Can I create custom brand guidelines?"
              answer="Yes! Pro and Annual subscribers can save brand colors, fonts, logos, and tone preferences for consistent output."
              colorTheme={colorTheme}
            />
            <FAQItem 
              question="What ad platforms can I export to?"
              answer="Export in formats optimized for Facebook, Instagram, Google Ads, LinkedIn, TikTok, Twitter, and more."
              colorTheme={colorTheme}
            />
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-20 px-6 bg-gradient-to-br from-gray-900 to-gray-800 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className={`absolute top-20 left-20 w-96 h-96 ${colorTheme === 'purple' ? 'bg-purple-600' : colorTheme === 'blue' ? 'bg-blue-600' : 'bg-green-600'} rounded-full blur-3xl`}></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-blue-600 rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-16 reveal">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Loved by 10,000+ Marketers</h2>
            <p className="text-xl text-gray-300">See what our customers are saying</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white/10 backdrop-blur-md rounded-3xl p-8 border border-white/20 hover:bg-white/15 transition transform hover:scale-105 reveal">
              <div className="text-yellow-400 text-2xl mb-4">⭐⭐⭐⭐⭐</div>
              <p className="text-white text-lg mb-6 leading-relaxed">"Cut our content creation time by 90%. AdElevate is a game-changer!"</p>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-pink-400 rounded-full mr-4"></div>
                <div>
                  <div className="text-white font-semibold">Jessica M.</div>
                  <div className="text-gray-400 text-sm">Marketing Director</div>
                </div>
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-3xl p-8 border border-white/20 hover:bg-white/15 transition transform hover:scale-105 reveal">
              <div className="text-yellow-400 text-2xl mb-4">⭐⭐⭐⭐⭐</div>
              <p className="text-white text-lg mb-6 leading-relaxed">"Best marketing investment we've made. ROI in the first week!"</p>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-cyan-400 rounded-full mr-4"></div>
                <div>
                  <div className="text-white font-semibold">David K.</div>
                  <div className="text-gray-400 text-sm">CEO, TechStart</div>
                </div>
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-3xl p-8 border border-white/20 hover:bg-white/15 transition transform hover:scale-105 reveal">
              <div className="text-yellow-400 text-2xl mb-4">⭐⭐⭐⭐⭐</div>
              <p className="text-white text-lg mb-6 leading-relaxed">"Replaced 3 tools and saved $3k/month. Incredible value!"</p>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-emerald-400 rounded-full mr-4"></div>
                <div>
                  <div className="text-white font-semibold">Amanda R.</div>
                  <div className="text-gray-400 text-sm">Growth Manager</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Enterprise/Agency Section */}
      <section id="enterprise" className="py-20 px-6 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className={`absolute top-20 left-20 w-96 h-96 ${colorTheme === 'purple' ? 'bg-purple-600' : colorTheme === 'blue' ? 'bg-blue-600' : 'bg-green-600'} rounded-full blur-3xl`}></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-yellow-500 rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-16 reveal">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Need Volume? Let's Talk Custom Plans</h2>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Running an agency or enterprise with high-volume needs? We offer custom plans with:
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {/* Features Card - Left Side */}
            <div className="bg-white/10 backdrop-blur-md rounded-3xl p-8 border border-white/20 reveal">
              <h3 className="text-2xl font-bold text-white mb-6 flex items-center">
                <span className={`w-10 h-10 rounded-full bg-gradient-to-r ${currentColors.buttonGradient} flex items-center justify-center mr-3`}>
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </span>
                Enterprise Features
              </h3>
              <ul className="space-y-4">
                {[
                  { icon: "💎", text: "Bulk credit discounts (50,000+ credits)" },
                  { icon: "👤", text: "Dedicated account manager" },
                  { icon: "🏷️", text: "White-label options" },
                  { icon: "🔌", text: "API access for integrations" },
                  { icon: "⚡", text: "Priority support & training" },
                  { icon: "🎨", text: "Custom brand guidelines" },
                  { icon: "👥", text: "Team collaboration tools" },
                  { icon: "📊", text: "Advanced analytics dashboard" }
                ].map((feature, idx) => (
                  <li key={idx} className="flex items-center text-gray-200 text-lg">
                    <span className="text-2xl mr-4">{feature.icon}</span>
                    <span>{feature.text}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            {/* Contact Form Card - Right Side */}
            <div className="bg-white rounded-3xl p-8 shadow-2xl reveal">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Get Custom Pricing</h3>
              <p className="text-gray-600 mb-6">Our team will create a tailored solution for your business</p>
              
              <EnterpriseContactForm colorTheme={colorTheme} currentColors={currentColors} />
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className={`py-32 px-6 bg-gradient-to-br ${colorTheme === 'purple' ? 'from-purple-600 via-pink-600 to-purple-700' : colorTheme === 'blue' ? 'from-blue-600 via-sky-600 to-blue-700' : 'from-green-600 via-emerald-600 to-teal-600'} relative overflow-hidden`}>
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-20 w-96 h-96 bg-yellow-400 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-blue-400 rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="inline-block px-6 py-2 bg-white/20 backdrop-blur-md text-white rounded-full text-sm font-semibold mb-8 pulse-slow">
            🎉 LIMITED TIME OFFER
          </div>
          <h2 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
            Ready to Transform Your Marketing?
          </h2>
          <p className="text-2xl text-white/90 mb-12 leading-relaxed">
            Join 10,000+ marketers creating amazing content in seconds
          </p>
          <button onClick={() => setShowSignupModal(true)} className={`px-16 py-6 bg-white ${currentColors.textColor} rounded-2xl text-xl font-bold hover:shadow-2xl transition transform hover:scale-110 mb-6 pulse-slow`}>
            Start Creating Ads Free →
          </button>
          <div className="flex justify-center items-center gap-8 text-white/90 text-sm flex-wrap">
            <div className="flex items-center"><span className="text-xl mr-2">✓</span><span>50 free credits</span></div>
            <div className="flex items-center"><span className="text-xl mr-2">✓</span><span>No credit card needed</span></div>
            <div className="flex items-center"><span className="text-xl mr-2">✓</span><span>Create ads instantly</span></div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white pt-16 pb-8 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Four Column Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12 mb-12">
            {/* Column 1: AdElevate */}
            <div className="col-span-2 md:col-span-1">
              <div className={`text-3xl font-bold bg-gradient-to-r ${currentColors.textGradient} bg-clip-text text-transparent mb-3`}>AdElevate</div>
              <p className="text-gray-400 text-sm mb-6">AI-Powered Marketing Made Simple</p>
              {/* Social Media Icons */}
              <div className="flex gap-4">
                <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 hover:bg-gray-700 rounded-full flex items-center justify-center transition" aria-label="Twitter">
                  <svg className="w-5 h-5 text-gray-400 hover:text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                </a>
                <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 hover:bg-gray-700 rounded-full flex items-center justify-center transition" aria-label="LinkedIn">
                  <svg className="w-5 h-5 text-gray-400 hover:text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                </a>
                <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 hover:bg-gray-700 rounded-full flex items-center justify-center transition" aria-label="Facebook">
                  <svg className="w-5 h-5 text-gray-400 hover:text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                </a>
                <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 hover:bg-gray-700 rounded-full flex items-center justify-center transition" aria-label="Instagram">
                  <svg className="w-5 h-5 text-gray-400 hover:text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                </a>
              </div>
            </div>
            
            {/* Column 2: Product */}
            <div>
              <h4 className="text-white font-semibold mb-4">Product</h4>
              <ul className="space-y-3">
                <li><a href="#showcase" className="text-gray-400 hover:text-white transition text-sm">Showcase</a></li>
                <li><a href="#how-it-works" className="text-gray-400 hover:text-white transition text-sm">How It Works</a></li>
                <li><a href="#features" className="text-gray-400 hover:text-white transition text-sm">Features</a></li>
                <li><a href="#faq" className="text-gray-400 hover:text-white transition text-sm">FAQ</a></li>
                <li><a href="#testimonials" className="text-gray-400 hover:text-white transition text-sm">Testimonials</a></li>
              </ul>
            </div>
            
            {/* Column 3: Resources */}
            <div>
              <h4 className="text-white font-semibold mb-4">Resources</h4>
              <ul className="space-y-3">
                <li><a href="/blog" className="text-gray-400 hover:text-white transition text-sm">Blog</a></li>
                <li><a href="/credits" className="text-gray-400 hover:text-white transition text-sm">Pricing</a></li>
                <li><a href="#faq" className="text-gray-400 hover:text-white transition text-sm">Help Center</a></li>
                <li><a href="/launch" className="text-gray-400 hover:text-white transition text-sm">Product Hunt</a></li>
              </ul>
            </div>
            
            {/* Column 4: Company */}
            <div>
              <h4 className="text-white font-semibold mb-4">Company</h4>
              <ul className="space-y-3">
                <li><a href="#" className="text-gray-400 hover:text-white transition text-sm">About Us</a></li>
                <li><a href="mailto:contact@3stech.ai" className="text-gray-400 hover:text-white transition text-sm">Contact</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition text-sm">Privacy Policy</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition text-sm">Terms of Service</a></li>
                <li><a href="#enterprise" className="text-gray-400 hover:text-white transition text-sm">Enterprise Solutions</a></li>
              </ul>
            </div>
          </div>
          
          {/* Bottom Bar / Copyright */}
          <div className="border-t border-gray-800 pt-8">
            <p className="text-center text-gray-500 text-sm">
              © 2026 3S Tech. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Modals */}
      <LoginModal isOpen={showLoginModal} onClose={() => setShowLoginModal(false)} />
      <SignupModal isOpen={showSignupModal} onClose={() => setShowSignupModal(false)} />
      
      {/* Waitlist Modal */}
      {showWaitlistModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={(e) => e.target === e.currentTarget && closeWaitlistModal()}>
          <div className="bg-white rounded-3xl p-8 md:p-12 max-w-md w-full mx-4 shadow-2xl transform transition-all">
            {!waitlistSuccess ? (
              <>
                <div className="text-center mb-6">
                  <div className="text-5xl mb-4">🎉</div>
                  <h3 className="text-3xl font-bold text-gray-900 mb-2">Join the Waitlist</h3>
                  <p className="text-gray-600">Be among the first to experience AdElevate's revolutionary marketing platform</p>
                </div>
                
                <form onSubmit={handleWaitlistSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Full Name</label>
                    <input 
                      type="text" 
                      value={waitlistName}
                      onChange={(e) => setWaitlistName(e.target.value)}
                      required 
                      placeholder="John Doe"
                      style={{ borderColor: 'rgb(209 213 219)' }}
                      className="w-full px-4 py-3 border-2 rounded-xl focus:outline-none transition"
                      onFocus={(e) => e.target.style.borderColor = currentColors.primary}
                      onBlur={(e) => e.target.style.borderColor = 'rgb(209 213 219)'}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Email Address</label>
                    <input 
                      type="email" 
                      value={waitlistEmail}
                      onChange={(e) => setWaitlistEmail(e.target.value)}
                      required 
                      placeholder="you@company.com"
                      style={{ borderColor: 'rgb(209 213 219)' }}
                      className="w-full px-4 py-3 border-2 rounded-xl focus:outline-none transition"
                      onFocus={(e) => e.target.style.borderColor = currentColors.primary}
                      onBlur={(e) => e.target.style.borderColor = 'rgb(209 213 219)'}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Company Name (Optional)</label>
                    <input 
                      type="text" 
                      value={waitlistCompany}
                      onChange={(e) => setWaitlistCompany(e.target.value)}
                      placeholder="Your Company"
                      style={{ borderColor: 'rgb(209 213 219)' }}
                      className="w-full px-4 py-3 border-2 rounded-xl focus:outline-none transition"
                      onFocus={(e) => e.target.style.borderColor = currentColors.primary}
                      onBlur={(e) => e.target.style.borderColor = 'rgb(209 213 219)'}
                    />
                  </div>
                  
                  <button 
                    type="submit" 
                    className={`w-full px-6 py-4 bg-gradient-to-r ${currentColors.buttonGradient} hover:${currentColors.hoverButtonGradient} text-white rounded-xl text-lg font-bold hover:shadow-xl transition transform hover:scale-105`}
                  >
                    Secure My Spot →
                  </button>
                  
                  <button 
                    type="button" 
                    onClick={closeWaitlistModal}
                    className="w-full px-6 py-3 text-gray-600 hover:text-gray-900 transition text-sm font-medium"
                  >
                    Maybe Later
                  </button>
                </form>
              </>
            ) : (
              <div className="text-center">
                <div className="text-6xl mb-4">✅</div>
                <h4 className="text-2xl font-bold text-gray-900 mb-2">You're on the list!</h4>
                <p className="text-gray-600 mb-6">We'll send you an invite as soon as we launch.</p>
                <button 
                  onClick={closeWaitlistModal}
                  className={`px-8 py-3 bg-gradient-to-r ${currentColors.buttonGradient} hover:${currentColors.hoverButtonGradient} text-white rounded-xl font-semibold hover:shadow-lg transition`}
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Signup Modal Component
const SignupModal = ({ isOpen, onClose }) => {
  const { currentColors } = useColorTheme();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: ''
  });

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      // Hash password before sending to server
      const hashedPassword = await hashPassword(formData.password);
      
      // Check for referral parameter in URL
      const urlParams = new URLSearchParams(window.location.search);
      const referral = urlParams.get('ref') || urlParams.get('referral') || null;
      
      const response = await axios.post(`${API}/auth/register`, {
        name: formData.name,
        email: formData.email,
        password: hashedPassword,
        password_hashed: true,
        referral: referral
      });
      const { user, session_token } = response.data;
      
      localStorage.setItem('session_token', session_token);
      window.location.reload();
      
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 w-full max-w-md relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-2xl"
        >
          ×
        </button>
        
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Create Account
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Start creating compelling content with AdElevate
          </p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 dark:bg-red-900 border border-red-400 text-red-700 dark:text-red-200 rounded-lg">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Full Name
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              style={{ borderColor: 'rgb(209 213 219)' }}
              className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent dark:bg-gray-800 dark:text-white"
              onFocus={(e) => e.target.style.borderColor = currentColors.primary}
              onBlur={(e) => e.target.style.borderColor = 'rgb(209 213 219)'}
              placeholder="Enter your full name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Email Address
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
              style={{ borderColor: 'rgb(209 213 219)' }}
              className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent dark:bg-gray-800 dark:text-white"
              onFocus={(e) => e.target.style.borderColor = currentColors.primary}
              onBlur={(e) => e.target.style.borderColor = 'rgb(209 213 219)'}
              placeholder="Enter your email"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Password
            </label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              required
              minLength="6"
              style={{ borderColor: 'rgb(209 213 219)' }}
              className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent dark:bg-gray-800 dark:text-white"
              onFocus={(e) => e.target.style.borderColor = currentColors.primary}
              onBlur={(e) => e.target.style.borderColor = 'rgb(209 213 219)'}
              placeholder="Create a password (min 6 characters)"
            />
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className={`w-full bg-gradient-to-r ${currentColors.buttonGradient} hover:${currentColors.hoverButtonGradient} disabled:from-gray-400 disabled:to-gray-500 text-white font-bold py-3 rounded-lg transition-all`}
          >
            {isLoading ? 'Creating Account...' : 'Create Account'}
          </button>
        </form>

        <div className="mt-6 text-center text-sm text-gray-600 dark:text-gray-400">
          By creating an account, you agree to our{' '}
          <a href="#" className={`${currentColors.textColor} hover:underline`}>
            Terms of Service
          </a>{' '}
          and{' '}
          <a href="#" className={`${currentColors.textColor} hover:underline`}>
            Privacy Policy
          </a>
        </div>
      </div>
    </div>
  );
};

// Admin Panel Components
const AdminLoginForm = ({ onLogin, error, isLoading }) => {
  const { currentColors, colorTheme } = useColorTheme();
  const [password, setPassword] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (password.trim()) {
      onLogin(password.trim());
    }
  };

  return (
    <div className={`min-h-screen bg-gradient-to-br ${colorTheme === 'purple' ? 'from-purple-50 via-white to-pink-50' : colorTheme === 'blue' ? 'from-blue-50 via-white to-sky-50' : 'from-green-50 via-white to-emerald-50'} dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center p-4`}>
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 w-full max-w-md shadow-2xl">
        <div className="text-center mb-8">
          <div className={`text-4xl font-bold bg-gradient-to-r ${currentColors.textGradient} bg-clip-text text-transparent mb-4`}>
            AdElevate
          </div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Admin Panel</h1>
          <p className="text-gray-600 dark:text-gray-400">
            Enter admin password to access user management
          </p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 dark:bg-red-900 border border-red-400 text-red-700 dark:text-red-200 rounded-lg">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Admin Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              style={{ borderColor: 'rgb(209 213 219)' }}
              className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent dark:bg-gray-800 dark:text-white"
              onFocus={(e) => e.target.style.borderColor = currentColors.primary}
              onBlur={(e) => e.target.style.borderColor = 'rgb(209 213 219)'}
              placeholder="Enter admin password"
              disabled={isLoading}
            />
          </div>

          <button
            type="submit"
            disabled={isLoading || !password.trim()}
            className={`w-full bg-gradient-to-r ${currentColors.buttonGradient} hover:${currentColors.hoverButtonGradient} disabled:from-gray-400 disabled:to-gray-500 text-white font-bold py-3 rounded-lg transition-all`}
          >
            {isLoading ? 'Logging in...' : 'Access Admin Panel'}
          </button>
        </form>

        <div className="mt-6 text-center">
          <button
            onClick={() => window.location.href = '/'}
            className={`${currentColors.textColor} ${currentColors.hoverTextColor} dark:text-yellow-400 dark:hover:text-yellow-300 font-medium`}
          >
            Back to Main App
          </button>
        </div>
      </div>
    </div>
  );
};

// Admin Campaign Management Component
const AdminCampaignManagement = ({ adminToken }) => {
  const [campaigns, setCampaigns] = useState([]);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState(null);
  
  // Inline editing state
  const [editingField, setEditingField] = useState(null); // {campaignId, adIndex, fieldName}
  const [editContent, setEditContent] = useState('');

  // Inline editing functions
  const startEditing = (campaignId, adIndex, fieldName, currentContent) => {
    setEditingField({ campaignId, adIndex, fieldName });
    setEditContent(currentContent);
  };

  const cancelEditing = () => {
    setEditingField(null);
    setEditContent('');
  };

  const saveEdit = async () => {
    if (!editingField || !selectedCampaign) return;
    
    try {
      // Update locally first for immediate feedback
      const updatedAds = [...selectedCampaign.generated_ads];
      updatedAds[editingField.adIndex][editingField.fieldName] = editContent;
      
      setSelectedCampaign({
        ...selectedCampaign,
        generated_ads: updatedAds
      });
      
      // Save to backend
      await axios.put(
        `${API}/campaigns/${selectedCampaign.id}/ads/${editingField.adIndex}`,
        {
          field: editingField.fieldName,
          value: editContent
        },
        {
          headers: { Authorization: `Bearer ${adminToken}` }
        }
      );
      
      cancelEditing();
    } catch (err) {
      console.error('Error saving edit:', err);
      alert('Failed to save changes');
    }
  };

  useEffect(() => {
    fetchCampaigns();
    fetchStats();
  }, [currentPage, searchTerm]);

  const fetchCampaigns = async () => {
    try {
      const response = await axios.get(`${API}/admin/campaigns`, {
        params: {
          search: searchTerm,
          limit: 20,
          skip: (currentPage - 1) * 20
        },
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setCampaigns(response.data.campaigns);
      setTotalPages(response.data.total_pages);
    } catch (err) {
      setError('Failed to load campaigns');
      console.error('Error fetching campaigns:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/campaigns/stats`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const fetchCampaignDetails = async (campaignId) => {
    try {
      const response = await axios.get(`${API}/admin/campaigns/${campaignId}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setSelectedCampaign(response.data);
      setShowDetailsModal(true);
    } catch (err) {
      setError('Failed to load campaign details');
      console.error('Error fetching campaign details:', err);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString() + ' ' + new Date(dateString).toLocaleTimeString();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                <span className="text-blue-600 dark:text-blue-400 text-xl">📊</span>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Campaigns</p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-white">{stats.total_campaigns}</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                <span className="text-green-600 dark:text-green-400 text-xl">👥</span>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Active Users</p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-white">{stats.active_users}</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
                <span className="text-purple-600 dark:text-purple-400 text-xl">🎯</span>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Ads</p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-white">{stats.total_ads_generated}</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <div className="p-2 bg-orange-100 dark:bg-orange-900 rounded-lg">
                <span className="text-orange-600 dark:text-orange-400 text-xl">📈</span>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Recent (30d)</p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-white">{stats.recent_campaigns_30_days}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search campaigns by product name or description..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            />
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-200 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Campaigns Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            Campaigns ({campaigns.length})
          </h3>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Campaign
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Ads
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Types
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {campaigns.map((campaign) => (
                <tr key={campaign.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {campaign.product_name}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs">
                        {campaign.product_description}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {campaign.user_name}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {campaign.user_email}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {formatDate(campaign.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                      {campaign.ad_count} ads
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-wrap gap-1">
                      {campaign.ad_types.map((type, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
                        >
                          {type}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => fetchCampaignDetails(campaign.id)}
                      className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 mr-4"
                    >
                      View Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-3 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700 dark:text-gray-300">
                Page {currentPage} of {totalPages}
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Campaign Details Modal */}
      {showDetailsModal && selectedCampaign && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white dark:bg-gray-800">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Campaign Details
                </h3>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                >
                  ✕
                </button>
              </div>
              
              <div className="space-y-4 max-h-96 overflow-y-auto">
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white">Product Information</h4>
                  <div className="mt-2 p-3 bg-gray-50 dark:bg-gray-700 rounded">
                    <p><strong>Name:</strong> {selectedCampaign.product?.product_name}</p>
                    <p><strong>Description:</strong> {selectedCampaign.product?.product_description}</p>
                    <p><strong>Price:</strong> {selectedCampaign.product?.price || 'N/A'}</p>
                    
                    {/* Campaign Strategy Details */}
                    {(selectedCampaign.product?.campaign_objective || selectedCampaign.product?.primary_platform || selectedCampaign.product?.pain_point) && (
                      <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Campaign Strategy:</p>
                        {selectedCampaign.product?.campaign_objective && (
                          <p><strong>Objective:</strong> <span className="capitalize">{selectedCampaign.product.campaign_objective.replace(/_/g, ' ')}</span></p>
                        )}
                        {selectedCampaign.product?.primary_platform && (
                          <p><strong>Platform:</strong> <span className="capitalize">{selectedCampaign.product.primary_platform}</span></p>
                        )}
                        {selectedCampaign.product?.cta_goal && (
                          <p><strong>CTA Goal:</strong> <span className="capitalize">{selectedCampaign.product.cta_goal.replace(/_/g, ' ')}</span></p>
                        )}
                        {selectedCampaign.product?.offer_incentive && (
                          <p><strong>Offer:</strong> {selectedCampaign.product.offer_incentive}</p>
                        )}
                        {selectedCampaign.product?.pain_point && (
                          <p><strong>Pain Point:</strong> {selectedCampaign.product.pain_point}</p>
                        )}
                        {selectedCampaign.product?.landing_page_url && (
                          <p><strong>Landing Page:</strong> <a href={selectedCampaign.product.landing_page_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline">{selectedCampaign.product.landing_page_url}</a></p>
                        )}
                      </div>
                    )}
                    
                    {/* References Used (compact row) */}
                    {(selectedCampaign.product?.product_image || selectedCampaign.product?.avatar_image_url || selectedCampaign.product?.brand_logo_url) && (
                      <div className="mt-3">
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">References Used:</p>
                        <CampaignReferencesPanel
                          product={selectedCampaign.product}
                          variant="compact"
                          testIdPrefix="inline-refs"
                        />
                      </div>
                    )}
                    
                    {/* Uploaded Image URL */}
                    {selectedCampaign.product?.uploaded_image_url && (
                      <div className="mt-3">
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Uploaded Image:</p>
                        <img 
                          src={selectedCampaign.product.uploaded_image_url} 
                          alt={selectedCampaign.product.product_name}
                          className="w-32 h-32 object-cover rounded-md border border-gray-300 dark:border-gray-600"
                          loading="lazy"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'block';
                          }}
                        />
                        <div className="hidden text-xs text-red-500">Uploaded image failed to load</div>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white">User Information</h4>
                  <div className="mt-2 p-3 bg-gray-50 dark:bg-gray-700 rounded">
                    <p><strong>Name:</strong> {selectedCampaign.user?.name}</p>
                    <p><strong>Email:</strong> {selectedCampaign.user?.email}</p>
                    <p><strong>User Since:</strong> {selectedCampaign.user?.created_at ? formatDate(selectedCampaign.user.created_at) : 'N/A'}</p>
                  </div>
                </div>

                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white">Generated Ads ({selectedCampaign.generated_ads?.length || 0})</h4>
                  <div className="mt-2 space-y-3 max-h-96 overflow-y-auto">
                    {selectedCampaign.generated_ads?.map((ad, index) => (
                      <div key={index} className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                        <div className="flex justify-between items-start mb-3">
                          <span className="font-medium text-gray-900 dark:text-white bg-blue-100 dark:bg-blue-900 px-2 py-1 rounded text-sm">
                            {ad.ad_type}
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">#{index + 1}</span>
                        </div>
                        
                        {/* Ad Title */}
                        {ad.title && (
                          <div className="flex justify-between items-start mb-3">
                            {editingField?.campaignId === selectedCampaign.id && editingField?.adIndex === index && editingField?.fieldName === 'title' ? (
                              <div className="flex-1 mr-2">
                                <textarea
                                  value={editContent}
                                  onChange={(e) => setEditContent(e.target.value)}
                                  className="w-full text-lg font-semibold text-gray-900 dark:text-white bg-white dark:bg-gray-800 border border-blue-300 rounded-md px-2 py-1 resize-none"
                                  rows="2"
                                  autoFocus
                                />
                                <div className="flex gap-2 mt-2">
                                  <button
                                    onClick={saveEdit}
                                    className={`text-xs ${colorTheme === 'purple' ? 'bg-purple-500 hover:bg-purple-600' : colorTheme === 'blue' ? 'bg-blue-500 hover:bg-blue-600' : 'bg-green-500 hover:bg-green-600'} text-white px-2 py-1 rounded transition-colors btn-ripple`}
                                  >
                                    ✅ Save
                                  </button>
                                  <button
                                    onClick={cancelEditing}
                                    className="text-xs bg-gray-500 hover:bg-gray-600 text-white px-2 py-1 rounded transition-colors"
                                  >
                                    ❌ Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <>
                                <h5 className="text-lg font-semibold text-gray-900 dark:text-white flex-1">{ad.title}</h5>
                                {/* Language Badge */}
                                {ad.language_name && (
                                  <span className="ml-2 px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                                    🌐 {ad.language_name}
                                  </span>
                                )}
                                <button
                                  onClick={() => {
                                    console.log('TITLE EDIT BUTTON CLICKED!');
                                    console.log('selectedCampaign.id:', selectedCampaign.id);
                                    console.log('index:', index);
                                    console.log('ad.title:', ad.title);
                                    startEditing(selectedCampaign.id, index, 'title', ad.title);
                                  }}
                                  className="text-xs bg-gray-200 hover:bg-gray-300 text-gray-700 px-2 py-1 rounded transition-colors ml-2"
                                >
                                  ✏️ Edit
                                </button>
                              </>
                            )}
                          </div>
                        )}
                        
                        {/* Ad Content */}
                        <div className="flex justify-between items-start mb-3">
                          {editingField?.campaignId === selectedCampaign.id && editingField?.adIndex === index && editingField?.fieldName === 'content' ? (
                            <div className="flex-1 mr-2">
                              <textarea
                                value={editContent}
                                onChange={(e) => setEditContent(e.target.value)}
                                className="w-full text-sm text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-800 border border-blue-300 rounded-md px-2 py-1 resize-none"
                                rows="3"
                                autoFocus
                              />
                              <div className="flex gap-2 mt-2">
                                <button
                                  onClick={saveEdit}
                                  className={`text-xs ${colorTheme === 'purple' ? 'bg-purple-500 hover:bg-purple-600' : colorTheme === 'blue' ? 'bg-blue-500 hover:bg-blue-600' : 'bg-green-500 hover:bg-green-600'} text-white px-2 py-1 rounded transition-colors btn-ripple`}
                                >
                                  ✅ Save
                                </button>
                                <button
                                  onClick={cancelEditing}
                                  className="text-xs bg-gray-500 hover:bg-gray-600 text-white px-2 py-1 rounded transition-colors"
                                >
                                  ❌ Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <p className="text-sm text-gray-600 dark:text-gray-300 flex-1">{ad.content}</p>
                              <button
                                onClick={() => startEditing(selectedCampaign.id, index, 'content', ad.content)}
                                className="text-xs bg-gray-200 hover:bg-gray-300 text-gray-700 px-2 py-1 rounded transition-colors ml-2"
                              >
                                ✏️ Edit
                              </button>
                            </>
                          )}
                        </div>
                        
                        {/* Media Content */}
                        <div className="space-y-3">
                          {/* Image */}
                          {ad.image_url && (
                            <div>
                              <h5 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Generated Image:</h5>
                              <div className="relative">
                                <img 
                                  src={ad.image_url} 
                                  alt={'Generated image for ' + ad.ad_type}
                                  className="w-full max-w-xs h-auto rounded-md border border-gray-300 dark:border-gray-600"
                                  crossOrigin="anonymous"
                                  loading="lazy"
                                  onError={(e) => {
                                    console.error('Image load error:', ad.image_url, e);
                                    // Don't hide immediately, give the image time to load
                                    setTimeout(() => {
                                      if (!e.target.complete || e.target.naturalHeight === 0) {
                                        e.target.style.display = 'none';
                                        if (e.target.nextSibling) e.target.nextSibling.style.display = 'block';
                                      }
                                    }, 2000);
                                  }}
                                />
                                <div className="hidden text-xs text-red-500">Image failed to load</div>
                              </div>
                            </div>
                          )}
                          
                          {/* Video */}
                          {ad.video_url && (
                            <div>
                              <h5 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Generated Video:</h5>
                              <div className="relative">
                                <video 
                                  controls 
                                  className="w-full max-w-xs h-auto rounded-md border border-gray-300 dark:border-gray-600"
                                  preload="metadata"
                                >
                                  <source src={ad.video_url} type="video/mp4" />
                                  Your browser does not support the video tag.
                                </video>
                                <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                  Video URL: {ad.video_url.substring(0, 50)}...
                                </div>
                              </div>
                            </div>
                          )}
                          
                          {/* Audio */}
                          {ad.audio_url && (
                            <div>
                              <h5 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Generated Audio:</h5>
                              <audio 
                                controls 
                                className="w-full max-w-xs"
                                preload="metadata"
                              >
                                <source src={ad.audio_url} type="audio/mpeg" />
                                Your browser does not support the audio tag.
                              </audio>
                            </div>
                          )}
                          
                          {/* Video Script */}
                          {ad.video_script && (
                            <div>
                              <h5 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Video Script:</h5>
                              <div className="text-xs text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 p-2 rounded border">
                                {ad.video_script}
                              </div>
                            </div>
                          )}
                          
                          {/* Media Status */}
                          {!ad.image_url && !ad.video_url && !ad.audio_url && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 italic">
                              📝 Text-only ad (no media generated)
                            </div>
                          )}

                          {/* Generation failure banner — surfaces Kie AI /
                              LLM provider errors (e.g. "Credits insufficient")
                              directly on the ad card so users don't have to
                              dig into server logs. */}
                          {ad.generation_failed && ad.generation_error && (
                            <div
                              data-testid={`ad-generation-error-${ad.ad_type}`}
                              className="mt-2 p-3 border border-amber-400 bg-amber-50 dark:bg-amber-950/30 rounded text-xs text-amber-900 dark:text-amber-200"
                            >
                              <div className="font-semibold mb-1">⚠️ Generation failed for this ad</div>
                              <div>{ad.generation_error}</div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-white">Sharing Stats</h4>
                    <div className="mt-2 p-3 bg-gray-50 dark:bg-gray-700 rounded">
                      <p><strong>Total Shares:</strong> {selectedCampaign.sharing_stats?.total_shares || 0}</p>
                      <p><strong>Channels:</strong> {selectedCampaign.sharing_stats?.share_channels?.join(', ') || 'None'}</p>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-white">Feedback Stats</h4>
                    <div className="mt-2 p-3 bg-gray-50 dark:bg-gray-700 rounded">
                      <p><strong>Total Feedback:</strong> {selectedCampaign.feedback_stats?.total_feedback || 0}</p>
                      <p><strong>Avg Rating:</strong> {selectedCampaign.feedback_stats?.average_rating?.toFixed(1) || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const AdminPanel = ({ onLogout, adminToken }) => {
  const { currentColors, colorTheme } = useColorTheme();
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true); // For initial load only
  const [gridLoading, setGridLoading] = useState(false); // For grid updates
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [isResetting, setIsResetting] = useState(false);
  
  // New states for enhanced admin features
  const [userStats, setUserStats] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total_count: 0 });
  const [currentPage, setCurrentPage] = useState(1);
  
  // Modal states with error handling
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  
  // Credit editing state
  const [creditEditingUser, setCreditEditingUser] = useState(null);
  const [creditAdjustment, setCreditAdjustment] = useState({ amount: 0, reason: '' });
  const [isAdjustingCredits, setIsAdjustingCredits] = useState(false);
  
  // Modal-specific error states
  const [modalError, setModalError] = useState(null);
  const [modalSuccess, setModalSuccess] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  
  // Refund confirmation modal state
  const [refundModal, setRefundModal] = useState({
    isOpen: false,
    user: null,
    isProcessing: false
  });

  useEffect(() => {
    fetchUsers();
    fetchUserStats();
    setInitialLoading(false); // Set initial loading to false after first load
  }, []);

  // Debounced search effect - no full screen refresh
  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      fetchUsers(1, false); // Don't show loading spinner for search
    }, 300); // Reduced to 300ms for faster response

    return () => clearTimeout(delayedSearch);
  }, [searchTerm]);

  const fetchUsers = async (page = 1, showLoading = true) => {
    try {
      if (showLoading) {
        setGridLoading(true); // Only show grid loading, not full screen
      }
      const response = await axios.get(`${API}/admin/users?search=${searchTerm}&page=${page}&limit=20`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      
      // Updated to work with new API response structure
      setUsers(response.data.users || []);
      setPagination(response.data.pagination || { page: 1, limit: 20, total_count: 0 });
      setCurrentPage(page);
    } catch (err) {
      setError('Failed to load users');
      console.error('Error fetching users:', err);
    } finally {
      if (showLoading) {
        setGridLoading(false);
      }
    }
  };
  
  const fetchUserStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/users/stats`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setUserStats(response.data);
    } catch (err) {
      console.error('Error fetching user stats:', err);
    }
  };

  // Handle refund for cancelled subscription
  const handleRefundConfirm = async () => {
    if (!refundModal.user) return;
    
    setRefundModal(prev => ({ ...prev, isProcessing: true }));
    try {
      const response = await axios.post(
        `${API}/subscriptions/admin/users/${refundModal.user.id}/refund`,
        {},
        { headers: { Authorization: `Bearer ${adminToken}` }}
      );
      setSuccess(`Refund issued: $${response.data.refund.amount} ${response.data.refund.currency}`);
      fetchUsers();
    } catch (err) {
      setError(`Refund failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setRefundModal({ isOpen: false, user: null, isProcessing: false });
    }
  };

  const openRefundModal = (user) => {
    setRefundModal({ isOpen: true, user, isProcessing: false });
  };

  const closeRefundModal = () => {
    setRefundModal({ isOpen: false, user: null, isProcessing: false });
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchUsers();
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const handlePasswordReset = async (email) => {
    if (!newPassword.trim()) {
      setError('Please enter a new password');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setIsResetting(true);
    setError(null);
    setSuccess(null);

    try {
      await axios.post(`${API}/admin/reset-password`, {
        email: email,
        new_password: newPassword
      }, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });

      setSuccess(`Password successfully reset for ${email}`);
      setSelectedUser(null);
      setNewPassword('');
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to reset password';
      setError(errorMessage);
    } finally {
      setIsResetting(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setModalLoading(true);
    setModalError(null);
    setModalSuccess(null);

    const userData = {
      name: document.getElementById('addUserName').value,
      email: document.getElementById('addUserEmail').value,
      password: document.getElementById('addUserPassword').value,
      is_admin: document.getElementById('addUserIsAdmin').checked
    };

    try {
      await axios.post(`${API}/admin/users`, userData, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      
      setModalSuccess(`User ${userData.name} created successfully`);
      
      // Close modal after short delay to show success message
      setTimeout(() => {
        setShowAddUserModal(false);
        setModalSuccess(null);
        setModalError(null);
        // Reset form
        document.getElementById('addUserName').value = '';
        document.getElementById('addUserEmail').value = '';
        document.getElementById('addUserPassword').value = '';
        document.getElementById('addUserIsAdmin').checked = false;
      }, 1500);
      
      // Refresh user list without full page reload
      fetchUsers(currentPage, false);
      
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to create user';
      setModalError(errorMessage);
      // Keep form data - don't clear on error
    } finally {
      setModalLoading(false);
    }
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    if (!editingUser) return;
    
    setModalLoading(true);
    setModalError(null);
    setModalSuccess(null);

    const userData = {
      name: document.getElementById('editUserName').value,
      email: document.getElementById('editUserEmail').value,
      is_admin: document.getElementById('editUserIsAdmin').checked
    };

    try {
      await axios.put(`${API}/admin/users/${editingUser.id}`, userData, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      
      setModalSuccess(`User ${userData.name} updated successfully`);
      
      // Close modal after short delay
      setTimeout(() => {
        setShowEditUserModal(false);
        setEditingUser(null);
        setModalSuccess(null);
        setModalError(null);
      }, 1500);
      
      // Refresh user list without full page reload
      fetchUsers(currentPage, false);
      
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to update user';
      setModalError(errorMessage);
    } finally {
      setModalLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!deletingUser) return;
    
    setModalLoading(true);
    setModalError(null);
    setModalSuccess(null);

    try {
      await axios.delete(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      
      setModalSuccess(`User ${deletingUser.name} deleted successfully`);
      
      // Close modal after short delay
      setTimeout(() => {
        setShowDeleteConfirm(false);
        setDeletingUser(null);
        setModalSuccess(null);
        setModalError(null);
      }, 1500);
      
      // Refresh user list without full page reload
      fetchUsers(currentPage, false);
      
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to delete user';
      setModalError(errorMessage);
    } finally {
      setModalLoading(false);
    }
  };

  const togglePowerUser = async (user) => {
    const newStatus = !user.is_power_user;
    const confirmMessage = newStatus 
      ? `Grant power user status to ${user.name}? They will be exempt from credit charges.`
      : `Remove power user status from ${user.name}? They will need credits for ad generation.`;
    
    if (!window.confirm(confirmMessage)) return;

    try {
      const response = await axios.post(`${API}/admin/credits/users/${user.id}/power-user`, 
        { is_power_user: newStatus },
        { headers: { Authorization: `Bearer ${adminToken}` }}
      );
      
      // Update user in the local state immediately for instant feedback
      setUsers(prevUsers => 
        prevUsers.map(u => 
          u.id === user.id 
            ? { ...u, is_power_user: newStatus }
            : u
        )
      );
      
      // Show success message
      const statusText = newStatus ? 'granted' : 'removed';
      alert(`Power user status ${statusText} for ${user.name}`);
      
      // Also refresh user list to get any other updates from server
      await fetchUsers(currentPage, false);
      
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to toggle power user status';
      alert(errorMessage);
      // Refresh to ensure state is in sync with server
      fetchUsers(currentPage, false);
    }
  };

  // Toggle user approval status
  const toggleUserApproval = async (user) => {
    const newStatus = !user.is_approved;
    const confirmMsg = newStatus 
      ? `Approve ${user.name} (${user.email})? They will be able to sign in via Google.`
      : `Suspend ${user.name} (${user.email})? They will no longer be able to sign in via Google.`;
    
    if (!window.confirm(confirmMsg)) return;
    
    try {
      await axios.post(`${API}/admin/credits/users/${user.id}/approval`, 
        { is_approved: newStatus },
        { headers: { Authorization: `Bearer ${adminToken}` }}
      );
      
      // Update local state immediately
      setUsers(prevUsers =>
        prevUsers.map(u =>
          u.id === user.id
            ? { ...u, is_approved: newStatus }
            : u
        )
      );
      
      const statusText = newStatus ? 'approved' : 'suspended';
      alert(`User ${user.name} has been ${statusText}`);
      
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to toggle user approval';
      alert(errorMessage);
      fetchUsers(currentPage, false);
    }
  };

  // Handle credit adjustment for a user
  const handleCreditAdjustment = async () => {
    if (!creditEditingUser || creditAdjustment.amount === 0) return;
    
    setIsAdjustingCredits(true);
    try {
      const response = await axios.post(
        `${API}/admin/credits/users/${creditEditingUser.id}/adjust-credits`,
        {
          amount: creditAdjustment.amount,
          reason: creditAdjustment.reason || 'Admin adjustment'
        },
        { headers: { Authorization: `Bearer ${adminToken}` }}
      );
      
      // Update user in the local state immediately
      setUsers(prevUsers =>
        prevUsers.map(u =>
          u.id === creditEditingUser.id
            ? { ...u, credits: response.data.new_balance }
            : u
        )
      );
      
      // Show success and close modal
      alert(`Credits updated for ${creditEditingUser.name}!\nPrevious: ${response.data.previous_balance}\nAdjustment: ${creditAdjustment.amount > 0 ? '+' : ''}${creditAdjustment.amount}\nNew Balance: ${response.data.new_balance}`);
      setCreditEditingUser(null);
      setCreditAdjustment({ amount: 0, reason: '' });
      
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to adjust credits');
    } finally {
      setIsAdjustingCredits(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="flex items-center justify-center pt-20">
          <div className={`animate-spin rounded-full h-12 w-12 border-b-2 ${currentColors.borderColor}`}></div>
          <span className="ml-3 text-lg text-gray-600 dark:text-gray-300">Loading admin panel...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Refund Confirmation Modal */}
      <ConfirmationModal
        isOpen={refundModal.isOpen}
        onClose={closeRefundModal}
        onConfirm={handleRefundConfirm}
        title="Issue Refund?"
        message={
          refundModal.user ? (
            <div>
              <p className="mb-2">You are about to issue a <strong>full refund</strong> for:</p>
              <div className="bg-white/50 dark:bg-gray-700/50 rounded-lg p-3 text-left">
                <p><strong>User:</strong> {refundModal.user.name}</p>
                <p><strong>Email:</strong> {refundModal.user.email}</p>
                <p><strong>Plan:</strong> {refundModal.user.subscription_plan}</p>
              </div>
              <p className="mt-3 text-sm text-orange-600 dark:text-orange-400">
                ⚠️ This will issue a real refund via Stripe.
              </p>
            </div>
          ) : ''
        }
        type="refund"
        icon="💰"
        confirmText="Issue Refund"
        cancelText="Cancel"
        isProcessing={refundModal.isProcessing}
      />
      
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <div className={`text-3xl font-bold bg-gradient-to-r ${currentColors.textGradient} bg-clip-text text-transparent`}>
                AdElevate Admin
              </div>
              <span className="ml-2 text-xs bg-red-200 dark:bg-red-900 text-red-800 dark:text-red-200 px-2 py-1 rounded">
                Admin Panel
              </span>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => window.location.href = '/'}
                className="text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 font-medium"
              >
                Main App
              </button>
              <button
                onClick={onLogout}
                className="bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('users')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'users'
                  ? `border-yellow-500 ${currentColors.textColor} dark:text-yellow-400`
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
              }`}
            >
              👥 User Management
            </button>
            <button
              onClick={() => setActiveTab('campaigns')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'campaigns'
                  ? `border-yellow-500 ${currentColors.textColor} dark:text-yellow-400`
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
              }`}
            >
              📊 Campaign Management
            </button>
            <button
              onClick={() => setActiveTab('settings')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'settings'
                  ? `border-yellow-500 ${currentColors.textColor} dark:text-yellow-400`
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
              }`}
            >
              ⚙️ System Settings
            </button>
            <button
              onClick={() => setActiveTab('waitlist')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'waitlist'
                  ? `border-yellow-500 ${currentColors.textColor} dark:text-yellow-400`
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
              }`}
            >
              👥 Waitlist
            </button>
            <button
              onClick={() => setActiveTab('credits')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'credits'
                  ? `border-yellow-500 ${currentColors.textColor} dark:text-yellow-400`
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
              }`}
            >
              💳 Credits
            </button>
            <button
              onClick={() => setActiveTab('subscriptions')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'subscriptions'
                  ? `border-yellow-500 ${currentColors.textColor} dark:text-yellow-400`
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
              }`}
            >
              🔄 Subscriptions
            </button>
            <button
              onClick={() => setActiveTab('email-campaigns')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'email-campaigns'
                  ? `border-pink-500 ${currentColors.textColor} dark:text-pink-400`
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
              }`}
            >
              📧 Email Campaigns
            </button>
            <button
              onClick={() => setActiveTab('appearance')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'appearance'
                  ? `border-yellow-500 ${currentColors.textColor} dark:text-yellow-400`
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
              }`}
            >
              🎨 Appearance
            </button>
          </nav>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tab Content */}
        {activeTab === 'credits' ? (
          <CreditManagement adminToken={adminToken} />
        ) : activeTab === 'subscriptions' ? (
          <SubscriptionPlanManager adminToken={adminToken} />
        ) : activeTab === 'email-campaigns' ? (
          <EmailCampaignManager adminToken={adminToken} />
        ) : activeTab === 'appearance' ? (
          <ThemeToggle adminToken={adminToken} />
        ) : activeTab === 'waitlist' ? (
          <WaitlistManagement adminToken={adminToken} />
        ) : activeTab === 'campaigns' ? (
          <div>
            <div className="mb-8">
              <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">Campaign Management</h1>
              <p className="text-xl text-gray-600 dark:text-gray-300">
                View and manage all campaigns across users
              </p>
            </div>
            <AdminCampaignManagement adminToken={adminToken} />
          </div>
        ) : activeTab === 'settings' ? (
          <div>
            <div className="mb-8">
              <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">System Settings</h1>
              <p className="text-xl text-gray-600 dark:text-gray-300">
                Configure system-wide settings and publishing requirements
              </p>
            </div>
            
            {/* Background Generation Settings */}
            <div className="mb-8">
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg p-6 border-2 border-blue-300 dark:border-blue-700 mb-6">
                <div className="flex items-center mb-4">
                  <span className="text-3xl mr-3">🚀</span>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                      Background Campaign Generation
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      System-wide settings for campaign generation behavior
                    </p>
                  </div>
                </div>
              </div>
              <BackgroundGenerationSettings sessionToken={adminToken} />
            </div>

            {/* Poster Builder Visibility */}
            <div className="mb-8">
              <div className="bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 rounded-lg p-6 border-2 border-emerald-300 dark:border-emerald-700 mb-6">
                <div className="flex items-center mb-4">
                  <span className="text-3xl mr-3">🎨</span>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                      Poster Builder Visibility
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Show or hide the Event Poster Builder tab in the left sidebar for all non-admin users.
                      Admins always see the tab regardless of this setting.
                    </p>
                  </div>
                </div>
              </div>
              <PosterBuilderVisibilitySettings sessionToken={adminToken} />
            </div>
            
            {/* Publish Validation Settings */}
            <div className="mb-8">
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-lg p-6 border-2 border-purple-300 dark:border-purple-700 mb-6">
                <div className="flex items-center mb-4">
                  <span className="text-3xl mr-3">📝</span>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                      Publish Validation Settings
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Configure publishing requirements and validation rules
                    </p>
                  </div>
                </div>
              </div>
              <PublishValidationSettings sessionToken={adminToken} adminMode={true} />
            </div>
          </div>
        ) : (
          <div>
            <div className="mb-8">
              <div className="flex justify-between items-center">
                <div>
                  <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">User Management</h1>
                  <p className="text-xl text-gray-600 dark:text-gray-300">
                    Manage user accounts and reset passwords directly
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowAddUserModal(true);
                    // Clear modal states when opening
                    setModalError(null);
                    setModalSuccess(null);
                  }}
                  className={`bg-gradient-to-r ${currentColors.buttonGradient} hover:${currentColors.hoverButtonGradient} text-white font-bold py-3 px-6 rounded-lg transition-all transform hover:scale-105 shadow-lg`}
                >
                  ➕ Add New User
                </button>
              </div>
            </div>

            {/* User Statistics Dashboard */}
            {userStats && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
                  <div className="p-5">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 bg-blue-500 rounded-md flex items-center justify-center">
                          <span className="text-white font-bold">👥</span>
                        </div>
                      </div>
                      <div className="ml-5 w-0 flex-1">
                        <dl>
                          <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                            Total Users
                          </dt>
                          <dd className="text-lg font-medium text-gray-900 dark:text-white">
                            {userStats.total_users.toLocaleString()}
                          </dd>
                        </dl>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
                  <div className="p-5">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className={`w-8 h-8 ${colorTheme === 'purple' ? 'bg-purple-500' : colorTheme === 'blue' ? 'bg-blue-500' : 'bg-green-500'} rounded-md flex items-center justify-center`}>
                          <span className="text-white font-bold">📈</span>
                        </div>
                      </div>
                      <div className="ml-5 w-0 flex-1">
                        <dl>
                          <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                            Active Today
                          </dt>
                          <dd className="text-lg font-medium text-gray-900 dark:text-white">
                            {userStats.active_users_today.toLocaleString()}
                          </dd>
                        </dl>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
                  <div className="p-5">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 bg-purple-500 rounded-md flex items-center justify-center">
                          <span className="text-white font-bold">🎯</span>
                        </div>
                      </div>
                      <div className="ml-5 w-0 flex-1">
                        <dl>
                          <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                            Total Campaigns
                          </dt>
                          <dd className="text-lg font-medium text-gray-900 dark:text-white">
                            {userStats.total_campaigns.toLocaleString()}
                          </dd>
                        </dl>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
                  <div className="p-5">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className={`w-8 h-8 ${colorTheme === 'purple' ? 'bg-purple-500' : colorTheme === 'blue' ? 'bg-blue-500' : 'bg-green-500'} rounded-md flex items-center justify-center`}>
                          <span className="text-white font-bold">🏆</span>
                        </div>
                      </div>
                      <div className="ml-5 w-0 flex-1">
                        <dl>
                          <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                            Achievements
                          </dt>
                          <dd className="text-lg font-medium text-gray-900 dark:text-white">
                            {userStats.total_achievements_unlocked.toLocaleString()}
                          </dd>
                        </dl>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

        {error && (
          <div className="mb-6 p-4 bg-red-100 dark:bg-red-900 border border-red-400 text-red-700 dark:text-red-200 rounded-lg">
            {error}
          </div>
        )}

        {success && (
          <div className="mb-6 p-4 bg-green-100 dark:bg-green-900 border border-green-400 text-green-700 dark:text-green-200 rounded-lg">
            {success}
          </div>
        )}

        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <input
              type="text"
              placeholder="Search users by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white dark:bg-gray-800 shadow-xl rounded-lg overflow-hidden relative">
          {/* Grid Loading Overlay */}
          {gridLoading && (
            <div className="absolute inset-0 bg-white dark:bg-gray-800 bg-opacity-75 flex items-center justify-center z-10">
              <div className="flex items-center">
                <div className={`animate-spin rounded-full h-6 w-6 border-b-2 ${currentColors.borderColor}`}></div>
                <span className="ml-3 text-sm text-gray-600 dark:text-gray-300">Updating users...</span>
              </div>
            </div>
          )}
          
          <div className="px-6 py-4 bg-yellow-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
              Users ({users.length})
            </h2>
          </div>
          
          {users.length === 0 ? (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">
              No users found
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Campaigns
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Credits
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Subscription
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Achievements
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                  {users.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className={`w-8 h-8 bg-gradient-to-r ${currentColors.buttonGradient} rounded-full flex items-center justify-center text-white font-bold text-sm`}>
                            {user.name.charAt(0).toUpperCase()}
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {user.name}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-300">
                        {user.email}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-2">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            user.is_admin 
                              ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' 
                              : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                          }`}>
                            {user.is_admin ? 'Admin' : 'User'}
                          </span>
                          {user.is_power_user && (
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                              ⚡ Power User
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm">
                        {user.is_approved !== false ? (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                            ✓ Approved
                          </span>
                        ) : (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                            ⏳ Pending Approval
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-300">
                        <span className="font-medium">{user.campaign_count || 0}</span>
                        <span className="text-gray-500 dark:text-gray-400 ml-1">campaigns</span>
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <div className="flex items-center gap-2">
                          {user.is_power_user ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                              ⚡ Unlimited
                            </span>
                          ) : (
                            <span className={`font-bold ${
                              user.credits > 50 ? 'text-green-600 dark:text-green-400' :
                              user.credits > 10 ? 'text-yellow-600 dark:text-yellow-400' :
                              'text-red-600 dark:text-red-400'
                            }`}>
                              {user.credits || 0}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm">
                        {user.subscription_plan ? (
                          <div className="flex flex-col gap-1">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              user.subscription_status === 'active' 
                                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                : user.subscription_status === 'past_due'
                                ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                                : user.subscription_status === 'cancelled'
                                ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                            }`}>
                              {user.subscription_status === 'cancelled' ? '❌' : '🔄'} {user.subscription_plan}
                            </span>
                            {user.subscription_status === 'active' && user.subscription_credits_remaining !== null && (
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {user.subscription_credits_remaining} cr remaining
                              </span>
                            )}
                            {user.subscription_status === 'cancelled' && (
                              user.subscription_refunded ? (
                                <span className="text-xs text-green-600 dark:text-green-400">
                                  ✓ Refunded
                                </span>
                              ) : user.subscription_stripe_id && (
                                <button
                                  onClick={() => openRefundModal(user)}
                                  className="text-xs px-2 py-1 bg-orange-100 hover:bg-orange-200 text-orange-700 dark:bg-orange-900/30 dark:hover:bg-orange-900/50 dark:text-orange-300 rounded transition-colors"
                                >
                                  💰 Refund
                                </button>
                              )
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400 dark:text-gray-500 text-xs">
                            No subscription
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-300">
                        <span className="font-medium">{user.achievement_count || 0}</span>
                        <span className="text-gray-500 dark:text-gray-400 ml-1">achievements</span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                        {formatDate(user.created_at)}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium">
                        <div className="flex gap-2 flex-wrap">
                          <button
                            onClick={() => {
                              setEditingUser(user);
                              setShowEditUserModal(true);
                              // Clear modal states when opening
                              setModalError(null);
                              setModalSuccess(null);
                            }}
                            className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
                            title="Edit User"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => setSelectedUser(user)}
                            className={`${currentColors.textColor} ${currentColors.hoverTextColor} dark:text-yellow-400 dark:hover:text-yellow-300 font-medium`}
                            title="Reset Password"
                          >
                            Reset
                          </button>
                          <button
                            onClick={() => {
                              setCreditEditingUser(user);
                              setCreditAdjustment({ amount: 0, reason: '' });
                            }}
                            className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300 font-medium"
                            title="Manage Credits"
                          >
                            💰Edit
                          </button>
                          <button
                            onClick={() => togglePowerUser(user)}
                            className={`${user.is_power_user ? 'text-orange-600 hover:text-orange-900 dark:text-orange-400 dark:hover:text-orange-300' : 'text-yellow-600 hover:text-yellow-900 dark:text-yellow-400 dark:hover:text-yellow-300'} font-medium`}
                            title={user.is_power_user ? "Remove Power User" : "Make Power User"}
                          >
                            {user.is_power_user ? '⚡Off' : '⚡On'}
                          </button>
                          <button
                            onClick={() => toggleUserApproval(user)}
                            className={`${user.is_approved !== false ? 'text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300' : 'text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300'} font-medium`}
                            title={user.is_approved !== false ? "Suspend User" : "Approve User"}
                          >
                            {user.is_approved !== false ? '🚫' : '✓Approve'}
                          </button>
                          <button
                            onClick={() => {
                              setDeletingUser(user);
                              setShowDeleteConfirm(true);
                              // Clear modal states when opening
                              setModalError(null);
                              setModalSuccess(null);
                            }}
                            className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 font-medium"
                            title="Delete User"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Credit Edit Modal */}
        {creditEditingUser && (
          <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="credit-modal-title" role="dialog" aria-modal="true">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setCreditEditingUser(null)}></div>
              
              <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
              
              <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-green-100 dark:bg-green-900 sm:mx-0 sm:h-10 sm:w-10">
                      <span className="text-xl">💰</span>
                    </div>
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                      <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white" id="credit-modal-title">
                        Manage Credits for {creditEditingUser.name}
                      </h3>
                      <div className="mt-4 space-y-4">
                        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                          <p className="text-sm text-gray-500 dark:text-gray-400">Email: {creditEditingUser.email}</p>
                          <p className="text-lg font-bold mt-2 text-gray-900 dark:text-white">
                            Current Balance: {creditEditingUser.is_power_user ? (
                              <span className="text-yellow-600 dark:text-yellow-400">⚡ Unlimited (Power User)</span>
                            ) : (
                              <span className={`${
                                creditEditingUser.credits > 50 ? 'text-green-600 dark:text-green-400' :
                                creditEditingUser.credits > 10 ? 'text-yellow-600 dark:text-yellow-400' :
                                'text-red-600 dark:text-red-400'
                              }`}>{creditEditingUser.credits || 0} credits</span>
                            )}
                          </p>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Credit Adjustment
                          </label>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setCreditAdjustment(prev => ({ ...prev, amount: prev.amount - 10 }))}
                              className="px-3 py-2 bg-red-100 hover:bg-red-200 dark:bg-red-900 dark:hover:bg-red-800 text-red-700 dark:text-red-300 rounded-lg font-medium"
                            >
                              -10
                            </button>
                            <button
                              onClick={() => setCreditAdjustment(prev => ({ ...prev, amount: prev.amount - 1 }))}
                              className="px-3 py-2 bg-red-100 hover:bg-red-200 dark:bg-red-900 dark:hover:bg-red-800 text-red-700 dark:text-red-300 rounded-lg font-medium"
                            >
                              -1
                            </button>
                            <input
                              type="number"
                              value={creditAdjustment.amount}
                              onChange={(e) => setCreditAdjustment(prev => ({ ...prev, amount: parseInt(e.target.value) || 0 }))}
                              className="w-24 text-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white font-bold text-lg"
                            />
                            <button
                              onClick={() => setCreditAdjustment(prev => ({ ...prev, amount: prev.amount + 1 }))}
                              className="px-3 py-2 bg-green-100 hover:bg-green-200 dark:bg-green-900 dark:hover:bg-green-800 text-green-700 dark:text-green-300 rounded-lg font-medium"
                            >
                              +1
                            </button>
                            <button
                              onClick={() => setCreditAdjustment(prev => ({ ...prev, amount: prev.amount + 10 }))}
                              className="px-3 py-2 bg-green-100 hover:bg-green-200 dark:bg-green-900 dark:hover:bg-green-800 text-green-700 dark:text-green-300 rounded-lg font-medium"
                            >
                              +10
                            </button>
                            <button
                              onClick={() => setCreditAdjustment(prev => ({ ...prev, amount: prev.amount + 100 }))}
                              className="px-3 py-2 bg-green-100 hover:bg-green-200 dark:bg-green-900 dark:hover:bg-green-800 text-green-700 dark:text-green-300 rounded-lg font-medium"
                            >
                              +100
                            </button>
                          </div>
                          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                            New Balance: <span className="font-bold">{Math.max(0, (creditEditingUser.credits || 0) + creditAdjustment.amount)}</span> credits
                          </p>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Reason (optional)
                          </label>
                          <input
                            type="text"
                            value={creditAdjustment.reason}
                            onChange={(e) => setCreditAdjustment(prev => ({ ...prev, reason: e.target.value }))}
                            placeholder="e.g., Bonus credits, Refund, Manual adjustment..."
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    onClick={handleCreditAdjustment}
                    disabled={isAdjustingCredits || creditAdjustment.amount === 0}
                    className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 ${
                      creditAdjustment.amount > 0 
                        ? 'bg-green-600 hover:bg-green-700' 
                        : creditAdjustment.amount < 0 
                          ? 'bg-red-600 hover:bg-red-700' 
                          : 'bg-gray-400'
                    } text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm disabled:bg-gray-400 disabled:cursor-not-allowed`}
                  >
                    {isAdjustingCredits ? 'Updating...' : creditAdjustment.amount > 0 ? `Add ${creditAdjustment.amount} Credits` : creditAdjustment.amount < 0 ? `Remove ${Math.abs(creditAdjustment.amount)} Credits` : 'Enter Amount'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setCreditEditingUser(null);
                      setCreditAdjustment({ amount: 0, reason: '' });
                    }}
                    disabled={isAdjustingCredits}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Password Reset Modal */}
        {selectedUser && (
          <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setSelectedUser(null)}></div>
              
              <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
              
              <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                      <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white" id="modal-title">
                        Reset Password for {selectedUser.name}
                      </h3>
                      <div className="mt-4">
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                          Email: {selectedUser.email}
                        </p>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            New Password
                          </label>
                          <input
                            type="password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            placeholder="Enter new password (min 8 characters)"
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                            disabled={isResetting}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    onClick={() => handlePasswordReset(selectedUser.email)}
                    disabled={isResetting || !newPassword.trim()}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:bg-gray-400"
                  >
                    {isResetting ? 'Resetting...' : 'Reset Password'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedUser(null);
                      setNewPassword('');
                      setError(null);
                    }}
                    disabled={isResetting}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add User Modal */}
        {showAddUserModal && (
          <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => {
                setShowAddUserModal(false);
                setModalError(null);
                setModalSuccess(null);
              }}></div>
              <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white mb-4">
                    Add New User
                  </h3>
                  
                  {/* Modal Error/Success Messages */}
                  {modalError && (
                    <div className="mb-4 p-3 bg-red-100 dark:bg-red-900 border border-red-400 dark:border-red-600 text-red-700 dark:text-red-200 rounded">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <p className="text-sm">{modalError}</p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {modalSuccess && (
                    <div className="mb-4 p-3 bg-green-100 dark:bg-green-900 border border-green-400 dark:border-green-600 text-green-700 dark:text-green-200 rounded">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <svg className="h-5 w-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <p className="text-sm">{modalSuccess}</p>
                        </div>
                      </div>
                    </div>
                  )}
                  <form onSubmit={(e) => handleAddUser(e)}>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Full Name
                        </label>
                        <input
                          type="text"
                          id="addUserName"
                          required
                          style={{ borderColor: 'rgb(209 213 219)' }}
                          className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:border-transparent dark:bg-gray-700 dark:text-white"
                          onFocus={(e) => e.target.style.borderColor = currentColors.primary}
                          onBlur={(e) => e.target.style.borderColor = 'rgb(209 213 219)'}
                          placeholder="Enter full name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Email Address
                        </label>
                        <input
                          type="email"
                          id="addUserEmail"
                          required
                          style={{ borderColor: 'rgb(209 213 219)' }}
                          className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:border-transparent dark:bg-gray-700 dark:text-white"
                          onFocus={(e) => e.target.style.borderColor = currentColors.primary}
                          onBlur={(e) => e.target.style.borderColor = 'rgb(209 213 219)'}
                          placeholder="Enter email address"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Password
                        </label>
                        <input
                          type="password"
                          id="addUserPassword"
                          required
                          style={{ borderColor: 'rgb(209 213 219)' }}
                          className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:border-transparent dark:bg-gray-700 dark:text-white"
                          onFocus={(e) => e.target.style.borderColor = currentColors.primary}
                          onBlur={(e) => e.target.style.borderColor = 'rgb(209 213 219)'}
                          placeholder="Enter password (min 8 characters)"
                        />
                      </div>
                      <div>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            id="addUserIsAdmin"
                            style={{ accentColor: currentColors.primary }}
                            className="mr-2 h-4 w-4 border-gray-300 rounded"
                          />
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Grant Admin Privileges
                          </span>
                        </label>
                      </div>
                    </div>
                    <div className="mt-6 flex justify-end gap-3">
                      <button
                        type="button"
                        onClick={() => {
                          setShowAddUserModal(false);
                          setModalError(null);
                          setModalSuccess(null);
                        }}
                        className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={modalLoading}
                        className={`px-4 py-2 bg-gradient-to-r ${currentColors.buttonGradient} text-white rounded-md hover:${currentColors.hoverButtonGradient} disabled:opacity-50`}
                      >
                        {modalLoading ? 'Creating...' : 'Create User'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit User Modal */}
        {showEditUserModal && editingUser && (
          <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => {
                setShowEditUserModal(false);
                setEditingUser(null);
                setModalError(null);
                setModalSuccess(null);
              }}></div>
              <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white mb-4">
                    Edit User: {editingUser.name}
                  </h3>
                  
                  {/* Modal Error/Success Messages */}
                  {modalError && (
                    <div className="mb-4 p-3 bg-red-100 dark:bg-red-900 border border-red-400 dark:border-red-600 text-red-700 dark:text-red-200 rounded">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <p className="text-sm">{modalError}</p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {modalSuccess && (
                    <div className="mb-4 p-3 bg-green-100 dark:bg-green-900 border border-green-400 dark:border-green-600 text-green-700 dark:text-green-200 rounded">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <svg className="h-5 w-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <p className="text-sm">{modalSuccess}</p>
                        </div>
                      </div>
                    </div>
                  )}
                  <form onSubmit={(e) => handleEditUser(e)}>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Full Name
                        </label>
                        <input
                          type="text"
                          id="editUserName"
                          defaultValue={editingUser.name}
                          required
                          style={{ borderColor: 'rgb(209 213 219)' }}
                          className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:border-transparent dark:bg-gray-700 dark:text-white"
                          onFocus={(e) => e.target.style.borderColor = currentColors.primary}
                          onBlur={(e) => e.target.style.borderColor = 'rgb(209 213 219)'}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Email Address
                        </label>
                        <input
                          type="email"
                          id="editUserEmail"
                          defaultValue={editingUser.email}
                          required
                          style={{ borderColor: 'rgb(209 213 219)' }}
                          className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:border-transparent dark:bg-gray-700 dark:text-white"
                          onFocus={(e) => e.target.style.borderColor = currentColors.primary}
                          onBlur={(e) => e.target.style.borderColor = 'rgb(209 213 219)'}
                        />
                      </div>
                      <div>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            id="editUserIsAdmin"
                            defaultChecked={editingUser.is_admin}
                            style={{ accentColor: currentColors.primary }}
                            className="mr-2 h-4 w-4 border-gray-300 rounded"
                          />
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Admin Privileges
                          </span>
                        </label>
                      </div>
                    </div>
                    <div className="mt-6 flex justify-end gap-3">
                      <button
                        type="button"
                        onClick={() => {
                          setShowEditUserModal(false);
                          setEditingUser(null);
                          setModalError(null);
                          setModalSuccess(null);
                        }}
                        className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={modalLoading}
                        className={`px-4 py-2 bg-gradient-to-r ${currentColors.buttonGradient} text-white rounded-md hover:${currentColors.hoverButtonGradient} disabled:opacity-50`}
                      >
                        {modalLoading ? 'Updating...' : 'Update User'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && deletingUser && (
          <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => {
                setShowDeleteConfirm(false);
                setDeletingUser(null);
                setModalError(null);
                setModalSuccess(null);
              }}></div>
              <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="flex items-start">
                    <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900 sm:mx-0 sm:h-10 sm:w-10">
                      <svg className="h-6 w-6 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                    </div>
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                      <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                        Delete User Account
                      </h3>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          Are you sure you want to delete <strong>{deletingUser.name}</strong> ({deletingUser.email})?
                          This action cannot be undone and will permanently delete:
                        </p>
                        <ul className="mt-2 text-sm text-gray-500 dark:text-gray-400 list-disc list-inside">
                          <li>User account and profile</li>
                          <li>All campaigns ({deletingUser.campaign_count || 0})</li>
                          <li>All achievements ({deletingUser.achievement_count || 0})</li>
                          <li>All activity history</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    onClick={() => handleDeleteUser(deletingUser.id)}
                    disabled={modalLoading}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                  >
                    {modalLoading ? 'Deleting...' : 'Delete User'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowDeleteConfirm(false);
                      setDeletingUser(null);
                      setModalError(null);
                      setModalSuccess(null);
                    }}
                    disabled={modalLoading}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
          </div>
        )}
      </div>
    </div>
  );
};

// Public Campaign View Component
const StarRating = ({ rating, setRating, readOnly = false, size = 'medium' }) => {
  const sizeClasses = {
    small: 'w-4 h-4',
    medium: 'w-6 h-6',
    large: 'w-8 h-8'
  };

  return (
    <div className="flex items-center space-x-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={readOnly}
          onClick={() => !readOnly && setRating(star)}
          className={`${sizeClasses[size]} ${
            readOnly ? 'cursor-default' : 'cursor-pointer hover:scale-110 transition-transform'
          } ${
            star <= rating
              ? 'text-yellow-500'
              : 'text-gray-300 dark:text-gray-600'
          }`}
        >
          <svg
            fill="currentColor"
            viewBox="0 0 20 20"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        </button>
      ))}
    </div>
  );
};

const FeedbackForm = ({ shareToken, onFeedbackSubmitted }) => {
  const { colorTheme } = useColorTheme();
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [authorName, setAuthorName] = useState('');
  const [authorEmail, setAuthorEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!comment.trim()) {
      setError('Please provide your feedback comment');
      return;
    }

    setIsSubmitting(true);
    setError('');
    setSuccess('');

    try {
      // Use the simple request structure
      await axios.post(`${API}/share/${shareToken}/feedback`, {
        rating,
        comment: comment.trim(),
        author_name: authorName.trim() || null,
        author_email: authorEmail.trim() || null
      });

      setSuccess('Thank you for your feedback!');
      setComment('');
      setAuthorName('');
      setAuthorEmail('');
      setRating(5);
      
      // Notify parent component to refresh feedback
      if (onFeedbackSubmitted) {
        onFeedbackSubmitted();
      }
    } catch (err) {
      // Properly handle different types of errors
      let errorMessage = 'Failed to submit feedback. Please try again.';
      
      if (err.response?.data) {
        const errorData = err.response.data;
        
        if (typeof errorData.detail === 'string') {
          // Simple string error
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail)) {
          // FastAPI validation errors - extract readable message
          const validationErrors = errorData.detail.map(error => {
            if (error.msg) {
              return error.msg;
            }
            return `${error.loc?.join(' → ') || 'Field'}: ${error.type || 'Invalid'}`;
          });
          errorMessage = validationErrors.join(', ');
        } else if (errorData.message) {
          // Alternative error message field
          errorMessage = errorData.message;
        }
      }
      
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6">
      <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
        Share Your Feedback
      </h3>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Rating */}
        <div>
          <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
            Your Rating
          </label>
          <StarRating rating={rating} setRating={setRating} size="large" />
        </div>

        {/* Comment */}
        <div>
          <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
            Your Comments <span className="text-red-500">*</span>
          </label>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Share your thoughts about this marketing campaign..."
            rows={4}
            className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 ${colorTheme === 'purple' ? 'focus:ring-purple-500' : colorTheme === 'blue' ? 'focus:ring-blue-500' : 'focus:ring-green-500'} focus:border-transparent dark:bg-gray-700 dark:text-white`}
            required
          />
        </div>

        {/* Optional contact info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
              Your Name (Optional)
            </label>
            <input
              type="text"
              value={authorName}
              onChange={(e) => setAuthorName(e.target.value)}
              placeholder="John Doe"
              className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 ${colorTheme === 'purple' ? 'focus:ring-purple-500' : colorTheme === 'blue' ? 'focus:ring-blue-500' : 'focus:ring-green-500'} focus:border-transparent dark:bg-gray-700 dark:text-white`}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
              Your Email (Optional)
            </label>
            <input
              type="email"
              value={authorEmail}
              onChange={(e) => setAuthorEmail(e.target.value)}
              placeholder="john@example.com"
              className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 ${colorTheme === 'purple' ? 'focus:ring-purple-500' : colorTheme === 'blue' ? 'focus:ring-blue-500' : 'focus:ring-green-500'} focus:border-transparent dark:bg-gray-700 dark:text-white`}
            />
          </div>
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
            <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
          </div>
        )}

        {success && (
          <div className={`rounded-lg p-3 border ${colorTheme === 'purple' ? 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800' : colorTheme === 'blue' ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800' : 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'}`}>
            <p className={`text-sm ${colorTheme === 'purple' ? 'text-purple-600 dark:text-purple-400' : colorTheme === 'blue' ? 'text-blue-600 dark:text-blue-400' : 'text-green-600 dark:text-green-400'}`}>{success}</p>
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isSubmitting}
          className={`w-full py-3 px-4 rounded-lg font-medium transition-colors btn-ripple ${
            isSubmitting
              ? 'bg-gray-400 cursor-not-allowed'
              : `bg-gradient-to-r ${colorTheme === 'purple' ? 'from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700' : colorTheme === 'blue' ? 'from-blue-600 to-sky-500 hover:from-blue-700 hover:to-sky-600' : 'from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700'}`
          } text-white`}
        >
          {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
        </button>
      </form>
    </div>
  );
};

const FeedbackComment = ({ feedback, shareToken, onReplySubmitted }) => {
  const { colorTheme } = useColorTheme();
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [replyComment, setReplyComment] = useState('');
  const [replyAuthorName, setReplyAuthorName] = useState('');
  const [isSubmittingReply, setIsSubmittingReply] = useState(false);
  const [replyError, setReplyError] = useState('');

  const handleReplySubmit = async (e) => {
    e.preventDefault();
    if (!replyComment.trim()) {
      setReplyError('Please provide a reply comment');
      return;
    }

    setIsSubmittingReply(true);
    setReplyError('');

    try {
      // Use the simple request structure
      await axios.post(`${API}/share/${shareToken}/feedback/${feedback.id}/reply`, {
        comment: replyComment.trim(),
        author_name: replyAuthorName.trim() || null,
        author_email: null
      });

      setReplyComment('');
      setReplyAuthorName('');
      setShowReplyForm(false);
      
      if (onReplySubmitted) {
        onReplySubmitted();
      }
    } catch (err) {
      // Properly handle different types of errors
      let errorMessage = 'Failed to submit reply. Please try again.';
      
      if (err.response?.data) {
        const errorData = err.response.data;
        
        if (typeof errorData.detail === 'string') {
          // Simple string error
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail)) {
          // FastAPI validation errors - extract readable message
          const validationErrors = errorData.detail.map(error => {
            if (error.msg) {
              return error.msg;
            }
            return `${error.loc?.join(' → ') || 'Field'}: ${error.type || 'Invalid'}`;
          });
          errorMessage = validationErrors.join(', ');
        } else if (errorData.message) {
          // Alternative error message field
          errorMessage = errorData.message;
        }
      }
      
      setReplyError(errorMessage);
    } finally {
      setIsSubmittingReply(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' at ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
      {/* Main comment */}
      <div className="flex items-start space-x-3">
        <div className="flex-shrink-0">
          <div className={`w-8 h-8 bg-gradient-to-r ${colorTheme === 'purple' ? 'from-purple-400 to-pink-500' : colorTheme === 'blue' ? 'from-blue-400 to-sky-500' : 'from-yellow-400 to-orange-500'} rounded-full flex items-center justify-center`}>
            <span className="text-white font-bold text-sm">
              {(feedback.author_name || 'Anonymous').charAt(0).toUpperCase()}
            </span>
          </div>
        </div>
        <div className="flex-grow">
          <div className="flex items-center space-x-2 mb-1">
            <span className="font-semibold text-gray-900 dark:text-white">
              {feedback.author_name || 'Anonymous'}
            </span>
            <StarRating rating={feedback.rating} readOnly size="small" />
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {formatDate(feedback.created_at)}
            </span>
          </div>
          <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
            {feedback.comment}
          </p>
          <button
            onClick={() => setShowReplyForm(!showReplyForm)}
            className={`text-sm font-medium mt-2 ${colorTheme === 'purple' ? 'text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300' : colorTheme === 'blue' ? 'text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300' : 'text-green-600 dark:text-yellow-400 hover:text-yellow-700 dark:hover:text-yellow-300'}`}
          >
            Reply
          </button>
        </div>
      </div>

      {/* Replies */}
      {feedback && feedback.replies && feedback.replies.length > 0 && (
        <div className="mt-4 ml-11 space-y-3">
          {feedback.replies.map((reply) => (
            <div key={reply.id} className="bg-white dark:bg-gray-600 rounded-lg p-3">
              <div className="flex items-start space-x-2">
                <div className="flex-shrink-0">
                  <div className={`w-6 h-6 bg-gradient-to-r ${colorTheme === 'purple' ? 'from-purple-300 to-pink-400' : colorTheme === 'blue' ? 'from-blue-300 to-sky-400' : 'from-gray-400 to-gray-500'} rounded-full flex items-center justify-center`}>
                    <span className="text-white font-bold text-xs">
                      {(reply.author_name || 'Anonymous').charAt(0).toUpperCase()}
                    </span>
                  </div>
                </div>
                <div className="flex-grow">
                  <div className="flex items-center space-x-2 mb-1">
                    <span className="font-medium text-gray-900 dark:text-white text-sm">
                      {reply.author_name || 'Anonymous'}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {formatDate(reply.created_at)}
                    </span>
                  </div>
                  <p className="text-gray-700 dark:text-gray-300 text-sm whitespace-pre-wrap">
                    {reply.comment}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Reply Form */}
      {showReplyForm && (
        <div className="mt-4 ml-11">
          <form onSubmit={handleReplySubmit} className="space-y-3">
            <div>
              <input
                type="text"
                value={replyAuthorName}
                onChange={(e) => setReplyAuthorName(e.target.value)}
                placeholder="Your name (optional)"
                className={`w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 ${colorTheme === 'purple' ? 'focus:ring-purple-500' : colorTheme === 'blue' ? 'focus:ring-blue-500' : 'focus:ring-green-500'} focus:border-transparent dark:bg-gray-700 dark:text-white`}
              />
            </div>
            <div>
              <textarea
                value={replyComment}
                onChange={(e) => setReplyComment(e.target.value)}
                placeholder="Write your reply..."
                rows={3}
                className={`w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 ${colorTheme === 'purple' ? 'focus:ring-purple-500' : colorTheme === 'blue' ? 'focus:ring-blue-500' : 'focus:ring-green-500'} focus:border-transparent dark:bg-gray-700 dark:text-white`}
                required
              />
            </div>
            
            {replyError && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-2">
                <p className="text-red-600 dark:text-red-400 text-xs">{replyError}</p>
              </div>
            )}

            <div className="flex space-x-2">
              <button
                type="submit"
                disabled={isSubmittingReply}
                className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors btn-ripple ${
                  isSubmittingReply
                    ? 'bg-gray-400 cursor-not-allowed'
                    : `bg-gradient-to-r ${colorTheme === 'purple' ? 'from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700' : colorTheme === 'blue' ? 'from-blue-600 to-sky-500 hover:from-blue-700 hover:to-sky-600' : 'from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700'}`
                } text-white`}
              >
                {isSubmittingReply ? 'Submitting...' : 'Post Reply'}
              </button>
              <button
                type="button"
                onClick={() => setShowReplyForm(false)}
                className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

const FeedbackSection = ({ shareToken }) => {
  const { colorTheme } = useColorTheme();
  const [feedback, setFeedback] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchFeedback = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${API}/share/${shareToken}/feedback`);
      
      // Handle different response formats
      if (Array.isArray(response.data)) {
        // Direct array response from new backend endpoint
        setFeedback(response.data);
        setStatistics({ 
          total_feedback: response.data.length,
          average_rating: response.data.length > 0 ? 
            response.data.reduce((sum, f) => sum + f.rating, 0) / response.data.length : 0
        });
      } else if (response.data.feedback) {
        // Old structure with feedback field
        setFeedback(response.data.feedback || []);
        setStatistics(response.data.statistics || { total_feedback: 0, average_rating: 0 });
      } else {
        // Fallback - empty feedback
        setFeedback([]);
        setStatistics({ total_feedback: 0, average_rating: 0 });
      }
      
      setError('');
    } catch (err) {
      setError('Failed to load feedback');
      setFeedback([]);
      setStatistics({ total_feedback: 0, average_rating: 0 });
      console.error('Error fetching feedback:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (shareToken) {
      fetchFeedback();
    }
  }, [shareToken]);

  const handleFeedbackSubmitted = () => {
    fetchFeedback(); // Refresh feedback after submission
  };

  if (isLoading) {
    return (
      <div className="text-center py-8">
        <div className={`animate-spin rounded-full h-8 w-8 border-b-2 ${colorTheme === 'purple' ? 'border-purple-600' : colorTheme === 'blue' ? 'border-blue-600' : 'border-green-600'} mx-auto mb-4`}></div>
        <p className="text-gray-600 dark:text-gray-400">Loading feedback...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Feedback Statistics */}
      {statistics && statistics.total_feedback > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
            Feedback Overview
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center">
              <div className={`text-3xl font-bold ${colorTheme === 'purple' ? 'text-purple-600' : colorTheme === 'blue' ? 'text-blue-600' : 'text-green-600'}`}>
                {statistics.total_feedback}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Total Reviews
              </div>
            </div>
            
            <div className="text-center">
              <div className="flex items-center justify-center space-x-1">
                <span className={`text-3xl font-bold ${colorTheme === 'purple' ? 'text-purple-600' : colorTheme === 'blue' ? 'text-blue-600' : 'text-green-600'}`}>
                  {statistics.average_rating}
                </span>
                <StarRating rating={Math.round(statistics.average_rating)} readOnly size="small" />
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Average Rating
              </div>
            </div>
            
            <div>
              <div className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                Rating Distribution
              </div>
              <div className="space-y-1">
                {[5, 4, 3, 2, 1].map((star) => (
                  <div key={star} className="flex items-center space-x-2">
                    <span className="text-xs w-8">{star} ⭐</span>
                    <div className="flex-grow bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div
                        className={`${colorTheme === 'purple' ? 'bg-purple-500' : colorTheme === 'blue' ? 'bg-blue-500' : 'bg-green-500'} h-2 rounded-full`}
                        style={{
                          width: statistics.total_feedback > 0 
                            ? `${(statistics.rating_distribution[star] / statistics.total_feedback) * 100}%`
                            : '0%'
                        }}
                      ></div>
                    </div>
                    <span className="text-xs w-8">{statistics.rating_distribution[star]}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Feedback Form */}
      <FeedbackForm shareToken={shareToken} onFeedbackSubmitted={handleFeedbackSubmitted} />

      {/* Feedback Comments */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6">
        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
          Community Feedback {statistics && `(${statistics.total_feedback})`}
        </h3>
        
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 mb-4">
            <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
          </div>
        )}
        
        {feedback && feedback.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-6xl mb-4">💬</div>
            <p className="text-gray-600 dark:text-gray-400">
              No feedback yet. Be the first to share your thoughts!
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {feedback.map((comment) => (
              <FeedbackComment
                key={comment.id}
                feedback={comment}
                shareToken={shareToken}
                onReplySubmitted={handleFeedbackSubmitted}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const PublicCampaignView = ({ shareToken }) => {
  const { colorTheme } = useColorTheme();
  const [campaign, setCampaign] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  
  useEffect(() => {
    if (shareToken) {
      fetchPublicCampaign();
    }
  }, [shareToken]);

  const fetchPublicCampaign = async () => {
    try {
      const response = await axios.get(`${API}/share/${shareToken}`);
      setCampaign(response.data);
    } catch (err) {
      setError('Campaign not found or is not publicly shared');
      console.error('Error fetching public campaign:', err);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className={`min-h-screen bg-gradient-to-br ${colorTheme === 'purple' ? 'from-purple-50 via-white to-pink-50' : colorTheme === 'blue' ? 'from-blue-50 via-white to-sky-50' : 'from-green-50 via-white to-emerald-50'} dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center`}>
        <div className="text-center">
          <div className={`animate-spin rounded-full h-12 w-12 border-b-2 ${colorTheme === 'purple' ? 'border-purple-600' : colorTheme === 'blue' ? 'border-blue-600' : 'border-green-600'} mx-auto mb-4`}></div>
          <p className="text-gray-600 dark:text-gray-400">Loading shared campaign...</p>
        </div>
      </div>
    );
  }

  if (error || !campaign) {
    return (
      <div className={`min-h-screen bg-gradient-to-br ${colorTheme === 'purple' ? 'from-purple-50 via-white to-pink-50' : colorTheme === 'blue' ? 'from-blue-50 via-white to-sky-50' : 'from-green-50 via-white to-emerald-50'} dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center`}>
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 w-full max-w-md shadow-2xl text-center">
          <div className="text-6xl mb-4">🔒</div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">Campaign Not Found</h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {error || "This campaign doesn't exist or is not publicly shared."}
          </p>
          <button
            onClick={() => window.location.href = '/'}
            className={`bg-gradient-to-r ${colorTheme === 'purple' ? 'from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700' : colorTheme === 'blue' ? 'from-blue-600 to-sky-500 hover:from-blue-700 hover:to-sky-600' : 'from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700'} text-white font-bold py-3 px-6 rounded-lg transition-all btn-ripple`}
          >
            Visit AdElevate
          </button>
        </div>
      </div>
    );
  }

  // Dynamic function to get ad type labels based on video model
  const getAdTypeLabel = (adType, videoModel = null, imageModel = null) => {
    const baseLabels = {
      facebook_ad: 'Facebook Ad',
      instagram_ad: 'Instagram Post',
      linkedin_ad: 'LinkedIn Ad',
      tiktok_ad: 'TikTok Content',
      telegram_ad: 'Telegram Channel',
      social_media: 'Social Media',
      google_ads: 'Google Ads',
      product_description: 'Product Description',
      email_marketing: 'Email Marketing'
    };

    // Dynamic image title based on model for image_promo
    if (adType === 'image_promo') {
      return 'AI Generated Image';
    }

    // Dynamic video title based on model for both video_promo and video_ad
    if (adType === 'video_promo' || adType === 'video_ad') {
      return 'AI Generated Video';
    }

    return baseLabels[adType] || adType;
  };

  const adTypeLabels = {
    facebook_ad: 'Facebook Ad',
    instagram_ad: 'Instagram Post',
    linkedin_ad: 'LinkedIn Ad',
    tiktok_ad: 'TikTok Content',
    telegram_ad: 'Telegram Channel',
    social_media: 'Social Media',
    google_ads: 'Google Ads',
    product_description: 'Product Description',
    email_marketing: 'Email Marketing',
    video_promo: 'Video Promotion',
    image_promo: 'Image Promotion'
  };

  // Helper function to remove model names from ad titles
  const cleanAdTitle = (title) => {
    if (!title) return title;
    // Remove model names from titles - hide technical details from users
    return title
      .replace(/\(Freepik Nano Banana\)/gi, '')
      .replace(/\(Kie AI Nano Banana\)/gi, '')
      .replace(/\(Nebius AI\)/gi, '')
      .replace(/\(Google Veo 3\)/gi, '')
      .replace(/\(Freepik MiniMax Hailuo\)/gi, '')
      .replace(/\(Replicate \+ ElevenLabs\)/gi, '')
      .replace(/\(GPT-4o\)/gi, '')
      .replace(/\(Ultra Image \(GPT-4o\)\)/gi, '')
      .replace(/\(Ultra Image\)/gi, '')
      .replace(/\(AI Image\)/gi, '')
      .replace(/\(Ultra HD Image\)/gi, '')
      .replace(/\(Premium Image\)/gi, '')
      .replace(/\(Quick Image\)/gi, '')
      .replace(/\(Standard Image\)/gi, '')
      .replace(/\(Best Value\)/gi, '')
      .replace(/\(Gemini 3\.0 Pro Ultra HD\)/gi, '')
      .replace(/GPT-4o/gi, '')
      .replace(/Nano Banana/gi, '')
      .replace(/\s+-\s+/g, ' - ')  // Clean up extra spaces around dashes
      .replace(/\s{2,}/g, ' ')      // Replace multiple spaces with single space
      .trim();
  };

  // Helper functions to get user-friendly model labels
  const getVideoModelLabel = (modelId) => {
    const labels = {
      'freepik': 'Quick Video ✨',
      'replicate': 'Standard Video ⚡',
      'veo3_fast': 'Fast Video 🚀',
      'veo3': 'Premium Video 🎥',
      'kie-ai': 'Premium Video 🎥',  // Legacy support
      'ultra_video': 'Ultra Video 🎬',
      'gemini_omni_video': 'Ultra Pro 🪐',
      // 🆕 Kling 3.0 / Seedance 2.0
      'kling-3.0-pro': 'Ultra Cinematic (Kling 3.0 Pro)',
      'kling-3.0-std': 'Ultra Cinematic Lite (Kling 3.0)',
      'seedance-2.0-pro': 'Realism Pro (Seedance 2.0)',
      'seedance-2.0-fast': 'Realism Lite (Seedance 2.0 Fast)',
      // 🎭 HeyGen Photo Avatar
      'heygen-photo-avatar': 'Talking Head (HeyGen Photo Avatar) 🎭'
    };
    return labels[modelId] || 'AI Powered';
  };

  const getImageModelLabel = (modelId) => {
    const labels = {
      'nebius': 'Quick Image 💨',
      'kie-ai': 'Standard Image 🎨',
      'freepik': 'Best Value 🎨',
      'nano-banana-pro': 'Premium Image',
      'nano-banana-2': 'Premium Image 2 (Flash 4K)',
      'gpt4o-image': 'Ultra Image'
    };
    return labels[modelId] || 'AI Powered';
  };

  const adTypeIcons = {
    facebook_ad: <BrandIcon templateId="facebook_ad" size={24} />,
    instagram_ad: <BrandIcon templateId="instagram_ad" size={24} />,
    linkedin_ad: <BrandIcon templateId="linkedin_ad" size={24} />,
    tiktok_ad: <BrandIcon templateId="tiktok_ad" size={24} />,
    telegram_ad: <BrandIcon templateId="telegram_ad" size={24} />,
    social_media: <BrandIcon templateId="social_media" size={24} />,
    google_ads: <BrandIcon templateId="google_ads" size={24} />,
    product_description: <BrandIcon templateId="product_description" size={24} />,
    email_marketing: <BrandIcon templateId="email_marketing" size={24} />,
    video_promo: <BrandIcon templateId="video_promo" size={24} />,
    video_ad: <BrandIcon templateId="video_promo" size={24} />,
    image_promo: <BrandIcon templateId="image_promo" size={24} />
  };

  return (
    <div className={`min-h-screen bg-gradient-to-br ${colorTheme === 'purple' ? 'from-purple-50 via-white to-pink-50' : colorTheme === 'blue' ? 'from-blue-50 via-white to-sky-50' : 'from-green-50 via-white to-emerald-50'} dark:from-gray-900 dark:via-gray-800 dark:to-gray-900`}>
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <div className={`text-3xl font-bold bg-gradient-to-r ${colorTheme === 'purple' ? 'from-purple-600 to-pink-600' : colorTheme === 'blue' ? 'from-blue-600 to-sky-500' : 'from-green-600 to-emerald-600'} bg-clip-text text-transparent`}>
                AdElevate
              </div>
              <span className="ml-2 text-xs bg-blue-200 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded">
                Shared Campaign
              </span>
            </div>
            <button
              onClick={() => window.location.href = '/'}
              className="text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 font-medium"
            >
              Create Your Own →
            </button>
          </div>
        </div>
      </div>

      {/* Campaign Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 mb-8">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              {campaign.product.product_name}
            </h1>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              Shared publicly • Created {new Date(campaign.created_at).toLocaleDateString()}
            </span>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {/* References Used */}
            {(campaign.product.product_image || campaign.product.avatar_image_url || campaign.product.brand_logo_url) && (
              <div>
                <CampaignReferencesPanel product={campaign.product} testIdPrefix="share-view-refs" />
              </div>
            )}
            
            {/* Product Details */}
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Product Details</h3>
              <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                <p><span className="font-medium">Product Description & Benefits:</span> {campaign.product.product_description || 'Not specified'}</p>
                {/* Only show Key Benefits if different from Description (avoid duplication) */}
                {campaign.product.key_benefits && 
                 campaign.product.key_benefits !== campaign.product.product_description && (
                  <p><span className="font-medium">Key Benefits:</span> {campaign.product.key_benefits}</p>
                )}
                <p><span className="font-medium">Price:</span> {campaign.product.price || 'Not specified'}</p>
                <p><span className="font-medium">Category:</span> {campaign.product.category || 'Not specified'}</p>
                <p><span className="font-medium">Brand Voice:</span> <span className="capitalize">{campaign.product.brand_voice || 'Not specified'}</span></p>
                <p><span className="font-medium">Industry/Business Type:</span> {campaign.product.industry_business_type || 'Not specified'}</p>
                <p><span className="font-medium">Budget Range:</span> {campaign.product.budget_range || 'Not specified'}</p>
                <p><span className="font-medium">Interests & Behaviors:</span> {campaign.product.interests_behaviors || 'Not specified'}</p>
              </div>
            </div>
            
            {/* Campaign Strategy */}
            {(campaign.product.campaign_objective || campaign.product.primary_platform || campaign.product.pain_point || campaign.product.offer_incentive) && (
              <div data-testid="public-campaign-strategy">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Campaign Strategy</h3>
                <div className="flex flex-wrap gap-2">
                  {campaign.product.campaign_objective && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded-md text-xs font-medium">
                      Objective: <span className="capitalize">{campaign.product.campaign_objective.replace(/_/g, ' ')}</span>
                    </span>
                  )}
                  {campaign.product.primary_platform && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 rounded-md text-xs font-medium">
                      Platform: <span className="capitalize">{campaign.product.primary_platform}</span>
                    </span>
                  )}
                  {campaign.product.cta_goal && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 rounded-md text-xs font-medium">
                      CTA: <span className="capitalize">{campaign.product.cta_goal.replace(/_/g, ' ')}</span>
                    </span>
                  )}
                  {campaign.product.offer_incentive && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 rounded-md text-xs font-medium">
                      Offer: {campaign.product.offer_incentive}
                    </span>
                  )}
                  {campaign.product.pain_point && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 rounded-md text-xs font-medium">
                      Pain Point: {campaign.product.pain_point}
                    </span>
                  )}
                  {campaign.product.landing_page_url && (
                    <a href={campaign.product.landing_page_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-2.5 py-1 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-md text-xs font-medium hover:underline">
                      Landing Page
                    </a>
                  )}
                </div>
              </div>
            )}
            
            {/* Demographics & Targeting */}
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Demographics & Targeting</h3>
              <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                <p><span className="font-medium">Age Range:</span> {campaign.product.age_range_min && campaign.product.age_range_max ? `${campaign.product.age_range_min}-${campaign.product.age_range_max}` : 'Not specified'}</p>
                <p><span className="font-medium">Gender:</span> {campaign.product.gender || 'Not specified'}</p>
                <p><span className="font-medium">Geography/Location:</span> {campaign.product.geography || 'Not specified'}</p>
                <p><span className="font-medium">Languages:</span> {(() => {
                  const langs = campaign.product.video_languages?.length > 0 ? campaign.product.video_languages : (campaign.product.languages?.length > 0 ? campaign.product.languages : ['en']);
                  const langMap = { 'en': 'English', 'hi': 'Hindi', 'ta': 'Tamil', 'te': 'Telugu', 'kn': 'Kannada', 'ml': 'Malayalam', 'mr': 'Marathi', 'bn': 'Bengali', 'gu': 'Gujarati', 'pa': 'Punjabi', 'es': 'Spanish', 'fr': 'French', 'de': 'German', 'zh': 'Chinese', 'ja': 'Japanese', 'ar': 'Arabic', 'pt': 'Portuguese' };
                  return langs.map(lang => langMap[lang] || lang.toUpperCase()).join(', ');
                })()}</p>
              </div>
            </div>
            
            {/* Personas */}
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Target Personas</h3>
              <div className="flex flex-wrap gap-1">
                {(() => {
                  // Extract unique personas from generated ads
                  const personas = [...new Set(campaign.generated_ads
                    .filter(ad => ad.target_persona)
                    .map(ad => ad.target_persona))];
                  
                  if (personas.length > 0) {
                    return personas.map((persona, index) => (
                      <span key={index} className="bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 text-xs px-2 py-1 rounded">
                        👤 {persona}
                      </span>
                    ));
                  } else {
                    return (
                      <span className="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-xs px-2 py-1 rounded">
                        General audience
                      </span>
                    );
                  }
                })()}
              </div>
            </div>
            
            {/* Generation Settings */}
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Generation Settings</h3>
              <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                {/* Only show image model if campaign has image-based ad types */}
                {(campaign.ad_types?.includes('image_promo') || campaign.ad_types?.includes('telegram_ad') || 
                  campaign.generated_ads?.some(ad => ad.ad_type === 'image_promo' || ad.ad_type === 'telegram_ad')) && 
                  campaign.product.image_model && (
                  <p>
                    <span className="font-medium">Image Generation:</span> {getImageModelLabel(campaign.product.image_model)}
                  </p>
                )}
                {/* Only show video specs if campaign has video ad types */}
                {(campaign.ad_types?.includes('video_promo') || campaign.ad_types?.includes('video_ad') || 
                  campaign.generated_ads?.some(ad => ad.ad_type === 'video_promo' || ad.ad_type === 'video_ad')) && (
                  <>
                    {campaign.product.video_model && (
                      <p>
                        <span className="font-medium">Video Generation:</span> {getVideoModelLabel(campaign.product.video_model)}
                      </p>
                    )}
                    {campaign.product.video_duration && (
                      <p><span className="font-medium">Duration:</span> {campaign.product.video_duration}s</p>
                    )}
                    {campaign.product.video_resolution && (
                      <p><span className="font-medium">Resolution:</span> {campaign.product.video_resolution}</p>
                    )}
                    {campaign.product.video_aspect_ratio && (
                      <p><span className="font-medium">Aspect Ratio:</span> {campaign.product.video_aspect_ratio}</p>
                    )}
                  </>
                )}
              </div>
            </div>
            
            {/* Campaign Stats */}
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Campaign Overview</h3>
              <div className="flex flex-wrap gap-1">
                {(() => {
                  const totalAds = campaign.generated_ads.length;
                  const adTypeCounts = campaign.generated_ads.reduce((acc, ad) => {
                    const baseType = ad.ad_type.replace('_', ' ');
                    acc[baseType] = (acc[baseType] || 0) + 1;
                    return acc;
                  }, {});
                  const uniqueAdTypes = Object.keys(adTypeCounts).length;
                  const hasPersonas = Object.values(adTypeCounts).some(count => count > 1);
                  const personaCount = hasPersonas ? Math.max(...Object.values(adTypeCounts)) : 1;
                  
                  return (
                    <>
                      <span className="bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 text-xs px-2 py-1 rounded">
                        {totalAds} pieces
                      </span>
                      <span className="bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 text-xs px-2 py-1 rounded">
                        {uniqueAdTypes} formats
                      </span>
                      {hasPersonas && (
                        <span className="bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 text-xs px-2 py-1 rounded">
                          {personaCount} personas
                        </span>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>

        {/* Generated Ads */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {campaign.generated_ads.map((ad, index) => (
            <div key={index} className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 relative">
              {/* Persistent favorite badge on the corner when marked */}
              {ad.is_favorite && (
                <div className="absolute top-3 left-3 w-7 h-7 rounded-full bg-yellow-400 text-white flex items-center justify-center shadow-lg text-sm" title="Favorited ad">
                  ★
                </div>
              )}
              <div className="flex items-center mb-4">
                <span className="mr-3"><BrandIcon templateId={ad.ad_type} size={24} /></span>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 dark:text-white">
                    {getAdTypeLabel(ad.ad_type, campaign?.product?.video_model, campaign?.product?.image_model)}
                  </h3>
                </div>
                {/* Language Badge */}
                {ad.language_name && (
                  <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 mr-2">
                    🌐 {ad.language_name}
                  </span>
                )}
                {/* Favorite toggle */}
                <button
                  type="button"
                  onClick={async () => {
                    const wantFav = !ad.is_favorite;
                    // Optimistic — flip in place; rollback on failure.
                    setCampaign(prev => prev ? {
                      ...prev,
                      generated_ads: prev.generated_ads.map((a, i) => i === index ? { ...a, is_favorite: wantFav } : a),
                    } : prev);
                    try {
                      const token = localStorage.getItem('session_token');
                      await axios.post(`${API}/campaigns/${campaign.id}/ads/${index}/favorite`,
                        { is_favorite: wantFav },
                        { headers: { 'Authorization': `Bearer ${token}` } });
                    } catch (err) {
                      setCampaign(prev => prev ? {
                        ...prev,
                        generated_ads: prev.generated_ads.map((a, i) => i === index ? { ...a, is_favorite: !wantFav } : a),
                      } : prev);
                    }
                  }}
                  title={ad.is_favorite ? 'Remove from favorites' : 'Add to favorites'}
                  data-testid={`ad-favorite-${index}`}
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm transition-colors ${
                    ad.is_favorite
                      ? 'bg-yellow-400 hover:bg-yellow-500 text-white shadow'
                      : 'bg-slate-100 dark:bg-slate-700 hover:bg-yellow-100 dark:hover:bg-yellow-900/40 text-slate-500 hover:text-yellow-600 dark:hover:text-yellow-400'
                  }`}
                >
                  {ad.is_favorite ? '★' : '☆'}
                </button>
              </div>
              
              {ad.title && (
                <div className="mb-3">
                  <h4 className="font-medium text-gray-900 dark:text-white text-sm mb-1">Title:</h4>
                  <p className="text-gray-700 dark:text-gray-300 text-sm">{cleanAdTitle(ad.title)}</p>
                </div>
              )}
              
              <div className="mb-3">
                <h4 className="font-medium text-gray-900 dark:text-white text-sm mb-1">Content:</h4>
                <p className="text-gray-700 dark:text-gray-300 text-sm whitespace-pre-wrap">{ad.content}</p>
              </div>
              
              {ad.call_to_action && (
                <div className="mb-3">
                  <h4 className="font-medium text-gray-900 dark:text-white text-sm mb-1">Call to Action:</h4>
                  <p className="text-gray-700 dark:text-gray-300 text-sm font-medium">{ad.call_to_action}</p>
                </div>
              )}
              
              {ad.video_url && (
                <div className="mb-3">
                  <h4 className="font-medium text-gray-900 dark:text-white text-sm mb-1">Video:</h4>
                  <video className="w-full rounded-lg" controls>
                    <source src={ad.video_url} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </div>
              )}
              
              {ad.image_url && (
                <div className="mb-3">
                  <h4 className="font-medium text-gray-900 dark:text-white text-sm mb-1">Image:</h4>
                  <img 
                    src={ad.image_url} 
                    alt={'Generated promotional image' + (ad.target_persona ? ' for ' + ad.target_persona : '')} 
                    className="w-full rounded-lg"
                    crossOrigin="anonymous"
                  />
                </div>
              )}
              
              {ad.audio_url && (
                <div className="mb-3">
                  <h4 className="font-medium text-gray-900 dark:text-white text-sm mb-1">Audio:</h4>
                  <audio className="w-full" controls>
                    <source src={ad.audio_url} type="audio/mpeg" />
                    Your browser does not support the audio tag.
                  </audio>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Call to Action */}
        <div className={`mt-12 bg-gradient-to-r ${colorTheme === 'purple' ? 'from-purple-600 to-pink-600' : colorTheme === 'blue' ? 'from-blue-600 to-sky-500' : 'from-green-600 to-emerald-600'} rounded-lg p-8 text-center`}>
          <h2 className="text-3xl font-bold text-white mb-4">Create Your Own AI Marketing Campaign</h2>
          <p className={`${colorTheme === 'purple' ? 'text-purple-100' : colorTheme === 'blue' ? 'text-blue-100' : 'text-green-100'} text-xl mb-6`}>
            Generate compelling marketing content for your products with AdElevate's AI-powered platform
          </p>
          <button
            onClick={() => window.location.href = '/'}
            className={`bg-white font-bold py-3 px-8 rounded-lg text-lg transition-colors btn-ripple ${colorTheme === 'purple' ? 'text-purple-600 hover:bg-purple-50' : colorTheme === 'blue' ? 'text-blue-600 hover:bg-blue-50' : 'text-green-600 hover:bg-green-50'}`}
          >
            Get Started Free
          </button>
        </div>

        {/* Feedback System */}
        <div className="mt-12">
          <FeedbackSection shareToken={shareToken} />
        </div>
      </div>
    </div>
  );
};

// Password Reset Page Component
const PasswordResetPage = () => {
  const { currentColors, colorTheme } = useColorTheme(); // Get theme from context
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [tokenValid, setTokenValid] = useState(null);
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get('token');

  useEffect(() => {
    if (token) {
      validateToken();
    }
  }, [token]);

  const validateToken = async () => {
    try {
      const response = await axios.get(`${API}/auth/validate-reset-token/${token}`);
      if (response.data.valid) {
        setTokenValid(true);
        setEmail(response.data.email);
      } else {
        setTokenValid(false);
        setError(response.data.message);
      }
    } catch (err) {
      setTokenValid(false);
      setError('Invalid or expired reset token');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      setIsLoading(false);
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      setIsLoading(false);
      return;
    }

    try {
      await axios.post(`${API}/auth/reset-password`, {
        token: token,
        new_password: newPassword
      });
      setSuccess('Password reset successful! You can now sign in with your new password.');
      setTimeout(() => {
        window.location.href = '/';
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setIsLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 w-full max-w-md shadow-2xl">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">Invalid Reset Link</h1>
            <p className="text-gray-600 dark:text-gray-400 mb-6">This password reset link is invalid or missing.</p>
            <button
              onClick={() => window.location.href = '/'}
              className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-bold py-3 px-6 rounded-lg transition-all"
            >
              Back to Sign In
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (tokenValid === null) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 w-full max-w-md shadow-2xl">
          <div className="text-center">
            <div className={`animate-spin rounded-full h-12 w-12 border-b-2 ${colorTheme === 'purple' ? 'border-purple-600' : colorTheme === 'blue' ? 'border-blue-600' : 'border-green-600'} mx-auto mb-4`}></div>
            <p className="text-gray-600 dark:text-gray-400">Validating reset token...</p>
          </div>
        </div>
      </div>
    );
  }

  if (tokenValid === false) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 w-full max-w-md shadow-2xl">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">Reset Link Expired</h1>
            <div className="mb-4 p-3 bg-red-100 dark:bg-red-900 border border-red-400 text-red-700 dark:text-red-200 rounded-lg">
              {error}
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-6">Please request a new password reset link.</p>
            <button
              onClick={() => window.location.href = '/'}
              className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-bold py-3 px-6 rounded-lg transition-all"
            >
              Back to Sign In
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-gradient-to-br ${colorTheme === 'purple' ? 'from-purple-50 via-white to-pink-50' : colorTheme === 'blue' ? 'from-blue-50 via-white to-sky-50' : 'from-green-50 via-white to-emerald-50'} dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center p-4`}>
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 w-full max-w-md shadow-2xl">
        <div className="text-center mb-8">
          <div className={`text-4xl font-bold bg-gradient-to-r ${currentColors.textGradient} bg-clip-text text-transparent mb-4`}>
            AdElevate
          </div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Reset Your Password</h1>
          <p className="text-gray-600 dark:text-gray-400">
            Enter a new password for {email}
          </p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 dark:bg-red-900 border border-red-400 text-red-700 dark:text-red-200 rounded-lg">
            {error}
          </div>
        )}

        {success && (
          <div className={`mb-4 p-3 ${currentColors.lightBg} dark:${currentColors.bgColor} dark:bg-opacity-20 border ${currentColors.borderColor} ${currentColors.textColor} dark:text-${colorTheme}-200 rounded-lg`}>
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              New Password
            </label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              className={`w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 ${colorTheme === 'purple' ? 'focus:ring-purple-500' : colorTheme === 'blue' ? 'focus:ring-blue-500' : 'focus:ring-green-500'} focus:border-transparent dark:bg-gray-800 dark:text-white`}
              placeholder="Enter new password (min 8 characters)"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Confirm New Password
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              className={`w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 ${colorTheme === 'purple' ? 'focus:ring-purple-500' : colorTheme === 'blue' ? 'focus:ring-blue-500' : 'focus:ring-green-500'} focus:border-transparent dark:bg-gray-800 dark:text-white`}
              placeholder="Confirm new password"
            />
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className={`w-full bg-gradient-to-r ${currentColors.buttonGradient} ${currentColors.hoverButtonGradient} disabled:from-gray-400 disabled:to-gray-500 text-white font-bold py-3 rounded-lg transition-all`}
          >
            {isLoading ? 'Resetting Password...' : 'Reset Password'}
          </button>
        </form>

        <div className="mt-6 text-center">
          <button
            onClick={() => window.location.href = '/'}
            className={`${currentColors.textColor} ${currentColors.hoverTextColor} dark:text-${colorTheme}-400 dark:hover:text-${colorTheme}-300 font-medium`}
          >
            Back to Sign In
          </button>
        </div>
      </div>
    </div>
  );
};

// ==========================================
// 🍞 Toast Notification Component
// ==========================================
const Toast = ({ id, type, title, message, onClose, onClick }) => {
  const [isExiting, setIsExiting] = React.useState(false);

  React.useEffect(() => {
    // Auto-dismiss after 5 seconds
    const timer = setTimeout(() => {
      handleClose();
    }, 5000);

    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => {
      onClose(id);
    }, 300); // Match animation duration
  };

  const getToastStyles = () => {
    switch (type) {
      case 'campaign_complete':
        return 'bg-gradient-to-r from-green-500 to-emerald-500 border-green-600';
      case 'campaign_failed':
        return 'bg-gradient-to-r from-red-500 to-rose-500 border-red-600';
      default:
        return 'bg-gradient-to-r from-blue-500 to-indigo-500 border-blue-600';
    }
  };

  const getIcon = () => {
    switch (type) {
      case 'campaign_complete':
        return '✅';
      case 'campaign_failed':
        return '❌';
      default:
        return '🔔';
    }
  };

  return (
    <div
      className={`${getToastStyles()} text-white rounded-lg shadow-2xl border-2 p-4 mb-3 cursor-pointer transition-all duration-300 transform ${
        isExiting ? 'translate-x-full opacity-0' : 'translate-x-0 opacity-100'
      } hover:scale-105`}
      onClick={() => {
        if (onClick) onClick();
        handleClose();
      }}
    >
      <div className="flex items-start space-x-3">
        <span className="text-3xl flex-shrink-0">{getIcon()}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1">
            <p className="font-bold text-sm">{title}</p>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleClose();
              }}
              className="text-white hover:text-gray-200 ml-2"
            >
              ✕
            </button>
          </div>
          <p className="text-xs opacity-90 line-clamp-2">{message}</p>
        </div>
      </div>
    </div>
  );
};

// ==========================================
// 🍞 Toast Container Component
// ==========================================
const ToastContainer = ({ toasts, onRemoveToast, onToastClick }) => {
  return (
    <div className="fixed bottom-4 right-4 z-[10000] w-80 max-w-full pointer-events-none">
      <div className="pointer-events-auto space-y-3">
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            {...toast}
            onClose={onRemoveToast}
            onClick={() => onToastClick && onToastClick(toast)}
          />
        ))}
      </div>
    </div>
  );
};

// ==========================================
// 🔔 Notification Bell Component
// ==========================================
const NotificationBell = ({ sessionToken, isCollapsed }) => {
  const { currentColors, colorTheme } = useColorTheme();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const dropdownRef = React.useRef(null);

  const fetchNotifications = useCallback(async () => {
    if (!sessionToken) return;
    
    try {
      const response = await axios.get(`${API}/notifications?limit=10`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      setNotifications(response.data);
      setUnreadCount(response.data.filter(n => !n.read).length);
    } catch (error) {
      // Silently fail to avoid cluttering console
      if (error.response?.status !== 401) {
        console.error('Error fetching notifications:', error);
      }
    }
  }, [sessionToken]);

  // Fetch notifications periodically
  useEffect(() => {
    if (sessionToken) {
      fetchNotifications();
      const interval = setInterval(fetchNotifications, 10000); // Poll every 10 seconds
      return () => clearInterval(interval);
    }
  }, [sessionToken, fetchNotifications]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const markAsRead = async (notificationId) => {
    try {
      await axios.post(
        `${API}/notifications/${notificationId}/read`,
        {},
        { headers: { Authorization: `Bearer ${sessionToken}` } }
      );
      // Update local state
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      markAsRead(notification.id);
    }
    setShowDropdown(false);
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'campaign_complete':
        return '✅';
      case 'campaign_failed':
        return '❌';
      default:
        return '🔔';
    }
  };

  const getTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);
    
    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className={`relative w-full px-3 py-2.5 rounded-lg transition-colors flex items-center ${
          isCollapsed 
            ? `justify-center hover:${currentColors.lightBg} dark:hover:bg-gray-700` 
            : `${currentColors.lightBg} dark:bg-gray-800 hover:bg-opacity-80 dark:hover:bg-gray-700`
        }`}
        title="Notifications"
      >
        <span className="text-lg">🔔</span>
        {!isCollapsed && (
          <span className="ml-2.5 text-sm font-medium text-gray-700 dark:text-gray-300">
            Notifications
          </span>
        )}
        {unreadCount > 0 && (
          <span className={`absolute ${isCollapsed ? '-top-1 -right-1' : 'right-3 top-1/2 -translate-y-1/2'} bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center`}>
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Notification Dropdown */}
      {showDropdown && (
        <div className={`fixed bottom-20 left-4 w-80 bg-white dark:bg-gray-800 rounded-lg shadow-2xl border-2 ${currentColors.borderColor} dark:border-gray-700 z-[9999] max-h-96 overflow-hidden flex flex-col animate-slideInUp`}>
          {/* Header */}
          <div className={`p-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r ${colorTheme === 'purple' ? 'from-purple-50 to-pink-50' : colorTheme === 'blue' ? 'from-blue-50 to-sky-50' : 'from-green-50 to-emerald-50'} dark:from-gray-900 dark:to-gray-800`}>
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-gray-900 dark:text-white">
                🔔 Notifications
              </h3>
              {unreadCount > 0 && (
                <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full animate-pulse">
                  {unreadCount} new
                </span>
              )}
            </div>
          </div>

          {/* Notification List */}
          <div className="overflow-y-auto flex-1">
            {notifications.length === 0 ? (
              <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                <span className="text-4xl block mb-2">📭</span>
                <p className="text-sm">No notifications yet</p>
                <p className="text-xs mt-1">You'll be notified when campaigns are ready</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {notifications.map((notification) => (
                  <button
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`w-full p-4 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200 ${
                      !notification.read ? `${colorTheme === 'purple' ? 'bg-purple-50' : colorTheme === 'blue' ? 'bg-blue-50' : 'bg-green-50'} dark:bg-gray-700/50` : ''
                    }`}
                  >
                    <div className="flex items-start space-x-3">
                      <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-xl ${
                        notification.type === 'campaign_complete' 
                          ? `${colorTheme === 'purple' ? 'bg-purple-100 dark:bg-purple-900/30' : 'bg-green-100 dark:bg-green-900/30'}` 
                          : notification.type === 'campaign_failed'
                          ? 'bg-red-100 dark:bg-red-900/30'
                          : `${colorTheme === 'purple' ? 'bg-purple-50 dark:bg-purple-900/20' : 'bg-green-50 dark:bg-green-900/20'}`
                      }`}>
                        {getNotificationIcon(notification.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <p className="font-semibold text-sm text-gray-900 dark:text-white truncate">
                            {notification.title}
                          </p>
                          {!notification.read && (
                            <span className={`flex-shrink-0 w-2 h-2 ${colorTheme === 'purple' ? 'bg-purple-500' : 'bg-blue-500'} rounded-full ml-2 animate-pulse`}></span>
                          )}
                        </div>
                        <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2">
                          {notification.message}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                          {getTimeAgo(notification.created_at)}
                        </p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="p-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
              <button
                onClick={() => {
                  setShowDropdown(false);
                  // Could navigate to notifications page
                }}
                className={`w-full text-center text-sm ${colorTheme === 'purple' ? 'text-purple-600 dark:text-purple-400 hover:text-purple-700' : 'text-green-600 dark:text-yellow-400 hover:text-yellow-700'} dark:hover:text-yellow-300 font-medium transition-colors`}
              >
                View all notifications
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
// ==========================================

// Modern Sidebar Navigation Component  
const ModernSidebar = ({ currentView, onNavigate, user, onLogout, onToggleCollapse, isCollapsed: collapsed, sessionToken, isMobile, showMobileMenu, setShowMobileMenu }) => {
  const { isDark, toggleTheme } = useTheme();
  const { currentColors, colorTheme } = useColorTheme();
  const [isCollapsed, setIsCollapsed] = useState(collapsed || false);
  const [showSettingsDropdown, setShowSettingsDropdown] = useState(false);

  const handleToggleCollapse = () => {
    const newCollapsedState = !isCollapsed;
    setIsCollapsed(newCollapsedState);
    onToggleCollapse(newCollapsedState);
  };

  // Close mobile menu when navigating
  const handleMobileNavigation = (view) => {
    onNavigate(view);
    if (isMobile && setShowMobileMenu) {
      setShowMobileMenu(false);
    }
  };

  const menuItems = [
    { id: 'content-studio', icon: '🎯', label: 'Content Studio', badge: null },
    { id: 'ai-assistant', icon: '🤖', label: 'AI Assistant', badge: 'AI' },
    { id: 'campaigns', icon: '📊', label: 'Dashboard', badge: null },
    { id: 'main', icon: '🎨', label: 'Ad Creator Hub', badge: null },
    { id: 'my-gallery', icon: '🖼️', label: 'My Gallery', badge: 'NEW' },
    { id: 'analytics', icon: '📈', label: 'Analytics', badge: null },
    // { id: 'dashboard', icon: '📁', label: 'My Masterpieces', badge: null }, // HIDDEN: Duplicate of Dashboard (campaigns)
    { id: 'achievements', icon: '🏆', label: 'Achievements', badge: null },
    { id: 'feedback', icon: '💬', label: 'Feedback', badge: null },
  ];

  return (
    <>
      {/* Mobile Overlay */}
      {isMobile && showMobileMenu && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 md:hidden"
          onClick={() => setShowMobileMenu(false)}
        />
      )}
      
      <div className={`
        ${isMobile 
          ? `fixed inset-y-0 left-0 z-40 w-64 transform transition-transform duration-300 ${showMobileMenu ? 'translate-x-0' : '-translate-x-full'}`
          : `${isCollapsed ? 'w-16' : 'w-64'} fixed left-0 top-0 z-40`
        }
        bg-gradient-to-b ${currentColors.bgGradientFrom} ${currentColors.bgGradientTo} dark:from-gray-900 dark:to-gray-900 border-r ${currentColors.borderColor} dark:border-gray-700 transition-all duration-300 flex flex-col h-screen overflow-hidden
      `}>
      {/* Header */}
      <div className={`p-4 border-b ${currentColors.borderColor} dark:border-gray-700`}>
        <div className="flex items-center justify-between">
          {!isCollapsed && (
            <div className="flex items-center">
              <div className={`text-2xl font-bold bg-gradient-to-r ${currentColors.textGradient} bg-clip-text text-transparent`}>
                AdElevate
              </div>
              <span className={`ml-2 text-xs ${currentColors.lightBg} ${currentColors.textColor} dark:bg-gray-700 dark:text-white px-2 py-1 rounded`}>
                Beta
              </span>
            </div>
          )}
          <button
            onClick={handleToggleCollapse}
            className={`p-1 rounded hover:${currentColors.lightBg} dark:hover:bg-gray-800 transition-colors text-gray-700 dark:text-gray-300`}
            title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {isCollapsed ? '→' : '←'}
          </button>
        </div>
      </div>

      {/* Navigation Menu */}
      <nav className="flex-1 p-4 overflow-y-auto scrollbar-hide">
        <div className="space-y-2">
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => handleMobileNavigation(item.id)}
              className={`w-full flex items-center ${isCollapsed && !isMobile ? 'justify-center' : 'justify-start'} px-3 py-2 rounded-lg transition-colors ${
                currentView === item.id
                  ? `bg-gradient-to-r ${currentColors.buttonGradient} text-white`
                  : `text-gray-700 dark:text-gray-300 hover:${currentColors.lightBg} dark:hover:bg-gray-800`
              }`}
            >
              <span className="text-lg">{item.icon}</span>
              {(!isCollapsed || isMobile) && (
                <>
                  <span className="ml-3 font-medium">{item.label}</span>
                  {item.badge && (
                    <span className="ml-auto bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                      {item.badge}
                    </span>
                  )}
                </>
              )}
            </button>
          ))}
        </div>
      </nav>

      {/* User Profile & Settings - Fixed at bottom */}
      <div className={`p-4 border-t ${currentColors.borderColor} dark:border-gray-700 ${currentColors.lightBg} dark:bg-gray-800 flex-shrink-0`}>
        {/* Notification Bell - Above profile for expanded state */}
        {!isCollapsed && (
          <div className="mb-3">
            <NotificationBell sessionToken={sessionToken} isCollapsed={isCollapsed} />
          </div>
        )}

        {!isCollapsed && (
          <div className="relative">
            {/* Profile button with dropdown trigger */}
            <button
              onClick={() => setShowSettingsDropdown(!showSettingsDropdown)}
              className={`w-full flex items-center justify-between p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors ${showSettingsDropdown ? 'bg-gray-200 dark:bg-gray-700' : ''}`}
            >
              <div className="flex items-center">
                {user?.picture ? (
                  <img
                    src={user.picture}
                    alt={user.name}
                    className="w-8 h-8 rounded-full"
                  />
                ) : (
                  <div className={`w-8 h-8 bg-gradient-to-r ${currentColors.buttonGradient} rounded-full flex items-center justify-center text-white font-bold text-sm`}>
                    {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                )}
                <div className="ml-3 flex-1 min-w-0 text-left">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {user?.name || 'User'}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {user?.email || 'user@example.com'}
                  </p>
                </div>
              </div>
              <span className={`text-gray-400 dark:text-gray-500 transition-transform duration-200 ${showSettingsDropdown ? 'rotate-180' : ''}`}>
                ▲
              </span>
            </button>
            
            {/* Profile & Settings Dropdown */}
            {showSettingsDropdown && (
              <>
                {/* Backdrop to close dropdown */}
                <div 
                  className="fixed inset-0 z-40" 
                  onClick={() => setShowSettingsDropdown(false)}
                />
                
                {/* Dropdown Menu */}
                <div className="absolute bottom-full left-0 right-0 mb-2 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 z-50 overflow-hidden">
                  <div className="p-1">
                    {/* Theme Toggle */}
                    <button
                      onClick={() => {
                        toggleTheme();
                      }}
                      className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-3"
                    >
                      <span className="text-lg">{isDark ? '☀️' : '🌙'}</span>
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{isDark ? 'Light Mode' : 'Dark Mode'}</span>
                    </button>
                    <button
                      onClick={() => {
                        setShowSettingsDropdown(false);
                        handleMobileNavigation('credits');
                      }}
                      className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-3"
                    >
                      <span className="text-lg">💳</span>
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Credits</span>
                    </button>
                    <button
                      onClick={() => {
                        setShowSettingsDropdown(false);
                        handleMobileNavigation('whatsapp');
                      }}
                      className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-3"
                    >
                      <span className="text-lg">💬</span>
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">WhatsApp</span>
                    </button>
                    <div className="border-t border-gray-200 dark:border-gray-700 my-1"></div>
                    <button
                      onClick={() => {
                        setShowSettingsDropdown(false);
                        onLogout();
                      }}
                      className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors flex items-center gap-3 text-red-600 dark:text-red-400"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      <span className="text-sm font-medium">Sign Out</span>
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Collapsed state - Icons only */}
        {isCollapsed && (
          <div className="space-y-2">
            <div className="flex justify-center">
              <NotificationBell sessionToken={sessionToken} isCollapsed={isCollapsed} />
            </div>
            <div className="relative flex justify-center">
              <button
                onClick={() => setShowSettingsDropdown(!showSettingsDropdown)}
                className={`p-2 rounded-lg ${currentColors.lightBg} dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors`}
                title="Profile & Settings"
              >
                {user?.picture ? (
                  <img
                    src={user.picture}
                    alt={user.name}
                    className="w-6 h-6 rounded-full"
                  />
                ) : (
                  <div className={`w-6 h-6 bg-gradient-to-r ${currentColors.buttonGradient} rounded-full flex items-center justify-center text-white font-bold text-xs`}>
                    {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                )}
              </button>
              
              {showSettingsDropdown && (
                <>
                  <div 
                    className="fixed inset-0 z-40" 
                    onClick={() => setShowSettingsDropdown(false)}
                  />
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 z-50">
                    <div className="p-2">
                      <div className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700 mb-1">
                        {user?.email || 'user@example.com'}
                      </div>
                      <button
                        onClick={() => {
                          setShowSettingsDropdown(false);
                          onNavigate('credits');
                        }}
                        className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-2"
                      >
                        <span>💳</span>
                        <span className="text-sm text-gray-700 dark:text-gray-300">Credits</span>
                      </button>
                      <button
                        onClick={() => {
                          setShowSettingsDropdown(false);
                          onNavigate('whatsapp');
                        }}
                        className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-2"
                      >
                        <span>💬</span>
                        <span className="text-sm text-gray-700 dark:text-gray-300">WhatsApp</span>
                      </button>
                      <div className="border-t border-gray-200 dark:border-gray-700 my-1"></div>
                      <button
                        onClick={() => {
                          setShowSettingsDropdown(false);
                          onLogout();
                        }}
                        className="w-full text-left px-3 py-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors flex items-center gap-2 text-red-600 dark:text-red-400"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        <span className="text-sm">Sign Out</span>
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
    </>
  );
};

const Header = () => {
  const { isDark, toggleTheme } = useTheme();
  const { user, logout, isAuthenticated } = useAuth();
  
  return (
    <header className="bg-yellow-400 dark:bg-yellow-600 shadow-lg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-6">
          <div className="flex items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                AdElevate
              </h1>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                Metabolic AI Powered Lead Enhancer
              </p>
            </div>
            <span className="ml-2 text-sm bg-yellow-200 dark:bg-yellow-800 text-gray-800 dark:text-white px-2 py-1 rounded">
              Beta
            </span>
          </div>
          <div className="flex items-center space-x-4">
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg bg-yellow-200 dark:bg-yellow-800 hover:bg-yellow-300 dark:hover:bg-yellow-700 transition-colors"
            >
              {isDark ? '☀️' : '🌙'}
            </button>
            
            {isAuthenticated ? (
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  {user?.picture && (
                    <img
                      src={user.picture}
                      alt={user.name}
                      className="w-8 h-8 rounded-full"
                    />
                  )}
                  <span className="text-gray-900 dark:text-white font-medium">
                    {user?.name}
                  </span>
                </div>
                <button
                  onClick={logout}
                  className="text-gray-900 dark:text-white hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                >
                  Logout
                </button>
              </div>
            ) : (
              <nav className="hidden md:flex space-x-8">
                <a href="#features" className="text-gray-900 dark:text-white hover:text-gray-700 dark:hover:text-gray-300">
                  Features
                </a>
                <a href="#pricing" className="text-gray-900 dark:text-white hover:text-gray-700 dark:hover:text-gray-300">
                  Pricing
                </a>
              </nav>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

const Hero = ({ onCreateNewCampaign, onViewAnalytics, refreshTrigger }) => {
  const { login, isAuthenticated, user } = useAuth();
  const [userCampaigns, setUserCampaigns] = useState([]);
  const [isLoadingCampaigns, setIsLoadingCampaigns] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      fetchUserCampaigns();
    }
  }, [isAuthenticated, refreshTrigger]); // Added refreshTrigger dependency

  const fetchUserCampaigns = async () => {
    setIsLoadingCampaigns(true);
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/campaigns`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Handle paginated response format
      if (response.data && response.data.campaigns && Array.isArray(response.data.campaigns)) {
        setUserCampaigns(response.data.campaigns);
      } else if (Array.isArray(response.data)) {
        setUserCampaigns(response.data);
      } else {
        setUserCampaigns([]);
      }
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      setUserCampaigns([]);
    } finally {
      setIsLoadingCampaigns(false);
    }
  };

  const isFirstTimeUser = isAuthenticated && userCampaigns.length === 0 && !isLoadingCampaigns;
  const hasExistingCampaigns = isAuthenticated && userCampaigns.length > 0;
  
  return (
    <section className="bg-gradient-to-br from-yellow-50 via-yellow-100 to-yellow-200 dark:from-gray-800 dark:via-gray-900 dark:to-black py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <h2 className="text-5xl md:text-6xl font-extrabold text-gray-900 dark:text-white mb-6">
            Transform Products into
            <span className="block text-green-600 dark:text-yellow-400">
              Compelling Leads
            </span>
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 mb-8 max-w-3xl mx-auto">
            AdElevate's AI-powered lead generation creates persuasive copy for social media, Google Ads, 
            product descriptions, and email marketing that converts prospects into customers.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {!isAuthenticated ? (
              <>
                <button 
                  onClick={login}
                  className="bg-yellow-600 hover:bg-yellow-700 text-white font-bold py-4 px-8 rounded-lg text-lg transition-colors"
                >
                  Get Started Free
                </button>
                <button className="border-2 border-green-600 text-green-600 hover:bg-yellow-600 hover:text-white font-bold py-4 px-8 rounded-lg text-lg transition-colors">
                  Watch Demo
                </button>
              </>
            ) : isFirstTimeUser ? (
              <button 
                onClick={onCreateNewCampaign}
                className="bg-yellow-600 hover:bg-yellow-700 text-white font-bold py-4 px-8 rounded-lg text-lg transition-colors"
              >
                Generate Your First Lead
              </button>
            ) : hasExistingCampaigns ? (
              <div className="space-y-4">
                <h3 className="text-2xl font-semibold text-gray-900 dark:text-white">
                  Welcome back, {user?.name}! 👋
                </h3>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button 
                    onClick={() => document.getElementById('dashboard').scrollIntoView({ behavior: 'smooth' })}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 px-8 rounded-lg text-lg transition-colors"
                  >
                    View Dashboard
                  </button>
                  <button 
                    onClick={onViewAnalytics}
                    className={`${colorTheme === 'purple' ? 'bg-purple-600 hover:bg-purple-700' : colorTheme === 'blue' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-green-600 hover:bg-green-700'} text-white font-bold py-4 px-8 rounded-lg text-lg transition-colors`}
                  >
                    View Analytics
                  </button>
                  <button 
                    onClick={onCreateNewCampaign}
                    className="bg-yellow-600 hover:bg-yellow-700 text-white font-bold py-4 px-8 rounded-lg text-lg transition-colors"
                  >
                    Create New Campaign
                  </button>
                </div>
              </div>
            ) : isLoadingCampaigns ? (
              <div className="flex items-center justify-center">
                <div className={`animate-spin rounded-full h-8 w-8 border-b-2 ${colorTheme === 'purple' ? 'border-purple-600' : colorTheme === 'blue' ? 'border-blue-600' : 'border-green-600'}`}></div>
                <span className="ml-2 text-gray-600 dark:text-gray-300">Loading your campaigns...</span>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
};

const Features = () => {
  const features = [
    {
      icon: "🎯",
      title: "Multi-Format Generation",
      description: "Create marketing content for social media, Google Ads, product descriptions, and email marketing simultaneously."
    },
    {
      icon: "🤖",
      title: "AI-Powered Lead Generation",
      description: "Leverage Claude AI to generate compelling, conversion-focused marketing copy that converts prospects into leads."
    },
    {
      icon: "⚡",
      title: "Instant Results",
      description: "Generate professional marketing copy in seconds, not hours. Accelerate your lead generation process."
    },
    {
      icon: "🎨",
      title: "Brand Voice Matching",
      description: "Maintain consistent brand voice across all your marketing materials with customizable tone settings."
    }
  ];

  return (
    <section id="features" className="py-20 bg-white dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h3 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Powerful Features
          </h3>
          <p className="text-xl text-gray-600 dark:text-gray-300">
            Everything you need to create compelling marketing content that converts
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {features.map((feature, index) => (
            <div key={index} className="text-center p-6 rounded-lg bg-yellow-50 dark:bg-gray-800 hover:shadow-lg transition-shadow">
              <div className="text-4xl mb-4">{feature.icon}</div>
              <h4 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                {feature.title}
              </h4>
              <p className="text-gray-600 dark:text-gray-300">
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const FeedbackDashboard = ({ onBackToMain }) => {
  const { sessionToken } = useAuth();
  const { colorTheme } = useColorTheme();
  const [campaigns, setCampaigns] = useState([]);
  const [campaignStats, setCampaignStats] = useState({});
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [feedbackData, setFeedbackData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isFeedbackLoading, setIsFeedbackLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Publishing status for feedback dashboard campaigns
  const [feedbackDashboardPublishingStatus, setFeedbackDashboardPublishingStatus] = useState({});
  const [aiFeedbackData, setAiFeedbackData] = useState(null);
  const [isAiFeedbackLoading, setIsAiFeedbackLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('user'); // 'user' or 'ai'

  useEffect(() => {
    fetchCampaigns();
  }, []);

  // Fetch publishing status for feedback dashboard campaigns
  const fetchFeedbackDashboardPublishingStatus = async (campaigns) => {
    if (!sessionToken || !campaigns?.length) {
      return;
    }

    console.log(`fetchFeedbackDashboardPublishingStatus: Starting for ${campaigns.length} campaigns`);
    const statusMap = {};
    
    try {
      // Process campaigns to get publishing status
      for (let i = 0; i < campaigns.length && i < 15; i++) {
        const campaign = campaigns[i];
        
        try {
          const response = await axios.get(
            `${API}/share/history/${campaign.id}`,
            { 
              headers: { Authorization: `Bearer ${sessionToken}` },
              timeout: 5000
            }
          );
          
          if (response.status === 200 && response.data) {
            const data = response.data;
            const channelCounts = {};
            
            // Count successful shares by channel type
            data.shares.forEach(share => {
              if (share.status === 'success') {
                const channelType = share.channel_type;
                channelCounts[channelType] = (channelCounts[channelType] || 0) + 1;
              }
            });
            
            statusMap[campaign.id] = {
              channelCounts,
              totalShares: data.total_shares,
              hasSuccessfulShares: Object.keys(channelCounts).length > 0
            };
            
            console.log(`✅ Feedback dashboard publishing status for ${campaign.id}:`, statusMap[campaign.id]);
          }
        } catch (error) {
          statusMap[campaign.id] = {
            channelCounts: {},
            totalShares: 0,
            hasSuccessfulShares: false
          };
        }
        
        // Small delay between requests
        if (i < campaigns.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 200));
        }
      }
      
      console.log('✅ Feedback dashboard publishing status completed:', Object.keys(statusMap).length, 'campaigns');
      setFeedbackDashboardPublishingStatus(statusMap);
    } catch (error) {
      console.warn('Error in fetchFeedbackDashboardPublishingStatus:', error);
    }
  };

  const fetchCampaigns = async () => {
    try {
      // Use pagination parameters to match new backend format and avoid conflicts (limit=100 max)
      const response = await axios.get(`${API}/campaigns?page=1&limit=100`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      
      // Handle new paginated response format
      let allCampaigns = [];
      if (response.data && response.data.campaigns && Array.isArray(response.data.campaigns)) {
        allCampaigns = response.data.campaigns;
      } else if (Array.isArray(response.data)) {
        allCampaigns = response.data;
      }
      
      // Check which campaigns have feedback (user OR AI feedback) or are eligible for feedback
      const campaignsWithFeedback = [];
      const statsPromises = allCampaigns.map(async (campaign) => {
        try {
          const statsResponse = await fetchCampaignStats(campaign.id);
          
          // Include all campaigns that:
          // 1. Have AI feedback saved OR
          // 2. Have user feedback OR
          // 3. Have completed generation (can receive feedback)
          const isCompleted = campaign.generation_status === 'completed';
          if (statsResponse.has_ai_feedback || statsResponse.total_feedback > 0 || isCompleted) {
            return { campaign, stats: statsResponse };
          }
          return null;
        } catch (error) {
          console.error(`Error fetching stats for campaign ${campaign.id}:`, error);
          // Include campaign even if stats fetch failed (show with zero stats)
          return { campaign, stats: { total_feedback: 0, average_rating: 0, user_feedback_count: 0, ai_feedback_count: 0, has_ai_feedback: false } };
        }
      });
      
      const allResults = await Promise.all(statsPromises);
      const validResults = allResults.filter(result => result !== null);
      
      // Extract campaigns and stats
      const filteredCampaigns = validResults.map(result => result.campaign);
      const statsMap = {};
      validResults.forEach(result => {
        statsMap[result.campaign.id] = result.stats;
      });
      
      setCampaigns(filteredCampaigns);
      setCampaignStats(statsMap);
      
      // Fetch publishing status for these campaigns
      fetchFeedbackDashboardPublishingStatus(filteredCampaigns);
      
    } catch (err) {
      setError('Failed to load campaigns');
      console.error('Error fetching campaigns:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch full campaign details including all product fields
  const fetchFullCampaignDetails = async (campaignId) => {
    try {
      const response = await axios.get(`${API}/campaigns/${campaignId}`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching full campaign details:', error);
      return null;
    }
  };

  const fetchCampaignStats = async (campaignId) => {
    try {
      const response = await axios.get(`${API}/campaigns/${campaignId}/feedback/combined`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      
      console.log(`Combined feedback response for campaign ${campaignId}:`, response.data);
      
      const combinedStats = response.data;
      
      // Return the combined metrics (user + AI feedback)
      return {
        total_feedback: combinedStats.combined_metrics.total_feedback || 0,
        average_rating: combinedStats.combined_metrics.average_rating || 0,
        user_feedback_count: combinedStats.user_feedback.total_feedback || 0,
        ai_feedback_count: combinedStats.ai_feedback.total_feedback || 0,
        has_ai_feedback: combinedStats.ai_feedback.has_saved_feedback || false,
        breakdown: {
          user: combinedStats.user_feedback,
          ai: combinedStats.ai_feedback,
          combined: combinedStats.combined_metrics
        }
      };
    } catch (error) {
      console.error(`Error fetching combined stats for campaign ${campaignId}:`, error);
      return { 
        total_feedback: 0, 
        average_rating: 0, 
        user_feedback_count: 0,
        ai_feedback_count: 0,
        has_ai_feedback: false,
        breakdown: null 
      };
    }
  };

  const fetchAiFeedback = async (campaignId) => {
    if (!campaignId || !sessionToken) {
      setAiFeedbackData(null);
      return;
    }
    
    setIsAiFeedbackLoading(true);
    try {
      const response = await axios.get(`${API}/campaigns/${campaignId}/ai-feedback/saved`, {
        headers: { 
          Authorization: `Bearer ${sessionToken}`,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        },
        timeout: 10000 // 10 second timeout
      });
      
      if (response.status === 200 && response.data) {
        setAiFeedbackData(response.data);
      } else {
        setAiFeedbackData(null);
      }
    } catch (err) {
      // 404 is expected if no AI feedback is saved - this is normal
      if (err.response?.status === 404) {
        setAiFeedbackData(null);
      } else {
        console.warn('Error fetching AI feedback for campaign:', campaignId, err.message);
        setAiFeedbackData(null);
      }
    } finally {
      setIsAiFeedbackLoading(false);
    }
  };

  const fetchCampaignFeedback = async (campaignId) => {
    // Add comprehensive validation
    if (!campaignId || !sessionToken) {
      console.log('Invalid campaignId or sessionToken, skipping feedback fetch');
      setIsFeedbackLoading(false);
      return;
    }
    
    // Validate that the campaign exists in our campaigns array
    const selectedCampaign = campaigns.find(c => c.id === campaignId);
    if (!selectedCampaign) {
      console.log(`Campaign ${campaignId} not found in campaigns array, skipping feedback fetch`);
      setIsFeedbackLoading(false);
      return;
    }
    
    try {
      setIsFeedbackLoading(true);
      
      // Fetch combined stats (required) and user feedback (optional)
      let userFeedbackData = { feedbacks: [], campaign_info: null };
      let combinedStatsResponse;
      
      try {
        // Try to fetch user feedback, but don't fail if it doesn't exist
        const [userFeedbackResponse, combinedResponse] = await Promise.all([
          axios.get(`${API}/campaigns/${campaignId}/feedback`, {
            headers: { Authorization: `Bearer ${sessionToken}` }
          }).catch(error => {
            // If user feedback doesn't exist, return empty structure
            console.log('User feedback not available for campaign:', campaignId);
            return { data: { feedbacks: [], campaign_info: null } };
          }),
          axios.get(`${API}/campaigns/${campaignId}/feedback/combined`, {
            headers: { Authorization: `Bearer ${sessionToken}` }
          })
        ]);
        
        userFeedbackData = userFeedbackResponse.data;
        combinedStatsResponse = combinedResponse;
        
      } catch (error) {
        console.error('Error fetching feedback data:', error);
        throw error;
      }
      
      // Build campaign_info from selected campaign if not available in user feedback
      let campaignInfo = userFeedbackData.campaign_info;
      
      if (!campaignInfo && selectedCampaign) {
        campaignInfo = {
          product_name: selectedCampaign.product?.product_name || 'Unknown Product',
          campaign_id: selectedCampaign.id,
          is_public: selectedCampaign.is_public || false,
          created_at: selectedCampaign.created_at
        };
      }
      
      // Combine the data - use combined stats for metrics but keep user feedback for display
      const enhancedFeedbackData = {
        ...userFeedbackData,
        campaign_info: campaignInfo,
        // Replace user-only statistics with combined statistics
        statistics: {
          total_feedback: combinedStatsResponse.data.combined_metrics.total_feedback,
          average_rating: combinedStatsResponse.data.combined_metrics.average_rating,
          rating_distribution: combinedStatsResponse.data.combined_metrics.rating_distribution,
          // Add breakdown information
          breakdown: {
            user_feedback_count: combinedStatsResponse.data.user_feedback.total_feedback,
            ai_feedback_count: combinedStatsResponse.data.ai_feedback.total_feedback,
            user_stats: combinedStatsResponse.data.user_feedback,
            ai_stats: combinedStatsResponse.data.ai_feedback
          }
        }
      };
      
      setFeedbackData(enhancedFeedbackData);
    } catch (err) {
      setError('Failed to load feedback data');
      console.error('Error fetching feedback:', err);
    } finally {
      setIsFeedbackLoading(false);
    }
  };

  const handleCampaignSelect = async (campaign) => {
    // Validate campaign before processing
    if (!campaign || !campaign.id) {
      console.error('Invalid campaign selected:', campaign);
      return;
    }
    
    // Validate campaign exists in campaigns array
    const validCampaign = campaigns.find(c => c.id === campaign.id);
    if (!validCampaign) {
      console.error('Campaign not found in campaigns array:', campaign.id);
      return;
    }
    
    // CRITICAL FIX: Fetch full campaign details before showing modal
    try {
      const response = await axios.get(`${API}/campaigns/${campaign.id}`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      setSelectedCampaign(response.data);
    } catch (error) {
      console.error('Error fetching full campaign details:', error);
      setSelectedCampaign(campaign);
    }
    
    setShowDetailsModal(true);  // Open the campaign details modal
    fetchCampaignFeedback(campaign.id);
    
    // Also fetch AI feedback
    fetchAiFeedback(campaign.id);
    
    // Enhanced auto-scroll with multiple fallback strategies
    setTimeout(() => {
      console.log('🔄 Starting auto-scroll sequence...');
      
      // Strategy 1: Scroll the main feedback details panel
      const feedbackPanel = document.getElementById('feedback-details-panel');
      if (feedbackPanel) {
        console.log('✅ Found feedback-details-panel, scrolling...');
        feedbackPanel.scrollTop = 0;
        feedbackPanel.scrollTo({ top: 0, behavior: 'smooth' });
      }
      
      // Strategy 2: Scroll the feedback section into view
      const feedbackSection = document.getElementById('feedback-section');
      if (feedbackSection) {
        console.log('✅ Found feedback-section, scrolling into view...');
        feedbackSection.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start',
          inline: 'nearest'
        });
      }
      
      // Strategy 3: Scroll the main content area (viewport)
      const mainContent = document.querySelector('.lg\\:col-span-2') || 
                         document.querySelector('[id*="feedback"]') ||
                         document.querySelector('.space-y-6');
      if (mainContent) {
        console.log('✅ Found main content area, scrolling...');
        mainContent.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
      }
      
      // Strategy 4: Scroll the entire window to the feedback area
      const feedbackContainer = document.querySelector('.grid.grid-cols-1.lg\\:grid-cols-3') ||
                               document.querySelector('.min-h-screen');
      if (feedbackContainer) {
        console.log('✅ Scrolling window to feedback container...');
        window.scrollTo({ 
          top: feedbackContainer.offsetTop || 0, 
          behavior: 'smooth' 
        });
      }
      
      // Strategy 5: Force scroll to top of page as fallback
      console.log('🔄 Fallback: Scrolling to top of page...');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
    }, 300); // Increased delay for content loading
  };

  if (isLoading) {
    return (
      <div className={`min-h-screen bg-gradient-to-br ${colorTheme === 'purple' ? 'from-purple-50 via-white to-pink-50' : colorTheme === 'blue' ? 'from-blue-50 via-white to-sky-50' : 'from-green-50 via-white to-emerald-50'} dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center`}>
        <div className="text-center">
          <div className={`animate-spin rounded-full h-12 w-12 border-b-2 ${colorTheme === 'purple' ? 'border-purple-600' : colorTheme === 'blue' ? 'border-blue-600' : 'border-green-600'} mx-auto mb-4`}></div>
          <p className="text-gray-600 dark:text-gray-400">Loading feedback dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-gradient-to-br ${colorTheme === 'purple' ? 'from-purple-50/80 via-white to-pink-50/50' : colorTheme === 'blue' ? 'from-blue-50/80 via-white to-sky-50/50' : 'from-emerald-50/80 via-white to-teal-50/50'} dark:from-slate-900 dark:via-slate-900 dark:to-slate-900`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className={`text-3xl font-bold bg-gradient-to-r ${colorTheme === 'purple' ? 'from-purple-600 to-pink-600' : colorTheme === 'blue' ? 'from-blue-600 to-sky-500' : 'from-emerald-600 to-teal-500'} bg-clip-text text-transparent`}>
              Feedback Dashboard
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1 text-sm">
              View feedback and ratings for your public campaigns
            </p>
          </div>
          <button
            onClick={onBackToMain}
            className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 font-medium text-sm rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            Back to Dashboard
          </button>
        </div>

        {campaigns.length === 0 ? (
          <div className="bg-white/70 dark:bg-slate-800/50 backdrop-blur-xl border border-slate-200/50 dark:border-slate-700/50 rounded-2xl p-12 text-center shadow-sm">
            <svg className={`w-12 h-12 mx-auto mb-4 ${colorTheme === 'purple' ? 'text-purple-400' : colorTheme === 'blue' ? 'text-blue-400' : 'text-emerald-400'}`} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z" /></svg>
            <h2 className="text-xl font-bold text-slate-800 dark:text-white mb-2">
              No Feedback Available
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
              Generate AI feedback or make campaigns public to start receiving reviews.
            </p>
            <button
              onClick={onBackToMain}
              className={`bg-gradient-to-r ${colorTheme === 'purple' ? 'from-purple-600 to-pink-600 shadow-purple-500/20' : colorTheme === 'blue' ? 'from-blue-600 to-sky-500 shadow-blue-500/20' : 'from-emerald-600 to-teal-500 shadow-emerald-500/20'} text-white font-bold py-3 px-6 rounded-xl shadow-lg text-sm transition-all`}
            >
              Go to Campaigns
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Feedback & Reviews List */}
            <div className="bg-white/70 dark:bg-slate-800/50 backdrop-blur-xl border border-slate-200/50 dark:border-slate-700/50 rounded-2xl p-5 shadow-sm">
              <h2 className={`text-base font-bold mb-4 ${colorTheme === 'purple' ? 'text-purple-900 dark:text-purple-100' : colorTheme === 'blue' ? 'text-blue-900 dark:text-blue-100' : 'text-emerald-900 dark:text-emerald-100'}`}>
                Feedback & Reviews ({campaigns.length})
              </h2>
              <div className="space-y-2.5">
                {campaigns.map((campaign) => (
                  <div
                    key={campaign.id}
                    onClick={async () => {
                      const fullCampaign = await fetchFullCampaignDetails(campaign.id);
                      setSelectedCampaign(fullCampaign || campaign);
                      fetchCampaignFeedback(campaign.id);
                      fetchAiFeedback(campaign.id);
                    }}
                    className={`p-3.5 rounded-xl transition-all cursor-pointer ${
                      selectedCampaign?.id === campaign.id
                        ? (colorTheme === 'purple' 
                          ? 'bg-purple-50 dark:bg-purple-900/20 border-2 border-purple-300 dark:border-purple-600'
                          : colorTheme === 'blue'
                          ? 'bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-300 dark:border-blue-600'
                          : 'bg-emerald-50 dark:bg-emerald-900/20 border-2 border-emerald-300 dark:border-emerald-600')
                        : 'bg-slate-50/80 dark:bg-slate-700/30 hover:bg-slate-100 dark:hover:bg-slate-700/50 border-2 border-transparent'
                    }`}
                  >
                    <h3 className="font-semibold text-slate-800 dark:text-white text-sm">
                      {campaign.product.product_name}
                    </h3>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                      Created {new Date(campaign.created_at).toLocaleDateString()}
                    </p>
                    <div className="flex items-center justify-between mt-2">
                      {campaignStats[campaign.id] ? (
                        <div className="flex flex-col space-y-1">
                          {campaignStats[campaign.id].total_feedback > 0 ? (
                            <>
                              <div className="flex items-center space-x-2">
                                <div className="flex items-center space-x-1">
                                  <StarRating 
                                    rating={Math.round(campaignStats[campaign.id].average_rating)} 
                                    readOnly 
                                    size="small" 
                                  />
                                  <span className="text-xs text-gray-600 dark:text-gray-400">
                                    {campaignStats[campaign.id].average_rating.toFixed(1)}
                                  </span>
                                </div>
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-400">
                                  💬 {campaignStats[campaign.id].total_feedback} total
                                </span>
                              </div>
                              <div className="flex items-center space-x-1 text-xs">
                                {campaignStats[campaign.id].user_feedback_count > 0 && (
                                  <span className={`inline-flex items-center px-1 py-0.5 rounded text-xs ${colorTheme === 'purple' ? 'bg-purple-100 dark:bg-purple-900/20 text-purple-800 dark:text-purple-400' : colorTheme === 'blue' ? 'bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-400' : 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-400'}`}>
                                    👤 {campaignStats[campaign.id].user_feedback_count}
                                  </span>
                                )}
                                {campaignStats[campaign.id].ai_feedback_count > 0 && (
                                  <span className="inline-flex items-center px-1 py-0.5 rounded text-xs bg-purple-100 dark:bg-purple-900/20 text-purple-800 dark:text-purple-400">
                                    🤖 {campaignStats[campaign.id].ai_feedback_count}
                                  </span>
                                )}
                              </div>
                            </>
                          ) : (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                              📢 No feedback yet
                            </span>
                          )}
                          
                          {/* Campaign Status Indicators for Feedback Dashboard */}
                          <div className="flex flex-wrap gap-1 mt-2">
                            {/* Published Channels & Sharing Status */}
                            {(() => {
                              const publishingStatus = feedbackDashboardPublishingStatus[campaign.id];
                              const channelBadges = [];
                              
                              // Show channel badges with counts for actually published channels
                              if (publishingStatus?.channelCounts) {
                                // Add Telegram if published
                                if (publishingStatus.channelCounts.telegram) {
                                  channelBadges.push({
                                    name: 'Telegram',
                                    icon: '📱',
                                    count: publishingStatus.channelCounts.telegram
                                  });
                                }
                                
                                // Add Facebook if published
                                if (publishingStatus.channelCounts.facebook) {
                                  channelBadges.push({
                                    name: 'Facebook', 
                                    icon: '📘',
                                    count: publishingStatus.channelCounts.facebook
                                  });
                                }
                                
                                // Add Instagram if published
                                if (publishingStatus.channelCounts.instagram) {
                                  channelBadges.push({
                                    name: 'Instagram',
                                    icon: '📸',
                                    count: publishingStatus.channelCounts.instagram
                                  });
                                }
                              }
                              
                              return (
                                <div className="flex items-center gap-1 mt-1">
                                  {/* Published channel badges */}
                                  {channelBadges.map((channel, index) => (
                                    <span key={index} className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-400">
                                      {channel.icon} {channel.name} ({channel.count})
                                    </span>
                                  ))}
                                  
                                  {/* Public sharing status */}
                                  {campaign.is_public && campaign.share_token && (
                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-400">
                                      🌍
                                    </span>
                                  )}
                                </div>
                              );
                            })()}
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center space-x-2">
                          <div className="animate-pulse bg-gray-200 dark:bg-gray-600 h-4 w-16 rounded"></div>
                          <div className="animate-pulse bg-gray-200 dark:bg-gray-600 h-4 w-12 rounded"></div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Feedback Display */}
            <div id="feedback-details-panel" className="lg:col-span-2">
              {!selectedCampaign ? (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8 text-center">
                  <div className="text-6xl mb-4">👈</div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                    Select a Campaign
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400">
                    Choose a campaign from the list to view its feedback and analytics.
                  </p>
                </div>
              ) : isFeedbackLoading ? (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8 text-center">
                  <div className={`animate-spin rounded-full h-8 w-8 border-b-2 ${colorTheme === 'purple' ? 'border-purple-600' : colorTheme === 'blue' ? 'border-blue-600' : 'border-green-600'} mx-auto mb-4`}></div>
                  <p className="text-gray-600 dark:text-gray-400">Loading feedback...</p>
                </div>
              ) : feedbackData ? (
                <div id="feedback-section" className="space-y-6">
                  {/* Campaign Info & Quick Actions */}
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                          {feedbackData.campaign_info?.product_name || 'Unknown Campaign'}
                        </h2>
                        <p className="text-gray-600 dark:text-gray-400 mt-1">
                          Campaign Feedback Overview
                        </p>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={async () => {
                            try {
                              // Ensure campaign has proper share URL
                              let shareUrl = feedbackData.campaign_info?.share_url;
                              
                              if (!shareUrl || shareUrl === 'null' || shareUrl === null) {
                                // Create public sharing link
                                const response = await axios.post(`${API}/campaigns/${feedbackData.campaign_info?.id || selectedCampaign?.id}/share`, 
                                  { is_public: true }, 
                                  { headers: { 'Authorization': `Bearer ${localStorage.getItem('session_token')}` } }
                                );
                                
                                if (response.data && response.data.share_url) {
                                  shareUrl = response.data.share_url;
                                }
                              }
                              
                              if (shareUrl && shareUrl !== 'null' && shareUrl !== null) {
                                window.open(shareUrl, '_blank');
                              } else {
                                alert('Unable to open public page. Please try again.');
                              }
                            } catch (error) {
                              console.error('Error opening public page:', error);
                              alert('Failed to open public page. Please try again.');
                            }
                          }}
                          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium"
                        >
                          🔗 View Public Page
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Consolidated Feedback Statistics - PRIORITY #1 */}
                  {feedbackData.statistics.total_feedback > 0 ? (
                    <>
                      <div className={`bg-gradient-to-br ${colorTheme === 'purple' ? 'from-purple-50 via-pink-50 to-purple-50 dark:from-purple-900/30 dark:via-pink-900/30 dark:to-purple-900/30' : colorTheme === 'blue' ? 'from-blue-50 via-sky-50 to-blue-50 dark:from-blue-900/30 dark:via-sky-900/30 dark:to-blue-900/30' : 'from-green-50 via-emerald-50 to-teal-50 dark:from-green-900/30 dark:via-emerald-900/30 dark:to-teal-900/30'} rounded-xl shadow-2xl p-6 border-2 ${colorTheme === 'purple' ? 'border-purple-300 dark:border-purple-700' : colorTheme === 'blue' ? 'border-blue-300 dark:border-blue-700' : 'border-green-300 dark:border-green-700'}`}>
                        <div className="flex items-center mb-5">
                          <span className="text-3xl mr-3">📊</span>
                          <h3 className={`text-2xl font-bold ${colorTheme === 'purple' ? 'text-purple-800 dark:text-purple-200' : colorTheme === 'blue' ? 'text-blue-800 dark:text-blue-200' : 'text-green-800 dark:text-green-200'}`}>
                            Consolidated Feedback Summary
                          </h3>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                          <div className="text-center p-5 bg-white dark:bg-gray-800 rounded-xl shadow-lg border-2 border-yellow-200 dark:border-yellow-700">
                            <div className={`text-4xl font-bold ${colorTheme === 'purple' ? 'text-purple-600' : colorTheme === 'blue' ? 'text-blue-600' : 'text-green-600'} dark:text-yellow-400 mb-2`}>
                              {feedbackData.statistics.total_feedback}
                            </div>
                            <div className="text-xs font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                              Total Reviews
                            </div>
                            {feedbackData.statistics.breakdown && (
                              <div className="text-xs text-gray-500 dark:text-gray-400 mt-2 font-medium">
                                👤{feedbackData.statistics.breakdown.user_feedback_count} + 🤖{feedbackData.statistics.breakdown.ai_feedback_count}
                              </div>
                            )}
                          </div>
                          
                          <div className={`text-center p-5 bg-white dark:bg-gray-800 rounded-xl shadow-lg border-2 ${colorTheme === 'purple' ? 'border-purple-200 dark:border-purple-700' : 'border-green-200 dark:border-green-700'}`}>
                            <div className="flex items-center justify-center space-x-1 mb-2">
                              <span className={`text-4xl font-bold ${colorTheme === 'purple' ? 'text-purple-600 dark:text-purple-400' : colorTheme === 'blue' ? 'text-blue-600 dark:text-blue-400' : 'text-green-600 dark:text-green-400'}`}>
                                {feedbackData.statistics.average_rating ? feedbackData.statistics.average_rating.toFixed(1) : '0.0'}
                              </span>
                              <span className="text-2xl text-yellow-500">⭐</span>
                            </div>
                            <div className="text-xs font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                              Combined Rating
                            </div>
                          </div>
                          
                          {feedbackData.statistics.breakdown && feedbackData.statistics.breakdown.user_feedback_count > 0 && (
                            <div className="text-center p-5 bg-white dark:bg-gray-800 rounded-xl shadow-lg border-2 border-blue-200 dark:border-blue-700">
                              <div className="text-3xl font-bold text-blue-600 dark:text-blue-400 mb-2">
                                {feedbackData.statistics.breakdown.user_stats.average_rating ? feedbackData.statistics.breakdown.user_stats.average_rating.toFixed(1) : '0.0'}
                              </div>
                              <div className="text-xs font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                                👤 User Rating
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400 mt-2 font-medium">
                                {feedbackData.statistics.breakdown.user_feedback_count} reviews
                              </div>
                            </div>
                          )}
                          
                          {feedbackData.statistics.breakdown && feedbackData.statistics.breakdown.ai_feedback_count > 0 && (
                            <div className="text-center p-5 bg-white dark:bg-gray-800 rounded-xl shadow-lg border-2 border-purple-200 dark:border-purple-700">
                              <div className="text-3xl font-bold text-purple-600 dark:text-purple-400 mb-2">
                                {feedbackData.statistics.breakdown.ai_stats.average_rating ? feedbackData.statistics.breakdown.ai_stats.average_rating.toFixed(1) : '0.0'}
                              </div>
                              <div className="text-xs font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                                🤖 AI Rating
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400 mt-2 font-medium">
                                {feedbackData.statistics.breakdown.ai_feedback_count} reviews
                              </div>
                            </div>
                          )}
                        </div>
                        
                        {/* Key Improvement Suggestions */}
                        {feedbackData.statistics.consolidated_suggestions && feedbackData.statistics.consolidated_suggestions.length > 0 && (
                          <div className={`mb-5 p-5 bg-white dark:bg-gray-800 rounded-xl shadow-md border-2 ${colorTheme === 'purple' ? 'border-purple-300 dark:border-purple-700' : colorTheme === 'blue' ? 'border-blue-300 dark:border-blue-700' : 'border-green-300 dark:border-green-700'}`}>
                            <h4 className={`text-base font-bold ${colorTheme === 'purple' ? 'text-purple-800 dark:text-purple-200' : colorTheme === 'blue' ? 'text-blue-800 dark:text-blue-200' : 'text-green-800 dark:text-green-200'} mb-3 flex items-center`}>
                              <span className="mr-2 text-xl">💡</span>
                              Key Improvement Suggestions
                            </h4>
                            <ul className="space-y-2">
                              {feedbackData.statistics.consolidated_suggestions.slice(0, 3).map((suggestion, index) => (
                                <li key={index} className="text-sm text-gray-700 dark:text-gray-300 flex items-start">
                                  <span className={`${colorTheme === 'purple' ? 'text-purple-600 dark:text-purple-400' : colorTheme === 'blue' ? 'text-blue-600 dark:text-blue-400' : 'text-green-600 dark:text-green-400'} mr-2 font-bold`}>{index + 1}.</span>
                                  <span>{suggestion}</span>
                                </li>
                              ))}
                            </ul>
                            {feedbackData.statistics.consolidated_suggestions.length > 3 && (
                              <p className={`text-xs ${colorTheme === 'purple' ? 'text-purple-600 dark:text-purple-400' : colorTheme === 'blue' ? 'text-blue-600 dark:text-blue-400' : 'text-green-600 dark:text-green-400'} mt-2 font-medium`}>
                                ...and {feedbackData.statistics.consolidated_suggestions.length - 3} more suggestions
                              </p>
                            )}
                          </div>
                        )}
                        
                        {/* Rating Distribution for Combined Data */}
                        <div className="p-5 bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700">
                          <div className="text-base font-bold text-gray-900 dark:text-white mb-4 flex items-center">
                            <span className="mr-2">📊</span>
                            Combined Rating Distribution
                          </div>
                          <div className="space-y-1">
                            {[5, 4, 3, 2, 1].map((star) => (
                              <div key={star} className="flex items-center space-x-2">
                                <span className="text-xs w-8">{star}⭐</span>
                                <div className="flex-grow bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                  <div
                                    className={`${colorTheme === 'purple' ? 'bg-purple-500' : colorTheme === 'blue' ? 'bg-blue-500' : 'bg-green-500'} h-2 rounded-full`}
                                    style={{
                                      width: feedbackData.statistics.total_feedback > 0 
                                        ? `${(feedbackData.statistics.rating_distribution[star] / feedbackData.statistics.total_feedback) * 100}%`
                                        : '0%'
                                    }}
                                  ></div>
                                </div>
                                <span className="text-xs w-6 text-gray-600 dark:text-gray-400">
                                  {feedbackData.statistics.rating_distribution[star]}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Feedback Tabs and Content */}
                      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6">
                        {/* Tab Navigation */}
                        <div className="flex border-b border-gray-200 dark:border-gray-600 mb-6">
                          <button
                            onClick={() => setActiveTab('user')}
                            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                              activeTab === 'user'
                                ? 'border-yellow-500 text-green-600 dark:text-yellow-400'
                                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                            }`}
                          >
                            👥 User Feedback ({feedbackData.feedback ? feedbackData.feedback.length : 0})
                          </button>
                          <button
                            onClick={() => setActiveTab('ai')}
                            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ml-6 ${
                              activeTab === 'ai'
                                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                            }`}
                          >
                            🤖 AI Feedback ({aiFeedbackData?.feedback?.length || 0})
                          </button>
                        </div>

                        {/* Tab Content */}
                        {activeTab === 'user' ? (
                          <div>
                            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                              💬 User Reviews & Feedback
                            </h3>
                            
                            <div className="space-y-4 max-h-96 overflow-y-auto">
                              {feedbackData.feedback.map((comment, index) => (
                                <div 
                                  key={comment.id} 
                                  className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4"
                                  data-feedback-item={index === 0 ? 'first' : undefined}
                                >
                                  <div className="flex items-start space-x-3">
                                    <div className="flex-shrink-0">
                                      <div className={`w-8 h-8 bg-gradient-to-r ${colorTheme === 'purple' ? 'from-purple-400 to-pink-500' : colorTheme === 'blue' ? 'from-blue-400 to-sky-500' : 'from-yellow-400 to-orange-500'} rounded-full flex items-center justify-center`}>
                                        <span className="text-white font-bold text-sm">
                                          {(comment.author_name || 'Anonymous').charAt(0).toUpperCase()}
                                        </span>
                                      </div>
                                    </div>
                                    <div className="flex-grow">
                                      <div className="flex items-center space-x-2 mb-1">
                                        <span className="font-semibold text-gray-900 dark:text-white">
                                          {comment.author_name || 'Anonymous'}
                                        </span>
                                        <StarRating rating={comment.rating} readOnly size="small" />
                                        <span className="text-xs text-gray-500 dark:text-gray-400">
                                          {new Date(comment.created_at).toLocaleDateString()}
                                        </span>
                                      </div>
                                      <p className="text-gray-700 dark:text-gray-300 text-sm whitespace-pre-wrap">
                                        {comment.comment}
                                      </p>
                                      {comment.replies && comment.replies.length > 0 && (
                                        <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                                          {comment.replies.length} {comment.replies.length === 1 ? 'reply' : 'replies'}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <div>
                            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center">
                              🤖 AI-Generated Feedback & Analysis
                              <span className="ml-3 text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                                AI Insights
                              </span>
                            </h3>

                            {isAiFeedbackLoading ? (
                              <div className="flex items-center justify-center py-8">
                                <div className={`animate-spin rounded-full h-6 w-6 border-b-2 ${colorTheme === 'purple' ? 'border-purple-600' : colorTheme === 'blue' ? 'border-blue-600' : 'border-green-600'}`}></div>
                                <span className="ml-3 text-gray-600 dark:text-gray-400">Loading AI feedback...</span>
                              </div>
                            ) : aiFeedbackData ? (
                              <div className="space-y-6">
                                {/* AI Statistics */}
                                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-600">
                                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    <div className="text-center">
                                      <div className="text-2xl font-bold text-blue-600">
                                        {aiFeedbackData.statistics?.total_feedback || 0}
                                      </div>
                                      <div className="text-sm text-blue-700 dark:text-blue-300">AI Reviews</div>
                                    </div>
                                    <div className="text-center">
                                      <div className="text-2xl font-bold text-blue-600">
                                        {aiFeedbackData.statistics?.average_rating || 0}/5
                                      </div>
                                      <div className="text-sm text-blue-700 dark:text-blue-300">AI Rating</div>
                                    </div>
                                    <div className="text-center">
                                      <div className="text-2xl font-bold text-green-600">
                                        {Math.round((aiFeedbackData.statistics?.sentiment_distribution?.positive || 0) / (aiFeedbackData.statistics?.total_feedback || 1) * 100)}%
                                      </div>
                                      <div className="text-sm text-blue-700 dark:text-blue-300">Positive</div>
                                    </div>
                                    <div className="text-center">
                                      <div className="text-2xl font-bold text-purple-600">
                                        {aiFeedbackData.consolidated_suggestions?.length || 0}
                                      </div>
                                      <div className="text-sm text-blue-700 dark:text-blue-300">Improvements</div>
                                    </div>
                                  </div>
                                </div>

                                {/* AI Improvements */}
                                {aiFeedbackData.consolidated_suggestions && aiFeedbackData.consolidated_suggestions.length > 0 && (
                                  <div className="bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 border border-green-200 dark:border-green-600 rounded-lg p-4">
                                    <h4 className="text-lg font-semibold text-green-800 dark:text-green-200 mb-3">
                                      💡 AI Improvement Recommendations
                                    </h4>
                                    <div className="space-y-2">
                                      {aiFeedbackData.consolidated_suggestions.map((suggestion, idx) => (
                                        <div key={idx} className="flex items-start text-sm">
                                          <span className="text-green-600 font-bold mr-2">{idx + 1}.</span>
                                          <span className="text-green-800 dark:text-green-200">{suggestion}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* AI Feedback List */}
                                <div className="space-y-3 max-h-80 overflow-y-auto">
                                  {aiFeedbackData.feedback && aiFeedbackData.feedback.map((feedback, index) => (
                                    <div key={index} className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-600">
                                      <div className="flex items-start space-x-3">
                                        <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
                                          <span className="text-xl">
                                            {feedback.user_type === 'enthusiast' ? '😍' : 
                                             feedback.user_type === 'skeptical' ? '🤔' : 
                                             feedback.user_type === 'expert' ? '🎓' : 
                                             feedback.user_type === 'critic' ? '😐' : '👤'}
                                          </span>
                                        </div>
                                        <div className="flex-grow">
                                          <div className="flex items-center space-x-2 mb-2">
                                            <span className="font-semibold text-blue-800 dark:text-blue-200 capitalize text-sm">
                                              {feedback.user_type} User
                                            </span>
                                            <StarRating rating={feedback.rating} readOnly size="small" />
                                            <span className="text-xs text-blue-600 dark:text-blue-400">
                                              ({feedback.rating}/5)
                                            </span>
                                            <span className={`text-xs px-2 py-1 rounded-full ${
                                              feedback.sentiment === 'positive' ? 'bg-green-100 text-green-800' :
                                              feedback.sentiment === 'negative' ? 'bg-red-100 text-red-800' :
                                              'bg-yellow-100 text-yellow-800'
                                            }`}>
                                              {feedback.sentiment}
                                            </span>
                                            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                              AI Generated
                                            </span>
                                          </div>
                                          <p className="text-blue-800 dark:text-blue-200 text-sm leading-relaxed">
                                            {feedback.comment}
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>

                                <div className="text-xs text-blue-600 dark:text-blue-400 text-center bg-blue-50 dark:bg-blue-900/20 p-3 rounded">
                                  Generated on {new Date(aiFeedbackData.saved_at).toLocaleDateString()} using {aiFeedbackData.generation_info?.model_used || 'AI'}
                                </div>
                              </div>
                            ) : (
                              <div className="text-center py-8">
                                <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
                                  <span className="text-3xl">🤖</span>
                                </div>
                                <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                                  No AI Feedback Available
                                </h4>
                                <p className="text-gray-500 dark:text-gray-400 text-sm">
                                  AI feedback has not been generated for this campaign yet.
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8 text-center">
                      <div className="text-6xl mb-4">💭</div>
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                        No Feedback Yet
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400 mb-4">
                        This campaign hasn't received any feedback yet. Share the public link to start gathering insights!
                      </p>
                      <button
                        onClick={() => window.open(feedbackData.campaign_info?.share_url, '_blank')}
                        className={`bg-gradient-to-r ${colorTheme === 'purple' ? 'from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700' : colorTheme === 'blue' ? 'from-blue-600 to-sky-500 hover:from-blue-700 hover:to-sky-600' : 'from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700'} text-white font-bold py-2 px-4 rounded-lg`}
                      >
                        🔗 Share Campaign
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8 text-center">
                  <div className="text-6xl mb-4">⚠️</div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                    Error Loading Feedback
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    {error || 'Unable to load feedback data. Please try again.'}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ==========================================
// ⚙️ Background Generation Settings Component (Admin)
// ==========================================
const BackgroundGenerationSettings = ({ sessionToken }) => {
  const { colorTheme } = useColorTheme();
  const [settings, setSettings] = useState({
    background_generation_enabled: false,
    notification_preferences: {
      campaign_complete: true,
      campaign_failed: true
    }
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/system-settings/background-generation`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleChange = async (field, value) => {
    setIsSaving(true);
    setSaveMessage('');

    try {
      const updateData = {};
      if (field === 'background_generation_enabled') {
        updateData.background_generation_enabled = value;
      } else {
        updateData.notification_preferences = {
          ...settings.notification_preferences,
          [field]: value
        };
      }

      await axios.put(
        `${API}/admin/system-settings/background-generation`,
        updateData,
        { headers: { Authorization: `Bearer ${sessionToken}` } }
      );

      // Update local state
      if (field === 'background_generation_enabled') {
        setSettings(prev => ({ ...prev, background_generation_enabled: value }));
      } else {
        setSettings(prev => ({
          ...prev,
          notification_preferences: {
            ...prev.notification_preferences,
            [field]: value
          }
        }));
      }

      setSaveMessage('✅ Settings saved successfully!');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error) {
      console.error('Error saving settings:', error);
      setSaveMessage('❌ Failed to save settings');
      setTimeout(() => setSaveMessage(''), 3000);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="text-center py-8">
        <div className={`animate-spin rounded-full h-8 w-8 border-b-2 ${colorTheme === 'purple' ? 'border-purple-600' : colorTheme === 'blue' ? 'border-blue-600' : 'border-green-600'} mx-auto mb-2`}></div>
        <p className="text-gray-600 dark:text-gray-400 text-sm">Loading settings...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Save Message */}
      {saveMessage && (
        <div className={`p-4 rounded-lg ${
          saveMessage.includes('✅') 
            ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 text-green-800 dark:text-green-200'
            : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 text-red-800 dark:text-red-200'
        }`}>
          {saveMessage}
        </div>
      )}

      {/* Campaign Generation Settings */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 border-2 border-yellow-200 dark:border-yellow-700">
          <div className="flex items-center mb-6">
            <span className="text-3xl mr-3">🚀</span>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                Campaign Generation
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Choose how you want campaigns to be generated
              </p>
            </div>
          </div>

          {/* Background Generation Toggle */}
          <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
            <div className="flex items-center justify-between">
              <div className="flex-1 pr-4">
                <div className="flex items-center mb-2">
                  <span className="text-xl mr-2">⚡</span>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Background Generation
                  </h3>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                  Generate campaigns in the background while you continue working. You'll receive a notification when your campaign is ready.
                </p>
                <div className="flex items-start space-x-2 text-xs text-gray-500 dark:text-gray-400 mt-3">
                  <div className="flex-1 space-y-1">
                    <div className="flex items-center">
                      <span className="mr-2">✅</span>
                      <span>Continue using the app during generation</span>
                    </div>
                    <div className="flex items-center">
                      <span className="mr-2">🔔</span>
                      <span>Get notified when campaign is ready</span>
                    </div>
                  </div>
                  <div className="flex-1 space-y-1">
                    <div className="flex items-center">
                      <span className="mr-2">📊</span>
                      <span>Track progress from dashboard</span>
                    </div>
                    <div className="flex items-center">
                      <span className="mr-2">⏱️</span>
                      <span>No waiting screens</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Toggle Switch */}
              <button
                onClick={() => handleToggleChange('background_generation_enabled', !settings.background_generation_enabled)}
                disabled={isSaving}
                className={`relative inline-flex h-10 w-20 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 ${
                  settings.background_generation_enabled 
                    ? 'bg-gradient-to-r from-green-500 to-emerald-500' 
                    : 'bg-gray-300 dark:bg-gray-600'
                } ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <span
                  className={`pointer-events-none inline-block h-9 w-9 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out ${
                    settings.background_generation_enabled ? 'translate-x-10' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>

            {/* Status Indicator */}
            <div className="mt-4 pt-4 border-t border-blue-200 dark:border-blue-700">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Current mode:</span>
                <span className={`font-semibold px-3 py-1 rounded-full ${
                  settings.background_generation_enabled
                    ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                }`}>
                  {settings.background_generation_enabled 
                    ? '⚡ Background Generation' 
                    : '⏳ Show Loading Screen'}
                </span>
              </div>
            </div>
        </div>
      </div>

      {/* Notification Preferences */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 border-2 border-purple-200 dark:border-purple-700">
          <div className="flex items-center mb-6">
            <span className="text-3xl mr-3">🔔</span>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                Notification Preferences
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Choose which notifications you want to receive
              </p>
            </div>
          </div>

          <div className="space-y-4">
            {/* Campaign Complete Notification */}
            <div className="flex items-center justify-between p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-700">
              <div className="flex items-center">
                <span className="text-2xl mr-3">✅</span>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white">Campaign Complete</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Notify me when my campaign generation is complete
                  </p>
                </div>
              </div>
              <button
                onClick={() => handleToggleChange('campaign_complete', !settings.notification_preferences.campaign_complete)}
                disabled={isSaving}
                className={`relative inline-flex h-8 w-16 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${
                  settings.notification_preferences.campaign_complete 
                    ? (colorTheme === 'purple' ? 'bg-purple-500' : colorTheme === 'blue' ? 'bg-blue-500' : 'bg-green-500')
                    : 'bg-gray-300 dark:bg-gray-600'
                } ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <span
                  className={`pointer-events-none inline-block h-7 w-7 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    settings.notification_preferences.campaign_complete ? 'translate-x-8' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>

            {/* Campaign Failed Notification */}
            <div className="flex items-center justify-between p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-700">
              <div className="flex items-center">
                <span className="text-2xl mr-3">❌</span>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white">Campaign Failed</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Notify me if campaign generation encounters an error
                  </p>
                </div>
              </div>
              <button
                onClick={() => handleToggleChange('campaign_failed', !settings.notification_preferences.campaign_failed)}
                disabled={isSaving}
                className={`relative inline-flex h-8 w-16 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${
                  settings.notification_preferences.campaign_failed 
                    ? 'bg-red-500' 
                    : 'bg-gray-300 dark:bg-gray-600'
                } ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <span
                  className={`pointer-events-none inline-block h-7 w-7 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    settings.notification_preferences.campaign_failed ? 'translate-x-8' : 'translate-x-0'
                  }`}
                />
              </button>
          </div>
        </div>
      </div>

      {/* Info Box */}
      <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg">
        <div className="flex items-start">
          <span className="text-2xl mr-3">💡</span>
          <div>
            <h3 className="font-semibold text-yellow-800 dark:text-yellow-200 mb-1">Pro Tip</h3>
            <p className="text-sm text-yellow-700 dark:text-yellow-300">
              Enable background generation to allow users to work more efficiently! They can create multiple campaigns, explore other features, or take a break while campaigns are being generated. Users will be notified when campaigns are ready.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==========================================
// 🎨 Poster Builder Visibility Settings Component (Admin)
// ==========================================
const PosterBuilderVisibilitySettings = ({ sessionToken }) => {
  const [enabled, setEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await axios.get(`${API}/admin/system-settings/poster-builder`, {
          headers: { Authorization: `Bearer ${sessionToken}` }
        });
        if (!cancelled) setEnabled(!!resp.data?.poster_builder_enabled);
      } catch (e) {
        console.error('Failed to load poster builder setting:', e);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [sessionToken]);

  const handleToggle = async (next) => {
    setIsSaving(true);
    setSaveMessage('');
    try {
      await axios.put(
        `${API}/admin/system-settings/poster-builder`,
        { poster_builder_enabled: next },
        { headers: { Authorization: `Bearer ${sessionToken}` } }
      );
      setEnabled(next);
      setSaveMessage(`✅ Poster Builder is now ${next ? 'VISIBLE' : 'HIDDEN'} for non-admin users`);
      setTimeout(() => setSaveMessage(''), 3500);
    } catch (e) {
      console.error('Failed to update poster builder setting:', e);
      setSaveMessage('❌ Failed to update. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 border-2 border-emerald-200 dark:border-emerald-700">
        <p className="text-gray-500 dark:text-gray-400 text-sm">Loading Poster Builder setting...</p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 border-2 border-emerald-200 dark:border-emerald-700">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white">
            Show Poster Builder tab to users
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            When <strong>OFF</strong>, regular users won't see the Poster Builder tab in the left sidebar.
            Admins will continue to see it (marked as <code className="px-1 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-xs">BETA</code>) so you can keep iterating.
          </p>
        </div>
        <button
          onClick={() => handleToggle(!enabled)}
          disabled={isSaving}
          className={`relative inline-flex h-9 w-20 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${
            enabled ? 'bg-emerald-600' : 'bg-gray-300 dark:bg-gray-600'
          } ${isSaving ? 'opacity-50 cursor-wait' : ''}`}
          data-testid="poster-builder-visibility-toggle"
          aria-label="Toggle Poster Builder visibility"
        >
          <span className={`pointer-events-none inline-block h-8 w-8 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out ${enabled ? 'translate-x-11' : 'translate-x-0'}`} />
        </button>
      </div>

      <div className="mt-4 pt-4 border-t border-emerald-100 dark:border-emerald-700/50 flex items-center justify-between">
        <span className="text-sm text-gray-600 dark:text-gray-400">Current status:</span>
        <span className={`font-semibold px-3 py-1 rounded-full text-sm ${
          enabled
            ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
            : 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
        }`} data-testid="poster-builder-visibility-status">
          {enabled ? '👁️ Visible to all users' : '🙈 Hidden (admins only)'}
        </span>
      </div>

      {saveMessage && (
        <p className="mt-3 text-sm text-gray-700 dark:text-gray-300" data-testid="poster-builder-save-message">{saveMessage}</p>
      )}
    </div>
  );
};


// ==========================================
// Confetti Component
// ==========================================
const Confetti = ({ trigger }) => {
  const [pieces, setPieces] = useState([]);

  useEffect(() => {
    if (!trigger) return;

    const colors = ['#9333ea', '#ec4899', '#8b5cf6', '#a855f7', '#d946ef', '#f59e0b', '#10b981'];
    const newPieces = [];
    
    for (let i = 0; i < 50; i++) {
      newPieces.push({
        id: Date.now() + i,
        left: Math.random() * 100,
        backgroundColor: colors[Math.floor(Math.random() * colors.length)],
        delay: Math.random() * 0.5,
      });
    }
    
    setPieces(newPieces);
    
    // Clear confetti after animation
    setTimeout(() => setPieces([]), 3000);
  }, [trigger]);

  return (
    <>
      {pieces.map((piece) => (
        <div
          key={piece.id}
          className="confetti-piece"
          style={{
            left: `${piece.left}%`,
            backgroundColor: piece.backgroundColor,
            animationDelay: `${piece.delay}s`,
          }}
        />
      ))}
    </>
  );
};

// ==========================================
// Animated Counter Component
// ==========================================
const AnimatedCounter = ({ value, duration = 1000 }) => {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    if (hasAnimated || value === 0) {
      setCount(value);
      return;
    }

    setHasAnimated(true);
    let startTime;
    const startValue = 0;
    const endValue = value;

    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      
      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const currentCount = Math.floor(startValue + (endValue - startValue) * easeOutQuart);
      
      setCount(currentCount);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setCount(endValue);
      }
    };

    requestAnimationFrame(animate);
  }, [value, duration, hasAnimated]);

  return <span className="animate-countUp">{count}</span>;
};

// ==========================================

const Dashboard = ({ onViewCampaign, onCreateNewCampaign, onBackToMain, refreshTrigger, showTitle = true, limit = null, hideAnalytics = false, currentView = 'dashboard', onEditCampaign, onRegenerateCampaign }) => {
  const { sessionToken, user } = useAuth();
  const { colorTheme } = useColorTheme();
  const [campaigns, setCampaigns] = useState([]);
  const [filteredCampaigns, setFilteredCampaigns] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showConfetti, setShowConfetti] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCampaigns, setTotalCampaigns] = useState(0);
  const campaignsPerPage = 15;
  
  // Feedback type selection modal state
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [selectedFeedbackCampaign, setSelectedFeedbackCampaign] = useState(null);
  
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('campaigns'); // 'campaigns' or 'analytics'
  
  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    category: '',
    content_type: '',
    gender: '',
    date_from: '',
    date_to: '',
    min_age: '',
    max_age: ''
  });
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [availableFilters, setAvailableFilters] = useState({
    categories: [],
    content_types: [],
    genders: []
  });
  const [showFilters, setShowFilters] = useState(false);

  // Bulk actions state
  const [selectedCampaigns, setSelectedCampaigns] = useState([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  
  // Edit campaign state
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  
  // Duplicate campaign state
  const [duplicatingCampaign, setDuplicatingCampaign] = useState(null);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  
  // Sharing state
  const [sharingCampaign, setSharingCampaign] = useState(null);
  const [showSharingModal, setShowSharingModal] = useState(false);
  
  // AI Feedback state
  const [aiFeedbackCampaign, setAiFeedbackCampaign] = useState(null);
  const [showAIFeedbackModal, setShowAIFeedbackModal] = useState(false);
  const [campaignsWithSavedFeedback, setCampaignsWithSavedFeedback] = useState(new Set());
  
  // Feedback statistics state
  const [campaignFeedbackStats, setCampaignFeedbackStats] = useState({});
  
  // Publishing status state
  const [campaignPublishingStatus, setCampaignPublishingStatus] = useState({});
  const [savedFeedbackData, setSavedFeedbackData] = useState(null);
  const [showSavedFeedbackModal, setShowSavedFeedbackModal] = useState(false);
  const [sharingLoading, setSharingLoading] = useState(false);
  
  // Telegram sharing state
  const [showTelegramSharingModal, setShowTelegramSharingModal] = useState(false);
  const [telegramSharingCampaign, setTelegramSharingCampaign] = useState(null);
  const [telegramSharingType, setTelegramSharingType] = useState('campaign'); // 'campaign' or 'individual'
  
  // WhatsApp sharing state
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [whatsappSharingCampaign, setWhatsappSharingCampaign] = useState(null);
  const [selectedAd, setSelectedAd] = useState(null);
  const [telegramSharingStatus, setTelegramSharingStatus] = useState({});
  
  // Generic multi-channel sharing state
  const [showChannelSelectionModal, setShowChannelSelectionModal] = useState(false);
  const [currentSharingCampaign, setCurrentSharingCampaign] = useState(null);
  const [availableChannels, setAvailableChannels] = useState([]);
  
  // Primary channel state for Dashboard
  const [showPrimaryChannelModal, setShowPrimaryChannelModal] = useState(false);
  const [primaryChannel, setPrimaryChannel] = useState(null);
  
  // Export state
  const [isExporting, setIsExporting] = useState(false);
  
  // Success celebration state
  const [successCelebration, setSuccessCelebration] = useState({
    isOpen: false,
    title: '',
    message: '',
    celebrationType: 'success',
    actionButton: null
  });

  // Functional Alert modal state for Dashboard
  const [functionalAlert, setFunctionalAlert] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'warning',
    icon: null,
    actionButton: null,
    secondaryButton: null
  });

  // URL Copy modal state for Dashboard
  const [urlCopyModal, setUrlCopyModal] = useState({
    isOpen: false,
    url: '',
    title: 'Link Copied!',
    description: 'Share this link with others to get feedback on your campaign:'
  });

  // PERFORMANCE OPTIMIZATION: Single useEffect to manage all campaign fetching (prevents duplicate API calls)
  useEffect(() => {
    if (!sessionToken) {
      console.log('No session token, skipping campaigns fetch');
      return;
    }
    
    console.log('Fetching campaigns due to dependency change:', {
      currentPage,
      searchTerm: searchTerm?.length || 0,
      hasFilters: Object.keys(filters).length > 0,
      sortBy,
      sortOrder
    });
    
    // Single fetch call with all current parameters
    fetchCampaigns(currentPage);
    
    // Fetch filters only once when session starts
    if (refreshTrigger === 0) {
      fetchAvailableFilters();
    }
    
    // Set up global function to refresh dashboard (lighter weight)
    window.refreshSavedFeedbackIndicators = () => {
      fetchCampaigns(currentPage); // Refresh with current page
    };
    
    return () => {
      // Cancel any ongoing fetch when dependencies change
      if (fetchControllerRef.current) {
        fetchControllerRef.current.abort();
      }
      // Clean up global function
      delete window.refreshSavedFeedbackIndicators;
    };
  }, [sessionToken, currentPage, searchTerm, filters, sortBy, sortOrder, refreshTrigger]); // Consolidated dependencies
  
  // Auto-polling: Refresh campaigns every 10 seconds if any are generating
  useEffect(() => {
    const hasGeneratingCampaigns = campaigns.some(c => 
      c.generation_status === 'queued' || c.generation_status === 'generating'
    );
    
    if (hasGeneratingCampaigns) {
      const pollInterval = setInterval(() => {
        console.log('🔄 Auto-refreshing campaigns (generating campaigns detected)');
        fetchCampaigns(currentPage);
      }, 10000); // Poll every 10 seconds
      
      return () => clearInterval(pollInterval);
    }
  }, [campaigns, currentPage]);

  // Use ref to store AbortController for cleanup
  const fetchControllerRef = useRef(null);
  
  const fetchCampaigns = async (page = 1) => {
    if (!sessionToken) {
      console.log('No session token available, skipping campaigns fetch');
      return;
    }
    
    // Cancel any ongoing fetch to prevent duplicate calls (only if older than 1 second)
    if (fetchControllerRef.current) {
      fetchControllerRef.current.abort();
    }
    
    // Create new AbortController for this fetch
    fetchControllerRef.current = new AbortController();
    
    try {
      // Build query parameters with pagination
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', campaignsPerPage.toString());
      
      if (searchTerm) params.append('search', searchTerm);
      if (filters.category) params.append('category', filters.category);
      if (filters.content_type) params.append('content_type', filters.content_type);
      if (filters.gender) params.append('gender', filters.gender);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      if (filters.min_age) params.append('min_age', filters.min_age);
      if (filters.max_age) params.append('max_age', filters.max_age);
      params.append('sort_by', sortBy);
      params.append('sort_order', sortOrder);
      
      const response = await axios.get(`${API}/campaigns?${params}`, {
        headers: { Authorization: `Bearer ${sessionToken}` },
        signal: fetchControllerRef.current.signal
      });
      
      // Handle paginated response
      let campaignsData = response.data;
      let totalCount = 0;
      
      console.log('🔍 DEBUG: Full response data structure:', JSON.stringify(response.data, null, 2));
      
      // Check if backend returns paginated structure
      if (response.data && response.data.campaigns && Array.isArray(response.data.campaigns)) {
        campaignsData = response.data.campaigns;
        totalCount = response.data.total_count || campaignsData.length;
        console.log(`✅ Paginated format: Found ${campaignsData.length} campaigns, total_count: ${totalCount}`);
      } else if (Array.isArray(response.data)) {
        campaignsData = response.data;
        totalCount = campaignsData.length;
      }
      
      // Check for newly completed campaigns (trigger confetti)
      const newlyCompleted = campaignsData.filter(c => 
        c.generation_status === 'completed' && 
        !campaigns.find(oldC => oldC.id === c.id && oldC.generation_status === 'completed')
      );
      
      if (newlyCompleted.length > 0) {
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 100);
      }
      
      setCampaigns(campaignsData);
      setFilteredCampaigns(campaignsData);
      setTotalCampaigns(totalCount);
      setCurrentPage(page);
      
      console.log(`Page ${page}: Loaded ${campaignsData.length} campaigns, Total: ${totalCount}`);
      
      // PERFORMANCE OPTIMIZATION: Process embedded stats from backend response (no extra API calls!)
      if (campaignsData.length > 0) {
        console.log(`Processing embedded stats for ${campaignsData.length} campaigns (no additional API calls)`);
        
        // DEBUG: Check if backend is returning embedded stats
        console.log('Sample campaign data structure:', {
          hasFeedbackStats: !!campaignsData[0]?.feedback_stats,
          hasPublishingStats: !!campaignsData[0]?.publishing_stats,
          sampleFeedbackStats: campaignsData[0]?.feedback_stats,
          samplePublishingStats: campaignsData[0]?.publishing_stats
        });
        
        // Process embedded feedback stats
        const feedbackStatsMap = {};
        const publishingStatsMap = {};
        
        campaignsData.forEach(campaign => {
          if (campaign.feedback_stats) {
            feedbackStatsMap[campaign.id] = campaign.feedback_stats;
          }
          if (campaign.publishing_stats) {
            publishingStatsMap[campaign.id] = campaign.publishing_stats;
          }
        });
        
        // Update state with embedded stats (instant, no loading delay)
        if (Object.keys(feedbackStatsMap).length > 0) {
          setCampaignFeedbackStats(feedbackStatsMap);
        }
        if (Object.keys(publishingStatsMap).length > 0) {
          setCampaignPublishingStatus(publishingStatsMap);
        }
        
        console.log(`Processed stats for ${Object.keys(feedbackStatsMap).length} campaigns instantly`);
        
        // Always call fetchFeedbackStats to ensure we have the latest feedback data
        console.log('🔄 Fetching latest feedback stats for campaigns...');
        fetchFeedbackStats(campaignsData);
        
        // Also fetch publishing status
        console.log('🔄 Fetching publishing status for campaigns...');
        fetchPublishingStatus(campaignsData);
      } else {
        setCampaigns([]);
        setFilteredCampaigns([]);
      }
    } catch (err) {
      // Don't show error for cancelled requests (prevents duplicate call errors)
      if (err.name !== 'AbortError' && err.name !== 'CanceledError') {
        setError('Failed to load campaigns');
        console.error('Error fetching campaigns:', err);
        setCampaigns([]);
        setFilteredCampaigns([]);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Check which campaigns have saved AI feedback
  const checkSavedAIFeedback = async (campaigns) => {
    if (!sessionToken || !campaigns?.length) return;

    const campaignsWithFeedback = new Set();
    
    try {
      // Process campaigns in batches to avoid overwhelming the API
      const batchSize = 5;
      for (let i = 0; i < campaigns.length; i += batchSize) {
        const batch = campaigns.slice(i, i + batchSize);
        
        await Promise.all(batch.map(async (campaign) => {
          try {
            const response = await axios.get(
              `${API}/campaigns/${campaign.id}/ai-feedback/saved`,
              { 
                headers: { 
                  Authorization: `Bearer ${sessionToken}`,
                  'Cache-Control': 'no-cache, no-store, must-revalidate',
                  'Pragma': 'no-cache'
                },
                timeout: 5000 // Add timeout to prevent hanging requests
              }
            );
            
            if (response.status === 200 && response.data) {
              campaignsWithFeedback.add(campaign.id);
            }
          } catch (error) {
            // 404 means no saved feedback - this is normal and expected
            // Only log unexpected errors
            if (error.response?.status !== 404) {
              console.warn(`Unexpected error checking saved feedback for campaign ${campaign.id}:`, error.message);
            }
          }
        }));
      }
    } catch (error) {
      console.warn('Error in checkSavedAIFeedback batch processing:', error);
    }
    
    setCampaignsWithSavedFeedback(campaignsWithFeedback);
  };

  // Fetch publishing status for dashboard campaigns
  const fetchPublishingStatus = async (campaigns) => {
    if (!sessionToken || !campaigns?.length) {
      return;
    }

    console.log(`fetchPublishingStatus: Starting for ${campaigns.length} campaigns`);
    const statusMap = {};
    
    try {
      // Process first 15 campaigns in parallel batches for better performance
      const campaignsToCheck = campaigns.slice(0, 15);
      const batchSize = 8;
      
      console.log(`Processing ${campaignsToCheck.length} campaigns for publishing status in parallel`);
      
      for (let i = 0; i < campaignsToCheck.length; i += batchSize) {
        const batch = campaignsToCheck.slice(i, i + batchSize);
        
        // Process batch in parallel
        const promises = batch.map(async (campaign) => {
          try {
            const response = await axios.get(
              `${API}/share/history/${campaign.id}`,
              { 
                headers: { Authorization: `Bearer ${sessionToken}` },
                timeout: 3000 // Reduced timeout
              }
            );
            
            if (response.status === 200 && response.data) {
              const data = response.data;
              const channelCounts = {};
              
              // Count successful shares by channel type
              data.shares.forEach(share => {
                if (share.status === 'success') {
                  const channelType = share.channel_type;
                  channelCounts[channelType] = (channelCounts[channelType] || 0) + 1;
                }
              });
              
              return {
                campaignId: campaign.id,
                status: {
                  channelCounts, // e.g., { telegram: 3, facebook: 1 }
                  totalShares: data.total_shares,
                  hasSuccessfulShares: Object.keys(channelCounts).length > 0
                }
              };
            }
          } catch (error) {
            // No sharing history is normal
            return {
              campaignId: campaign.id,
              status: {
                channelCounts: {},
                totalShares: 0,
                hasSuccessfulShares: false
              }
            };
          }
        });
        
        // Wait for batch to complete
        const results = await Promise.all(promises);
        
        // Update status for this batch
        results.forEach(result => {
          if (result) {
            statusMap[result.campaignId] = result.status;
            console.log(`✅ Publishing status for ${result.campaignId}:`, result.status);
            if (result.status.channelCounts.telegram) {
              console.log(`🎯 FOUND TELEGRAM CAMPAIGN with ${result.status.channelCounts.telegram} shares`);
            }
          }
        });
      }
      
      console.log('✅ Publishing status completed:', Object.keys(statusMap).length, 'campaigns');
      setCampaignPublishingStatus(statusMap);
    } catch (error) {
      console.warn('Error in fetchPublishingStatus:', error);
    }
  };

  // Fetch feedback statistics for dashboard campaigns
  const fetchFeedbackStats = async (campaigns) => {
    if (!sessionToken || !campaigns?.length) {
      console.log('fetchFeedbackStats: No sessionToken or campaigns');
      return;
    }

    console.log(`fetchFeedbackStats: Starting for ${campaigns.length} campaigns`);
    const statsMap = {};
    
    // Set initial loading states for all campaigns to avoid stuck loading
    campaigns.forEach(campaign => {
      statsMap[campaign.id] = {
        total_feedback: 0,
        average_rating: 0,
        user_feedback_count: 0,
        ai_feedback_count: 0,
        has_ai_feedback: false,
        isLoading: true
      };
    });
    
    // Set initial state to show loading indicators
    setCampaignFeedbackStats({...statsMap});
    
    try {
      // Process campaigns in parallel batches for better performance
      const batchSize = 10;
      const limitedCampaigns = campaigns.slice(0, 20); // Limit to 20 campaigns
      
      console.log(`Processing ${limitedCampaigns.length} campaigns in parallel batches`);
      
      for (let i = 0; i < limitedCampaigns.length; i += batchSize) {
        const batch = limitedCampaigns.slice(i, i + batchSize);
        
        // Process batch in parallel
        const promises = batch.map(async (campaign) => {
          try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 3000); // Reduced to 3 seconds
            
            const response = await axios.get(
              `${API}/campaigns/${campaign.id}/feedback/combined`,
              { 
                headers: { Authorization: `Bearer ${sessionToken}` },
                signal: controller.signal
              }
            );
            
            clearTimeout(timeoutId);
            
            if (response.status === 200 && response.data) {
              const data = response.data;
              return {
                campaignId: campaign.id,
                stats: {
                  total_feedback: data.combined_metrics.total_feedback || 0,
                  average_rating: data.combined_metrics.average_rating || 0,
                  user_feedback_count: data.user_feedback.total_feedback || 0,
                  ai_feedback_count: data.ai_feedback.total_feedback || 0,
                  has_ai_feedback: data.ai_feedback.has_saved_feedback || false,
                  isLoading: false
                }
              };
            }
          } catch (error) {
            return {
              campaignId: campaign.id,
              stats: {
                total_feedback: 0,
                average_rating: 0,
                user_feedback_count: 0,
                ai_feedback_count: 0,
                has_ai_feedback: false,
                isLoading: false
              }
            };
          }
        });
        
        // Wait for batch to complete
        const results = await Promise.all(promises);
        
        // Update stats for this batch
        results.forEach(result => {
          if (result) {
            statsMap[result.campaignId] = result.stats;
          }
        });
        
        // Update state after each batch to show progressive loading
        setCampaignFeedbackStats({...statsMap});
      }
      
      console.log('✅ Feedback stats completed:', Object.keys(statsMap).filter(id => statsMap[id].total_feedback > 0).length, 'campaigns with feedback');
    } catch (error) {
      console.warn('Error in fetchFeedbackStats:', error);
      // Ensure all campaigns have non-loading state
      Object.keys(statsMap).forEach(id => {
        if (statsMap[id].isLoading) {
          statsMap[id].isLoading = false;
        }
      });
      setCampaignFeedbackStats({...statsMap});
    }
  };

  // Handle viewing saved AI feedback
  const handleViewSavedFeedback = async (campaign) => {
    if (!campaign?.id || !sessionToken) {
      alert('Unable to load AI feedback. Please try again.');
      return;
    }
    
    try {
      const response = await axios.get(
        `${API}/campaigns/${campaign.id}/ai-feedback/saved`,
        { 
          headers: { 
            Authorization: `Bearer ${sessionToken}`,
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache'
          },
          timeout: 10000
        }
      );
      
      if (response.status === 200 && response.data) {
        setSavedFeedbackData({
          ...response.data,
          campaign: campaign
        });
        setShowSavedFeedbackModal(true);
      }
    } catch (error) {
      if (error.response?.status === 404) {
        alert('No saved AI feedback found for this campaign. Please generate AI feedback first.');
      } else {
        console.error('Error fetching saved feedback:', error);
        alert('Failed to load saved AI feedback. Please check your connection and try again.');
      }
    }
  };

  const fetchAvailableFilters = async () => {
    if (!sessionToken) {
      console.log('No session token available, skipping filters fetch');
      return;
    }
    
    try {
      const response = await axios.get(`${API}/campaigns/filters`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      setAvailableFilters(response.data);
    } catch (err) {
      console.error('Error fetching filters:', err);
    }
  };

  // REMOVED: Duplicate useEffect - now handled by consolidated useEffect above

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
    setCurrentPage(1); // Reset to first page when filtering
  };

  const handleSortChange = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilters({
      category: '',
      content_type: '',
      gender: '',
      date_from: '',
      date_to: '',
      min_age: '',
      max_age: ''
    });
    setSortBy('created_at');
    setSortOrder('desc');
    setCurrentPage(1); // Reset to first page when clearing filters
    fetchCampaigns(1); // Refresh with clear filters
  };

  const deleteCampaign = async (campaignId) => {
    if (!window.confirm('Are you sure you want to delete this campaign?')) return;
    
    try {
      await axios.delete(`${API}/campaigns/${campaignId}`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      setCampaigns(campaigns.filter(c => c.id !== campaignId));
      setFilteredCampaigns(filteredCampaigns.filter(c => c.id !== campaignId));
      // Remove from selection if it was selected
      setSelectedCampaigns(selectedCampaigns.filter(id => id !== campaignId));
    } catch (err) {
      console.error('Error deleting campaign:', err);
      alert('Failed to delete campaign');
    }
  };

  // Bulk Actions
  const handleBulkSelect = (campaignId) => {
    setSelectedCampaigns(prev => 
      prev.includes(campaignId) 
        ? prev.filter(id => id !== campaignId)
        : [...prev, campaignId]
    );
  };

  const handleSelectAll = () => {
    setSelectedCampaigns(
      selectedCampaigns.length === filteredCampaigns.length 
        ? [] 
        : filteredCampaigns.map(c => c.id)
    );
  };

  const handleBulkDelete = async () => {
    if (selectedCampaigns.length === 0) {
      alert('Please select campaigns to delete');
      return;
    }
    
    if (!window.confirm(`Are you sure you want to delete ${selectedCampaigns.length} campaigns?`)) {
      return;
    }
    
    setBulkActionLoading(true);
    try {
      await axios.post(`${API}/campaigns/bulk-delete`, {
        campaign_ids: selectedCampaigns
      }, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      
      // Remove deleted campaigns from state
      setCampaigns(campaigns.filter(c => !selectedCampaigns.includes(c.id)));
      setFilteredCampaigns(filteredCampaigns.filter(c => !selectedCampaigns.includes(c.id)));
      setSelectedCampaigns([]);
      
      alert(`Successfully deleted ${selectedCampaigns.length} campaigns`);
    } catch (err) {
      console.error('Error bulk deleting campaigns:', err);
      alert('Failed to delete campaigns');
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Edit Campaign
  const handleEditCampaign = (campaign) => {
    setEditingCampaign(campaign);
    setShowEditModal(true);
  };

  const handleUpdateCampaign = async (updatedProductData) => {
    try {
      const response = await axios.put(`${API}/campaigns/${editingCampaign.id}`, updatedProductData, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      
      // Update campaign in state
      const updatedCampaigns = campaigns.map(c => 
        c.id === editingCampaign.id ? response.data : c
      );
      setCampaigns(updatedCampaigns);
      setFilteredCampaigns(updatedCampaigns);
      
      setShowEditModal(false);
      setEditingCampaign(null);
      alert('Campaign updated successfully');
    } catch (err) {
      console.error('Error updating campaign:', err);
      alert('Failed to update campaign');
    }
  };

  // Share Campaign
  const handleShareCampaign = (campaign) => {
    setSharingCampaign(campaign);
    setShowSharingModal(true);
  };

  const handleUpdateSharing = async (isPublic) => {
    if (!sharingCampaign) return;
    
    setSharingLoading(true);
    try {
      const response = await axios.put(
        `${API}/campaigns/${sharingCampaign.id}/sharing`,
        { is_public: isPublic },
        { headers: { Authorization: `Bearer ${sessionToken}` } }
      );
      
      // Update campaign in state
      const updatedCampaigns = campaigns.map(c => 
        c.id === sharingCampaign.id ? response.data : c
      );
      setCampaigns(updatedCampaigns);
      setFilteredCampaigns(updatedCampaigns);
      setSharingCampaign(response.data); // Update the modal data
      
      if (isPublic) {
        // Show beautiful celebration modal instead of basic alert
        setSuccessCelebration({
          isOpen: true,
          title: "Shared Successfully! 💬",
          message: "Amazing! Your campaign is now shared and public! Keep sharing to unlock more achievements and reach new audiences. You're spreading your creative work - that's awesome!",
          celebrationType: "sharing",
          actionButton: (
            <button
              onClick={() => {
                if (response.data.share_url && navigator.clipboard) {
                  navigator.clipboard.writeText(response.data.share_url);
                }
                setSuccessCelebration(prev => ({ ...prev, isOpen: false }));
              }}
              className="w-full px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg font-bold hover:from-blue-600 hover:to-purple-600 transition-all duration-200 transform hover:scale-105"
            >
              📋 Copy Share URL
            </button>
          )
        });
        
        // Copy share URL to clipboard automatically
        setTimeout(async () => {
          try {
            const token = localStorage.getItem('session_token');
            const headers = { 'Authorization': `Bearer ${token}` };
            
            const response = await axios.post(`${API}/achievements/manual-check`, {}, { headers });
            
            if (response.data.newly_unlocked && response.data.newly_unlocked.length > 0) {
              console.log('🏆 New achievements unlocked after publishing campaign:', response.data.newly_unlocked);
              // Trigger achievement celebrations via global event
              window.dispatchEvent(new CustomEvent('triggerAchievementCheck'));
            }
          } catch (error) {
            console.error('Error checking for achievements:', error);
          }
        }, 500);
        
        // Copy share URL to clipboard
        if (response.data.share_url && navigator.clipboard) {
          try {
            await navigator.clipboard.writeText(response.data.share_url);
          } catch (err) {
            console.error('Failed to copy URL to clipboard:', err);
          }
        }
      } else {
        alert('Campaign is now private.');
      }
      
    } catch (err) {
      console.error('Error updating sharing:', err);
      alert('Failed to update campaign sharing');
    } finally {
      setSharingLoading(false);
    }
  };

  const copyShareUrl = async () => {
    if (sharingCampaign?.share_url && navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(sharingCampaign.share_url);
        alert('Share URL copied to clipboard!');
      } catch (err) {
        console.error('Failed to copy URL:', err);
        alert('Failed to copy URL to clipboard');
      }
    }
  };

  // Channel detection and sharing logic
  const getAvailableChannels = (campaign) => {
    const channels = [];
    const adTypes = campaign.generated_ads.map(ad => ad.ad_type);
    
    // Channel mapping based on ad types generated
    const channelMappings = {
      'telegram_ad': {
        id: 'telegram',
        name: 'Telegram',
        icon: '📱',
        description: 'Publish to Telegram chats/channels'
      },
      'email_marketing': {
        id: 'email',
        name: 'Email',
        icon: '📧', 
        description: 'Send via Email'
      },
      'facebook_ad': {
        id: 'facebook',
        name: 'Facebook',
        icon: '📘',
        description: 'Share to Facebook (Coming Soon)',
        disabled: true
      },
      'instagram_ad': {
        id: 'instagram',
        name: 'Instagram', 
        icon: '📸',
        description: 'Share to Instagram (Coming Soon)',
        disabled: true
      },
      'linkedin_ad': {
        id: 'linkedin',
        name: 'LinkedIn',
        icon: '💼',
        description: 'Share to LinkedIn (Coming Soon)', 
        disabled: true
      }
    };
    
    // Get unique channels based on generated ad types
    const uniqueChannels = new Set();
    adTypes.forEach(adType => {
      if (channelMappings[adType]) {
        uniqueChannels.add(channelMappings[adType]);
      }
    });
    
    // Add WhatsApp as always available (works with any ad that has text/images/videos)
    uniqueChannels.add({
      id: 'whatsapp',
      name: 'WhatsApp',
      icon: '💬',
      description: 'Send to WhatsApp contacts or groups'
    });
    
    return Array.from(uniqueChannels);
  };

  const handleGenericShare = (campaign) => {
    console.log('Dashboard handleGenericShare called for campaign:', campaign);
    
    // Determine primary channel based on campaign's main ad types
    const primaryChannel = getDashboardPrimaryChannel(campaign);
    console.log('Dashboard primary channel determined:', primaryChannel);
    
    // Always show primary channel modal first (even if disabled) for better UX
    setCurrentSharingCampaign(campaign);
    setPrimaryChannel(primaryChannel);
    setShowPrimaryChannelModal(true);
  };

  // Helper function for Dashboard to determine primary channel
  const getDashboardPrimaryChannel = (campaign) => {
    // Get the first ad type from the campaign
    if (campaign.generated_ads && campaign.generated_ads.length > 0) {
      const firstAd = campaign.generated_ads[0];
      return getDashboardChannelByAdType(firstAd.ad_type);
    }
    
    // Fallback to Telegram as primary
    return {
      id: 'telegram',
      name: 'Telegram',
      icon: '📱',
      description: 'Publish to Telegram chats/channels',
      disabled: false
    };
  };

  // Helper function for Dashboard to get channel by ad type
  const getDashboardChannelByAdType = (adType) => {
    const channelMap = {
      'facebook_ad': {
        id: 'facebook',
        name: 'Facebook',
        icon: '📘',
        description: 'Share to Facebook (Coming Soon)',
        disabled: true
      },
      'instagram_ad': {
        id: 'instagram',
        name: 'Instagram',
        icon: '📸',
        description: 'Share to Instagram (Coming Soon)',
        disabled: true
      },
      'linkedin_ad': {
        id: 'linkedin',
        name: 'LinkedIn',
        icon: '💼',
        description: 'Share to LinkedIn (Coming Soon)',
        disabled: true
      },
      'telegram_ad': {
        id: 'telegram',
        name: 'Telegram',
        icon: '📱',
        description: 'Publish to Telegram chats/channels',
        disabled: false
      }
    };
    
    return channelMap[adType] || {
      id: 'telegram',
      name: 'Telegram',
      icon: '📱',
      description: 'Publish to Telegram chats/channels',
      disabled: false
    };
  };

  // Helper function for Dashboard to get all available channels
  const getDashboardAllChannels = () => {
    return [
      {
        id: 'telegram',
        name: 'Telegram',
        icon: '📱',
        description: 'Publish to Telegram chats/channels',
        disabled: false
      },
      {
        id: 'whatsapp',
        name: 'WhatsApp',
        icon: '💬',
        description: 'Send to WhatsApp contacts or groups',
        disabled: false
      },
      {
        id: 'facebook',
        name: 'Facebook',
        icon: '📘',
        description: 'Share to Facebook (Coming Soon)',
        disabled: true
      },
      {
        id: 'instagram',
        name: 'Instagram', 
        icon: '📸',
        description: 'Share to Instagram (Coming Soon)',
        disabled: true
      },
      {
        id: 'email',
        name: 'Email',
        icon: '📧', 
        description: 'Send via Email (Coming Soon)',
        disabled: true
      },
      {
        id: 'linkedin',
        name: 'LinkedIn',
        icon: '💼',
        description: 'Share to LinkedIn (Coming Soon)', 
        disabled: true
      }
    ];
  };

  const handleChannelSelection = (channelId) => {
    setShowChannelSelectionModal(false);
    setShowPrimaryChannelModal(false);
    
    switch (channelId) {
      case 'telegram':
        // Use Dashboard's Telegram modal for now - need to unify later
        handleTelegramShareCampaign(currentSharingCampaign);
        break;
      case 'whatsapp':
        handleWhatsAppShareCampaign(currentSharingCampaign);
        break;
      case 'email':
        alert('Email sharing coming soon!');
        break;
      case 'facebook':
        alert('Facebook sharing coming soon!');
        break;
      case 'instagram':
        alert('Instagram sharing coming soon!');
        break;
      case 'linkedin':
        alert('LinkedIn sharing coming soon!');
        break;
      default:
        alert('Channel not supported yet.');
    }
  };

  // Telegram sharing functions
  const handleTelegramShareCampaign = (campaign) => {
    setTelegramSharingCampaign(campaign);
    setTelegramSharingType('campaign');
    setShowTelegramSharingModal(true);
  };

  const handleTelegramShareIndividualAd = (campaign, ad, adIndex) => {
    setTelegramSharingCampaign(campaign);
    setSelectedAd({ ad, adIndex });
    setTelegramSharingType('individual');
    setShowTelegramSharingModal(true);
  };

  // WhatsApp sharing handler
  const handleWhatsAppShareCampaign = (campaign) => {
    setWhatsappSharingCampaign(campaign);
    setShowWhatsAppModal(true);
  };

  // Function to validate campaign before publishing
  const validateCampaignForPublishing = async (campaignId) => {
    try {
      const token = localStorage.getItem('session_token');
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const response = await axios.get(`${API}/campaigns/${campaignId}/publish-validation`, { headers });
      return response.data;
    } catch (error) {
      console.error('Error validating campaign:', error);
      throw error;
    }
  };

  // Function to show functional alert
  const showFunctionalAlert = (title, message, type = 'warning', icon = null, actionButton = null, secondaryButton = null) => {
    setFunctionalAlert({
      isOpen: true,
      title,
      message,
      type,
      icon,
      actionButton,
      secondaryButton
    });
  };

  // Function to close functional alert
  const closeFunctionalAlert = () => {
    setFunctionalAlert(prev => ({
      ...prev,
      isOpen: false
    }));
  };

  const shareToTelegram = async (chatId, customMessage = '') => {
    try {
      setTelegramSharingStatus({ status: 'sharing', message: 'Validating campaign...' });
      
      // VALIDATION: Check if campaign meets publishing requirements
      try {
        const validationResult = await validateCampaignForPublishing(telegramSharingCampaign.id);
        
        if (!validationResult.can_publish) {
          // Show beautiful functional alert for validation failure
          const requirementMessage = 
            !validationResult.requirements.meets_feedback_requirement
              ? `Your campaign needs more feedback before publishing. Currently: ${validationResult.feedback_count}/3 feedback required.`
              : `Your campaign needs a higher average rating before publishing. Currently: ${validationResult.average_rating}/5.0 stars (needs >4.0).`;
          
          showFunctionalAlert(
            "Publishing Requirements Not Met 📋",
            `${requirementMessage}\n\nGet more feedback by sharing your campaign's feedback link with clients and colleagues. Once you have 3+ feedback entries with an average rating above 4 stars, you'll be able to publish to all channels!`,
            "warning",
            "📊",
            <button
              onClick={async () => {
                closeFunctionalAlert();
                
                try {
                  // Check if campaign is already public
                  const token = localStorage.getItem('session_token');
                  const headers = { 'Authorization': `Bearer ${token}` };
                  const campaignResponse = await axios.get(`${API}/campaigns/${telegramSharingCampaign.id}`, { headers });
                  const campaign = campaignResponse.data;
                  
                  if (campaign.is_public && campaign.share_url) {
                    // Campaign is already public - show feedback link directly
                    setUrlCopyModal({
                      isOpen: true,
                      url: campaign.share_url,
                      title: '🔗 Feedback Link Ready!',
                      description: 'Your campaign is public! Share this link with clients and colleagues to get more feedback:'
                    });
                  } else {
                    // Campaign is not public - show modal to make it public first
                    showFunctionalAlert(
                      "Make Campaign Public First 🌍",
                      "To get feedback, your campaign needs to be public first. Making it public will generate a shareable feedback link that you can send to clients and colleagues.\n\nDon't worry - only people with the link can view your campaign!",
                      "info",
                      "🔓",
                      <button
                        onClick={async () => {
                          try {
                            // Make campaign public
                            const shareResponse = await axios.put(
                              `${API}/campaigns/${telegramSharingCampaign.id}/sharing`,
                              { is_public: true },
                              { headers }
                            );
                            
                            closeFunctionalAlert();
                            
                            // Show success and feedback link
                            setUrlCopyModal({
                              isOpen: true,
                              url: shareResponse.data.share_url,
                              title: '🎉 Campaign Now Public!',
                              description: 'Perfect! Your campaign is now public. Share this feedback link with clients and colleagues:'
                            });
                            
                          } catch (error) {
                            console.error('Error making campaign public:', error);
                            showFunctionalAlert(
                              "Error Making Public ❌",
                              "Sorry, there was an error making your campaign public. Please try again or contact support if the issue persists.",
                              "error"
                            );
                          }
                        }}
                        className="w-full px-6 py-3 bg-gradient-to-r from-green-500 to-blue-500 text-white rounded-lg font-bold hover:from-green-600 hover:to-blue-600 transition-all duration-200 transform hover:scale-105"
                      >
                        🌍 Make Public & Get Feedback Link
                      </button>,
                      <button
                        onClick={closeFunctionalAlert}
                        className="w-full px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-300 dark:hover:bg-gray-500 transition-all duration-200"
                      >
                        Maybe Later
                      </button>
                    );
                  }
                } catch (error) {
                  console.error('Error checking campaign status:', error);
                  showFunctionalAlert(
                    "Error Checking Campaign ❌",
                    "Sorry, there was an error checking your campaign status. Please try again or contact support if the issue persists.",
                    "error"
                  );
                }
              }}
              className="w-full px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg font-bold hover:from-blue-600 hover:to-purple-600 transition-all duration-200 transform hover:scale-105"
            >
              📝 Get More Feedback
            </button>
          );
          
          setTelegramSharingStatus({});
          return;
        }
      } catch (validationError) {
        console.error('Validation error:', validationError);
        // Continue with sharing if validation fails (fallback)
      }
      
      // Proceed with sharing if validation passes
      setTelegramSharingStatus({ status: 'sharing', message: 'Publishing to Telegram...' });
      
      const shareData = {
        campaign_id: telegramSharingCampaign.id,
        chat_id: chatId,
        share_type: telegramSharingType,
        message_template: customMessage,
        ad_index: telegramSharingType === 'individual' && selectedAd ? selectedAd.adIndex : null
      };

      const response = await axios.post(
        `${API}/share/telegram`,
        shareData,
        {
          headers: {
            'Authorization': `Bearer ${sessionToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.successful_shares > 0) {
        // Show success celebration modal for sharing
        setSuccessCelebration({
          isOpen: true,
          title: "Campaign Published! 🚀",
          message: `Your campaign is now live and published! You successfully published ${response.data.successful_shares} item(s) to Telegram. You're building an amazing portfolio - keep creating and publishing!`,
          celebrationType: "publishing",
          actionButton: null
        });
        
        // Also update the legacy status for any UI that depends on it
        setTelegramSharingStatus({ 
          status: 'success', 
          message: `🎉 Successfully shared ${response.data.successful_shares} item(s) to Telegram! Keep sharing to unlock more achievements and reach new audiences!` 
        });
        
        // Check for newly unlocked achievements after successful sharing
        setTimeout(async () => {
          try {
            const token = localStorage.getItem('session_token');
            const headers = { 'Authorization': `Bearer ${token}` };
            
            const response = await axios.post(`${API}/achievements/manual-check`, {}, { headers });
            
            if (response.data.newly_unlocked && response.data.newly_unlocked.length > 0) {
              console.log('🏆 New achievements unlocked after sharing:', response.data.newly_unlocked);
              // Trigger achievement celebrations via global event
              window.dispatchEvent(new CustomEvent('triggerAchievementCheck'));
            }
          } catch (error) {
            console.error('Error checking for achievements:', error);
          }
        }, 500);
      } else {
        setTelegramSharingStatus({ 
          status: 'error', 
          message: 'The bot may not have access to this chat. Try: 1) Add @maple_ai_generator_bot to your chat first, or 2) Use your username (e.g., @yourusername), or 3) Use "me" to test.' 
        });
      }

      setTimeout(() => {
        setTelegramSharingStatus({});
        setShowTelegramSharingModal(false);
      }, 3000);

    } catch (error) {
      console.error('Error sharing to Telegram:', error);
      let errorMessage = 'Failed to share to Telegram';
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (detail.includes('Chat not found')) {
          errorMessage = '❌ Chat not found! The bot cannot access this chat. Try:\n• Add @maple_ai_generator_bot to your chat/channel first\n• Use your Telegram username: @yourusername\n• Use "me" to test with the bot directly';
        } else {
          errorMessage = detail;
        }
      }
      
      setTelegramSharingStatus({ 
        status: 'error', 
        message: errorMessage 
      });

      setTimeout(() => {
        setTelegramSharingStatus({});
      }, 5000);
    }
  };

  // Duplicate Campaign
  const handleDuplicateCampaign = (campaign) => {
    setDuplicatingCampaign(campaign);
    setShowDuplicateModal(true);
  };

  const handleDuplicateWithModifications = async (modifiedProductData) => {
    try {
      const response = await axios.post(`${API}/campaigns/${duplicatingCampaign.id}/duplicate`, 
        modifiedProductData, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      
      // Add new campaign to state
      const newCampaigns = [response.data, ...campaigns];
      setCampaigns(newCampaigns);
      setFilteredCampaigns(newCampaigns);
      
      setShowDuplicateModal(false);
      setDuplicatingCampaign(null);
      alert('Campaign duplicated successfully');
    } catch (err) {
      console.error('Error duplicating campaign:', err);
      alert('Failed to duplicate campaign');
    }
  };

  // Export Functions
  const handleExport = async (format) => {
    setIsExporting(true);
    try {
      const campaignIds = selectedCampaigns.length > 0 ? selectedCampaigns : null;
      const params = new URLSearchParams();
      params.append('format', format);
      
      if (campaignIds) {
        campaignIds.forEach(id => params.append('campaign_ids', id));
      }
      
      const response = await axios.get(`${API}/campaigns/export?${params}`, {
        headers: { Authorization: `Bearer ${sessionToken}` },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `campaigns.${format}`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      if (selectedCampaigns.length > 0) {
        alert(`Exported ${selectedCampaigns.length} selected campaigns`);
      } else {
        alert('Exported all campaigns');
      }
    } catch (err) {
      console.error('Error exporting campaigns:', err);
      alert('Failed to export campaigns');
    } finally {
      setIsExporting(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading && campaigns.length === 0) {
    return (
      <section id="dashboard" className="py-8 dashboard-container">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Show skeleton for campaigns view */}
          {currentView === 'campaigns' && (
            <>
              <div className="mb-6">
                <div className="mb-4">
                  <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-64 mb-2 animate-pulse"></div>
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-96 animate-pulse"></div>
                </div>
                
                {/* Stat Cards Skeleton */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="bg-gradient-to-br from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-600 rounded-xl p-4 shadow-md animate-pulse">
                      <div className="h-9 bg-gray-300 dark:bg-gray-600 rounded w-16 mb-1"></div>
                      <div className="h-3 bg-gray-300 dark:bg-gray-600 rounded w-24"></div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="text-center py-8">
                <div className={`animate-spin rounded-full h-10 w-10 border-b-2 ${colorTheme === 'purple' ? 'border-purple-600' : colorTheme === 'blue' ? 'border-blue-600' : 'border-green-600'} mx-auto mb-3`}></div>
                <span className="text-sm text-gray-600 dark:text-gray-300">Loading your campaigns...</span>
              </div>
            </>
          )}
          
          {/* Other views - simple loading */}
          {currentView !== 'campaigns' && (
            <div className="flex items-center justify-center py-20">
              <div className={`animate-spin rounded-full h-12 w-12 border-b-2 ${colorTheme === 'purple' ? 'border-purple-600' : colorTheme === 'blue' ? 'border-blue-600' : 'border-green-600'}`}></div>
              <span className="ml-3 text-lg text-gray-600 dark:text-gray-300">Loading your campaigns...</span>
            </div>
          )}
        </div>
      </section>
    );
  }

  return (
    <section id="dashboard" className="py-8 dashboard-container">
      <Confetti trigger={showConfetti} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Title and Stat Cards for Dashboard view (campaigns) */}
        {showTitle && currentView === 'campaigns' && (
          <div className="mb-6">
            <div className="mb-4">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                📊 Dashboard
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Manage and organize all your marketing campaigns
              </p>
            </div>
            
            {/* Stat Cards with Unified Theme */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Total Campaigns */}
              <div className={`bg-gradient-to-br ${colorTheme === 'purple' ? 'from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border-purple-200 dark:border-purple-700' : colorTheme === 'blue' ? 'from-blue-50 to-sky-50 dark:from-blue-900/20 dark:to-sky-900/20 border-blue-200 dark:border-blue-700' : 'from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-green-200 dark:border-green-700'} border rounded-xl p-4 shadow-sm transition-all duration-300 hover:shadow-md`}>
                <div className={`text-3xl font-bold mb-1 ${colorTheme === 'purple' ? 'text-purple-600 dark:text-purple-400' : colorTheme === 'blue' ? 'text-blue-600 dark:text-blue-400' : 'text-green-600 dark:text-green-400'}`}>
                  <AnimatedCounter value={totalCampaigns || 0} />
                </div>
                <div className="text-gray-600 dark:text-gray-400 text-xs font-medium">Total Campaigns</div>
              </div>
              
              {/* Completed Campaigns - Approximate from current page */}
              <div className={`bg-gradient-to-br ${colorTheme === 'purple' ? 'from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border-purple-200 dark:border-purple-700' : colorTheme === 'blue' ? 'from-blue-50 to-sky-50 dark:from-blue-900/20 dark:to-sky-900/20 border-blue-200 dark:border-blue-700' : 'from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-green-200 dark:border-green-700'} border rounded-xl p-4 shadow-sm transition-all duration-300 hover:shadow-md`}>
                <div className={`text-3xl font-bold mb-1 ${colorTheme === 'purple' ? 'text-purple-600 dark:text-purple-400' : colorTheme === 'blue' ? 'text-blue-600 dark:text-blue-400' : 'text-green-600 dark:text-green-400'}`}>
                  <AnimatedCounter value={Math.round((campaigns.filter(c => !c.generation_status || c.generation_status === 'completed').length / campaigns.length) * totalCampaigns) || 0} />
                </div>
                <div className="text-gray-600 dark:text-gray-400 text-xs font-medium">Completed</div>
              </div>
              
              {/* Generating Campaigns - Approximate from current page */}
              <div className="bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl p-4 shadow-sm transition-all duration-300 hover:shadow-md">
                <div className="text-3xl font-bold mb-1 text-gray-800 dark:text-gray-200">
                  <AnimatedCounter value={Math.round((campaigns.filter(c => c.generation_status === 'generating' || c.generation_status === 'queued').length / campaigns.length) * totalCampaigns) || 0} />
                </div>
                <div className="text-gray-600 dark:text-gray-400 text-xs font-medium">Generating</div>
              </div>
              
              {/* Success Rate - Based on current page ratio */}
              <div className="bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl p-4 shadow-sm transition-all duration-300 hover:shadow-md">
                <div className="text-3xl font-bold mb-1 text-gray-800 dark:text-gray-200">
                  <AnimatedCounter value={campaigns.length > 0 
                    ? Math.round((campaigns.filter(c => !c.generation_status || c.generation_status === 'completed').length / campaigns.length) * 100)
                    : 0} />%
                </div>
                <div className="text-gray-600 dark:text-gray-400 text-xs font-medium">Success Rate</div>
              </div>
            </div>
          </div>
        )}
        
        {/* Title for other views */}
        {showTitle && currentView !== 'campaigns' && (
          <div>
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
              {currentView === 'main' ? 'Advanced Content Creator' : 
               currentView === 'content-studio' ? 'Content Studio' :
               currentView === 'dashboard' ? 'My Masterpieces' :
               'Your Campaign Dashboard'}
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300">
              {currentView === 'main' ? 'Create persona-driven marketing content with advanced customization' :
               currentView === 'content-studio' ? 'Choose a template and generate content instantly' :
               currentView === 'dashboard' ? 'View and manage all your marketing masterpieces' :
               'Manage and view all your AI-generated marketing campaigns'}
            </p>
          </div>
        )}

        {/* Tab Navigation - Hidden for My Masterpieces view */}
        {!hideAnalytics && currentView !== 'campaigns' ? (
          <div className="flex border-b border-gray-200 dark:border-gray-700 mb-8">
            <button
              onClick={() => setActiveTab('campaigns')}
              className={`py-4 px-6 text-lg font-medium transition-colors ${
                activeTab === 'campaigns'
                  ? 'dashboard-tab-active'
                  : 'dashboard-tab-inactive'
              }`}
            >
              📊 Campaigns
            </button>
            <button
              onClick={() => setActiveTab('analytics')}
              className={`py-4 px-6 text-lg font-medium transition-colors ${
                activeTab === 'analytics'
                  ? 'dashboard-tab-active'
                  : 'dashboard-tab-inactive'
              }`}
            >
              📈 Analytics
            </button>
          </div>
        ) : null}

        {/* Tab Content */}
        {(hideAnalytics || activeTab === 'campaigns') ? (
          <>
            {/* Unified Search, Results & Actions Card */}
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 mb-6 border border-gray-200 dark:border-gray-700">
              {/* Top Row: Search Bar + Results Count + Filter Buttons */}
              <div className="flex flex-col lg:flex-row gap-3 items-start lg:items-center mb-3">
                {/* Search Bar */}
                <div className="flex-1 w-full lg:w-auto">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search campaigns..."
                      value={searchTerm}
                      onChange={handleSearchChange}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dashboard-focus-green dark:bg-gray-700 dark:text-white text-sm"
                    />
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                  </div>
                </div>
                
                {/* Results Count */}
                <div className="text-sm font-medium text-gray-600 dark:text-gray-300 whitespace-nowrap">
                  {filteredCampaigns.length} campaign{filteredCampaigns.length !== 1 ? 's' : ''} found
                  {searchTerm && ` for "${searchTerm}"`}
                </div>
                
                {/* Filter Buttons */}
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      showFilters 
                        ? `${colorTheme === 'purple' ? 'bg-purple-600' : colorTheme === 'blue' ? 'bg-blue-600' : 'bg-green-600'} text-white` 
                        : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500'
                    }`}
                  >
                    🔍 Filters
                  </button>
                  
                  <button
                    onClick={clearFilters}
                    className="px-3 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors text-sm"
                  >
                    Clear All
                  </button>
                </div>
              </div>

              {/* Filter Options */}
              {showFilters && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 pt-4 border-t border-gray-200 dark:border-gray-600">
                  {/* Category Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Category
                    </label>
                    <select
                      value={filters.category}
                      onChange={(e) => handleFilterChange('category', e.target.value)}
                      className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg dashboard-focus-green dark:bg-gray-700 dark:text-white"
                    >
                      <option value="">All Categories</option>
                      {availableFilters.categories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>

                  {/* Content Type Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Content Type
                    </label>
                    <select
                      value={filters.content_type}
                      onChange={(e) => handleFilterChange('content_type', e.target.value)}
                      className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg dashboard-focus-green dark:bg-gray-700 dark:text-white"
                    >
                      <option value="">All Types</option>
                      <option value="social_media">Social Media</option>
                      <option value="google_ads">Google Ads</option>
                      <option value="product_description">Product Description</option>
                      <option value="email_marketing">Email Marketing</option>
                    </select>
                  </div>

                  {/* Gender Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Target Gender
                    </label>
                    <select
                      value={filters.gender}
                      onChange={(e) => handleFilterChange('gender', e.target.value)}
                      className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg dashboard-focus-green dark:bg-gray-700 dark:text-white"
                    >
                      <option value="">All Genders</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="All">All</option>
                    </select>
                  </div>

                  {/* Date Range Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Date Range
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="date"
                        value={filters.date_from}
                        onChange={(e) => handleFilterChange('date_from', e.target.value)}
                        className="flex-1 p-2 border border-gray-300 dark:border-gray-600 rounded-lg dashboard-focus-green dark:bg-gray-700 dark:text-white text-sm"
                      />
                      <input
                        type="date"
                        value={filters.date_to}
                        onChange={(e) => handleFilterChange('date_to', e.target.value)}
                        className="flex-1 p-2 border border-gray-300 dark:border-gray-600 rounded-lg dashboard-focus-green dark:bg-gray-700 dark:text-white text-sm"
                      />
                    </div>
                  </div>

                  {/* Age Range Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Age Range
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        placeholder="Min"
                        value={filters.min_age}
                        onChange={(e) => handleFilterChange('min_age', e.target.value)}
                        className="flex-1 p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-sm"
                      />
                      <input
                        type="number"
                        placeholder="Max"
                        value={filters.max_age}
                        onChange={(e) => handleFilterChange('max_age', e.target.value)}
                        className="flex-1 p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-sm"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Sort Options */}
              <div className="flex items-center gap-4 mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Sort by:</span>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleSortChange('created_at')}
                    className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                      sortBy === 'created_at' 
                        ? (colorTheme === 'purple' ? 'bg-purple-600 text-white' : colorTheme === 'blue' ? 'bg-blue-600 text-white' : 'bg-green-600 text-white')
                        : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500'
                    }`}
                  >
                    Date {sortBy === 'created_at' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </button>
                  <button
                    onClick={() => handleSortChange('product_name')}
                    className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                      sortBy === 'product_name' 
                        ? (colorTheme === 'purple' ? 'bg-purple-600 text-white' : colorTheme === 'blue' ? 'bg-blue-600 text-white' : 'bg-green-600 text-white')
                        : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500'
                    }`}
                  >
                    Name {sortBy === 'product_name' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </button>
                  <button
                    onClick={() => handleSortChange('category')}
                    className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                      sortBy === 'category' 
                        ? (colorTheme === 'purple' ? 'bg-purple-600 text-white' : colorTheme === 'blue' ? 'bg-blue-600 text-white' : 'bg-green-600 text-white')
                        : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500'
                    }`}
                  >
                    Category {sortBy === 'category' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </button>
                </div>
              </div>
              
              {/* Bulk Actions Row */}
              {filteredCampaigns.length > 0 && (
                <div className="flex flex-wrap items-center gap-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={selectedCampaigns.length === filteredCampaigns.length && filteredCampaigns.length > 0}
                      onChange={handleSelectAll}
                      className={`w-4 h-4 ${colorTheme === 'purple' ? 'text-purple-600' : colorTheme === 'blue' ? 'text-blue-600' : 'text-green-600'} rounded`}
                    />
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Select All ({selectedCampaigns.length}/{filteredCampaigns.length})
                    </label>
                  </div>
                  
                  {selectedCampaigns.length > 0 && (
                    <div className="flex gap-2">
                      <button
                        onClick={handleBulkDelete}
                        disabled={bulkActionLoading}
                        className="px-3 py-1 bg-red-600 hover:bg-red-700 disabled:bg-red-300 text-white rounded-lg text-sm font-medium transition-colors"
                      >
                        {bulkActionLoading ? 'Deleting...' : `Delete ${selectedCampaigns.length}`}
                      </button>
                      
                      <button
                        onClick={() => handleExport('csv')}
                        disabled={isExporting}
                        className="px-3 py-1 bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-600 dark:disabled:bg-gray-800 text-gray-700 dark:text-gray-300 disabled:text-gray-400 rounded-lg text-sm font-medium transition-colors border border-gray-300 dark:border-gray-600"
                      >
                        {isExporting ? 'Exporting...' : '📤 Export CSV'}
                      </button>
                      
                      <button
                        onClick={() => handleExport('json')}
                        disabled={isExporting}
                        className="px-3 py-1 bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-600 dark:disabled:bg-gray-800 text-gray-700 dark:text-gray-300 disabled:text-gray-400 rounded-lg text-sm font-medium transition-colors border border-gray-300 dark:border-gray-600"
                      >
                        {isExporting ? 'Exporting...' : '📥 Export JSON'}
                      </button>
                    </div>
                  )}
                  
                  {selectedCampaigns.length === 0 && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleExport('csv')}
                        disabled={isExporting}
                        className="px-3 py-1 bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-600 dark:disabled:bg-gray-800 text-gray-700 dark:text-gray-300 disabled:text-gray-400 rounded-lg text-sm font-medium transition-colors border border-gray-300 dark:border-gray-600"
                      >
                        {isExporting ? 'Exporting...' : '📤 Export All CSV'}
                      </button>
                      
                      <button
                        onClick={() => handleExport('json')}
                        disabled={isExporting}
                        className="px-3 py-1 bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-600 dark:disabled:bg-gray-800 text-gray-700 dark:text-gray-300 disabled:text-gray-400 rounded-lg text-sm font-medium transition-colors border border-gray-300 dark:border-gray-600"
                      >
                        {isExporting ? 'Exporting...' : '📥 Export All JSON'}
                      </button>
                    </div>
                  )}
                  
                  {/* Refresh and New Campaign Buttons for My Masterpieces */}
                  {currentView === 'campaigns' && (
                    <div className="flex gap-2 ml-auto">
                      <button
                        onClick={() => {
                          console.log('🔄 Manual refresh clicked');
                          fetchCampaigns(currentPage);
                        }}
                        className="px-3 py-1 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg text-sm font-medium transition-colors flex items-center gap-1"
                        title="Refresh campaigns"
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Refresh
                      </button>
                      <button
                        onClick={onCreateNewCampaign}
                        className={`px-3 py-1 ${colorTheme === 'purple' ? 'bg-purple-600 hover:bg-purple-700' : colorTheme === 'blue' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-green-600 hover:bg-green-700'} text-white rounded-lg text-sm font-medium transition-colors btn-ripple`}
                      >
                        + New Campaign
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
                {error}
              </div>
            )}

            {filteredCampaigns.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">📝</div>
                <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                  {campaigns.length === 0 ? 'No campaigns yet' : 'No campaigns match your filters'}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-6">
                  {campaigns.length === 0 
                    ? 'Create your first AI-powered marketing campaign to get started'
                    : 'Try adjusting your search terms or filters to find more campaigns'
                  }
                </p>
                {campaigns.length === 0 ? (
                  <button
                    onClick={onCreateNewCampaign}
                    className="dashboard-btn-primary"
                  >
                    Create First Campaign
                  </button>
                ) : (
                  <button
                    onClick={clearFilters}
                    className="dashboard-btn-primary"
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 gap-6">
                {filteredCampaigns.map((campaign) => {
                  // Helper function to get campaign icon matching One Click Content Studio exactly
                  const getCampaignIcon = () => {
                    const adTypes = campaign.generated_ads?.map(ad => ad.ad_type) || [];
                    const uniqueTypes = [...new Set(adTypes)];
                    
                    // Multi-content type
                    if (uniqueTypes.length > 1) return '🎭';
                    
                    // Single content type - match One Click Content Studio icons exactly
                    const primaryType = uniqueTypes[0] || '';
                    
                    // Exact icon mapping from One Click Content Studio
                    if (primaryType.includes('facebook')) return '📘';
                    if (primaryType.includes('instagram')) return '📸';
                    if (primaryType.includes('linkedin')) return '💼';
                    if (primaryType.includes('tiktok')) return '🎵';
                    if (primaryType.includes('telegram')) return '✈️';
                    if (primaryType.includes('google')) return '🔍';
                    if (primaryType.includes('video') || primaryType === 'video_promo') return '🎬';
                    if (primaryType.includes('image') || primaryType === 'image_promo') return '🎨';
                    if (primaryType.includes('email')) return '📧';
                    if (primaryType.includes('social_media')) return '📱';
                    if (primaryType.includes('product_description')) return '📦';
                    
                    return '📄'; // Default
                  };
                  
                  // Get status badge
                  const getStatusBadge = () => {
                    if (campaign.generation_status === 'generating' || campaign.generation_status === 'queued') {
                      return (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200">
                          ⏳ Generating
                        </span>
                      );
                    }
                    return (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800">
                        ✓ Completed
                      </span>
                    );
                  };

                  return (
                  <div key={campaign.id} className={`bg-gradient-to-br ${colorTheme === 'purple' ? 'from-purple-50 to-pink-50 dark:from-gray-800 dark:to-gray-800 border-purple-200 dark:border-gray-700' : colorTheme === 'blue' ? 'from-blue-50 to-sky-50 dark:from-gray-800 dark:to-gray-800 border-blue-200 dark:border-gray-700' : 'from-green-50 to-emerald-50 dark:from-gray-800 dark:to-gray-800 border-green-200 dark:border-gray-700'} rounded-lg p-6 shadow-lg min-w-0 min-h-[280px] campaign-card-hover campaign-fade-in border`}>
                    <div className="flex gap-4">
                      {/* Campaign Icon */}
                      <div className="flex-shrink-0">
                        <div className={`w-16 h-16 ${colorTheme === 'purple' ? 'bg-blue-100 dark:bg-blue-900/20' : 'bg-green-100 dark:bg-green-900/20'} rounded-lg flex items-center justify-center text-3xl border ${colorTheme === 'purple' ? 'border-blue-200 dark:border-blue-800' : 'border-green-200 dark:border-green-800'}`}>
                          {getCampaignIcon()}
                        </div>
                      </div>
                      
                      {/* Campaign Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex items-start gap-3 flex-1">
                            <input
                              type="checkbox"
                              checked={selectedCampaigns.includes(campaign.id)}
                              onChange={() => handleBulkSelect(campaign.id)}
                              className={`w-4 h-4 ${colorTheme === 'purple' ? 'text-purple-600' : colorTheme === 'blue' ? 'text-blue-600' : 'text-green-600'} rounded flex-shrink-0 mt-1`}
                            />
                            <div className="flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white campaign-name-multiline" title={campaign.product.product_name}>
                                  {campaign.product.product_name}
                                </h3>
                                {getStatusBadge()}
                              </div>
                            </div>
                          </div>
                          <div className="flex gap-1 flex-shrink-0 ml-2">
                            <button
                              onClick={() => {
                                const content = campaign.generated_ads.map(ad => 
                                  `${ad.ad_type.toUpperCase()}\nTitle: ${ad.title}\nContent: ${ad.content}\nCTA: ${ad.call_to_action}\n\n`
                                ).join('');
                                navigator.clipboard.writeText(content);
                                alert('Campaign copied to clipboard!');
                              }}
                              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 text-sm p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 flex-shrink-0"
                              title="Copy Campaign Content"
                            >
                              📋
                            </button>
                            <button
                              onClick={() => onRegenerateCampaign && onRegenerateCampaign(campaign)}
                              className={`${colorTheme === 'purple' ? 'text-purple-500 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300' : 'text-green-500 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300'} text-sm p-1 rounded hover:bg-green-50 dark:hover:bg-green-900/20 flex-shrink-0`}
                              title="Regenerate Campaign"
                            >
                              ✨
                            </button>
                            <button
                              onClick={() => handleDuplicateCampaign(campaign)}
                              className="text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 text-sm p-1 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20 flex-shrink-0"
                              title="Duplicate Campaign"
                            >
                              🔄
                            </button>
                            <button
                              onClick={() => deleteCampaign(campaign.id)}
                              className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 text-sm p-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20 flex-shrink-0"
                              title="Delete Campaign"
                            >
                              🗑️
                            </button>
                          </div>
                        </div>
                        
                        <div className="space-y-3">
                      
                      {/* 🎨 SPINNING WHEEL - Show during background generation */}
                      {campaign.generation_status && (campaign.generation_status === 'queued' || campaign.generation_status === 'generating') && (
                        <div className="col-span-2">
                          <div className="flex items-center gap-3 dashboard-generating-bg dashboard-generating-border rounded-xl px-4 py-3 shadow-lg animate-pulse-glow">
                            <svg className={`animate-spin h-6 w-6 ${colorTheme === 'purple' ? 'text-purple-600 dark:text-purple-400' : colorTheme === 'blue' ? 'text-blue-600 dark:text-blue-400' : 'text-green-600 dark:text-green-400'}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <div className="flex-1">
                              <p className="text-sm font-bold dashboard-generating-text flex items-center gap-2">
                                🎨 AI is generating your ad creatives...
                              </p>
                              <p className="text-xs dashboard-generating-subtext mt-1">
                                {campaign.generation_status === 'queued' ? 'Campaign queued, starting soon...' : 'Creating personalized content'}
                              </p>
                              {campaign.generation_progress !== undefined && campaign.generation_progress > 0 && (
                                <div className="mt-2">
                                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                                    <div className={`h-1.5 rounded-full transition-all duration-300 ${colorTheme === 'purple' ? 'bg-purple-600 dark:bg-purple-400' : colorTheme === 'blue' ? 'bg-blue-600 dark:bg-blue-400' : 'bg-green-600 dark:bg-green-400'}`} style={{width: `${campaign.generation_progress}%`}}></div>
                                  </div>
                                  <p className={`text-xs ${colorTheme === 'purple' ? 'text-purple-600 dark:text-purple-400' : colorTheme === 'blue' ? 'text-blue-600 dark:text-blue-400' : 'text-green-600 dark:text-green-400'} mt-1`}>{campaign.generation_progress}% complete</p>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Campaign Summary */}
                      <div>
                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Campaign Overview</p>
                        <div className="flex items-center gap-3 mt-1">
                          {(() => {
                            const totalAds = campaign.generated_ads.length;
                            const adTypeCounts = campaign.generated_ads.reduce((acc, ad) => {
                              const baseType = ad.ad_type.replace('_', ' ');
                              acc[baseType] = (acc[baseType] || 0) + 1;
                              return acc;
                            }, {});
                            const uniqueAdTypes = Object.keys(adTypeCounts).length;
                            const hasPersonas = Object.values(adTypeCounts).some(count => count > 1);
                            const personaCount = hasPersonas ? Math.max(...Object.values(adTypeCounts)) : 1;
                            
                            return (
                              <>
                                <span className={`text-xs px-2 py-1 rounded border font-semibold ${colorTheme === 'purple' ? 'bg-purple-100 dark:bg-purple-900/20 text-purple-800 dark:text-purple-300 border-purple-200 dark:border-purple-700' : colorTheme === 'blue' ? 'bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300 border-blue-200 dark:border-blue-700' : 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300 border-green-200 dark:border-green-700'}`}>
                                  {totalAds} pieces
                                </span>
                                <span className="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-600">
                                  {uniqueAdTypes} types
                                </span>
                                {hasPersonas && (
                                  <span className="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-600">
                                    {personaCount} personas
                                  </span>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </div>
                      
                      <div>
                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Content Types</p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {(() => {
                            // Group ads by type and count them
                            const adTypeCounts = campaign.generated_ads.reduce((acc, ad) => {
                              const baseType = ad.ad_type.replace('_', ' ');
                              acc[baseType] = (acc[baseType] || 0) + 1;
                              return acc;
                            }, {});
                            
                            // Check if we have personas (multiple ads of same type indicates personas)
                            const hasPersonas = Object.values(adTypeCounts).some(count => count > 1);
                            const totalPersonas = hasPersonas ? Math.max(...Object.values(adTypeCounts)) : 1;
                            
                            return Object.entries(adTypeCounts).map(([adType, count]) => (
                              <span 
                                key={adType} 
                                className="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs px-2 py-1 rounded flex items-center gap-1 border border-gray-300 dark:border-gray-600"
                                title={count > 1 ? `${adType} content created for ${count} different personas` : `${adType} content`}
                              >
                                <span className="capitalize">{adType}</span>
                                {count > 1 && (
                                  <span className={`px-1.5 py-0.5 rounded-full text-xs font-bold min-w-[1.25rem] text-center ${colorTheme === 'purple' ? 'bg-purple-200 dark:bg-purple-700 text-purple-900 dark:text-purple-100' : 'bg-green-200 dark:bg-green-700 text-green-900 dark:text-green-100'}`}>
                                    ×{count}
                                  </span>
                                )}
                              </span>
                            ));
                          })()}
                        </div>
                      </div>
                      
                      <div>
                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Created</p>
                        <p className="text-gray-700 dark:text-gray-300 text-sm">
                          {formatDate(campaign.created_at)}
                        </p>
                      </div>
                      
                      {/* Status, Feedback & Published Channel */}
                      <div>
                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Status, Feedback & Published Channel</p>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {/* 1. Status - Public/Private */}
                          {campaign.is_public ? (
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${colorTheme === 'purple' ? 'bg-purple-100 dark:bg-purple-900/20 text-purple-800 dark:text-purple-400' : colorTheme === 'blue' ? 'bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-400' : 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-400'}`}>
                              🌍 Public
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                              🔒 Private
                            </span>
                          )}
                          
                          {/* 2. Feedback - Combined User + AI rating */}
                          {campaignFeedbackStats[campaign.id] ? (
                            campaignFeedbackStats[campaign.id].total_feedback > 0 ? (
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${colorTheme === 'purple' ? 'bg-purple-100 dark:bg-purple-900/20 text-purple-800 dark:text-purple-400' : colorTheme === 'blue' ? 'bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-400' : 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-400'} font-semibold`}>
                                ⭐ {campaignFeedbackStats[campaign.id].average_rating.toFixed(1)} ({campaignFeedbackStats[campaign.id].total_feedback} {campaignFeedbackStats[campaign.id].total_feedback === 1 ? 'review' : 'reviews'})
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400">
                                📊 No feedback yet
                              </span>
                            )
                          ) : (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-200 dark:bg-gray-600 text-gray-500 dark:text-gray-400">
                              ⏳ Loading feedback...
                            </span>
                          )}

                          {/* Published Channel Status */}
                          {(() => {
                            const publishingStatus = campaignPublishingStatus[campaign.id];
                            const channelBadges = [];
                            
                            // Show channel badges with counts for actually published channels
                            if (publishingStatus?.channelCounts) {
                              // Add Telegram if published
                              if (publishingStatus.channelCounts.telegram) {
                                channelBadges.push({
                                  name: 'Telegram',
                                  icon: '📱',
                                  count: publishingStatus.channelCounts.telegram
                                });
                              }
                              
                              // Add Facebook if published
                              if (publishingStatus.channelCounts.facebook) {
                                channelBadges.push({
                                  name: 'Facebook',
                                  icon: '📘',
                                  count: publishingStatus.channelCounts.facebook
                                });
                              }
                              
                              // Add Instagram if published
                              if (publishingStatus.channelCounts.instagram) {
                                channelBadges.push({
                                  name: 'Instagram',
                                  icon: '📸',
                                  count: publishingStatus.channelCounts.instagram
                                });
                              }
                            }
                            
                            // Show published channel badges with counts
                            if (channelBadges.length > 0) {
                              return (
                                <div className="flex flex-wrap gap-1">
                                  {channelBadges.map((channel, index) => (
                                    <span key={index} className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${colorTheme === 'purple' ? 'bg-purple-100 dark:bg-purple-900/20 text-purple-800 dark:text-purple-400' : colorTheme === 'blue' ? 'bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-400' : 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-400'}`}>
                                      {channel.icon} {channel.name} ({channel.count})
                                    </span>
                                  ))}
                                  {campaign.is_public && campaign.share_token && (
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600">
                                      🔗 Shareable
                                    </span>
                                  )}
                                </div>
                              );
                            } else if (campaign.is_public && campaign.share_token) {
                              return (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600">
                                  🔗 Shareable
                                </span>
                              );
                            } else {
                              return (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-orange-100 dark:bg-orange-900/20 text-orange-800 dark:text-orange-400">
                                  📝 Draft
                                </span>
                              );
                            }
                          })()}
                        </div>
                      </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-4 flex flex-wrap gap-2">
                      <button
                        onClick={() => onViewCampaign(campaign)}
                        className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-xs font-medium rounded transition-colors btn-ripple border border-gray-300 dark:border-gray-600"
                      >
                        View Details
                      </button>
                      <button
                        onClick={() => onRegenerateCampaign && onRegenerateCampaign(campaign)}
                        className={`px-3 py-1.5 ${colorTheme === 'purple' ? 'bg-purple-100 hover:bg-purple-200 dark:bg-purple-900/20 dark:hover:bg-purple-800/30 text-purple-700 dark:text-purple-300' : 'bg-green-100 hover:bg-green-200 dark:bg-green-900/20 dark:hover:bg-green-800/30 text-green-700 dark:text-green-300'} text-xs font-medium rounded transition-colors btn-ripple border ${colorTheme === 'purple' ? 'border-purple-300 dark:border-purple-700' : 'border-green-300 dark:border-green-700'}`}
                        title="Regenerate campaign with same settings"
                      >
                        ✨ Regenerate
                      </button>
                      <button
                        onClick={() => onEditCampaign && onEditCampaign(campaign)}
                        className={`px-3 py-1.5 ${colorTheme === 'purple' ? 'bg-purple-600 hover:bg-purple-700' : colorTheme === 'blue' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-green-600 hover:bg-green-700'} text-white text-xs font-medium rounded transition-colors btn-ripple`}
                      >
                        ✏️ Edit Content
                      </button>
                      <button
                        onClick={() => {
                          setSelectedFeedbackCampaign(campaign);
                          setShowFeedbackModal(true);
                        }}
                        className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-xs font-medium rounded transition-colors btn-ripple border border-gray-300 dark:border-gray-600"
                      >
                        💬 Get Feedback
                      </button>
                      <button
                        onClick={() => handleGenericShare(campaign)}
                        className={`px-3 py-1.5 ${colorTheme === 'purple' ? 'bg-purple-600 hover:bg-purple-700' : colorTheme === 'blue' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-green-600 hover:bg-green-700'} text-white text-xs font-medium rounded transition-colors btn-ripple`}
                        title={getAvailableChannels(campaign).length > 0 
                          ? `Publish Campaign (${getAvailableChannels(campaign).map(c => c.name).join(', ')} available)` 
                          : 'Publish Campaign'}
                      >
                        🚀 Publish
                      </button>
                    </div>
                  </div>
                  );
                })}
                </div>
                
                {!isLoading && totalCampaigns > campaignsPerPage && (
                <div className="mt-8 flex items-center justify-between">
                  <div className="text-sm text-gray-700 dark:text-gray-300">
                    Showing page {currentPage} of {Math.ceil(totalCampaigns / campaignsPerPage)} 
                    ({totalCampaigns} total campaigns)
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => {
                        const newPage = currentPage - 1;
                        setCurrentPage(newPage);
                        fetchCampaigns(newPage);
                      }}
                      disabled={currentPage === 1}
                      className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                    >
                      ← Previous
                    </button>
                    
                    {/* Page Numbers */}
                    {Array.from({ length: Math.min(5, Math.ceil(totalCampaigns / campaignsPerPage)) }, (_, i) => {
                      const totalPages = Math.ceil(totalCampaigns / campaignsPerPage);
                      let pageNum;
                      
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => {
                            setCurrentPage(pageNum);
                            fetchCampaigns(pageNum);
                          }}
                          className={`px-3 py-2 text-sm font-medium rounded-md ${
                            currentPage === pageNum
                              ? 'bg-blue-600 text-white'
                              : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    
                    <button
                      onClick={() => {
                        const newPage = currentPage + 1;
                        setCurrentPage(newPage);
                        fetchCampaigns(newPage);
                      }}
                      disabled={currentPage >= Math.ceil(totalCampaigns / campaignsPerPage)}
                      className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                    >
                      Next →
                    </button>
                  </div>
                </div>
              )}
              </>
            )}
          </>
        ) : (
          !hideAnalytics && <Analytics />
        )}
      </div>
      
      {/* Edit Campaign Modal */}
    {showEditModal && editingCampaign && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto border-2 border-green-500">
          <h2 className="text-2xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent mb-6">Edit Campaign</h2>
          
          <EditCampaignForm 
            campaign={editingCampaign}
            onSave={handleUpdateCampaign}
            onCancel={() => {
              setShowEditModal(false);
              setEditingCampaign(null);
            }}
          />
        </div>
      </div>
    )}
    
    {/* Duplicate Campaign Modal */}
    {showDuplicateModal && duplicatingCampaign && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Duplicate Campaign</h2>
          
          <DuplicateCampaignForm 
            campaign={duplicatingCampaign}
            onSave={handleDuplicateWithModifications}
            onCancel={() => {
              setShowDuplicateModal(false);
              setDuplicatingCampaign(null);
            }}
          />
        </div>
      </div>
    )}
    
    {/* Sharing Modal */}
    {showSharingModal && sharingCampaign && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Share feedback link</h2>
          
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                {sharingCampaign.product.product_name}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Current status: {sharingCampaign.is_public ? 'Public' : 'Private'}
              </p>
            </div>
            
            {sharingCampaign.is_public && sharingCampaign.share_url && (
              <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Share URL:
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={sharingCampaign.share_url}
                    readOnly
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
                  />
                  <button
                    onClick={copyShareUrl}
                    className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm"
                  >
                    Copy
                  </button>
                </div>
              </div>
            )}
            
            <div className="flex gap-3">
              <button
                onClick={() => handleUpdateSharing(true)}
                disabled={sharingLoading}
                className={`flex-1 py-2 px-4 rounded-lg font-medium btn-ripple ${
                  sharingCampaign.is_public
                    ? 'bg-gray-200 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                    : (colorTheme === 'purple' ? 'bg-purple-600 hover:bg-purple-700 text-white' : colorTheme === 'blue' ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-green-600 hover:bg-green-700 text-white')
                } ${sharingLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {sharingLoading ? 'Updating...' : 'Make Public'}
              </button>
              
              <button
                onClick={() => handleUpdateSharing(false)}
                disabled={sharingLoading}
                className={`flex-1 py-2 px-4 rounded-lg font-medium btn-ripple ${
                  !sharingCampaign.is_public
                    ? 'bg-gray-200 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                    : 'bg-red-600 hover:bg-red-700 text-white'
                } ${sharingLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {sharingLoading ? 'Updating...' : 'Make Private'}
              </button>
            </div>
            
            <button
              onClick={() => {
                setShowSharingModal(false);
                setSharingCampaign(null);
              }}
              disabled={sharingLoading}
              className="w-full py-2 px-4 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    )}
    
    {/* Primary Channel Modal for Dashboard */}
    {showPrimaryChannelModal && primaryChannel && currentSharingCampaign && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className={`bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full border-2 ${colorTheme === 'purple' ? 'border-purple-500' : colorTheme === 'blue' ? 'border-blue-500' : 'border-green-500'}`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className={`text-lg font-semibold bg-gradient-to-r ${colorTheme === 'purple' ? 'from-purple-600 to-pink-600' : colorTheme === 'blue' ? 'from-blue-600 to-sky-500' : 'from-green-600 to-emerald-600'} bg-clip-text text-transparent`}>
              Publish Campaign: "{currentSharingCampaign.product.product_name}"
            </h3>
            <button
              onClick={() => setShowPrimaryChannelModal(false)}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Based on your campaign content, we recommend:
            </p>
            
            {/* Primary Channel Display */}
            <div className={`p-4 rounded-lg border-2 ${
              primaryChannel.disabled 
                ? 'border-orange-200 bg-orange-50 dark:bg-orange-900/20 dark:border-orange-700'
                : (colorTheme === 'purple' 
                  ? 'border-purple-300 bg-purple-50 dark:bg-purple-900/20 dark:border-purple-500'
                  : 'border-green-300 bg-green-50 dark:bg-green-900/20 dark:border-green-500')
            }`}>
              <div className="flex items-center">
                <span className="text-3xl mr-3">{primaryChannel.icon}</span>
                <div className="flex-1">
                  <div className="font-semibold text-gray-900 dark:text-white flex items-center">
                    {primaryChannel.name}
                    {primaryChannel.disabled && (
                      <span className="ml-2 px-2 py-1 text-xs bg-orange-200 text-orange-800 rounded-full">
                        Coming Soon
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    {primaryChannel.description}
                  </div>
                </div>
              </div>
            </div>

            {primaryChannel.disabled ? (
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-4">
                <div className="flex items-start">
                  <span className="text-amber-600 dark:text-amber-400 text-xl mr-3">⚠️</span>
                  <div>
                    <p className="text-sm text-amber-800 dark:text-amber-300 font-medium">
                      {primaryChannel.name} publishing is coming soon!
                    </p>
                    <p className="text-sm text-amber-700 dark:text-amber-400 mt-1">
                      Would you like to publish to an available channel instead?
                    </p>
                  </div>
                </div>
              </div>
            ) : null}
          </div>

          <div className="flex space-x-3 mt-6">
            {!primaryChannel.disabled ? (
              <button
                onClick={() => {
                  setShowPrimaryChannelModal(false);
                  handleChannelSelection(primaryChannel.id);
                }}
                className={`flex-1 ${colorTheme === 'purple' ? 'bg-purple-600 hover:bg-purple-700' : colorTheme === 'blue' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-green-600 hover:bg-green-700'} text-white py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center`}
              >
                <span className="mr-2">{primaryChannel.icon}</span>
                Publish to {primaryChannel.name}
              </button>
            ) : (
              <button
                onClick={() => {
                  setShowPrimaryChannelModal(false);
                  // Direct to Telegram since it's the only available channel
                  handleChannelSelection('telegram');
                }}
                className={`flex-1 ${colorTheme === 'purple' ? 'bg-purple-600 hover:bg-purple-700' : colorTheme === 'blue' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-green-600 hover:bg-green-700'} text-white py-3 px-4 rounded-lg font-medium transition-colors btn-ripple`}
              >
                📱 Publish to Telegram
              </button>
            )}
            
            <button
              onClick={() => {
                setShowPrimaryChannelModal(false);
                const allChannels = getDashboardAllChannels();
                setAvailableChannels(allChannels);
                setShowChannelSelectionModal(true);
              }}
              className="px-4 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Choose Different Channel
            </button>
          </div>
        </div>
      </div>
    )}
    
    {/* Channel Selection Modal */}
    {showChannelSelectionModal && currentSharingCampaign && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className={`bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full border-2 ${colorTheme === 'purple' ? 'border-purple-500' : colorTheme === 'blue' ? 'border-blue-500' : 'border-green-500'}`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className={`text-lg font-semibold bg-gradient-to-r ${colorTheme === 'purple' ? 'from-purple-600 to-pink-600' : colorTheme === 'blue' ? 'from-blue-600 to-sky-500' : 'from-green-600 to-emerald-600'} bg-clip-text text-transparent`}>
              Publish Campaign: "{currentSharingCampaign.product.product_name}"
            </h3>
            <button
              onClick={() => setShowChannelSelectionModal(false)}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="space-y-3">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Choose a channel to publish your campaign:
            </p>
            
            {availableChannels.map((channel) => (
              <button
                key={channel.id}
                onClick={() => handleChannelSelection(channel.id)}
                disabled={channel.disabled}
                className={`w-full flex items-center p-3 rounded-lg border text-left transition-colors ${
                  channel.disabled
                    ? 'border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 cursor-not-allowed opacity-50'
                    : (colorTheme === 'purple' 
                      ? 'border-gray-200 dark:border-gray-600 hover:border-purple-400 dark:hover:border-purple-500 hover:bg-purple-50 dark:hover:bg-purple-900/20'
                      : colorTheme === 'blue'
                      ? 'border-gray-200 dark:border-gray-600 hover:border-blue-400 dark:hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20'
                      : 'border-gray-200 dark:border-gray-600 hover:border-green-400 dark:hover:border-green-500 hover:bg-green-50 dark:hover:bg-green-900/20')
                }`}
              >
                <span className="text-2xl mr-3">{channel.icon}</span>
                <div className="flex-1">
                  <div className="font-medium text-gray-900 dark:text-white">
                    {channel.name}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {channel.description}
                  </div>
                </div>
                {!channel.disabled && (
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                )}
              </button>
            ))}
          </div>

          <div className="mt-6">
            <button
              onClick={() => setShowChannelSelectionModal(false)}
              className="w-full py-2 px-4 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    )}
    
    {/* Telegram Sharing Modal - Updated for consistency */}
    {showTelegramSharingModal && telegramSharingCampaign && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className={`bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full border-2 ${colorTheme === 'purple' ? 'border-purple-500' : colorTheme === 'blue' ? 'border-blue-500' : 'border-green-500'}`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className={`text-lg font-semibold bg-gradient-to-r ${colorTheme === 'purple' ? 'from-purple-600 to-pink-600' : colorTheme === 'blue' ? 'from-blue-600 to-sky-500' : 'from-green-600 to-emerald-600'} bg-clip-text text-transparent`}>
              {telegramSharingType === 'individual' && selectedAd ? 
                `Publish "${selectedAd.ad.title}"` : 
                `Publish Campaign: "${telegramSharingCampaign.product.product_name}"`
              }
            </h3>
            <button
              onClick={() => setShowTelegramSharingModal(false)}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Telegram Chat ID or Username
              </label>
              <input
                type="text"
                id="telegram-chat-id-dashboard"
                placeholder="@yourusername, @channelname, or me (for testing)"
                className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
              <div className="mt-2 space-y-1">
                <p className="text-xs text-blue-600 dark:text-blue-400">
                  💡 <strong>For yourself:</strong> Use your username <strong>@yourusername</strong>
                </p>
                <p className="text-xs text-blue-600 dark:text-blue-400">
                  💡 <strong>For testing:</strong> Use <strong>me</strong>
                </p>
                <p className="text-xs text-blue-600 dark:text-blue-400">
                  💡 <strong>For channels:</strong> Add the bot first, then use <strong>@channelname</strong>
                </p>
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  ⚠️ <strong>Bot must be added to groups/channels before sharing</strong>
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Custom Message (Optional)
              </label>
              <textarea
                id="telegram-message-dashboard"
                rows="3"
                placeholder="Add a personal message..."
                className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white resize-none"
              ></textarea>
            </div>
          </div>

          {/* Status Display */}
          {telegramSharingStatus.status && (
            <div className="mt-4">
              <div className={`p-3 rounded-lg text-sm ${
                telegramSharingStatus.status === 'success' 
                  ? 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300' 
                  : telegramSharingStatus.status === 'sharing'
                  ? 'bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300'
                  : 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-300'
                } flex items-center`}>
                {telegramSharingStatus.status === 'sharing' && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                )}
                <span>{telegramSharingStatus.message}</span>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex space-x-3 mt-6">
            <button
              onClick={() => {
                const chatId = document.getElementById('telegram-chat-id-dashboard').value;
                const customMessage = document.getElementById('telegram-message-dashboard').value;
                if (chatId.trim()) {
                  shareToTelegram(chatId.trim(), customMessage.trim());
                } else {
                  setTelegramSharingStatus({ status: 'error', message: 'Please enter a valid Chat ID or Channel Username' });
                }
              }}
              disabled={telegramSharingStatus.status === 'sharing'}
              className={`flex-1 ${colorTheme === 'purple' ? 'bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400' : colorTheme === 'blue' ? 'bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400' : 'bg-green-600 hover:bg-green-700 disabled:bg-green-400'} text-white py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center btn-ripple`}
            >
              {telegramSharingStatus.status === 'sharing' ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Publishing...
                </>
              ) : (
                <>
                  🚀 Publish to Telegram
                </>
              )}
            </button>
            <button
              onClick={() => setShowTelegramSharingModal(false)}
              className="px-4 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    )}
    
    {/* WhatsApp Publishing Modal */}
    {showWhatsAppModal && whatsappSharingCampaign && (
      <WhatsAppPublishModal
        isOpen={showWhatsAppModal}
        onClose={() => {
          setShowWhatsAppModal(false);
          setWhatsappSharingCampaign(null);
        }}
        campaign={whatsappSharingCampaign}
        sessionToken={sessionToken}
      />
    )}
    
    {/* Success Celebration Modal */}
    <SuccessCelebrationModal
      isOpen={successCelebration.isOpen}
      onClose={() => setSuccessCelebration(prev => ({ ...prev, isOpen: false }))}
      title={successCelebration.title}
      message={successCelebration.message}
      celebrationType={successCelebration.celebrationType}
      actionButton={successCelebration.actionButton}
    />
    
    {/* Functional Alert Modal */}
    <FunctionalAlertModal
      isOpen={functionalAlert.isOpen}
      onClose={closeFunctionalAlert}
      title={functionalAlert.title}
      message={functionalAlert.message}
      type={functionalAlert.type}
      icon={functionalAlert.icon}
      actionButton={functionalAlert.actionButton}
      secondaryButton={functionalAlert.secondaryButton}
    />
    
    {/* URL Copy Modal */}
    <URLCopyModal
      isOpen={urlCopyModal.isOpen}
      onClose={() => setUrlCopyModal(prev => ({ ...prev, isOpen: false }))}
      url={urlCopyModal.url}
      title={urlCopyModal.title}
      description={urlCopyModal.description}
    />
    
    {/* AI Feedback Modal */}
    {showAIFeedbackModal && aiFeedbackCampaign && sessionToken && (
      <AIFeedbackSimulation
        campaign={aiFeedbackCampaign}
        sessionToken={sessionToken}
        onClose={() => {
          setShowAIFeedbackModal(false);
          setAiFeedbackCampaign(null);
        }}
      />
    )}

    {/* Saved Feedback Modal */}
    {showSavedFeedbackModal && savedFeedbackData && (
      <SavedFeedbackModal
        isOpen={showSavedFeedbackModal}
        savedFeedbackData={savedFeedbackData}
        onClose={() => {
          setShowSavedFeedbackModal(false);
          setSavedFeedbackData(null);
        }}
      />
    )}
    
    {/* Feedback Type Selection Modal */}
    {showFeedbackModal && selectedFeedbackCampaign && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 shadow-2xl">
          <div className="text-center">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
              💬 Choose Feedback Type
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              How would you like to get feedback for "{selectedFeedbackCampaign.product?.product_name}"?
            </p>
            
            <div className="space-y-3">
              {/* User Feedback Option */}
              <button
                onClick={async () => {
                  setShowFeedbackModal(false);
                  // Use existing handleShareCampaign function for user feedback
                  await handleShareCampaign(selectedFeedbackCampaign);
                  setSelectedFeedbackCampaign(null);
                }}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2 btn-ripple"
              >
                <span className="text-lg">👤</span>
                <div>
                  <div className="font-semibold">User Feedback</div>
                  <div className="text-sm opacity-90">Get feedback from real users & clients</div>
                </div>
              </button>
              
              {/* AI Feedback Option */}
              <button
                onClick={() => {
                  setShowFeedbackModal(false);
                  // Use existing AI feedback functionality
                  setAiFeedbackCampaign(selectedFeedbackCampaign);
                  setShowAIFeedbackModal(true);
                  setSelectedFeedbackCampaign(null);
                }}
                className={`w-full ${colorTheme === 'purple' ? 'bg-purple-600 hover:bg-purple-700' : colorTheme === 'blue' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-green-600 hover:bg-green-700'} text-white font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2 btn-ripple`}
              >
                <span className="text-lg">🤖</span>
                <div>
                  <div className="font-semibold">AI Feedback</div>
                  <div className="text-sm opacity-90">Get instant AI-powered feedback & suggestions</div>
                </div>
              </button>
            </div>
            
            <button
              onClick={() => {
                setShowFeedbackModal(false);
                setSelectedFeedbackCampaign(null);
              }}
              className="mt-4 w-full bg-gray-500 hover:bg-gray-600 text-white font-medium py-2 px-4 rounded-lg transition-colors btn-ripple"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    )}
    
    </section>
  );
};

const CampaignDetail = ({ 
  campaign, 
  onBack, 
  onShareIndividualAd, 
  onShareEntireCampaign, 
  shareToTelegram, 
  showSharingModal, 
  setShowSharingModal,
  onRegenerateCampaign,
  onRegenerateIndividualAd 
}) => {
  const { sessionToken } = useAuth();
  const { colorTheme } = useColorTheme();
  const [feedbackData, setFeedbackData] = useState(null);
  const [isLoadingFeedback, setIsLoadingFeedback] = useState(false);
  const [activeTab, setActiveTab] = useState('ads'); // 'ads' or 'feedback'
  const [aiFeedback, setAiFeedback] = useState(null);
  const [isLoadingAiFeedback, setIsLoadingAiFeedback] = useState(false);
  const [copiedAll, setCopiedAll] = useState(false);
  
  // Fix: Add missing showDetailsModal state for inline editing
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  
  // Copy to clipboard function
  const copyToClipboard = (text) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(() => {
        console.log('Copied to clipboard');
      }).catch(err => {
        console.error('Failed to copy:', err);
        // Fallback to old method
        const textArea = document.createElement('textarea');
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      });
    } else {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  };
  
  // URL Copy modal state for CampaignDetail
  const [urlCopyModal, setUrlCopyModal] = useState({
    isOpen: false,
    url: '',
    title: 'Link Copied!',
    description: 'Share this link with others to get feedback on your campaign:'
  });
  
  if (!campaign) return null;

  // Fetch campaign details with feedback
  const fetchCampaignFeedback = async () => {
    if (!campaign.id) return;
    
    setIsLoadingFeedback(true);
    try {
      const response = await axios.get(`${API}/campaigns/${campaign.id}/details`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      
      if (response.data && response.data.feedback) {
        setFeedbackData(response.data.feedback);
      }
    } catch (err) {
      console.error('Error fetching campaign feedback:', err);
    } finally {
      setIsLoadingFeedback(false);
    }
  };

  // Fetch AI feedback
  const fetchAiFeedback = async () => {
    if (!campaign.id) return;
    
    setIsLoadingAiFeedback(true);
    try {
      const response = await axios.get(`${API}/campaigns/${campaign.id}/ai-feedback/saved`, {
        headers: { 
          Authorization: `Bearer ${sessionToken}`,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });
      
      if (response.status === 200) {
        setAiFeedback(response.data);
      }
    } catch (err) {
      // 404 is expected if no AI feedback is saved
      if (err.response?.status !== 404) {
        console.error('Error fetching AI feedback:', err);
      }
    } finally {
      setIsLoadingAiFeedback(false);
    }
  };

  // PERFORMANCE OPTIMIZATION: Pre-load feedback data when component mounts for faster tab switching
  useEffect(() => {
    // Load feedback data immediately when component mounts (not when tab switches)
    console.log('🔄 Campaign Detail mounted, loading feedback for campaign:', campaign.id);
    fetchCombinedFeedback(); // Use single combined API call instead of two separate calls
  }, [campaign.id]);

  // Optimized combined feedback fetch (single API call instead of two)
  const fetchCombinedFeedback = async () => {
    setIsLoadingFeedback(true);
    setIsLoadingAiFeedback(true);
    
    try {
      console.log('📊 Fetching combined feedback from:', `${BACKEND_URL}/api/campaigns/${campaign.id}/feedback/combined`);
      // Use the enhanced combined endpoint for better performance
      const response = await axios.get(
        `${BACKEND_URL}/api/campaigns/${campaign.id}/feedback/combined`,
        {
          headers: {
            Authorization: `Bearer ${sessionToken}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('📊 Combined feedback response:', response.data);
      
      if (response.data) {
        // Extract both user and AI feedback from single response
        // Note: Use combined_metrics for accurate counts, not individual feedback_count
        const userData = response.data.user_feedback || {};
        const aiData = response.data.ai_feedback || {};
        const combinedMetrics = response.data.combined_metrics || {};
        
        setFeedbackData({
          ...userData,
          feedback_count: userData.total_feedback || 0,
          average_rating: userData.average_rating || 0,
          reviews: userData.feedback || []
        });
        
        setAiFeedback({
          ...aiData,
          feedback_count: aiData.total_feedback || 0,
          average_rating: aiData.average_rating || 0,
          feedback: aiData.feedback_entries || []
        });
        
        console.log('✅ Feedback data set:', {
          userFeedback: userData.total_feedback || 0,
          aiFeedback: aiData.total_feedback || 0,
          totalCombined: combinedMetrics.total_feedback || 0,
          averageRating: combinedMetrics.average_rating || 0
        });
      }
    } catch (error) {
      console.error('❌ Error loading combined feedback:', error);
      console.log('🔄 Falling back to individual API calls...');
      // Fallback to individual calls if combined fails
      fetchCampaignFeedback();
      fetchAiFeedback();
    } finally {
      setIsLoadingFeedback(false);
      setIsLoadingAiFeedback(false);
    }
  };

  const formatFeedbackDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderStarRating = (rating) => {
    return '★'.repeat(rating) + '☆'.repeat(5 - rating);
  };

  const getRatingColor = (rating) => {
    if (rating >= 4) return 'text-green-600 dark:text-green-400';
    if (rating >= 3) return 'text-green-600 dark:text-yellow-400';
    return 'text-red-600 dark:text-red-400';
  };

  // Dynamic function to get ad type labels based on video model
  const getAdTypeLabel = (adType, videoModel = null, imageModel = null) => {
    const baseLabels = {
      facebook_ad: 'Facebook Ad',
      instagram_ad: 'Instagram Post',
      linkedin_ad: 'LinkedIn Ad',
      tiktok_ad: 'TikTok Content',
      telegram_ad: 'Telegram Channel',
      social_media: 'Social Media',
      google_ads: 'Google Ads',
      product_description: 'Product Description',
      email_marketing: 'Email Marketing'
    };

    // Dynamic image title based on model for image_promo
    if (adType === 'image_promo') {
      return 'AI Generated Image';
    }

    // Dynamic video title based on model for both video_promo and video_ad
    if (adType === 'video_promo' || adType === 'video_ad') {
      return 'AI Generated Video';
    }

    return baseLabels[adType] || adType;
  };

  // Helper function to remove model names from ad titles
  const cleanAdTitle = (title) => {
    if (!title) return title;
    // Remove model names from titles - hide technical details from users
    return title
      .replace(/\(Freepik Nano Banana\)/gi, '')
      .replace(/\(Kie AI Nano Banana\)/gi, '')
      .replace(/\(Nebius AI\)/gi, '')
      .replace(/\(Google Veo 3\)/gi, '')
      .replace(/\(Freepik MiniMax Hailuo\)/gi, '')
      .replace(/\(Replicate \+ ElevenLabs\)/gi, '')
      .replace(/\(GPT-4o\)/gi, '')
      .replace(/\(Ultra Image \(GPT-4o\)\)/gi, '')
      .replace(/\(Ultra Image\)/gi, '')
      .replace(/\(AI Image\)/gi, '')
      .replace(/\(Ultra HD Image\)/gi, '')
      .replace(/\(Premium Image\)/gi, '')
      .replace(/\(Quick Image\)/gi, '')
      .replace(/\(Standard Image\)/gi, '')
      .replace(/\(Best Value\)/gi, '')
      .replace(/\(Gemini 3\.0 Pro Ultra HD\)/gi, '')
      .replace(/GPT-4o/gi, '')
      .replace(/Nano Banana/gi, '')
      .replace(/\s+-\s+/g, ' - ')  // Clean up extra spaces around dashes
      .replace(/\s{2,}/g, ' ')      // Replace multiple spaces with single space
      .trim();
  };

  // Helper functions to get user-friendly model labels
  const getVideoModelLabel = (modelId) => {
    const labels = {
      'freepik': 'Quick Video ✨',
      'replicate': 'Standard Video ⚡',
      'veo3_fast': 'Fast Video 🚀',
      'veo3': 'Premium Video 🎥',
      'kie-ai': 'Premium Video 🎥',  // Legacy support
      'ultra_video': 'Ultra Video 🎬',
      'gemini_omni_video': 'Ultra Pro 🪐',
      // 🆕 Kling 3.0 / Seedance 2.0
      'kling-3.0-pro': 'Ultra Cinematic (Kling 3.0 Pro)',
      'kling-3.0-std': 'Ultra Cinematic Lite (Kling 3.0)',
      'seedance-2.0-pro': 'Realism Pro (Seedance 2.0)',
      'seedance-2.0-fast': 'Realism Lite (Seedance 2.0 Fast)',
      // 🎭 HeyGen Photo Avatar
      'heygen-photo-avatar': 'Talking Head (HeyGen Photo Avatar) 🎭'
    };
    return labels[modelId] || 'AI Powered';
  };

  const getImageModelLabel = (modelId) => {
    const labels = {
      'nebius': 'Quick Image 💨',
      'kie-ai': 'Standard Image 🎨',
      'freepik': 'Best Value 🎨',
      'nano-banana-pro': 'Premium Image',
      'nano-banana-2': 'Premium Image 2 (Flash 4K)',
      'gpt4o-image': 'Ultra Image'
    };
    return labels[modelId] || 'AI Powered';
  };

  const adTypeLabels = {
    facebook_ad: 'Facebook Ad',
    instagram_ad: 'Instagram Post',
    linkedin_ad: 'LinkedIn Ad',
    tiktok_ad: 'TikTok Content',
    telegram_ad: 'Telegram Channel',
    social_media: 'Social Media',
    google_ads: 'Google Ads',
    product_description: 'Product Description',
    email_marketing: 'Email Marketing',
    video_promo: 'AI Video with Voiceover (Replicate + ElevenLabs)' // Default fallback
    // image_promo removed - handled dynamically by function logic above
  };

  const adTypeIcons = {
    facebook_ad: <BrandIcon templateId="facebook_ad" size={24} />,
    instagram_ad: <BrandIcon templateId="instagram_ad" size={24} />,
    linkedin_ad: <BrandIcon templateId="linkedin_ad" size={24} />,
    tiktok_ad: <BrandIcon templateId="tiktok_ad" size={24} />,
    telegram_ad: <BrandIcon templateId="telegram_ad" size={24} />,
    social_media: <BrandIcon templateId="social_media" size={24} />,
    google_ads: <BrandIcon templateId="google_ads" size={24} />,
    product_description: <BrandIcon templateId="product_description" size={24} />,
    email_marketing: <BrandIcon templateId="email_marketing" size={24} />,
    video_promo: <BrandIcon templateId="video_promo" size={24} />,
    image_promo: <BrandIcon templateId="image_promo" size={24} />
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <section className="min-h-screen py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <button
            onClick={onBack}
            className={`mb-4 font-medium text-sm flex items-center gap-1.5 ${colorTheme === 'purple' ? 'text-purple-600 hover:text-purple-700' : colorTheme === 'blue' ? 'text-blue-600 hover:text-blue-700' : 'text-emerald-600 hover:text-emerald-700'} transition-colors`}
            data-testid="campaign-detail-back-btn"
          >
            ← Back to Dashboard
          </button>
          
          <div className="bg-white/70 dark:bg-slate-800/50 backdrop-blur-xl border border-slate-200/50 dark:border-slate-700/50 rounded-2xl p-6 mb-8 shadow-sm">
            <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start mb-4">
              <h1 className={`text-3xl font-bold bg-gradient-to-r ${colorTheme === 'purple' ? 'from-purple-600 to-pink-600' : colorTheme === 'blue' ? 'from-blue-600 to-sky-500' : 'from-emerald-600 to-teal-500'} bg-clip-text text-transparent mb-4 lg:mb-0`} data-testid="campaign-detail-title">
                {campaign.product.product_name}
              </h1>
              
              {/* Action Buttons */}
              <div className="flex flex-wrap gap-2">
                {/* Regenerate Campaign */}
                <button
                  onClick={() => onRegenerateCampaign && onRegenerateCampaign(campaign)}
                  className={`flex items-center justify-center px-3 py-2 bg-white/80 dark:bg-slate-700/50 hover:bg-white dark:hover:bg-slate-700 ${colorTheme === 'purple' ? 'text-purple-700 dark:text-purple-300 border-purple-200/50 dark:border-purple-700/50' : colorTheme === 'blue' ? 'text-blue-700 dark:text-blue-300 border-blue-200/50 dark:border-blue-700/50' : 'text-emerald-700 dark:text-emerald-300 border-emerald-200/50 dark:border-emerald-700/50'} border rounded-xl transition-all duration-200 font-medium text-xs shadow-sm hover:shadow-md`}
                  title="Generate new content with same settings"
                  data-testid="campaign-detail-regenerate-btn"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  ✨ Regenerate
                </button>

                {/* Get Feedback Link */}
                <button
                  onClick={async () => {
                    try {
                      if (!sessionToken) {
                        alert('Please sign in to create a feedback link.');
                        return;
                      }
                      
                      // Create public sharing link for feedback
                      const response = await axios.post(`${API}/campaigns/${campaign.id}/share`, 
                        { is_public: true },
                        { headers: { Authorization: `Bearer ${sessionToken}` }}
                      );
                      
                      console.log('Share response:', response.data);
                      
                      if (response.data && response.data.share_url) {
                        const shareUrl = response.data.share_url;
                        
                        // Copy to clipboard with fallback
                        try {
                          await navigator.clipboard.writeText(shareUrl);
                        } catch (clipErr) {
                          console.warn('Clipboard API failed, using fallback:', clipErr);
                          const textArea = document.createElement('textarea');
                          textArea.value = shareUrl;
                          document.body.appendChild(textArea);
                          textArea.select();
                          document.execCommand('copy');
                          document.body.removeChild(textArea);
                        }
                        
                        // Show URL copy modal instead of alert
                        setUrlCopyModal({
                          isOpen: true,
                          url: shareUrl,
                          title: '🔗 Feedback Link Copied!',
                          description: 'Share this link with others to get feedback on your campaign:'
                        });
                      } else {
                        console.error('Share response missing share_url:', response.data);
                        alert('Failed to create feedback link. Please try again.');
                      }
                    } catch (error) {
                      console.error('Error creating feedback link:', error);
                      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
                      alert(`Failed to create feedback link: ${errorMsg}`);
                    }
                  }}
                  className={`flex items-center justify-center px-3 py-2 bg-gradient-to-r ${colorTheme === 'purple' ? 'from-purple-600 to-pink-600' : colorTheme === 'blue' ? 'from-blue-600 to-sky-500' : 'from-emerald-600 to-teal-500'} text-white rounded-xl transition-all duration-200 font-medium text-xs shadow-sm hover:shadow-md hover:-translate-y-0.5`}
                  data-testid="campaign-detail-feedback-link-btn"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-3.582 8-8 8a8.955 8.955 0 01-4.126-.98L3 20l1.98-5.874A8.955 8.955 0 013 12c0-4.418 3.582-8 8-8s8 3.582 8 8z" />
                  </svg>
                Get Feedback Link
                </button>
                
                {/* Publish Entire Campaign */}
                <button
                  onClick={() => onShareEntireCampaign()}
                  className="flex items-center justify-center px-3 py-2 bg-gradient-to-r from-blue-600 to-sky-500 text-white rounded-xl transition-all duration-200 font-medium text-xs shadow-sm hover:shadow-md hover:-translate-y-0.5"
                  data-testid="campaign-detail-publish-btn"
                >
                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                  Publish Entire Campaign
                </button>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* References Used (Product Image(s) + Avatar + Brand Logo) */}
              {(campaign.product.product_image || (Array.isArray(campaign.product.product_images) && campaign.product.product_images.length > 0) || campaign.product.avatar_image_url || campaign.product.brand_logo_url) && (
                <div className="md:col-span-1">
                  <CampaignReferencesPanel product={campaign.product} testIdPrefix="view-details-refs" />
                </div>
              )}
              
              {/* Product Details */}
              <div className={(campaign.product.product_image || (Array.isArray(campaign.product.product_images) && campaign.product.product_images.length > 0) || campaign.product.avatar_image_url || campaign.product.brand_logo_url) ? "md:col-span-2" : "md:col-span-3"}>
                <h3 className="font-semibold text-slate-800 dark:text-white mb-2 text-sm">Product Details</h3>
                <div className="space-y-2 text-xs text-slate-600 dark:text-slate-300">
                  <p><span className="font-medium text-slate-700 dark:text-white">Product Description & Benefits:</span> {campaign.product.product_description || 'Not specified'}</p>
                  {/* Only show Key Benefits if different from Description (avoid duplication) */}
                  {campaign.product.key_benefits && 
                   campaign.product.key_benefits !== campaign.product.product_description && (
                    <p><span className="font-medium dark:text-white">Key Benefits:</span> {campaign.product.key_benefits}</p>
                  )}
                  <p><span className="font-medium dark:text-white">Price:</span> {campaign.product.price || 'Not specified'}</p>
                  <p><span className="font-medium dark:text-white">Category:</span> {campaign.product.category || 'Not specified'}</p>
                  <p><span className="font-medium dark:text-white">Brand Voice:</span> {campaign.product.brand_voice || 'Not specified'}</p>
                  <p><span className="font-medium dark:text-white">Industry/Business Type:</span> {campaign.product.industry_business_type || 'Not specified'}</p>
                  <p><span className="font-medium dark:text-white">Budget Range:</span> {campaign.product.budget_range || 'Not specified'}</p>
                  <p><span className="font-medium dark:text-white">Interests & Behaviors:</span> {campaign.product.interests_behaviors || 'Not specified'}</p>
                </div>
              </div>
              
              {/* Campaign Strategy */}
              {(campaign.product.campaign_objective || campaign.product.primary_platform || campaign.product.pain_point || campaign.product.offer_incentive) && (
                <div className="md:col-span-3">
                  <h3 className="font-semibold text-slate-800 dark:text-white mb-2 text-sm" data-testid="campaign-strategy-section">Campaign Strategy</h3>
                  <div className="flex flex-wrap gap-3">
                    {campaign.product.campaign_objective && (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-lg text-xs font-medium" data-testid="strategy-objective">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                        Objective: <span className="capitalize">{campaign.product.campaign_objective.replace(/_/g, ' ')}</span>
                      </span>
                    )}
                    {campaign.product.primary_platform && (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-lg text-xs font-medium" data-testid="strategy-platform">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2m-9 0h10m-10 0H5a2 2 0 00-2 2v12a2 2 0 002 2h14a2 2 0 002-2V6a2 2 0 00-2-2h-2" /></svg>
                        Platform: <span className="capitalize">{campaign.product.primary_platform}</span>
                      </span>
                    )}
                    {campaign.product.cta_goal && (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 rounded-lg text-xs font-medium" data-testid="strategy-cta">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" /></svg>
                        CTA: <span className="capitalize">{campaign.product.cta_goal.replace(/_/g, ' ')}</span>
                      </span>
                    )}
                    {campaign.product.offer_incentive && (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded-lg text-xs font-medium" data-testid="strategy-offer">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" /></svg>
                        Offer: {campaign.product.offer_incentive}
                      </span>
                    )}
                    {campaign.product.pain_point && (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-lg text-xs font-medium" data-testid="strategy-pain-point">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                        Pain Point: {campaign.product.pain_point}
                      </span>
                    )}
                    {campaign.product.landing_page_url && (
                      <a href={campaign.product.landing_page_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-50 dark:bg-slate-700/50 text-slate-700 dark:text-slate-300 rounded-lg text-xs font-medium hover:underline" data-testid="strategy-landing-page">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                        Landing Page
                      </a>
                    )}
                  </div>
                </div>
              )}
              
              {/* Demographics & Targeting */}
              <div className="md:col-span-1">
                <h3 className="font-semibold text-slate-800 dark:text-white mb-2 text-sm">Demographics & Targeting</h3>
                <div className="space-y-2 text-xs text-slate-600 dark:text-slate-300">
                  <p><span className="font-medium text-slate-700 dark:text-white">Age Range:</span> {campaign.product.age_range_min && campaign.product.age_range_max ? `${campaign.product.age_range_min}-${campaign.product.age_range_max}` : 'Not specified'}</p>
                  <p><span className="font-medium text-slate-700 dark:text-white">Gender:</span> {campaign.product.gender || 'Not specified'}</p>
                  <p><span className="font-medium text-slate-700 dark:text-white">Geography/Location:</span> {campaign.product.geography || 'Not specified'}</p>
                  <p><span className="font-medium dark:text-white">Languages:</span> {(() => {
                    const langs = campaign.product.video_languages?.length > 0 ? campaign.product.video_languages : (campaign.product.languages?.length > 0 ? campaign.product.languages : ['en']);
                    const langMap = { 'en': 'English', 'hi': 'Hindi', 'ta': 'Tamil', 'te': 'Telugu', 'kn': 'Kannada', 'ml': 'Malayalam', 'mr': 'Marathi', 'bn': 'Bengali', 'gu': 'Gujarati', 'pa': 'Punjabi', 'es': 'Spanish', 'fr': 'French', 'de': 'German', 'zh': 'Chinese', 'ja': 'Japanese', 'ar': 'Arabic', 'pt': 'Portuguese' };
                    return langs.map(lang => langMap[lang] || lang.toUpperCase()).join(', ');
                  })()}</p>
                  
                  {/* Show Personas if Available */}
                  {(() => {
                    const personas = [...new Set(campaign.generated_ads
                      .filter(ad => ad.target_persona)
                      .map(ad => ad.target_persona))];
                    
                    if (personas.length > 0) {
                      return (
                        <div>
                          <p className="font-medium dark:text-white">Generated Personas:</p>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {personas.map((persona, index) => (
                              <span key={index} className="bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 text-xs px-2 py-1 rounded">
                                👤 {persona}
                              </span>
                            ))}
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })()}
                  
                  {/* Generation Settings */}
                  <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <p className="font-medium dark:text-white mb-2">Generation Settings:</p>
                    <div className="space-y-1">
                      {/* Only show image model if campaign has image-based ad types */}
                      {(campaign.ad_types?.includes('image_promo') || campaign.ad_types?.includes('telegram_ad') || 
                        campaign.generated_ads?.some(ad => ad.ad_type === 'image_promo' || ad.ad_type === 'telegram_ad')) && 
                        campaign.product.image_model && (
                        <p className="text-xs text-gray-700 dark:text-gray-300">
                          <span className="font-medium dark:text-white">Image Generation:</span> {getImageModelLabel(campaign.product.image_model)}
                          {['nano-banana-pro', 'nano-banana-2'].includes(campaign.product.image_model) && campaign.product.nano_banana_resolution && (
                            <span className="text-slate-500 dark:text-slate-400"> ({campaign.product.nano_banana_resolution})</span>
                          )}
                        </p>
                      )}
                      {/* Localized images toggle status */}
                      {(campaign.ad_types?.includes('image_promo') || 
                        campaign.generated_ads?.some(ad => ad.ad_type === 'image_promo')) && (
                        <p className="text-xs text-gray-700 dark:text-gray-300" data-testid="setting-localized-images">
                          <span className="font-medium dark:text-white">Localized Images:</span>{' '}
                          {campaign.product.generate_localized_images ? (
                            <span className="text-emerald-600 dark:text-emerald-400 font-semibold">
                              ON · one image per language
                            </span>
                          ) : (
                            <span className="text-slate-500 dark:text-slate-400">OFF · single image</span>
                          )}
                        </p>
                      )}
                      {/* Persona settings */}
                      <p className="text-xs text-gray-700 dark:text-gray-300" data-testid="setting-personas">
                        <span className="font-medium dark:text-white">Personas:</span>{' '}
                        {campaign.product.enable_personas ? (
                          <span className="text-purple-600 dark:text-purple-400 font-semibold">
                            ON{campaign.product.persona_count && campaign.product.persona_count !== 'ai_determined'
                              ? ` · ${campaign.product.persona_count}`
                              : ' · AI-determined count'}
                          </span>
                        ) : (
                          <span className="text-slate-500 dark:text-slate-400">OFF</span>
                        )}
                      </p>
                      {/* Logo watermark (only meaningful when a brand logo was attached) */}
                      {campaign.product.brand_logo_url && (
                        <p className="text-xs text-gray-700 dark:text-gray-300" data-testid="setting-logo-watermark">
                          <span className="font-medium dark:text-white">Logo Watermark Fallback:</span>{' '}
                          {campaign.product.apply_logo_watermark !== false ? (
                            <span className="text-emerald-600 dark:text-emerald-400">ON</span>
                          ) : (
                            <span className="text-slate-500 dark:text-slate-400">OFF</span>
                          )}
                        </p>
                      )}
                      {/* Tagline + overlay method (only when tagline present) */}
                      {campaign.product.product_tagline && (
                        <p className="text-xs text-gray-700 dark:text-gray-300" data-testid="setting-tagline">
                          <span className="font-medium dark:text-white">Tagline:</span> "{campaign.product.product_tagline}"
                          {campaign.product.text_overlay_method && (
                            <span className="text-slate-500 dark:text-slate-400"> · rendered via {campaign.product.text_overlay_method === 'ai-text' ? 'AI text' : 'overlay'}</span>
                          )}
                        </p>
                      )}
                      {/* Only show video specs if campaign has video ad types */}
                      {(campaign.ad_types?.includes('video_promo') || campaign.ad_types?.includes('video_ad') || 
                        campaign.generated_ads?.some(ad => ad.ad_type === 'video_promo' || ad.ad_type === 'video_ad')) && (
                        <>
                          {campaign.product.video_model && (
                            <p className="text-xs text-gray-700 dark:text-gray-300">
                              <span className="font-medium dark:text-white">Video Generation:</span> {getVideoModelLabel(campaign.product.video_model)}
                            </p>
                          )}
                          {campaign.product.video_duration && (
                            <p className="text-xs text-gray-700 dark:text-gray-300"><span className="font-medium dark:text-white">Duration:</span> {campaign.product.video_duration}s</p>
                          )}
                          {campaign.product.video_resolution && (
                            <p className="text-xs text-gray-700 dark:text-gray-300"><span className="font-medium dark:text-white">Resolution:</span> {campaign.product.video_resolution}</p>
                          )}
                          {campaign.product.video_aspect_ratio && (
                            <p className="text-xs text-gray-700 dark:text-gray-300"><span className="font-medium dark:text-white">Aspect Ratio:</span> {campaign.product.video_aspect_ratio}</p>
                          )}
                          {campaign.product.video_generation_mode && (
                            <p className="text-xs text-gray-700 dark:text-gray-300">
                              <span className="font-medium dark:text-white">Video Mode:</span>{' '}
                              {{ 'text': 'Text-to-Video', 'image_reference': 'Image-to-Video', 'image_transition': 'Image Transition', 'multi_reference': 'Multi-Reference (Product+Avatar+Logo)' }[campaign.product.video_generation_mode] || campaign.product.video_generation_mode}
                            </p>
                          )}
                          {/* Render-text-in-video setting (only relevant for image-based video modes) */}
                          {campaign.product.video_generation_mode && campaign.product.video_generation_mode !== 'text' && (
                            <p className="text-xs text-gray-700 dark:text-gray-300" data-testid="setting-render-text-video">
                              <span className="font-medium dark:text-white">On-Video Text:</span>{' '}
                              {campaign.product.render_text_in_video ? (
                                <span className="text-amber-600 dark:text-amber-400">ON · model renders text (may mis-spell)</span>
                              ) : (
                                <span className="text-emerald-600 dark:text-emerald-400">OFF · logo carries typography</span>
                              )}
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Display Generated Personas if available */}
          {campaign.personas && campaign.personas.length > 0 && (
            <div className={`bg-white/70 dark:bg-slate-800/50 backdrop-blur-xl border border-slate-200/50 dark:border-slate-700/50 rounded-2xl p-6 mb-8 shadow-sm`}>
              <h3 className={`text-lg font-bold bg-gradient-to-r ${colorTheme === 'purple' ? 'from-purple-600 to-pink-600' : colorTheme === 'blue' ? 'from-blue-600 to-sky-500' : 'from-emerald-600 to-teal-500'} bg-clip-text text-transparent mb-4`}>
                Generated Buyer Personas ({campaign.personas.length})
              </h3>
              {campaign.persona_reasoning && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                  {campaign.persona_reasoning}
                </p>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {campaign.personas.map((persona, index) => (
                  <div key={persona.id || index} className="bg-white/60 dark:bg-slate-700/30 rounded-xl p-4 border border-slate-200/30 dark:border-slate-600/30">
                    <div className="flex items-center mb-3">
                      <div className={`w-8 h-8 bg-gradient-to-br ${colorTheme === 'purple' ? 'from-purple-500 to-pink-500' : colorTheme === 'blue' ? 'from-blue-500 to-sky-500' : 'from-emerald-500 to-teal-500'} rounded-lg flex items-center justify-center text-white font-bold text-sm`}>
                        {persona.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="ml-2">
                        <h4 className="font-bold text-slate-800 dark:text-white text-sm">
                          {persona.name}
                        </h4>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          {persona.age_range} • {persona.gender}
                        </p>
                      </div>
                    </div>
                    
                    <div className="space-y-2 text-xs">
                      <div>
                        <p className="font-semibold text-gray-700 dark:text-gray-300">Profile:</p>
                        <p className="text-gray-600 dark:text-gray-400">
                          {persona.occupation} • {persona.location}
                        </p>
                      </div>
                      
                      {persona.pain_points && persona.pain_points.length > 0 && (
                        <div>
                          <p className="font-semibold text-gray-700 dark:text-gray-300">Pain Points:</p>
                          <div className="flex flex-wrap gap-1">
                            {persona.pain_points.slice(0, 2).map((pain, idx) => (
                              <span key={idx} className="bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-xs px-1 py-0.5 rounded">
                                {pain}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {persona.preferred_platforms && persona.preferred_platforms.length > 0 && (
                        <div>
                          <p className="font-semibold text-gray-700 dark:text-gray-300">Platforms:</p>
                          <div className="flex flex-wrap gap-1">
                            {persona.preferred_platforms.slice(0, 2).map((platform, idx) => (
                              <span key={idx} className="bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs px-1 py-0.5 rounded">
                                {platform}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tab Navigation */}
          <div className="flex gap-2 mb-8">
              <button
                onClick={() => setActiveTab('ads')}
                className={`px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                  activeTab === 'ads'
                    ? `bg-gradient-to-r ${colorTheme === 'purple' ? 'from-purple-600 to-pink-600' : colorTheme === 'blue' ? 'from-blue-600 to-sky-500' : 'from-emerald-600 to-teal-500'} text-white shadow-lg`
                    : 'text-slate-600 dark:text-slate-400 bg-white/50 dark:bg-slate-800/30 hover:bg-white/80 dark:hover:bg-slate-800/60 border border-slate-200/50 dark:border-slate-700/50'
                }`}
                data-testid="campaign-detail-tab-ads"
              >
                Generated Content ({campaign.generated_ads?.length || 0})
              </button>
              <button
                onClick={() => setActiveTab('feedback')}
                className={`px-4 py-2.5 rounded-xl text-sm font-semibold transition-all flex items-center gap-2 ${
                  activeTab === 'feedback'
                    ? `bg-gradient-to-r ${colorTheme === 'purple' ? 'from-purple-600 to-pink-600' : colorTheme === 'blue' ? 'from-blue-600 to-sky-500' : 'from-emerald-600 to-teal-500'} text-white shadow-lg`
                    : 'text-slate-600 dark:text-slate-400 bg-white/50 dark:bg-slate-800/30 hover:bg-white/80 dark:hover:bg-slate-800/60 border border-slate-200/50 dark:border-slate-700/50'
                }`}
                data-testid="campaign-detail-tab-feedback"
              >
                Feedback & Reviews
                {campaign.is_public && feedbackData && feedbackData.feedback_count > 0 && (
                  <span className="bg-white/20 text-[10px] font-bold px-1.5 py-0.5 rounded-md">
                    {feedbackData.feedback_count}
                  </span>
                )}
              </button>
          </div>

          {/* Tab Content */}
          {activeTab === 'ads' && (
            <div>
              <h2 className={`text-xl font-bold bg-gradient-to-r ${colorTheme === 'purple' ? 'from-purple-600 to-pink-600' : colorTheme === 'blue' ? 'from-blue-600 to-sky-500' : 'from-emerald-600 to-teal-500'} bg-clip-text text-transparent mb-6`}>
                Generated Marketing Content
              </h2>
          
            <div className="space-y-6">
              {(() => {
                // Group ads by type
                const adsByType = campaign.generated_ads.reduce((acc, ad) => {
                  const adType = ad.ad_type;
                  if (!acc[adType]) {
                    acc[adType] = [];
                  }
                  acc[adType].push(ad);
                  return acc;
                }, {});

                return Object.entries(adsByType).map(([adType, ads]) => (
                  <div key={adType} className="bg-white/70 dark:bg-slate-800/50 backdrop-blur-xl border border-slate-200/50 dark:border-slate-700/50 rounded-2xl shadow-sm overflow-hidden">
                    <div className="bg-white/40 dark:bg-slate-700/30 px-6 py-4 border-b border-slate-200/50 dark:border-slate-700/50">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <span className="mr-3"><BrandIcon templateId={adType} size={24} /></span>
                          <div>
                            <h3 className="text-sm font-bold text-slate-800 dark:text-white">
                              {getAdTypeLabel(adType, campaign?.product?.video_model, campaign?.product?.image_model)}
                            </h3>
                            <p className="text-xs text-slate-500 dark:text-slate-400">
                              {ads.length > 1 ? `${ads.length} persona variations` : '1 variation'}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            const content = ads.map(ad => 
                              `${ad.title}\n\n${ad.content}\n\n${ad.call_to_action}`
                            ).join('\n\n---\n\n');
                            copyToClipboard(content);
                            setCopiedAll(true);
                            setTimeout(() => setCopiedAll(false), 2000);
                          }}
                          className={`text-sm font-medium ${colorTheme === 'purple' ? 'text-purple-600 hover:text-purple-700' : 'text-green-600 hover:text-green-700'}`}
                        >
                          {copiedAll ? '✅ Copied All!' : '📋 Copy All'}
                        </button>
                      </div>
                    </div>
                    
                    <div className="p-6">
                      <div className={`grid gap-4 ${ads.length > 1 ? 'lg:grid-cols-2' : 'grid-cols-1'}`}>
                        {ads.map((ad, adIndex) => (
                          <div key={adIndex} className="bg-slate-50/80 dark:bg-slate-700/30 rounded-xl p-4 border border-slate-200/30 dark:border-slate-600/30">
                            {ad.target_persona && (
                              <div className="flex items-center space-x-2 mb-3">
                                <span className={`text-xs px-2 py-1 rounded-full font-medium ${colorTheme === 'purple' ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300' : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'}`}>
                                  👤 {ad.target_persona}
                                </span>
                                {ad.persona_reasoning && (
                                  <span className="text-xs text-gray-500 dark:text-gray-400" title={ad.persona_reasoning}>
                                    ℹ️
                                  </span>
                                )}
                              </div>
                            )}
                            
                            <div className="space-y-3">
                              <div>
                                <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                                  Title
                                </p>
                                <p className="font-semibold text-gray-900 dark:text-white">
                                  {cleanAdTitle(ad.title)}
                                </p>
                              </div>
                              
                              <div>
                                <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                                  Content
                                </p>
                                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                                  {ad.content}
                                </p>
                              </div>
                              
                              <div>
                                <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                                  Call to Action
                                </p>
                                <p className={`font-semibold ${colorTheme === 'purple' ? 'text-purple-600 dark:text-purple-400' : colorTheme === 'blue' ? 'text-blue-600 dark:text-blue-400' : 'text-green-600 dark:text-green-400'}`}>
                                  {ad.call_to_action}
                                </p>
                              </div>
                              
                              {/* Media Content */}
                              {ad.video_url && (
                                <div>
                                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                                    Generated Video
                                  </p>
                                  <video className="w-full rounded-lg" controls preload="metadata">
                                    <source src={ad.video_url} type="video/mp4" />
                                    Your browser does not support the video tag.
                                  </video>
                                </div>
                              )}
                              
                              {ad.image_url && (
                                <div>
                                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                                    Generated Image{ad.target_persona ? ` for ${ad.target_persona}` : ''}
                                  </p>
                                  <img 
                                    src={ad.image_url} 
                                    alt={'Generated promotional image' + (ad.target_persona ? ' for ' + ad.target_persona : '')} 
                                    className="w-full rounded-lg"
                                    crossOrigin="anonymous"
                                    loading="lazy"
                                  />
                                </div>
                              )}
                              
                              {ad.audio_url && (
                                <div>
                                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                                    Generated Audio
                                  </p>
                                  <audio className="w-full" controls>
                                    <source src={ad.audio_url} type="audio/mpeg" />
                                    Your browser does not support the audio tag.
                                  </audio>
                                </div>
                              )}
                              
                              {/* Individual Ad Actions */}
                              <div className="pt-3 border-t border-slate-200/30 dark:border-slate-600/30 space-y-2">
                                {/* Regenerate Individual Ad Button */}
                                <button
                                  onClick={() => {
                                    console.log('Regenerate button clicked', { adType, persona: ad.target_persona, hasFunction: !!onRegenerateIndividualAd });
                                    if (onRegenerateIndividualAd) {
                                      onRegenerateIndividualAd(campaign, adType, ad.target_persona);
                                    } else {
                                      console.error('onRegenerateIndividualAd function not available');
                                      alert('Regenerate function not available. Please refresh the page.');
                                    }
                                  }}
                                  className={`w-full flex items-center justify-center px-3 py-2 bg-white/80 dark:bg-slate-600/30 hover:bg-white dark:hover:bg-slate-600/50 ${colorTheme === 'purple' ? 'text-purple-700 dark:text-purple-300 border-purple-200/50 dark:border-purple-700/50' : colorTheme === 'blue' ? 'text-blue-700 dark:text-blue-300 border-blue-200/50 dark:border-blue-700/50' : 'text-emerald-700 dark:text-emerald-300 border-emerald-200/50 dark:border-emerald-700/50'} border rounded-xl transition-all duration-200 font-medium text-xs`}
                                  title={`Regenerate this ${adType}${ad.target_persona ? ' for ' + ad.target_persona : ''}`}
                                  data-testid={`regenerate-ad-${adType}-${adIndex}`}
                                >
                                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                  </svg>
                                  ✨ Regenerate This Ad
                                </button>
                                
                                {/* Regenerate in different language (image_promo + Ultra Image only) */}
                                {adType === 'image_promo' && (campaign.product?.video_languages?.length > 1 || campaign.product?.image_languages?.length > 1) && (
                                  (() => {
                                    const LANG_NAMES = { en: 'English', es: 'Spanish', fr: 'French', de: 'German', hi: 'Hindi', ta: 'Tamil', te: 'Telugu', bn: 'Bengali', mr: 'Marathi', gu: 'Gujarati', kn: 'Kannada', ml: 'Malayalam', pa: 'Punjabi', ar: 'Arabic', zh: 'Mandarin', ja: 'Japanese', ko: 'Korean', pt: 'Portuguese', it: 'Italian', ms: 'Malay' };
                                    const langs = campaign.product?.image_languages?.length > 0 ? campaign.product.image_languages : (campaign.product?.video_languages || []);
                                    const currentLang = ad.language || 'en';
                                    const otherLangs = langs.filter(l => l && l !== currentLang);
                                    if (otherLangs.length === 0) return null;
                                    return (
                                      <div className="flex flex-wrap gap-1.5 pt-1" data-testid={`language-regen-${adIndex}`}>
                                        <span className="text-[10px] font-medium text-slate-500 dark:text-slate-400 self-center mr-1">🌐 Regen in:</span>
                                        {otherLangs.map(langCode => (
                                          <button
                                            key={langCode}
                                            onClick={() => {
                                              if (onRegenerateIndividualAd) {
                                                onRegenerateIndividualAd(campaign, adType, ad.target_persona, langCode);
                                              }
                                            }}
                                            className={`px-2 py-1 rounded-md text-[11px] font-medium border transition-colors ${
                                              colorTheme === 'purple'
                                                ? 'border-purple-200 dark:border-purple-700 text-purple-700 dark:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-950/40'
                                                : colorTheme === 'blue'
                                                ? 'border-blue-200 dark:border-blue-700 text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-950/40'
                                                : 'border-emerald-200 dark:border-emerald-700 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-950/40'
                                            }`}
                                            title={`Regenerate this image with ${LANG_NAMES[langCode] || langCode.toUpperCase()} on-image text`}
                                            data-testid={`regen-lang-${langCode}-${adIndex}`}
                                          >
                                            {LANG_NAMES[langCode] || langCode.toUpperCase()}
                                          </button>
                                        ))}
                                      </div>
                                    );
                                  })()
                                )}

                                {/* Regenerate in different language (video_promo / video_ad — Ultra Pro 🪐 Gemini Omni narration) */}
                                {(adType === 'video_promo' || adType === 'video_ad') && (
                                  (() => {
                                    const LANG_NAMES = { en: 'English', es: 'Spanish', fr: 'French', de: 'German', hi: 'Hindi', ta: 'Tamil', te: 'Telugu', bn: 'Bengali', mr: 'Marathi', gu: 'Gujarati', kn: 'Kannada', ml: 'Malayalam', pa: 'Punjabi', ar: 'Arabic', zh: 'Mandarin', ja: 'Japanese', ko: 'Korean', pt: 'Portuguese', it: 'Italian', ms: 'Malay' };
                                    // Source langs from the campaign first; fall back to a curated quick-pick set
                                    // so users can re-narrate even single-language campaigns in Tamil/Hindi/etc.
                                    const campaignLangs = campaign.product?.video_languages?.length > 0 ? campaign.product.video_languages : [];
                                    const QUICK_PICK = ['ta', 'hi', 'es', 'ar', 'zh', 'fr'];
                                    const currentLang = ad.language || 'en';
                                    let langs = campaignLangs.filter((l) => l && l !== currentLang);
                                    if (langs.length === 0) {
                                      // Single-language campaign — offer the curated set (excluding current).
                                      langs = QUICK_PICK.filter((l) => l !== currentLang);
                                    }
                                    if (langs.length === 0) return null;
                                    return (
                                      <div className="flex flex-wrap gap-1.5 pt-1" data-testid={`video-language-regen-${adIndex}`}>
                                        <span className="text-[10px] font-medium text-slate-500 dark:text-slate-400 self-center mr-1">🌐 Re-narrate in:</span>
                                        {langs.map((langCode) => (
                                          <button
                                            key={langCode}
                                            onClick={() => {
                                              if (onRegenerateIndividualAd) {
                                                onRegenerateIndividualAd(campaign, adType, ad.target_persona, langCode);
                                              }
                                            }}
                                            className={`px-2 py-1 rounded-md text-[11px] font-medium border transition-colors ${
                                              colorTheme === 'purple'
                                                ? 'border-purple-200 dark:border-purple-700 text-purple-700 dark:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-950/40'
                                                : colorTheme === 'blue'
                                                ? 'border-blue-200 dark:border-blue-700 text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-950/40'
                                                : 'border-emerald-200 dark:border-emerald-700 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-950/40'
                                            }`}
                                            title={`Regenerate this video with ${LANG_NAMES[langCode] || langCode.toUpperCase()} narration (Ultra Pro 🪐)`}
                                            data-testid={`video-regen-lang-${langCode}-${adIndex}`}
                                          >
                                            {LANG_NAMES[langCode] || langCode.toUpperCase()}
                                          </button>
                                        ))}
                                      </div>
                                    );
                                  })()
                                )}
                                
                                {/* Publish Individual Ad Button */}
                                <button
                                  onClick={() => {
                                    const overallAdIndex = campaign.generated_ads.findIndex(
                                      campaignAd => campaignAd === ad
                                    );
                                    onShareIndividualAd(ad, overallAdIndex);
                                  }}
                                  className={`w-full flex items-center justify-center px-3 py-2 bg-gradient-to-r ${colorTheme === 'purple' ? 'from-purple-600 to-pink-600' : colorTheme === 'blue' ? 'from-blue-600 to-sky-500' : 'from-emerald-600 to-teal-500'} text-white rounded-xl transition-all duration-200 font-medium text-xs shadow-sm hover:shadow-md`}
                                  data-testid={`publish-ad-${adType}-${adIndex}`}
                                >
                                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                                  </svg>
                                  🚀 Publish This Ad
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ));
              })()}
            </div>
            </div>
          )}

          {/* Feedback Tab Content */}
          {activeTab === 'feedback' && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                Campaign Feedback & Reviews
              </h2>

              {!campaign.is_public ? (
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                        Campaign Not Public
                      </h3>
                      <div className="mt-2 text-sm text-yellow-700 dark:text-yellow-300">
                        <p>This campaign is private. To receive feedback and reviews, make it public by sharing it first.</p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : isLoadingFeedback ? (
                <div className="flex items-center justify-center py-12">
                  <div className={`animate-spin rounded-full h-8 w-8 border-b-2 ${colorTheme === 'purple' ? 'border-purple-600' : colorTheme === 'blue' ? 'border-blue-600' : 'border-green-600'}`}></div>
                  <span className="ml-3 text-gray-600 dark:text-gray-400">Loading feedback...</span>
                </div>
              ) : feedbackData && feedbackData.feedback_count > 0 ? (
                <div>
                  {/* Feedback Statistics */}
                  <div className="bg-gradient-to-br from-blue-50 to-purple-50 dark:from-gray-800 dark:to-gray-700 rounded-lg p-6 mb-6 border border-blue-200 dark:border-gray-600">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-gray-900 dark:text-white">
                          {feedbackData.feedback_count}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          Total Reviews
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-gray-900 dark:text-white">
                          {feedbackData.average_rating}/5
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          Average Rating
                        </div>
                        <div className="text-yellow-400 text-lg">
                          {renderStarRating(Math.round(feedbackData.average_rating))}
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">Rating Distribution</div>
                        <div className="space-y-1">
                          {[5, 4, 3, 2, 1].map(rating => (
                            <div key={rating} className="flex items-center text-xs">
                              <span className="w-4">{rating}★</span>
                              <div className="flex-1 bg-gray-200 dark:bg-gray-600 rounded-full h-2 mx-2">
                                <div 
                                  className="bg-yellow-400 h-2 rounded-full" 
                                  style={{ 
                                    width: `${feedbackData.feedback_count > 0 ? (feedbackData.rating_distribution[rating] / feedbackData.feedback_count) * 100 : 0}%` 
                                  }}
                                ></div>
                              </div>
                              <span className="w-8 text-right">{feedbackData.rating_distribution[rating]}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Feedback List */}
                  <div className="space-y-6">
                    {feedbackData.feedback_list.map((feedback) => (
                      <div key={feedback.id} className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 border border-gray-200 dark:border-gray-600">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center">
                            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold">
                              {feedback.author_name.charAt(0).toUpperCase()}
                            </div>
                            <div className="ml-3">
                              <h4 className="font-semibold text-gray-900 dark:text-white">
                                {feedback.author_name}
                              </h4>
                              <div className="flex items-center space-x-2">
                                <span className={`text-lg ${getRatingColor(feedback.rating)}`}>
                                  {renderStarRating(feedback.rating)}
                                </span>
                                <span className="text-sm text-gray-500 dark:text-gray-400">
                                  {formatFeedbackDate(feedback.created_at)}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        <div className="mb-4">
                          <p className="text-gray-700 dark:text-gray-300">{feedback.comment}</p>
                        </div>

                        {/* Replies */}
                        {feedback.replies && feedback.replies.length > 0 && (
                          <div className="border-t border-gray-200 dark:border-gray-600 pt-4">
                            <h5 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-3">
                              Replies ({feedback.replies.length})
                            </h5>
                            <div className="space-y-3">
                              {feedback.replies.map((reply) => (
                                <div key={reply.id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                                  <div className="flex items-center mb-2">
                                    <div className="w-6 h-6 bg-gradient-to-br from-green-500 to-blue-500 rounded-full flex items-center justify-center text-white font-bold text-xs">
                                      {reply.author_name.charAt(0).toUpperCase()}
                                    </div>
                                    <div className="ml-2">
                                      <span className="font-medium text-gray-900 dark:text-white text-sm">
                                        {reply.author_name}
                                      </span>
                                      <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                                        {formatFeedbackDate(reply.created_at)}
                                      </span>
                                    </div>
                                  </div>
                                  <p className="text-sm text-gray-700 dark:text-gray-300">{reply.comment}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-3.582 8-8 8a8.959 8.959 0 01-4.906-1.468L3 21l2.468-5.094A8.959 8.959 0 013 12c0-4.418 3.582-8 8-8s8 3.582 8 8z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    No User Feedback Yet
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400">
                    Share your campaign to start receiving feedback and reviews from users.
                  </p>
                </div>
              )}

              {/* AI Feedback Section */}
              <div className="mt-8 border-t border-gray-200 dark:border-gray-700 pt-8">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
                  <span className="mr-3">🤖</span>
                  AI-Generated Feedback & Analysis
                  <span className="ml-3 text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                    AI Insights
                  </span>
                </h3>

                {isLoadingAiFeedback ? (
                  <div className="flex items-center justify-center py-8">
                    <div className={`animate-spin rounded-full h-6 w-6 border-b-2 ${colorTheme === 'purple' ? 'border-purple-600' : colorTheme === 'blue' ? 'border-blue-600' : 'border-green-600'}`}></div>
                    <span className="ml-3 text-gray-600 dark:text-gray-400">Loading AI feedback...</span>
                  </div>
                ) : aiFeedback && (aiFeedback.feedback_count > 0 || (aiFeedback.feedback && aiFeedback.feedback.length > 0)) ? (
                  <div>
                    {/* AI Feedback Statistics */}
                    <div className={`bg-gradient-to-br ${colorTheme === 'purple' ? 'from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border-purple-200 dark:border-purple-600' : colorTheme === 'blue' ? 'from-blue-50 to-sky-50 dark:from-blue-900/20 dark:to-sky-900/20 border-blue-200 dark:border-blue-600' : 'from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-green-200 dark:border-green-600'} rounded-lg p-6 mb-6 border`}>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="text-center">
                          <div className="text-2xl font-bold text-green-600">
                            {aiFeedback.feedback_count || aiFeedback.total_feedback || (aiFeedback.feedback && aiFeedback.feedback.length) || 0}
                          </div>
                          <div className="text-sm text-green-700 dark:text-green-300">
                            AI Reviews
                          </div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-green-600">
                            {(aiFeedback.average_rating || 0).toFixed(1)}/5
                          </div>
                          <div className="text-sm text-green-700 dark:text-green-300">
                            AI Rating
                          </div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-green-600">
                            {Math.round((aiFeedback.sentiment_distribution?.positive || 0) / (aiFeedback.feedback_count || 1) * 100)}%
                          </div>
                          <div className="text-sm text-green-700 dark:text-green-300">
                            Positive
                          </div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-green-600">
                            {aiFeedback.consolidated_suggestions?.length || 0}
                          </div>
                          <div className="text-sm text-green-700 dark:text-green-300">
                            Improvements
                          </div>
                        </div>
                      </div>
                      
                      <div className="mt-4 text-xs text-green-600 dark:text-green-400 text-center">
                        Generated on {aiFeedback.saved_at ? new Date(aiFeedback.saved_at).toLocaleDateString() : 'Recently'} using {aiFeedback.generation_info?.model_used || 'AI'}
                      </div>
                    </div>

                    {/* AI Improvement Suggestions */}
                    {aiFeedback.consolidated_suggestions && aiFeedback.consolidated_suggestions.length > 0 && (
                      <div className="bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 border border-green-200 dark:border-green-600 rounded-lg p-6 mb-6">
                        <h4 className="text-lg font-semibold text-green-800 dark:text-green-200 mb-4">
                          💡 AI Improvement Recommendations
                        </h4>
                        <div className="space-y-3">
                          {aiFeedback.consolidated_suggestions.map((suggestion, idx) => (
                            <div key={idx} className="flex items-start">
                              <span className="text-green-600 font-bold mr-3">{idx + 1}.</span>
                              <span className="text-green-800 dark:text-green-200 leading-relaxed">{suggestion}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* AI Feedback List */}
                    {aiFeedback.feedback && aiFeedback.feedback.length > 0 && (
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                          Individual AI Reviews ({aiFeedback.feedback.length})
                        </h4>
                        <div className="space-y-4">
                          {aiFeedback.feedback.map((feedback, index) => (
                            <div key={index} className="bg-blue-50 dark:bg-blue-900/20 rounded-lg shadow border border-blue-200 dark:border-blue-600 p-4">
                              <div className="flex items-start justify-between mb-3">
                                <div className="flex items-center">
                                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-bold text-xl">
                                    {feedback.user_type === 'enthusiast' ? '😍' : 
                                     feedback.user_type === 'skeptical' ? '🤔' : 
                                     feedback.user_type === 'expert' ? '🎓' : 
                                     feedback.user_type === 'critic' ? '😐' : '👤'}
                                  </div>
                                  <div className="ml-3">
                                    <h5 className="font-semibold text-blue-800 dark:text-blue-200 capitalize">
                                      {feedback.user_type} User
                                    </h5>
                                    <div className="flex items-center space-x-2">
                                      <span className="text-yellow-400 text-lg">
                                        {renderStarRating(feedback.rating)}
                                      </span>
                                      <span className="text-sm text-blue-600 dark:text-blue-400">
                                        ({feedback.rating}/5)
                                      </span>
                                      <span className={`text-xs px-2 py-1 rounded-full ${
                                        feedback.sentiment === 'positive' ? 'bg-green-100 text-green-800' :
                                        feedback.sentiment === 'negative' ? 'bg-red-100 text-red-800' :
                                        'bg-yellow-100 text-yellow-800'
                                      }`}>
                                        {feedback.sentiment}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                  AI Generated
                                </span>
                              </div>
                              <p className="text-blue-800 dark:text-blue-200 leading-relaxed">
                                {feedback.comment}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-3xl">🤖</span>
                    </div>
                    <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                      No AI Feedback Generated
                    </h4>
                    <p className="text-gray-500 dark:text-gray-400">
                      Generate AI feedback for this campaign to get insights and improvement suggestions.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* URL Copy Modal */}
      <URLCopyModal
        isOpen={urlCopyModal.isOpen}
        onClose={() => setUrlCopyModal(prev => ({ ...prev, isOpen: false }))}
        url={urlCopyModal.url}
        title={urlCopyModal.title}
        description={urlCopyModal.description}
      />
    </section>
  );
};

const ProductForm = ({ onSubmit, isLoading, clearTrigger, generationProgress, setGenerationProgress, isGenerating, setIsGenerating, setCreditPreviewData, setShowCreditPreview, setPendingGenerationData, pendingGenerationData }) => {
  const { sessionToken, isAuthenticated, login } = useAuth();
  const { colorTheme } = useColorTheme();

  // Show authentication required message if user is not authenticated
  if (!isAuthenticated && !sessionToken) {
    return (
      <div className={`min-h-screen bg-gradient-to-br ${colorTheme === 'purple' ? 'from-purple-50 via-white to-pink-50' : colorTheme === 'blue' ? 'from-blue-50 via-white to-sky-50' : 'from-green-50 via-white to-emerald-50'} dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center`}>
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 max-w-md w-full shadow-xl border border-gray-200 dark:border-gray-700">
          <div className="text-center">
            <div className="text-6xl mb-4">🔐</div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Authentication Required
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Please sign in to access the Ad Creator Hub and generate AI-powered marketing content.
            </p>
            <button
              onClick={() => {
                // Redirect to authentication or trigger login modal
                window.location.href = '/';
              }}
              className={`w-full bg-gradient-to-r ${colorTheme === 'purple' ? 'from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600' : 'from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600'} text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 transform hover:scale-105`}
            >
              Go to Sign In
            </button>
          </div>
        </div>
      </div>
    );
  }
  const [formData, setFormData] = useState({
    product_name: '',
    product_description: '',
    key_benefits: '',
    brand_voice: 'professional',
    price: '',
    category: '',
    product_image: '',
    product_images: [], // Multiple images for video transition mode
    avatar_image_url: '', // User-uploaded portrait/avatar for hero placement in generated ads
    age_range_min: 25,
    age_range_max: 55,
    gender: 'All',
    geography: '',
    geographies: [], // Multiple geographies for multi-region campaigns
    video_languages: [], // Languages for video voiceover
    video_duration: 10,
    video_resolution: '720p',
    video_aspect_ratio: '16:9',
    video_model: 'gemini_omni_video', // Ultra Pro 🪐 (Default - Recommended)
    video_platform_preset: null, // Set by VideoPlatformPicker (5-tile picker)
    video_generation_mode: 'text', // text, image_reference, image_transition, multi_reference
    render_text_in_video: false, // OFF by default — Veo 3 often mis-spells brand names
    image_model: 'freepik', // Best Value (Recommended)
    nano_banana_resolution: '4K', // Default resolution for Nano Banana Pro
    generate_localized_images: false, // When ON, generates 1 image per language (Ultra Image only)
    // Text overlay options
    product_tagline: '',  // Optional tagline for branding
    text_overlay_method: 'ai-text',  // 'overlay' (post-processing) or 'ai-text' (AI-generated)
    // Persona-driven optimization fields
    industry_business_type: '',
    budget_range: '',
    interests_behaviors: '',
    persona_count: 'ai_determined',
    enable_personas: false,  // Start with false, users can toggle on if needed
    // Website URL for auto-fill
    website_url: '',
    // Campaign Strategy fields
    campaign_objective: '',
    primary_platform: 'instagram',
    cta_goal: '',
    offer_incentive: '',
    landing_page_url: '',
    pain_point: '',
    // Strict Reference Mode — when ON, tells image & video models to treat
    // uploaded/extracted references as pixel-faithful source of truth AND
    // keeps on-image text short + medium-sized to avoid spelling glitches.
    // Recommended when the user attaches their own product/logo/founder images.
    strict_reference_mode: false,
    // Voice narration overlay — Kling 3.0 / Seedance 2.0 ship silent. When
    // this is ON (default), the backend runs an ElevenLabs multilingual TTS
    // over each silent video after generation and muxes the result to a new
    // S3 MP4 with real spoken narration. Ignored for models with native
    // audio (Veo 3, Gemini Omni, Sora 2, Ultra Video).
    add_voice_narration: true,
    // ElevenLabs cloned voice_id — empty means auto-pick a stock voice.
    cloned_voice_id: ''
  });
  
  // State for website extraction
  const [isExtractingWebsite, setIsExtractingWebsite] = useState(false);
  const [websiteExtractionError, setWebsiteExtractionError] = useState('');
  const [websiteExtractionSuccess, setWebsiteExtractionSuccess] = useState(false);
  // Product catalog scraped from the ingested website URL. Each entry:
  //   { name, image_url, product_url, price, original_price, discount_pct }
  // User can remove ones they don't want and click a card to swap the
  // product image + name/price into the campaign form.
  const [extractedProducts, setExtractedProducts] = useState([]);
  const [selectedExtractedProductIdx, setSelectedExtractedProductIdx] = useState(null);
  // Founder / team avatars scraped from about-us/founder/team pages of the
  // same website. Each entry: { name, role, image_url, context_text }.
  // Click a card → sets avatar_image_url (single) OR appends to
  // avatar_image_urls (multi). Remove with the ✕ button.
  const [extractedAvatars, setExtractedAvatars] = useState([]);
  const [selectedExtractedAvatarIdxs, setSelectedExtractedAvatarIdxs] = useState([]);
  // Cloned voices (ElevenLabs). User uploads an audio sample → we call
  // /api/voices/clone → get back voice_id → they can pick it per campaign.
  // Persisted server-side under user.cloned_voices; fetched on mount.
  const [myClonedVoices, setMyClonedVoices] = useState([]);
  const [isCloningVoice, setIsCloningVoice] = useState(false);
  const [voiceCloneError, setVoiceCloneError] = useState('');
  const [previewingVoiceId, setPreviewingVoiceId] = useState('');
  const voicePreviewAudioRef = useRef(null);
  // Cache voice_id -> data:audio/mpeg URL so download-after-preview is instant
  // and doesn't burn a second ElevenLabs call.
  const voicePreviewCacheRef = useRef({});
  // Modal state (rename + delete) — replaces window.prompt / window.confirm so
  // the flow matches the rest of the app's polished modal design.
  const [voiceRenameModal, setVoiceRenameModal] = useState({ isOpen: false, voice: null, isProcessing: false });
  const [voiceDeleteModal, setVoiceDeleteModal] = useState({ isOpen: false, voice: null, isProcessing: false });
  
  // Phase 4: Credit preview - (moved to main App component scope)

  // 🆕 Kling 3.0 / Seedance 2.0 model registry — fetched once from backend so
  // the dropdown, credit hint & smart-default logic can stay data-driven.
  const [newVideoModels, setNewVideoModels] = useState([]);
  const [newVideoModelsById, setNewVideoModelsById] = useState({});
  const [recommendedNewVideoModel, setRecommendedNewVideoModel] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/video-models/new`);
        if (cancelled) return;
        const list = res?.data?.models || [];
        setNewVideoModels(list);
        const byId = {};
        for (const m of list) byId[m.id] = m;
        setNewVideoModelsById(byId);
      } catch (e) {
        console.warn('Could not load new video models list:', e?.message);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Load the user's cloned voices on mount (auth required)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('session_token');
        if (!token) return; // Not logged in yet — endpoint requires auth
        const res = await axios.get(`${API}/voices/mine`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        setMyClonedVoices(res?.data?.voices || []);
      } catch (e) {
        // Not fatal — user might not be logged in yet
        console.warn('Could not load cloned voices:', e?.message);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (newVideoModels.length === 0) return;
    const refCount = (formData.product_images?.length || 0)
      + (formData.avatar_image_urls?.length || 0)
      + (formData.avatar_image_url ? 1 : 0)
      + (formData.brand_logo_url ? 1 : 0);
    const payload = {
      category: formData.category || '',
      industry_business_type: formData.industry_business_type || '',
      campaign_objective: formData.campaign_objective || '',
      ref_image_count: refCount,
      video_languages: formData.video_languages || [],
    };
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.post(`${API}/video-models/suggest-default`, payload);
        if (!cancelled) setRecommendedNewVideoModel(res?.data?.model_id || null);
      } catch (_) { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, [
    newVideoModels.length,
    formData.category,
    formData.industry_business_type,
    formData.campaign_objective,
    formData.video_languages,
    formData.product_images,
    formData.avatar_image_urls,
    formData.avatar_image_url,
    formData.brand_logo_url,
  ]);


  // Currency mapping for different countries
  const getCurrencyAndBudgetRanges = (geography) => {
    const currencyMap = {
      'United States': {
        currency: 'USD',
        symbol: '$',
        ranges: [
          'Under $100',
          '$100-$500',
          '$500-$1,000',
          '$1,000-$5,000',
          '$5,000-$10,000',
          '$10,000+',
          'Enterprise'
        ]
      },
      'United Kingdom': {
        currency: 'GBP',
        symbol: '£',
        ranges: [
          'Under £80',
          '£80-£400',
          '£400-£800',
          '£800-£4,000',
          '£4,000-£8,000',
          '£8,000+',
          'Enterprise'
        ]
      },
      'India': {
        currency: 'INR',
        symbol: '₹',
        ranges: [
          'Under ₹8,000',
          '₹8,000-₹40,000',
          '₹40,000-₹80,000',
          '₹80,000-₹4,00,000',
          '₹4,00,000-₹8,00,000',
          '₹8,00,000+',
          'Enterprise'
        ]
      },
      'Australia': {
        currency: 'AUD',
        symbol: 'A$',
        ranges: [
          'Under A$150',
          'A$150-A$750',
          'A$750-A$1,500',
          'A$1,500-A$7,500',
          'A$7,500-A$15,000',
          'A$15,000+',
          'Enterprise'
        ]
      },
      'Singapore': {
        currency: 'SGD',
        symbol: 'S$',
        ranges: [
          'Under S$140',
          'S$140-S$700',
          'S$700-S$1,400',
          'S$1,400-S$7,000',
          'S$7,000-S$14,000',
          'S$14,000+',
          'Enterprise'
        ]
      },
      'Canada': {
        currency: 'CAD',
        symbol: 'C$',
        ranges: [
          'Under C$130',
          'C$130-C$650',
          'C$650-C$1,300',
          'C$1,300-C$6,500',
          'C$6,500-C$13,000',
          'C$13,000+',
          'Enterprise'
        ]
      },
      'Germany': {
        currency: 'EUR',
        symbol: '€',
        ranges: [
          'Under €90',
          '€90-€450',
          '€450-€900',
          '€900-€4,500',
          '€4,500-€9,000',
          '€9,000+',
          'Enterprise'
        ]
      },
      'France': {
        currency: 'EUR',
        symbol: '€',
        ranges: [
          'Under €90',
          '€90-€450',
          '€450-€900',
          '€900-€4,500',
          '€4,500-€9,000',
          '€9,000+',
          'Enterprise'
        ]
      }
    };

    return currencyMap[geography] || {
      currency: 'USD',
      symbol: '$',
      ranges: [
        'Under $100',
        '$100-$500',
        '$500-$1,000',
        '$1,000-$5,000',
        '$5,000-$10,000',
        '$10,000+',
        'Enterprise'
      ]
    };
  };

  const currentBudgetOptions = getCurrencyAndBudgetRanges(formData.geography);

  // Clear budget range when geography changes (currency change)
  useEffect(() => {
    if (formData.geography && formData.budget_range) {
      const currentOptions = getCurrencyAndBudgetRanges(formData.geography);
      if (!currentOptions.ranges.includes(formData.budget_range)) {
        setFormData(prev => ({ ...prev, budget_range: '' }));
      }
    }
  }, [formData.geography]);

  const [selectedAdTypes, setSelectedAdTypes] = useState([
    'facebook_ad',
    'instagram_ad',
    'google_ads',
    'product_description',
    'video_promo',
    'image_promo'
  ]);

  const [imagePreview, setImagePreview] = useState(null);
  const [showAdPreview, setShowAdPreview] = useState(false);
  // Free-form additional instructions for this generation. Empty = no override.
  const [userFeedback, setUserFeedback] = useState('');

  // Clear form when clearTrigger changes
  useEffect(() => {
    if (clearTrigger > 0) {
      clearForm();
    }
  }, [clearTrigger]);

  // Close credit preview modal when cost-affecting fields change
  useEffect(() => {
    // If credit preview is open and user changes video/image settings, close it to force re-fetch
    if (setShowCreditPreview) {
      setShowCreditPreview(false);
    }
  }, [formData.video_model, formData.video_resolution, formData.video_duration, formData.image_model, formData.nano_banana_resolution, formData.enable_personas, formData.persona_count, setShowCreditPreview]);

  // Auto-enable "Generate Localized Images" the first time the user picks 2+
  // languages with a multilingual-capable model selected. They can still toggle
  // it OFF if they only want one image — but the default now matches intent.
  useEffect(() => {
    const multiLang = (formData.video_languages?.length || 0) > 1;
    const supportsLocalized = ['gpt4o-image', 'nano-banana-pro', 'nano-banana-2'].includes(formData.image_model);
    if (multiLang && supportsLocalized && !formData.generate_localized_images) {
      setFormData((prev) => ({ ...prev, generate_localized_images: true }));
    }
    // Intentionally do NOT auto-disable when languages drop back to 1 —
    // honour explicit user toggle once they have engaged with it.
  }, [formData.video_languages, formData.image_model, formData.generate_localized_images]);

  // Auto-suggest Multi-Reference Video mode when 2+ brand assets are uploaded
  // and the user hasn't already chosen a non-default mode. Honour explicit
  // user choice once they've engaged with the radio group.
  useEffect(() => {
    const refCount =
      ((formData.product_images?.length || 0) > 0 ? 1 : 0) +
      (formData.avatar_image_url ? 1 : 0) +
      (formData.brand_logo_url ? 1 : 0);
    if (refCount >= 2 && formData.video_generation_mode === 'text') {
      setFormData((prev) => ({ ...prev, video_generation_mode: 'multi_reference' }));
    }
  }, [formData.product_images, formData.avatar_image_url, formData.brand_logo_url, formData.video_generation_mode]);


  const clearForm = () => {
    setFormData({
      product_name: '',
      product_description: '',
      key_benefits: '',
      brand_voice: 'professional',
      price: '',
      category: '',
      product_image: '',
      product_images: [], // Multiple images for video transition mode
      avatar_image_url: '',
      age_range_min: 18,
      age_range_max: 65,
      gender: 'All',
      geography: '',
      geographies: [], // Multiple geographies for multi-region campaigns
      video_languages: [], // Languages for video voiceover
      video_duration: 10,
      video_resolution: '720p',
      video_aspect_ratio: '16:9',
      video_model: 'gemini_omni_video', // Ultra Pro 🪐 (Default - Recommended)
      video_platform_preset: null, // Set by VideoPlatformPicker
      video_generation_mode: 'text', // text, image_reference, image_transition
      image_model: 'freepik', // Best Value (Recommended)
      // Persona-driven optimization fields
      industry_business_type: '',
      budget_range: '',
      interests_behaviors: '',
      persona_count: 'ai_determined',
      enable_personas: false  // Start with false, users can toggle on if needed
    });
    setSelectedAdTypes([
      'social_media',
      'google_ads',
      'product_description',
      'email_marketing',
      'video_promo',
      'image_promo'
    ]);
    setImagePreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent multiple submissions
    if (isGenerating) {
      return;
    }
    
    // ALWAYS reset flag at the START of each new campaign generation
    // This ensures each campaign starts fresh with sync flow as default
    window.userChoseBackgroundMode = false;
    console.log('🎯 ProductForm: New campaign starting - Reset userChoseBackgroundMode to false');
    
    // Phase 4: Get credit cost preview before generation
    try {
      console.log('💰 Fetching credit cost preview...');
      
      // Create a lightweight copy of formData without large base64 images for preview
      const lightweightFormData = { ...formData };
      // Remove or truncate large image data for preview request
      if (lightweightFormData.product_image) {
        lightweightFormData.product_image = lightweightFormData.product_image.substring(0, 100);
      }
      if (lightweightFormData.product_images && lightweightFormData.product_images.length > 0) {
        lightweightFormData.product_images = lightweightFormData.product_images.map(img => 
          img ? img.substring(0, 100) : img
        );
      }
      // Avatar base64 data URLs can be several MB — strip before preview request
      if (lightweightFormData.avatar_image_url && lightweightFormData.avatar_image_url.startsWith('data:')) {
        lightweightFormData.avatar_image_url = lightweightFormData.avatar_image_url.substring(0, 100);
      }
      // Multi-avatar array — same treatment. Without this, a 12MB base64
      // avatar in `avatar_image_urls[0]` gets uploaded on EVERY credit-preview
      // request, blowing past the 10s timeout on mobile networks.
      if (Array.isArray(lightweightFormData.avatar_image_urls)) {
        lightweightFormData.avatar_image_urls = lightweightFormData.avatar_image_urls.map(
          (img) => (img && img.startsWith('data:') ? img.substring(0, 100) : img)
        );
      }
      // Brand logo — same treatment (may also be base64).
      if (lightweightFormData.brand_logo_url && lightweightFormData.brand_logo_url.startsWith('data:')) {
        lightweightFormData.brand_logo_url = lightweightFormData.brand_logo_url.substring(0, 100);
      }
      
      const previewResponse = await axios.post(`${API}/preview-credit-cost`, {
        product: lightweightFormData,
        ad_types: selectedAdTypes
      }, {
        headers: {
          Authorization: `Bearer ${sessionToken}`
        },
        timeout: 10000 // 10 second timeout
      });
      
      console.log('💰 Credit preview received:', previewResponse.data);
      
      // Store the preview data and pending generation data (with full images)
      setCreditPreviewData(previewResponse.data);
      setPendingGenerationData({
        formData, // Keep original formData with full images
        selectedAdTypes
      });
      
      // Show credit preview modal
      setShowCreditPreview(true);
      return; // Stop here and wait for user confirmation
      
    } catch (error) {
      console.error('❌ Error fetching credit preview:', error);
      console.error('Error details:', error.response?.data || error.message);
      // If preview fails, show error but allow generation to proceed
      alert(`Unable to fetch credit preview: ${error.response?.data?.detail || error.message || 'Please try again'}`);
      return;
    }
  };

  // Phase 4: Handle credit preview confirmation
  const handleCreditPreviewProceed = async () => {
    console.log('💰 ProductForm: Credit confirmed, proceeding with generation');
    setShowCreditPreview(false);
    setIsGenerating(true);
    
    // Use saved data from pendingGenerationData if available, otherwise use current formData
    const dataToUse = pendingGenerationData?.formData || formData;
    const adTypesToUse = pendingGenerationData?.selectedAdTypes || selectedAdTypes;
    
    console.log('📊 Using data for generation:', { dataToUse, adTypesToUse });
    
    // Calculate total expected ads based on personas and ad types
    const hasPersonas = dataToUse.enable_personas;
    const personaCount = hasPersonas ? (dataToUse.persona_count === 'ai_determined' ? 3 : parseInt(dataToUse.persona_count)) : 1;
    const totalAds = adTypesToUse.length * personaCount;
    
    // Initialize enhanced progress tracking
    setGenerationProgress({
      step: 0,
      total: totalAds,
      currentStage: 'initializing',
      currentAdType: '',
      currentPersona: '',
      completedAds: [],
      message: '🚀 Initializing AI-powered marketing content generation...',
      startTime: Date.now(),
      hasPersonas: hasPersonas,
      personaCount: personaCount
    });
    
    // Enhanced progress messages for different stages
    const stageMessages = {
      initializing: [
        '🔍 Analyzing your product information...',
        '🎯 Understanding target audience requirements...',
        '🧠 Preparing AI models for content generation...'
      ],
      persona_generation: [
        '👥 Creating detailed buyer personas...',
        '📊 Analyzing demographic data...',
        '🎨 Crafting persona-specific strategies...'
      ],
      content_generation: [
        '✍️ Generating Facebook Ad copy...',
        '📱 Creating Instagram content...',
        '💼 Crafting LinkedIn professional posts...',
        '🎵 Generating TikTok content...',
        '📧 Creating email marketing copy...',
        '🖼️ Generating promotional images with AI...',
        '🎬 Creating video content with AI...',
        '🔧 Optimizing content for maximum impact...'
      ],
      finalizing: [
        '🎉 Finalizing your marketing materials...',
        '✨ Adding finishing touches...',
        '📋 Preparing content for review...'
      ]
    };
    
    let currentStage = 'initializing';
    let completedCount = 0;
    let progressStep = 0;
    
    // Simulate realistic progress updates
    const simulateProgress = () => {
      const baseProgressPerAd = Math.floor(95 / totalAds); // Reserve 5% for finalization
      const expectedDuration = 60000; // 60 seconds expected duration
      const progressInterval = 2000; // Update every 2 seconds
      const totalSteps = Math.floor(expectedDuration / progressInterval);
      
      return setInterval(() => {
        setGenerationProgress(prev => {
          const elapsed = Date.now() - prev.startTime;
          let newStage = currentStage;
          let newMessage = prev.message;
          let newStep = prev.step;
          
          // Determine current stage based on elapsed time
          if (elapsed < 3000) {
            newStage = 'initializing';
          } else if (elapsed < 8000 && hasPersonas) {
            newStage = 'persona_generation';
          } else if (elapsed < 55000) {
            newStage = 'content_generation';
            // Simulate gradual progress during content generation
            const contentProgress = Math.min(Math.floor((elapsed - 8000) / 1500), totalAds - 1);
            newStep = Math.min(contentProgress, totalAds);
          } else {
            newStage = 'finalizing';
            newStep = Math.min(Math.floor(totalAds * 0.95), totalAds);
          }
          
          // Update message based on current stage and progress
          if (newStage !== prev.currentStage || Math.random() < 0.4) {
            const messages = stageMessages[newStage];
            
            if (newStage === 'content_generation') {
              // Show specific ad type being generated
              const adTypeNames = {
                'facebook_ad': 'Facebook Ad',
                'instagram_ad': 'Instagram content',
                'linkedin_ad': 'LinkedIn post',
                'tiktok_ad': 'TikTok content',
                'telegram_ad': 'Telegram content',
                'google_ads': 'Google Ads copy',
                'email_marketing': 'Email marketing copy',
                'product_description': 'Product description',
                'video_promo': 'AI video with voiceover',
                'image_promo': 'AI promotional image'
              };
              
              const currentAdIndex = Math.floor(newStep);
              if (currentAdIndex < selectedAdTypes.length) {
                const currentAdType = selectedAdTypes[currentAdIndex];
                const adTypeName = adTypeNames[currentAdType] || currentAdType;
                
                if (hasPersonas && personaCount > 1) {
                  const personaNames = ['Tech Enthusiast', 'Budget Buyer', 'Premium Customer', 'Early Adopter', 'Value Seeker'];
                  const currentPersonaIndex = Math.floor(newStep / selectedAdTypes.length);
                  const personaName = personaNames[currentPersonaIndex] || `Persona ${currentPersonaIndex + 1}`;
                  newMessage = `✍️ Creating ${adTypeName} for ${personaName} persona...`;
                } else {
                  newMessage = `✍️ Generating ${adTypeName}...`;
                }
              } else {
                newMessage = messages[Math.floor(Math.random() * messages.length)];
              }
            } else {
              newMessage = messages[Math.floor(Math.random() * messages.length)];
            }
          }
          
          return {
            ...prev,
            currentStage: newStage,
            message: newMessage,
            step: newStep
          };
        });
      }, progressInterval);
    };
    
    // Start progress simulation
    const progressInterval = simulateProgress();
    
    // Check authentication before making request
    if (!sessionToken) {
      console.error('❌ No session token available - user not authenticated');
      clearInterval(progressInterval);
      setGenerationProgress(prev => ({
        ...prev,
        message: '❌ Authentication required. Please sign in to generate content.',
        currentStage: 'error'
      }));
      setIsGenerating(false);
      return;
    }

    console.log('🔍 Making AI generation request with session token:', sessionToken.substring(0, 20) + '...');
    console.log('📊 Request data:', {
      product: dataToUse,
      ad_types: adTypesToUse,
      // Debug geography and language
      debug_geography: dataToUse.geography,
      debug_geographies: dataToUse.geographies,
      debug_video_languages: dataToUse.video_languages
    });

    // Make authenticated request
    try {
      const generatePayload = {
        product: dataToUse,
        ad_types: adTypesToUse,
      };
      // Optional free-form user instructions captured by the "Additional
      // instructions" collapsible above the Generate button. Empty = no override.
      if (userFeedback && userFeedback.trim()) {
        generatePayload.user_feedback = userFeedback.trim();
      }
      const response = await axios.post(`${API}/generate-ads`, generatePayload, {
        headers: {
          Authorization: `Bearer ${sessionToken}`
        },
        timeout: 900000 // 15 minutes timeout for AI content generation
      });
      
      // Clear the simulated progress
      clearInterval(progressInterval);
      
      // Backend always uses background generation - start polling
      const campaignId = response.data.id;
      console.log('🎯 ProductForm: Starting campaign polling for:', campaignId);
      console.log('🎯 ProductForm: Current userChoseBackgroundMode:', window.userChoseBackgroundMode);
      
      // Store campaign ID for polling
      window.currentPollingCampaignId = campaignId;
      
      // Start polling for campaign status.
      // Stall-detection: large video-heavy campaigns sit at progress=50 for the
      // entire asyncio.gather (3-7 min). Use a generous 10-min ceiling and ONLY
      // auto-exit when the campaign has actually accumulated ads — otherwise
      // keep polling and surface a "taking longer than usual" hint.
      let _lastProgress = -1;
      let _lastProgressTs = Date.now();
      const STALL_TIMEOUT_MS = 600000; // 10 minutes (was 3 — caused premature popup close)
      const SLOW_HINT_MS = 300000;     // 5 min — show "taking longer" hint but keep polling

      const pollCampaign = async () => {
        try {
          const statusResponse = await axios.get(
            `${API}/campaigns/${campaignId}/status`,
            { headers: { Authorization: `Bearer ${sessionToken}` } }
          );
          
          const { status, progress, error_message } = statusResponse.data;
          console.log(`📊 ProductForm: Campaign ${campaignId} polling: status=${status}, progress=${progress}%`);

          // Track stall
          if (progress !== _lastProgress) {
            _lastProgress = progress;
            _lastProgressTs = Date.now();
          }
          const stallElapsed = Date.now() - _lastProgressTs;
          const isStalled = stallElapsed > STALL_TIMEOUT_MS;
          const isSlow = stallElapsed > SLOW_HINT_MS;
          
          // Update progress UI with backend progress
          if (setGenerationProgress) {
            let stageToSet = 'content_generation';
            if (status === 'completed') {
              stageToSet = 'complete';
            } else if (status === 'queued') {
              stageToSet = 'initializing';
            } else if (status === 'failed') {
              stageToSet = 'error';
            }
            
            // Build a friendly status line. When stuck at one progress for >5 min,
            // append a "taking longer than usual" hint so the user knows we're
            // still actively polling and the popup hasn't frozen.
            let msg = 'Processing...';
            if (status === 'generating') {
              msg = `🎨 Generating your ads... ${progress}% complete`;
              if (isSlow) {
                msg += ' · taking longer than usual (videos can take 3–7 min)';
              }
            } else if (status === 'completed') {
              msg = '✅ Campaign generation complete!';
            } else if (status === 'queued') {
              msg = '⏳ Campaign queued for generation...';
            } else if (status === 'failed') {
              msg = error_message
                ? `❌ Campaign generation failed: ${error_message}`
                : '❌ Campaign generation failed';
            }
            
            setGenerationProgress(prev => ({
              ...prev,
              actualProgress: progress,
              currentStage: stageToSet,
              message: msg,
            }));
          }
          
          // If completed OR genuinely stalled (>10 min with no progress AND ads exist), exit.
          if (status === 'completed' || isStalled) {
            if (isStalled && status !== 'completed') {
              console.warn(`⚠️ ProductForm: Polling stalled at ${progress}% for ${STALL_TIMEOUT_MS/1000}s — force-fetching campaign to recover.`);
            } else {
              console.log('✅ ProductForm: Campaign status is COMPLETED');
            }
            console.log('   User chose background mode:', window.userChoseBackgroundMode);
            console.log('   Fetching complete campaign data from:', `${API}/campaigns/${campaignId}`);
            
            // Retry the final GET — auth blips, network blips, backend restarts,
            // or even replica lag can make the very first GET return empty/null
            // after `status='completed'`. Falling back silently to the stub
            // (which has generated_ads: []) is what was causing the empty
            // Results page bug. Better: retry, and only on total failure
            // surface a clear error.
            const fetchResult = await fetchCampaignWithRetry(campaignId, sessionToken);
            const camp = fetchResult.campaign;

            try {
              if (!camp) {
                // All 3 fetches threw — show error and bail
                console.error('   All 3 fetch attempts threw — surfacing error.');
                if (setGenerationProgress) {
                  setGenerationProgress(prev => ({
                    ...prev,
                    currentStage: 'error',
                    message: '⚠️ Could not load your campaign. It may still be in your dashboard — please refresh and check.',
                  }));
                }
                setIsGenerating(false);
                return true;
              }

              const hasAds = Array.isArray(camp.generated_ads) && camp.generated_ads.length > 0;

              if (isStalled && !hasAds) {
                console.error('   Stalled with NO ads — surfacing error.');
                if (setGenerationProgress) {
                  setGenerationProgress(prev => ({
                    ...prev,
                    currentStage: 'error',
                    message: '⚠️ Generation appears stuck. The backend may have restarted — please retry.',
                  }));
                }
                setIsGenerating(false);
                return true;
              }

              if (!hasAds) {
                // status=completed but no ads after 3 retries — most likely a
                // backend bug or partial run. Don't silently render empty.
                console.error('   Campaign marked completed but 0 ads after 3 retries — surfacing error.');
                if (setGenerationProgress) {
                  setGenerationProgress(prev => ({
                    ...prev,
                    currentStage: 'error',
                    message: '⚠️ Your campaign generated 0 ads. Please check the dashboard or click Retry on the campaign card.',
                  }));
                }
                setIsGenerating(false);
                return true;
              }

              // Call onSubmit with complete data
              onSubmit(camp);

              if (window.userChoseBackgroundMode === true) {
                console.log('   User went to dashboard - modal already closed');
              } else {
                console.log('   User stayed on modal - closing modal now');
              }
            } catch (handoffError) {
              console.error('   Error handing off campaign data:', handoffError);
              setIsGenerating(false);
            }
            
            return true; // Stop polling
          } else if (status === 'failed') {
            console.error('❌ ProductForm: Campaign generation failed', error_message || '(no error_message from backend)');
            throw new Error(
              error_message
                ? `Campaign generation failed: ${error_message}`
                : 'Campaign generation failed'
            );
          }
          
          return false; // Continue polling
        } catch (pollError) {
          console.error('Error polling campaign status:', pollError);
          return false;
        }
      };
      
      // Poll every 2 seconds
      const pollInterval = setInterval(async () => {
        const shouldStop = await pollCampaign();
        if (shouldStop) {
          clearInterval(pollInterval);
        }
      }, 2000);
      
      // Initial poll
      const shouldStop = await pollCampaign();
      if (shouldStop) {
        clearInterval(pollInterval);
      }
      
    } catch (error) {
      console.error('Error generating ads:', error);
      clearInterval(progressInterval);
      
      // Handle different types of errors
      console.error('❌ AI Generation Error Details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
        code: error.code
      });

      let errorMessage = 'Generation failed. Please try again.';
      
      // Phase 4: Handle 402 Insufficient Credits error
      if (error.response?.status === 402) {
        console.log('💰 Insufficient credits error:', error.response.data);
        // Show credit preview modal with the error details
        setCreditPreviewData({
          ...error.response.data.detail,
          can_generate: false
        });
        setShowCreditPreview(true);
        return; // Don't show generic error
      } else if (error.response?.status === 429) {
        // Ultra Video rate limit - another campaign is still generating
        const detail = error.response.data?.detail || 'Please wait for your previous Ultra Video campaign to complete before starting a new one.';
        errorMessage = `⏳ ${detail}`;
        // Also show as alert for visibility
        alert(`⏳ Ultra Video In Progress\n\n${detail}`);
      } else if (error.response?.status === 401 || error.response?.status === 403) {
        errorMessage = '🔐 Authentication failed. Please sign in again to generate AI content.';
      } else if (
        // Gateway / proxy timeout signals — the backend may STILL be processing
        // and a campaign stub may have already been persisted. Recover gracefully
        // by checking the user's recent campaigns instead of stranding the user
        // with a closed modal + a campaign silently generating in their dashboard.
        error.code === 'ECONNABORTED' ||
        error.message === 'Network Error' ||
        error.response?.status === 502 ||
        error.response?.status === 503 ||
        error.response?.status === 504 ||
        error.response?.status === 524
      ) {
        console.warn('⚠️ Gateway timeout — attempting recovery via recent-campaign lookup');
        try {
          // 5-second wait gives the backend time to finish persisting the stub.
          await new Promise(r => setTimeout(r, 5000));
          const recent = await axios.get(`${API}/campaigns?limit=5`, {
            headers: { Authorization: `Bearer ${sessionToken}` }
          });
          const submittedName = dataToUse.product_name;
          const match = (recent.data || []).find(c =>
            c.product?.product_name === submittedName &&
            ['queued', 'generating'].includes(c.generation_status)
          );
          if (match) {
            console.log('✅ Recovery: found matching campaign', match.id, '— navigating to dashboard');
            // Close this modal cleanly and navigate to dashboard where the user
            // can watch progress on the campaign card itself (it polls its own
            // /status endpoint). This is far better than stranding them on the
            // form page with a generic error toast.
            setGenerationProgress(prev => ({
              ...prev,
              currentStage: 'queued',
              message: `🚀 Campaign "${submittedName}" queued — opening dashboard so you can watch progress…`,
            }));
            setIsGenerating(false);
            if (typeof window !== 'undefined' && typeof window.handleNavigation === 'function') {
              window.handleNavigation('campaigns');
            }
            return; // Skip the generic error path below
          }
          errorMessage = 'Connection timed out, but your campaign may still be processing. Please check your dashboard in a moment.';
        } catch (recoveryErr) {
          console.error('Recovery lookup failed:', recoveryErr);
          errorMessage = 'Connection timed out. Please check your dashboard — your campaign may still be generating.';
        }
      } else if (error.response?.status >= 500) {
        errorMessage = 'Server error occurred. Please try again later.';
      } else if (error.response?.data?.detail) {
        errorMessage = `Backend error: ${error.response.data.detail}`;
      }
      
      setGenerationProgress(prev => ({
        ...prev,
        currentStage: 'error',
        message: errorMessage
      }));
      
      // Close modal on error
      setIsGenerating(false);
      throw error;
    }
    // Don't close modal in finally block - let polling complete and handleAdGeneration handle it
  };

  // Phase 4: Handle credit preview cancel
  const handleCreditPreviewCancel = () => {
    setShowCreditPreview(false);
    setCreditPreviewData(null);
    setPendingGenerationData(null);
  };

  // Expose proceed function for App's credit modal handler
  useEffect(() => {
    window.proceedWithAdCreatorHubGeneration = async () => {
      await handleCreditPreviewProceed();
    };
    return () => {
      delete window.proceedWithAdCreatorHubGeneration;
    };
  }, [handleCreditPreviewProceed, pendingGenerationData, sessionToken, formData, selectedAdTypes]);

  // Phase 4: Handle purchase credits from preview modal
  const handlePurchaseCreditsFromPreview = () => {
    setShowCreditPreview(false);
    // Navigate to credits page
    if (window.handleNavigation) {
      window.handleNavigation('credits');
    } else {
      window.location.href = '/credits';
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // When video model changes, adjust duration and resolution to valid values for that model
    if (name === 'video_model') {
      const newDuration = value === 'ultra_video' ? 10 : 8; // Default: 10s for Ultra Video, 8s for others
      // Reset resolution: Sora 2 (ultra_video) max is 1080p, Veo 3.1 supports 4K
      let newResolution = formData.video_resolution;
      if (value === 'ultra_video' && formData.video_resolution === '4K') {
        newResolution = '1080p'; // Downgrade from 4K to 1080p for Sora 2
      } else if (value === 'freepik' && (formData.video_resolution === '4K' || formData.video_resolution === '1080p')) {
        newResolution = '720p'; // Freepik only supports 720p
      }
      setFormData({
        ...formData,
        [name]: value,
        video_duration: newDuration,
        video_resolution: newResolution
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  // Handle website URL extraction
  const handleExtractWebsite = async () => {
    const url = formData.website_url?.trim();
    if (!url) {
      setWebsiteExtractionError('Please enter a website URL');
      return;
    }
    
    setIsExtractingWebsite(true);
    setWebsiteExtractionError('');
    
    try {
      const response = await axios.post(`${API}/extract-website-content`, {
        url: url
      }, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      
      if (response.data.success && response.data.data) {
        const extracted = response.data.data;
        const detectedLogo = extracted.logo_url || extracted.logo_absolute_url || '';
        
        // Auto-fill form with extracted data
        setFormData(prev => ({
          ...prev,
          product_name: extracted.product_name || prev.product_name,
          product_description: extracted.product_description || prev.product_description,
          key_benefits: extracted.key_benefits || prev.key_benefits,
          brand_voice: extracted.brand_voice || prev.brand_voice,
          price: extracted.price || prev.price,
          category: extracted.category || prev.category,
          product_tagline: extracted.tagline || prev.product_tagline,
          target_audience: extracted.target_audience || prev.target_audience,
          campaign_objective: extracted.campaign_objective || prev.campaign_objective,
          primary_platform: extracted.primary_platform || prev.primary_platform,
          cta_goal: extracted.cta_goal || prev.cta_goal,
          offer_incentive: extracted.offer_incentive || prev.offer_incentive,
          pain_point: extracted.pain_point || prev.pain_point,
          landing_page_url: extracted.landing_page_url || prev.landing_page_url,
          brand_logo_url: detectedLogo || prev.brand_logo_url,
          // Auto-switch to GPT Image 2 ("Ultra Image") when a logo is detected
          // for best brand retention via image-to-image embedding.
          image_model: (detectedLogo && !['gpt4o-image', 'nano-banana-pro', 'nano-banana-2'].includes(prev.image_model)) ? 'gpt4o-image' : prev.image_model
        }));

        // Persist the scraped product catalog so the user can pick which
        // specific SKU they want to build a campaign around and remove any
        // they don't want. Auto-flip strict-reference-mode ON when we found
        // real products — using them as refs works best with strict mode.
        const scrapedProducts = Array.isArray(extracted.products) ? extracted.products : [];
        setExtractedProducts(scrapedProducts);
        setSelectedExtractedProductIdx(null);
        if (scrapedProducts.length > 0) {
          setFormData(prev => ({ ...prev, strict_reference_mode: true }));
        }

        // Persist the scraped founder / team avatars. Auto-select the FIRST
        // extracted avatar so it lands in `avatar_image_url(s)` immediately
        // (users otherwise miss the picker card higher in the page and
        // wonder why "Your Avatar (0/7)" is still empty). Additional
        // avatars can be added by clicking their cards below.
        const scrapedAvatars = Array.isArray(extracted.avatars) ? extracted.avatars : [];
        setExtractedAvatars(scrapedAvatars);
        if (scrapedAvatars.length > 0 && scrapedAvatars[0].image_url) {
          setSelectedExtractedAvatarIdxs([0]);
          setFormData(prev => ({
            ...prev,
            avatar_image_url: scrapedAvatars[0].image_url,
            avatar_image_urls: [scrapedAvatars[0].image_url],
            strict_reference_mode: true,
          }));
        } else {
          setSelectedExtractedAvatarIdxs([]);
        }
        
        setWebsiteExtractionError('');
        // Show celebration popup
        setWebsiteExtractionSuccess(true);
      } else {
        setWebsiteExtractionError(response.data.error || 'Failed to extract website content');
      }
    } catch (error) {
      console.error('Website extraction error:', error);
      setWebsiteExtractionError(error.response?.data?.detail || 'Failed to extract website content. Please try again.');
    } finally {
      setIsExtractingWebsite(false);
    }
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    // Support up to 2 images for transition mode
    const filesToProcess = files.slice(0, 2);
    const imagePromises = filesToProcess.map(file => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(file);
      });
    });
    
    Promise.all(imagePromises).then(base64Images => {
      setFormData({
        ...formData,
        product_image: base64Images[0], // Backward compatibility
        product_images: base64Images
      });
      setImagePreview(base64Images);
    });
  };

  const handleAdTypeChange = (adType) => {
    setSelectedAdTypes(prev => 
      prev.includes(adType) 
        ? prev.filter(type => type !== adType)
        : [...prev, adType]
    );
  };

  // Function to get AI model name from ad title or data
  const getImageModelName = (ad) => {
    if (ad && ad.title) {
      if (ad.title.includes('Freepik') || ad.title.includes('AI Image')) {
        return 'AI Image';
      } else if (ad.title.includes('Kie AI') || ad.title.includes('Nano Banana')) {
        return 'AI Image';
      } else if (ad.title.includes('Nebius')) {
        return 'AI Image';
      }
    }
    // Check form data if available
    if (formData) {
      if (formData.image_model === 'freepik') {
        return 'AI Image';
      } else if (formData.image_model === 'kie-ai') {
        return 'AI Image';
      }
    }
    return 'AI Image'; // Default fallback
  };

  // Function to get dynamic image promo label
  const getImagePromoLabel = (ad = null) => {
    return 'AI Generated Image';
  };

  const adTypeLabels = {
    facebook_ad: 'Facebook Ad',
    instagram_ad: 'Instagram Ad',
    linkedin_ad: 'LinkedIn Ad',
    tiktok_ad: 'TikTok Ad',
    twitter_ad: 'Twitter/X Ad',
    social_media: 'Social Media',
    google_ads: 'Google Ads',
    product_description: 'Product Description',
    email_marketing: 'Email Marketing',
    video_promo: 'AI Video with Voiceover (Replicate + ElevenLabs)', // Default fallback
    image_promo: 'AI Generated Image' // Will be made dynamic
  };

  const adTypes = [
    { value: 'facebook_ad', label: 'Facebook Ad' },
    { value: 'instagram_ad', label: 'Instagram Post' },
    { value: 'linkedin_ad', label: 'LinkedIn Ad' },
    { value: 'tiktok_ad', label: 'TikTok Content' },
    { value: 'telegram_ad', label: 'Telegram Channel' },
    { value: 'whatsapp_ad', label: 'WhatsApp Message' },
    { value: 'google_ads', label: 'Google Ads' },
    { value: 'product_description', label: 'Product Description' },
    { value: 'email_marketing', label: 'Email Marketing' },
    { value: 'video_promo', label: 'AI Video with Voiceover' },
    { value: 'image_promo', label: `${getImagePromoLabel()}` }
  ];

  const incomeRanges = [
    '$0 - $25,000',
    '$25,001 - $50,000',
    '$50,001 - $75,000',
    '$75,001 - $100,000',
    '$100,001 - $150,000',
    '$150,001+'
  ];

  // Theme-aware input styling
  const inputClass = `w-full px-3.5 py-2.5 text-sm border rounded-xl focus:ring-2 focus:border-transparent dark:bg-slate-800/50 dark:text-white transition-colors ${
    colorTheme === 'purple' ? 'border-slate-200 dark:border-slate-700/50 focus:ring-purple-500' :
    colorTheme === 'blue' ? 'border-slate-200 dark:border-slate-700/50 focus:ring-blue-500' :
    'border-slate-200 dark:border-slate-700/50 focus:ring-emerald-500'
  }`;
  const labelClass = 'block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1.5';
  const sectionCardClass = 'bg-white/70 dark:bg-slate-800/50 backdrop-blur-xl border border-slate-200/50 dark:border-slate-700/50 rounded-2xl p-6 shadow-sm';
  const sectionTitleClass = `text-lg font-semibold ${
    colorTheme === 'purple' ? 'text-purple-900 dark:text-purple-100' :
    colorTheme === 'blue' ? 'text-blue-900 dark:text-blue-100' :
    'text-emerald-900 dark:text-emerald-100'
  }`;

  return (
    <section id="product-form" className={`py-8 min-h-screen ${
      colorTheme === 'purple' ? 'bg-gradient-to-br from-purple-50/80 via-white to-pink-50/50' :
      colorTheme === 'blue' ? 'bg-gradient-to-br from-blue-50/80 via-white to-sky-50/50' :
      'bg-gradient-to-br from-emerald-50/80 via-white to-teal-50/50'
    } dark:from-slate-900 dark:via-slate-900 dark:to-slate-900`}>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h3 className={`text-3xl font-bold bg-gradient-to-r ${
            colorTheme === 'purple' ? 'from-purple-700 to-pink-600' :
            colorTheme === 'blue' ? 'from-blue-700 to-sky-500' :
            'from-emerald-700 to-teal-500'
          } bg-clip-text text-transparent`}>
            Ad Creator Hub
          </h3>
          <p className="text-slate-500 dark:text-slate-400 mt-1 text-sm">
            Configure your product details and let AI generate compelling marketing content
          </p>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-6" data-testid="ad-creator-form">
          {/* Website URL Auto-Fill Section */}
          <div className={`p-5 rounded-2xl border backdrop-blur-xl ${
            colorTheme === 'purple' ? 'bg-purple-50/50 dark:bg-purple-900/10 border-purple-200/50 dark:border-purple-700/30' :
            colorTheme === 'blue' ? 'bg-blue-50/50 dark:bg-blue-900/10 border-blue-200/50 dark:border-blue-700/30' :
            'bg-emerald-50/50 dark:bg-emerald-900/10 border-emerald-200/50 dark:border-emerald-700/30'
          }`} data-testid="website-url-section">
            <div className="flex items-center gap-2 mb-3">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                colorTheme === 'purple' ? 'bg-purple-100 dark:bg-purple-500/20' :
                colorTheme === 'blue' ? 'bg-blue-100 dark:bg-blue-500/20' :
                'bg-emerald-100 dark:bg-emerald-500/20'
              }`}>
                <span className="text-lg">🌐</span>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-slate-800 dark:text-white">
                  Quick Start with Website URL
                </h4>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Auto-extract product info from any URL
                </p>
              </div>
              <span className={`ml-auto text-[10px] font-medium px-2 py-0.5 rounded-full ${
                colorTheme === 'purple' ? 'bg-purple-100 dark:bg-purple-500/20 text-purple-600 dark:text-purple-300' :
                colorTheme === 'blue' ? 'bg-blue-100 dark:bg-blue-500/20 text-blue-600 dark:text-blue-300' :
                'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-300'
              }`}>Optional</span>
            </div>
            <div className="flex gap-2">
              <input
                type="url"
                name="website_url"
                value={formData.website_url}
                onChange={handleInputChange}
                placeholder="https://example.com/product"
                className={`flex-1 px-4 py-2.5 text-sm border rounded-xl focus:ring-2 focus:border-transparent dark:bg-slate-800/50 dark:text-white transition-colors ${
                  colorTheme === 'purple' ? 'border-purple-200 dark:border-purple-700/50 focus:ring-purple-500' :
                  colorTheme === 'blue' ? 'border-blue-200 dark:border-blue-700/50 focus:ring-blue-500' :
                  'border-emerald-200 dark:border-emerald-700/50 focus:ring-emerald-500'
                }`}
                data-testid="website-url-input"
              />
              <button
                type="button"
                onClick={handleExtractWebsite}
                disabled={isExtractingWebsite || !formData.website_url?.trim()}
                className={`px-5 py-2.5 rounded-xl font-medium text-sm transition-all flex items-center gap-2 shrink-0 ${
                  isExtractingWebsite || !formData.website_url?.trim()
                    ? 'bg-slate-200 dark:bg-slate-700 text-slate-400 cursor-not-allowed'
                    : colorTheme === 'purple' ? 'bg-purple-600 hover:bg-purple-700 text-white shadow-lg shadow-purple-500/20'
                    : colorTheme === 'blue' ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-500/20'
                    : 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-500/20'
                }`}
                data-testid="fetch-content-btn"
              >
                {isExtractingWebsite ? (
                  <>
                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Extracting...
                  </>
                ) : 'Fetch Content'}
              </button>
            </div>
            {websiteExtractionError && (
              <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                ⚠️ {websiteExtractionError}
              </p>
            )}
          </div>

          {/* Website Extraction Success Modal */}
          {websiteExtractionSuccess && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full p-8 transform transition-all animate-bounce-in">
                {/* Celebration Icon */}
                <div className="text-center mb-6">
                  <div className={`inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r ${
                    colorTheme === 'purple' ? 'from-purple-400 to-pink-500' : 
                    colorTheme === 'blue' ? 'from-blue-400 to-sky-500' : 
                    'from-green-400 to-emerald-500'
                  } rounded-full mb-4 animate-pulse`}>
                    <span className="text-4xl">🎉</span>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                    Content Extracted Successfully!
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    We've automatically filled in your product details from the website.
                  </p>
                </div>
                
                {/* Important Notice */}
                <div className={`rounded-lg p-4 mb-6 border ${
                  colorTheme === 'purple' ? 'bg-purple-50 dark:bg-purple-900/30 border-purple-200 dark:border-purple-700' : 
                  colorTheme === 'blue' ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-700' : 
                  'bg-amber-50 dark:bg-amber-900/30 border-amber-200 dark:border-amber-700'
                }`}>
                  <div className="flex items-start gap-3">
                    <span className="text-xl">⚠️</span>
                    <div>
                      <p className={`text-sm font-medium ${
                        colorTheme === 'purple' ? 'text-purple-800 dark:text-purple-300' : 
                        colorTheme === 'blue' ? 'text-blue-800 dark:text-blue-300' : 
                        'text-amber-800 dark:text-amber-300'
                      }`}>
                        Please Review Before Generating
                      </p>
                      <p className={`text-xs mt-1 ${
                        colorTheme === 'purple' ? 'text-purple-700 dark:text-purple-400' : 
                        colorTheme === 'blue' ? 'text-blue-700 dark:text-blue-400' : 
                        'text-amber-700 dark:text-amber-400'
                      }`}>
                        AI-extracted content may need adjustments. Verify all details are accurate before creating your ad.
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Action Button */}
                <button
                  onClick={() => setWebsiteExtractionSuccess(false)}
                  className={`w-full py-3 px-6 bg-gradient-to-r ${
                    colorTheme === 'purple' ? 'from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700' : 
                    colorTheme === 'blue' ? 'from-blue-500 to-sky-600 hover:from-blue-600 hover:to-sky-700' : 
                    'from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700'
                  } text-white font-semibold rounded-lg transition-all transform hover:scale-[1.02] shadow-lg`}
                >
                  Got it, let me review ✓
                </button>
              </div>
            </div>
          )}

          {/* Extracted Products Picker — shown after a successful website
              extraction that returned a product catalog. User can remove
              unwanted SKUs and click a card to swap the product image + name
              + price into the campaign form. */}
          {extractedProducts && extractedProducts.length > 0 && (
            <div
              className="bg-white/70 dark:bg-slate-800/50 backdrop-blur-xl border border-emerald-200/60 dark:border-emerald-700/40 rounded-2xl p-5 shadow-sm"
              data-testid="extracted-products-picker"
            >
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h4 className="text-base font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                    <span>🛒 Products found on your site</span>
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300">
                      {extractedProducts.length}
                    </span>
                  </h4>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    Click a product to use its image & details in the campaign. Remove any you don't want with the ✕.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => { setExtractedProducts([]); setSelectedExtractedProductIdx(null); }}
                  data-testid="extracted-products-clear"
                  className="text-xs text-slate-500 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                >
                  Clear all
                </button>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {extractedProducts.map((p, idx) => (
                  <div
                    key={`${p.product_url || p.image_url || idx}`}
                    className={`relative group cursor-pointer rounded-xl overflow-hidden border-2 transition-all ${
                      selectedExtractedProductIdx === idx
                        ? 'border-emerald-500 ring-2 ring-emerald-300 dark:ring-emerald-700'
                        : 'border-slate-200 dark:border-slate-700 hover:border-emerald-400'
                    }`}
                    data-testid={`extracted-product-card-${idx}`}
                    onClick={() => {
                      setSelectedExtractedProductIdx(idx);
                      const numericPrice = (p.price || '').toString().replace(/,/g, '');
                      setFormData(prev => ({
                        ...prev,
                        product_name: p.name || prev.product_name,
                        price: numericPrice || prev.price,
                        product_image: p.image_url || prev.product_image,
                        product_images: [p.image_url, ...(Array.isArray(prev.product_images) ? prev.product_images.filter(u => u && u !== p.image_url) : [])].slice(0, 4),
                        strict_reference_mode: true
                      }));
                    }}
                  >
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setExtractedProducts(prev => prev.filter((_, i) => i !== idx));
                        if (selectedExtractedProductIdx === idx) setSelectedExtractedProductIdx(null);
                        else if (selectedExtractedProductIdx !== null && idx < selectedExtractedProductIdx) {
                          setSelectedExtractedProductIdx(selectedExtractedProductIdx - 1);
                        }
                      }}
                      data-testid={`extracted-product-remove-${idx}`}
                      aria-label={`Remove ${p.name || 'product'}`}
                      className="absolute top-1.5 right-1.5 z-10 w-6 h-6 rounded-full bg-white/90 dark:bg-slate-900/90 text-slate-600 dark:text-slate-300 hover:bg-red-500 hover:text-white shadow-md flex items-center justify-center text-xs font-bold transition-colors opacity-0 group-hover:opacity-100"
                    >
                      ✕
                    </button>
                    {p.discount_pct ? (
                      <span className="absolute top-1.5 left-1.5 z-10 text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-500 text-white shadow">
                        -{p.discount_pct}%
                      </span>
                    ) : null}
                    <div className="aspect-square bg-slate-100 dark:bg-slate-900 overflow-hidden">
                      <img
                        src={p.image_url}
                        alt={p.name || 'Product'}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => { e.currentTarget.style.opacity = '0.3'; }}
                      />
                    </div>
                    <div className="p-2 bg-white dark:bg-slate-900">
                      <p className="text-xs font-medium text-slate-800 dark:text-slate-200 line-clamp-2 leading-tight" title={p.name}>
                        {p.name}
                      </p>
                      {p.price && (
                        <div className="flex items-baseline gap-1 mt-1">
                          <span className="text-xs font-bold text-emerald-700 dark:text-emerald-300">₹{p.price}</span>
                          {p.original_price && (
                            <span className="text-[10px] text-slate-400 line-through">₹{p.original_price}</span>
                          )}
                        </div>
                      )}
                    </div>
                    {selectedExtractedProductIdx === idx && (
                      <div className="absolute inset-x-0 bottom-0 bg-emerald-500 text-white text-[10px] font-bold py-1 text-center">
                        ✓ USING THIS PRODUCT
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Extracted Avatars Picker — founder / team photos scraped from the
              same website. Click one (or more) to attach as a person-hero
              reference. Multi-select supported via avatar_image_urls[]. */}
          {extractedAvatars && extractedAvatars.length > 0 && (
            <div
              className="bg-white/70 dark:bg-slate-800/50 backdrop-blur-xl border border-indigo-200/60 dark:border-indigo-700/40 rounded-2xl p-5 shadow-sm"
              data-testid="extracted-avatars-picker"
            >
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h4 className="text-base font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                    <span>👤 Founder & team photos</span>
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300">
                      {extractedAvatars.length}
                    </span>
                  </h4>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    Click one (or more) to feature them as the hero. Click again to unselect. Remove any you don't want with the ✕.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setExtractedAvatars([]);
                    setSelectedExtractedAvatarIdxs([]);
                    setFormData(prev => ({ ...prev, avatar_image_url: '', avatar_image_urls: [] }));
                  }}
                  data-testid="extracted-avatars-clear"
                  className="text-xs text-slate-500 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                >
                  Clear all
                </button>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {extractedAvatars.map((a, idx) => {
                  const isSelected = selectedExtractedAvatarIdxs.includes(idx);
                  return (
                    <div
                      key={`${a.image_url || idx}`}
                      className={`relative group cursor-pointer rounded-xl overflow-hidden border-2 transition-all ${
                        isSelected
                          ? 'border-indigo-500 ring-2 ring-indigo-300 dark:ring-indigo-700'
                          : 'border-slate-200 dark:border-slate-700 hover:border-indigo-400'
                      }`}
                      data-testid={`extracted-avatar-card-${idx}`}
                      onClick={() => {
                        const nextIdxs = isSelected
                          ? selectedExtractedAvatarIdxs.filter(i => i !== idx)
                          : [...selectedExtractedAvatarIdxs, idx];
                        setSelectedExtractedAvatarIdxs(nextIdxs);
                        // Rebuild the avatar URL list from selected indexes so
                        // it always stays in sync (adding & removing).
                        const selectedUrls = nextIdxs
                          .map(i => extractedAvatars[i]?.image_url)
                          .filter(Boolean);
                        setFormData(prev => ({
                          ...prev,
                          avatar_image_url: selectedUrls[0] || '',
                          avatar_image_urls: selectedUrls,
                          // Auto-enable strict mode when a face is attached —
                          // preserving that likeness needs the strict clause.
                          strict_reference_mode: selectedUrls.length > 0 ? true : prev.strict_reference_mode
                        }));
                      }}
                    >
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setExtractedAvatars(prev => prev.filter((_, i) => i !== idx));
                          // Rebuild selection indexes after splice
                          const nextIdxs = selectedExtractedAvatarIdxs
                            .filter(i => i !== idx)
                            .map(i => (i > idx ? i - 1 : i));
                          setSelectedExtractedAvatarIdxs(nextIdxs);
                          const nextList = extractedAvatars.filter((_, i) => i !== idx);
                          const selectedUrls = nextIdxs.map(i => nextList[i]?.image_url).filter(Boolean);
                          setFormData(prev => ({
                            ...prev,
                            avatar_image_url: selectedUrls[0] || '',
                            avatar_image_urls: selectedUrls
                          }));
                        }}
                        data-testid={`extracted-avatar-remove-${idx}`}
                        aria-label={`Remove ${a.name || 'avatar'}`}
                        className="absolute top-1.5 right-1.5 z-10 w-6 h-6 rounded-full bg-white/90 dark:bg-slate-900/90 text-slate-600 dark:text-slate-300 hover:bg-red-500 hover:text-white shadow-md flex items-center justify-center text-xs font-bold transition-colors opacity-0 group-hover:opacity-100"
                      >
                        ✕
                      </button>
                      {a.role && (
                        <span className="absolute top-1.5 left-1.5 z-10 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-indigo-500 text-white shadow">
                          {a.role}
                        </span>
                      )}
                      <div className="aspect-square bg-slate-100 dark:bg-slate-900 overflow-hidden">
                        <img
                          src={a.image_url}
                          alt={a.name || 'Team photo'}
                          loading="lazy"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          onError={(e) => { e.currentTarget.style.opacity = '0.3'; }}
                        />
                      </div>
                      <div className="p-2 bg-white dark:bg-slate-900">
                        <p className="text-xs font-medium text-slate-800 dark:text-slate-200 line-clamp-2 leading-tight" title={a.name || ''}>
                          {a.name || 'Team member'}
                        </p>
                        {a.context_text && (
                          <p className="text-[10px] text-slate-500 dark:text-slate-400 line-clamp-2 mt-0.5 leading-tight">
                            {a.context_text}
                          </p>
                        )}
                      </div>
                      {isSelected && (
                        <div className="absolute inset-x-0 bottom-0 bg-indigo-500 text-white text-[10px] font-bold py-1 text-center">
                          ✓ HERO IN AD
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Voice Cloning Panel — upload a voice sample → get a cloned
              ElevenLabs voice_id → pick it per campaign. Persisted server-side
              under user.cloned_voices; the same voice is reusable across
              campaigns without re-upload. When picked, the backend uses it
              for BOTH Kling/Seedance voice overlay AND HeyGen audio-driven
              lip-sync (perfect sync — HeyGen tracks the cloned audio track). */}
          <div className="bg-white/70 dark:bg-slate-800/50 backdrop-blur-xl border border-fuchsia-200/60 dark:border-fuchsia-700/40 rounded-2xl p-5 shadow-sm" data-testid="voice-clone-panel">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h4 className="text-base font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                  <span>🎤 Your voice library</span>
                  {myClonedVoices.length > 0 && (
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-fuchsia-100 dark:bg-fuchsia-900/40 text-fuchsia-700 dark:text-fuchsia-300">
                      {myClonedVoices.length} cloned
                    </span>
                  )}
                </h4>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  Upload 30 s – 5 min of clean speech (MP3/WAV/M4A ≤ 25 MB). Your cloned voices are saved to your library — <strong>preview (▶) · download (⬇) · rename (✎) · remove (✕)</strong> anytime. HeyGen will lip-sync to <em>your actual voice</em>.
                </p>
              </div>
              <label className="cursor-pointer text-xs font-semibold px-3 py-1.5 rounded-lg bg-fuchsia-500 hover:bg-fuchsia-600 text-white shadow transition-colors">
                {isCloningVoice ? 'Cloning…' : '+ Upload'}
                <input
                  type="file"
                  accept="audio/mpeg,audio/mp3,audio/wav,audio/x-wav,audio/mp4,audio/x-m4a"
                  disabled={isCloningVoice}
                  className="hidden"
                  data-testid="voice-clone-upload-input"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    e.target.value = ''; // allow re-picking same file later
                    setVoiceCloneError('');
                    const token = localStorage.getItem('session_token');
                    if (!token) {
                      setVoiceCloneError('Please log in first, then try uploading again.');
                      return;
                    }
                    const defaultName = `Voice-${new Date().toISOString().slice(0,10)}-${(file.name || '').replace(/\.[^.]+$/, '').slice(0,30)}`;
                    setIsCloningVoice(true);
                    try {
                      const fd = new FormData();
                      fd.append('audio', file);
                      fd.append('name', defaultName);
                      fd.append('description', 'Uploaded via AdElevate campaign form');
                      const res = await axios.post(`${API}/voices/clone`, fd, {
                        headers: {
                          'Content-Type': 'multipart/form-data',
                          'Authorization': `Bearer ${token}`,
                        },
                      });
                      if (res.data?.voice_id) {
                        // Optimistic append + auto-select
                        setMyClonedVoices(prev => [...prev, {
                          voice_id: res.data.voice_id,
                          name: res.data.name,
                          provider: res.data.provider,
                          created_at: new Date().toISOString(),
                        }]);
                        setFormData(prev => ({ ...prev, cloned_voice_id: res.data.voice_id }));
                      }
                    } catch (err) {
                      const msg = err.response?.data?.detail || err.message || 'Voice cloning failed';
                      setVoiceCloneError(String(msg).slice(0, 240));
                    } finally {
                      setIsCloningVoice(false);
                    }
                  }}
                />
              </label>
            </div>
            {voiceCloneError && (
              <p className="text-xs text-red-600 dark:text-red-400 mb-2" data-testid="voice-clone-error">
                ⚠️ {voiceCloneError}
              </p>
            )}
            <div className="flex flex-wrap gap-2 items-center">
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, cloned_voice_id: '' }))}
                data-testid="voice-clone-use-default"
                className={`text-xs px-3 py-1.5 rounded-full border-2 transition-colors ${
                  !formData.cloned_voice_id
                    ? 'border-fuchsia-500 bg-fuchsia-50 dark:bg-fuchsia-900/30 text-fuchsia-700 dark:text-fuchsia-200 font-semibold'
                    : 'border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:border-fuchsia-300'
                }`}
              >
                Auto (best stock voice)
              </button>
              {myClonedVoices.map(v => (
                <div
                  key={v.voice_id}
                  data-testid={`voice-clone-chip-${v.voice_id.slice(0,8)}`}
                  className={`inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full border-2 transition-colors ${
                    formData.cloned_voice_id === v.voice_id
                      ? 'border-fuchsia-500 bg-fuchsia-50 dark:bg-fuchsia-900/30 text-fuchsia-700 dark:text-fuchsia-200 font-semibold'
                      : 'border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:border-fuchsia-300'
                  }`}
                  title={`Cloned ${v.created_at?.slice(0,10)}`}
                >
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, cloned_voice_id: v.voice_id }))}
                    data-testid={`voice-clone-select-${v.voice_id.slice(0,8)}`}
                    className="text-xs px-2 py-1"
                  >
                    🎤 {(v.name || v.voice_id.slice(0,10)).slice(0, 28)}
                  </button>
                  <button
                    type="button"
                    disabled={previewingVoiceId === v.voice_id}
                    onClick={async () => {
                      const token = localStorage.getItem('session_token');
                      if (!token) { setVoiceCloneError('Please log in first.'); return; }
                      setVoiceCloneError('');
                      setPreviewingVoiceId(v.voice_id);
                      try {
                        let audioUrl = voicePreviewCacheRef.current[v.voice_id];
                        if (!audioUrl) {
                          const res = await axios.post(`${API}/voices/${v.voice_id}/preview`, {}, {
                            headers: { 'Authorization': `Bearer ${token}` },
                          });
                          audioUrl = res.data?.audio_url;
                          if (audioUrl) voicePreviewCacheRef.current[v.voice_id] = audioUrl;
                        }
                        if (audioUrl) {
                          if (voicePreviewAudioRef.current) {
                            try { voicePreviewAudioRef.current.pause(); } catch (_) {}
                          }
                          const audio = new Audio(audioUrl);
                          voicePreviewAudioRef.current = audio;
                          audio.onended = () => setPreviewingVoiceId('');
                          audio.onerror = () => setPreviewingVoiceId('');
                          await audio.play();
                        }
                      } catch (err) {
                        const msg = err.response?.data?.detail || err.message || 'Preview failed';
                        setVoiceCloneError(String(msg).slice(0, 240));
                        setPreviewingVoiceId('');
                      }
                    }}
                    data-testid={`voice-clone-preview-${v.voice_id.slice(0,8)}`}
                    title="Preview this cloned voice"
                    className="w-6 h-6 rounded-full text-xs bg-slate-100 dark:bg-slate-700 hover:bg-fuchsia-100 dark:hover:bg-fuchsia-800/50 flex items-center justify-center disabled:opacity-50"
                  >
                    {previewingVoiceId === v.voice_id ? '…' : '▶'}
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      const token = localStorage.getItem('session_token');
                      if (!token) { setVoiceCloneError('Please log in first.'); return; }
                      setVoiceCloneError('');
                      try {
                        let audioUrl = voicePreviewCacheRef.current[v.voice_id];
                        if (!audioUrl) {
                          const res = await axios.post(`${API}/voices/${v.voice_id}/preview`, {}, {
                            headers: { 'Authorization': `Bearer ${token}` },
                          });
                          audioUrl = res.data?.audio_url;
                          if (audioUrl) voicePreviewCacheRef.current[v.voice_id] = audioUrl;
                        }
                        if (!audioUrl) throw new Error('No audio to download');
                        // Convert data:audio/mpeg;base64,... to a Blob and trigger a download
                        const commaIdx = audioUrl.indexOf(',');
                        const b64 = audioUrl.slice(commaIdx + 1);
                        const byteChars = atob(b64);
                        const byteNums = new Array(byteChars.length);
                        for (let i = 0; i < byteChars.length; i++) byteNums[i] = byteChars.charCodeAt(i);
                        const blob = new Blob([new Uint8Array(byteNums)], { type: 'audio/mpeg' });
                        const link = document.createElement('a');
                        link.href = URL.createObjectURL(blob);
                        const safeName = (v.name || v.voice_id).replace(/[^a-z0-9_-]+/gi, '_').slice(0, 40);
                        link.download = `${safeName}.mp3`;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        setTimeout(() => URL.revokeObjectURL(link.href), 5000);
                      } catch (err) {
                        const msg = err.response?.data?.detail || err.message || 'Download failed';
                        setVoiceCloneError(String(msg).slice(0, 240));
                      }
                    }}
                    data-testid={`voice-clone-download-${v.voice_id.slice(0,8)}`}
                    title="Download preview MP3"
                    className="w-6 h-6 rounded-full text-xs bg-slate-100 dark:bg-slate-700 hover:bg-emerald-100 dark:hover:bg-emerald-900/40 text-slate-600 hover:text-emerald-600 dark:text-slate-300 dark:hover:text-emerald-400 flex items-center justify-center"
                  >
                    ⬇
                  </button>
                  <button
                    type="button"
                    onClick={() => setVoiceRenameModal({ isOpen: true, voice: v, isProcessing: false })}
                    data-testid={`voice-clone-rename-${v.voice_id.slice(0,8)}`}
                    title="Rename this voice"
                    className="w-6 h-6 rounded-full text-xs bg-slate-100 dark:bg-slate-700 hover:bg-blue-100 dark:hover:bg-blue-900/40 text-slate-600 hover:text-blue-600 dark:text-slate-300 dark:hover:text-blue-400 flex items-center justify-center"
                  >
                    ✎
                  </button>
                  <button
                    type="button"
                    onClick={() => setVoiceDeleteModal({ isOpen: true, voice: v, isProcessing: false })}
                    data-testid={`voice-clone-remove-${v.voice_id.slice(0,8)}`}
                    title="Remove this cloned voice"
                    className="w-6 h-6 rounded-full text-xs bg-slate-100 dark:bg-slate-700 hover:bg-red-100 dark:hover:bg-red-900/40 text-slate-600 hover:text-red-600 dark:text-slate-300 dark:hover:text-red-400 flex items-center justify-center"
                  >
                    ✕
                  </button>
                </div>
              ))}
              {myClonedVoices.length === 0 && !isCloningVoice && (
                <span className="text-xs text-slate-500 dark:text-slate-400 italic">
                  No cloned voices yet — upload a sample to make one.
                </span>
              )}
            </div>
            {formData.cloned_voice_id && (
              <p className="mt-3 text-xs text-fuchsia-700 dark:text-fuchsia-300" data-testid="voice-clone-active-hint">
                ✨ <strong>Cloned voice active.</strong> This voice is now attached to your campaign — when you click <em>Generate Ads</em>, HeyGen will lip-sync to it and any Kling/Seedance video ad will use it as narration. No extra step needed.
              </p>
            )}
            {!formData.cloned_voice_id && myClonedVoices.length > 0 && (
              <p className="mt-3 text-xs text-slate-500 dark:text-slate-400" data-testid="voice-clone-auto-hint">
                Using <strong>Auto</strong> (best stock voice). Tap a cloned voice above to use yours instead.
              </p>
            )}

            {/* Rename modal — reuses PromptModal design system */}
            <PromptModal
              isOpen={voiceRenameModal.isOpen}
              onClose={() => voiceRenameModal.isProcessing ? null : setVoiceRenameModal({ isOpen: false, voice: null, isProcessing: false })}
              onSubmit={async (newName) => {
                const v = voiceRenameModal.voice;
                if (!v || newName === v.name) {
                  setVoiceRenameModal({ isOpen: false, voice: null, isProcessing: false });
                  return;
                }
                const token = localStorage.getItem('session_token');
                if (!token) { setVoiceCloneError('Please log in first.'); return; }
                setVoiceRenameModal(prev => ({ ...prev, isProcessing: true }));
                try {
                  const res = await axios.patch(`${API}/voices/${v.voice_id}`, { name: newName }, {
                    headers: { 'Authorization': `Bearer ${token}` },
                  });
                  const savedName = res.data?.name || newName;
                  setMyClonedVoices(prev => prev.map(x =>
                    x.voice_id === v.voice_id ? { ...x, name: savedName } : x));
                  setVoiceRenameModal({ isOpen: false, voice: null, isProcessing: false });
                } catch (err) {
                  const msg = err.response?.data?.detail || err.message || 'Rename failed';
                  setVoiceCloneError(String(msg).slice(0, 240));
                  setVoiceRenameModal(prev => ({ ...prev, isProcessing: false }));
                }
              }}
              title="Rename voice"
              message="Pick a memorable name — it stays in your voice library."
              defaultValue={voiceRenameModal.voice?.name || ''}
              placeholder="e.g. My Founder Voice"
              confirmText="Save name"
              type="info"
              icon="✎"
              maxLength={80}
              isProcessing={voiceRenameModal.isProcessing}
            />

            {/* Delete confirmation — reuses ConfirmationModal (danger preset) */}
            <ConfirmationModal
              isOpen={voiceDeleteModal.isOpen}
              onClose={() => voiceDeleteModal.isProcessing ? null : setVoiceDeleteModal({ isOpen: false, voice: null, isProcessing: false })}
              onConfirm={async () => {
                const v = voiceDeleteModal.voice;
                if (!v) return;
                const token = localStorage.getItem('session_token');
                if (!token) { setVoiceCloneError('Please log in first.'); return; }
                setVoiceDeleteModal(prev => ({ ...prev, isProcessing: true }));
                try {
                  await axios.delete(`${API}/voices/${v.voice_id}`, {
                    headers: { 'Authorization': `Bearer ${token}` },
                  });
                  setMyClonedVoices(prev => prev.filter(x => x.voice_id !== v.voice_id));
                  setFormData(prev => prev.cloned_voice_id === v.voice_id
                    ? { ...prev, cloned_voice_id: '' }
                    : prev);
                  delete voicePreviewCacheRef.current[v.voice_id];
                  setVoiceDeleteModal({ isOpen: false, voice: null, isProcessing: false });
                } catch (err) {
                  const msg = err.response?.data?.detail || err.message || 'Delete failed';
                  setVoiceCloneError(String(msg).slice(0, 240));
                  setVoiceDeleteModal(prev => ({ ...prev, isProcessing: false }));
                }
              }}
              title="Delete cloned voice?"
              message={voiceDeleteModal.voice
                ? `“${voiceDeleteModal.voice.name || voiceDeleteModal.voice.voice_id.slice(0,10)}” will be permanently removed from your ElevenLabs account and voice library. This cannot be undone.`
                : ''}
              confirmText="Delete voice"
              cancelText="Keep it"
              type="danger"
              isProcessing={voiceDeleteModal.isProcessing}
            />
          </div>

          {/* Product Information Section */}
          <div className="bg-white/70 dark:bg-slate-800/50 backdrop-blur-xl border border-slate-200/50 dark:border-slate-700/50 rounded-2xl p-6 shadow-sm" data-testid="product-info-section">
            <h4 className={`text-lg font-semibold mb-5 ${
              colorTheme === 'purple' ? 'text-purple-900 dark:text-purple-100' :
              colorTheme === 'blue' ? 'text-blue-900 dark:text-blue-100' :
              'text-emerald-900 dark:text-emerald-100'
            }`}>
              Product Information
            </h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Product Name *</label>
                <input
                  type="text"
                  name="product_name"
                  value={formData.product_name}
                  onChange={handleInputChange}
                  required
                  className={inputClass}
                  placeholder="e.g., EcoClean Laundry Detergent"
                  data-testid="product-name-input"
                />
              </div>
              
              <div>
                <label className={labelClass}>Price (Optional)</label>
                <input
                  type="text"
                  name="price"
                  value={formData.price}
                  onChange={handleInputChange}
                  className={inputClass}
                  placeholder="e.g., $19.99"
                  data-testid="price-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <label className={labelClass}>Category</label>
                <input
                  type="text"
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  className={inputClass}
                  placeholder="e.g., Household, Electronics, Fashion"
                  data-testid="category-input"
                />
              </div>
              
              <div>
                <label className={labelClass}>Brand Voice</label>
                <select
                  name="brand_voice"
                  value={formData.brand_voice}
                  onChange={handleInputChange}
                  className={inputClass}
                  data-testid="brand-voice-select"
                >
                  <option value="professional">Professional</option>
                  <option value="friendly">Friendly</option>
                  <option value="casual">Casual</option>
                  <option value="luxury">Luxury</option>
                  <option value="playful">Playful</option>
                </select>
              </div>
            </div>

            <div className="mt-4">
              <label className={labelClass}>Product Description & Benefits *</label>
              <textarea
                name="product_description"
                value={formData.product_description}
                onChange={handleInputChange}
                required
                rows="4"
                className={inputClass}
                placeholder="Describe your product, its features, benefits, and what makes it special..."
                data-testid="product-description-input"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                💡 Tip: Include both product description and key benefits in one field for easier input
              </p>
            </div>

            {/* Product Tagline */}
            <div className="mt-4">
              <label className={labelClass}>Product Tagline (Optional)</label>
              <input
                type="text"
                name="product_tagline"
                value={formData.product_tagline}
                onChange={handleInputChange}
                className={inputClass}
                placeholder="e.g., Smarter Ads. Better Leads. Longer Runway"
                data-testid="product-tagline-input"
              />
              <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-1">
                A catchy slogan for your generated images (if text overlay is enabled)
              </p>
            </div>

            {/* Text Overlay Method Selector */}
            {formData.product_tagline && selectedAdTypes.includes('image_promo') && (
              <div className={`mt-4 p-4 rounded-xl border ${
                colorTheme === 'purple' ? 'bg-purple-50/50 dark:bg-purple-900/10 border-purple-200/50 dark:border-purple-700/30' :
                colorTheme === 'blue' ? 'bg-blue-50/50 dark:bg-blue-900/10 border-blue-200/50 dark:border-blue-700/30' :
                'bg-emerald-50/50 dark:bg-emerald-900/10 border-emerald-200/50 dark:border-emerald-700/30'
              }`}>
                <label className={`${labelClass} mb-3`}>Text Overlay Method</label>
                <div className="space-y-4">
                  <label className="flex items-start cursor-pointer p-3 border-2 border-transparent hover:border-green-400 rounded-lg transition">
                    <input
                      type="radio"
                      name="text_overlay_method"
                      value="ai-text"
                      checked={formData.text_overlay_method === 'ai-text'}
                      onChange={handleInputChange}
                      className={`mt-1 mr-3 ${
                        colorTheme === 'purple' ? 'text-purple-600 focus:ring-purple-500' :
                        colorTheme === 'blue' ? 'text-blue-600 focus:ring-blue-500' :
                        'text-emerald-600 focus:ring-emerald-500'
                      }`}
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-slate-800 dark:text-white">AI-Generated Text</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                          colorTheme === 'purple' ? 'bg-purple-100 text-purple-700' :
                          colorTheme === 'blue' ? 'bg-blue-100 text-blue-700' :
                          'bg-emerald-100 text-emerald-700'
                        }`}>Recommended</span>
                      </div>
                      <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                        AI includes text naturally as part of the image scene
                      </p>
                    </div>
                  </label>
                  
                  <label className={`flex items-start cursor-pointer p-2.5 border-2 border-transparent rounded-xl transition ${
                    colorTheme === 'purple' ? 'hover:border-purple-300' :
                    colorTheme === 'blue' ? 'hover:border-blue-300' :
                    'hover:border-emerald-300'
                  }`}>
                    <input
                      type="radio"
                      name="text_overlay_method"
                      value="overlay"
                      checked={formData.text_overlay_method === 'overlay'}
                      onChange={handleInputChange}
                      className={`mt-1 mr-3 ${
                        colorTheme === 'purple' ? 'text-purple-600 focus:ring-purple-500' :
                        colorTheme === 'blue' ? 'text-blue-600 focus:ring-blue-500' :
                        'text-emerald-600 focus:ring-emerald-500'
                      }`}
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-slate-800 dark:text-white">Post-Processing Overlay</span>
                        <span className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">Optional</span>
                      </div>
                      <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                        Adds a text banner overlay after image generation
                      </p>
                    </div>
                  </label>
                </div>
              </div>
            )}

            <div className="mt-4">
              {/* Unified Brand Assets card — Product / Avatar / Logo */}
              <BrandAssetsCard
                formData={formData}
                setFormData={setFormData}
                sessionToken={sessionToken}
                testIdPrefix="hub-brand-assets"
              />
              
              {/* Video Generation Mode Selector — shown when video_promo is selected
                  AND the user has uploaded at least one ref (product/avatar/logo) */}
              {selectedAdTypes.includes('video_promo') && (
                (formData.product_images && formData.product_images.length > 0)
                || formData.avatar_image_url
                || formData.brand_logo_url
              ) && (
                <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg" data-testid="hub-video-mode-panel">
                  <label className="block text-sm font-medium text-gray-900 dark:text-white mb-3">
                    🎬 Video Generation Mode
                  </label>
                  <div className="space-y-3">
                    <label className="flex items-start cursor-pointer">
                      <input
                        type="radio"
                        name="video_generation_mode"
                        value="text"
                        checked={formData.video_generation_mode === 'text'}
                        onChange={handleInputChange}
                        className="mt-1 mr-3 text-green-600 focus:ring-green-500"
                      />
                      <div>
                        <span className="text-sm font-medium text-gray-900 dark:text-white">Text-to-Video</span>
                        <p className="text-xs text-gray-600 dark:text-gray-400">Use your product description as a prompt (ignores uploaded images)</p>
                      </div>
                    </label>
                    
                    {/* Multi-Reference Video — surfaces when at least 1 ref exists.
                        Recommended when 2+ refs are uploaded (product+avatar / product+logo / avatar+logo / all 3). */}
                    {(((formData.product_images && formData.product_images.length > 0) ? 1 : 0)
                      + (formData.avatar_image_url ? 1 : 0)
                      + (formData.brand_logo_url ? 1 : 0)) >= 1 && (
                      <label className="flex items-start cursor-pointer" data-testid="hub-video-mode-multi-ref">
                        <input
                          type="radio"
                          name="video_generation_mode"
                          value="multi_reference"
                          checked={formData.video_generation_mode === 'multi_reference'}
                          onChange={handleInputChange}
                          className="mt-1 mr-3 text-green-600 focus:ring-green-500"
                        />
                        <div className="flex-1">
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            Multi-Reference Video <span className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 ml-1">RECOMMENDED</span>
                          </span>
                          <p className="text-xs text-gray-600 dark:text-gray-400">Send your product, portrait & logo together so the video preserves all three.</p>
                          <div className="flex flex-wrap gap-1 mt-1.5">
                            {formData.product_images && formData.product_images.length > 0 && (
                              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300">Product</span>
                            )}
                            {formData.avatar_image_url && (
                              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300">Avatar</span>
                            )}
                            {formData.brand_logo_url && (
                              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300">Logo</span>
                            )}
                          </div>
                          <p className="text-[11px] text-yellow-600 dark:text-yellow-400 mt-1">⚠️ Auto-forces Fast model + 16:9 aspect ratio</p>
                        </div>
                      </label>
                    )}
                    
                    {formData.product_images && formData.product_images.length >= 1 && (
                      <label className="flex items-start cursor-pointer">
                        <input
                          type="radio"
                          name="video_generation_mode"
                          value="image_reference"
                          checked={formData.video_generation_mode === 'image_reference'}
                          onChange={handleInputChange}
                          className="mt-1 mr-3 text-green-600 focus:ring-green-500"
                        />
                        <div>
                          <span className="text-sm font-medium text-gray-900 dark:text-white">Image-to-Video</span>
                          <p className="text-xs text-gray-600 dark:text-gray-400">Use product image only as reference</p>
                          <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">⚠️ Auto-forces Fast model + 16:9 aspect ratio</p>
                        </div>
                      </label>
                    )}
                    
                    {formData.product_images && formData.product_images.length >= 2 && (
                      <label className="flex items-start cursor-pointer">
                        <input
                          type="radio"
                          name="video_generation_mode"
                          value="image_transition"
                          checked={formData.video_generation_mode === 'image_transition'}
                          onChange={handleInputChange}
                          className="mt-1 mr-3 text-green-600 focus:ring-green-500"
                        />
                        <div>
                          <span className="text-sm font-medium text-gray-900 dark:text-white">Image Transition Video</span>
                          <p className="text-xs text-gray-600 dark:text-gray-400">Create transition between 2 uploaded images</p>
                        </div>
                      </label>
                    )}
                  </div>
                  
                  {/* Render-text toggle — only meaningful when the model will create
                      typography. Hidden in text-to-video (which has no constraint). */}
                  {formData.video_generation_mode !== 'text' && (
                    <div className="mt-4 pt-4 border-t border-blue-200 dark:border-blue-700/50" data-testid="hub-render-text-row">
                      <label className="flex items-start cursor-pointer">
                        <input
                          type="checkbox"
                          name="render_text_in_video"
                          checked={!!formData.render_text_in_video}
                          onChange={(e) => setFormData((prev) => ({ ...prev, render_text_in_video: e.target.checked }))}
                          className="mt-1 mr-3 text-green-600 focus:ring-green-500"
                          data-testid="hub-render-text-checkbox"
                        />
                        <div>
                          <span className="text-sm font-medium text-gray-900 dark:text-white">Render text in video</span>
                          {(() => {
                            // Gemini Omni Video (Ultra Pro / "Veo 3.5 Omni" via Kie AI) renders
                            // brand typography reliably — it was trained with text awareness and
                            // does NOT have the misspelling problem Veo 3.x suffers from. So we
                            // tailor the helper copy by model.
                            const isOmni = formData.video_model === 'gemini_omni' || formData.video_model === 'ultra_video';
                            if (isOmni) {
                              return (
                                <p className="text-xs text-emerald-700 dark:text-emerald-300 mt-0.5 leading-snug">
                                  ✨ Gemini Omni renders <strong>brand names &amp; headlines reliably</strong>. Turn ON to bake taglines / CTAs directly into the video without needing a logo overlay.
                                </p>
                              );
                            }
                            return (
                              <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-0.5 leading-snug">
                                ⚠️ Veo 3 often <strong>mis-spells brand names</strong> (e.g. "AdElevate" → "AddElevate"). Keeping this <strong>OFF</strong> lets your logo image carry the typography crisply. Turn ON only if you want headlines / taglines rendered inside the video.
                              </p>
                            );
                          })()}
                        </div>
                      </label>
                    </div>
                  )}

                  {/* Strict Reference Mode — image + video applicability.
                      Turns image/video models into pixel-faithful reference
                      followers AND keeps on-image/video text short so it's
                      spelled correctly. Auto-enabled when a product image is
                      picked from the extracted-products picker. */}
                  <div className="mt-4 pt-4 border-t border-blue-200 dark:border-blue-700/50" data-testid="hub-strict-ref-row">
                    <label className="flex items-start cursor-pointer">
                      <input
                        type="checkbox"
                        name="strict_reference_mode"
                        checked={!!formData.strict_reference_mode}
                        onChange={(e) => setFormData((prev) => ({ ...prev, strict_reference_mode: e.target.checked }))}
                        className="mt-1 mr-3 text-emerald-600 focus:ring-emerald-500"
                        data-testid="hub-strict-ref-checkbox"
                      />
                      <div>
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          🔒 Strict Reference Mode
                          {formData.strict_reference_mode && (
                            <span className="ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-500 text-white align-middle">ON</span>
                          )}
                        </span>
                        <p className="text-xs text-slate-600 dark:text-slate-400 mt-0.5 leading-snug">
                          Uses your uploaded product / logo / avatar images as the <strong>pixel-faithful</strong> source of truth (no re-imagining).
                          Keeps on-image text <strong>short & confident</strong> (headlines under 6 words, no dense body copy, medium-sized letters) to avoid model spelling glitches.
                          Recommended when you've picked a product above or uploaded your own images.
                        </p>
                      </div>
                    </label>
                  </div>

                  {/* Voice Narration Overlay — visible only when a silent video
                      model is selected (Kling / Seedance). HeyGen bakes voice
                      in natively, so we don't show this row when HeyGen is picked. */}
                  {['kling-3.0-pro', 'kling-3.0-std', 'seedance-2.0-pro', 'seedance-2.0-fast'].includes(formData.video_model) && (
                    <div className="mt-4 pt-4 border-t border-blue-200 dark:border-blue-700/50" data-testid="hub-voice-narration-row">
                      <label className="flex items-start cursor-pointer">
                        <input
                          type="checkbox"
                          name="add_voice_narration"
                          checked={!!formData.add_voice_narration}
                          onChange={(e) => setFormData((prev) => ({ ...prev, add_voice_narration: e.target.checked }))}
                          className="mt-1 mr-3 text-purple-600 focus:ring-purple-500"
                          data-testid="hub-voice-narration-checkbox"
                        />
                        <div>
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            🎙️ Add AI Voice Narration
                            {formData.add_voice_narration && (
                              <span className="ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded bg-purple-500 text-white align-middle">ON</span>
                            )}
                          </span>
                          <p className="text-xs text-slate-600 dark:text-slate-400 mt-0.5 leading-snug">
                            {formData.video_model && formData.video_model.startsWith('kling') ? 'Kling 3.0' : 'Seedance 2.0'} ships <strong>silent</strong>.
                            When ON, we generate a matching-language <strong>ElevenLabs</strong> voiceover
                            (Tamil, Hindi, Telugu, English, and 25+ languages via <code className="text-[10px]">eleven_multilingual_v2</code>)
                            and mux it over your video. Adds ~5-8 seconds to generation.
                            Turn OFF only if you plan to edit the audio track externally.
                          </p>
                        </div>
                      </label>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Target Audience Section - Removed redundant field */}

          {/* Campaign Strategy Section */}
          <CampaignStrategy
            values={{
              campaign_objective: formData.campaign_objective,
              primary_platform: formData.primary_platform,
              cta_goal: formData.cta_goal,
              offer_incentive: formData.offer_incentive,
              landing_page_url: formData.landing_page_url,
              pain_point: formData.pain_point,
            }}
            onChange={(field, value) => setFormData(prev => ({ ...prev, [field]: value }))}
            productCategory={formData.category}
            productDescription={formData.product_description}
          />

          {/* Persona-Driven Optimization Section */}
          <div className={sectionCardClass} data-testid="persona-section">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h4 className={sectionTitleClass}>
                  Persona-Driven Optimization
                </h4>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                  Create detailed buyer personas for more targeted content
                </p>
              </div>
              <div className="flex items-center space-x-3">
                <label className="text-xs font-medium text-slate-600 dark:text-slate-300">
                  Enable Personas
                </label>
                <button
                  type="button"
                  onClick={() => handleInputChange({ target: { name: 'enable_personas', value: !formData.enable_personas } })}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 ${
                    colorTheme === 'purple' ? 'focus:ring-purple-500' :
                    colorTheme === 'blue' ? 'focus:ring-blue-500' :
                    'focus:ring-emerald-500'
                  } focus:ring-offset-2 ${
                    formData.enable_personas 
                      ? colorTheme === 'purple' ? 'bg-purple-600' : colorTheme === 'blue' ? 'bg-blue-600' : 'bg-emerald-600'
                      : 'bg-slate-200 dark:bg-slate-600'
                  }`}
                  data-testid="enable-personas-toggle"
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      formData.enable_personas ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
            </div>

            {formData.enable_personas && (
              <>
                <div className="mb-5 p-4 bg-slate-50/80 dark:bg-slate-800/30 rounded-xl border border-slate-100 dark:border-slate-700/30">
                  <h5 className="text-sm font-semibold text-slate-800 dark:text-white mb-3">
                    Demographics
                  </h5>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div>
                      <label className={labelClass}>Age Range</label>
                      <div className="flex items-center space-x-2">
                        <input
                          type="number"
                          name="age_range_min"
                          value={formData.age_range_min}
                          onChange={handleInputChange}
                          min="15"
                          max="65"
                          className={`w-20 !px-2 !py-2 text-center ${inputClass}`}
                          placeholder="Min"
                        />
                        <span className="text-slate-400 text-xs">to</span>
                        <input
                          type="number"
                          name="age_range_max"
                          value={formData.age_range_max}
                          onChange={handleInputChange}
                          min="15"
                          max="65"
                          className={`w-20 !px-2 !py-2 text-center ${inputClass}`}
                          placeholder="Max"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className={labelClass}>Gender</label>
                      <select
                        name="gender"
                        value={formData.gender}
                        onChange={handleInputChange}
                        className={inputClass}
                      >
                        <option value="All">All</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                      </select>
                    </div>
                  </div>
                  
                  {/* Geography and Language Selection */}
                  <div>
                    <GeographyLanguageSelector
                      selectedGeographies={formData.geographies}
                      selectedLanguages={formData.video_languages}
                      onGeographyChange={(geographies) => {
                        setFormData(prev => ({
                          ...prev,
                          geographies,
                          // Also set single geography for backward compatibility
                          geography: geographies.length > 0 ? geographies[0] : '',
                          // Reset languages when geography changes
                          video_languages: []
                        }));
                      }}
                      onLanguageChange={(languages) => {
                        // Check if any non-English language is selected
                        const hasNonEnglish = languages.some(lang => lang !== 'en');
                        
                        setFormData(prev => {
                          const updates = {
                            ...prev,
                            video_languages: languages
                          };
                          
                          // Only auto-suggest a multilingual-capable video model when
                          // the user is on a model that can't render non-Latin audio
                          // well (freepik). Veo 3.1 (veo3 / veo3_fast) supports
                          // multilingual voiceover natively, so leave it alone.
                          // User can still manually pick Ultra Video (Sora 2) if desired.
                          // Kling 3.0 Pro/Std and Seedance 2.0 Pro also render native
                          // Tamil / Hindi / Bengali narration well when prompted.
                          const multilingualVideoModels = ['veo3', 'veo3_fast', 'ultra_video', 'gemini_omni_video',
                                                            'kling-3.0-pro', 'kling-3.0-std', 'seedance-2.0-pro'];
                          if (hasNonEnglish && !multilingualVideoModels.includes(prev.video_model)) {
                            updates.video_model = 'veo3_fast'; // Premium Veo 3.1 — multilingual + faster + cheaper
                            // Don't force duration — keep whatever they picked.
                          }
                          
                          // Image model: only switch FROM weak multilingual models.
                          // GPT Image 2 / Nano Banana Pro / Nano Banana 2 all handle
                          // non-English text well — don't override the user's choice.
                          const multilingualImageModels = ['gpt4o-image', 'nano-banana-pro', 'nano-banana-2'];
                          if (hasNonEnglish && !multilingualImageModels.includes(prev.image_model)) {
                            updates.image_model = 'gpt4o-image';
                          }
                          
                          return updates;
                        });
                      }}
                      showLanguageSelector={selectedAdTypes.includes('video_promo') || selectedAdTypes.some(type => ['facebook_ad', 'instagram_ad', 'linkedin_ad', 'google_ads', 'product_description', 'email_marketing', 'whatsapp_ad', 'telegram_ad', 'tiktok_ad', 'social_media'].includes(type))}
                      maxGeographies={1}
                      maxLanguages={3}
                    />
                    {/* Localized Image Toggle — Ultra Image, Nano Banana Pro & Nano Banana 2 all render multilingual text well */}
                    {selectedAdTypes.includes('image_promo') && ['gpt4o-image', 'nano-banana-pro', 'nano-banana-2'].includes(formData.image_model) && formData.video_languages && formData.video_languages.length > 1 && (
                      <div className="mt-3 flex items-start gap-3 p-3 rounded-xl bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800" data-testid="localized-images-toggle-section">
                        <label className="flex items-center gap-2 cursor-pointer shrink-0 mt-1" data-testid="generate-localized-images-toggle">
                          <input
                            type="checkbox"
                            checked={!!formData.generate_localized_images}
                            onChange={(e) => setFormData(prev => ({ ...prev, generate_localized_images: e.target.checked }))}
                            className="sr-only peer"
                          />
                          <div className="relative w-10 h-5 bg-slate-300 dark:bg-slate-600 peer-checked:bg-blue-500 rounded-full transition-colors">
                            <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${formData.generate_localized_images ? 'translate-x-5' : ''}`}></div>
                          </div>
                        </label>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-slate-800 dark:text-white">Generate Localized Images ({formData.video_languages.length}× images)</p>
                          <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                            {formData.generate_localized_images
                              ? `Will generate ${formData.video_languages.length} images — one per language with native on-image text.`
                              : `Currently generating 1 image with English text. Enable to localize on-image text into each language.`}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className={labelClass}>Industry/Business Type</label>
                    <input
                      type="text"
                      name="industry_business_type"
                      value={formData.industry_business_type}
                      onChange={handleInputChange}
                      className={inputClass}
                      placeholder="e.g., Technology, Healthcare, Retail"
                    />
                  </div>
                  
                  <div>
                    <label className={labelClass}>Budget Range</label>
                    <select
                      name="budget_range"
                      value={formData.budget_range}
                      onChange={handleInputChange}
                      className={inputClass}
                    >
                      <option value="">
                        Select budget range {formData.geography ? `(${currentBudgetOptions.currency})` : ''}
                      </option>
                      {currentBudgetOptions.ranges.map((range, index) => (
                        <option key={index} value={range}>
                          {range}
                        </option>
                      ))}
                    </select>
                    {formData.geography && (
                      <p className="text-[11px] text-slate-400 mt-1">
                        Budget in {currentBudgetOptions.currency} based on selected geography
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className={labelClass}>Interests & Behaviors</label>
                    <textarea
                      name="interests_behaviors"
                      value={formData.interests_behaviors}
                      onChange={handleInputChange}
                      rows="3"
                      className={inputClass}
                      placeholder="e.g., Tech enthusiasts, Early adopters, Online shoppers"
                    />
                  </div>
                  
                  <div>
                    <label className={labelClass}>Number of Personas</label>
                    <select
                      name="persona_count"
                      value={formData.persona_count}
                      onChange={handleInputChange}
                      className={inputClass}
                    >
                      <option value="ai_determined">AI Determined (Recommended)</option>
                      <option value="1">1 Persona</option>
                      <option value="2">2 Personas</option>
                      <option value="3">3 Personas</option>
                      <option value="4">4 Personas</option>
                      <option value="5">5 Personas</option>
                    </select>
                    <p className="text-[11px] text-slate-400 mt-1">
                      AI creates 3-4 personas based on product complexity
                    </p>
                  </div>
                </div>

                <div className={`rounded-xl p-3 border ${
                  colorTheme === 'purple' ? 'bg-purple-50/50 dark:bg-purple-900/10 border-purple-200/30 dark:border-purple-700/20' :
                  colorTheme === 'blue' ? 'bg-blue-50/50 dark:bg-blue-900/10 border-blue-200/30 dark:border-blue-700/20' :
                  'bg-amber-50/50 dark:bg-amber-900/10 border-amber-200/30 dark:border-amber-700/20'
                }`}>
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.19-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                        Persona-Driven Optimization
                      </h3>
                      <div className="mt-1 text-sm text-yellow-700 dark:text-yellow-300">
                        <p>When enabled, AI will create detailed buyer personas based on your product and demographic data, then generate personalized marketing content for each persona. This results in more targeted and effective campaigns.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Video Options Section */}
          {selectedAdTypes.includes('video_promo') && (
            <div className={sectionCardClass} data-testid="video-specs-section">
              <h4 className={`${sectionTitleClass} mb-5`}>
                Video Specifications
              </h4>
              <div className="mb-5">
                <VideoPlatformPicker
                  selected={formData.video_platform_preset}
                  formData={formData}
                  onApply={(patched) => setFormData((prev) => ({ ...prev, ...patched }))}
                  recommendedKey={getSuggestedPresetForPlatform(formData.primary_platform)}
                  recommendedSourceLabel={getPlatformLabel(formData.primary_platform)}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className={labelClass}>AI Model</label>
                  <select
                    name="video_model"
                    value={formData.video_model || 'veo3_fast'}
                    onChange={handleInputChange}
                    className={inputClass}
                    data-testid="video-model-select"
                  >
                    <option value="freepik">Quick Video ✨</option>
                    <option value="veo3_fast">Fast Video 🚀</option>
                    <option value="veo3">Premium Video 🎥</option>
                    <option value="ultra_video">Ultra Video 🎬</option>
                    <option value="gemini_omni_video">Ultra Pro 🪐 (Recommended)</option>
                    {/* 🆕 Kling 3.0 / Seedance 2.0 — populated from /api/video-models/new */}
                    {newVideoModels.length > 0 && (
                      <optgroup label="🆕 New — Kling 3.0 / Seedance 2.0">
                        {newVideoModels.map((m) => (
                          <option key={m.id} value={m.id} data-testid={`video-model-option-${m.id}`}>
                            {m.name}{m.id === recommendedNewVideoModel ? ' — ⭐ Recommended' : ''}
                          </option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                  {newVideoModelsById[formData.video_model] && (
                    <p
                      className="mt-2 text-sm text-purple-700 dark:text-purple-300"
                      data-testid="new-video-model-hint"
                    >
                      {newVideoModelsById[formData.video_model].positioning}
                      {' · '}
                      {newVideoModelsById[formData.video_model].credit_cost_per_sec} credits/sec
                      {' · up to '}
                      {newVideoModelsById[formData.video_model].max_duration_sec}s
                      {newVideoModelsById[formData.video_model].supports_native_audio ? ' · native audio' : ''}
                    </p>
                  )}
                  {/* Kling/Seedance audio warning — hidden when the user has
                      turned ON the ElevenLabs voice-overlay toggle (which now
                      supplies real multilingual TTS via `eleven_multilingual_v2`
                      → muxed post-generation), OR when HeyGen is picked
                      (native lip-sync included). */}
                  {newVideoModelsById[formData.video_model] &&
                   formData.video_model !== 'heygen-photo-avatar' &&
                   !formData.add_voice_narration &&
                   formData.video_languages?.some(lang => lang !== 'en') ? (
                    <p
                      className="mt-2 text-sm text-amber-600 dark:text-amber-400 flex items-start"
                      data-testid="new-model-audio-warning"
                    >
                      <svg className="w-4 h-4 mr-1 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      <span>
                        <strong>Heads up:</strong> Kling & Seedance produce <em>ambient audio</em> by default. Turn ON <strong>🎙️ AI Voice Narration</strong> above to get real multilingual TTS — or switch to <strong>Talking Head (HeyGen) 🎭</strong> for lip-synced founder narration.
                      </span>
                    </p>
                  ) : null}

                  {/* HeyGen no-avatar warning — HeyGen needs a founder portrait.
                      Show if user picked it but hasn't attached any avatar. */}
                  {formData.video_model === 'heygen-photo-avatar' &&
                   !(Array.isArray(formData.avatar_image_urls) && formData.avatar_image_urls.length > 0) &&
                   !formData.avatar_image_url ? (
                    <p
                      className="mt-2 text-sm text-red-600 dark:text-red-400 flex items-start"
                      data-testid="heygen-no-avatar-warning"
                    >
                      <svg className="w-4 h-4 mr-1 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-5a1 1 0 011 1v4a1 1 0 11-2 0V6a1 1 0 011-1zm0 10a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                      </svg>
                      <span>
                        <strong>HeyGen needs a founder photo.</strong> Add one under <em>Your Avatar</em> below, or pick one from <em>Founder & team photos</em> if the website extraction found any. Without a photo, we'll automatically fall back to Ultra Pro 🪐 (Gemini Omni).
                      </span>
                    </p>
                  ) : null}
                  {formData.video_model === 'heygen-photo-avatar' &&
                   ((Array.isArray(formData.avatar_image_urls) && formData.avatar_image_urls.length > 0) || formData.avatar_image_url) ? (
                    <p
                      className="mt-2 text-sm text-emerald-700 dark:text-emerald-300 flex items-start"
                      data-testid="heygen-ready-hint"
                    >
                      <span className="mr-1">🎭</span>
                      <span>
                        <strong>Ready.</strong> HeyGen will animate your founder photo with a natural, language-matched voice. First-time avatar creation adds a one-time <strong>100-credit</strong> setup fee; subsequent runs of the same photo reuse the cached avatar.
                      </span>
                    </p>
                  ) : null}

                  {/* Existing legacy hints preserved below */}
                  {formData.video_languages?.some(lang => lang !== 'en') && !newVideoModelsById[formData.video_model] ? (
                    <p className="mt-2 text-sm text-blue-600 dark:text-blue-400 flex items-center" data-testid="video-model-lang-hint">
                      <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                      Fast Video (Veo 3.1) handles multilingual voiceover well — switch to Ultra Video for non-Latin text-heavy scenes.
                    </p>
                  ) : null}
                  {/* Seedance 2.0 is optimised for English/Chinese. For Dravidian
                      / Indic languages we warn the user because its Tamil TTS
                      is phonetically weak and can drift to Malayalam/Kannada. */}
                  {(formData.video_model === 'seedance-2.0-pro' || formData.video_model === 'seedance-2.0-fast') &&
                   formData.video_languages?.some(lang => ['ta','ml','kn','te','hi','bn','mr','gu','pa'].includes(lang)) ? (
                    <p
                      className="mt-2 text-sm text-amber-600 dark:text-amber-400 flex items-start"
                      data-testid="seedance-indic-warning"
                    >
                      <svg className="w-4 h-4 mr-1 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      <span>
                        <strong>Heads up:</strong> Seedance 2.0's native audio is tuned for English/Chinese. For Tamil / Hindi / other Indian languages, <strong>Kling 3.0 Pro</strong> gives much clearer, on-language narration. Consider switching if audio accuracy matters.
                      </span>
                    </p>
                  ) : null}
                </div>
                
                <div>
                  <label className={labelClass}>Duration</label>
                  <select
                    name="video_duration"
                    value={formData.video_duration}
                    onChange={handleInputChange}
                    className={inputClass}
                  >
                    {newVideoModelsById[formData.video_model] ? (
                      // 🆕 New models — offer 5/8/10 and 15 (Seedance Pro only)
                      <>
                        <option value={5}>5 seconds</option>
                        <option value={8}>8 seconds (Recommended)</option>
                        <option value={10}>10 seconds</option>
                        {newVideoModelsById[formData.video_model].max_duration_sec >= 15 && (
                          <option value={15}>15 seconds</option>
                        )}
                      </>
                    ) : formData.video_model === 'ultra_video' ? (
                      <>
                        <option value={10}>10 seconds (Recommended)</option>
                        <option value={15}>15 seconds</option>
                      </>
                    ) : formData.video_model === 'gemini_omni_video' ? (
                      <>
                        <option value={4}>4 seconds</option>
                        <option value={6}>6 seconds</option>
                        <option value={8}>8 seconds</option>
                        <option value={10}>10 seconds (Recommended)</option>
                      </>
                    ) : (
                      <>
                        <option value={4}>4 seconds</option>
                        <option value={6}>6 seconds</option>
                        <option value={8}>8 seconds (Recommended)</option>
                      </>
                    )}
                  </select>
                </div>
                
                <div>
                  <label className={labelClass}>Resolution</label>
                  <select
                    name="video_resolution"
                    value={
                      newVideoModelsById[formData.video_model]
                        ? newVideoModelsById[formData.video_model].resolution
                        : formData.video_resolution
                    }
                    onChange={handleInputChange}
                    className={inputClass}
                    disabled={!!newVideoModelsById[formData.video_model]}
                  >
                    {newVideoModelsById[formData.video_model] ? (
                      <option value={newVideoModelsById[formData.video_model].resolution}>
                        {newVideoModelsById[formData.video_model].resolution} (fixed for this model)
                      </option>
                    ) : (
                      <>
                        <option value="720p">720p (HD) - 50 credits</option>
                        {/* 1080p supported by Veo 3.1 and Sora 2 */}
                        {(formData.video_model === 'veo3' || formData.video_model === 'veo3_fast') && (
                          <option value="1080p">1080p (Full HD) - 60 credits</option>
                        )}
                        {formData.video_model === 'ultra_video' && (
                          <option value="1080p">1080p (Full HD) - 75 credits</option>
                        )}
                        {formData.video_model === 'gemini_omni_video' && (
                          <option value="1080p">1080p (Full HD) - 80 credits</option>
                        )}
                        {/* 4K only supported by Veo 3.1 models — Gemini Omni rejects 4K */}
                        {(formData.video_model === 'veo3' || formData.video_model === 'veo3_fast') && (
                          <option value="4K">4K (Ultra HD) - 100 credits (~7-13 min)</option>
                        )}
                      </>
                    )}
                  </select>
                  <p className="text-[11px] text-slate-400 mt-1">
                    {newVideoModelsById[formData.video_model]
                      ? `${newVideoModelsById[formData.video_model].name} — fixed ${newVideoModelsById[formData.video_model].resolution} output`
                      : formData.video_model === 'veo3' 
                      ? 'Premium Video supports up to 4K resolution' 
                      : formData.video_model === 'veo3_fast'
                        ? 'Fast Video supports up to 4K resolution'
                        : formData.video_model === 'ultra_video'
                          ? 'Ultra Video supports up to 1080p resolution'
                          : formData.video_model === 'gemini_omni_video'
                            ? 'Gemini Omni supports up to 4K with crisp text rendering'
                            : 'Standard HD resolution'}
                  </p>
                </div>
                
                <div>
                  <label className={labelClass}>Aspect Ratio</label>
                  <select
                    name="video_aspect_ratio"
                    value={formData.video_aspect_ratio}
                    onChange={handleInputChange}
                    className={inputClass}
                  >
                    <option value="16:9">16:9 (Landscape)</option>
                    <option value="1:1">1:1 (Square)</option>
                    {(formData.video_model === 'gemini_omni_video' || newVideoModelsById[formData.video_model]) && (
                      <option value="9:16">9:16 (Vertical / Reels)</option>
                    )}
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Image Specifications Section */}
          {(selectedAdTypes.includes('image_promo') || selectedAdTypes.includes('telegram_ad')) && (
            <div className={sectionCardClass} data-testid="image-specs-section">
              <h4 className={`${sectionTitleClass} mb-5`}>
                Image Specifications
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>AI Image Model</label>
                  <select
                    name="image_model"
                    value={formData.image_model || 'freepik'}
                    onChange={handleInputChange}
                    className={inputClass}
                    data-testid="image-model-select"
                  >
                    <option value="nebius">Quick Image</option>
                    <option value="kie-ai">Standard Image</option>
                    <option value="freepik">Best Value (Recommended)</option>
                    <option value="nano-banana-pro">Premium Image</option>
                    <option value="nano-banana-2">Premium Image 2 (Flash 4K)</option>
                    <option value="gpt4o-image">Ultra Image</option>
                  </select>
                  {formData.video_languages?.some(lang => lang !== 'en') ? (
                    <p className="mt-1 text-[11px] text-blue-600 dark:text-blue-400 flex items-center" data-testid="image-model-lang-hint">
                      <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                      Ultra Image picked by default for non-English text — Premium Image (Nano Banana Pro) also handles multilingual text well, switch freely.
                    </p>
                  ) : (
                    <p className="mt-1 text-[11px] text-slate-400">
                      {formData.image_model === 'gpt4o-image'
                        ? 'Best quality with accurate text rendering'
                        : formData.image_model === 'nano-banana-pro'
                        ? '4K ultra-high definition with next-gen AI'
                        : formData.image_model === 'nano-banana-2'
                        ? 'Flash speed + 4K quality (Gemini 3.1 Flash)'
                        : formData.image_model === 'kie-ai' 
                        ? 'Standard quality with fast generation'
                        : formData.image_model === 'freepik'
                        ? 'Premium quality, fast, cost-effective'
                        : 'Very fast with good quality results'
                      }
                    </p>
                  )}
                </div>
                
                {/* Resolution selector for Nano Banana Pro / Nano Banana 2 */}
                {(formData.image_model === 'nano-banana-pro' || formData.image_model === 'nano-banana-2') && (
                  <div>
                    <label className={labelClass}>Image Resolution</label>
                    <select
                      name="nano_banana_resolution"
                      value={formData.nano_banana_resolution || '4K'}
                      onChange={handleInputChange}
                      className={inputClass}
                    >
                      <option value="1K">Standard Quality (1K) - 18 credits</option>
                      <option value="2K">High Quality (2K) - 18 credits</option>
                      <option value="4K">Ultra HD (4K) - 24 credits (Recommended)</option>
                    </select>
                    <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                      Higher resolution provides sharper details and better quality
                    </p>
                  </div>
                )}
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Image Quality Tips
                  </label>
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                    <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
                      <li>• Include product prominently in the image</li>
                      <li>• Specify target demographic for better results</li>
                      <li>• Professional photography style works best</li>
                      <li>• Geographic context improves authenticity</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Ad Types Section */}
          <div className={sectionCardClass} data-testid="ad-types-section">
            <h4 className={`${sectionTitleClass} mb-5`}>
              Marketing Content Types
            </h4>
            <div className="grid grid-cols-2 gap-3">
              {adTypes.map(adType => (
                <label key={adType.value} className={`flex items-center p-2.5 rounded-xl border cursor-pointer transition-all ${
                  selectedAdTypes.includes(adType.value)
                    ? colorTheme === 'purple' ? 'bg-purple-50 dark:bg-purple-900/20 border-purple-300 dark:border-purple-600' :
                      colorTheme === 'blue' ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-600' :
                      'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-300 dark:border-emerald-600'
                    : 'bg-white/50 dark:bg-slate-800/30 border-slate-200 dark:border-slate-700/50 hover:border-slate-300'
                }`} data-testid={`ad-type-${adType.value}`}>
                  <input
                    type="checkbox"
                    checked={selectedAdTypes.includes(adType.value)}
                    onChange={() => handleAdTypeChange(adType.value)}
                    data-testid={`ad-type-${adType.value}-checkbox`}
                    className={`mr-2.5 w-4 h-4 rounded ${
                      colorTheme === 'purple' ? 'text-purple-600 focus:ring-purple-500' :
                      colorTheme === 'blue' ? 'text-blue-600 focus:ring-blue-500' :
                      'text-emerald-600 focus:ring-emerald-500'
                    }`}
                  />
                  <BrandIcon templateId={adType.value} size={16} className="mr-1.5 flex-shrink-0" />
                  <span className="text-sm text-slate-700 dark:text-slate-300">{adType.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="mt-2">
            {/* Optional free-form instructions for this generation. Same input
                we use for regeneration — modular component. Leave blank to
                generate without overrides. */}
            <details className="mb-3 group">
              <summary className="cursor-pointer text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white flex items-center gap-1.5 select-none">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="transition-transform group-open:rotate-90"><polyline points="9 18 15 12 9 6"/></svg>
                <span>Additional instructions</span>
                <span className="text-xs text-slate-400 font-normal">(optional)</span>
              </summary>
              <div className="mt-2 pl-5">
                <FeedbackInput
                  value={userFeedback}
                  onChange={setUserFeedback}
                  label=""
                  optionalText=""
                  rows={3}
                  testIdBase="ad-creator-feedback"
                  helperText="Anything to fix, avoid, or emphasise? The AI will incorporate this for every ad in this campaign."
                />
              </div>
            </details>
          </div>

          <div className="mt-2 flex gap-3">
            <button
              type="button"
              onClick={() => setShowAdPreview(true)}
              className={`px-5 py-4 rounded-2xl text-sm font-semibold transition-all border-2 flex items-center gap-2 ${
                colorTheme === 'purple' ? 'border-purple-300 dark:border-purple-600 text-purple-700 dark:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-900/20' :
                colorTheme === 'blue' ? 'border-blue-300 dark:border-blue-600 text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20' :
                'border-emerald-300 dark:border-emerald-600 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-900/20'
              }`}
              data-testid="preview-ads-btn"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/></svg>
              Preview
            </button>
            <button
              type="submit"
              disabled={isGenerating || isLoading || !selectedAdTypes || selectedAdTypes.length === 0}
              title={(!selectedAdTypes || selectedAdTypes.length === 0)
                ? 'Select at least one ad type above to enable generation'
                : ''}
              className={`flex-1 ${
                colorTheme === 'purple' ? 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:from-purple-300 disabled:to-pink-300 shadow-lg shadow-purple-500/20' :
                colorTheme === 'blue' ? 'bg-gradient-to-r from-blue-600 to-sky-500 hover:from-blue-700 hover:to-sky-600 disabled:from-blue-300 disabled:to-sky-300 shadow-lg shadow-blue-500/20' :
                'bg-gradient-to-r from-emerald-600 to-teal-500 hover:from-emerald-700 hover:to-teal-600 disabled:from-emerald-300 disabled:to-teal-300 shadow-lg shadow-emerald-500/20'
              } disabled:cursor-not-allowed text-white font-bold py-4 px-8 rounded-2xl text-base transition-all duration-200 flex items-center justify-center space-x-2 hover:scale-[1.01]`}
              data-testid="generate-ads-btn"
            >
              {isGenerating || isLoading ? (
                <div className="flex flex-col items-center w-full">
                  <div className="flex items-center mb-4">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                    <span className="text-sm font-medium">{generationProgress.message || 'Generating Marketing Content...'}</span>
                  </div>
                  
                  {generationProgress.total > 0 && (
                    <div className="w-full max-w-md">
                      {/* Progress Stats */}
                      <div className={`flex justify-between text-xs mb-2 ${colorTheme === 'purple' ? 'text-purple-100' : colorTheme === 'blue' ? 'text-blue-100' : 'text-green-100'}`}>
                        <span>
                          {generationProgress.hasPersonas && generationProgress.personaCount > 1
                            ? `${Math.ceil(generationProgress.step / generationProgress.personaCount)} ads × ${generationProgress.personaCount} personas`
                            : `${generationProgress.step} of ${generationProgress.total} pieces`
                          }
                        </span>
                        {generationProgress.startTime && (
                          <span>{Math.floor((Date.now() - generationProgress.startTime) / 1000)}s elapsed</span>
                        )}
                      </div>
                      
                      {/* Progress Bar */}
                      <div className={`w-full ${colorTheme === 'purple' ? 'bg-purple-200' : 'bg-green-200'} rounded-full h-3 mb-3`}>
                        <div 
                          className={`bg-gradient-to-r ${colorTheme === 'purple' ? 'from-purple-400 to-purple-600' : 'from-green-400 to-green-600'} h-3 rounded-full transition-all duration-1000 ease-out flex items-center justify-end pr-2`}
                          style={{ width: `${Math.min(95, (generationProgress.step / generationProgress.total) * 100)}%` }}
                        >
                          {generationProgress.step > 0 && (
                            <div className="text-white text-xs font-bold">
                              {Math.round((generationProgress.step / generationProgress.total) * 100)}%
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Stage Indicator */}
                      <div className={`flex items-center justify-center space-x-4 text-xs ${colorTheme === 'purple' ? 'text-purple-100' : colorTheme === 'blue' ? 'text-blue-100' : 'text-green-100'}`}>
                        <div className={`flex items-center space-x-1 ${generationProgress.currentStage === 'initializing' ? (colorTheme === 'purple' ? 'text-purple-300' : 'text-green-300') + ' font-bold' : ''}`}>
                          <div className={`w-2 h-2 rounded-full ${['initializing', 'persona_generation', 'content_generation', 'finalizing', 'complete'].includes(generationProgress.currentStage) ? (colorTheme === 'purple' ? 'bg-purple-300' : colorTheme === 'blue' ? 'bg-blue-300' : 'bg-green-300') : (colorTheme === 'purple' ? 'bg-purple-500' : colorTheme === 'blue' ? 'bg-blue-500' : 'bg-green-500')}`}></div>
                          <span>Init</span>
                        </div>
                        
                        {generationProgress.hasPersonas && (
                          <div className={`flex items-center space-x-1 ${generationProgress.currentStage === 'persona_generation' ? (colorTheme === 'purple' ? 'text-purple-300' : 'text-green-300') + ' font-bold' : ''}`}>
                            <div className={`w-2 h-2 rounded-full ${['persona_generation', 'content_generation', 'finalizing', 'complete'].includes(generationProgress.currentStage) ? (colorTheme === 'purple' ? 'bg-purple-300' : colorTheme === 'blue' ? 'bg-blue-300' : 'bg-green-300') : (colorTheme === 'purple' ? 'bg-purple-500' : colorTheme === 'blue' ? 'bg-blue-500' : 'bg-green-500')}`}></div>
                            <span>Personas</span>
                          </div>
                        )}
                        
                        <div className={`flex items-center space-x-1 ${generationProgress.currentStage === 'content_generation' ? (colorTheme === 'purple' ? 'text-purple-300' : 'text-green-300') + ' font-bold' : ''}`}>
                          <div className={`w-2 h-2 rounded-full ${['content_generation', 'finalizing', 'complete'].includes(generationProgress.currentStage) ? (colorTheme === 'purple' ? 'bg-purple-300' : colorTheme === 'blue' ? 'bg-blue-300' : 'bg-green-300') : (colorTheme === 'purple' ? 'bg-purple-500' : colorTheme === 'blue' ? 'bg-blue-500' : 'bg-green-500')}`}></div>
                          <span>Content</span>
                        </div>
                        
                        <div className={`flex items-center space-x-1 ${generationProgress.currentStage === 'finalizing' ? (colorTheme === 'purple' ? 'text-purple-300' : 'text-green-300') + ' font-bold' : ''}`}>
                          <div className={`w-2 h-2 rounded-full ${['finalizing', 'complete'].includes(generationProgress.currentStage) ? (colorTheme === 'purple' ? 'bg-purple-300' : colorTheme === 'blue' ? 'bg-blue-300' : 'bg-green-300') : (colorTheme === 'purple' ? 'bg-purple-500' : colorTheme === 'blue' ? 'bg-blue-500' : 'bg-green-500')}`}></div>
                          <span>Final</span>
                        </div>
                        
                        <div className={`flex items-center space-x-1 ${generationProgress.currentStage === 'complete' ? (colorTheme === 'purple' ? 'text-purple-300' : 'text-green-300') + ' font-bold' : ''}`}>
                          <div className={`w-2 h-2 rounded-full ${generationProgress.currentStage === 'complete' ? (colorTheme === 'purple' ? 'bg-purple-300' : colorTheme === 'blue' ? 'bg-blue-300' : 'bg-green-300') : (colorTheme === 'purple' ? 'bg-purple-500' : colorTheme === 'blue' ? 'bg-blue-500' : 'bg-green-500')}`}></div>
                          <span>Done</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <span>🚀</span>
                  <span>Generate AI Marketing Content</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Phase 4: Credit Cost Preview Modal - Moved to App level (see line ~18991) */}

      {/* Ad Preview Panel */}
      <AdPreviewPanel
        formData={formData}
        selectedAdTypes={selectedAdTypes}
        isOpen={showAdPreview}
        onClose={() => setShowAdPreview(false)}
        colorTheme={colorTheme}
      />
    </section>
  );
};

const AdResults = ({ 
  results, 
  onClearForm, 
  onShareIndividualAd, 
  onShareEntireCampaign, 
  shareToTelegram,
  showSharingModal,
  setShowSharingModal,
  sharingType,
  setSharingType,
  selectedAd,
  setSelectedAd,
  sharingStatus,
  setSharingStatus,
  setShowChannelSelectionModal,
  showChannelSelectionModal,
  copiedText,
  copyToClipboard,
  saveStatus,
  setSaveStatus,
  // Inline editing props
  editingField,
  setEditingField,
  editContent,
  setEditContent,
  startEditing,
  cancelEditing,
  saveEdit,
  showContentHistory,
  onRegenerate,
  onRegenerateIndividualAd,
  isGenerating
}) => {
  // Function to get AI model name from ad title or form data
  const getImageModelName = (ad) => {
    if (ad && ad.title) {
      if (ad.title.includes('Freepik') || ad.title.includes('AI Image')) {
        return 'AI Image';
      } else if (ad.title.includes('Kie AI') || ad.title.includes('Nano Banana')) {
        return 'AI Image';
      } else if (ad.title.includes('Nebius')) {
        return 'AI Image';
      }
    }
    // Check form data if available
    if (results && results.form_data) {
      if (results.form_data.image_model === 'freepik') {
        return 'AI Image';
      } else if (results.form_data.image_model === 'kie-ai') {
        return 'AI Image';
      }
    }
    return 'AI Image'; // Default fallback
  };

  // Function to get dynamic image promo label
  const getImagePromoLabel = (ad = null) => {
    return 'AI Generated Image';
  };

  const { sessionToken } = useAuth();
  const { colorTheme } = useColorTheme();
  const [savedCampaigns, setSavedCampaigns] = useState([]);

  // URL Copy modal state for AdResults
  const [urlCopyModal, setUrlCopyModal] = useState({
    isOpen: false,
    url: '',
    title: 'Link Copied!',
    description: 'Share this link with others to get feedback on your campaign:'
  });

  // AI Feedback modal state
  const [showAIFeedback, setShowAIFeedback] = useState(false);

  if (!results) return null;

  // Dynamic function to get ad type labels based on video model
  const getAdTypeLabel = (adType, videoModel = null, imageModel = null) => {
    // Dynamic image title based on model for image_promo
    if (adType === 'image_promo') {
      return 'AI Generated Image';
    }

    // Dynamic video title based on model for both video_promo and video_ad
    if (adType === 'video_promo' || adType === 'video_ad') {
      return 'AI Generated Video';
    }

    const baseLabels = {
      facebook_ad: 'Facebook Ad',
      instagram_ad: 'Instagram Post',
      linkedin_ad: 'LinkedIn Ad',
      tiktok_ad: 'TikTok Content',
      telegram_ad: 'Telegram Channel',
      social_media: 'Social Media',
      google_ads: 'Google Ads',
      product_description: 'Product Description',
      email_marketing: 'Email Marketing'
    };

    return baseLabels[adType] || adType;
  };

  // Helper function to remove model names from ad titles
  const cleanAdTitle = (title) => {
    if (!title) return title;
    // Remove model names from titles - hide technical details from users
    return title
      .replace(/\(Freepik Nano Banana\)/gi, '')
      .replace(/\(Kie AI Nano Banana\)/gi, '')
      .replace(/\(Nebius AI\)/gi, '')
      .replace(/\(Google Veo 3\)/gi, '')
      .replace(/\(Freepik MiniMax Hailuo\)/gi, '')
      .replace(/\(Replicate \+ ElevenLabs\)/gi, '')
      .replace(/\(GPT-4o\)/gi, '')
      .replace(/\(Ultra Image \(GPT-4o\)\)/gi, '')
      .replace(/\(Ultra Image\)/gi, '')
      .replace(/\(AI Image\)/gi, '')
      .replace(/\(Ultra HD Image\)/gi, '')
      .replace(/\(Premium Image\)/gi, '')
      .replace(/\(Quick Image\)/gi, '')
      .replace(/\(Standard Image\)/gi, '')
      .replace(/\(Best Value\)/gi, '')
      .replace(/\(Gemini 3\.0 Pro Ultra HD\)/gi, '')
      .replace(/GPT-4o/gi, '')
      .replace(/Nano Banana/gi, '')
      .replace(/\s+-\s+/g, ' - ')  // Clean up extra spaces around dashes
      .replace(/\s{2,}/g, ' ')      // Replace multiple spaces with single space
      .trim();
  };

  // This adTypeLabels object is NOT used for dynamic labels - getAdTypeLabel function handles that
  // Keeping this for backward compatibility with non-dynamic rendering
  const adTypeLabels = {
    facebook_ad: 'Facebook Ad',
    instagram_ad: 'Instagram Post',
    linkedin_ad: 'LinkedIn Ad',
    tiktok_ad: 'TikTok Content',
    telegram_ad: 'Telegram Channel',
    social_media: 'Social Media',
    google_ads: 'Google Ads',
    product_description: 'Product Description',
    email_marketing: 'Email Marketing',
    video_promo: 'AI Video with Voiceover',
    image_promo: 'AI Generated Image'  // Generic fallback - actual label is dynamic
  };

  const adTypeIcons = {
    facebook_ad: <BrandIcon templateId="facebook_ad" size={24} />,
    instagram_ad: <BrandIcon templateId="instagram_ad" size={24} />,
    linkedin_ad: <BrandIcon templateId="linkedin_ad" size={24} />,
    tiktok_ad: <BrandIcon templateId="tiktok_ad" size={24} />,
    telegram_ad: <BrandIcon templateId="telegram_ad" size={24} />,
    social_media: <BrandIcon templateId="social_media" size={24} />,
    google_ads: <BrandIcon templateId="google_ads" size={24} />,
    product_description: <BrandIcon templateId="product_description" size={24} />,
    email_marketing: <BrandIcon templateId="email_marketing" size={24} />,
    video_promo: <BrandIcon templateId="video_promo" size={24} />,
    image_promo: <BrandIcon templateId="image_promo" size={24} />
  };

  const saveToFavorites = async (campaignId) => {
    try {
      setSaveStatus({ ...saveStatus, [campaignId]: 'saving' });
      
      // The campaign is already saved to database when generated
      // This is just for UI feedback
      setSavedCampaigns([...savedCampaigns, campaignId]);
      setSaveStatus({ ...saveStatus, [campaignId]: 'saved' });
      
      setTimeout(() => {
        setSaveStatus({ ...saveStatus, [campaignId]: '' });
      }, 2000);
      
    } catch (error) {
      console.error('Error saving campaign:', error);
      setSaveStatus({ ...saveStatus, [campaignId]: 'error' });
    }
  };

  return (
    <section className="min-h-screen py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <h3 className={`text-3xl font-bold bg-gradient-to-r ${colorTheme === 'purple' ? 'from-purple-600 to-pink-600' : colorTheme === 'blue' ? 'from-blue-600 to-sky-500' : 'from-emerald-600 to-teal-500'} bg-clip-text text-transparent mb-2`}>
            Your Generated Marketing Content
          </h3>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            AI-powered copy ready to convert your audience into leads
          </p>
          <div className="mt-3 flex items-center justify-center">
            <div className="flex items-center text-emerald-600 dark:text-emerald-400 bg-emerald-50/80 dark:bg-emerald-900/20 px-3 py-1.5 rounded-xl text-xs font-medium">
              <span className="mr-1.5">✓</span>
              <span>Campaign automatically saved to your library</span>
            </div>
          </div>
        </div>
        
        {/* Display the user-supplied free-form instructions for this campaign
            (if any) — shown on the Results page, on Campaign Detail, and on
            Share-Feedback views. Helps the user remember what they asked the
            AI to do, and helps reviewers see what context produced the ads. */}
        {(results?.product?.user_feedback || results?.user_feedback) && (
          <div className="mb-6 max-w-3xl mx-auto" data-testid="campaign-user-feedback-banner">
            <div className="rounded-xl border border-amber-200 dark:border-amber-700/50 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 p-4">
              <div className="flex items-start gap-3">
                <span className="text-xl leading-none flex-shrink-0">🎯</span>
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300 mb-1">
                    User instructions applied to this campaign
                  </div>
                  <p className="text-sm text-amber-900 dark:text-amber-100 whitespace-pre-line break-words">
                    {results?.product?.user_feedback || results?.user_feedback}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {results.personas && results.personas.length > 0 && (
          <div className="mb-8">
            <div className="text-center mb-6">
              <h4 className={`text-xl font-bold bg-gradient-to-r ${colorTheme === 'purple' ? 'from-purple-600 to-pink-600' : colorTheme === 'blue' ? 'from-blue-600 to-sky-500' : 'from-emerald-600 to-teal-500'} bg-clip-text text-transparent mb-1`}>
                Generated Buyer Personas
              </h4>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {results.persona_reasoning || "These personas inform your marketing strategy"}
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
              {results.personas.map((persona, index) => (
                <div key={persona.id || index} className="bg-white/70 dark:bg-slate-800/50 backdrop-blur-xl border border-slate-200/50 dark:border-slate-700/50 rounded-2xl p-5 shadow-sm">
                  <div className="flex items-center mb-3">
                    <div className={`w-10 h-10 bg-gradient-to-br ${colorTheme === 'purple' ? 'from-purple-500 to-pink-500' : colorTheme === 'blue' ? 'from-blue-500 to-sky-500' : 'from-emerald-500 to-teal-500'} rounded-xl flex items-center justify-center text-white font-bold text-sm`}>
                      {persona.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="ml-3">
                      <h5 className="text-sm font-bold text-slate-800 dark:text-white">
                        {persona.name}
                      </h5>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        {persona.age_range} • {persona.gender}
                      </p>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <div>
                      <p className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                        Profile
                      </p>
                      <p className="text-xs text-slate-600 dark:text-slate-300">
                        {persona.occupation} in {persona.location}
                      </p>
                      <p className="text-xs text-slate-600 dark:text-slate-300">
                        Income: {persona.income_level}
                      </p>
                    </div>
                    
                    <div>
                      <p className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                        Key Pain Points
                      </p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {persona.pain_points.slice(0, 3).map((pain, idx) => (
                          <span key={idx} className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-300 text-[10px] px-1.5 py-0.5 rounded-md">
                            {pain}
                          </span>
                        ))}
                      </div>
                    </div>
                    
                    <div>
                      <p className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                        Motivations
                      </p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {persona.motivations.slice(0, 3).map((motivation, idx) => (
                          <span key={idx} className="bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-300 text-[10px] px-1.5 py-0.5 rounded-md">
                            {motivation}
                          </span>
                        ))}
                      </div>
                    </div>
                    
                    <div>
                      <p className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                        Preferred Platforms
                      </p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {persona.preferred_platforms.map((platform, idx) => (
                          <span key={idx} className="bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-300 text-[10px] px-1.5 py-0.5 rounded-md">
                            {platform}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {results.generated_ads.map((ad, index) => {
            // Group persona-specific ads and add numbering
            const isPersonaAd = ad.target_persona;
            
            // Extract base ad type and persona number for labeling
            let displayLabel = getAdTypeLabel(ad.ad_type, results?.product?.video_model, results?.product?.image_model);
            if (isPersonaAd) {
              // Count how many ads of this type we've seen so far for numbering
              const adsOfSameType = results.generated_ads.slice(0, index + 1).filter(a => a.ad_type === ad.ad_type);
              const adNumber = adsOfSameType.length;
              displayLabel = `${getAdTypeLabel(ad.ad_type, results?.product?.video_model, results?.product?.image_model)} ${adNumber}`;
            }

            return (
              <div 
                key={index} 
                className="bg-white/70 dark:bg-slate-800/50 backdrop-blur-xl border border-slate-200/50 dark:border-slate-700/50 rounded-2xl p-5 shadow-sm transition-all duration-300 hover:shadow-lg"
                data-testid={`ad-result-card-${index}`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center">
                    <span className="mr-3"><BrandIcon templateId={ad.ad_type} size={24} /></span>
                    <div>
                      <h4 className="text-sm font-bold text-slate-800 dark:text-white">
                        {displayLabel}
                      </h4>
                      {isPersonaAd && (
                        <div className="flex items-center space-x-2 mt-1">
                          <span className={`${colorTheme === 'purple' ? 'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-300' : colorTheme === 'blue' ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-300' : 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-300'} text-[10px] px-1.5 py-0.5 rounded-md font-medium`}>
                            {ad.target_persona}
                          </span>
                          {ad.persona_reasoning && (
                            <span className="text-[10px] text-slate-400 dark:text-slate-500" title={ad.persona_reasoning}>
                              i
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                
                <div className="flex gap-1.5">
                  <button
                    onClick={() => {
                      const uniqueKey = `${ad.ad_type}-${ad.target_persona || 'default'}-${index}`;
                      copyToClipboard(`${cleanAdTitle(ad.title)}\n\n${ad.content}\n\n${ad.call_to_action}`);
                      setSaveStatus(prev => ({ ...prev, [uniqueKey]: 'copied' }));
                      setTimeout(() => setSaveStatus(prev => ({ ...prev, [uniqueKey]: '' })), 2000);
                    }}
                    className="text-xs bg-white/80 dark:bg-slate-700/50 hover:bg-white dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 px-2.5 py-1.5 rounded-lg transition-colors border border-slate-200/50 dark:border-slate-600/50"
                    data-testid={`copy-ad-${index}`}
                  >
                    {saveStatus[`${ad.ad_type}-${ad.target_persona || 'default'}-${index}`] === 'copied' ? '✓ Copied' : 'Copy'}
                  </button>
                  
                  <button
                    onClick={() => onShareIndividualAd(ad, index)}
                    className={`text-xs bg-gradient-to-r ${colorTheme === 'purple' ? 'from-purple-600 to-pink-600' : colorTheme === 'blue' ? 'from-blue-600 to-sky-500' : 'from-emerald-600 to-teal-500'} text-white px-2.5 py-1.5 rounded-lg transition-all shadow-sm hover:shadow-md`}
                    data-testid={`publish-ad-${index}`}
                  >
                    Publish
                  </button>
                  
                  {/* Regenerate Individual Ad Button */}
                  <button
                    onClick={() => {
                      if (isGenerating) {
                        console.log('Already generating, ignoring click');
                        return;
                      }
                      if (onRegenerateIndividualAd) {
                        onRegenerateIndividualAd(results, ad.ad_type, ad.target_persona);
                      } else {
                        console.error('onRegenerateIndividualAd function not available');
                        alert('Regenerate function not available. Please refresh the page.');
                      }
                    }}
                    disabled={isGenerating}
                    className={`text-xs ${isGenerating ? 'opacity-50 cursor-not-allowed' : ''} bg-white/80 dark:bg-slate-700/50 hover:bg-white dark:hover:bg-slate-700 ${colorTheme === 'purple' ? 'text-purple-600 dark:text-purple-300 border-purple-200/50 dark:border-purple-700/50' : colorTheme === 'blue' ? 'text-blue-600 dark:text-blue-300 border-blue-200/50 dark:border-blue-700/50' : 'text-emerald-600 dark:text-emerald-300 border-emerald-200/50 dark:border-emerald-700/50'} px-2.5 py-1.5 rounded-lg transition-colors border`}
                    title={isGenerating ? 'Generation in progress...' : `Regenerate this ${ad.ad_type}${ad.target_persona ? ' for ' + ad.target_persona : ''}`}
                    data-testid={`regenerate-individual-${index}`}
                  >
                    {isGenerating ? 'Generating...' : 'Regenerate'}
                  </button>
                </div>
              </div>
              
              <div className="space-y-3">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
                      Title/Headline
                    </p>
                    <div className="flex items-center space-x-1.5">
                      {ad.editable_fields?.title?.is_edited && (
                        <span className="text-[10px] bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-300 px-1.5 py-0.5 rounded-md font-medium">
                          Edited
                        </span>
                      )}
                      {editingField?.campaignId === results.id && editingField?.adIndex === index && editingField?.fieldName === 'title' ? (
                        <div className="flex items-center space-x-1">
                          <button
                            onClick={saveEdit}
                            className={`text-[10px] bg-gradient-to-r ${colorTheme === 'purple' ? 'from-purple-600 to-pink-600' : colorTheme === 'blue' ? 'from-blue-600 to-sky-500' : 'from-emerald-600 to-teal-500'} text-white px-2 py-1 rounded-lg transition-colors`}
                          >
                            Save
                          </button>
                          <button
                            onClick={cancelEditing}
                            className="text-[10px] bg-slate-200 hover:bg-slate-300 dark:bg-slate-600 dark:hover:bg-slate-500 text-slate-700 dark:text-slate-300 px-2 py-1 rounded-lg transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => startEditing(results.id, index, 'title', ad.title)}
                          className="text-[10px] bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-600 dark:text-slate-300 px-2 py-1 rounded-lg transition-colors"
                          data-testid={`edit-title-${index}`}
                        >
                          Edit
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {editingField?.campaignId === results.id && editingField?.adIndex === index && editingField?.fieldName === 'title' ? (
                    <textarea
                      value={editContent}
                      onChange={(e) => setEditContent(e.target.value)}
                      className="w-full text-sm font-semibold text-slate-800 dark:text-white bg-white/80 dark:bg-slate-700/50 border border-slate-200/50 dark:border-slate-600/50 rounded-xl px-3 py-2 resize-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500/50 outline-none"
                      rows="2"
                      autoFocus
                    />
                  ) : (
                    <p className="text-sm font-semibold text-slate-800 dark:text-white">
                      {cleanAdTitle(ad.title)}
                    </p>
                  )}
                </div>
                
                {/* Video Content */}
                {(ad.ad_type === 'video_promo' || ad.ad_type === 'video_ad') ? (
                  <>
                    {ad.video_url ? (
                      <div>
                        <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                          Generated Video
                        </p>
                        <video 
                          controls 
                          className="w-full rounded-xl border border-slate-200/50 dark:border-slate-700/50"
                          poster=""
                        >
                          <source src={ad.video_url} type="video/mp4" />
                          Your browser does not support the video tag.
                        </video>
                      </div>
                    ) : (
                      <div>
                        <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                          Video Status
                        </p>
                        <div className="bg-amber-50/80 dark:bg-amber-900/20 border border-amber-200/50 dark:border-amber-700/50 rounded-xl p-3">
                          <div className="flex items-center">
                            <div className={`animate-spin rounded-full h-4 w-4 border-b-2 ${colorTheme === 'purple' ? 'border-purple-600' : colorTheme === 'blue' ? 'border-blue-600' : 'border-emerald-600'} mr-2`}></div>
                            <span className="text-amber-700 dark:text-amber-300 text-xs">
                              Video generation in progress... This may take a few minutes.
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {ad.audio_url && (
                      <div>
                        <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                          AI-Generated Voiceover
                        </p>
                        <div className="bg-white/60 dark:bg-slate-700/30 rounded-xl p-3 border border-slate-200/30 dark:border-slate-600/30">
                          <audio 
                            controls 
                            className="w-full"
                            preload="metadata"
                          >
                            <source src={ad.audio_url} type="audio/mpeg" />
                            Your browser does not support the audio element.
                          </audio>
                          <div className="flex items-center mt-2 text-[10px] text-slate-500 dark:text-slate-400">
                            <span>Professional AI voiceover generated with ElevenLabs</span>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {ad.video_script && (
                      <div>
                        <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                          Video Script
                        </p>
                        <div className="bg-white/60 dark:bg-slate-700/30 rounded-xl p-3">
                          <pre className="text-slate-700 dark:text-slate-300 text-xs whitespace-pre-wrap">
                            {ad.video_script}
                          </pre>
                        </div>
                      </div>
                    )}
                    
                    <div>
                      <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                        Video Specifications
                      </p>
                      <p className="text-xs text-slate-700 dark:text-slate-300 leading-relaxed">
                        {ad.content}
                      </p>
                    </div>
                  </>
                ) : ad.ad_type === 'image_promo' ? (
                  /* Image Content */
                  <>
                    {ad.image_url ? (
                      <div>
                        <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                          {getImagePromoLabel(ad)}
                        </p>
                        <div className="bg-white/60 dark:bg-slate-700/30 rounded-xl p-3 border border-slate-200/30 dark:border-slate-600/30">
                          <img 
                            src={ad.image_url} 
                            alt={"AI Generated image for " + ad.title}
                            className="w-full rounded-xl shadow-sm"
                            crossOrigin="anonymous"
                            onError={(e) => {
                              console.error('Image load error:', ad.image_url, e);
                              setTimeout(() => {
                                if (!e.target.complete || e.target.naturalHeight === 0) {
                                  e.target.style.display = 'none';
                                  if (e.target.nextSibling) e.target.nextSibling.style.display = 'block';
                                }
                              }, 2000);
                            }}
                          />
                          <div className="hidden text-center text-slate-400 dark:text-slate-500 py-4">
                            Image failed to load
                          </div>
                          <div className="flex items-center mt-2 text-[10px] text-slate-500 dark:text-slate-400">
                            <span>Professional AI-generated marketing image</span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                          AI Image Generation
                        </p>
                        <div className="bg-slate-50/80 dark:bg-slate-700/30 rounded-xl p-4 border-2 border-dashed border-slate-200/50 dark:border-slate-600/50">
                          <div className="text-center">
                            <span className="text-slate-400 dark:text-slate-500 text-xs">
                              Image generation in progress... This may take a few minutes.
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    <div>
                      <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                        Image Description
                      </p>
                      <p className="text-xs text-slate-700 dark:text-slate-300 leading-relaxed">
                        {ad.content}
                      </p>
                    </div>
                  </>
                ) : ad.ad_type === 'whatsapp_ad' ? (
                  /* WhatsApp Message Content */
                  <>
                    <div>
                      <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                        WhatsApp Message Preview
                      </p>
                      <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-lg p-4 border border-green-200 dark:border-green-800">
                        <div className="flex items-center mb-2 text-[10px] text-green-600 dark:text-green-400">
                          <span>Optimized for WhatsApp direct messaging</span>
                        </div>
                        {/* Display actual WhatsApp message content */}
                        <div className="mt-3 text-slate-700 dark:text-slate-200 whitespace-pre-wrap text-xs leading-relaxed">
                          {ad.content}
                        </div>
                      </div>
                    </div>
                  </>
                ) : ad.ad_type === 'telegram_ad' ? (
                  /* Telegram Ad Content with Image Support */
                  <>
                    {ad.image_url ? (
                      <div>
                        <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                          AI Generated Telegram Image
                        </p>
                        <div className="bg-white/60 dark:bg-slate-700/30 rounded-xl p-3 border border-slate-200/30 dark:border-slate-600/30">
                          <img 
                            src={ad.image_url} 
                            alt={"AI Generated image for Telegram: " + ad.title}
                            className="w-full rounded-xl shadow-sm"
                            crossOrigin="anonymous"
                            onError={(e) => {
                              console.error('Image load error:', ad.image_url, e);
                              setTimeout(() => {
                                if (!e.target.complete || e.target.naturalHeight === 0) {
                                  e.target.style.display = 'none';
                                  if (e.target.nextSibling) e.target.nextSibling.style.display = 'block';
                                }
                              }, 2000);
                            }}
                          />
                          <div className="hidden text-center text-slate-400 dark:text-slate-500 py-4">
                            Image failed to load
                          </div>
                          <div className="flex items-center mt-2 text-[10px] text-slate-500 dark:text-slate-400">
                            <span>Optimized for Telegram channel sharing with persona targeting</span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                          AI Image Generation
                        </p>
                        <div className="bg-slate-50/80 dark:bg-slate-700/30 rounded-xl p-4 border-2 border-dashed border-slate-200/50 dark:border-slate-600/50">
                          <div className="text-center">
                            <span className="text-slate-400 dark:text-slate-500 text-xs">
                              Telegram image generation in progress...
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
                          Telegram Message Content
                        </p>
                        <div className="flex items-center space-x-1.5">
                          {ad.editable_fields?.content?.is_edited && (
                            <span className="text-[10px] bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-300 px-1.5 py-0.5 rounded-md font-medium">
                              Edited
                            </span>
                          )}
                          {editingField?.campaignId === results.id && editingField?.adIndex === index && editingField?.fieldName === 'content' ? (
                            <div className="flex items-center space-x-1">
                              <button
                                onClick={saveEdit}
                                className={`text-[10px] bg-gradient-to-r ${colorTheme === 'purple' ? 'from-purple-600 to-pink-600' : colorTheme === 'blue' ? 'from-blue-600 to-sky-500' : 'from-emerald-600 to-teal-500'} text-white px-2 py-1 rounded-lg transition-colors`}
                              >
                                Save
                              </button>
                              <button
                                onClick={cancelEditing}
                                className="text-[10px] bg-slate-200 hover:bg-slate-300 dark:bg-slate-600 dark:hover:bg-slate-500 text-slate-700 dark:text-slate-300 px-2 py-1 rounded-lg transition-colors"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => startEditing(results.id, index, 'content', ad.content)}
                              className="text-[10px] bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-600 dark:text-slate-300 px-2 py-1 rounded-lg transition-colors"
                            >
                              Edit
                            </button>
                          )}
                        </div>
                      </div>
                      
                      {editingField?.campaignId === results.id && editingField?.adIndex === index && editingField?.fieldName === 'content' ? (
                        <textarea
                          value={editContent}
                          onChange={(e) => setEditContent(e.target.value)}
                          className="w-full bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 border border-blue-300 rounded-lg p-3 text-blue-800 dark:text-blue-300 leading-relaxed resize-none"
                          rows="4"
                          autoFocus
                        />
                      ) : (
                        <div className="bg-white/60 dark:bg-slate-700/30 rounded-xl p-3 border border-slate-200/30 dark:border-slate-600/30">
                          <p className="text-slate-700 dark:text-slate-300 leading-relaxed text-xs">
                            {ad.content}
                          </p>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  /* Regular Content for non-video ads */
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
                        Content
                      </p>
                      <div className="flex items-center space-x-1.5">
                        {ad.editable_fields?.content?.is_edited && (
                          <span className="text-[10px] bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-300 px-1.5 py-0.5 rounded-md font-medium">
                            Edited
                          </span>
                        )}
                        {editingField?.campaignId === results.id && editingField?.adIndex === index && editingField?.fieldName === 'content' ? (
                          <div className="flex items-center space-x-1">
                            <button
                              onClick={saveEdit}
                              className={`text-[10px] bg-gradient-to-r ${colorTheme === 'purple' ? 'from-purple-600 to-pink-600' : colorTheme === 'blue' ? 'from-blue-600 to-sky-500' : 'from-emerald-600 to-teal-500'} text-white px-2 py-1 rounded-lg transition-colors`}
                            >
                              Save
                            </button>
                            <button
                              onClick={cancelEditing}
                              className="text-[10px] bg-slate-200 hover:bg-slate-300 dark:bg-slate-600 dark:hover:bg-slate-500 text-slate-700 dark:text-slate-300 px-2 py-1 rounded-lg transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => startEditing(results.id, index, 'content', ad.content)}
                            className="text-[10px] bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-600 dark:text-slate-300 px-2 py-1 rounded-lg transition-colors"
                          >
                            Edit
                          </button>
                        )}
                      </div>
                    </div>
                    
                    {editingField?.campaignId === results.id && editingField?.adIndex === index && editingField?.fieldName === 'content' ? (
                      <textarea
                        value={editContent}
                        onChange={(e) => setEditContent(e.target.value)}
                        className="w-full text-xs text-slate-700 dark:text-slate-300 bg-white/80 dark:bg-slate-700/50 border border-slate-200/50 dark:border-slate-600/50 rounded-xl px-3 py-2 leading-relaxed resize-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500/50 outline-none"
                        rows="4"
                        autoFocus
                      />
                    ) : (
                      <p className="text-xs text-slate-700 dark:text-slate-300 leading-relaxed">
                        {ad.content}
                      </p>
                    )}
                  </div>
                )}
                
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
                      Call to Action
                    </p>
                    <div className="flex items-center space-x-1.5">
                      {ad.editable_fields?.call_to_action?.is_edited && (
                        <span className="text-[10px] bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-300 px-1.5 py-0.5 rounded-md font-medium">
                          Edited
                        </span>
                      )}
                      {editingField?.campaignId === results.id && editingField?.adIndex === index && editingField?.fieldName === 'call_to_action' ? (
                        <div className="flex items-center space-x-1">
                          <button
                            onClick={saveEdit}
                            className={`text-[10px] bg-gradient-to-r ${colorTheme === 'purple' ? 'from-purple-600 to-pink-600' : colorTheme === 'blue' ? 'from-blue-600 to-sky-500' : 'from-emerald-600 to-teal-500'} text-white px-2 py-1 rounded-lg transition-colors`}
                          >
                            Save
                          </button>
                          <button
                            onClick={cancelEditing}
                            className="text-[10px] bg-slate-200 hover:bg-slate-300 dark:bg-slate-600 dark:hover:bg-slate-500 text-slate-700 dark:text-slate-300 px-2 py-1 rounded-lg transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => startEditing(results.id, index, 'call_to_action', ad.call_to_action)}
                          className="text-[10px] bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-600 dark:text-slate-300 px-2 py-1 rounded-lg transition-colors"
                        >
                          Edit
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {editingField?.campaignId === results.id && editingField?.adIndex === index && editingField?.fieldName === 'call_to_action' ? (
                    <input
                      type="text"
                      value={editContent}
                      onChange={(e) => setEditContent(e.target.value)}
                      className={`w-full ${colorTheme === 'purple' ? 'text-purple-600' : colorTheme === 'blue' ? 'text-blue-600' : 'text-emerald-600'} dark:text-amber-400 font-semibold text-sm bg-white/80 dark:bg-slate-700/50 border border-slate-200/50 dark:border-slate-600/50 rounded-xl px-3 py-2 focus:ring-2 focus:ring-emerald-500/30 outline-none`}
                      autoFocus
                    />
                  ) : (
                    <p className={`${colorTheme === 'purple' ? 'text-purple-600' : colorTheme === 'blue' ? 'text-blue-600' : 'text-emerald-600'} dark:text-amber-400 font-semibold text-sm`}>
                      {ad.call_to_action}
                    </p>
                  )}
                </div>

                {/* Platform-Specific Metadata */}
                {(ad.hashtags && ad.hashtags.length > 0) && (
                  <div>
                    <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                      Recommended Hashtags
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {ad.hashtags.map((hashtag, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-300 border border-blue-200/50 dark:border-blue-800/50"
                        >
                          #{hashtag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {ad.character_count && (
                  <div>
                    <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                      Character Count
                    </p>
                    <div className="flex items-center space-x-2">
                      <span className="text-xs text-slate-700 dark:text-slate-300">
                        {ad.character_count} characters
                      </span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-md ${
                        ad.character_count <= 125 
                          ? (colorTheme === 'purple' ? 'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-300' : 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-300')
                          : 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-300'
                      }`}>
                        {ad.character_count <= 125 ? 'Optimal' : 'Long'}
                      </span>
                    </div>
                  </div>
                )}

                {ad.optimal_posting_time && (
                  <div>
                    <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                      Optimal Posting Time
                    </p>
                    <p className="text-xs text-slate-700 dark:text-slate-300">
                      {ad.optimal_posting_time}
                    </p>
                  </div>
                )}

                {ad.platform_format && (
                  <div>
                    <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                      Platform Format
                    </p>
                    <p className="text-xs text-slate-700 dark:text-slate-300">
                      {ad.platform_format}
                    </p>
                  </div>
                )}

                {ad.platform_specific_notes && (
                  <div>
                    <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                      Platform Tips
                    </p>
                    <div className="bg-white/60 dark:bg-slate-700/30 rounded-xl p-3 border border-slate-200/30 dark:border-slate-600/30">
                      <p className="text-xs text-slate-700 dark:text-slate-300">
                        {ad.platform_specific_notes}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
            );
          })}
        </div>

        {/* Action Buttons Section */}
        <div className="mt-8 space-y-4">
          
          {/* Primary Actions */}
          <div className="bg-white/70 dark:bg-slate-800/50 backdrop-blur-xl border border-slate-200/50 dark:border-slate-700/50 rounded-2xl p-6 shadow-sm">
            <div className="text-center mb-4">
              <h4 className={`text-lg font-bold bg-gradient-to-r ${colorTheme === 'purple' ? 'from-purple-600 to-pink-600' : colorTheme === 'blue' ? 'from-blue-600 to-sky-500' : 'from-emerald-600 to-teal-500'} bg-clip-text text-transparent mb-1`}>
                Campaign Actions
              </h4>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                What would you like to do with your campaign?
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {/* AI Feedback Preview */}
              <button
                onClick={() => setShowAIFeedback(true)}
                className="flex items-center justify-center px-4 py-3 bg-white/80 dark:bg-slate-700/50 hover:bg-white dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-xl transition-all font-medium text-xs border border-slate-200/50 dark:border-slate-600/50 shadow-sm hover:shadow-md"
                data-testid="preview-ai-feedback-btn"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
                Preview AI Feedback
              </button>

              {/* Regenerate Campaign */}
              <button
                onClick={async () => {
                  // Use the handleRegenerate with credit preview modal
                  const regenerationData = {
                    product: {
                      product_name: results.product.product_name,
                      product_description: results.product.product_description,
                      key_benefits: results.product.key_benefits,
                      brand_voice: results.product.brand_voice || 'professional',
                      price: results.product.price,
                      category: results.product.category,
                      age_range_min: results.product.age_range_min || 25,
                      age_range_max: results.product.age_range_max || 55,
                      gender: results.product.gender || 'All',
                      geography: results.product.geography,
                      occupation: results.product.occupation,
                      industry_business_type: results.product.industry_business_type,
                      budget_range: results.product.budget_range,
                      interests_behaviors: results.product.interests_behaviors,
                      persona_count: results.product.persona_count || 'ai_determined',
                      enable_personas: results.product.enable_personas || false,
                      image_model: results.product.image_model || 'nebius',
                      nano_banana_resolution: results.product.nano_banana_resolution || '4K'
                    },
                    ad_types: [...new Set(results.generated_ads.map(ad => ad.ad_type))]
                  };
                  
                  // Call the regeneration handler which shows credit modal
                  if (onRegenerate) {
                    await onRegenerate(regenerationData, results);
                  }
                }}
                className={`flex items-center justify-center px-4 py-3 ${colorTheme === 'purple' ? 'bg-purple-600 hover:bg-purple-700' : colorTheme === 'blue' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-green-600 hover:bg-green-700'} text-white rounded-lg transition-colors duration-200 font-medium`}
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Regenerate Campaign
              </button>

              {/* Get Feedback Link */}
              <button
                onClick={async () => {
                  try {
                    if (!sessionToken) {
                      alert('Please sign in to create a feedback link.');
                      return;
                    }
                    
                    const response = await axios.post(`${API}/campaigns/${results.id}/share`, 
                      { is_public: true },
                      { headers: { Authorization: `Bearer ${sessionToken}` }}
                    );
                    
                    if (response.data && response.data.share_url) {
                      const shareUrl = response.data.share_url;
                      try {
                        await navigator.clipboard.writeText(shareUrl);
                      } catch (clipErr) {
                        const textArea = document.createElement('textarea');
                        textArea.value = shareUrl;
                        document.body.appendChild(textArea);
                        textArea.select();
                        document.execCommand('copy');
                        document.body.removeChild(textArea);
                      }
                      setUrlCopyModal({
                        isOpen: true,
                        url: shareUrl,
                        title: 'Feedback Link Copied!',
                        description: 'Share this link with others to get feedback on your campaign:'
                      });
                    } else {
                      alert('Failed to create feedback link. Please try again.');
                    }
                  } catch (error) {
                    const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
                    alert(`Failed to create feedback link: ${errorMsg}`);
                  }
                }}
                className={`flex items-center justify-center px-4 py-3 bg-gradient-to-r ${colorTheme === 'purple' ? 'from-purple-600 to-pink-600' : colorTheme === 'blue' ? 'from-blue-600 to-sky-500' : 'from-emerald-600 to-teal-500'} text-white rounded-xl transition-all font-medium text-xs shadow-sm hover:shadow-md hover:-translate-y-0.5`}
                data-testid="get-feedback-link-btn"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-3.582 8-8 8a8.955 8.955 0 01-4.126-.98L3 20l1.98-5.874A8.955 8.955 0 013 12c0-4.418 3.582-8 8-8s8 3.582 8 8z" />
                </svg>
                Get Feedback Link
              </button>
            </div>
          </div>

          {/* Sharing Section */}
          <div className="bg-white/70 dark:bg-slate-800/50 backdrop-blur-xl border border-slate-200/50 dark:border-slate-700/50 rounded-2xl p-6 shadow-sm">
            <div className="text-center mb-4">
              <h4 className={`text-lg font-bold bg-gradient-to-r ${colorTheme === 'purple' ? 'from-purple-600 to-pink-600' : colorTheme === 'blue' ? 'from-blue-600 to-sky-500' : 'from-emerald-600 to-teal-500'} bg-clip-text text-transparent mb-1`}>
                Publish Your Campaign
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                Distribute your marketing content across multiple channels instantly
              </p>
            </div>
            <div className="flex flex-wrap justify-center gap-4">
              <button
                onClick={() => onShareEntireCampaign()}
                className="flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200 text-sm font-medium"
              >
                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
                Publish Entire Campaign
              </button>
              
              <button
                onClick={() => {/* Individual publishing handled per ad */}}
                disabled={true}
                className="flex items-center px-4 py-2 bg-slate-200/50 dark:bg-slate-700/30 text-slate-500 dark:text-slate-400 rounded-xl text-xs font-medium cursor-help border border-slate-200/50 dark:border-slate-600/50"
                title="Individual ads can be published using the publish buttons on each ad card below"
              >
                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z" />
                </svg>
                Individual Ad Publishing (See ad cards)
              </button>
              
              <div className="text-[10px] text-slate-400 dark:text-slate-500 w-full text-center mt-2">
                Currently supporting: Telegram • More channels coming soon
              </div>
            </div>
          </div>
          
        </div>

        {/* AI Feedback Preview Section */}
        <div className="mt-8">
          <AIFeedbackSimulation 
            campaign={results}
            sessionToken={sessionToken}
            compact={true}
          />
        </div>

        <div className="mt-8 text-center">
          <div className="bg-white/70 dark:bg-slate-800/50 backdrop-blur-xl border border-slate-200/50 dark:border-slate-700/50 rounded-2xl p-6 shadow-sm">
            <h4 className={`text-lg font-bold bg-gradient-to-r ${colorTheme === 'purple' ? 'from-purple-600 to-pink-600' : colorTheme === 'blue' ? 'from-blue-600 to-sky-500' : 'from-emerald-600 to-teal-500'} bg-clip-text text-transparent mb-3`}>
              Campaign Saved Successfully!
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs text-slate-600 dark:text-slate-300 mb-4">
              <div className="flex items-center justify-center space-x-2">
                <span>Automatically saved to your library</span>
              </div>
              <div className="flex items-center justify-center space-x-2">
                <span>Dashboard updated with new campaign</span>
              </div>
              <div className="flex items-center justify-center space-x-2">
                <span>Generate more campaigns anytime</span>
              </div>
            </div>
            <div>
              <button
                onClick={() => {
                  onClearForm();
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className={`bg-gradient-to-r ${colorTheme === 'purple' ? 'from-purple-600 to-pink-600' : colorTheme === 'blue' ? 'from-blue-600 to-sky-500' : 'from-emerald-600 to-teal-500'} text-white font-bold py-2.5 px-6 rounded-xl transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 text-sm`}
                data-testid="create-another-campaign-btn"
              >
                Create Another Campaign
              </button>
            </div>
          </div>
        </div>

        {/* AI Feedback Modal */}
        {showAIFeedback && results && sessionToken && (
          <AIFeedbackSimulation
            campaign={results}
            sessionToken={sessionToken}
            onClose={() => setShowAIFeedback(false)}
          />
        )}

        {/* URL Copy Modal */}
        {urlCopyModal.isOpen && (
          <URLCopyModal
            isOpen={urlCopyModal.isOpen}
            url={urlCopyModal.url}
            title={urlCopyModal.title}
            description={urlCopyModal.description}
            onClose={() => setUrlCopyModal({ ...urlCopyModal, isOpen: false })}
          />
        )}
      </div>
    </section>
  );
};

const Analytics = () => {
  const { sessionToken } = useAuth();
  const { colorTheme } = useColorTheme();
  const [analyticsData, setAnalyticsData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timePeriod, setTimePeriod] = useState('all_time');
  const [customDateRange, setCustomDateRange] = useState({
    date_from: '',
    date_to: ''
  });

  useEffect(() => {
    fetchAnalytics();
  }, [timePeriod, customDateRange]);

  const fetchAnalytics = async () => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams();
      params.append('time_period', timePeriod);
      
      if (timePeriod === 'custom' && customDateRange.date_from && customDateRange.date_to) {
        params.append('date_from', customDateRange.date_from);
        params.append('date_to', customDateRange.date_to);
      }
      
      const response = await axios.get(`${API}/analytics/overview?${params}`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      setAnalyticsData(response.data);
    } catch (err) {
      setError('Failed to load analytics data');
      console.error('Error fetching analytics:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTimePeriodChange = (period) => {
    setTimePeriod(period);
  };

  const handleCustomDateChange = (field, value) => {
    setCustomDateRange(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (isLoading) {
    return (
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center">
            <div className={`animate-spin rounded-full h-12 w-12 border-b-2 ${colorTheme === 'purple' ? 'border-purple-600' : colorTheme === 'blue' ? 'border-blue-600' : 'border-green-600'}`}></div>
            <span className="ml-3 text-lg text-gray-600 dark:text-gray-300">Loading analytics...</span>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        </div>
      </section>
    );
  }

  if (!analyticsData) {
    return (
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-12">
            <div className="text-6xl mb-4">📊</div>
            <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              No analytics data available
            </h3>
            <p className="text-gray-600 dark:text-gray-300">
              Create some campaigns to see your analytics
            </p>
          </div>
        </div>
      </section>
    );
  }

  const { campaign_stats, performance_metrics } = analyticsData;

  // Prepare chart data
  const contentTypeData = Object.entries(performance_metrics.content_types || {}).map(([key, value]) => ({
    name: key.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()),
    value: value
  }));

  const categoryData = Object.entries(performance_metrics.categories || {}).map(([key, value]) => ({
    name: key,
    value: value
  }));

  const genderData = Object.entries(performance_metrics.demographics.genders || {}).map(([key, value]) => ({
    name: key,
    value: value
  }));

  const monthlyTrendData = Object.entries(campaign_stats.monthly_trends || {}).map(([key, value]) => ({
    month: key.split(' ')[0], // Just the month name
    campaigns: value
  }));

  return (
    <section className="py-20 bg-white dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-12">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Analytics Overview
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-300">
              Insights into your campaign performance and audience targeting
            </p>
          </div>
        </div>

        {/* Time Period Filter */}
        <div className="dashboard-search-bg rounded-lg p-6 mb-8">
          <div className="flex flex-col lg:flex-row gap-4 items-center">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Time Period:</span>
            <div className="flex gap-2">
              <button
                onClick={() => handleTimePeriodChange('all_time')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  timePeriod === 'all_time' 
                    ? (colorTheme === 'purple' ? 'bg-purple-600 text-white' : colorTheme === 'blue' ? 'bg-blue-600 text-white' : 'bg-green-600 text-white')
                    : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500'
                }`}
              >
                All Time
              </button>
              <button
                onClick={() => handleTimePeriodChange('last_6_months')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  timePeriod === 'last_6_months' 
                    ? (colorTheme === 'purple' ? 'bg-purple-600 text-white' : colorTheme === 'blue' ? 'bg-blue-600 text-white' : 'bg-green-600 text-white')
                    : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500'
                }`}
              >
                Last 6 Months
              </button>
              <button
                onClick={() => handleTimePeriodChange('custom')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  timePeriod === 'custom' 
                    ? (colorTheme === 'purple' ? 'bg-purple-600 text-white' : colorTheme === 'blue' ? 'bg-blue-600 text-white' : 'bg-green-600 text-white')
                    : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500'
                }`}
              >
                Custom Range
              </button>
            </div>
            
            {timePeriod === 'custom' && (
              <div className="flex gap-2">
                <input
                  type="date"
                  value={customDateRange.date_from}
                  onChange={(e) => handleCustomDateChange('date_from', e.target.value)}
                  className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg dashboard-focus-green dark:bg-gray-700 dark:text-white text-sm"
                />
                <input
                  type="date"
                  value={customDateRange.date_to}
                  onChange={(e) => handleCustomDateChange('date_to', e.target.value)}
                  className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg dashboard-focus-green dark:bg-gray-700 dark:text-white text-sm"
                />
              </div>
            )}
          </div>
        </div>

        {/* Campaign Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm font-medium">Total Campaigns</p>
                <p className="text-3xl font-bold">{campaign_stats.total_campaigns}</p>
              </div>
              <div className="text-4xl opacity-80">📊</div>
            </div>
          </div>

          <div className={`bg-gradient-to-r ${colorTheme === 'purple' ? 'from-purple-500 to-purple-600' : 'from-green-500 to-green-600'} rounded-lg p-6 text-white`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`${colorTheme === 'purple' ? 'text-purple-100' : colorTheme === 'blue' ? 'text-blue-100' : 'text-green-100'} text-sm font-medium`}>This Month</p>
                <p className="text-3xl font-bold">{campaign_stats.this_month_campaigns}</p>
              </div>
              <div className="text-4xl opacity-80">📈</div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm font-medium">Avg Per Month</p>
                <p className="text-3xl font-bold">{campaign_stats.avg_campaigns_per_month}</p>
              </div>
              <div className="text-4xl opacity-80">📅</div>
            </div>
          </div>
        </div>

        {/* Performance Metrics */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          {/* Content Types Distribution */}
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              Content Types Distribution
            </h3>
            <div className="space-y-3">
              {contentTypeData.map((item, index) => (
                <div key={index} className="flex items-center justify-between">
                  <span className="text-gray-700 dark:text-gray-300">{item.name}</span>
                  <div className="flex items-center">
                    <div className="w-32 bg-gray-200 dark:bg-gray-700 rounded-full h-2 mr-3">
                      <div 
                        className="bg-yellow-600 h-2 rounded-full"
                        style={{ width: `${Math.max(10, (item.value / Math.max(...contentTypeData.map(d => d.value))) * 100)}%` }}
                      ></div>
                    </div>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">{item.value}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Categories Distribution */}
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              Categories Distribution
            </h3>
            <div className="space-y-3">
              {categoryData.map((item, index) => (
                <div key={index} className="flex items-center justify-between">
                  <span className="text-gray-700 dark:text-gray-300">{item.name}</span>
                  <div className="flex items-center">
                    <div className="w-32 bg-gray-200 dark:bg-gray-700 rounded-full h-2 mr-3">
                      <div 
                        className="bg-blue-600 h-2 rounded-full"
                        style={{ width: `${Math.max(10, (item.value / Math.max(...categoryData.map(d => d.value))) * 100)}%` }}
                      ></div>
                    </div>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">{item.value}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Gender Distribution */}
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              Target Gender Distribution
            </h3>
            <div className="space-y-3">
              {genderData.map((item, index) => (
                <div key={index} className="flex items-center justify-between">
                  <span className="text-gray-700 dark:text-gray-300">{item.name}</span>
                  <div className="flex items-center">
                    <div className="w-32 bg-gray-200 dark:bg-gray-700 rounded-full h-2 mr-3">
                      <div 
                        className={`${colorTheme === 'purple' ? 'bg-purple-600' : colorTheme === 'blue' ? 'bg-blue-600' : 'bg-green-600'} h-2 rounded-full`}
                        style={{ width: `${Math.max(10, (item.value / Math.max(...genderData.map(d => d.value))) * 100)}%` }}
                      ></div>
                    </div>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">{item.value}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Monthly Trends */}
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              Campaign Creation Trends
            </h3>
            <div className="space-y-3">
              {monthlyTrendData.slice(0, 6).reverse().map((item, index) => (
                <div key={index} className="flex items-center justify-between">
                  <span className="text-gray-700 dark:text-gray-300">{item.month}</span>
                  <div className="flex items-center">
                    <div className="w-32 bg-gray-200 dark:bg-gray-700 rounded-full h-2 mr-3">
                      <div 
                        className="bg-purple-600 h-2 rounded-full"
                        style={{ width: `${Math.max(10, (item.campaigns / Math.max(...monthlyTrendData.map(d => d.campaigns), 1)) * 100)}%` }}
                      ></div>
                    </div>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">{item.campaigns}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Additional Metrics */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-lg">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            Brand Voice Usage
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {Object.entries(performance_metrics.brand_voices || {}).map(([voice, count]) => (
              <div key={voice} className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className={`text-2xl font-bold ${colorTheme === 'purple' ? 'text-purple-600' : colorTheme === 'blue' ? 'text-blue-600' : 'text-green-600'} dark:text-yellow-400`}>{count}</div>
                <div className="text-sm text-gray-600 dark:text-gray-300 capitalize">{voice}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const Footer = () => {
  return (
    <footer className="bg-gray-900 text-white py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <h3 className="text-2xl font-bold mb-4">AdElevate</h3>
          <p className="text-gray-400 mb-4">
            Metabolic AI Powered Lead Enhancer - Transform your products into compelling leads with the power of AI
          </p>
          <p className="text-sm text-gray-500">
            © 2026 AdElevate. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

// AI Assistant Chat Component
const AIAssistantChat = ({ user, onCampaignGenerated, setIsGenerating, setGenerationProgress, onNavigate }) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [isGenerating, setIsGeneratingLocal] = useState(false);
  const [currentOptions, setCurrentOptions] = useState([]); // New state for options
  
  // Image upload state
  const [uploadedImage, setUploadedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  
  // AI Assistant Mode State - Default to Agent Mode
  const [aiMode, setAiMode] = useState('agent'); // 'agent' = campaign mode, 'ask' = general Q&A
  const [showModeSelection, setShowModeSelection] = useState(false);
  
  const { sessionToken } = useAuth();
  
  // Initialize Agent Mode by default when session token is available
  useEffect(() => {
    if (sessionToken && aiMode === 'agent') {
      // Auto-initialize Agent Mode conversation
      const initializeAgentMode = async () => {
        try {
          const response = await axios.post(
            `${API}/ai-assistant/chat`,
            {
              message: "I want to create a marketing campaign",
              conversation_id: null
            },
            {
              headers: {
                'Authorization': `Bearer ${sessionToken}`,
                'Content-Type': 'application/json'
              },
              timeout: 30000
            }
          );

          const initialMessage = {
            role: 'assistant',
            content: response.data.message || response.data.response,
            timestamp: new Date(),
            options: response.data.options || []
          };

          setMessages([initialMessage]);
          setConversationId(response.data.conversation_id);
          setCurrentOptions(response.data.options || []);
        } catch (error) {
          console.error('Error initializing Agent Mode:', error);
        }
      };
      
      // Only initialize if no messages exist
      if (messages.length === 0) {
        initializeAgentMode();
      }
    }
  }, [sessionToken, aiMode]);

  // Expose AI Assistant generation function will be set after the function is defined

  // Image upload handler
  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please upload a valid image file');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('Image size must be less than 5MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (e) => {
        setUploadedImage(e.target.result); // base64 string
        setImagePreview(URL.createObjectURL(file));
        setImageFile(file);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setUploadedImage(null);
    setImagePreview(null);
    setImageFile(null);
    // Reset file input
    const fileInput = document.getElementById('image-upload-input');
    if (fileInput) fileInput.value = '';
  };

  const sendMessage = async (messageText = null, isOptionSelect = false) => {
    // Handle case where event object might be passed accidentally
    if (typeof messageText === 'object' && messageText !== null && messageText.nativeEvent) {
      messageText = null; // Reset if an event object was passed
    }
    
    const textToSend = messageText || inputMessage.trim();
    if (!textToSend || isLoading) return;

    const userMessage = {
      role: 'user',
      content: textToSend,
      timestamp: new Date(),
      isOptionSelect: isOptionSelect
    };

    // Add user message to chat
    setMessages(prev => [...prev, userMessage]);
    if (!messageText) setInputMessage(''); // Only clear if it's manual input
    setCurrentOptions([]); // Clear current options
    setIsLoading(true);

    try {
      const payload = {
        message: textToSend,
        conversation_id: conversationId
      };
      
      // Include image if uploaded
      if (uploadedImage) {
        payload.product_image = uploadedImage;
      }

      const response = await axios.post(
        `${API}/ai-assistant/chat`,
        payload,
        {
          headers: {
            'Authorization': `Bearer ${sessionToken}`,
            'Content-Type': 'application/json'
          },
          timeout: 30000
        }
      );

      const aiMessage = {
        role: 'assistant',
        content: response.data.message || response.data.response,
        timestamp: new Date(),
        options: response.data.options || []
      };

      // Clear options from previous messages and add new message
      setMessages(prev => {
        const updatedMessages = prev.map(msg => 
          msg.role === 'assistant' ? { ...msg, options: [] } : msg
        );
        return [...updatedMessages, aiMessage];
      });
      setConversationId(response.data.conversation_id);
      
      // Check if conversation is complete and ready for campaign generation
      if (response.data.is_complete && response.data.collected_data) {
        // DON'T show any options when conversation is complete - go straight to credit preview
        setCurrentOptions([]);
        // Show generate campaign button or auto-generate
        setTimeout(() => {
          generateCampaign(response.data.conversation_id);
        }, 2000);
      } else {
        // Only show options if conversation is NOT complete
        setCurrentOptions(response.data.options || []);
      }

    } catch (error) {
      console.error('AI Assistant error:', error);
      const errorMessage = {
        role: 'assistant',
        content: 'I apologize, but I encountered an error. Let\'s start fresh - what product would you like to create a campaign for?',
        timestamp: new Date(),
        options: [
          {"text": "Electronics (Headphones, Mobile phones, Laptops)", "value": "1"},
          {"text": "Beauty & Health (Skincare, Cosmetics, Supplements)", "value": "2"},
          {"text": "Fashion (Clothing, Accessories, Shoes)", "value": "3"},
          {"text": "Home & Garden (Furniture, Decor, Tools)", "value": "4"},
          {"text": "Fitness (Equipment, Apparel, Nutrition)", "value": "5"},
          {"text": "Other (Type your product)", "value": "6"}
        ]
      };
      setMessages(prev => [...prev, errorMessage]);
      setCurrentOptions(errorMessage.options);
    } finally {
      setIsLoading(false);
      // Don't clear image automatically - let user remove it manually or keep it for the conversation
    }
  };

  // Ask mode message handler (general Q&A)
  const handleAskModeMessage = async (message) => {
    if (!message.trim()) return;
    
    // Add user message to chat
    const userMessage = {
      role: 'user',
      content: message,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const response = await axios.post(
        `${API}/ai-assistant/ask-mode`,
        { 
          message: message,
          session_token: sessionToken 
        },
        {
          headers: {
            'Authorization': `Bearer ${sessionToken}`,
            'Content-Type': 'application/json'
          },
          timeout: 30000
        }
      );

      // Add AI response to chat
      const aiResponse = {
        role: 'assistant',
        content: response.data.response || 'I apologize, but I encountered an issue processing your request. Please try again.',
        timestamp: new Date(),
        options: []
      };
      
      setMessages(prev => [...prev, aiResponse]);
      
    } catch (error) {
      console.error('Ask mode error:', error);
      const errorMessage = {
        role: 'assistant',
        content: 'I apologize, but I encountered an error. Please try your question again or switch to Agent Mode for campaign creation.',
        timestamp: new Date(),
        options: []
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  // Enhanced message handler that routes to appropriate mode
  const handleSendMessage = () => {
    if (aiMode === 'ask') {
      handleAskModeMessage(inputMessage);
    } else if (aiMode === 'agent') {
      sendMessage(); // Existing agent mode handler
    }
  };

  const handleButtonClick = async (action, data) => {
    if (action === 'proceed_generation') {
      // User confirmed - proceed with generation
      await startGenerationAfterCreditConfirmation(data.convId);
    } else if (action === 'cancel_generation') {
      // User cancelled
      const cancelMessage = {
        role: 'assistant',
        content: '👌 No problem! Campaign generation cancelled. Feel free to modify your requirements or start over.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, cancelMessage]);
    } else if (action === 'go_to_credits') {
      // Navigate to credits page
      if (onNavigate) {
        onNavigate('credits');
      } else if (window.handleNavigation) {
        window.handleNavigation('credits');
      }
    }
  };

  const handleOptionSelect = (optionValue, optionText) => {
    sendMessage(optionValue, true);
  };

  const generateCampaign = async (convId) => {
    if (!convId) return;

    // ALWAYS reset flag at the START of each new campaign generation
    // Step 1: Get credit preview first (before showing generation modal)
    try {
      console.log('💰 AI Assistant: Fetching credit cost preview...');
      
      const previewResponse = await axios.post(`${API}/ai-assistant/preview-credit-cost`, {
        conversation_id: convId
      }, {
        headers: {
          Authorization: `Bearer ${sessionToken}`
        },
        timeout: 60000  // 60 seconds for multi-content campaigns
      });
      
      console.log('💰 AI Assistant: Credit preview received:', previewResponse.data);
      
      // Clear any pending options
      setCurrentOptions([]);
      
      // Show credit cost info in chat and ask for confirmation
      const costData = previewResponse.data;
      const confirmMessage = {
        role: 'assistant',
        content: `💳 **Credit Cost Preview**\n\n**Campaign Cost Breakdown:**\n• Gross Cost: ${costData.gross_cost?.total || 0} credits\n• Free Tier Discount: -${costData.free_tier_discount?.amount || 0} credits\n• **Net Cost: ${costData.net_cost} credits**\n\n${costData.using_free_tier ? '🎉 You\'re using free tier benefits!' : ''}\n\nProceed with campaign generation?`,
        timestamp: new Date(),
        hasButtons: true,
        buttons: [
          { label: '✅ Proceed', action: 'proceed_generation', data: { convId } },
          { label: '❌ Cancel', action: 'cancel_generation' }
        ]
      };
      setMessages(prev => [...prev, confirmMessage]);
      return; // Wait for user to click button
      
    } catch (error) {
      console.error('❌ AI Assistant: Error fetching credit preview:', error);
      
      // Clear any pending options
      setCurrentOptions([]);
      
      // Check if it's a credit error (insufficient credits)
      if (error.response && error.response.status === 402) {
        const errorData = error.response.data.detail || error.response.data;
        const requiredCredits = errorData.required_credits || 'unknown';
        const currentBalance = errorData.current_balance || 0;
        const shortfall = errorData.shortfall || requiredCredits;
        
        const errorMessage = {
          role: 'assistant',
          content: `💳 **Insufficient Credits**\n\nYou need **${requiredCredits} credits** to generate this campaign, but you only have **${currentBalance} credits**.\n\n**Shortfall: ${shortfall} credits**\n\nPlease add credits to continue.\n\nYou can add credits from your Profile → Credits page.`,
          timestamp: new Date(),
          hasButtons: true,
          buttons: [
            { label: '💰 Add Credits', action: 'go_to_credits' },
            { label: '❌ Cancel', action: 'cancel_generation' }
          ]
        };
        setMessages(prev => [...prev, errorMessage]);
        return;
      }
      
      // For other errors, show generic message
      const errorMessage = {
        role: 'assistant',
        content: 'Unable to fetch credit preview. Please try again.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
      return;
    }
  };

  // Separate function to actually start generation after credit confirmation
  const startGenerationAfterCreditConfirmation = async (convId) => {
    // This ensures each campaign starts fresh with sync flow as default
    window.userChoseBackgroundMode = false;
    console.log('🎯 New campaign starting - Reset userChoseBackgroundMode to false');

    // Show proper progress overlay like the main form
    setIsGeneratingLocal(true); // Local state for AI Assistant UI
    if (setIsGenerating && setGenerationProgress) {
      setIsGenerating(true); // Parent state for progress modal
      setGenerationProgress({
        isVisible: true,
        currentStage: 'initializing',
        step: 0,
        total: 4,
        actualProgress: 0, // Start at 0%, backend will update immediately
        message: 'Starting AI campaign generation...',
        personaCount: 2,
        hasPersonas: true,
        startTime: Date.now()
      });
    }

    try {
      // Show progress message in chat
      const progressMessage = {
        role: 'assistant',
        content: '🚀 Generating your campaign... Please wait while I create amazing marketing content for you!',
        timestamp: new Date(),
        isGenerating: true
      };
      setMessages(prev => [...prev, progressMessage]);

      // Stage 1: Initializing - Don't update progress, let backend polling handle it
      await new Promise(resolve => setTimeout(resolve, 800)); // Brief initialization

      // Stage 2: Persona Generation - Don't update progress, let backend polling handle it
      // Removed manual progress updates - backend polling will handle all progress updates
      
      // Simulate persona generation time
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Stage 3: Content Generation - Don't update progress, let backend polling handle it
      // Removed manual progress updates - backend polling will handle all progress updates

      // Make the actual API call - this is where the real processing happens
      const response = await axios.post(
        `${API}/ai-assistant/generate-campaign`,
        { conversation_id: convId },
        {
          headers: {
            'Authorization': `Bearer ${sessionToken}`,
            'Content-Type': 'application/json'
          },
          timeout: 300000
        }
      );

      // Backend always uses background generation - no flag check needed
      // Just start polling for any campaign
      const campaignId = response.data.id;
      console.log('🎯 Starting campaign polling for:', campaignId);
      
      // Don't reset flag here - it was already reset at the start of generateCampaign()
      // If user clicked "Go to Dashboard", we must preserve that choice
      console.log('🎯 Current userChoseBackgroundMode:', window.userChoseBackgroundMode);
      
      // Add message to chat
      const backgroundMessage = {
        role: 'assistant',
        content: '🚀 Your campaign is being generated. You can stay here to watch the progress, or click "Go to Dashboard" to view it there.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, backgroundMessage]);
      
      // Poll for completion
        const pollInterval = setInterval(async () => {
          try {
            const statusResponse = await axios.get(
              `${API}/campaigns/${campaignId}/status`,
              {
                headers: { 'Authorization': `Bearer ${sessionToken}` }
              }
            );
            
            const status = statusResponse.data.status;
            const progress = statusResponse.data.progress || 0;
            
            console.log(`📊 Campaign ${campaignId} polling: status=${status}, progress=${progress}%`);
            
            // Update progress UI with proper step calculation
            if (setGenerationProgress) {
              // Map progress 0-100 to steps 1-4
              let progressStep = 1;
              let displayProgress = progress;
              
              if (progress >= 75) {
                progressStep = 4;
                displayProgress = progress; // Show actual backend progress
              } else if (progress >= 50) {
                progressStep = 3;
                displayProgress = progress;
              } else if (progress >= 25) {
                progressStep = 2;
                displayProgress = progress;
              } else {
                progressStep = 1;
                displayProgress = progress;
              }
              
              setGenerationProgress(prev => ({
                ...prev,
                step: progressStep,
                total: 4,
                actualProgress: displayProgress, // Add actual backend progress for sync
                message: status === 'generating' 
                  ? `🎨 Generating your ads... ${displayProgress}% complete`
                  : status === 'completed'
                  ? '✅ Campaign generation complete!'
                  : status === 'queued'
                  ? '🚀 Campaign queued for generation...'
                  : '⚙️ Processing...'
              }));
            }
            
            // If completed
            if (status === 'completed') {
              clearInterval(pollInterval);
              
              console.log('✅ Campaign status is COMPLETED');
              console.log('   User chose background mode:', window.userChoseBackgroundMode);
              console.log('   Fetching complete campaign data from:', `${API}/campaigns/${campaignId}`);
              
              // Fetch complete campaign data with retry (handles replica lag, auth blips, etc.)
              const fetchResult = await fetchCampaignWithRetry(campaignId, sessionToken);
              if (fetchResult.ok) {
                console.log('   Complete campaign fetched successfully (with retry)');
                onCampaignGenerated(fetchResult.campaign);
              } else {
                console.error('   fetchCampaignWithRetry failed:', fetchResult.reason);
                // Tell user via the chat history rather than silently rendering empty
                const errorMessage = {
                  role: 'assistant',
                  content: fetchResult.reason === 'campaign_failed'
                    ? '❌ Generation failed. Please try again or check the dashboard for details.'
                    : '⚠️ Could not load your campaign right away. It may still be ready in your dashboard — please refresh and check.',
                  timestamp: new Date()
                };
                setMessages(prev => [...prev, errorMessage]);
                // Still hand off whatever data we have so the UI can surface failure state
                if (fetchResult.campaign) {
                  onCampaignGenerated(fetchResult.campaign);
                }
              }
              
              const successMessage = {
                role: 'assistant',
                content: '🎉 Perfect! Your campaign is ready!',
                timestamp: new Date()
              };
              setMessages(prev => [...prev, successMessage]);
              
              // Handle modal closing based on user choice
              if (window.userChoseBackgroundMode === true) {
                // User went to dashboard - close modal after delay
                console.log('   User went to dashboard - closing modal');
                setTimeout(() => {
                  setIsGeneratingLocal(false);
                  if (setIsGenerating) {
                    setIsGenerating(false);
                    setGenerationProgress({ step: 0, total: 0, currentStage: '', isVisible: false });
                  }
                }, 1500);
              } else {
                // User stayed on modal - close modal and navigate to results
                console.log('   User stayed on modal - closing modal and navigating to results');
                setTimeout(() => {
                  setIsGeneratingLocal(false);
                  if (setIsGenerating) {
                    setIsGenerating(false);
                    setGenerationProgress({ step: 0, total: 0, currentStage: '', isVisible: false });
                  }
                }, 1500); // Brief delay to show "Complete!" message
              }
            }
          } catch (error) {
            console.error('Poll error:', error);
          }
        }, 5000);
        
        return; // Exit - polling handles rest

    } catch (error) {
      console.error('Campaign generation error:', error);
      
      // Check if it's a credit error (402 status)
      let errorContent = 'I encountered an issue while generating your campaign. Would you like to try again or provide additional details?';
      
      if (error.response && error.response.status === 402) {
        const errorData = error.response.data.detail || error.response.data;
        const requiredCredits = errorData.required_credits || 'unknown';
        const currentBalance = errorData.current_balance || 0;
        const shortfall = errorData.shortfall || requiredCredits;
        
        errorContent = `💳 **Insufficient Credits**\n\nYou need **${requiredCredits} credits** to generate this campaign, but you only have **${currentBalance} credits**.\n\nPlease add **${shortfall} more credits** to continue.\n\nYou can add credits from your Profile → Credits page.`;
      } else if (error.response && error.response.data) {
        // Show other API errors
        const errorMsg = error.response.data.detail || error.response.data.message || error.message;
        errorContent = `❌ **Error**: ${errorMsg}\n\nWould you like to try again?`;
      }
      
      const errorMessage = {
        role: 'assistant',
        content: errorContent,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
      
      // Close modal on error
      setIsGeneratingLocal(false);
      if (setIsGenerating) {
        setIsGenerating(false);
        setGenerationProgress({
          step: 0,
          total: 0,
          currentStage: '',
          isVisible: false
        });
      }
    }
    // NO FINALLY BLOCK - polling handles modal closure based on user choice
  };

  // Expose AI Assistant generation function for credit confirmation callback
  useEffect(() => {
    window.aiAssistantRef = {
      startGenerationAfterCreditConfirmation
    };
    return () => {
      delete window.aiAssistantRef;
    };
  }, []);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(); // Call without passing the event
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <div className="text-2xl mr-3">🤖</div>
              <div>
                <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                  AI Assistant
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {aiMode === 'agent' ? 'Interactive Campaign Agent - Guided campaign creation' : 'Ask Assistant Mode - Q&A and advice'}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <span className={`text-xs font-medium px-3 py-1 rounded-full flex items-center ${
                aiMode === 'agent' 
                  ? 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200'
                  : 'bg-emerald-100 dark:bg-emerald-900 text-emerald-800 dark:text-emerald-200'
              }`}>
                <span className="mr-1">{aiMode === 'agent' ? '🚀' : '💬'}</span>
                {aiMode === 'agent' ? 'Interactive Campaign Agent' : 'Ask Assistant'}
              </span>
              <span className="bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 text-xs font-medium px-2.5 py-0.5 rounded-full">
                AI Powered
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Chat Container - Always Show */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          
          {/* Chat Messages */}
          <div className="h-96 overflow-y-auto p-6 space-y-4">
            {/* Always show chat messages since we default to Agent Mode */}
                {messages && messages.length > 0 && messages.map((message, index) => {
              // Add validation to prevent undefined errors
              if (!message || typeof message !== 'object') {
                console.warn('Invalid message object at index', index, message);
                return null;
              }
              
              const messageRole = message.role || 'assistant';
              const messageContent = message.content || '';
              
              return (
                <div key={index}>
                  <div className={`flex ${messageRole === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-xs lg:max-w-md px-4 py-3 rounded-2xl ${
                      messageRole === 'user'
                        ? message.isOptionSelect
                          ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white'
                          : 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
                    }`}>
                      <div className="flex items-start">
                        {messageRole === 'assistant' && (
                          <div className="text-lg mr-2 mt-0.5">🤖</div>
                        )}
                        <div className="whitespace-pre-wrap text-sm leading-relaxed">
                          {messageContent}
                        </div>
                      </div>
                      <div className={`text-xs mt-2 opacity-70 ${
                        messageRole === 'user' ? 'text-blue-100' : 'text-gray-500 dark:text-gray-400'
                      }`}>
                        {message.timestamp && message.timestamp.toLocaleTimeString()}
                      </div>
                    </div>
                  </div>
                  
                  {/* Option Bubbles - Show for assistant messages with options */}
                  {messageRole === 'assistant' && message.options && message.options.length > 0 && (
                    <div className="mt-3 ml-8 space-y-2">
                      <div className="grid grid-cols-1 gap-2 max-w-md">
                        {message.options.map((option, optionIndex) => (
                          <button
                            key={optionIndex}
                            onClick={() => handleOptionSelect(option.value, option.text)}
                            disabled={isLoading || isGenerating}
                            className="text-left px-4 py-3 bg-white dark:bg-gray-600 text-gray-800 dark:text-white border-2 border-blue-200 dark:border-gray-500 rounded-xl hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-gray-500 transition-all duration-200 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md"
                          >
                            {option.text}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Action Buttons - Show for credit confirmation */}
                  {messageRole === 'assistant' && message.hasButtons && message.buttons && message.buttons.length > 0 && (
                    <div className="mt-3 ml-8 flex gap-3">
                      {message.buttons.map((button, btnIndex) => (
                        <button
                          key={btnIndex}
                          onClick={() => handleButtonClick(button.action, button.data)}
                          disabled={isLoading || isGenerating}
                          className={`px-6 py-2 rounded-lg font-medium transition-all duration-200 ${
                            button.action === 'proceed_generation'
                              ? 'bg-green-600 hover:bg-green-700 text-white'
                              : 'bg-gray-300 hover:bg-gray-400 text-gray-800'
                          } disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                          {button.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            
            {/* Current Options - Show latest options if no message-specific options */}
            {!isLoading && !isGenerating && currentOptions.length > 0 && 
             !messages[messages.length - 1]?.options?.length && (
              <div className="space-y-2">
                <div className="text-sm text-gray-500 dark:text-gray-400 ml-8">Choose an option:</div>
                <div className="ml-8 grid grid-cols-1 gap-2 max-w-md">
                  {currentOptions.map((option, optionIndex) => (
                    <button
                      key={optionIndex}
                      onClick={() => handleOptionSelect(option.value, option.text)}
                      className="text-left px-4 py-3 bg-white dark:bg-gray-600 text-gray-800 dark:text-white border-2 border-blue-200 dark:border-gray-500 rounded-xl hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-gray-500 transition-all duration-200 text-sm font-medium shadow-sm hover:shadow-md"
                    >
                      {option.text}
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-gray-100 dark:bg-gray-700 px-4 py-3 rounded-2xl">
                  <div className="flex items-center">
                    <div className="text-lg mr-2">🤖</div>
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-gray-500 rounded-full animate-pulse"></div>
                      <div className="w-2 h-2 bg-gray-500 rounded-full animate-pulse" style={{animationDelay: '0.1s'}}></div>
                      <div className="w-2 h-2 bg-gray-500 rounded-full animate-pulse" style={{animationDelay: '0.2s'}}></div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Current Options - Show latest options if no message-specific options */}
            {!isLoading && !isGenerating && currentOptions.length > 0 && 
             !messages[messages.length - 1]?.options?.length && (
              <div className="space-y-2">
                <div className="text-sm text-gray-500 dark:text-gray-400 ml-8">Choose an option:</div>
                <div className="ml-8 grid grid-cols-1 gap-2 max-w-md">
                  {currentOptions.map((option, optionIndex) => (
                    <button
                      key={optionIndex}
                      onClick={() => handleOptionSelect(option.value, option.text)}
                      className="text-left px-4 py-3 bg-white dark:bg-gray-600 text-gray-800 dark:text-white border-2 border-blue-200 dark:border-gray-500 rounded-xl hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-gray-500 transition-all duration-200 text-sm font-medium shadow-sm hover:shadow-md"
                    >
                      {option.text}
                    </button>
                  ))}
                </div>
              </div>
            )}

          </div>

          {/* Modern Message Input Area */}
          <div className="border-t border-gray-200 dark:border-gray-700 p-4">
            
            {/* Image Preview Area */}
            {imagePreview && (
              <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    <img 
                      src={imagePreview} 
                      alt="Product preview" 
                      className="w-16 h-16 object-cover rounded-lg border border-gray-300 dark:border-gray-600"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Product Image Attached</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {imageFile ? `${imageFile.name} (${(imageFile.size / 1024 / 1024).toFixed(1)}MB) - Image will be used in campaign generation` : 'Image will be used in campaign generation'}
                    </p>
                  </div>
                  <button
                    onClick={removeImage}
                    className="flex-shrink-0 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                    title="Remove image"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            )}

            {/* Input Row */}
            <div className="flex items-end space-x-3">
              
              {/* Text Input */}
              <div className="flex-1 relative">
                <textarea
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Type your message here..."
                  className="w-full px-4 py-3 pr-12 border border-gray-300 dark:border-gray-600 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white resize-none shadow-sm"
                  rows={2}
                  disabled={isLoading || isGenerating}
                />
                
                {/* Image Upload Button Inside Input */}
                <label 
                  className="absolute right-3 bottom-3 cursor-pointer p-1.5 text-gray-400 hover:text-blue-500 dark:hover:text-blue-400 transition-colors"
                  title="Upload product image"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                  </svg>
                  <input 
                    id="image-upload-input"
                    type="file" 
                    accept="image/*" 
                    onChange={handleImageUpload}
                    className="hidden"
                    disabled={isLoading || isGenerating}
                  />
                </label>
              </div>
              
              {/* Send Button */}
              <button
                onClick={() => handleSendMessage()}
                disabled={!inputMessage.trim() || isLoading || isGenerating}
                className="p-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-2xl hover:from-blue-600 hover:to-indigo-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md"
                title="Send message"
              >
                {isLoading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                )}
              </button>
            </div>
          
          {/* Modern Mode Toggle Section */}
          <div className="border-t border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 px-4 py-3">
            <div className="flex justify-center space-x-2">
                <button
                  onClick={() => {
                    if (aiMode !== 'agent') {
                      setAiMode('agent');
                      setMessages([]);
                      setConversationId(null);
                      setCurrentOptions([]);
                      // Clear image when switching modes
                      removeImage();
                      // Initialize agent mode conversation
                      const initializeAgentMode = async () => {
                        try {
                          const response = await axios.post(
                            `${API}/ai-assistant/chat`,
                            {
                              message: "I want to create a marketing campaign",
                              conversation_id: null
                            },
                            {
                              headers: {
                                'Authorization': `Bearer ${sessionToken}`,
                                'Content-Type': 'application/json'
                              },
                              timeout: 30000
                            }
                          );

                          const initialMessage = {
                            role: 'assistant',
                            content: response.data.message || response.data.response,
                            timestamp: new Date(),
                            options: response.data.options || []
                          };

                          setMessages([initialMessage]);
                          setConversationId(response.data.conversation_id);
                          setCurrentOptions(response.data.options || []);
                        } catch (error) {
                          console.error('Error initializing Agent Mode:', error);
                        }
                      };
                      initializeAgentMode();
                    }
                  }}
                  className={`group flex items-center px-6 py-3 rounded-xl transition-all duration-300 font-semibold text-sm transform hover:scale-105 hover:shadow-xl ${
                    aiMode === 'agent'
                      ? 'bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-600 text-white shadow-xl shadow-blue-500/30 ring-2 ring-blue-300 ring-opacity-60 hover:shadow-2xl hover:shadow-blue-500/40'
                      : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-2 border-gray-200 dark:border-gray-600 hover:border-blue-400 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 dark:hover:bg-gray-700 shadow-lg hover:shadow-xl'
                  }`}
                >
                  <span className={`mr-2 text-lg transition-transform duration-300 group-hover:rotate-12 ${
                    aiMode === 'agent' ? 'animate-pulse' : ''
                  }`}>
                    🚀
                  </span>
                  <span>Interactive Campaign Agent</span>
                </button>
                <button
                  onClick={() => {
                    if (aiMode !== 'ask') {
                      setAiMode('ask');
                      setMessages([{
                        role: 'assistant',
                        content: 'Hi! I\'m your general Q&A assistant. Ask me anything about marketing, business strategies, or any other topic!',
                        timestamp: new Date(),
                        options: []
                      }]);
                      setConversationId(null);
                      setCurrentOptions([]);
                      // Clear image when switching modes
                      removeImage();
                    }
                  }}
                  className={`group flex items-center px-6 py-3 rounded-xl transition-all duration-300 font-semibold text-sm transform hover:scale-105 hover:shadow-xl ${
                    aiMode === 'ask'
                      ? 'bg-gradient-to-r from-emerald-500 via-green-500 to-teal-600 text-white shadow-xl shadow-emerald-500/30 ring-2 ring-emerald-300 ring-opacity-60 hover:shadow-2xl hover:shadow-emerald-500/40'
                      : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-2 border-gray-200 dark:border-gray-600 hover:border-emerald-400 hover:bg-gradient-to-r hover:from-emerald-50 hover:to-green-50 dark:hover:bg-gray-700 shadow-lg hover:shadow-xl'
                  }`}
                >
                  <span className={`mr-2 text-lg transition-transform duration-300 group-hover:bounce ${
                    aiMode === 'ask' ? 'animate-pulse' : ''
                  }`}>
                    💬
                  </span>
                  <span>Ask Assistant</span>
                </button>
              </div>
              
              {/* Mode Description */}
              <div className="text-center">
                <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                  {aiMode === 'agent' 
                    ? '🎯 Interactive Campaign Agent: Guided step-by-step campaign creation with AI assistance and conversation flow'
                    : '💡 Ask Assistant: General Q&A for marketing advice, business strategies, and expert insights'
                  }
                </p>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

// One-Click Content Studio Component
const OneClickContentStudio = ({ onCreateContent, user, isGenerating, setIsGenerating, generationProgress, setGenerationProgress, onNavigate, setCreditPreviewData, setShowCreditPreview, setPendingGenerationData }) => {
  const { colorTheme } = useColorTheme();
  const { sessionToken } = useAuth(); // Get session token for API calls
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showQuickForm, setShowQuickForm] = useState(false);

  // Content templates organized by categories
  const contentCategories = {
    'Social Media': [
      {
        id: 'facebook_ad',
        title: 'Facebook Ad',
        description: 'Create engaging Facebook ad copy with targeted messaging',
        icon: '📘',
        color: 'from-blue-500 to-blue-600',
        features: ['Audience targeting', 'Call-to-action optimization', 'A/B test ready']
      },
      {
        id: 'instagram_ad',
        title: 'Instagram Post',
        description: 'Design captivating Instagram content with visual appeal',
        icon: '📸',
        color: 'from-pink-500 to-purple-600',
        features: ['Hashtag optimization', 'Visual storytelling', 'Engagement focused']
      },
      {
        id: 'linkedin_ad',
        title: 'LinkedIn Ad',
        description: 'Professional networking content for B2B marketing',
        icon: '💼',
        color: 'from-blue-600 to-blue-700',
        features: ['Professional tone', 'B2B targeting', 'Lead generation']
      },
      {
        id: 'tiktok_ad',
        title: 'TikTok Content',
        description: 'Viral-ready content for younger demographics',
        icon: '🎵',
        color: 'from-red-500 to-pink-500',
        features: ['Trend-aware', 'Youth appeal', 'Video-first approach']
      },
      {
        id: 'telegram_ad',
        title: 'Telegram Channel',
        description: 'Community-focused messaging for engaged audiences',
        icon: '✈️',
        color: 'from-blue-400 to-cyan-500',
        features: ['Community building', 'Direct messaging', 'Engagement driven']
      },
      {
        id: 'whatsapp_ad',
        title: 'WhatsApp Message',
        description: 'Personal, direct messaging for customer engagement',
        icon: '💬',
        color: 'from-green-400 to-green-600',
        features: ['Personal touch', 'Direct communication', 'High engagement rate']
      }
    ],
    'Advertising': [
      {
        id: 'google_ads',
        title: 'Google Ads',
        description: 'Search-optimized ad copy for maximum visibility',
        icon: '🔍',
        color: 'from-green-500 to-blue-500',
        features: ['Keyword optimization', 'High conversion', 'Search intent']
      },
      {
        id: 'image_promo',
        title: 'Image Promotion',
        description: 'Visual promotional content with AI-generated images',
        icon: '🎨',
        color: 'from-purple-500 to-pink-500',
        features: ['AI imagery', 'Brand consistency', 'Visual impact']
      },
      {
        id: 'video_promo',
        title: 'Video Promotion',
        description: 'Engaging video content with scripts and visuals',
        icon: '🎬',
        color: 'from-red-600 to-orange-500',
        features: ['Video scripts', 'Visual storytelling', 'Multi-platform']
      }
    ],
    'Business': [
      {
        id: 'product_description',
        title: 'Product Description',
        description: 'Compelling product descriptions that convert browsers to buyers',
        icon: '📦',
        color: 'from-orange-500 to-red-500',
        features: ['SEO optimized', 'Conversion focused', 'Feature highlighting']
      },
      {
        id: 'email_marketing',
        title: 'Email Marketing',
        description: 'Persuasive email campaigns for customer engagement',
        icon: '📧',
        color: 'from-indigo-500 to-purple-500',
        features: ['Personalization', 'A/B testing', 'Open rate optimization']
      }
    ]
  };

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setShowQuickForm(true);
  };

  const handleQuickCreate = (productData) => {
    // Create the ad generation request with the selected template
    const adGenerationData = {
      ...productData,
      ad_types: [selectedTemplate.id] // Only generate the selected type
    };
    
    onCreateContent(adGenerationData);
    setShowQuickForm(false);
    setSelectedTemplate(null);
  };

  if (showQuickForm && selectedTemplate) {
    return (
      <>
        <QuickContentForm 
          template={selectedTemplate}
          onSubmit={handleQuickCreate}
          onCancel={() => {
            setShowQuickForm(false);
            setSelectedTemplate(null);
          }}
          isGenerating={isGenerating}
          setIsGenerating={setIsGenerating}
          generationProgress={generationProgress}
          setGenerationProgress={setGenerationProgress}
          onNavigate={onNavigate}
          sessionToken={sessionToken}
          setCreditPreviewData={setCreditPreviewData}
          setShowCreditPreview={setShowCreditPreview}
          setPendingGenerationData={setPendingGenerationData}
        />
        
        {/* Generating Modal - Same as AI Assistant and ProductForm */}
        {isGenerating && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl">
              <div className="text-center">
                <div className={`animate-spin rounded-full h-16 w-16 border-4 ${colorTheme === 'purple' ? 'border-purple-200 border-t-purple-600' : colorTheme === 'blue' ? 'border-blue-200 border-t-blue-600' : 'border-green-200 border-t-green-600'} mx-auto mb-6`}></div>
                
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                  🎨 Generating {selectedTemplate.title}
                </h3>
                
                <div className="mb-6">
                  <p className="text-gray-600 dark:text-gray-300 mb-3 font-medium text-base">
                    {generationProgress.message || 'AI is creating your content...'}
                  </p>
                  
                  {generationProgress.total > 0 && (
                    <div className="w-full">
                      {/* Progress Stats */}
                      <div className="flex justify-between text-sm mb-3 text-gray-500 dark:text-gray-400">
                        <span className="font-semibold text-gray-700 dark:text-gray-300">
                          {generationProgress.hasPersonas && generationProgress.personaCount > 1
                            ? generationProgress.message || `🎨 Generating content...`
                            : `${generationProgress.step} of ${generationProgress.total} pieces`
                          }
                        </span>
                        {generationProgress.startTime && (
                          <span className={`${colorTheme === 'purple' ? 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200' : colorTheme === 'blue' ? 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200' : 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'} px-3 py-1 rounded-full text-xs font-bold`}>
                            {Math.floor((Date.now() - generationProgress.startTime) / 1000)}s
                          </span>
                        )}
                      </div>
                      
                      {/* Progress Bar */}
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-4 mb-4 shadow-inner">
                        <div 
                          className={`bg-gradient-to-r ${colorTheme === 'purple' ? 'from-purple-500 to-pink-500' : colorTheme === 'blue' ? 'from-blue-500 to-sky-500' : 'from-green-500 to-emerald-500'} h-4 rounded-full transition-all duration-1000 ease-out shadow-lg flex items-center justify-end pr-3`}
                          style={{ width: `${generationProgress.actualProgress || Math.min(95, (generationProgress.step / generationProgress.total) * 100)}%` }}
                        >
                          {(generationProgress.actualProgress > 0 || generationProgress.step > 0) && (
                            <span className="text-white text-xs font-bold">
                              {generationProgress.actualProgress || Math.round((generationProgress.step / generationProgress.total) * 100)}%
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Stage Indicators */}
                      <div className="flex items-center justify-center space-x-6 mb-4">
                        <div className={`flex flex-col items-center space-y-1 ${generationProgress.currentStage === 'initializing' ? (colorTheme === 'purple' ? 'text-purple-600' : colorTheme === 'blue' ? 'text-blue-600' : 'text-green-600') + ' font-bold' : 'text-gray-400'}`}>
                          <div className={`w-3 h-3 rounded-full ${['initializing', 'persona_generation', 'content_generation', 'finalizing', 'complete'].includes(generationProgress.currentStage) ? (colorTheme === 'purple' ? 'bg-purple-500 ring-2 ring-purple-300' : colorTheme === 'blue' ? 'bg-blue-500 ring-2 ring-blue-300' : 'bg-green-500 ring-2 ring-green-300') : 'bg-gray-300'}`}></div>
                          <span className="text-xs">Initialize</span>
                        </div>
                        
                        {generationProgress.hasPersonas && (
                          <div className={`flex flex-col items-center space-y-1 ${generationProgress.currentStage === 'persona_generation' ? (colorTheme === 'purple' ? 'text-purple-600' : colorTheme === 'blue' ? 'text-blue-600' : 'text-green-600') + ' font-bold' : 'text-gray-400'}`}>
                            <div className={`w-3 h-3 rounded-full ${['persona_generation', 'content_generation', 'finalizing', 'complete'].includes(generationProgress.currentStage) ? (colorTheme === 'purple' ? 'bg-purple-500 ring-2 ring-purple-300' : colorTheme === 'blue' ? 'bg-blue-500 ring-2 ring-blue-300' : 'bg-green-500 ring-2 ring-green-300') : 'bg-gray-300'}`}></div>
                            <span className="text-xs">Personas</span>
                          </div>
                        )}
                        
                        <div className={`flex flex-col items-center space-y-1 ${generationProgress.currentStage === 'content_generation' ? (colorTheme === 'purple' ? 'text-purple-600' : colorTheme === 'blue' ? 'text-blue-600' : 'text-green-600') + ' font-bold' : 'text-gray-400'}`}>
                          <div className={`w-3 h-3 rounded-full ${['content_generation', 'finalizing', 'complete'].includes(generationProgress.currentStage) ? (colorTheme === 'purple' ? 'bg-purple-500 ring-2 ring-purple-300' : colorTheme === 'blue' ? 'bg-blue-500 ring-2 ring-blue-300' : 'bg-green-500 ring-2 ring-green-300') : 'bg-gray-300'}`}></div>
                          <span className="text-xs">Generate</span>
                        </div>
                        
                        <div className={`flex flex-col items-center space-y-1 ${['finalizing', 'complete'].includes(generationProgress.currentStage) ? (colorTheme === 'purple' ? 'text-purple-600' : colorTheme === 'blue' ? 'text-blue-600' : 'text-green-600') + ' font-bold' : 'text-gray-400'}`}>
                          <div className={`w-3 h-3 rounded-full ${['finalizing', 'complete'].includes(generationProgress.currentStage) ? (colorTheme === 'purple' ? 'bg-purple-500 ring-2 ring-purple-300' : colorTheme === 'blue' ? 'bg-blue-500 ring-2 ring-blue-300' : 'bg-green-500 ring-2 ring-green-300') : 'bg-gray-300'}`}></div>
                          <span className="text-xs">Finalize</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Action Buttons */}
                <div className="space-y-3">
                  <button
                    onClick={() => {
                      console.log('🚀 QuickContentStudio: User clicked "Go to Dashboard"');
                      window.userChoseBackgroundMode = true;
                      setIsGenerating(false);
                      if (onNavigate) {
                        onNavigate('dashboard');
                      }
                    }}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    Continue in Background & Go to Dashboard
                  </button>
                  
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Your content will continue generating. Check the dashboard or notifications for completion.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <div className={`min-h-screen bg-gradient-to-br ${colorTheme === 'purple' ? 'from-purple-50 via-white to-pink-50' : colorTheme === 'blue' ? 'from-blue-50 via-white to-sky-50' : 'from-green-50 via-white to-emerald-50'} dark:from-gray-900 dark:via-gray-800 dark:to-gray-900`}>
      {/* Header */}
      <div className={`bg-white dark:bg-gray-800 shadow-lg border-b ${colorTheme === 'purple' ? 'border-purple-200' : colorTheme === 'blue' ? 'border-blue-200' : 'border-green-200'} dark:border-gray-700`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h1 className="text-4xl font-bold mb-4">
              <span className="mr-2">🎯</span>
              <span className={`bg-gradient-to-r ${colorTheme === 'purple' ? 'from-purple-600 to-pink-600' : colorTheme === 'blue' ? 'from-blue-600 to-sky-500' : 'from-green-600 to-emerald-600'} bg-clip-text text-transparent`}>One-Click Content Studio</span>
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Choose your content type and let AI create compelling marketing materials in seconds. 
              No complex forms, just instant professional results.
            </p>
          </div>
        </div>
      </div>

      {/* Content Categories */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {Object.entries(contentCategories).map(([category, templates]) => (
          <div key={category} className="mb-16">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                {category}
              </h2>
              <div className={`w-24 h-1 bg-gradient-to-r ${colorTheme === 'purple' ? 'from-purple-500 to-pink-500' : 'from-green-500 to-emerald-500'} mx-auto rounded-full`}></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {templates.map((template) => (
                <div
                  key={template.id}
                  className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 cursor-pointer border border-gray-100 dark:border-gray-700 group"
                  onClick={() => handleTemplateSelect(template)}
                >
                  {/* Template Header */}
                  <div className={`bg-gradient-to-r ${template.color} p-6 rounded-t-2xl`}>
                    <div className="text-center">
                      <div className="text-4xl mb-3">{template.icon}</div>
                      <h3 className="text-xl font-bold text-white mb-2">
                        {template.title}
                      </h3>
                      <p className="text-white/90 text-sm">
                        {template.description}
                      </p>
                    </div>
                  </div>

                  {/* Template Body */}
                  <div className="p-6">
                    <div className="space-y-3 mb-6">
                      {template.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center text-gray-600 dark:text-gray-300">
                          <div className={`w-2 h-2 ${colorTheme === 'purple' ? 'bg-purple-500' : colorTheme === 'blue' ? 'bg-blue-500' : 'bg-green-500'} rounded-full mr-3`}></div>
                          <span className="text-sm">{feature}</span>
                        </div>
                      ))}
                    </div>

                    <button
                      className={`w-full bg-gradient-to-r ${colorTheme === 'purple' ? 'from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700' : colorTheme === 'blue' ? 'from-blue-600 to-sky-500 hover:from-blue-700 hover:to-sky-600' : 'from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700'} text-white font-bold py-3 px-6 rounded-lg transition-all duration-300 transform group-hover:scale-105`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleTemplateSelect(template);
                      }}
                    >
                      Create {template.title}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Bottom CTA */}
      <div className={`bg-gradient-to-r ${colorTheme === 'purple' ? 'from-purple-500 to-pink-500' : 'from-green-500 to-emerald-500'} py-16`}>
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white mb-4">
            Need More Customization?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Use our Ad Creator Hub for persona-driven content generation with multiple ad types
          </p>
          <button
            onClick={() => onCreateContent('advanced-form')}
            className={`bg-white ${colorTheme === 'purple' ? 'text-purple-600' : colorTheme === 'blue' ? 'text-blue-600' : 'text-green-600'} hover:bg-gray-100 font-bold py-3 px-8 rounded-lg transition-colors shadow-lg hover:shadow-xl`}
          >
            Advanced Content Creator
          </button>
        </div>
      </div>
    </div>
  );
};

// Quick Content Form Component
const QuickContentForm = ({ template, onSubmit, onCancel, isGenerating, setIsGenerating, generationProgress, setGenerationProgress, onNavigate, sessionToken, setCreditPreviewData, setShowCreditPreview, setPendingGenerationData }) => {
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const { colorTheme } = useColorTheme();
  const [formData, setFormData] = useState({
    product_name: '',
    product_description: '',
    key_benefits: '',
    brand_voice: 'professional',
    price: '',
    category: '',
    product_image: '',
    product_images: [], // Multiple images for video transition mode
    avatar_image_url: '', // User-uploaded portrait/avatar for hero placement in generated ads
    age_range_min: 25,
    age_range_max: 55,
    gender: 'All',
    geography: '',
    video_duration: 10,
    video_resolution: '720p',
    video_aspect_ratio: '16:9',
    video_model: 'gemini_omni_video', // Ultra Pro 🪐 (Default - Recommended)
    video_platform_preset: null, // Set by VideoPlatformPicker (5-tile picker)
    video_generation_mode: 'text', // text, image_reference, image_transition, multi_reference
    render_text_in_video: false, // OFF by default — Veo 3 often mis-spells brand names
    image_model: 'freepik', // Best Value (Recommended)
    nano_banana_resolution: '4K', // Default resolution for Nano Banana Pro
    generate_localized_images: false, // When ON, generates 1 image per language (Ultra Image only)
    // Text overlay options
    product_tagline: '',  // Optional tagline for branding
    text_overlay_method: 'ai-text',  // 'overlay' (post-processing) or 'ai-text' (AI-generated)
    // Persona-driven optimization fields
    industry_business_type: '',
    budget_range: '',
    interests_behaviors: '',
    persona_count: 'ai_determined',
    enable_personas: false,  // Start with false, users can toggle on if needed
    // Website URL for auto-fill
    website_url: '',
    // Language settings
    languages: ['en'],
    video_languages: ['en'],
    // Strict Reference Mode — see primary form docs
    strict_reference_mode: false,
    // Voice narration overlay — see primary form docs
    add_voice_narration: true,
    // Cloned voice — see primary form docs
    cloned_voice_id: ''
  });
  
  // Geography and Language state (sync with Ad Creator Hub)
  const [selectedGeographies, setSelectedGeographies] = useState([]);
  const [selectedLanguages, setSelectedLanguages] = useState(['en']);
  
  // State for website extraction
  const [isExtractingWebsite, setIsExtractingWebsite] = useState(false);
  const [websiteExtractionError, setWebsiteExtractionError] = useState('');
  const [websiteExtractionSuccess, setWebsiteExtractionSuccess] = useState(false);
  // Product catalog scraped from the website URL. See primary form for full docs.
  const [extractedProducts, setExtractedProducts] = useState([]);
  const [selectedExtractedProductIdx, setSelectedExtractedProductIdx] = useState(null);
  // Founder / team avatars — see primary form for full docs.
  const [extractedAvatars, setExtractedAvatars] = useState([]);
  const [selectedExtractedAvatarIdxs, setSelectedExtractedAvatarIdxs] = useState([]);

  // Sync geography and language with formData
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      geography: selectedGeographies.length > 0 ? selectedGeographies[0] : '',
      languages: selectedLanguages,
      video_languages: selectedLanguages
    }));
  }, [selectedGeographies, selectedLanguages]);

  // Close credit preview modal when cost-affecting fields change
  useEffect(() => {
    if (setShowCreditPreview) {
      setShowCreditPreview(false);
    }
  }, [formData.video_model, formData.video_resolution, formData.video_duration, formData.image_model, formData.nano_banana_resolution, formData.enable_personas, formData.persona_count, selectedLanguages, setShowCreditPreview]);

  // Currency mapping for different countries (same as main form)
  const getCurrencyAndBudgetRanges = (geography) => {
    const currencyMap = {
      'United States': {
        currency: 'USD',
        symbol: '$',
        ranges: [
          'Under $100',
          '$100-$500',
          '$500-$1,000',
          '$1,000-$5,000',
          '$5,000-$10,000',
          '$10,000+',
          'Enterprise'
        ]
      },
      'United Kingdom': {
        currency: 'GBP',
        symbol: '£',
        ranges: [
          'Under £80',
          '£80-£400',
          '£400-£800',
          '£800-£4,000',
          '£4,000-£8,000',
          '£8,000+',
          'Enterprise'
        ]
      },
      'India': {
        currency: 'INR',
        symbol: '₹',
        ranges: [
          'Under ₹8,000',
          '₹8,000-₹40,000',
          '₹40,000-₹80,000',
          '₹80,000-₹4,00,000',
          '₹4,00,000-₹8,00,000',
          '₹8,00,000+',
          'Enterprise'
        ]
      },
      'Australia': {
        currency: 'AUD',
        symbol: 'A$',
        ranges: [
          'Under A$150',
          'A$150-A$750',
          'A$750-A$1,500',
          'A$1,500-A$7,500',
          'A$7,500-A$15,000',
          'A$15,000+',
          'Enterprise'
        ]
      },
      'Singapore': {
        currency: 'SGD',
        symbol: 'S$',
        ranges: [
          'Under S$140',
          'S$140-S$700',
          'S$700-S$1,400',
          'S$1,400-S$7,000',
          'S$7,000-S$14,000',
          'S$14,000+',
          'Enterprise'
        ]
      },
      'Canada': {
        currency: 'CAD',
        symbol: 'C$',
        ranges: [
          'Under C$130',
          'C$130-C$650',
          'C$650-C$1,300',
          'C$1,300-C$6,500',
          'C$6,500-C$13,000',
          'C$13,000+',
          'Enterprise'
        ]
      },
      'Germany': {
        currency: 'EUR',
        symbol: '€',
        ranges: [
          'Under €90',
          '€90-€450',
          '€450-€900',
          '€900-€4,500',
          '€4,500-€9,000',
          '€9,000+',
          'Enterprise'
        ]
      },
      'France': {
        currency: 'EUR',
        symbol: '€',
        ranges: [
          'Under €90',
          '€90-€450',
          '€450-€900',
          '€900-€4,500',
          '€4,500-€9,000',
          '€9,000+',
          'Enterprise'
        ]
      }
    };

    return currencyMap[geography] || {
      currency: 'USD',
      symbol: '$',
      ranges: [
        'Under $100',
        '$100-$500',
        '$500-$1,000',
        '$1,000-$5,000',
        '$5,000-$10,000',
        '$10,000+',
        'Enterprise'
      ]
    };
  };

  const currentBudgetOptions = getCurrencyAndBudgetRanges(formData.geography);

  // Clear budget range when geography changes (currency change)
  useEffect(() => {
    if (formData.geography && formData.budget_range) {
      const currentOptions = getCurrencyAndBudgetRanges(formData.geography);
      if (!currentOptions.ranges.includes(formData.budget_range)) {
        setFormData(prev => ({ ...prev, budget_range: '' }));
      }
    }
  }, [formData.geography]);

  const [imagePreview, setImagePreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  
  // Store reference to proceed function for credit preview callback
  const proceedFunctionRef = useRef(null);
  
  // Expose the proceed function to global scope for credit preview modal
  useEffect(() => {
    window.oneClickStudioProceedWithGeneration = async () => {
      console.log('💰 Global proceed function called from credit preview modal');
      if (proceedFunctionRef.current) {
        await proceedFunctionRef.current();
      }
    };
    
    return () => {
      delete window.oneClickStudioProceedWithGeneration;
      delete window.oneClickStudioPendingData;
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // When video model changes, adjust resolution to valid value for that model
    if (name === 'video_model') {
      let newResolution = formData.video_resolution;
      if (value === 'ultra_video' && formData.video_resolution === '4K') {
        newResolution = '1080p'; // Downgrade from 4K to 1080p for Sora 2
      } else if (value === 'freepik' && (formData.video_resolution === '4K' || formData.video_resolution === '1080p')) {
        newResolution = '720p'; // Freepik only supports 720p
      }
      setFormData(prev => ({
        ...prev,
        [name]: value,
        video_resolution: newResolution
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Handle website URL extraction
  const handleExtractWebsite = async () => {
    const url = formData.website_url?.trim();
    if (!url) {
      setWebsiteExtractionError('Please enter a website URL');
      return;
    }
    
    setIsExtractingWebsite(true);
    setWebsiteExtractionError('');
    
    try {
      const response = await axios.post(`${API}/extract-website-content`, {
        url: url
      }, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      
      if (response.data.success && response.data.data) {
        const extracted = response.data.data;
        const detectedLogo = extracted.logo_url || extracted.logo_absolute_url || '';
        
        // Auto-fill form with extracted data
        setFormData(prev => ({
          ...prev,
          product_name: extracted.product_name || prev.product_name,
          product_description: extracted.product_description || prev.product_description,
          key_benefits: extracted.key_benefits || prev.key_benefits,
          brand_voice: extracted.brand_voice || prev.brand_voice,
          price: extracted.price || prev.price,
          category: extracted.category || prev.category,
          product_tagline: extracted.tagline || prev.product_tagline,
          campaign_objective: extracted.campaign_objective || prev.campaign_objective,
          primary_platform: extracted.primary_platform || prev.primary_platform,
          cta_goal: extracted.cta_goal || prev.cta_goal,
          offer_incentive: extracted.offer_incentive || prev.offer_incentive,
          pain_point: extracted.pain_point || prev.pain_point,
          landing_page_url: extracted.landing_page_url || prev.landing_page_url,
          brand_logo_url: detectedLogo || prev.brand_logo_url,
          image_model: (detectedLogo && !['gpt4o-image', 'nano-banana-pro', 'nano-banana-2'].includes(prev.image_model)) ? 'gpt4o-image' : prev.image_model
        }));

        // Persist the scraped product catalog for the picker UI. Auto-enable
        // strict reference mode when the site returned a real product list.
        const scrapedProducts = Array.isArray(extracted.products) ? extracted.products : [];
        setExtractedProducts(scrapedProducts);
        setSelectedExtractedProductIdx(null);
        if (scrapedProducts.length > 0) {
          setFormData(prev => ({ ...prev, strict_reference_mode: true }));
        }

        // Persist founder / team avatars for opt-in selection. Auto-select
        // the first one so it lands in avatar_image_url(s) immediately.
        const scrapedAvatars = Array.isArray(extracted.avatars) ? extracted.avatars : [];
        setExtractedAvatars(scrapedAvatars);
        if (scrapedAvatars.length > 0 && scrapedAvatars[0].image_url) {
          setSelectedExtractedAvatarIdxs([0]);
          setFormData(prev => ({
            ...prev,
            avatar_image_url: scrapedAvatars[0].image_url,
            avatar_image_urls: [scrapedAvatars[0].image_url],
            strict_reference_mode: true,
          }));
        } else {
          setSelectedExtractedAvatarIdxs([]);
        }
        
        setWebsiteExtractionError('');
        // Show celebration popup
        setWebsiteExtractionSuccess(true);
      } else {
        setWebsiteExtractionError(response.data.error || 'Failed to extract website content');
      }
    } catch (error) {
      console.error('Website extraction error:', error);
      setWebsiteExtractionError(error.response?.data?.detail || 'Failed to extract website content. Please try again.');
    } finally {
      setIsExtractingWebsite(false);
    }
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    // Support up to 2 images for transition mode
    const filesToProcess = files.slice(0, 2);
    const imagePromises = filesToProcess.map(file => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(file);
      });
    });
    
    Promise.all(imagePromises).then(base64Images => {
      setFormData({
        ...formData,
        product_image: base64Images[0], // Backward compatibility
        product_images: base64Images
      });
      setImagePreview(base64Images);
    });
  };

  const incomeRanges = [
    '$0 - $25,000',
    '$25,001 - $50,000',
    '$50,001 - $75,000',
    '$75,001 - $100,000',
    '$100,001 - $150,000',
    '$150,001+'
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent multiple submissions
    if (isSubmitting) {
      return;
    }
    
    setError('');
    
    // ===============================================
    // 💰 PHASE 4: CREDIT PREVIEW (LIKE AD CREATOR HUB)
    // Show credit preview modal BEFORE generation
    // NOTE: Don't set isSubmitting=true yet to avoid loading modal
    // ===============================================
    try {
      // Calculate persona count for credit calculation
      const hasPersonas = formData.enable_personas;
      const personaCount = hasPersonas ? (formData.persona_count === 'ai_determined' ? 3 : parseInt(formData.persona_count)) : 1;
      
      // Strip large base64 payloads before preview (cost preview doesn't need pixels)
      const lightweightFormData = { ...formData };
      if (lightweightFormData.product_image) {
        lightweightFormData.product_image = lightweightFormData.product_image.substring(0, 100);
      }
      if (lightweightFormData.product_images && lightweightFormData.product_images.length > 0) {
        lightweightFormData.product_images = lightweightFormData.product_images.map(img =>
          img ? img.substring(0, 100) : img
        );
      }
      if (lightweightFormData.avatar_image_url && lightweightFormData.avatar_image_url.startsWith('data:')) {
        lightweightFormData.avatar_image_url = lightweightFormData.avatar_image_url.substring(0, 100);
      }
      // Multi-avatar array — same treatment. A 12MB base64 avatar in
      // `avatar_image_urls[0]` here would blow the 10s mobile timeout.
      if (Array.isArray(lightweightFormData.avatar_image_urls)) {
        lightweightFormData.avatar_image_urls = lightweightFormData.avatar_image_urls.map(
          (img) => (img && img.startsWith('data:') ? img.substring(0, 100) : img)
        );
      }
      // Brand logo — same treatment (may also be base64).
      if (lightweightFormData.brand_logo_url && lightweightFormData.brand_logo_url.startsWith('data:')) {
        lightweightFormData.brand_logo_url = lightweightFormData.brand_logo_url.substring(0, 100);
      }
      
      // Get credit preview from backend
      const previewResponse = await axios.post(`${API}/preview-credit-cost`, {
        product: {
          ...lightweightFormData,
          enable_personas: formData.enable_personas,
          persona_count: formData.persona_count || '1',
          image_model: formData.image_model || 'nebius',
          nano_banana_resolution: formData.nano_banana_resolution || '4K'
        },
        ad_types: [template.id]
      }, {
        headers: {
          'Authorization': `Bearer ${sessionToken}`
        }
      });
      
      // DIRECT STATE SETTERS APPROACH (like Ad Creator Hub)
      if (!setCreditPreviewData || !setShowCreditPreview || !setPendingGenerationData) {
        console.error('❌ State setters are undefined! Props not passed correctly.');
        setError('Credit preview not available. Please try again.');
        return;
      }
      
      // Set credit preview data
      setCreditPreviewData(previewResponse.data);
      
      // Store pending generation data
      setPendingGenerationData({
        source: 'one-click-studio',
        formData: formData,
        template: template,
        hasPersonas: formData.enable_personas,
        personaCount: hasPersonas ? (formData.persona_count === 'ai_determined' ? 3 : parseInt(formData.persona_count)) : 1
      });
      
      // Store proceed function for when user clicks "Proceed"
      proceedFunctionRef.current = async () => {
        await proceedWithGeneration();
      };
      window.oneClickStudioProceedWithGeneration = async () => {
        if (proceedFunctionRef.current) {
          await proceedFunctionRef.current();
        }
      };
      
      // Show the modal
      setShowCreditPreview(true);
      
      // IMPORTANT: Return here to prevent generation from starting
      return;
      
    } catch (error) {
      console.error('Error getting credit preview:', error);
      setIsSubmitting(false);
      
      // Check if it's an insufficient credits error
      if (error.response && error.response.status === 402) {
        const errorData = error.response.data;
        setError(`Insufficient credits. You need ${errorData.required_credits} credits but only have ${errorData.current_balance}.`);
        return;
      }
      
      setError('Failed to calculate credit cost. Please try again.');
      return;
    }
  };
  
  // Separate function to proceed with generation after credit confirmation
  const proceedWithGeneration = async () => {
    console.log('💰 proceedWithGeneration called - starting campaign generation');
    
    // NOW set isSubmitting since user confirmed credit cost
    setIsSubmitting(true);
    
    // ALWAYS reset flag at the START of each new campaign generation
    window.userChoseBackgroundMode = false;
    console.log('🎯 QuickContentForm: New campaign starting - Reset userChoseBackgroundMode to false');
    
    setIsGenerating(true); // Set parent state to show modal
    
    // Calculate total expected ads based on personas and ad types
    const hasPersonas = formData.enable_personas;
    const personaCount = hasPersonas ? (formData.persona_count === 'ai_determined' ? 3 : parseInt(formData.persona_count)) : 1;
    const totalAds = 1 * personaCount; // Only one ad type for template
    
    // Initialize enhanced progress tracking (using parent state)
    setGenerationProgress({
      step: 0,
      total: totalAds,
      currentStage: 'initializing',
      currentAdType: template.id,
      currentPersona: '',
      completedAds: [],
      message: '🚀 Initializing AI-powered content generation...',
      startTime: Date.now(),
      hasPersonas: hasPersonas,
      personaCount: personaCount,
      actualProgress: 0
    });
    
    // No local progress simulation - backend will provide real progress
    
    try {
      // Prepare data for backend API
      const requestData = {
        product: {
          ...formData,
          occupation: '' // Add missing field that backend expects
        },
        ad_types: [template.id] // Single ad type for template
      };

      console.log('Submitting template data:', requestData);

      const response = await axios.post(`${API}/generate-ads`, requestData, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionToken}`
        },
        timeout: 900000 // 15 minutes timeout
      });

      // ===============================================
      // 🚀 ALWAYS START POLLING (LIKE PRODUCTFORM & AI ASSISTANT)
      // Backend creates stub and may use sync or async generation
      // ===============================================
      const campaignId = response.data.id;
      console.log('🎯 QuickContentForm: Starting campaign polling for:', campaignId);
      console.log('🎯 QuickContentForm: Current userChoseBackgroundMode:', window.userChoseBackgroundMode);
      
      // Store campaign ID for polling
      window.currentPollingCampaignId = campaignId;
      
      // Start polling for campaign status (exactly like ProductForm & AI Assistant)
      const pollCampaign = async () => {
        try {
          const statusResponse = await axios.get(
            `${API}/campaigns/${campaignId}/status`,
            { headers: { Authorization: `Bearer ${sessionToken}` } }
          );
          
          const { status, progress } = statusResponse.data;
          console.log(`📊 QuickContentForm: Campaign ${campaignId} polling: status=${status}, progress=${progress}%`);
          
          // Update progress UI with backend progress
          if (setGenerationProgress) {
            let stageToSet = 'content_generation';
            if (status === 'completed') {
              stageToSet = 'complete';
            } else if (status === 'queued') {
              stageToSet = 'initializing';
            } else if (status === 'failed') {
              stageToSet = 'error';
            }
            
            setGenerationProgress(prev => ({
              ...prev,
              actualProgress: progress,
              currentStage: stageToSet,
              message: status === 'generating' 
                ? `🎨 Generating your ${template.title}... ${progress}% complete`
                : status === 'completed'
                ? '✅ Campaign generation complete!'
                : status === 'queued'
                ? '⏳ Campaign queued for generation...'
                : status === 'failed'
                ? '❌ Campaign generation failed'
                : 'Processing...'
            }));
          }
          
          // If completed, fetch full campaign data
          if (status === 'completed') {
            console.log('✅ QuickContentForm: Campaign status is COMPLETED');
            console.log('   User chose background mode:', window.userChoseBackgroundMode);
            console.log('   Fetching complete campaign data from:', `${API}/campaigns/${campaignId}`);
            
            // Add a small delay to show 100% completion before closing
            await new Promise(resolve => setTimeout(resolve, 1500));
            
            // Fetch complete campaign data with retry (handles replica lag, auth blips, etc.)
            const fetchResult = await fetchCampaignWithRetry(campaignId, sessionToken);
            if (fetchResult.ok) {
              console.log('   Complete campaign fetched successfully (with retry)');
              onSubmit(fetchResult.campaign);
              if (window.userChoseBackgroundMode === true) {
                console.log('   User went to dashboard - modal already closed');
              } else {
                console.log('   User stayed on modal - closing modal now');
                setIsGenerating(false);
                setIsSubmitting(false);
              }
            } else {
              console.error('   fetchCampaignWithRetry failed:', fetchResult.reason);
              if (fetchResult.campaign) {
                onSubmit(fetchResult.campaign);
              }
              if (setGenerationProgress) {
                setGenerationProgress(prev => ({
                  ...prev,
                  currentStage: 'error',
                  message: fetchResult.reason === 'campaign_failed'
                    ? '❌ Generation failed. Please try again or check the dashboard for details.'
                    : '⚠️ Could not load your campaign right away. It may still be ready in your dashboard — please refresh.',
                }));
              }
              setIsGenerating(false);
              setIsSubmitting(false);
            }
            
            // Clear polling campaign ID
            window.currentPollingCampaignId = null;
            return true; // Stop polling
          } else if (status === 'failed') {
            console.error('❌ QuickContentForm: Campaign generation failed');
            setError('Campaign generation failed. Please try again.');
            setIsGenerating(false);
            setIsSubmitting(false);
            window.currentPollingCampaignId = null;
            return true; // Stop polling
          }
          
          return false; // Continue polling
        } catch (pollError) {
          console.error('Error polling campaign status:', pollError);
          return false;
        }
      };
      
      // Poll every 2 seconds
      const pollInterval = setInterval(async () => {
        const shouldStop = await pollCampaign();
        if (shouldStop) {
          clearInterval(pollInterval);
        }
      }, 2000);
      
      // Initial poll
      const shouldStop = await pollCampaign();
      if (shouldStop) {
        clearInterval(pollInterval);
      }
      
      // Don't execute the code below - polling handles everything
      return;

    } catch (err) {
      console.error('❌ Template generation failed:', err);
      
      let errorMessage = 'Failed to generate content. Please try again.';
      
      // Handle 429 - Ultra Video rate limit
      if (err.response?.status === 429) {
        const detail = err.response.data?.detail || 'Please wait for your previous Ultra Video campaign to complete before starting a new one.';
        errorMessage = `⏳ ${detail}`;
        alert(`⏳ Ultra Video In Progress\n\n${detail}`);
      } else if (err.code === 'ECONNABORTED') {
        errorMessage = 'Generation is taking longer than expected. The content might still be processing in the background.';
      } else if (err.response?.data) {
        const errorData = err.response.data;
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail)) {
          const validationErrors = errorData.detail.map(error => error.msg || error.type || 'Invalid field');
          errorMessage = validationErrors.join(', ');
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
      }
      
      setError(errorMessage);
      setGenerationProgress(prev => ({
        ...prev,
        currentStage: 'error',
        message: `❌ Error generating ${template.title}`,
        actualProgress: 0
      }));
      setIsGenerating(false);
      setIsSubmitting(false);
    }
  };

  return (
    <div className={`min-h-screen bg-gradient-to-br ${colorTheme === 'purple' ? 'from-purple-50 via-white to-pink-50' : colorTheme === 'blue' ? 'from-blue-50 via-white to-sky-50' : 'from-green-50 via-white to-emerald-50'} dark:from-gray-900 dark:via-gray-800 dark:to-gray-900`}>
      {/* Header */}
      <div className={`bg-white dark:bg-gray-800 shadow-lg border-b ${colorTheme === 'purple' ? 'border-purple-200' : colorTheme === 'blue' ? 'border-blue-200' : 'border-green-200'} dark:border-gray-700`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className={`w-12 h-12 bg-gradient-to-r ${template.color} rounded-lg flex items-center justify-center text-2xl`}>
                {template.icon}
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Create {template.title}
                </h1>
                <p className="text-gray-600 dark:text-gray-300">
                  {template.description}
                </p>
              </div>
            </div>
            <button
              onClick={onCancel}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <span className="text-2xl">×</span>
            </button>
          </div>
        </div>
      </div>

      {/* Generation Progress Modal */}
      {isSubmitting && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl">
            <div className="text-center">
              <div className={`animate-spin rounded-full h-16 w-16 border-4 ${colorTheme === 'purple' ? 'border-purple-200 border-t-purple-600' : colorTheme === 'blue' ? 'border-blue-200 border-t-blue-600' : 'border-green-200 border-t-green-600'} mx-auto mb-6`}></div>
              
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                🚀 AI Content Generation
              </h3>
              
              <p className="text-gray-600 dark:text-gray-300 mb-6 text-sm">
                {generationProgress.message || 'Generating your content...'}
              </p>
              
              <div className="mb-6">
                {generationProgress.total > 0 && (
                  <div className="w-full">
                    {/* Timer Badge */}
                    <div className="flex justify-end mb-3">
                      {generationProgress.startTime && (
                        <span className={`${colorTheme === 'purple' ? 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200' : colorTheme === 'blue' ? 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200' : 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'} px-3 py-1 rounded-full text-xs font-bold`}>
                          {Math.floor((Date.now() - generationProgress.startTime) / 1000)}s
                        </span>
                      )}
                    </div>
                    
                    {/* Enhanced Progress Bar */}
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-4 mb-4 shadow-inner">
                      <div 
                        className={`bg-gradient-to-r ${colorTheme === 'purple' ? 'from-purple-500 to-pink-500' : colorTheme === 'blue' ? 'from-blue-500 to-sky-500' : 'from-green-500 to-emerald-500'} h-4 rounded-full transition-all duration-1000 ease-out shadow-lg flex items-center justify-center`}
                        style={{ 
                          width: `${generationProgress.actualProgress || (generationProgress.step && generationProgress.total ? Math.min(95, (generationProgress.step / generationProgress.total) * 100) : 5)}%`,
                          minWidth: '40px'
                        }}
                      >
                        <span className="text-white text-xs font-bold drop-shadow-lg">
                          {generationProgress.actualProgress || (generationProgress.step && generationProgress.total ? Math.round((generationProgress.step / generationProgress.total) * 100) : 0)}%
                        </span>
                      </div>
                    </div>
                    
                    {/* Enhanced Stage Indicators */}
                    <div className="flex items-center justify-center space-x-6 mb-4">
                      <div className={`flex flex-col items-center space-y-1 ${generationProgress.currentStage === 'initializing' ? (colorTheme === 'purple' ? 'text-purple-600' : colorTheme === 'blue' ? 'text-blue-600' : 'text-green-600') + ' font-bold' : 'text-gray-400'}`}>
                        <div className={`w-3 h-3 rounded-full ${['initializing', 'persona_generation', 'content_generation', 'finalizing', 'complete'].includes(generationProgress.currentStage) ? (colorTheme === 'purple' ? 'bg-purple-500 ring-2 ring-purple-300' : colorTheme === 'blue' ? 'bg-blue-500 ring-2 ring-blue-300' : 'bg-green-500 ring-2 ring-green-300') : 'bg-gray-300'}`}></div>
                        <span className="text-xs">Setup</span>
                      </div>
                      
                      {generationProgress.hasPersonas && (
                        <div className={`flex flex-col items-center space-y-1 ${generationProgress.currentStage === 'persona_generation' ? (colorTheme === 'purple' ? 'text-purple-600' : colorTheme === 'blue' ? 'text-blue-600' : 'text-green-600') + ' font-bold' : 'text-gray-400'}`}>
                          <div className={`w-3 h-3 rounded-full ${['persona_generation', 'content_generation', 'finalizing', 'complete'].includes(generationProgress.currentStage) ? (colorTheme === 'purple' ? 'bg-purple-500 ring-2 ring-purple-300' : colorTheme === 'blue' ? 'bg-blue-500 ring-2 ring-blue-300' : 'bg-green-500 ring-2 ring-green-300') : 'bg-gray-300'}`}></div>
                          <span className="text-xs">Personas</span>
                        </div>
                      )}
                      
                      <div className={`flex flex-col items-center space-y-1 ${generationProgress.currentStage === 'content_generation' ? (colorTheme === 'purple' ? 'text-purple-600' : colorTheme === 'blue' ? 'text-blue-600' : 'text-green-600') + ' font-bold' : 'text-gray-400'}`}>
                        <div className={`w-3 h-3 rounded-full ${['content_generation', 'finalizing', 'complete'].includes(generationProgress.currentStage) ? (colorTheme === 'purple' ? 'bg-purple-500 ring-2 ring-purple-300' : colorTheme === 'blue' ? 'bg-blue-500 ring-2 ring-blue-300' : 'bg-green-500 ring-2 ring-green-300') : 'bg-gray-300'}`}></div>
                        <span className="text-xs">Content</span>
                      </div>
                      
                      <div className={`flex flex-col items-center space-y-1 ${generationProgress.currentStage === 'finalizing' ? (colorTheme === 'purple' ? 'text-purple-600' : colorTheme === 'blue' ? 'text-blue-600' : 'text-green-600') + ' font-bold' : 'text-gray-400'}`}>
                        <div className={`w-3 h-3 rounded-full ${['finalizing', 'complete'].includes(generationProgress.currentStage) ? (colorTheme === 'purple' ? 'bg-purple-500 ring-2 ring-purple-300' : colorTheme === 'blue' ? 'bg-blue-500 ring-2 ring-blue-300' : 'bg-green-500 ring-2 ring-green-300') : 'bg-gray-300'}`}></div>
                        <span className="text-xs">Finalize</span>
                      </div>
                      
                      <div className={`flex flex-col items-center space-y-1 ${generationProgress.currentStage === 'complete' ? (colorTheme === 'purple' ? 'text-purple-600' : colorTheme === 'blue' ? 'text-blue-600' : 'text-green-600') + ' font-bold' : 'text-gray-400'}`}>
                        <div className={`w-3 h-3 rounded-full ${generationProgress.currentStage === 'complete' ? (colorTheme === 'purple' ? 'bg-purple-500 ring-2 ring-purple-300' : colorTheme === 'blue' ? 'bg-blue-500 ring-2 ring-blue-300' : 'bg-green-500 ring-2 ring-green-300') : 'bg-gray-300'}`}></div>
                        <span className="text-xs">Complete</span>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="flex items-center justify-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
                  <div className="flex space-x-1">
                    <div className={`w-2 h-2 ${colorTheme === 'purple' ? 'bg-purple-500' : colorTheme === 'blue' ? 'bg-blue-500' : 'bg-green-500'} rounded-full animate-pulse`}></div>
                    <div className={`w-2 h-2 ${colorTheme === 'purple' ? 'bg-purple-500' : colorTheme === 'blue' ? 'bg-blue-500' : 'bg-green-500'} rounded-full animate-pulse`} style={{animationDelay: '0.1s'}}></div>
                    <div className={`w-2 h-2 ${colorTheme === 'purple' ? 'bg-purple-500' : colorTheme === 'blue' ? 'bg-blue-500' : 'bg-green-500'} rounded-full animate-pulse`} style={{animationDelay: '0.2s'}}></div>
                  </div>
                  <span className="font-medium">
                    {generationProgress.currentStage === 'persona_generation' ? 'Creating buyer personas...' : 
                     generationProgress.currentStage === 'content_generation' ? 'AI is crafting your content...' :
                     generationProgress.currentStage === 'finalizing' ? 'Adding final touches...' :
                     'AI is working its magic...'}
                  </span>
                </div>
              </div>
              
              <div className="text-xs text-gray-400 dark:text-gray-500 bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                <p className="flex items-center justify-center">
                  <span className="mr-2">⚡</span>
                  Powered by Claude AI & Replicate
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quick Form */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Error Display */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                    Generation Error
                  </h3>
                  <div className="mt-1 text-sm text-red-700 dark:text-red-300">
                    <p>{error}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Website URL Auto-Fill Section */}
          <div className={`mb-6 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-2xl border-2 ${colorTheme === 'purple' ? 'border-purple-200' : colorTheme === 'blue' ? 'border-blue-200' : 'border-blue-200'} dark:border-blue-700`}>
            <div className="flex items-center gap-2 mb-3">
              <span className="text-2xl">🌐</span>
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                Quick Start with Website URL
              </h4>
              <span className="text-xs bg-blue-200 dark:bg-blue-800 text-blue-800 dark:text-blue-200 px-2 py-1 rounded-full">Optional</span>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Enter your website or product page URL to automatically extract product information
            </p>
            <div className="flex gap-3">
              <input
                type="url"
                name="website_url"
                value={formData.website_url}
                onChange={handleInputChange}
                placeholder="https://example.com/product"
                className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
              <button
                type="button"
                onClick={handleExtractWebsite}
                disabled={isExtractingWebsite || !formData.website_url?.trim()}
                className={`px-6 py-3 rounded-lg font-medium transition-all flex items-center gap-2 ${
                  isExtractingWebsite || !formData.website_url?.trim()
                    ? 'bg-gray-300 dark:bg-gray-700 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                }`}
              >
                {isExtractingWebsite ? (
                  <>
                    <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Extracting...
                  </>
                ) : (
                  <>
                    <span>🔍</span>
                    Fetch Content
                  </>
                )}
              </button>
            </div>
            {websiteExtractionError && (
              <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                ⚠️ {websiteExtractionError}
              </p>
            )}
          </div>

          {/* Website Extraction Success Modal */}
          {websiteExtractionSuccess && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full p-8 transform transition-all animate-bounce-in">
                {/* Celebration Icon */}
                <div className="text-center mb-6">
                  <div className={`inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r ${
                    colorTheme === 'purple' ? 'from-purple-400 to-pink-500' : 
                    colorTheme === 'blue' ? 'from-blue-400 to-sky-500' : 
                    'from-green-400 to-emerald-500'
                  } rounded-full mb-4 animate-pulse`}>
                    <span className="text-4xl">🎉</span>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                    Content Extracted Successfully!
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    We've automatically filled in your product details from the website.
                  </p>
                </div>
                
                {/* Important Notice */}
                <div className={`rounded-lg p-4 mb-6 border ${
                  colorTheme === 'purple' ? 'bg-purple-50 dark:bg-purple-900/30 border-purple-200 dark:border-purple-700' : 
                  colorTheme === 'blue' ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-700' : 
                  'bg-amber-50 dark:bg-amber-900/30 border-amber-200 dark:border-amber-700'
                }`}>
                  <div className="flex items-start gap-3">
                    <span className="text-xl">⚠️</span>
                    <div>
                      <p className={`text-sm font-medium ${
                        colorTheme === 'purple' ? 'text-purple-800 dark:text-purple-300' : 
                        colorTheme === 'blue' ? 'text-blue-800 dark:text-blue-300' : 
                        'text-amber-800 dark:text-amber-300'
                      }`}>
                        Please Review Before Generating
                      </p>
                      <p className={`text-xs mt-1 ${
                        colorTheme === 'purple' ? 'text-purple-700 dark:text-purple-400' : 
                        colorTheme === 'blue' ? 'text-blue-700 dark:text-blue-400' : 
                        'text-amber-700 dark:text-amber-400'
                      }`}>
                        AI-extracted content may need adjustments. Verify all details are accurate before creating your ad.
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Action Button */}
                <button
                  onClick={() => setWebsiteExtractionSuccess(false)}
                  className={`w-full py-3 px-6 bg-gradient-to-r ${
                    colorTheme === 'purple' ? 'from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700' : 
                    colorTheme === 'blue' ? 'from-blue-500 to-sky-600 hover:from-blue-600 hover:to-sky-700' : 
                    'from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700'
                  } text-white font-semibold rounded-lg transition-all transform hover:scale-[1.02] shadow-lg`}
                >
                  Got it, let me review ✓
                </button>
              </div>
            </div>
          )}

          {/* Extracted Products Picker (QuickContentForm variant). Mirror of
              the primary form's picker — see line ~13574 for full docs. */}
          {extractedProducts && extractedProducts.length > 0 && (
            <div
              className="bg-white/70 dark:bg-slate-800/50 backdrop-blur-xl border border-emerald-200/60 dark:border-emerald-700/40 rounded-2xl p-5 shadow-sm"
              data-testid="quick-extracted-products-picker"
            >
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h4 className="text-base font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                    <span>🛒 Products found on your site</span>
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300">
                      {extractedProducts.length}
                    </span>
                  </h4>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    Click a product to use its image & details. Remove any you don't want with the ✕.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => { setExtractedProducts([]); setSelectedExtractedProductIdx(null); }}
                  data-testid="quick-extracted-products-clear"
                  className="text-xs text-slate-500 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                >
                  Clear all
                </button>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {extractedProducts.map((p, idx) => (
                  <div
                    key={`${p.product_url || p.image_url || idx}`}
                    className={`relative group cursor-pointer rounded-xl overflow-hidden border-2 transition-all ${
                      selectedExtractedProductIdx === idx
                        ? 'border-emerald-500 ring-2 ring-emerald-300 dark:ring-emerald-700'
                        : 'border-slate-200 dark:border-slate-700 hover:border-emerald-400'
                    }`}
                    data-testid={`quick-extracted-product-card-${idx}`}
                    onClick={() => {
                      setSelectedExtractedProductIdx(idx);
                      const numericPrice = (p.price || '').toString().replace(/,/g, '');
                      setFormData(prev => ({
                        ...prev,
                        product_name: p.name || prev.product_name,
                        price: numericPrice || prev.price,
                        product_image: p.image_url || prev.product_image,
                        product_images: [p.image_url, ...(Array.isArray(prev.product_images) ? prev.product_images.filter(u => u && u !== p.image_url) : [])].slice(0, 4),
                        strict_reference_mode: true
                      }));
                    }}
                  >
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setExtractedProducts(prev => prev.filter((_, i) => i !== idx));
                        if (selectedExtractedProductIdx === idx) setSelectedExtractedProductIdx(null);
                        else if (selectedExtractedProductIdx !== null && idx < selectedExtractedProductIdx) {
                          setSelectedExtractedProductIdx(selectedExtractedProductIdx - 1);
                        }
                      }}
                      data-testid={`quick-extracted-product-remove-${idx}`}
                      aria-label={`Remove ${p.name || 'product'}`}
                      className="absolute top-1.5 right-1.5 z-10 w-6 h-6 rounded-full bg-white/90 dark:bg-slate-900/90 text-slate-600 dark:text-slate-300 hover:bg-red-500 hover:text-white shadow-md flex items-center justify-center text-xs font-bold transition-colors opacity-0 group-hover:opacity-100"
                    >
                      ✕
                    </button>
                    {p.discount_pct ? (
                      <span className="absolute top-1.5 left-1.5 z-10 text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-500 text-white shadow">
                        -{p.discount_pct}%
                      </span>
                    ) : null}
                    <div className="aspect-square bg-slate-100 dark:bg-slate-900 overflow-hidden">
                      <img
                        src={p.image_url}
                        alt={p.name || 'Product'}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => { e.currentTarget.style.opacity = '0.3'; }}
                      />
                    </div>
                    <div className="p-2 bg-white dark:bg-slate-900">
                      <p className="text-xs font-medium text-slate-800 dark:text-slate-200 line-clamp-2 leading-tight" title={p.name}>
                        {p.name}
                      </p>
                      {p.price && (
                        <div className="flex items-baseline gap-1 mt-1">
                          <span className="text-xs font-bold text-emerald-700 dark:text-emerald-300">₹{p.price}</span>
                          {p.original_price && (
                            <span className="text-[10px] text-slate-400 line-through">₹{p.original_price}</span>
                          )}
                        </div>
                      )}
                    </div>
                    {selectedExtractedProductIdx === idx && (
                      <div className="absolute inset-x-0 bottom-0 bg-emerald-500 text-white text-[10px] font-bold py-1 text-center">
                        ✓ USING THIS PRODUCT
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Extracted Avatars Picker (QuickContentForm variant). Mirror of
              the primary form's avatar picker — see line ~13680 for full docs. */}
          {extractedAvatars && extractedAvatars.length > 0 && (
            <div
              className="bg-white/70 dark:bg-slate-800/50 backdrop-blur-xl border border-indigo-200/60 dark:border-indigo-700/40 rounded-2xl p-5 shadow-sm"
              data-testid="quick-extracted-avatars-picker"
            >
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h4 className="text-base font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                    <span>👤 Founder & team photos</span>
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300">
                      {extractedAvatars.length}
                    </span>
                  </h4>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    Click one (or more) to feature them as the hero. Click again to unselect.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setExtractedAvatars([]);
                    setSelectedExtractedAvatarIdxs([]);
                    setFormData(prev => ({ ...prev, avatar_image_url: '', avatar_image_urls: [] }));
                  }}
                  data-testid="quick-extracted-avatars-clear"
                  className="text-xs text-slate-500 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                >
                  Clear all
                </button>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {extractedAvatars.map((a, idx) => {
                  const isSelected = selectedExtractedAvatarIdxs.includes(idx);
                  return (
                    <div
                      key={`${a.image_url || idx}`}
                      className={`relative group cursor-pointer rounded-xl overflow-hidden border-2 transition-all ${
                        isSelected
                          ? 'border-indigo-500 ring-2 ring-indigo-300 dark:ring-indigo-700'
                          : 'border-slate-200 dark:border-slate-700 hover:border-indigo-400'
                      }`}
                      data-testid={`quick-extracted-avatar-card-${idx}`}
                      onClick={() => {
                        const nextIdxs = isSelected
                          ? selectedExtractedAvatarIdxs.filter(i => i !== idx)
                          : [...selectedExtractedAvatarIdxs, idx];
                        setSelectedExtractedAvatarIdxs(nextIdxs);
                        const selectedUrls = nextIdxs
                          .map(i => extractedAvatars[i]?.image_url)
                          .filter(Boolean);
                        setFormData(prev => ({
                          ...prev,
                          avatar_image_url: selectedUrls[0] || '',
                          avatar_image_urls: selectedUrls,
                          strict_reference_mode: selectedUrls.length > 0 ? true : prev.strict_reference_mode
                        }));
                      }}
                    >
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setExtractedAvatars(prev => prev.filter((_, i) => i !== idx));
                          const nextIdxs = selectedExtractedAvatarIdxs
                            .filter(i => i !== idx)
                            .map(i => (i > idx ? i - 1 : i));
                          setSelectedExtractedAvatarIdxs(nextIdxs);
                          const nextList = extractedAvatars.filter((_, i) => i !== idx);
                          const selectedUrls = nextIdxs.map(i => nextList[i]?.image_url).filter(Boolean);
                          setFormData(prev => ({
                            ...prev,
                            avatar_image_url: selectedUrls[0] || '',
                            avatar_image_urls: selectedUrls
                          }));
                        }}
                        data-testid={`quick-extracted-avatar-remove-${idx}`}
                        aria-label={`Remove ${a.name || 'avatar'}`}
                        className="absolute top-1.5 right-1.5 z-10 w-6 h-6 rounded-full bg-white/90 dark:bg-slate-900/90 text-slate-600 dark:text-slate-300 hover:bg-red-500 hover:text-white shadow-md flex items-center justify-center text-xs font-bold transition-colors opacity-0 group-hover:opacity-100"
                      >
                        ✕
                      </button>
                      {a.role && (
                        <span className="absolute top-1.5 left-1.5 z-10 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-indigo-500 text-white shadow">
                          {a.role}
                        </span>
                      )}
                      <div className="aspect-square bg-slate-100 dark:bg-slate-900 overflow-hidden">
                        <img
                          src={a.image_url}
                          alt={a.name || 'Team photo'}
                          loading="lazy"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          onError={(e) => { e.currentTarget.style.opacity = '0.3'; }}
                        />
                      </div>
                      <div className="p-2 bg-white dark:bg-slate-900">
                        <p className="text-xs font-medium text-slate-800 dark:text-slate-200 line-clamp-2 leading-tight" title={a.name || ''}>
                          {a.name || 'Team member'}
                        </p>
                        {a.context_text && (
                          <p className="text-[10px] text-slate-500 dark:text-slate-400 line-clamp-2 mt-0.5 leading-tight">
                            {a.context_text}
                          </p>
                        )}
                      </div>
                      {isSelected && (
                        <div className="absolute inset-x-0 bottom-0 bg-indigo-500 text-white text-[10px] font-bold py-1 text-center">
                          ✓ HERO IN AD
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Product Information Section */}
          <div className={`bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 border-2 ${colorTheme === 'purple' ? 'border-purple-200' : colorTheme === 'blue' ? 'border-blue-200' : 'border-green-200'} dark:border-gray-700`}>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              Product Information
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Product Name *
                </label>
                <input
                  type="text"
                  name="product_name"
                  value={formData.product_name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  placeholder="e.g., Smart Fitness Watch"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Price (Optional)
                </label>
                <input
                  type="text"
                  name="price"
                  value={formData.price}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  placeholder="e.g., $299"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Category
                </label>
                <input
                  type="text"
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  placeholder="e.g., Household, Electronics, Fashion"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Brand Voice
                </label>
                <select
                  name="brand_voice"
                  value={formData.brand_voice}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                >
                  <option value="professional">Professional</option>
                  <option value="friendly">Friendly</option>
                  <option value="casual">Casual</option>
                  <option value="luxury">Luxury</option>
                  <option value="playful">Playful</option>
                </select>
              </div>
            </div>

            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Product Description & Benefits *
              </label>
              <textarea
                name="product_description"
                value={formData.product_description}
                onChange={handleInputChange}
                required
                rows="6"
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                placeholder="Describe your product, its features, benefits, and what makes it special. Include key selling points, unique advantages, and value propositions..."
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                💡 Tip: Include both product description and key benefits in one field for easier input
              </p>
            </div>

            {/* Product Tagline - Full Width */}
            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Product Tagline (Optional)
              </label>
              <input
                type="text"
                name="product_tagline"
                value={formData.product_tagline}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                placeholder="e.g., Innovation at your fingertips"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                💡 A catchy slogan that will appear on your generated images (if text overlay is enabled)
              </p>
            </div>
            
            {/* Text Overlay Method - Only show for image-based templates when tagline is entered */}
            {(template.id === 'image_promo' || template.id === 'telegram_ad') && formData.product_tagline && formData.product_tagline.trim() !== '' && (
              <div className="mt-6 p-6 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
                  ✨ Text Overlay Method
                </h3>
                
                <div className="space-y-3">
                  <label className="flex items-start p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                    <input
                      type="radio"
                      name="text_overlay_method"
                      value="ai-text"
                      checked={formData.text_overlay_method !== 'post_processing'}
                      onChange={handleInputChange}
                      className="mt-1 mr-3"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900 dark:text-white">AI-Generated Text</span>
                        <span className="text-xs bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 px-2 py-1 rounded">Recommended</span>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        AI includes text naturally as part of the image scene
                      </p>
                    </div>
                  </label>
                  
                  <label className="flex items-start p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                    <input
                      type="radio"
                      name="text_overlay_method"
                      value="post_processing"
                      checked={formData.text_overlay_method === 'post_processing'}
                      onChange={handleInputChange}
                      className="mt-1 mr-3"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900 dark:text-white">Post-Processing Overlay</span>
                        <span className="text-xs bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300 px-2 py-1 rounded">Optional</span>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Adds a text banner overlay after image generation
                      </p>
                    </div>
                  </label>
                </div>
              </div>
            )}
            
            {/* Unified Brand Assets card — Product / Avatar / Logo */}
            <div className="mt-6">
              <BrandAssetsCard
                formData={formData}
                setFormData={setFormData}
                sessionToken={sessionToken}
                testIdPrefix="quick-brand-assets"
              />
            </div>
            
            {/* Video Generation Mode - Only for video templates and only when product image is uploaded */}
            {template.id === 'video_promo' && formData.product_image && (
              <div className="mt-6 p-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                  <span className="mr-2">🎬</span>
                  Video Generation Mode
                </h3>
                
                <div className="space-y-3">
                  <label className="flex items-start p-4 border-2 rounded-lg cursor-pointer hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors">
                    <input
                      type="radio"
                      name="video_generation_mode"
                      value="text"
                      checked={formData.video_generation_mode === 'text'}
                      onChange={handleInputChange}
                      className="mt-1 mr-3"
                    />
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">Text-to-Video</div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        Use your product description as a prompt (ignores uploaded images)
                      </div>
                    </div>
                  </label>
                  
                  <label className="flex items-start p-4 border-2 rounded-lg cursor-pointer hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors">
                    <input
                      type="radio"
                      name="video_generation_mode"
                      value="image_reference"
                      checked={formData.video_generation_mode === 'image_reference'}
                      onChange={handleInputChange}
                      className="mt-1 mr-3"
                    />
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">Image-to-Video</div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        Use uploaded image as reference
                      </div>
                      <div className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">
                        ⚠️ Auto-forces Fast model + 16:9 aspect ratio
                      </div>
                    </div>
                  </label>
                  
                  <label className="flex items-start p-4 border-2 rounded-lg cursor-pointer hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors">
                    <input
                      type="radio"
                      name="video_generation_mode"
                      value="image_transition"
                      checked={formData.video_generation_mode === 'image_transition'}
                      onChange={handleInputChange}
                      className="mt-1 mr-3"
                    />
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">Image Transition Video</div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        Create transition between 2 uploaded images
                      </div>
                    </div>
                  </label>
                </div>
              </div>
            )}
          </div>

          {/* Demographics Section */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 border-2 border-green-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              Target Demographics
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Age Range
                </label>
                <div className="flex space-x-2">
                  <input
                    type="number"
                    name="age_range_min"
                    value={formData.age_range_min}
                    onChange={handleInputChange}
                    min="18"
                    max="80"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  />
                  <span className="flex items-center text-gray-500">to</span>
                  <input
                    type="number"
                    name="age_range_max"
                    value={formData.age_range_max}
                    onChange={handleInputChange}
                    min="18"
                    max="80"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Gender
                </label>
                <select
                  name="gender"
                  value={formData.gender}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                >
                  <option value="All">All Genders</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Non-binary">Non-binary</option>
                </select>
              </div>
            </div>
            
            {/* Geography & Language Selection */}
            <div className="mt-6">
              <GeographyLanguageSelector
                selectedGeographies={selectedGeographies}
                onGeographyChange={setSelectedGeographies}
                selectedLanguages={selectedLanguages}
                onLanguageChange={setSelectedLanguages}
                maxGeographies={1}
                maxLanguages={3}
                showLanguageSelector={true}
              />
            </div>
          </div>

          {/* Persona-Driven Optimization */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 border-2 border-green-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                Persona-Driven Optimization
              </h2>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  name="enable_personas"
                  checked={formData.enable_personas}
                  onChange={(e) => setFormData(prev => ({ ...prev, enable_personas: e.target.checked }))}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-yellow-300 dark:peer-focus:ring-yellow-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-yellow-600"></div>
                <span className="ml-3 text-sm font-medium text-gray-900 dark:text-gray-300">Enable</span>
              </label>
            </div>

            {formData.enable_personas && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Industry/Business Type
                    </label>
                    <input
                      type="text"
                      name="industry_business_type"
                      value={formData.industry_business_type}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      placeholder="e.g., Technology, Healthcare, Retail, Education"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Budget Range
                    </label>
                    <select
                      name="budget_range"
                      value={formData.budget_range}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    >
                      <option value="">
                        Select budget range {formData.geography ? `(${currentBudgetOptions.currency})` : ''}
                      </option>
                      {currentBudgetOptions.ranges.map((range, index) => (
                        <option key={index} value={range}>
                          {range}
                        </option>
                      ))}
                    </select>
                    {formData.geography && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        💰 Budget shown in {currentBudgetOptions.currency} based on your selected geography
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Interests & Behaviors
                    </label>
                    <textarea
                      name="interests_behaviors"
                      value={formData.interests_behaviors}
                      onChange={handleInputChange}
                      rows="3"
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      placeholder="e.g., Tech enthusiasts, Early adopters, Online shoppers, Health-conscious consumers"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Number of Personas
                    </label>
                    <select
                      name="persona_count"
                      value={formData.persona_count}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    >
                      <option value="ai_determined">🤖 AI Determined (Recommended)</option>
                      <option value="1">1 Persona</option>
                      <option value="2">2 Personas</option>
                      <option value="3">3 Personas</option>
                      <option value="4">4 Personas</option>
                      <option value="5">5 Personas</option>
                    </select>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      AI-determined creates 3-4 personas based on product complexity
                    </p>
                  </div>
                </div>
              </>
            )}
            
          </div>

          {/* Image Specifications Section - After Persona like Ad Creator Hub */}
          {(template.id === 'image_promo' || template.id === 'telegram_ad') && (
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 border-2 border-green-200 dark:border-gray-700">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                Image Specifications
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    AI Image Model
                  </label>
                  <select
                    name="image_model"
                    value={formData.image_model || 'freepik'}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  >
                    <option value="nebius">Quick Image 💨</option>
                    <option value="kie-ai">Standard Image 🎨</option>
                    <option value="freepik">Best Value 🎨 (Recommended)</option>
                    <option value="nano-banana-pro">Premium Image</option>
                    <option value="nano-banana-2">Premium Image 2 (Flash 4K)</option>
                    <option value="gpt4o-image">Ultra Image</option>
                  </select>
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                    {formData.image_model === 'gpt4o-image'
                      ? 'Best quality with accurate text rendering'
                      : formData.image_model === 'nano-banana-pro'
                      ? '4K ultra-high definition with next-generation AI technology'
                      : formData.image_model === 'nano-banana-2'
                      ? 'Flash speed + 4K quality (Gemini 3.1 Flash)'
                      : formData.image_model === 'kie-ai' 
                      ? 'Standard quality with fast generation'
                      : formData.image_model === 'freepik'
                      ? 'Premium quality, fast, and cost-effective'
                      : 'Very fast with good quality results'
                    }
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Image Generation Options
                  </label>
                  <div className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-300">Quick Image:</span>
                        <span className="text-blue-600 dark:text-blue-400 font-medium">Very fast generation</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-300">Standard Image:</span>
                        <span className="text-purple-600 dark:text-purple-400 font-medium">Good quality</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-300">Best Value:</span>
                        <span className="text-green-600 dark:text-green-400 font-medium">Recommended</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-300">Premium Image:</span>
                        <span className="text-pink-600 dark:text-pink-400 font-medium">4K HD quality</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-300">Premium Image 2:</span>
                        <span className="text-teal-600 dark:text-teal-400 font-medium">Flash speed + 4K (New!)</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-300">Ultra Image:</span>
                        <span className="text-orange-600 dark:text-orange-400 font-medium">Best quality</span>
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                        Credits are shown in the preview before generation
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Resolution selector for Nano Banana Pro / Nano Banana 2 */}
              {(formData.image_model === 'nano-banana-pro' || formData.image_model === 'nano-banana-2') && (
                <div className="mt-6">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Image Resolution
                  </label>
                  <select
                    name="nano_banana_resolution"
                    value={formData.nano_banana_resolution || '4K'}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  >
                    <option value="1K">Standard Quality (1K) - 18 credits</option>
                    <option value="2K">High Quality (2K) - 18 credits</option>
                    <option value="4K">Ultra HD (4K) - 24 credits (Recommended)</option>
                  </select>
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                    Higher resolution provides sharper details and better quality
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Video Options - Only show for video content types */}
          {template.id === 'video_promo' && (
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 border-2 border-green-200 dark:border-gray-700">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                Video Specifications
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    AI Model
                  </label>
                  <select
                    name="video_model"
                    value={formData.video_model}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  >
                    <option value="freepik">Quick Video ✨</option>
                    <option value="veo3_fast">Fast Video 🚀 (Recommended)</option>
                    <option value="veo3">Premium Video 🎥</option>
                    <option value="ultra_video">Ultra Video 🎬</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Duration
                  </label>
                  <select
                    name="video_duration"
                    value={formData.video_duration}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  >
                    {formData.video_model === 'ultra_video' ? (
                      <>
                        <option value={10}>10 seconds (Recommended)</option>
                        <option value={15}>15 seconds</option>
                      </>
                    ) : (
                      <>
                        <option value={4}>4 seconds</option>
                        <option value={6}>6 seconds</option>
                        <option value={8}>8 seconds (Recommended)</option>
                      </>
                    )}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Resolution
                  </label>
                  <select
                    name="video_resolution"
                    value={formData.video_resolution}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  >
                    <option value="720p">720p (HD) - 50 credits</option>
                    {/* 1080p supported by Veo 3.1 and Sora 2 */}
                    {(formData.video_model === 'veo3' || formData.video_model === 'veo3_fast') && (
                      <option value="1080p">1080p (Full HD) - 60 credits</option>
                    )}
                    {formData.video_model === 'ultra_video' && (
                      <option value="1080p">1080p (Full HD) - 75 credits</option>
                    )}
                    {/* 4K only supported by Veo 3.1 models */}
                    {(formData.video_model === 'veo3' || formData.video_model === 'veo3_fast') && (
                      <option value="4K">4K (Ultra HD) - 100 credits (~7-13 min)</option>
                    )}
                  </select>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {formData.video_model === 'veo3' 
                      ? 'Premium Video supports up to 4K resolution' 
                      : formData.video_model === 'veo3_fast'
                        ? 'Fast Video supports up to 4K resolution'
                        : formData.video_model === 'ultra_video'
                          ? 'Ultra Video supports up to 1080p resolution'
                          : 'Standard HD resolution'}
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Aspect Ratio
                  </label>
                  <select
                    name="video_aspect_ratio"
                    value={formData.video_aspect_ratio}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  >
                    <option value="16:9">16:9 (Landscape)</option>
                    <option value="1:1">1:1 (Square)</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Submit Actions */}
          <div className="flex justify-between space-x-4">
            <button
              type="button"
              onClick={onCancel}
              disabled={isSubmitting}
              className="px-8 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            
            <button
              type="submit"
              disabled={isSubmitting}
              className={`px-8 py-3 bg-gradient-to-r ${colorTheme === 'purple' ? 'from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700' : colorTheme === 'blue' ? 'from-blue-600 to-sky-500 hover:from-blue-700 hover:to-sky-600' : 'from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700'} text-white font-bold rounded-lg transition-all ${
                isSubmitting ? 'opacity-50 cursor-not-allowed' : 'transform hover:scale-105'
              }`}
            >
              {isSubmitting ? `🚀 Generating ${template.title}...` : `🚀 Generate ${template.title}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const App = () => {
  const { isLoading, isAuthenticated, user, logout, sessionToken } = useAuth();
  const { currentColors, colorTheme } = useColorTheme();
  const { isDark, toggleTheme } = useTheme();
  const [adResults, setAdResults] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState({
    step: 0,
    total: 0,
    currentStage: '',
    currentAdType: '',
    currentPersona: '',
    completedAds: [],
    message: '',
    startTime: null,
    hasPersonas: false,
    personaCount: 0
  });
  const [error, setError] = useState(null);
  const [clearFormTrigger, setClearFormTrigger] = useState(0);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [currentView, setCurrentView] = useState('campaigns'); // 'content-studio', 'main', 'dashboard', 'analytics', 'campaign-detail', 'campaigns', 'admin', 'blog', 'blog-post' - Default to Dashboard (My Masterpieces)
  const [selectedBlogPost, setSelectedBlogPost] = useState(null); // For blog post slug
  const [refreshDashboard, setRefreshDashboard] = useState(0);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  
  // Feature flag for new Dashboard V2 - set to true to enable modern redesign
  const [useNewDashboard, setUseNewDashboard] = useState(true);

  // Credit preview state (for AI Assistant and main form)
  const [showCreditPreview, setShowCreditPreview] = useState(false);
  const [creditPreviewData, setCreditPreviewData] = useState(null);
  const [pendingGenerationData, setPendingGenerationData] = useState(null);

  // Credit preview handler functions
  const handleCreditPreviewCancel = () => {
    setShowCreditPreview(false);
    setCreditPreviewData(null);
    setPendingGenerationData(null);
  };

  const handlePurchaseCreditsFromPreview = () => {
    setShowCreditPreview(false);
    // Navigate to credits page
    if (window.handleNavigation) {
      window.handleNavigation('credits');
    } else {
      window.location.href = '/credits';
    }
  };
  
  const handleCreditPreviewProceed = async () => {
    console.log('💰 handleCreditPreviewProceed called in App');
    setShowCreditPreview(false);
    
    if (!pendingGenerationData) {
      console.error('No pending generation data');
      return;
    }
    
    // Check if this is from One Click Content Studio
    if (pendingGenerationData.source === 'one-click-studio') {
      console.log('💰 Credit confirmed for One Click Content Studio - proceeding with generation');
      
      // Call the global proceed function that was set up by QuickContentForm
      if (window.oneClickStudioProceedWithGeneration) {
        await window.oneClickStudioProceedWithGeneration();
      } else {
        console.error('❌ oneClickStudioProceedWithGeneration not found');
      }
      return;
    }
    
    // Check if this is from AI Assistant
    if (pendingGenerationData.source === 'ai-assistant') {
      console.log('💰 Credit confirmed for AI Assistant - proceeding with generation');
      const conversationId = window.aiAssistantConversationId;
      if (conversationId && window.aiAssistantRef && window.aiAssistantRef.startGenerationAfterCreditConfirmation) {
        window.aiAssistantRef.startGenerationAfterCreditConfirmation(conversationId);
      }
      return;
    }
    
    // Default case: Ad Creator Hub (ProductForm) - no source means main form
    // This was the original working behavior before source field was added
    console.log('💰 Credit confirmed for Ad Creator Hub (ProductForm) - proceeding with generation');
    if (window.proceedWithAdCreatorHubGeneration) {
      await window.proceedWithAdCreatorHubGeneration();
    } else {
      console.log('💰 Using onSubmit callback for Ad Creator Hub generation');
      // The ProductForm sets pendingGenerationData, so we need to trigger its submit
      // This will be handled by the submit callback passed to ProductForm
    }
  };

  // Sharing state
  const [selectedAd, setSelectedAd] = useState(null);
  const [sharingType, setSharingType] = useState('individual');
  const [showSharingModal, setShowSharingModal] = useState(false);
  const [sharingStatus, setSharingStatus] = useState({});
  const [shareUrl, setShareUrl] = useState('');
  const [shareToken, setShareToken] = useState('');
  const [sharedCampaigns, setSharedCampaigns] = useState([]);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareData, setShareData] = useState(null);
  
  // Inline editing states
  const [editingField, setEditingField] = useState(null); // {campaignId, adIndex, fieldName}
  const [editContent, setEditContent] = useState('');
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [versionHistory, setVersionHistory] = useState([]);
  const [copiedText, setCopiedText] = useState('');
  const [saveStatus, setSaveStatus] = useState({});

  // Channel selection state
  const [showChannelSelectionModal, setShowChannelSelectionModal] = useState(false);
  const [currentSharingCampaign, setCurrentSharingCampaign] = useState(null);
  const [availableChannels, setAvailableChannels] = useState([]);
  
  // WhatsApp sharing state (for main App component after generation)
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  
  // Campaign details modal state
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // Primary channel state for improved UX
  const [showPrimaryChannelModal, setShowPrimaryChannelModal] = useState(false);
  const [primaryChannel, setPrimaryChannel] = useState(null);

  // Admin state
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminToken, setAdminToken] = useState(null);
  const [adminError, setAdminError] = useState(null);
  const [adminLoading, setAdminLoading] = useState(false);

  // Toast notification state
  const [toasts, setToasts] = useState([]);
  const [lastNotificationCheck, setLastNotificationCheck] = useState(Date.now());

  // Achievement state
  const [achievementView, setAchievementView] = useState('timeline');
  const [achievementNotification, setAchievementNotification] = useState(null);
  const [showUnlockAnimation, setShowUnlockAnimation] = useState(null);
  const [showShareableCard, setShowShareableCard] = useState(null);
  const [userAchievementStats, setUserAchievementStats] = useState(null);
  
  // Success celebration state
  const [successCelebration, setSuccessCelebration] = useState({
    isOpen: false,
    title: '',
    message: '',
    icon: '🎉',
    celebrationType: 'success',
    actionButton: null
  });

  // Confirmation modal state for regenerate actions
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
    confirmText: 'Confirm',
    cancelText: 'Cancel'
  });

  // Regenerate-with-feedback modal state (per-ad regeneration with
  // optional free-form user feedback like "fix spelling of AdElevate").
  const [regenFeedbackModal, setRegenFeedbackModal] = useState({
    isOpen: false,
    title: '',
    description: '',
    confirmText: 'Regenerate',
    isProcessing: false,
    onConfirm: null,
  });

  // URL Copy modal state
  const [urlCopyModal, setUrlCopyModal] = useState({
    isOpen: false,
    url: '',
    title: 'Link Copied!',
    description: 'Share this link with others to get feedback on your campaign:'
  });

  // Functional Alert modal state
  const [functionalAlert, setFunctionalAlert] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'warning',
    icon: null,
    actionButton: null,
    secondaryButton: null
  });

  // Mobile-specific state
  const [isMobile, setIsMobile] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Copy to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedText(text);
      setTimeout(() => setCopiedText(''), 2000);
    }).catch(err => {
      console.error('Failed to copy text: ', err);
    });
  };

  // Inline editing functions
  const startEditing = (campaignId, adIndex, fieldName, currentContent) => {
    console.log('Starting edit:', { campaignId, adIndex, fieldName, currentContent });
    setEditingField({ campaignId, adIndex, fieldName });
    setEditContent(currentContent);
    console.log('Edit state set');
  };

  const cancelEditing = () => {
    setEditingField(null);
    setEditContent('');
  };

  const saveEdit = async () => {
    if (!editingField || !editContent.trim()) return;

    try {
      const response = await fetch(`${API}/campaigns/${editingField.campaignId}/edit-content`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionToken}`
        },
        body: JSON.stringify({
          ad_index: editingField.adIndex,
          field_name: editingField.fieldName,
          new_content: editContent.trim()
        })
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Content updated:', result);
        
        // Update the campaign data in state
        if (adResults && adResults.id === editingField.campaignId) {
          const updatedAds = [...adResults.generated_ads];
          updatedAds[editingField.adIndex][editingField.fieldName] = editContent.trim();
          
          // Mark as edited in editable_fields
          if (!updatedAds[editingField.adIndex].editable_fields) {
            updatedAds[editingField.adIndex].editable_fields = {};
          }
          updatedAds[editingField.adIndex].editable_fields[editingField.fieldName] = {
            ...updatedAds[editingField.adIndex].editable_fields[editingField.fieldName],
            current_content: editContent.trim(),
            is_edited: true,
            last_edited: new Date().toISOString()
          };
          
          setAdResults({ ...adResults, generated_ads: updatedAds });
        }
        
        // Update selectedCampaign if we're editing from Dashboard modal
        if (selectedCampaign && selectedCampaign.id === editingField.campaignId) {
          const updatedAds = [...selectedCampaign.generated_ads];
          updatedAds[editingField.adIndex][editingField.fieldName] = editContent.trim();
          
          // Mark as edited in editable_fields
          if (!updatedAds[editingField.adIndex].editable_fields) {
            updatedAds[editingField.adIndex].editable_fields = {};
          }
          updatedAds[editingField.adIndex].editable_fields[editingField.fieldName] = {
            ...updatedAds[editingField.adIndex].editable_fields[editingField.fieldName],
            current_content: editContent.trim(),
            is_edited: true,
            last_edited: new Date().toISOString()
          };
          
          setSelectedCampaign({ ...selectedCampaign, generated_ads: updatedAds });
        }
        
        // Clear editing state
        setEditingField(null);
        setEditContent('');
        
        // Show success feedback
        setCopiedText('Content updated!');
        setTimeout(() => setCopiedText(''), 2000);
        
      } else {
        throw new Error('Failed to update content');
      }
    } catch (error) {
      console.error('Error updating content:', error);
      alert('Failed to update content. Please try again.');
    }
  };

  const showContentHistory = async (campaignId, adIndex, fieldName) => {
    try {
      const response = await fetch(`${API}/campaigns/${campaignId}/content-history/${adIndex}/${fieldName}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${sessionToken}`
        }
      });

      if (response.ok) {
        const history = await response.json();
        setVersionHistory(history);
        setShowVersionHistory(true);
      } else {
        throw new Error('Failed to fetch history');
      }
    } catch (error) {
      console.error('Error fetching content history:', error);
      alert('Failed to load content history. Please try again.');
    }
  };

  // Send achievement notifications to service worker for PWA
  const sendAchievementNotification = (achievement) => {
    if ('serviceWorker' in navigator && 'Notification' in window) {
      navigator.serviceWorker.ready.then((registration) => {
        registration.active?.postMessage({
          type: 'ACHIEVEMENT_UNLOCKED',
          achievement: achievement
        });
      });
    }
  };

  // Function to show success celebration
  const showSuccessCelebration = (title, message, celebrationType = 'success', actionButton = null) => {
    setSuccessCelebration({
      isOpen: true,
      title,
      message,
      celebrationType,
      actionButton
    });
  };

  // Function to close success celebration
  const closeSuccessCelebration = () => {
    setSuccessCelebration(prev => ({
      ...prev,
      isOpen: false
    }));
  };

  // Function to validate campaign before publishing
  const validateCampaignForPublishing = async (campaignId) => {
    try {
      const token = localStorage.getItem('session_token');
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const response = await axios.get(`${API}/campaigns/${campaignId}/publish-validation`, { headers });
      return response.data;
    } catch (error) {
      console.error('Error validating campaign:', error);
      throw error;
    }
  };

  // Function to show functional alert
  const showFunctionalAlert = (title, message, type = 'warning', icon = null, actionButton = null, secondaryButton = null) => {
    setFunctionalAlert({
      isOpen: true,
      title,
      message,
      type,
      icon,
      actionButton,
      secondaryButton
    });
  };

  // Function to close functional alert
  const closeFunctionalAlert = () => {
    setFunctionalAlert(prev => ({
      ...prev,
      isOpen: false
    }));
  };

  // Function to determine celebration type based on achievement importance
  const getCelebrationType = (achievement) => {
    if (!achievement || !achievement.definition) return 'notification';
    
    const { name, points, category } = achievement.definition;
    
    // Major milestones get full modal celebrations
    const majorMilestones = [
      'Campaign Creator', // First campaign
      'Campaign Veteran', // 5th campaign
      'Campaign Master', // 10th campaign
      'Social Butterfly', // 10 shares
      'Creative Genius', // 100 media generated
      'Campaign Legend', // 100 campaigns
      'Veteran Marketer' // 100 days active
    ];
    
    // High-point achievements or specific major achievements get modal celebrations
    if (majorMilestones.includes(name) || points >= 50) {
      return 'modal';
    }
    
    // Everything else gets subtle notifications
    return 'notification';
  };

  // Function to check for new achievements after campaign generation
  const checkForNewAchievements = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const response = await axios.post(`${API}/achievements/manual-check`, {}, { headers });
      
      if (response.data.newly_unlocked && response.data.newly_unlocked.length > 0) {
        // Process all newly unlocked achievements
        const newAchievements = response.data.newly_unlocked;
        console.log('🏆 New achievements unlocked:', newAchievements);
        
        // Group achievements by celebration type
        const modalAchievements = newAchievements.filter(ach => getCelebrationType(ach) === 'modal');
        const notificationAchievements = newAchievements.filter(ach => getCelebrationType(ach) === 'notification');
        
        // Show modal celebration for first major achievement (if any)
        if (modalAchievements.length > 0) {
          setShowUnlockAnimation(modalAchievements[0]);
          
          // Queue additional modal achievements with delay
          modalAchievements.slice(1).forEach((achievement, index) => {
            setTimeout(() => {
              setShowUnlockAnimation(achievement);
            }, (index + 1) * 4000); // 4 second delay between modal celebrations
          });
        }
        
        // Show subtle notifications for minor achievements
        notificationAchievements.forEach((achievement, index) => {
          setTimeout(() => {
            setAchievementNotification(achievement);
          }, index * 6000); // 6 second delay between notifications
        });
        
        // Send PWA notifications for all achievements
        newAchievements.forEach((achievement, index) => {
          setTimeout(() => {
            sendAchievementNotification(achievement);
          }, index * 1000);
        });
      }

      // Also load updated user stats
      await loadUserAchievementStats();
    } catch (error) {
      console.error('Error checking for achievements:', error);
    }
  };

  // Global achievement trigger using custom events
  useEffect(() => {
    const handleAchievementCheck = () => {
      checkForNewAchievements();
    };

    // Listen for custom achievement events
    window.addEventListener('triggerAchievementCheck', handleAchievementCheck);
    
    return () => {
      window.removeEventListener('triggerAchievementCheck', handleAchievementCheck);
    };
  }, []);

  // Function to load user achievement stats
  const loadUserAchievementStats = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const response = await axios.get(`${API}/achievements/user`, { headers });
      setUserAchievementStats(response.data.user_stats);
    } catch (error) {
      console.error('Error loading user achievement stats:', error);
    }
  };

  // Admin functions
  const handleAdminLogin = async (password) => {
    setAdminLoading(true);
    setAdminError(null);
    
    try {
      console.log('Admin login attempt:', password);
      const response = await axios.post(`${API}/admin/login`, {
        password: password
      });
      
      console.log('Admin login response:', response);
      console.log('Response data:', response.data);
      
      // Handle both wrapped and unwrapped response formats
      const sessionToken = response.data.session_token || response.data.data?.session_token;
      
      if (sessionToken) {
        setAdminToken(sessionToken);
        setIsAdmin(true);
        setCurrentView('admin');
        console.log('Admin login successful');
      } else {
        throw new Error('No session token in response');
      }
    } catch (err) {
      console.error('Admin login error:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Invalid admin password';
      setAdminError(errorMessage);
    } finally {
      setAdminLoading(false);
    }
  };

  const handleAdminLogout = async () => {
    if (adminToken) {
      try {
        await axios.post(`${API}/admin/logout`, {}, {
          headers: { Authorization: `Bearer ${adminToken}` }
        });
      } catch (err) {
        console.error('Admin logout error:', err);
      }
    }
    
    setAdminToken(null);
    setIsAdmin(false);
    setCurrentView('campaigns');
    setAdminError(null);
  };

  // ==========================================
  // 🍞 Toast Notification Functions
  // ==========================================
  const addToast = (notification) => {
    const toast = {
      id: notification.id || Date.now().toString(),
      type: notification.type,
      title: notification.title,
      message: notification.message,
      campaignId: notification.campaign_id
    };
    
    setToasts(prev => [...prev, toast]);
  };

  const removeToast = (toastId) => {
    setToasts(prev => prev.filter(t => t.id !== toastId));
  };

  const handleToastClick = (toast) => {
    // Navigate to campaign when toast is clicked
    if (toast.campaignId) {
      // Could navigate to specific campaign
      setCurrentView('campaigns');
    }
    removeToast(toast.id);
  };

  // ==========================================
  // 🔄 Campaign Regeneration Functions
  // ==========================================
  
  // Regenerate entire campaign with same settings
  const handleRegenerateCampaign = async (campaign) => {
    try {
      // ALWAYS fetch full campaign data for regeneration to ensure all product fields are present
      // Dashboard uses projection that may not include all fields (geography, video_languages, etc.)
      console.log('📥 Fetching full campaign data for regeneration...');
      const fullCampaign = await fetchFullCampaignData(campaign.id, sessionToken);
      console.log('📥 Full campaign data received:', fullCampaign);
      
      // DEBUG: Log the original campaign product data
      console.log('🔍 Original campaign product data for regeneration:', {
        geography: fullCampaign.product?.geography,
        geographies: fullCampaign.product?.geographies,
        video_languages: fullCampaign.product?.video_languages,
        key_benefits: fullCampaign.product?.key_benefits,
        budget_range: fullCampaign.product?.budget_range,
        interests_behaviors: fullCampaign.product?.interests_behaviors,
        industry_business_type: fullCampaign.product?.industry_business_type
      });
      
      // Extract ads - EXCLUDE versions/variations (only count original ads)
      const generatedAds = fullCampaign.generated_ads || [];
      const originalAds = generatedAds.filter(ad => !ad.is_variation && (!ad.version || ad.version === 1));
      
      console.log('📊 Ad counts:', {
        totalAds: generatedAds.length,
        originalAds: originalAds.length,
        variations: generatedAds.length - originalAds.length
      });
      
      // Get persona count
      let personaCount = fullCampaign.num_personas || 0;
      if (personaCount === 0 && fullCampaign.personas && fullCampaign.personas.length > 0) {
        personaCount = fullCampaign.personas.length;
      }
      if (personaCount === 0) {
        // Count unique personas from original ads only
        const uniquePersonas = [...new Set(originalAds.filter(ad => ad.target_persona).map(ad => ad.target_persona))];
        personaCount = uniquePersonas.length > 0 ? uniquePersonas.length : 1;
      }
      
      // Get unique ad types from original ads only
      const uniqueAdTypes = [...new Set(originalAds.map(ad => ad.ad_type))];
      const adTypes = uniqueAdTypes.length > 0 ? uniqueAdTypes : (fullCampaign.ad_types || ['social_media']);
      const adTypesCount = adTypes.length;
      
      // Calculate total ads to regenerate (ad types × personas)
      const totalAds = adTypesCount * personaCount;
      
      // Calculate credit breakdown by ad type
      const creditsByType = {
        text: 0,
        image: 0,
        video: 0
      };
      
      const textTypes = ['whatsapp_ad', 'social_media', 'facebook_ad', 'instagram_ad', 'linkedin_ad', 'tiktok_script', 'telegram_ad', 'google_ads', 'email_marketing', 'product_description'];
      const imageTypes = ['image_promo'];
      const videoTypes = ['video_promo', 'video_ad', 'tiktok_video', 'youtube_short'];
      
      adTypes.forEach(adType => {
        if (textTypes.includes(adType)) {
          creditsByType.text += 3 * personaCount;
        } else if (imageTypes.includes(adType)) {
          creditsByType.image += 15 * personaCount;
        } else if (videoTypes.includes(adType)) {
          creditsByType.video += 50 * personaCount;
        }
      });
      
      console.log('🔄 Regenerate Campaign Info:', { 
        adTypes, 
        adTypesCount, 
        personaCount, 
        totalAds,
        creditsByType,
        originalAdsCount: originalAds.length,
        // Log full product data to debug missing fields
        fullProductData: fullCampaign.product
      });
      
      // 💰 Fetch credit cost preview
      let creditInfo = '';
      let canGenerate = true;
      let creditPreview = null;
      
      try {
        console.log('💰 Fetching campaign regenerate credit preview...');
        // Pass the full product data for accurate credit calculation
        const productForPreview = {
          ...fullCampaign.product,
          enable_personas: personaCount > 1,
          persona_count: personaCount > 1 ? String(personaCount) : '1',
          image_model: fullCampaign.product?.image_model || 'nebius',
          nano_banana_resolution: fullCampaign.product?.nano_banana_resolution || '4K'
        };
        
        creditPreview = await previewCampaignRegenerateCost(
          adTypes,
          personaCount,
          fullCampaign.product?.image_model || 'nebius',
          fullCampaign.product?.nano_banana_resolution || '4K',
          sessionToken,
          productForPreview
        );
        console.log('💰 Campaign credit preview received:', creditPreview);
        
        creditInfo = formatCreditInfo(creditPreview);
        canGenerate = creditPreview.can_generate;
        
      } catch (previewError) {
        console.error('❌ Error fetching credit preview:', previewError);
        creditInfo = '\n\n💰 Credit cost: Unable to fetch';
      }
      
      const netCost = creditPreview?.cost_breakdown?.net_cost || 0;
      
      // Build ad types description with counts
      const adTypeDetails = adTypes.map(t => {
        const displayName = t.replace('_', ' ');
        return `${displayName} × ${personaCount}`;
      }).join(', ');
      
      // Show feedback modal so the user can optionally describe what to change
      // (e.g. "Make all ad copy more aggressive", "Fix spelling of MyBrand").
      const baseDescription = `Regenerate this entire campaign with fresh AI content?\n\nThis will create a NEW campaign with:\n• ${adTypesCount} ad type${adTypesCount > 1 ? 's' : ''} (${adTypeDetails})\n• ${personaCount} persona${personaCount > 1 ? 's' : ''}\n• Total: ${totalAds} new ad${totalAds > 1 ? 's' : ''}\n\nYour original campaign will remain unchanged.${creditInfo}`;
      
      if (!canGenerate) {
        // Insufficient credits — simple confirm modal, no feedback prompt
        setConfirmModal({
          isOpen: true,
          title: '🔄 Regenerate Campaign',
          message: baseDescription,
          confirmText: 'Buy Credits',
          cancelText: 'Cancel',
          onConfirm: () => {
            setConfirmModal(prev => ({ ...prev, isOpen: false }));
            if (window.handleNavigation) window.handleNavigation('credits');
          },
        });
      } else {
        // Use the same RegenerateWithFeedbackModal as per-ad regen for consistency.
        setRegenFeedbackModal({
          isOpen: true,
          title: '🔄 Regenerate Campaign',
          description: baseDescription,
          confirmText: `Regenerate${netCost ? ` (${netCost} credits)` : ''}`,
          isProcessing: false,
          onConfirm: async (userFeedback) => {
            setRegenFeedbackModal(prev => ({ ...prev, isProcessing: true }));
            try {
              await executeRegenerateCampaign(fullCampaign, totalAds, adTypes, personaCount, userFeedback);
            } finally {
              setRegenFeedbackModal(prev => ({ ...prev, isOpen: false, isProcessing: false }));
            }
          },
        });
      }
    } catch (error) {
      console.error('❌ Error in handleRegenerateCampaign:', error);
      setConfirmModal({
        isOpen: true,
        title: '❌ Error',
        message: 'Failed to load campaign data. Please try again.',
        confirmText: 'OK',
        cancelText: '',
        onConfirm: () => setConfirmModal(prev => ({ ...prev, isOpen: false }))
      });
    }
  };

  const executeRegenerateCampaign = async (campaign, totalAds, adTypes, personaCount, userFeedback = '') => {

    try {
      setIsGenerating(true);
      
      // Build complete product data with all fields from original campaign
      const productData = { 
        ...campaign.product,
        // Ensure persona settings are properly set
        enable_personas: personaCount > 1,
        persona_count: personaCount > 1 ? String(personaCount) : '1',
      };
      
      // Log what we're sending for debugging
      console.log('🔄 Regenerating campaign with:', {
        productData,
        adTypes,
        personaCount,
        totalAds,
        video_languages: productData.video_languages,
        has_user_feedback: !!(userFeedback && userFeedback.trim()),
      });
      
      const requestBody = {
        product: productData,
        ad_types: adTypes,
        num_personas: personaCount,
      };
      // Optional: pass through user's free-form instructions ("fix spelling of X",
      // "make all tone more urgent", etc.). Empty = original behaviour.
      if (userFeedback && userFeedback.trim()) {
        requestBody.user_feedback = userFeedback.trim();
      }
      const response = await axios.post(
        `${API}/generate-ads`,
        requestBody,
        { headers: { Authorization: `Bearer ${sessionToken}` } }
      );
      
      // Handle both synchronous and background generation
      if (response.data._background_generation) {
        // Background generation - show notification modal
        setSuccessCelebration({
          isOpen: true,
          title: '🎨 Campaign Queued!',
          message: `Generating ${totalAds} ad${totalAds > 1 ? 's' : ''} in the background.\n\nYou can continue working and will be notified when it's ready.\nCheck your notifications bell 🔔 for updates.`,
          icon: '⏳',
          celebrationType: 'info'
        });
        
        const pollInterval = setInterval(async () => {
          try {
            const statusResponse = await axios.get(
              `${API}/campaigns/${response.data.id}/status`,
              { headers: { Authorization: `Bearer ${sessionToken}` } }
            );
            
            // Status endpoint returns `status` (not `generation_status`).
            // Previously the wrong field was checked → polling never detected
            // completion → modal sat forever and the user saw it auto-dismiss
            // after the 2-min clearInterval timeout below.
            const _polledStatus = statusResponse.data.status;
            if (_polledStatus === 'completed') {
              clearInterval(pollInterval);
              setRefreshDashboard(prev => prev + 1);
              setSuccessCelebration({
                isOpen: true,
                title: '✅ Campaign Ready!',
                message: `Your new campaign with ${totalAds} ad${totalAds > 1 ? 's' : ''} has been generated successfully!\n\nCheck your campaigns dashboard!`,
                icon: '🎉',
                celebrationType: 'success'
              });
            } else if (_polledStatus === 'failed') {
              clearInterval(pollInterval);
              setSuccessCelebration({
                isOpen: true,
                title: '❌ Generation Failed',
                message: 'Something went wrong. Please try again or contact support if the issue persists.',
                icon: '⚠️',
                celebrationType: 'error'
              });
            }
          } catch (err) {
            console.error('Error polling campaign status:', err);
          }
        }, 3000);
        
        // Stop polling after 10 minutes (was 2 min — too short for video campaigns).
        setTimeout(() => clearInterval(pollInterval), 600000);
        
      } else {
        // Synchronous generation - immediate success
        setRefreshDashboard(prev => prev + 1);
        setSuccessCelebration({
          isOpen: true,
          title: '✅ Campaign Generated!',
          message: `Your new campaign with ${totalAds} ad${totalAds > 1 ? 's' : ''} is ready!\n\nCheck your campaigns dashboard!`,
          icon: '🎉',
          celebrationType: 'success'
        });
      }
      
    } catch (err) {
      console.error('Error regenerating campaign:', err);
      setSuccessCelebration({
        isOpen: true,
        title: '❌ Generation Failed',
        message: err.response?.data?.detail || 'An error occurred. Please try again.',
        icon: '⚠️',
        celebrationType: 'error'
      });
    } finally {
      setIsGenerating(false);
    }
  };

  // Regenerate individual ad (single ad type/persona combination)
  const handleRegenerateIndividualAd = async (campaign, adType, personaName = null, targetLanguage = null) => {
    try {
      // Prevent double submissions
      if (isGenerating) {
        console.log('⚠️ Regeneration already in progress, ignoring duplicate click');
        return;
      }
      
      console.log('🔄 handleRegenerateIndividualAd called', { campaign, adType, personaName, targetLanguage });
      
      // Calculate version number
      const baseProductName = campaign.product.product_name ? campaign.product.product_name.split(' (v')[0] : campaign.product.product_name;
      let versionNumber = 2;
      const versionMatch = campaign.product.product_name ? campaign.product.product_name.match(/\(v(\d+)\)/) : null;
      if (versionMatch) {
        versionNumber = parseInt(versionMatch[1]) + 1;
      }
      
      const adTypeDisplay = adType.replace('_', ' ');
      const adTypeCapitalized = adTypeDisplay.charAt(0).toUpperCase() + adTypeDisplay.slice(1);
      
      // 💰 Fetch credit cost preview
      let creditInfo = '';
      let canGenerate = true;
      let creditPreview = null;
      
      try {
        console.log('💰 Fetching regenerate credit preview...');
        creditPreview = await previewRegenerateCost(campaign.id, adType, sessionToken);
        console.log('💰 Credit preview received:', creditPreview);
        
        const { cost_breakdown, current_balance, can_generate, is_power_user, shortfall } = creditPreview;
        canGenerate = can_generate;
        
        if (is_power_user) {
          creditInfo = '\n\n⭐ **Power User** - No credits will be charged';
        } else {
          const netCost = cost_breakdown?.net_cost || 0;
          const freeDiscount = cost_breakdown?.free_tier_discount?.total_credits || 0;
          
          if (freeDiscount > 0) {
            creditInfo = `\n\n💰 **Credit Cost:** ${netCost} credits (Free tier applied! 🎁)\n📊 **Your Balance:** ${current_balance} credits`;
          } else {
            creditInfo = `\n\n💰 **Credit Cost:** ${netCost} credits\n📊 **Your Balance:** ${current_balance} credits`;
          }
          
          if (!can_generate) {
            creditInfo += `\n\n⚠️ **Insufficient Credits!** You need ${shortfall} more credits.`;
          } else {
            creditInfo += `\n✅ **After regeneration:** ${current_balance - netCost} credits`;
          }
        }
      } catch (previewError) {
        console.error('❌ Error fetching credit preview:', previewError);
        // If 402 error, show insufficient credits message
        if (previewError.response?.status === 402) {
          const detail = previewError.response.data?.detail;
          creditInfo = `\n\n⚠️ **Insufficient Credits!**\nRequired: ${detail?.required || 'Unknown'} credits\nYour Balance: ${detail?.current_balance || 0} credits`;
          canGenerate = false;
        } else {
          // For other errors, proceed without credit info
          creditInfo = '\n\n💰 Credit cost: Unable to fetch (proceeding anyway)';
        }
      }
      
      // Build description for the feedback modal
      const baseDescription = personaName
        ? `Generate a fresh ${adTypeDisplay} for "${personaName}".\nThis will create version ${versionNumber} with ONE new ad variation.`
        : `Regenerate a fresh ${adTypeDisplay}.\nThis will create version ${versionNumber} with ONE new ad.`;
      
      if (!canGenerate) {
        // Insufficient credits — keep the simple confirm modal flow (no feedback needed)
        setConfirmModal({
          isOpen: true,
          title: `✨ Regenerate ${adTypeCapitalized}`,
          message: baseDescription + creditInfo,
          confirmText: 'Buy Credits',
          cancelText: 'Cancel',
          onConfirm: () => {
            setConfirmModal(prev => ({ ...prev, isOpen: false }));
            if (window.handleNavigation) window.handleNavigation('credits');
          },
        });
      } else {
        // Show the new feedback modal so the user can optionally tell the AI
        // what to fix/change (spelling, tone, content). Empty feedback = same
        // behaviour as before.
        console.log('✅ Showing regenerate-with-feedback modal');
        setRegenFeedbackModal({
          isOpen: true,
          title: `✨ Regenerate ${adTypeCapitalized}`,
          description: baseDescription + creditInfo,
          confirmText: `Regenerate${creditPreview?.cost_breakdown?.net_cost ? ` (${creditPreview.cost_breakdown.net_cost} credits)` : ''}`,
          isProcessing: false,
          onConfirm: async (userFeedback) => {
            setRegenFeedbackModal(prev => ({ ...prev, isProcessing: true }));
            try {
              await executeRegenerateIndividualAd(campaign, adType, personaName, versionNumber, targetLanguage, userFeedback);
            } finally {
              setRegenFeedbackModal(prev => ({ ...prev, isOpen: false, isProcessing: false }));
            }
          },
        });
      }
      
    } catch (error) {
      console.error('❌ Error in handleRegenerateIndividualAd:', error);
    }
  };

  const executeRegenerateIndividualAd = async (campaign, adType, personaName, versionNumber, targetLanguage = null, userFeedback = '') => {
    try {
      setIsGenerating(true);
      
      // Find the original ad to get its persona data
      const originalAd = campaign.generated_ads.find(ad => 
        ad.ad_type === adType && ad.target_persona === personaName
      );
      
      console.log('🔍 Original ad found:', originalAd);
      
      // Find the full persona object from the campaign's personas array
      let personaData = null;
      if (personaName && campaign.personas && campaign.personas.length > 0) {
        personaData = campaign.personas.find(p => p.name === personaName);
        console.log('🎯 Found persona data from campaign:', personaData);
      }
      
      // If no persona found in campaign, create one from the original ad info
      if (!personaData && personaName && originalAd) {
        console.log('⚠️ Creating persona from original ad context');
        personaData = {
          name: personaName,
          age_range: `${campaign.product.age_range_min || 25}-${campaign.product.age_range_max || 45}`,
          gender: campaign.product.gender || 'Any',
          location: campaign.product.geography || 'India',
          occupation: 'Target customer',
          income_level: 'Middle to upper',
          lifestyle: 'Active lifestyle',
          pain_points: ['Looking for solutions'],
          motivations: ['Quality and value'],
          shopping_behaviors: ['Online research'],
          preferred_platforms: ['whatsapp'],
          communication_style: 'Conversational',
          interests: ['Relevant to product']
        };
      }
      
      const adTypeDisplay = adType.replace('_', ' ');
      
      // Use the dedicated regenerate endpoint that preserves persona
      // skip_credit_check: true because credit was already checked in the preview modal
      const requestData = {
        ad_type: adType,
        persona_name: personaName,
        persona: personaData,
        version: versionNumber,
        skip_credit_check: true  // Credits already previewed and confirmed
      };
      // Optional: regenerate image_promo in a specific language (Ultra Image / GPT Image 2)
      if (targetLanguage) {
        requestData.target_language = targetLanguage;
      }
      // Optional: free-form user feedback ("fix spelling of AdElevate",
      // "make tone more urgent", etc.). Empty string = same behaviour as before.
      if (userFeedback && userFeedback.trim()) {
        requestData.user_feedback = userFeedback.trim();
      }
      
      console.log('📤 Sending regenerate request:', requestData);
      
      // Use the modularized regenerateAd service function
      const updatedCampaign = await regenerateAd(campaign.id, requestData, sessionToken);
      
      console.log('📥 Regenerate response:', updatedCampaign);
      
      // Success! The backend returns the updated campaign.
      // Always update both `selectedCampaign` AND `adResults` so when the user
      // navigates back to the campaign detail view, they immediately see the
      // newest version (no stale snapshot). The previous guard skipped the
      // update if `selectedCampaign` was null, causing the user to see the
      // old video card after regeneration completed.
      if (!selectedCampaign || selectedCampaign.id === campaign.id) {
        setSelectedCampaign(updatedCampaign);
      }

      // Also update adResults if this is the current campaign in the results view
      if (adResults && adResults.id === campaign.id) {
        console.log('📊 Updating adResults with new campaign data');
        setAdResults(updatedCampaign);
      }
      
      setRefreshDashboard(prev => prev + 1);
      
      // Check if video ad was regenerated but only got a script (no actual video)
      const isVideoAd = adType === 'video_promo' || adType === 'video_ad';
      const newAds = updatedCampaign.generated_ads || [];
      const latestAd = newAds.length > 0 ? newAds[newAds.length - 1] : null;
      const hasActualVideo = latestAd && latestAd.video_url && 
                             latestAd.video_url.startsWith('http') && 
                             !latestAd.video_url.includes('data:image');
      
      // Check for image ads too
      const isImageAd = adType === 'image_promo';
      const hasActualImage = latestAd && latestAd.image_url && 
                             (latestAd.image_url.startsWith('http') || latestAd.image_url.startsWith('data:image'));
      
      if (isVideoAd && !hasActualVideo) {
        // Video ad regeneration returned script only
        setSuccessCelebration({
          isOpen: true,
          title: '⚠️ Video Generation Issue',
          message: `Video generation service encountered an issue.\n\n${personaName ? `Persona: "${personaName}"` : ''}\n\nA video script has been saved. Please try again later or check your Kie AI credits.`,
          icon: '📹',
          celebrationType: 'warning'
        });
      } else if (isImageAd && !hasActualImage) {
        // Image ad regeneration failed
        setSuccessCelebration({
          isOpen: true,
          title: '⚠️ Image Generation Issue',
          message: `Image generation service encountered an issue.\n\nPlease try again later.`,
          icon: '🖼️',
          celebrationType: 'warning'
        });
      } else {
        setSuccessCelebration({
          isOpen: true,
          title: '✅ New Version Added!',
          message: personaName
            ? `New ${adTypeDisplay} version (v${versionNumber}) added for "${personaName}"!\n\nYou can now see all versions in this campaign.`
            : `New ${adTypeDisplay} version (v${versionNumber}) added!\n\nYou can now see all versions in this campaign.`,
          icon: '🎉',
          celebrationType: 'success'
        });
      }
      
    } catch (err) {
      console.error('Error regenerating ad:', err);
      setSuccessCelebration({
        isOpen: true,
        title: '❌ Generation Failed',
        message: err.response?.data?.detail || 'Failed to regenerate ad. Please try again.',
        icon: '⚠️',
        celebrationType: 'error'
      });
    } finally {
      setIsGenerating(false);
    }
  };

  // Poll for new notifications and show toasts
  useEffect(() => {
    if (!isAuthenticated || !sessionToken) return;

    let isFirstLoad = true;
    const shownToastIds = new Set(); // Track which notifications we've already shown toasts for

    const checkForNewNotifications = async () => {
      try {
        const response = await axios.get(`${API}/notifications?limit=5`, {
          headers: { Authorization: `Bearer ${sessionToken}` }
        });
        
        const notifications = response.data || [];
        
        // On first load, DON'T show any toasts (they'll see them in notification bell)
        // After that, only show new ones since last check that we haven't shown yet
        const newNotifications = notifications.filter(notif => {
          // Skip first load - don't show old notifications as toasts
          if (isFirstLoad) {
            return false;
          }
          
          // After first load, only show notifications that:
          // 1. Are newer than our last check
          // 2. Are unread
          // 3. We haven't already shown as a toast
          const notifTime = new Date(notif.created_at).getTime();
          return notifTime > lastNotificationCheck && 
                 !notif.read && 
                 !shownToastIds.has(notif.id);
        });

        // Show toast for each new notification and track it
        newNotifications.forEach(notif => {
          addToast(notif);
          shownToastIds.add(notif.id);
        });

        // Update last check time and mark first load as complete
        if (newNotifications.length > 0 || isFirstLoad) {
          setLastNotificationCheck(Date.now());
          isFirstLoad = false;
        }
      } catch (error) {
        // Silently fail to avoid console clutter
        // Don't log 401 (unauthorized) or 404 (not found) errors
        if (error.response?.status !== 401 && error.response?.status !== 404) {
          console.error('Error checking notifications:', error);
        }
      }
    };

    // Initial check
    checkForNewNotifications();

    // Poll every 15 seconds
    const interval = setInterval(checkForNewNotifications, 15000);

    return () => clearInterval(interval);
  }, [isAuthenticated, sessionToken]);
  // ==========================================

  // Check if current URL is admin route or credits route
  useEffect(() => {
    const currentPath = window.location.pathname;
    if (currentPath === '/admin' && !isAdmin) {
      setCurrentView('admin-login');
    }
    // Handle Stripe payment return - redirect to credits page
    if (currentPath === '/credits' || currentPath === '/credits/success' || currentPath.startsWith('/credits/')) {
      if (user) {
        // Only set credits view if user is logged in
        setCurrentView('credits');
      }
    }
    // Handle subscription success page
    if (currentPath === '/subscription/success' || currentPath.startsWith('/subscription/')) {
      if (user) {
        setCurrentView('subscription-success');
      }
    }
  }, [isAdmin, user]);

  // Load user achievement stats when user is logged in
  useEffect(() => {
    if (user) {
      loadUserAchievementStats();
    }
  }, [user]);

  const handleAdGeneration = async (adResponse) => {
    setIsGenerating(true);
    setError(null);
    
    try {
      // Process the ad response to ensure proper persona-image mapping and prevent caching issues
      const processedResponse = {
        ...adResponse,
        generated_ads: adResponse.generated_ads?.map(ad => ({
          ...ad,
          // Add unique identifier to prevent browser caching issues with persona images
          _cacheBreaker: Date.now(),
          // Ensure target_persona is properly set for display
          target_persona: ad.target_persona || 'default'
        })) || []
      };
      
      console.log('🎯 Processed campaign response with persona-image mapping:', {
        totalAds: processedResponse.generated_ads.length,
        personas: processedResponse.personas?.map(p => ({ name: p.name, gender: p.gender })) || [],
        adTypes: [...new Set(processedResponse.generated_ads.map(ad => ad.ad_type))]
      });
      
      setAdResults(processedResponse);
      
      // Navigate to results UNLESS user clicked "Go to Dashboard" button
      console.log('🔍 ProductForm Navigation Decision:');
      console.log('   window.userChoseBackgroundMode:', window.userChoseBackgroundMode);
      
      if (window.userChoseBackgroundMode === true) {
        console.log('✅ User went to dashboard - NOT navigating');
        // User already on dashboard, just refresh
      } else {
        console.log('✅ User stayed on modal - navigating to results screen');
        setCurrentView('results');
      }
      
      // Check for newly unlocked achievements
      setTimeout(() => {
        checkForNewAchievements();
      }, 1000); // Small delay to ensure backend has processed the activity
      
      // Trigger dashboard refresh after successful campaign creation
      setRefreshDashboard(prev => prev + 1);
      
      // Only scroll to results if navigating to results view
      if (window.userChoseBackgroundMode !== true) {
        setTimeout(() => {
          const resultsSection = document.querySelector('#results');
          if (resultsSection) {
            resultsSection.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      }
      
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate ads. Please try again.');
      console.error('Error generating ads:', err);
    } finally {
      // Only close modal if user didn't choose background mode
      setTimeout(() => {
        // Check if user clicked "Go to Dashboard" for background generation
        if (window.userChoseBackgroundMode !== true) {
          setIsGenerating(false);
          setGenerationProgress({
            step: 0,
            total: 0,
            currentStage: '',
            currentAdType: '',
            currentPersona: '',
            completedAds: [],
            message: '',
            startTime: null,
            hasPersonas: false,
            personaCount: 0
          });
        }
        // If user chose background mode, modal stays open for status polling
      }, 1500); // 1.5 second delay to show completion
    }
  };

  const handleClearForm = () => {
    setAdResults(null);
    setError(null);
    setClearFormTrigger(prev => prev + 1);
    // Trigger dashboard refresh when clearing form
    setRefreshDashboard(prev => prev + 1);
  };

  const handleRegenerate = async (regenerationData, campaignResults = null) => {
    try {
      const adTypes = regenerationData.ad_types || ['social_media'];
      const adTypesCount = adTypes.length;
      const personaCount = regenerationData.product.enable_personas 
        ? (regenerationData.product.persona_count === 'ai_determined' ? 2 : parseInt(regenerationData.product.persona_count) || 1)
        : 1;
      const totalAds = adTypesCount * personaCount;
      
      // 💰 Fetch credit cost preview
      let creditInfo = '';
      let canGenerate = true;
      let creditPreview = null;
      
      try {
        console.log('💰 Fetching campaign regenerate credit preview...');
        // Pass the full product data for accurate credit calculation
        const productForPreview = {
          ...regenerationData.product,
          enable_personas: personaCount > 1,
          persona_count: personaCount > 1 ? String(personaCount) : '1'
        };
        
        creditPreview = await previewCampaignRegenerateCost(
          adTypes,
          personaCount,
          regenerationData.product?.image_model || 'nebius',
          regenerationData.product?.nano_banana_resolution || '4K',
          sessionToken,
          productForPreview
        );
        console.log('💰 Campaign credit preview received:', creditPreview);
        
        creditInfo = formatCreditInfo(creditPreview);
        canGenerate = creditPreview.can_generate;
        
      } catch (previewError) {
        console.error('❌ Error fetching credit preview:', previewError);
        creditInfo = '\n\n💰 Credit cost: Unable to fetch';
      }
      
      const netCost = creditPreview?.cost_breakdown?.net_cost || 0;
      
      // Show confirmation modal with credit info
      setConfirmModal({
        isOpen: true,
        title: '🔄 Regenerate Campaign',
        message: `Regenerate this campaign with fresh AI content?\n\nThis will create a NEW campaign with:\n• ${adTypesCount} ad type${adTypesCount > 1 ? 's' : ''}\n• ${personaCount > 1 ? personaCount + ' persona variations\n' : ''}• Total: ${totalAds} new ad${totalAds > 1 ? 's' : ''}\n\nYour original campaign will remain unchanged.${creditInfo}`,
        confirmText: canGenerate ? `Regenerate${netCost ? ` (${netCost} credits)` : ''}` : 'Buy Credits',
        cancelText: 'Cancel',
        onConfirm: canGenerate 
          ? async () => {
              setConfirmModal(prev => ({ ...prev, isOpen: false }));
              await executeRegenerate(regenerationData);
            }
          : () => {
              setConfirmModal(prev => ({ ...prev, isOpen: false }));
              // Navigate to credits page
              if (window.handleNavigation) {
                window.handleNavigation('credits');
              }
            }
      });
      
    } catch (error) {
      console.error('❌ Error in handleRegenerate:', error);
    }
  };

  const executeRegenerate = async (regenerationData) => {
    try {
      // ALWAYS reset flag at the START of each new campaign generation (including regeneration)
      window.userChoseBackgroundMode = false;
      console.log('🔄 Regenerate: New campaign starting - Reset userChoseBackgroundMode to false');
      
      // Show progress overlay with proper state
      setIsGenerating(true);
      setError(null);
      setGenerationProgress({
        isVisible: true,
        currentStage: 'content_generation',
        step: 0,
        total: regenerationData.ad_types.length,
        message: 'AI is regenerating your campaign...',
        personaCount: regenerationData.product.enable_personas ? (regenerationData.product.persona_count === 'ai_determined' ? 2 : parseInt(regenerationData.product.persona_count)) : 1,
        hasPersonas: regenerationData.product.enable_personas || false,
        startTime: Date.now(),
        actualProgress: 0
      });

      // Call the API to regenerate
      const response = await axios.post(`${API}/generate-ads`, regenerationData, {
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json'
        },
        timeout: 900000 // 15 minutes timeout for AI content regeneration
      });

      // ===============================================
      // 🚀 START POLLING FOR REGENERATION (SAME AS INITIAL GENERATION)
      // Backend creates stub and may use sync or async generation
      // ===============================================
      const campaignId = response.data.id;
      console.log('🔄 Regenerate: Starting campaign polling for:', campaignId);
      console.log('🔄 Regenerate: Current userChoseBackgroundMode:', window.userChoseBackgroundMode);
      
      // Store campaign ID for polling
      window.currentPollingCampaignId = campaignId;
      
      // Start polling for campaign status (exactly like initial generation)
      const pollCampaign = async () => {
        try {
          const statusResponse = await axios.get(
            `${API}/campaigns/${campaignId}/status`,
            { headers: { Authorization: `Bearer ${sessionToken}` } }
          );
          
          const { status, progress } = statusResponse.data;
          console.log(`📊 Regenerate: Campaign ${campaignId} polling: status=${status}, progress=${progress}%`);
          
          // Update progress UI with backend progress
          setGenerationProgress(prev => ({
            ...prev,
            actualProgress: progress,
            currentStage: status === 'completed' ? 'complete' : status === 'queued' ? 'initializing' : status === 'failed' ? 'error' : 'content_generation',
            message: status === 'generating' 
              ? `🎨 Regenerating your ads... ${progress}% complete`
              : status === 'completed'
              ? '✅ Campaign regeneration complete!'
              : status === 'queued'
              ? '⏳ Campaign queued for regeneration...'
              : status === 'failed'
              ? '❌ Campaign regeneration failed'
              : 'Processing...'
          }));
          
          // If completed, fetch full campaign data
          if (status === 'completed') {
            console.log('✅ Regenerate: Campaign status is COMPLETED');
            console.log('   User chose background mode:', window.userChoseBackgroundMode);
            
            // Add a small delay to show 100% completion before closing
            await new Promise(resolve => setTimeout(resolve, 1500));
            
            // Fetch complete campaign data with retry (handles replica lag, auth blips, etc.)
            const fetchResult = await fetchCampaignWithRetry(campaignId, sessionToken);
            if (fetchResult.ok) {
              console.log('   Complete campaign fetched successfully (with retry)');
              setAdResults(fetchResult.campaign);
              setRefreshDashboard(prev => prev + 1);
              
              if (window.userChoseBackgroundMode === true) {
                console.log('   User went to dashboard - modal already closed');
              } else {
                console.log('   User stayed on modal - closing modal and staying on results');
                setIsGenerating(false);
                setGenerationProgress({
                  step: 0,
                  total: 0,
                  currentStage: '',
                  isVisible: false
                });
                
                // Scroll to results
                setTimeout(() => {
                  const resultsSection = document.querySelector('#results');
                  if (resultsSection) {
                    resultsSection.scrollIntoView({ behavior: 'smooth' });
                  }
                }, 100);
              }
            } else {
              console.error('   fetchCampaignWithRetry failed:', fetchResult.reason);
              // If we have partial data still show it (failed campaigns will surface their own error state in AdResults)
              if (fetchResult.campaign) {
                setAdResults(fetchResult.campaign);
              }
              setRefreshDashboard(prev => prev + 1);
              setIsGenerating(false);
              setGenerationProgress({
                step: 0,
                total: 0,
                currentStage: 'error',
                message: fetchResult.reason === 'campaign_failed'
                  ? '❌ Regeneration failed. Please try again or check the dashboard for details.'
                  : '⚠️ Could not load the regenerated campaign right away. Please check your dashboard.',
                isVisible: false
              });
            }
            
            // Clear polling campaign ID
            window.currentPollingCampaignId = null;
            return true; // Stop polling
          } else if (status === 'failed') {
            console.error('❌ Regenerate: Campaign regeneration failed');
            setError('Campaign regeneration failed. Please try again.');
            setIsGenerating(false);
            setGenerationProgress({
              step: 0,
              total: 0,
              currentStage: 'error',
              message: 'Regeneration failed',
              isVisible: false
            });
            window.currentPollingCampaignId = null;
            return true; // Stop polling
          }
          
          return false; // Continue polling
        } catch (pollError) {
          console.error('Error polling campaign status:', pollError);
          return false;
        }
      };
      
      // Poll every 2 seconds
      const pollInterval = setInterval(async () => {
        const shouldStop = await pollCampaign();
        if (shouldStop) {
          clearInterval(pollInterval);
        }
      }, 2000);
      
      // Initial poll
      const shouldStop = await pollCampaign();
      if (shouldStop) {
        clearInterval(pollInterval);
      }
      
      // Don't execute the code below - polling handles everything
      return;
      
    } catch (error) {
      console.error('Regeneration failed:', error);
      setError(error.response?.data?.detail || 'Failed to regenerate campaign. Please try again.');
      setIsGenerating(false);
      setGenerationProgress({
        step: 0,
        total: 0,
        currentStage: 'error',
        message: 'Regeneration failed',
        isVisible: false
      });
    }
  };

  const handleViewCampaign = async (campaign) => {
    // CRITICAL FIX: Always fetch full campaign details with all product fields
    // The pagination endpoint returns limited fields for performance
    try {
      const response = await axios.get(`${API}/campaigns/${campaign.id}`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      setSelectedCampaign(response.data);
    } catch (error) {
      console.error('Error fetching full campaign details:', error);
      // Fallback to campaign from list if API call fails
      setSelectedCampaign(campaign);
    }
    setCurrentView('campaign-detail');
  };

  const handleBackToDashboard = () => {
    setSelectedCampaign(null);
    setCurrentView('campaigns');
  };

  const handleCreateNewCampaign = () => {
    setCurrentView('main');
    setSelectedCampaign(null);
    // Scroll to form after view change
    setTimeout(() => {
      const productForm = document.getElementById('product-form');
      if (productForm) {
        productForm.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  const handleViewAnalytics = () => {
    setCurrentView('analytics');
    setSelectedCampaign(null);
    // Scroll to analytics after view change
    setTimeout(() => {
      const analyticsSection = document.getElementById('analytics');
      if (analyticsSection) {
        analyticsSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  const handleBackToMain = () => {
    setCurrentView('campaigns');
    setSelectedCampaign(null);
    // Scroll to top after view change
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const handleNavigation = (viewId) => {
    setCurrentView(viewId);
    setSelectedCampaign(null);
    setAdResults(null);
    setError(null);
    
    // Update browser URL to match current view
    const urlMap = {
      'dashboard': '/',
      'campaigns': '/',
      'content-studio': '/',
      'main': '/',
      'credits': '/credits',
      'admin': '/admin',
      'analytics': '/',
      'results': '/',
      'whatsapp': '/'
    };
    const newUrl = urlMap[viewId] || '/';
    if (window.location.pathname !== newUrl) {
      window.history.pushState({}, document.title, newUrl);
    }
  };

  // Use refs to store current state setters so they're always up to date
  const creditPreviewSettersRef = useRef({
    setCreditPreviewData,
    setPendingGenerationData,
    setShowCreditPreview
  });
  
  // Update ref whenever setters change
  useEffect(() => {
    creditPreviewSettersRef.current = {
      setCreditPreviewData,
      setPendingGenerationData,
      setShowCreditPreview
    };
  }, [setCreditPreviewData, setPendingGenerationData, setShowCreditPreview]);
  
  // Expose handleNavigation and credit preview modal for other components
  useEffect(() => {
    console.log('🔧 useEffect running - setting up global functions');
    
    window.handleNavigation = handleNavigation;
    
    // Expose credit preview modal function - Using refs to access current state setters
    window.showCreditPreviewModal = (previewData, source) => {
      console.log('🎯 showCreditPreviewModal called - USING REFS');
      console.log('🎯 Data:', { previewData, source });
      
      // Get current state setters from ref
      const { setCreditPreviewData, setPendingGenerationData, setShowCreditPreview } = creditPreviewSettersRef.current;
      
      console.log('🎯 Calling setCreditPreviewData via ref...');
      setCreditPreviewData(previewData);
      
      console.log('🎯 Calling setPendingGenerationData via ref...');
      setPendingGenerationData({ source });
      
      console.log('🎯 Calling setShowCreditPreview(true) via ref...');
      setShowCreditPreview(true);
      
      console.log('🎯 All state setters called via ref - React should re-render now');
    };
    
    return () => {
      delete window.handleNavigation;
      delete window.showCreditPreviewModal;
    };
  }, []); // Empty deps - only set once, but uses refs for current setters

  // Handle browser back/forward buttons
  useEffect(() => {
    const handlePopState = () => {
      const path = window.location.pathname;
      if (path === '/credits') {
        setCurrentView('credits');
      } else if (path === '/admin') {
        setCurrentView('admin');
      } else if (path === '/blog') {
        setCurrentView('blog');
        setSelectedBlogPost(null);
      } else if (path.startsWith('/blog/')) {
        const slug = path.replace('/blog/', '');
        setCurrentView('blog-post');
        setSelectedBlogPost(slug);
      } else {
        setCurrentView('campaigns');
      }
    };
    
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Check if this is a password reset page
  const urlParams = new URLSearchParams(window.location.search);
  const resetToken = urlParams.get('token');
  const isPasswordResetPage = window.location.pathname.includes('reset-password') || resetToken;

  // Check if this is admin page
  const isAdminPage = window.location.pathname === '/admin';

  // Check if this is Google OAuth callback page
  const isGoogleAuthCallback = window.location.pathname === '/auth/google/callback';

  // Check if this is a hero preview page
  const isHeroPreviewPage = window.location.pathname === '/hero-preview';
  
  // Check if this is a Calm-style landing page preview
  const isCalmPreviewPage = window.location.pathname === '/calm-preview';

  // Check if this is a share page
  const shareMatch = window.location.pathname.match(/^\/share\/(.+)$/);
  const currentShareToken = shareMatch ? shareMatch[1] : null;
  const isSharePage = Boolean(currentShareToken);

  // Check if this is a public reel page (/reel/:slug)
  const isPublicReelPage = /^\/reel\/[^/?#]+$/.test(window.location.pathname);

  // Public reel — no auth required
  if (isPublicReelPage) {
    return <PublicReelView />;
  }

  // Handle Google OAuth callback first (before auth check)
  if (isGoogleAuthCallback) {
    return <GoogleAuthCallback />;
  }

  // Handle Hero Preview page
  if (isHeroPreviewPage) {
    return (
      <HeroMockups 
        onGetStarted={() => window.location.href = '/'} 
        initialVariant="hybrid"
      />
    );
  }

  // Handle Calm-Style Landing Page Preview
  if (isCalmPreviewPage) {
    return (
      <CalmStyleLandingPage 
        onGetStarted={() => window.location.href = '/'} 
        showcaseVideoUrl={null}
      />
    );
  }

  if (isPasswordResetPage) {
    return <PasswordResetPage />;
  }

  if (isSharePage) {
    return <PublicCampaignView shareToken={currentShareToken} />;
  }

  if (isAdminPage) {
    if (isAdmin) {
      return <AdminPanel onLogout={handleAdminLogout} adminToken={adminToken} />;
    } else {
      return <AdminLoginForm onLogin={handleAdminLogin} error={adminError} isLoading={adminLoading} />;
    }
  }

  // Check if this is a blog page
  const isBlogPage = window.location.pathname === '/blog';
  const blogPostMatch = window.location.pathname.match(/^\/blog\/(.+)$/);
  const isBlogPostPage = Boolean(blogPostMatch);
  const blogPostSlug = blogPostMatch ? blogPostMatch[1] : null;

  if (isBlogPage) {
    return (
      <BlogList 
        onSelectPost={(slug) => {
          window.history.pushState({}, '', `/blog/${slug}`);
          window.location.reload();
        }}
        onBack={() => {
          window.history.pushState({}, '', '/');
          window.location.reload();
        }}
      />
    );
  }

  if (isBlogPostPage) {
    return (
      <BlogPost 
        slug={blogPostSlug}
        onBack={() => {
          window.history.pushState({}, '', '/blog');
          window.location.reload();
        }}
        onSelectPost={(slug) => {
          window.history.pushState({}, '', `/blog/${slug}`);
          window.location.reload();
        }}
      />
    );
  }

  // Check if this is Product Hunt launch page
  const isLaunchPage = window.location.pathname === '/launch' || window.location.pathname === '/producthunt';
  
  if (isLaunchPage) {
    return <ProductHuntLaunchPage />;
  }

  if (isLoading) {
    return (
      <div className={`min-h-screen bg-gradient-to-br ${colorTheme === 'purple' ? 'from-purple-50 via-pink-50 to-purple-100' : 'from-green-50 via-emerald-50 to-green-100'} dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center`}>
        <div className="text-center">
          <div className={`animate-spin rounded-full h-32 w-32 border-b-2 ${colorTheme === 'purple' ? 'border-purple-600' : colorTheme === 'blue' ? 'border-blue-600' : 'border-green-600'} mx-auto`}></div>
          <p className="mt-4 text-gray-600 dark:text-gray-300">Loading AdElevate...</p>
        </div>
      </div>
    );
  }

  // Check for dashboard access - either authenticated user or testing parameter
  const shouldShowDashboard = isAuthenticated || window.location.search.includes('dashboard=true');
  
  // Check for showcase mockups page
  if (window.location.search.includes('mockups=true') || window.location.pathname === '/mockups') {
    const ShowcaseMockups = require('./components/pages/ShowcaseMockups').default;
    return <ShowcaseMockups />;
  }
  
  if (!shouldShowDashboard) {
    return <ModernLandingPage />;
  }

  // Render content based on current view
  const renderContent = () => {
    switch (currentView) {
      case 'results':
        return (
          <div className="min-h-screen py-8" data-testid="campaign-results-page">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="mb-8">
                <h2 className={`text-3xl font-bold bg-gradient-to-r ${colorTheme === 'purple' ? 'from-purple-600 to-pink-600' : colorTheme === 'blue' ? 'from-blue-600 to-sky-500' : 'from-emerald-600 to-teal-500'} bg-clip-text text-transparent mb-2`}>
                  Generated Content
                </h2>
                <p className="text-slate-500 dark:text-slate-400 text-sm">Your AI-generated marketing campaign is ready</p>
              </div>
              
              {adResults && (
                <AdResults 
                  results={adResults} 
                  onClearForm={() => {
                    setAdResults(null);
                    setCurrentView('content-studio');
                  }}
                  onShareIndividualAd={handleShareIndividualAd}
                  onShareEntireCampaign={handleShareEntireCampaign}
                  shareToTelegram={shareToTelegram}
                  showSharingModal={showSharingModal}
                  setShowSharingModal={setShowSharingModal}
                  sharingType={sharingType}
                  setSharingType={setSharingType}
                  selectedAd={selectedAd}
                  setSelectedAd={setSelectedAd}
                  sharingStatus={sharingStatus}
                  setSharingStatus={setSharingStatus}
                  setShowChannelSelectionModal={setShowChannelSelectionModal}
                  showChannelSelectionModal={showChannelSelectionModal}
                  copiedText={copiedText}
                  copyToClipboard={copyToClipboard}
                  saveStatus={saveStatus}
                  setSaveStatus={setSaveStatus}
                  // Inline editing props
                  editingField={editingField}
                  setEditingField={setEditingField}
                  editContent={editContent}
                  setEditContent={setEditContent}
                  startEditing={startEditing}
                  cancelEditing={cancelEditing}
                  saveEdit={saveEdit}
                  showContentHistory={showContentHistory}
                  onRegenerate={handleRegenerate}
                  onRegenerateIndividualAd={handleRegenerateIndividualAd}
                  isGenerating={isGenerating}
                />
              )}
            </div>
            
            {/* Generation Progress Modal for Results View */}
            {isGenerating && (
              <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
                <div className="bg-white/90 dark:bg-slate-800/90 backdrop-blur-xl rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl border border-slate-200/50 dark:border-slate-700/50">
                  <div className="text-center">
                    <div className={`animate-spin rounded-full h-16 w-16 border-4 ${colorTheme === 'purple' ? 'border-purple-200 border-t-purple-600' : colorTheme === 'blue' ? 'border-blue-200 border-t-blue-600' : 'border-green-200 border-t-green-600'} mx-auto mb-6`}></div>
                    
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                      🚀 AI Marketing Content Generation
                    </h3>
                    
                    <div className="mb-6">
                      {/* Show main message only once */}
                      <p className="text-gray-600 dark:text-gray-300 mb-3 font-medium text-base">
                        {generationProgress.message || 'Regenerating Marketing Content...'}
                      </p>
                      
                      {generationProgress.total > 0 && (
                        <div className="w-full">
                          {/* Enhanced Progress Stats - Don't repeat the message */}
                          <div className="flex justify-between text-sm mb-3 text-gray-500 dark:text-gray-400">
                            <span className="font-semibold text-gray-700 dark:text-gray-300">
                              {generationProgress.hasPersonas && generationProgress.personaCount > 1
                                ? `${generationProgress.step} of ${generationProgress.total} pieces`
                                : `${generationProgress.step} of ${generationProgress.total} marketing pieces`
                              }
                            </span>
                            {generationProgress.startTime && (
                              <span className={`${colorTheme === 'purple' ? 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200' : colorTheme === 'blue' ? 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200' : 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'} px-3 py-1 rounded-full text-xs font-bold`}>
                                {Math.floor((Date.now() - generationProgress.startTime) / 1000)}s
                              </span>
                            )}
                          </div>
                          
                          {/* Enhanced Progress Bar */}
                          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-4 mb-4 shadow-inner">
                            <div 
                              className={`bg-gradient-to-r ${colorTheme === 'purple' ? 'from-purple-500 to-pink-500' : colorTheme === 'blue' ? 'from-blue-500 to-sky-500' : 'from-green-500 to-emerald-500'} h-4 rounded-full transition-all duration-1000 ease-out shadow-lg flex items-center justify-end pr-3`}
                              style={{ width: `${generationProgress.actualProgress || Math.min(95, (generationProgress.step / generationProgress.total) * 100)}%` }}
                            >
                              {generationProgress.step > 0 && (
                                <span className="text-white text-xs font-bold">
                                  {generationProgress.actualProgress || Math.round((generationProgress.step / generationProgress.total) * 100)}%
                                </span>
                              )}
                            </div>
                          </div>
                          
                          {/* Enhanced Stage Indicators */}
                          <div className="flex items-center justify-center space-x-6 mb-4">
                            <div className={`flex flex-col items-center space-y-1 ${generationProgress.currentStage === 'initializing' ? (colorTheme === 'purple' ? 'text-purple-600' : colorTheme === 'blue' ? 'text-blue-600' : 'text-green-600') + ' font-bold' : 'text-gray-400'}`}>
                              <div className={`w-3 h-3 rounded-full ${['initializing', 'persona_generation', 'content_generation', 'finalizing', 'complete'].includes(generationProgress.currentStage) ? (colorTheme === 'purple' ? 'bg-purple-500 ring-2 ring-purple-300' : colorTheme === 'blue' ? 'bg-blue-500 ring-2 ring-blue-300' : 'bg-green-500 ring-2 ring-green-300') : 'bg-gray-300'}`}></div>
                              <span className="text-xs">Setup</span>
                            </div>
                            
                            {generationProgress.hasPersonas && (
                              <div className={`flex flex-col items-center space-y-1 ${generationProgress.currentStage === 'persona_generation' ? (colorTheme === 'purple' ? 'text-purple-600' : colorTheme === 'blue' ? 'text-blue-600' : 'text-green-600') + ' font-bold' : 'text-gray-400'}`}>
                                <div className={`w-3 h-3 rounded-full ${['persona_generation', 'content_generation', 'finalizing', 'complete'].includes(generationProgress.currentStage) ? (colorTheme === 'purple' ? 'bg-purple-500 ring-2 ring-purple-300' : colorTheme === 'blue' ? 'bg-blue-500 ring-2 ring-blue-300' : 'bg-green-500 ring-2 ring-green-300') : 'bg-gray-300'}`}></div>
                                <span className="text-xs">Personas</span>
                              </div>
                            )}
                            
                            <div className={`flex flex-col items-center space-y-1 ${generationProgress.currentStage === 'content_generation' ? (colorTheme === 'purple' ? 'text-purple-600' : colorTheme === 'blue' ? 'text-blue-600' : 'text-green-600') + ' font-bold' : 'text-gray-400'}`}>
                              <div className={`w-3 h-3 rounded-full ${['content_generation', 'finalizing', 'complete'].includes(generationProgress.currentStage) ? (colorTheme === 'purple' ? 'bg-purple-500 ring-2 ring-purple-300' : colorTheme === 'blue' ? 'bg-blue-500 ring-2 ring-blue-300' : 'bg-green-500 ring-2 ring-green-300') : 'bg-gray-300'}`}></div>
                              <span className="text-xs">Content</span>
                            </div>
                            
                            <div className={`flex flex-col items-center space-y-1 ${generationProgress.currentStage === 'finalizing' ? (colorTheme === 'purple' ? 'text-purple-600' : colorTheme === 'blue' ? 'text-blue-600' : 'text-green-600') + ' font-bold' : 'text-gray-400'}`}>
                              <div className={`w-3 h-3 rounded-full ${['finalizing', 'complete'].includes(generationProgress.currentStage) ? (colorTheme === 'purple' ? 'bg-purple-500 ring-2 ring-purple-300' : colorTheme === 'blue' ? 'bg-blue-500 ring-2 ring-blue-300' : 'bg-green-500 ring-2 ring-green-300') : 'bg-gray-300'}`}></div>
                              <span className="text-xs">Finalize</span>
                            </div>
                            
                            <div className={`flex flex-col items-center space-y-1 ${generationProgress.currentStage === 'complete' ? (colorTheme === 'purple' ? 'text-purple-600' : colorTheme === 'blue' ? 'text-blue-600' : 'text-green-600') + ' font-bold' : 'text-gray-400'}`}>
                              <div className={`w-3 h-3 rounded-full ${generationProgress.currentStage === 'complete' ? (colorTheme === 'purple' ? 'bg-purple-500 ring-2 ring-purple-300' : colorTheme === 'blue' ? 'bg-blue-500 ring-2 ring-blue-300' : 'bg-green-500 ring-2 ring-green-300') : 'bg-gray-300'}`}></div>
                              <span className="text-xs">Complete</span>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      <div className="flex items-center justify-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
                        <div className="flex space-x-1">
                          <div className={`w-2 h-2 ${colorTheme === 'purple' ? 'bg-purple-500' : colorTheme === 'blue' ? 'bg-blue-500' : 'bg-green-500'} rounded-full animate-pulse`}></div>
                          <div className={`w-2 h-2 ${colorTheme === 'purple' ? 'bg-purple-500' : colorTheme === 'blue' ? 'bg-blue-500' : 'bg-green-500'} rounded-full animate-pulse`} style={{animationDelay: '0.1s'}}></div>
                          <div className={`w-2 h-2 ${colorTheme === 'purple' ? 'bg-purple-500' : colorTheme === 'blue' ? 'bg-blue-500' : 'bg-green-500'} rounded-full animate-pulse`} style={{animationDelay: '0.2s'}}></div>
                        </div>
                        <span className="font-medium">
                          {generationProgress.currentStage === 'persona_generation' ? 'Creating buyer personas...' : 
                           generationProgress.currentStage === 'content_generation' ? 'AI is crafting your content...' :
                           generationProgress.currentStage === 'finalizing' ? 'Adding final touches...' :
                           'AI is working its magic...'}
                        </span>
                      </div>
                    </div>
                    
                    <div className="text-xs text-gray-400 dark:text-gray-500 bg-gray-50 dark:bg-gray-700 rounded-lg p-3 mb-4">
                      <p className="flex items-center justify-center">
                        <span className="mr-2">⚡</span>
                        Powered by Claude AI, ElevenLabs, and Replicate
                      </p>
                    </div>
                    
                    {/* Dashboard Button - Always available for user to navigate away */}
                    {generationProgress.currentStage !== 'complete' && (
                      <button
                        onClick={() => {
                          console.log('🚀 USER CLICKED: Go to Dashboard button');
                          
                          // Set flag to indicate user chose background mode
                          window.userChoseBackgroundMode = true;
                          console.log('   Set userChoseBackgroundMode = true');
                          
                          // Stop modal polling by clearing the campaign ID
                          if (window.currentPollingCampaignId) {
                            window.currentPollingCampaignId = null;
                            console.log('   Stopped modal polling');
                          }
                          
                          // Trigger dashboard refresh to show the new campaign
                          console.log('   Triggering dashboard refresh...');
                          setRefreshDashboard(prev => prev + 1);
                          
                          // IMMEDIATELY close modal and navigate
                          console.log('   Closing modal IMMEDIATELY and navigating to dashboard...');
                          setIsGenerating(false);
                          setGenerationProgress({
                            step: 0,
                            total: 0,
                            currentStage: '',
                            isVisible: false
                          });
                          setCurrentView('campaigns');
                          console.log('   Navigation complete - dashboard should show campaign with spinner');
                        }}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                        </svg>
                        Continue in Background & Go to Dashboard
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      
      case 'campaign-detail':
        return (
          <div className="min-h-screen" data-testid="campaign-detail-page">
            <CampaignDetail 
              campaign={selectedCampaign} 
              onBack={handleBackToDashboard} 
              onShareIndividualAd={handleShareIndividualAd}
              onShareEntireCampaign={handleShareEntireCampaign}
              shareToTelegram={shareToTelegram}
              showSharingModal={showSharingModal}
              setShowSharingModal={setShowSharingModal}
              onRegenerateCampaign={handleRegenerateCampaign}
              onRegenerateIndividualAd={handleRegenerateIndividualAd}
            />
          </div>
        );
      
      case 'analytics':
        return <AnalyticsV2 sessionToken={sessionToken} />;
      
      case 'main':
        return (
          <div className={`p-6 ${colorTheme === 'purple' ? 'bg-purple-50' : colorTheme === 'blue' ? 'bg-blue-50' : 'bg-green-50'} dark:bg-gray-900 min-h-screen`}>
            <div id="product-form">
              <ProductForm 
                onSubmit={handleAdGeneration} 
                isLoading={isGenerating} 
                clearTrigger={clearFormTrigger}
                generationProgress={generationProgress}
                setGenerationProgress={setGenerationProgress}
                isGenerating={isGenerating}
                setIsGenerating={setIsGenerating}
                setCreditPreviewData={setCreditPreviewData}
                setShowCreditPreview={setShowCreditPreview}
                setPendingGenerationData={setPendingGenerationData}
                pendingGenerationData={pendingGenerationData}
              />
            </div>
            
            {isGenerating && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl">
                  <div className="text-center">
                    <div className={`animate-spin rounded-full h-16 w-16 border-4 ${colorTheme === 'purple' ? 'border-purple-200 border-t-purple-600' : colorTheme === 'blue' ? 'border-blue-200 border-t-blue-600' : 'border-green-200 border-t-green-600'} mx-auto mb-6`}></div>
                    
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                      🚀 AI Marketing Content Generation
                    </h3>
                    
                    <div className="mb-6">
                      <p className="text-gray-600 dark:text-gray-300 mb-3 font-medium text-base">
                        {generationProgress.message || 'Generating Marketing Content...'}
                      </p>
                      
                      {generationProgress.total > 0 && (
                        <div className="w-full">
                          {/* Enhanced Progress Stats */}
                          <div className="flex justify-between text-sm mb-3 text-gray-500 dark:text-gray-400">
                            <span className="font-semibold text-gray-700 dark:text-gray-300">
                              {generationProgress.hasPersonas && generationProgress.personaCount > 1
                                ? generationProgress.message || `🎨 Generating your marketing campaign...`
                                : `${generationProgress.step} of ${generationProgress.total} marketing pieces`
                              }
                            </span>
                            {generationProgress.startTime && (
                              <span className={`${colorTheme === 'purple' ? 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200' : colorTheme === 'blue' ? 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200' : 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'} px-3 py-1 rounded-full text-xs font-bold`}>
                                {Math.floor((Date.now() - generationProgress.startTime) / 1000)}s
                              </span>
                            )}
                          </div>
                          
                          {/* Enhanced Progress Bar */}
                          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-4 mb-4 shadow-inner">
                            <div 
                              className={`bg-gradient-to-r ${colorTheme === 'purple' ? 'from-purple-500 to-pink-500' : colorTheme === 'blue' ? 'from-blue-500 to-sky-500' : 'from-green-500 to-emerald-500'} h-4 rounded-full transition-all duration-1000 ease-out shadow-lg flex items-center justify-end pr-3`}
                              style={{ width: `${generationProgress.actualProgress || Math.min(95, (generationProgress.step / generationProgress.total) * 100)}%` }}
                            >
                              {generationProgress.step > 0 && (
                                <span className="text-white text-xs font-bold">
                                  {generationProgress.actualProgress || Math.round((generationProgress.step / generationProgress.total) * 100)}%
                                </span>
                              )}
                            </div>
                          </div>
                          
                          {/* Enhanced Stage Indicators */}
                          <div className="flex items-center justify-center space-x-6 mb-4">
                            <div className={`flex flex-col items-center space-y-1 ${generationProgress.currentStage === 'initializing' ? (colorTheme === 'purple' ? 'text-purple-600' : colorTheme === 'blue' ? 'text-blue-600' : 'text-green-600') + ' font-bold' : 'text-gray-400'}`}>
                              <div className={`w-3 h-3 rounded-full ${['initializing', 'persona_generation', 'content_generation', 'finalizing', 'complete'].includes(generationProgress.currentStage) ? (colorTheme === 'purple' ? 'bg-purple-500 ring-2 ring-purple-300' : colorTheme === 'blue' ? 'bg-blue-500 ring-2 ring-blue-300' : 'bg-green-500 ring-2 ring-green-300') : 'bg-gray-300'}`}></div>
                              <span className="text-xs">Setup</span>
                            </div>
                            
                            {generationProgress.hasPersonas && (
                              <div className={`flex flex-col items-center space-y-1 ${generationProgress.currentStage === 'persona_generation' ? (colorTheme === 'purple' ? 'text-purple-600' : colorTheme === 'blue' ? 'text-blue-600' : 'text-green-600') + ' font-bold' : 'text-gray-400'}`}>
                                <div className={`w-3 h-3 rounded-full ${['persona_generation', 'content_generation', 'finalizing', 'complete'].includes(generationProgress.currentStage) ? (colorTheme === 'purple' ? 'bg-purple-500 ring-2 ring-purple-300' : colorTheme === 'blue' ? 'bg-blue-500 ring-2 ring-blue-300' : 'bg-green-500 ring-2 ring-green-300') : 'bg-gray-300'}`}></div>
                                <span className="text-xs">Personas</span>
                              </div>
                            )}
                            
                            <div className={`flex flex-col items-center space-y-1 ${generationProgress.currentStage === 'content_generation' ? (colorTheme === 'purple' ? 'text-purple-600' : colorTheme === 'blue' ? 'text-blue-600' : 'text-green-600') + ' font-bold' : 'text-gray-400'}`}>
                              <div className={`w-3 h-3 rounded-full ${['content_generation', 'finalizing', 'complete'].includes(generationProgress.currentStage) ? (colorTheme === 'purple' ? 'bg-purple-500 ring-2 ring-purple-300' : colorTheme === 'blue' ? 'bg-blue-500 ring-2 ring-blue-300' : 'bg-green-500 ring-2 ring-green-300') : 'bg-gray-300'}`}></div>
                              <span className="text-xs">Content</span>
                            </div>
                            
                            <div className={`flex flex-col items-center space-y-1 ${generationProgress.currentStage === 'finalizing' ? (colorTheme === 'purple' ? 'text-purple-600' : colorTheme === 'blue' ? 'text-blue-600' : 'text-green-600') + ' font-bold' : 'text-gray-400'}`}>
                              <div className={`w-3 h-3 rounded-full ${['finalizing', 'complete'].includes(generationProgress.currentStage) ? (colorTheme === 'purple' ? 'bg-purple-500 ring-2 ring-purple-300' : colorTheme === 'blue' ? 'bg-blue-500 ring-2 ring-blue-300' : 'bg-green-500 ring-2 ring-green-300') : 'bg-gray-300'}`}></div>
                              <span className="text-xs">Finalize</span>
                            </div>
                            
                            <div className={`flex flex-col items-center space-y-1 ${generationProgress.currentStage === 'complete' ? (colorTheme === 'purple' ? 'text-purple-600' : colorTheme === 'blue' ? 'text-blue-600' : 'text-green-600') + ' font-bold' : 'text-gray-400'}`}>
                              <div className={`w-3 h-3 rounded-full ${generationProgress.currentStage === 'complete' ? (colorTheme === 'purple' ? 'bg-purple-500 ring-2 ring-purple-300' : colorTheme === 'blue' ? 'bg-blue-500 ring-2 ring-blue-300' : 'bg-green-500 ring-2 ring-green-300') : 'bg-gray-300'}`}></div>
                              <span className="text-xs">Complete</span>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      <div className="flex items-center justify-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
                        <div className="flex space-x-1">
                          <div className={`w-2 h-2 ${colorTheme === 'purple' ? 'bg-purple-500' : colorTheme === 'blue' ? 'bg-blue-500' : 'bg-green-500'} rounded-full animate-pulse`}></div>
                          <div className={`w-2 h-2 ${colorTheme === 'purple' ? 'bg-purple-500' : colorTheme === 'blue' ? 'bg-blue-500' : 'bg-green-500'} rounded-full animate-pulse`} style={{animationDelay: '0.1s'}}></div>
                          <div className={`w-2 h-2 ${colorTheme === 'purple' ? 'bg-purple-500' : colorTheme === 'blue' ? 'bg-blue-500' : 'bg-green-500'} rounded-full animate-pulse`} style={{animationDelay: '0.2s'}}></div>
                        </div>
                        <span className="font-medium">
                          {generationProgress.currentStage === 'persona_generation' ? 'Creating buyer personas...' : 
                           generationProgress.currentStage === 'content_generation' ? 'AI is crafting your content...' :
                           generationProgress.currentStage === 'finalizing' ? 'Adding final touches...' :
                           'AI is working its magic...'}
                        </span>
                      </div>
                    </div>
                    
                    <div className="text-xs text-gray-400 dark:text-gray-500 bg-gray-50 dark:bg-gray-700 rounded-lg p-3 mb-4">
                      <p className="flex items-center justify-center">
                        <span className="mr-2">⚡</span>
                        Powered by Claude AI, ElevenLabs, and Replicate
                      </p>
                    </div>
                    
                    {/* Dashboard Button - Allow user to navigate while generation continues */}
                    {generationProgress.currentStage !== 'complete' && (
                      <button
                        onClick={() => {
                          console.log('🚀 ProductForm: USER CLICKED: Go to Dashboard button');
                          
                          // Set flag to indicate user chose background mode
                          window.userChoseBackgroundMode = true;
                          console.log('   Set userChoseBackgroundMode = true');
                          
                          // DON'T stop polling - let it continue in background
                          // The polling will check the flag when complete and won't navigate
                          console.log('   Keeping polling active in background');
                          
                          // Close modal immediately
                          console.log('   Closing modal and navigating to dashboard...');
                          setIsGenerating(false);
                          setGenerationProgress({
                            step: 0,
                            total: 0,
                            currentStage: '',
                            isVisible: false
                          });
                          setCurrentView('campaigns');
                          
                          // Trigger dashboard refresh after a short delay to allow backend to write campaign
                          console.log('   Scheduling dashboard refresh in 200ms to allow backend write...');
                          setTimeout(() => {
                            console.log('   Triggering dashboard refresh now...');
                            setRefreshDashboard(prev => prev + 1);
                          }, 200);
                          
                          console.log('   Navigation complete - dashboard should show campaign with spinner');
                        }}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                        </svg>
                        Continue in Background & Go to Dashboard
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Main content area */}
            <div className={`transition-all duration-300 ${isGenerating ? 'blur-sm' : ''}`}>
              {error && (
                <div className="mt-6 p-4 bg-red-100 dark:bg-red-900 border border-red-400 text-red-700 dark:text-red-200 rounded-lg">
                  <p className="font-semibold">Error:</p>
                  <p>{error}</p>
                </div>
              )}
              
              {adResults && (
                <div id="results" className="mt-8">
                  <AdResults 
                    results={adResults} 
                    onClearForm={handleClearForm}
                    onShareIndividualAd={handleShareIndividualAd}
                    onShareEntireCampaign={handleShareEntireCampaign}
                    shareToTelegram={shareToTelegram}
                    showSharingModal={showSharingModal}
                    setShowSharingModal={setShowSharingModal}
                    sharingType={sharingType}
                    setSharingType={setSharingType}
                    selectedAd={selectedAd}
                    setSelectedAd={setSelectedAd}
                    sharingStatus={sharingStatus}
                    setSharingStatus={setSharingStatus}
                    setShowChannelSelectionModal={setShowChannelSelectionModal}
                    showChannelSelectionModal={showChannelSelectionModal}
                    copiedText={copiedText}
                    copyToClipboard={copyToClipboard}
                    saveStatus={saveStatus}
                    setSaveStatus={setSaveStatus}
                    // Inline editing props
                    editingField={editingField}
                    setEditingField={setEditingField}
                    editContent={editContent}
                    setEditContent={setEditContent}
                    startEditing={startEditing}
                    cancelEditing={cancelEditing}
                    saveEdit={saveEdit}
                    showContentHistory={showContentHistory}
                    onRegenerate={handleRegenerate}
                    onRegenerateIndividualAd={handleRegenerateIndividualAd}
                    isGenerating={isGenerating}
                  />
                </div>
              )}
            </div>
          </div>
        );
      
      case 'poster-builder':
        return (
          <PosterBuilder
            onBack={() => setCurrentView('campaigns')}
            sessionToken={sessionToken}
          />
        );
      
      case 'admin-login':
        return <AdminLoginForm onLogin={handleAdminLogin} error={adminError} isLoading={adminLoading} />;
      
      case 'admin':
        return isAdmin ? (
          <AdminPanel onLogout={handleAdminLogout} adminToken={adminToken} />
        ) : (
          <AdminLoginForm onLogin={handleAdminLogin} error={adminError} isLoading={adminLoading} />
        );
      
      case 'feedback':
        return (
          <FeedbackDashboard 
            onBackToMain={() => setCurrentView('campaigns')} 
          />
        );
      
      case 'whatsapp':
        return (
          <WhatsAppSettingsV2 
            onBackToMain={() => setCurrentView('campaigns')}
            sessionToken={sessionToken}
          />
        );
      
      case 'campaigns':
        // Feature flag: Use new modern Dashboard V2 or classic Dashboard
        if (useNewDashboard) {
          return (
            <DashboardV2 
              onViewCampaign={handleViewCampaign}
              onCreateNewCampaign={handleCreateNewCampaign}
              onEditCampaign={async (campaign) => {
                try {
                  const response = await axios.get(`${API}/campaigns/${campaign.id}`, {
                    headers: { Authorization: `Bearer ${sessionToken}` }
                  });
                  setSelectedCampaign(response.data);
                } catch (error) {
                  console.error('Error fetching full campaign details:', error);
                  setSelectedCampaign(campaign);
                }
                setShowDetailsModal(true);
              }}
              onRegenerateCampaign={handleRegenerateCampaign}
              onNavigateToContentStudio={() => setCurrentView('content-studio')}
              sessionToken={sessionToken}
              user={user}
              refreshTrigger={refreshDashboard}
            />
          );
        }
        
        // Classic Dashboard (fallback)
        return (
          <div className={`p-6 ${colorTheme === 'purple' ? 'bg-purple-50' : colorTheme === 'blue' ? 'bg-blue-50' : 'bg-green-50'} dark:bg-gray-900 min-h-screen`}>
            {/* Recent Creations Section */}
            <div className="max-w-7xl mx-auto mb-6">
              <RecentCreations 
                token={sessionToken}
                onViewAll={() => setCurrentView('my-gallery')}
                onViewItem={(item) => {
                  // Open lightbox or navigate to gallery with item selected
                  setCurrentView('my-gallery');
                }}
              />
            </div>
            
            <Dashboard 
              onViewCampaign={handleViewCampaign} 
              onCreateNewCampaign={handleCreateNewCampaign}
              onBackToMain={handleBackToMain} 
              refreshTrigger={refreshDashboard}
              showTitle={true}
              hideAnalytics={true}
              currentView={'campaigns'}
              setIsGenerating={setIsGenerating}
              setGenerationProgress={setGenerationProgress}
              onRegenerateCampaign={handleRegenerateCampaign}
              onEditCampaign={async (campaign) => {
                // CRITICAL FIX: Fetch full campaign details before showing modal
                try {
                  const response = await axios.get(`${API}/campaigns/${campaign.id}`, {
                    headers: { Authorization: `Bearer ${sessionToken}` }
                  });
                  setSelectedCampaign(response.data);
                } catch (error) {
                  console.error('Error fetching full campaign details:', error);
                  setSelectedCampaign(campaign);
                }
                setShowDetailsModal(true);
              }}
            />
          </div>
        );

      case 'credits':
        return <CreditsPageV2 sessionToken={sessionToken} />;

      case 'subscription-success':
        return <SubscriptionSuccess sessionToken={sessionToken} onNavigate={handleNavigation} />;

      case 'achievements':
        return <AchievementsPageV2 user={user} sessionToken={sessionToken} />;
      
      case 'my-gallery':
        return (
          <MyGallery 
            token={sessionToken}
            onBack={() => setCurrentView('campaigns')}
          />
        );

      case 'asset-library':
        return <AssetLibraryPage />;

      case 'voice-library':
        return <VoiceLibraryPage />;

      case 'public-reel':
        return <PublicReelPage />;
      
      case 'content-studio':
      case 'dashboard':
      default:
        if (currentView === 'content-studio') {
          return (
            <ContentStudioV2 
              onCreateContent={(data) => {
                if (data === 'advanced-form') {
                  // Switch to Ad Creator Hub
                  setCurrentView('main');
                } else {
                  // Handle quick content creation - data is the API response
                  console.log('Content generated from template:', data);
                  setAdResults(data);
                  
                  // Navigate to results UNLESS user clicked "Go to Dashboard" button
                  if (window.userChoseBackgroundMode === true) {
                    console.log('ContentStudioV2: User went to dashboard - NOT navigating');
                  } else {
                    console.log('ContentStudioV2: User stayed on modal - navigating to results screen');
                    setCurrentView('results'); // Switch to results view
                  }
                  
                  // Check for newly unlocked achievements
                  setTimeout(() => {
                    checkForNewAchievements();
                  }, 1000);
                  // Trigger dashboard refresh
                  setRefreshDashboard(prev => prev + 1);
                }
              }}
              user={user}
              isGenerating={isGenerating}
              setIsGenerating={setIsGenerating}
              generationProgress={generationProgress}
              setGenerationProgress={setGenerationProgress}
              onNavigate={(view) => setCurrentView(view)}
              setCreditPreviewData={setCreditPreviewData}
              setShowCreditPreview={setShowCreditPreview}
              setPendingGenerationData={setPendingGenerationData}
              sessionToken={sessionToken}
            />
          );
        }
        
        if (currentView === 'ai-assistant') {
          return (
            <div>
              <AIAssistantV2
                user={user}
                setIsGenerating={setIsGenerating}
                setGenerationProgress={setGenerationProgress}
                onNavigate={(view) => setCurrentView(view)}
                sessionToken={sessionToken}
                onCampaignGenerated={(data) => {
                  console.log('Campaign generated from AI Assistant:', data);
                  setAdResults(data);
                  if (window.userChoseBackgroundMode === true) {
                    console.log('User went to dashboard - NOT navigating');
                  } else {
                    console.log('User stayed on modal - navigating to results screen');
                    setCurrentView('results');
                  }
                  setTimeout(() => { checkForNewAchievements(); }, 1000);
                  setRefreshDashboard(prev => prev + 1);
                }}
              />
              
              {/* AI Assistant Progress Modal */}
              {isGenerating && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
                  <div className="bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl border border-slate-200/50 dark:border-slate-700/50">
                    <div className="text-center">
                      <div className={`animate-spin rounded-full h-12 w-12 border-4 border-slate-200 dark:border-slate-700 mx-auto mb-5 ${
                        colorTheme === 'purple' ? 'border-t-purple-600' : colorTheme === 'blue' ? 'border-t-blue-600' : 'border-t-emerald-600'
                      }`} />
                      
                      <h3 className="text-lg font-bold text-slate-800 dark:text-white mb-3">
                        AI Campaign Generation
                      </h3>
                      
                      <p className="text-sm text-slate-500 dark:text-slate-400 mb-5">
                        {generationProgress.message || 'AI Assistant is creating your campaign...'}
                      </p>
                      
                      {generationProgress.total > 0 && (
                        <div className="mb-5">
                          <div className="flex justify-between text-xs mb-1.5 text-slate-500">
                            <span className="font-medium text-slate-700 dark:text-slate-300">
                              {generationProgress.hasPersonas && generationProgress.personaCount > 1
                                ? generationProgress.message || 'Generating your marketing campaign...'
                                : `${generationProgress.step} of ${generationProgress.total} pieces`
                              }
                            </span>
                            {generationProgress.startTime && (
                              <span className={`${colorTheme === 'purple' ? 'text-purple-600' : colorTheme === 'blue' ? 'text-blue-600' : 'text-emerald-600'} font-medium`}>
                                {Math.floor((Date.now() - generationProgress.startTime) / 1000)}s
                              </span>
                            )}
                          </div>
                          <div className="w-full bg-slate-100 dark:bg-slate-700 rounded-full h-2.5">
                            <div 
                              className={`bg-gradient-to-r ${colorTheme === 'purple' ? 'from-purple-500 to-pink-500' : colorTheme === 'blue' ? 'from-blue-500 to-sky-500' : 'from-emerald-500 to-teal-500'} h-2.5 rounded-full transition-all duration-1000`}
                              style={{ width: `${generationProgress.actualProgress || Math.min(95, (generationProgress.step / generationProgress.total) * 100)}%` }}
                            />
                          </div>
                          <div className="flex items-center justify-center gap-4 mt-3 text-[10px] text-slate-400">
                            {['ai_conversation', ...(generationProgress.hasPersonas ? ['persona_generation'] : []), 'content_generation', 'finalizing'].map(stage => (
                              <div key={stage} className="flex items-center gap-1">
                                <div className={`w-1.5 h-1.5 rounded-full ${
                                  ['ai_conversation','persona_generation','content_generation','finalizing','complete'].indexOf(generationProgress.currentStage) >= ['ai_conversation','persona_generation','content_generation','finalizing','complete'].indexOf(stage)
                                    ? colorTheme === 'purple' ? 'bg-purple-500' : colorTheme === 'blue' ? 'bg-blue-500' : 'bg-emerald-500'
                                    : 'bg-slate-300 dark:bg-slate-600'
                                }`} />
                                <span className={generationProgress.currentStage === stage ? 'font-semibold text-slate-600 dark:text-slate-300' : ''}>
                                  {stage === 'ai_conversation' ? 'Analysis' : stage === 'persona_generation' ? 'Personas' : stage === 'content_generation' ? 'Content' : 'Finalize'}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {generationProgress.currentStage !== 'complete' && (
                        <button
                          onClick={() => {
                            window.userChoseBackgroundMode = true;
                            if (window.currentPollingCampaignId) window.currentPollingCampaignId = null;
                            setRefreshDashboard(prev => prev + 1);
                            setIsGenerating(false);
                            setGenerationProgress({ step: 0, total: 0, currentStage: '', isVisible: false });
                            setCurrentView('campaigns');
                          }}
                          className={`w-full bg-gradient-to-r ${colorTheme === 'purple' ? 'from-purple-600 to-pink-600 shadow-purple-500/20' : colorTheme === 'blue' ? 'from-blue-600 to-sky-500 shadow-blue-500/20' : 'from-emerald-600 to-teal-500 shadow-emerald-500/20'} text-white font-semibold py-3 px-6 rounded-xl transition-all shadow-lg text-sm`}
                        >
                          Continue in Background
                        </button>
                      )}
                      <p className="text-[10px] text-slate-400 mt-2">Content will keep generating. Check dashboard for status.</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        }
        
        return (
          <div className={`p-6 ${colorTheme === 'purple' ? 'bg-purple-50' : colorTheme === 'blue' ? 'bg-blue-50' : 'bg-green-50'} dark:bg-gray-900 min-h-screen`}>
            <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
              {/* Main Dashboard */}
              <div className="xl:col-span-4">
                <Dashboard 
                  onViewCampaign={handleViewCampaign} 
                  onCreateNewCampaign={handleCreateNewCampaign}
                  onBackToMain={handleBackToMain} 
                  refreshTrigger={refreshDashboard}
                  showTitle={true}
                  hideAnalytics={false}
                  currentView={'dashboard'}
                  onRegenerateCampaign={handleRegenerateCampaign}
                  onEditCampaign={async (campaign) => {
                    // CRITICAL FIX: Fetch full campaign details before showing modal
                    try {
                      const response = await axios.get(`${API}/campaigns/${campaign.id}`, {
                        headers: { Authorization: `Bearer ${sessionToken}` }
                      });
                      setSelectedCampaign(response.data);
                    } catch (error) {
                      console.error('Error fetching full campaign details:', error);
                      setSelectedCampaign(campaign);
                    }
                    setShowDetailsModal(true);
                  }}
                />
              </div>
              
              {/* Achievement Summary Sidebar */}
              <div className="xl:col-span-1">
                <AchievementDashboardSummary user={user} />
              </div>
            </div>
          </div>
        );
    }
  };

  // Sharing Functions

  // Channel detection and sharing logic
  const getAvailableChannels = (campaign) => {
    const channels = [];
    const adTypes = campaign.generated_ads.map(ad => ad.ad_type);
    
    // Channel mapping based on ad types generated
    const channelMappings = {
      'telegram_ad': {
        id: 'telegram',
        name: 'Telegram',
        icon: '📱',
        description: 'Publish to Telegram chats/channels'
      },
      'facebook_ad': {
        id: 'facebook',
        name: 'Facebook',
        icon: '📘',
        description: 'Publish to Facebook pages/groups',
        disabled: true
      },
      'instagram_ad': {
        id: 'instagram',
        name: 'Instagram',
        icon: '📷',
        description: 'Publish to Instagram accounts',
        disabled: true
      },
      'twitter_ad': {
        id: 'twitter',
        name: 'Twitter/X',
        icon: '🐦',
        description: 'Publish to Twitter/X accounts',
        disabled: true
      },
      'linkedin_ad': {
        id: 'linkedin',
        name: 'LinkedIn',
        icon: '💼',
        description: 'Publish to LinkedIn pages',
        disabled: true
      }
    };
    
    // Get unique channels based on generated ad types
    const uniqueChannels = new Set();
    adTypes.forEach(adType => {
      if (channelMappings[adType]) {
        uniqueChannels.add(channelMappings[adType]);
      }
    });
    
    return Array.from(uniqueChannels);
  };

  // Enhanced channel-aware sharing functions with primary channel logic
  const handleShareIndividualAd = (ad, adIndex) => {
    console.log('handleShareIndividualAd called with ad:', ad, 'adIndex:', adIndex);
    setSelectedAd({ ad, adIndex });
    setSharingType('individual');
    
    // Get current campaign for channel checking
    const currentCampaign = adResults || selectedCampaign;
    if (!currentCampaign) {
      alert('No campaign available for sharing.');
      return;
    }
    
    // Determine primary channel based on ad type or campaign ads
    const primaryChannel = getPrimaryChannel(currentCampaign, ad);
    console.log('Primary channel determined:', primaryChannel);
    
    // Always show primary channel modal first (even if disabled) for better UX
    setCurrentSharingCampaign(currentCampaign);
    setPrimaryChannel(primaryChannel);
    setShowPrimaryChannelModal(true);
  };

  const handleShareEntireCampaign = () => {
    console.log('handleShareEntireCampaign called');
    setSharingType('campaign');
    
    // Get current campaign for channel checking
    const currentCampaign = adResults || selectedCampaign;
    if (!currentCampaign) {
      alert('No campaign available for sharing.');
      return;
    }
    
    // Determine primary channel based on campaign's main ad types
    const primaryChannel = getPrimaryChannel(currentCampaign);
    console.log('Primary channel for entire campaign:', primaryChannel);
    
    // Always show primary channel modal first (even if disabled) for better UX
    setCurrentSharingCampaign(currentCampaign);
    setPrimaryChannel(primaryChannel);
    setShowPrimaryChannelModal(true);
  };

  // Helper function to determine primary channel
  const getPrimaryChannel = (campaign, specificAd = null) => {
    // If specific ad provided, use its type
    if (specificAd) {
      return getChannelByAdType(specificAd.ad_type);
    }
    
    // For entire campaign, use the first available ad type
    if (campaign.generated_ads && campaign.generated_ads.length > 0) {
      const firstAd = campaign.generated_ads[0];
      return getChannelByAdType(firstAd.ad_type);
    }
    
    // Fallback to Telegram as primary
    return {
      id: 'telegram',
      name: 'Telegram',
      icon: '📱',
      description: 'Publish to Telegram chats/channels',
      disabled: false
    };
  };

  // Helper function to get channel by ad type
  const getChannelByAdType = (adType) => {
    const channelMap = {
      'facebook_ad': {
        id: 'facebook',
        name: 'Facebook',
        icon: '📘',
        description: 'Share to Facebook (Coming Soon)',
        disabled: true
      },
      'instagram_ad': {
        id: 'instagram',
        name: 'Instagram',
        icon: '📸',
        description: 'Share to Instagram (Coming Soon)',
        disabled: true
      },
      'linkedin_ad': {
        id: 'linkedin',
        name: 'LinkedIn',
        icon: '💼',
        description: 'Share to LinkedIn (Coming Soon)',
        disabled: true
      },
      'telegram_ad': {
        id: 'telegram',
        name: 'Telegram',
        icon: '📱',
        description: 'Publish to Telegram chats/channels',
        disabled: false
      }
    };
    
    return channelMap[adType] || {
      id: 'telegram',
      name: 'Telegram',
      icon: '📱',
      description: 'Publish to Telegram chats/channels',
      disabled: false
    };
  };

  // Helper function to get all available channels
  const getAllAvailableChannels = () => {
    return [
      {
        id: 'telegram',
        name: 'Telegram',
        icon: '📱',
        description: 'Publish to Telegram chats/channels',
        disabled: false
      },
      {
        id: 'whatsapp',
        name: 'WhatsApp',
        icon: '💬',
        description: 'Send to WhatsApp contacts or groups',
        disabled: false
      },
      {
        id: 'facebook',
        name: 'Facebook',
        icon: '📘',
        description: 'Share to Facebook (Coming Soon)',
        disabled: true
      },
      {
        id: 'instagram',
        name: 'Instagram', 
        icon: '📸',
        description: 'Share to Instagram (Coming Soon)',
        disabled: true
      },
      {
        id: 'email',
        name: 'Email',
        icon: '📧', 
        description: 'Send via Email (Coming Soon)',
        disabled: true
      },
      {
        id: 'linkedin',
        name: 'LinkedIn',
        icon: '💼',
        description: 'Share to LinkedIn (Coming Soon)', 
        disabled: true
      }
    ];
  };

  // Updated channel selection handler that can access sharingType state
  const handleChannelSelectionWithType = (channelId) => {
    setShowChannelSelectionModal(false);
    
    switch (channelId) {
      case 'telegram':
        if (sharingType === 'individual') {
          // For individual ads, show the telegram sharing modal
          setShowSharingModal(true);
        } else {
          // For entire campaigns, directly set up telegram sharing
          setShowSharingModal(true);
        }
        break;
      case 'whatsapp':
        // Show WhatsApp modal for campaign publishing
        setShowWhatsAppModal(true);
        break;
      case 'email':
        alert('Email sharing coming soon!');
        break;
      case 'facebook':
        alert('Facebook sharing coming soon!');
        break;
      case 'instagram':
        alert('Instagram sharing coming soon!');
        break;
      case 'linkedin':
        alert('LinkedIn sharing coming soon!');
        break;
      default:
        alert('Channel not supported yet.');
    }
  };

  const shareToTelegram = async (chatId, customMessage = '') => {
    try {
      setSharingStatus({ status: 'sharing', message: 'Validating campaign...' });
      
      // Use campaign ID from either adResults or selectedCampaign depending on current view
      const campaignId = adResults ? adResults.id : selectedCampaign ? selectedCampaign.id : null;
      
      if (!campaignId) {
        setSharingStatus({ 
          status: 'error', 
          message: 'No campaign selected for sharing.' 
        });
        return;
      }
      
      // VALIDATION: Check if campaign meets publishing requirements
      try {
        const validationResult = await validateCampaignForPublishing(campaignId);
        
        if (!validationResult.can_publish) {
          // Show beautiful functional alert for validation failure
          const requirementMessage = 
            !validationResult.requirements.meets_feedback_requirement
              ? `Your campaign needs more feedback before publishing. Currently: ${validationResult.feedback_count}/3 feedback required.`
              : `Your campaign needs a higher average rating before publishing. Currently: ${validationResult.average_rating}/5.0 stars (needs >4.0).`;
          
          showFunctionalAlert(
            "Publishing Requirements Not Met 📋",
            `${requirementMessage}\n\nGet more feedback by sharing your campaign's feedback link with clients and colleagues. Once you have 3+ feedback entries with an average rating above 4 stars, you'll be able to publish to all channels!`,
            "warning",
            "📊",
            <button
              onClick={async () => {
                closeFunctionalAlert();
                
                try {
                  // Check if campaign is already public
                  const token = localStorage.getItem('session_token');
                  const headers = { 'Authorization': `Bearer ${token}` };
                  const campaignResponse = await axios.get(`${API}/campaigns/${campaignId}`, { headers });
                  const campaign = campaignResponse.data;
                  
                  if (campaign.is_public && campaign.share_url) {
                    // Campaign is already public - show feedback link directly
                    setUrlCopyModal({
                      isOpen: true,
                      url: campaign.share_url,
                      title: '🔗 Feedback Link Ready!',
                      description: 'Your campaign is public! Share this link with clients and colleagues to get more feedback:'
                    });
                  } else {
                    // Campaign is not public - show modal to make it public first
                    showFunctionalAlert(
                      "Make Campaign Public First 🌍",
                      "To get feedback, your campaign needs to be public first. Making it public will generate a shareable feedback link that you can send to clients and colleagues.\n\nDon't worry - only people with the link can view your campaign!",
                      "info",
                      "🔓",
                      <button
                        onClick={async () => {
                          try {
                            // Make campaign public
                            const shareResponse = await axios.put(
                              `${API}/campaigns/${campaignId}/sharing`,
                              { is_public: true },
                              { headers }
                            );
                            
                            closeFunctionalAlert();
                            
                            // Show success and feedback link
                            setUrlCopyModal({
                              isOpen: true,
                              url: shareResponse.data.share_url,
                              title: '🎉 Campaign Now Public!',
                              description: 'Perfect! Your campaign is now public. Share this feedback link with clients and colleagues:'
                            });
                            
                          } catch (error) {
                            console.error('Error making campaign public:', error);
                            showFunctionalAlert(
                              "Error Making Public ❌",
                              "Sorry, there was an error making your campaign public. Please try again or contact support if the issue persists.",
                              "error"
                            );
                          }
                        }}
                        className="w-full px-6 py-3 bg-gradient-to-r from-green-500 to-blue-500 text-white rounded-lg font-bold hover:from-green-600 hover:to-blue-600 transition-all duration-200 transform hover:scale-105"
                      >
                        🌍 Make Public & Get Feedback Link
                      </button>,
                      <button
                        onClick={closeFunctionalAlert}
                        className="w-full px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-300 dark:hover:bg-gray-500 transition-all duration-200"
                      >
                        Maybe Later
                      </button>
                    );
                  }
                } catch (error) {
                  console.error('Error checking campaign status:', error);
                  showFunctionalAlert(
                    "Error Checking Campaign ❌",
                    "Sorry, there was an error checking your campaign status. Please try again or contact support if the issue persists.",
                    "error"
                  );
                }
              }}
              className="w-full px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg font-bold hover:from-blue-600 hover:to-purple-600 transition-all duration-200 transform hover:scale-105"
            >
              📝 Get More Feedback
            </button>
          );
          
          setSharingStatus({});
          return;
        }
      } catch (validationError) {
        console.error('Validation error:', validationError);
        // Continue with sharing if validation fails (fallback)
      }
      
      // Proceed with sharing if validation passes
      setSharingStatus({ status: 'sharing', message: 'Publishing to Telegram...' });
      
      const shareData = {
        campaign_id: campaignId,
        chat_id: chatId,
        share_type: sharingType,
        message_template: customMessage,
        ad_index: sharingType === 'individual' && selectedAd ? selectedAd.adIndex : null
      };

      const response = await axios.post(
        `${API}/share/telegram`,
        shareData,
        {
          headers: {
            'Authorization': `Bearer ${sessionToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.successful_shares > 0) {
        // Show success celebration modal for sharing
        setSuccessCelebration({
          isOpen: true,
          title: "Campaign Published! 🚀",
          message: `Your campaign is now live and published! You successfully published ${response.data.successful_shares} item(s) to Telegram. You're building an amazing portfolio - keep creating and publishing!`,
          celebrationType: "publishing",
          actionButton: null
        });
        
        // Also update the legacy status for any UI that depends on it
        setSharingStatus({ 
          status: 'success', 
          message: `🎉 Successfully shared ${response.data.successful_shares} item(s) to Telegram! Try making your campaigns public to get feedback and unlock publishing achievements!` 
        });
        
        // Check for newly unlocked achievements after successful sharing
        setTimeout(() => {
          checkForNewAchievements();
        }, 500);
      } else {
        setSharingStatus({ 
          status: 'error', 
          message: 'Failed to share to Telegram. The bot may not have access to this chat. Try: 1) Add @maple_ai_generator_bot to your chat first, or 2) Use your username (e.g., @yourusername), or 3) Use "me" to test.' 
        });
      }

      setTimeout(() => {
        setSharingStatus({});
        setShowSharingModal(false);
      }, 3000);

    } catch (error) {
      console.error('Error sharing to Telegram:', error);
      let errorMessage = 'Failed to share to Telegram';
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (detail.includes('Chat not found')) {
          errorMessage = '❌ Chat not found! The bot cannot access this chat. Try:\n• Add @maple_ai_generator_bot to your chat/channel first\n• Use your Telegram username: @yourusername\n• Use "me" to test with the bot directly';
        } else {
          errorMessage = detail;
        }
      }
      
      setSharingStatus({ 
        status: 'error', 
        message: errorMessage 
      });

      setTimeout(() => {
        setSharingStatus({});
      }, 5000); // Longer timeout for detailed error message
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      {/* Mobile Header with Hamburger */}
      {isMobile && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl border-b border-slate-200/50 dark:border-slate-800/50 px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => setShowMobileMenu(!showMobileMenu)}
            className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            <svg className="w-6 h-6 text-slate-700 dark:text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {showMobileMenu ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
          <span className={`text-xl font-bold bg-gradient-to-r ${colorTheme === 'purple' ? 'from-purple-600 to-pink-600' : colorTheme === 'blue' ? 'from-blue-600 to-sky-500' : 'from-emerald-600 to-teal-500'} bg-clip-text text-transparent`}>
            AdElevate
          </span>
          <div className="w-10" />
        </div>
      )}

      {/* Fixed Sidebar */}
      <SidebarV2 
        currentView={currentView}
        onNavigate={handleNavigation}
        user={user}
        onLogout={logout}
        onToggleCollapse={setSidebarCollapsed}
        isCollapsed={sidebarCollapsed}
        sessionToken={sessionToken}
        isMobile={isMobile}
        showMobileMenu={showMobileMenu}
        setShowMobileMenu={setShowMobileMenu}
        isDark={isDark}
        toggleTheme={toggleTheme}
      />
      
      {/* Achievement Notification */}
      {achievementNotification && (
        <AchievementNotification 
          achievement={achievementNotification}
          onClose={() => setAchievementNotification(null)}
        />
      )}

      {/* Achievement Unlock Animation */}
      {showUnlockAnimation && (
        <AchievementUnlockAnimation
          achievement={showUnlockAnimation}
          onComplete={() => {
            setShowUnlockAnimation(null);
            // Show regular notification after animation
            setAchievementNotification(showUnlockAnimation);
          }}
        />
      )}

      {/* Toast Notifications */}
      <ToastContainer 
        toasts={toasts} 
        onRemoveToast={removeToast}
        onToastClick={handleToastClick}
      />

      {/* Shareable Achievement Card */}
      {showShareableCard && (
        <ShareableAchievementCard
          achievement={showShareableCard}
          user={user}
          onClose={() => setShowShareableCard(null)}
          onShare={(platform) => {
            console.log(`Achievement shared on ${platform}`);
            // Could track sharing activity here
          }}
        />
      )}
      
      {/* Main Content with left margin to account for sidebar */}
      <div className={`min-h-screen transition-all duration-300 overflow-x-hidden bg-slate-50 dark:bg-slate-900 ${isMobile ? 'ml-0 pt-16' : sidebarCollapsed ? 'ml-20' : 'ml-72'}`}>
        {renderContent()}
      </div>
      
      {/* Campaign Details Modal for Dashboard Inline Editing */}
      {showDetailsModal && selectedCampaign && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white dark:bg-gray-800">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Campaign Details
                </h3>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                >
                  ✕
                </button>
              </div>
              
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {/* Campaign Strategy Info */}
                {(selectedCampaign.product?.campaign_objective || selectedCampaign.product?.primary_platform || selectedCampaign.product?.pain_point || selectedCampaign.product?.offer_incentive) && (
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-white mb-2">Campaign Strategy</h4>
                    <div className="flex flex-wrap gap-2 p-3 bg-gray-50 dark:bg-gray-700 rounded">
                      {selectedCampaign.product?.campaign_objective && (
                        <span className="inline-flex items-center px-2 py-1 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded text-xs font-medium">
                          Objective: <span className="capitalize ml-1">{selectedCampaign.product.campaign_objective.replace(/_/g, ' ')}</span>
                        </span>
                      )}
                      {selectedCampaign.product?.primary_platform && (
                        <span className="inline-flex items-center px-2 py-1 bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 rounded text-xs font-medium">
                          Platform: <span className="capitalize ml-1">{selectedCampaign.product.primary_platform}</span>
                        </span>
                      )}
                      {selectedCampaign.product?.cta_goal && (
                        <span className="inline-flex items-center px-2 py-1 bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 rounded text-xs font-medium">
                          CTA: <span className="capitalize ml-1">{selectedCampaign.product.cta_goal.replace(/_/g, ' ')}</span>
                        </span>
                      )}
                      {selectedCampaign.product?.offer_incentive && (
                        <span className="inline-flex items-center px-2 py-1 bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 rounded text-xs font-medium">
                          Offer: {selectedCampaign.product.offer_incentive}
                        </span>
                      )}
                      {selectedCampaign.product?.pain_point && (
                        <span className="inline-flex items-center px-2 py-1 bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 rounded text-xs font-medium">
                          Pain Point: {selectedCampaign.product.pain_point}
                        </span>
                      )}
                      {selectedCampaign.product?.landing_page_url && (
                        <a href={selectedCampaign.product.landing_page_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-2 py-1 bg-slate-100 dark:bg-slate-600 text-slate-700 dark:text-slate-300 rounded text-xs font-medium hover:underline">
                          Landing Page
                        </a>
                      )}
                    </div>
                  </div>
                )}
                
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white">Generated Ads ({selectedCampaign.generated_ads?.length || 0})</h4>
                  <div className="mt-2 space-y-3 max-h-96 overflow-y-auto">
                    {selectedCampaign.generated_ads?.map((ad, index) => (
                      <div key={index} className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                        <div className="flex justify-between items-start mb-3">
                          <span className="font-medium text-gray-900 dark:text-white bg-blue-100 dark:bg-blue-900 px-2 py-1 rounded text-sm">
                            {ad.ad_type}
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">#{index + 1}</span>
                        </div>
                        
                        {/* Ad Title */}
                        {ad.title && (
                          <div className="flex justify-between items-start mb-3">
                            {(() => {
                              const isEditing = editingField?.campaignId === selectedCampaign.id && editingField?.adIndex === index && editingField?.fieldName === 'title';
                              console.log('Title edit check:', { editingField, campaignId: selectedCampaign.id, adIndex: index, fieldName: 'title', isEditing });
                              return isEditing;
                            })() ? (
                              <div className="flex-1 mr-2">
                                <textarea
                                  value={editContent}
                                  onChange={(e) => setEditContent(e.target.value)}
                                  className="w-full text-lg font-semibold text-gray-900 dark:text-white bg-white dark:bg-gray-800 border border-blue-300 rounded-md px-2 py-1 resize-none"
                                  rows="2"
                                  autoFocus
                                />
                                <div className="flex gap-2 mt-2">
                                  <button
                                    onClick={saveEdit}
                                    className={`text-xs ${colorTheme === 'purple' ? 'bg-purple-500 hover:bg-purple-600' : colorTheme === 'blue' ? 'bg-blue-500 hover:bg-blue-600' : 'bg-green-500 hover:bg-green-600'} text-white px-2 py-1 rounded transition-colors btn-ripple`}
                                  >
                                    ✅ Save
                                  </button>
                                  <button
                                    onClick={cancelEditing}
                                    className="text-xs bg-gray-500 hover:bg-gray-600 text-white px-2 py-1 rounded transition-colors"
                                  >
                                    ❌ Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <>
                                <h5 className="text-lg font-semibold text-gray-900 dark:text-white flex-1">{ad.title}</h5>
                                <button
                                  onClick={() => startEditing(selectedCampaign.id, index, 'title', ad.title)}
                                  className="text-xs bg-gray-200 hover:bg-gray-300 text-gray-700 px-2 py-1 rounded transition-colors ml-2"
                                >
                                  ✏️ Edit
                                </button>
                              </>
                            )}
                          </div>
                        )}
                        
                        {/* Ad Content */}
                        <div className="flex justify-between items-start mb-3">
                          {editingField?.campaignId === selectedCampaign.id && editingField?.adIndex === index && editingField?.fieldName === 'content' ? (
                            <div className="flex-1 mr-2">
                              <textarea
                                value={editContent}
                                onChange={(e) => setEditContent(e.target.value)}
                                className="w-full text-sm text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-800 border border-blue-300 rounded-md px-2 py-1 resize-none"
                                rows="3"
                                autoFocus
                              />
                              <div className="flex gap-2 mt-2">
                                <button
                                  onClick={saveEdit}
                                  className={`text-xs ${colorTheme === 'purple' ? 'bg-purple-500 hover:bg-purple-600' : colorTheme === 'blue' ? 'bg-blue-500 hover:bg-blue-600' : 'bg-green-500 hover:bg-green-600'} text-white px-2 py-1 rounded transition-colors btn-ripple`}
                                >
                                  ✅ Save
                                </button>
                                <button
                                  onClick={cancelEditing}
                                  className="text-xs bg-gray-500 hover:bg-gray-600 text-white px-2 py-1 rounded transition-colors"
                                >
                                  ❌ Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <p className="text-sm text-gray-600 dark:text-gray-300 flex-1">{ad.content}</p>
                              <button
                                onClick={() => startEditing(selectedCampaign.id, index, 'content', ad.content)}
                                className="text-xs bg-gray-200 hover:bg-gray-300 text-gray-700 px-2 py-1 rounded transition-colors ml-2"
                              >
                                ✏️ Edit
                              </button>
                            </>
                          )}
                        </div>
                        
                        {/* Call to Action */}
                        {ad.call_to_action && (
                          <div className="flex justify-between items-start">
                            {editingField?.campaignId === selectedCampaign.id && editingField?.adIndex === index && editingField?.fieldName === 'call_to_action' ? (
                              <div className="flex-1 mr-2">
                                <textarea
                                  value={editContent}
                                  onChange={(e) => setEditContent(e.target.value)}
                                  className="w-full text-sm font-medium text-gray-800 dark:text-gray-200 bg-white dark:bg-gray-800 border border-blue-300 rounded-md px-2 py-1 resize-none"
                                  rows="1"
                                  autoFocus
                                />
                                <div className="flex gap-2 mt-2">
                                  <button
                                    onClick={saveEdit}
                                    className={`text-xs ${colorTheme === 'purple' ? 'bg-purple-500 hover:bg-purple-600' : colorTheme === 'blue' ? 'bg-blue-500 hover:bg-blue-600' : 'bg-green-500 hover:bg-green-600'} text-white px-2 py-1 rounded transition-colors btn-ripple`}
                                  >
                                    ✅ Save
                                  </button>
                                  <button
                                    onClick={cancelEditing}
                                    className="text-xs bg-gray-500 hover:bg-gray-600 text-white px-2 py-1 rounded transition-colors"
                                  >
                                    ❌ Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <>
                                <p className="text-sm font-medium text-gray-800 dark:text-gray-200 flex-1">CTA: {ad.call_to_action}</p>
                                <button
                                  onClick={() => startEditing(selectedCampaign.id, index, 'call_to_action', ad.call_to_action)}
                                  className="text-xs bg-gray-200 hover:bg-gray-300 text-gray-700 px-2 py-1 rounded transition-colors ml-2"
                                >
                                  ✏️ Edit
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Sharing Modal for AdResults */}
      {showSharingModal && (adResults || selectedCampaign) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full max-h-screen overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {sharingType === 'individual' && selectedAd ? 
                  `Publish "${selectedAd.ad.title}"` : 
                  `Publish Campaign: "${(adResults || selectedCampaign).product.product_name}"`
                }
              </h3>
              <button
                onClick={() => setShowSharingModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Telegram Chat ID or Username
                </label>
                <input
                  type="text"
                  id="chatId"
                  placeholder="@username or chat ID"
                  className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Try: "me" for testing, or your username like "@yourusername"
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Custom Message (Optional)
                </label>
                <textarea
                  id="customMessage"
                  placeholder="Add a custom message..."
                  rows={3}
                  className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
              </div>

              {/* Status Messages */}
              {sharingStatus.status && (
                <div className={`p-3 rounded-lg ${
                  sharingStatus.status === 'sharing' ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800' :
                  sharingStatus.status === 'success' ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800' :
                  'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
                }`}>
                  <p className={`text-sm ${
                    sharingStatus.status === 'sharing' ? 'text-blue-600 dark:text-blue-400' :
                    sharingStatus.status === 'success' ? 'text-green-600 dark:text-green-400' :
                    'text-red-600 dark:text-red-400'
                  }`}>
                    {sharingStatus.message}
                  </p>
                </div>
              )}

              <div className="flex space-x-3">
                <button
                  onClick={() => setShowSharingModal(false)}
                  className="flex-1 px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    const chatId = document.getElementById('chatId').value;
                    const customMessage = document.getElementById('customMessage').value;
                    shareToTelegram(chatId, customMessage);
                  }}
                  className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
                >
                  🚀 Publish to Telegram
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* WhatsApp Publishing Modal for Main App */}
      {showWhatsAppModal && (adResults || currentSharingCampaign) && (
        <WhatsAppPublishModal
          isOpen={showWhatsAppModal}
          onClose={() => setShowWhatsAppModal(false)}
          campaign={adResults || currentSharingCampaign}
          sessionToken={sessionToken}
        />
      )}

      {/* Primary Channel Modal */}
      {showPrimaryChannelModal && primaryChannel && currentSharingCampaign && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full border-2 ${colorTheme === 'purple' ? 'border-purple-500' : colorTheme === 'blue' ? 'border-blue-500' : 'border-green-500'}`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={`text-lg font-semibold bg-gradient-to-r ${colorTheme === 'purple' ? 'from-purple-600 to-pink-600' : colorTheme === 'blue' ? 'from-blue-600 to-sky-500' : 'from-green-600 to-emerald-600'} bg-clip-text text-transparent`}>
                Publish Campaign: "{currentSharingCampaign.product.product_name}"
              </h3>
              <button
                onClick={() => setShowPrimaryChannelModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Based on your campaign content, we recommend:
              </p>
              
              {/* Primary Channel Display */}
              <div className={`p-4 rounded-lg border-2 ${
                primaryChannel.disabled 
                  ? 'border-orange-200 bg-orange-50 dark:bg-orange-900/20 dark:border-orange-700'
                  : (colorTheme === 'purple' 
                    ? 'border-purple-300 bg-purple-50 dark:bg-purple-900/20 dark:border-purple-500'
                    : 'border-green-300 bg-green-50 dark:bg-green-900/20 dark:border-green-500')
              }`}>
                <div className="flex items-center">
                  <span className="text-3xl mr-3">{primaryChannel.icon}</span>
                  <div className="flex-1">
                    <div className="font-semibold text-gray-900 dark:text-white flex items-center">
                      {primaryChannel.name}
                      {primaryChannel.disabled && (
                        <span className="ml-2 px-2 py-1 text-xs bg-orange-200 text-orange-800 rounded-full">
                          Coming Soon
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {primaryChannel.description}
                    </div>
                  </div>
                </div>
              </div>

              {primaryChannel.disabled ? (
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-4">
                  <div className="flex items-start">
                    <span className="text-amber-600 dark:text-amber-400 text-xl mr-3">⚠️</span>
                    <div>
                      <p className="text-sm text-amber-800 dark:text-amber-300 font-medium">
                        {primaryChannel.name} publishing is coming soon!
                      </p>
                      <p className="text-sm text-amber-700 dark:text-amber-400 mt-1">
                        Would you like to publish to an available channel instead?
                      </p>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>

            <div className="flex space-x-3 mt-6">
              {!primaryChannel.disabled ? (
                <button
                  onClick={() => {
                    setShowPrimaryChannelModal(false);
                    handleChannelSelectionWithType(primaryChannel.id);
                  }}
                  className={`flex-1 ${colorTheme === 'purple' ? 'bg-purple-600 hover:bg-purple-700' : colorTheme === 'blue' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-green-600 hover:bg-green-700'} text-white py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center`}
                >
                  <span className="mr-2">{primaryChannel.icon}</span>
                  Publish to {primaryChannel.name}
                </button>
              ) : (
                <button
                  onClick={() => {
                    setShowPrimaryChannelModal(false);
                    // Direct to Telegram since it's the only available channel
                    handleChannelSelectionWithType('telegram');
                  }}
                  className={`flex-1 ${colorTheme === 'purple' ? 'bg-purple-600 hover:bg-purple-700' : colorTheme === 'blue' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-green-600 hover:bg-green-700'} text-white py-3 px-4 rounded-lg font-medium transition-colors btn-ripple`}
                >
                  📱 Publish to Telegram
                </button>
              )}
              
              <button
                onClick={() => {
                  setShowPrimaryChannelModal(false);
                  const allChannels = getAllAvailableChannels();
                  setAvailableChannels(allChannels);
                  setShowChannelSelectionModal(true);
                }}
                className="px-4 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Choose Different Channel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Channel Selection Modal */}
      {showChannelSelectionModal && currentSharingCampaign && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full border-2 ${colorTheme === 'purple' ? 'border-purple-500' : colorTheme === 'blue' ? 'border-blue-500' : 'border-green-500'}`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={`text-lg font-semibold bg-gradient-to-r ${colorTheme === 'purple' ? 'from-purple-600 to-pink-600' : colorTheme === 'blue' ? 'from-blue-600 to-sky-500' : 'from-green-600 to-emerald-600'} bg-clip-text text-transparent`}>
                Choose Publishing Channel
              </h3>
              <button
                onClick={() => setShowChannelSelectionModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-3">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Select a channel to publish your campaign:
              </p>
              
              {availableChannels.map((channel) => (
                <button
                  key={channel.id}
                  onClick={() => handleChannelSelectionWithType(channel.id)}
                  disabled={channel.disabled}
                  className={`w-full flex items-center p-3 rounded-lg border text-left transition-colors ${
                    channel.disabled
                      ? 'border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 cursor-not-allowed opacity-50'
                      : `border-gray-200 dark:border-gray-600 ${colorTheme === 'purple' ? 'hover:border-purple-400 dark:hover:border-purple-500 hover:bg-purple-50 dark:hover:bg-purple-900/20' : colorTheme === 'blue' ? 'hover:border-blue-400 dark:hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20' : 'hover:border-green-400 dark:hover:border-green-500 hover:bg-green-50 dark:hover:bg-green-900/20'}`
                  }`}
                >
                  <span className="text-2xl mr-3">{channel.icon}</span>
                  <div className="flex-1">
                    <div className="font-medium text-gray-900 dark:text-white">
                      {channel.name}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {channel.description}
                    </div>
                  </div>
                  {!channel.disabled && (
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  )}
                </button>
              ))}
            </div>

            <div className="mt-6">
              <button
                onClick={() => setShowChannelSelectionModal(false)}
                className="w-full py-2 px-4 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      

      {/* Confirmation Modal for Regenerate Actions */}
      {confirmModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`bg-white dark:bg-gray-800 rounded-xl p-6 max-w-md w-full shadow-2xl border-2 ${colorTheme === 'purple' ? 'border-purple-500' : colorTheme === 'blue' ? 'border-blue-500' : 'border-green-500'}`}>
            <div className="text-center mb-6">
              <h3 className={`text-2xl font-bold mb-3 bg-gradient-to-r ${colorTheme === 'purple' ? 'from-purple-600 to-pink-600' : colorTheme === 'blue' ? 'from-blue-600 to-sky-500' : 'from-green-600 to-emerald-600'} bg-clip-text text-transparent`}>
                {confirmModal.title}
              </h3>
              <p className="text-gray-700 dark:text-gray-300 whitespace-pre-line text-sm">
                {confirmModal.message}
              </p>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
                className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors font-medium"
              >
                {confirmModal.cancelText}
              </button>
              <button
                onClick={confirmModal.onConfirm}
                className={`flex-1 px-4 py-3 ${colorTheme === 'purple' ? 'bg-purple-600 hover:bg-purple-700' : colorTheme === 'blue' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-green-600 hover:bg-green-700'} text-white rounded-lg transition-colors font-medium btn-ripple`}
              >
                {confirmModal.confirmText}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Regenerate-with-Feedback Modal */}
      <RegenerateWithFeedbackModal
        isOpen={regenFeedbackModal.isOpen}
        onClose={() => setRegenFeedbackModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={regenFeedbackModal.onConfirm}
        title={regenFeedbackModal.title}
        description={regenFeedbackModal.description}
        confirmText={regenFeedbackModal.confirmText}
        isProcessing={regenFeedbackModal.isProcessing}
      />

      {/* Success Celebration Modal */}
      <SuccessCelebrationModal
        isOpen={successCelebration.isOpen}
        onClose={closeSuccessCelebration}
        title={successCelebration.title}
        message={successCelebration.message}
        celebrationType={successCelebration.celebrationType}
        actionButton={successCelebration.actionButton}
      />
      
      {/* Functional Alert Modal */}
      <FunctionalAlertModal
        isOpen={functionalAlert.isOpen}
        onClose={closeFunctionalAlert}
        title={functionalAlert.title}
        message={functionalAlert.message}
        type={functionalAlert.type}
        icon={functionalAlert.icon}
        actionButton={functionalAlert.actionButton}
        secondaryButton={functionalAlert.secondaryButton}
      />
      
      {/* URL Copy Modal */}
      <URLCopyModal
        isOpen={urlCopyModal.isOpen}
        onClose={() => setUrlCopyModal(prev => ({ ...prev, isOpen: false }))}
        url={urlCopyModal.url}
        title={urlCopyModal.title}
        description={urlCopyModal.description}
      />
      
      {/* Global Credit Cost Preview Modal */}
      {showCreditPreview && creditPreviewData && (
        <CreditCostPreview
          costPreview={creditPreviewData}
          onProceed={handleCreditPreviewProceed}
          onCancel={handleCreditPreviewCancel}
          onPurchaseCredits={handlePurchaseCreditsFromPreview}
        />
      )}
    </div>
  );
};

// Edit Campaign Form Component
const EditCampaignForm = ({ campaign, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    product_name: campaign.product.product_name || '',
    product_description: campaign.product.product_description || '',
    target_audience: campaign.product.target_audience || '',
    key_benefits: campaign.product.key_benefits || '',
    brand_voice: campaign.product.brand_voice || 'professional',
    price: campaign.product.price || '',
    category: campaign.product.category || '',
    age_range_min: campaign.product.age_range_min || 25,
    age_range_max: campaign.product.age_range_max || 55,
    gender: campaign.product.gender || 'All',
    geography: campaign.product.geography || '',
    occupation: campaign.product.occupation || '',
    industry_business_type: campaign.product.industry_business_type || '',
    budget_range: campaign.product.budget_range || ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };


  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Product Name
          </label>
          <input
            type="text"
            name="product_name"
            value={formData.product_name}
            onChange={handleChange}
            required
            className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Category
          </label>
          <input
            type="text"
            name="category"
            value={formData.category}
            onChange={handleChange}
            className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Product Description
        </label>
        <textarea
          name="product_description"
          value={formData.product_description}
          onChange={handleChange}
          required
          rows={3}
          className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Key Benefits
        </label>
        <textarea
          name="key_benefits"
          value={formData.key_benefits}
          onChange={handleChange}
          required
          rows={3}
          className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
        />
      </div>

      {/* Persona Fields */}
      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Persona Settings</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Age Range (Min)
            </label>
            <input
              type="number"
              name="age_range_min"
              value={formData.age_range_min}
              onChange={handleChange}
              min="18"
              max="100"
              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Age Range (Max)
            </label>
            <input
              type="number"
              name="age_range_max"
              value={formData.age_range_max}
              onChange={handleChange}
              min="18"
              max="100"
              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            />
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Gender
            </label>
            <select
              name="gender"
              value={formData.gender}
              onChange={handleChange}
              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            >
              <option value="All">All</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Geography
            </label>
            <input
              type="text"
              name="geography"
              value={formData.geography}
              onChange={handleChange}
              placeholder="e.g., California, USA"
              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            />
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Industry/Business Type
            </label>
            <input
              type="text"
              name="industry_business_type"
              value={formData.industry_business_type}
              onChange={handleChange}
              placeholder="e.g., Technology, Healthcare"
              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Budget Range
            </label>
            <input
              type="text"
              name="budget_range"
              value={formData.budget_range}
              onChange={handleChange}
              placeholder="e.g., $1,000 - $5,000"
              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            />
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Key Benefits
        </label>
        <textarea
          name="key_benefits"
          value={formData.key_benefits}
          onChange={handleChange}
          required
          rows={2}
          className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Brand Voice
          </label>
          <select
            name="brand_voice"
            value={formData.brand_voice}
            onChange={handleChange}
            className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
          >
            <option value="professional">Professional</option>
            <option value="friendly">Friendly</option>
            <option value="casual">Casual</option>
            <option value="authoritative">Authoritative</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Price
          </label>
          <input
            type="text"
            name="price"
            value={formData.price}
            onChange={handleChange}
            className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Gender
          </label>
          <select
            name="gender"
            value={formData.gender}
            onChange={handleChange}
            className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
          >
            <option value="All">All</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
          </select>
        </div>
      </div>

      <div className="flex justify-end space-x-4">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg transition-colors"
        >
          Save Changes
        </button>
      </div>
    </form>
  );
};

// Duplicate Campaign Form Component
const DuplicateCampaignForm = ({ campaign, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    product_name: (campaign.product.product_name || '') + ' (Copy)',
    product_description: campaign.product.product_description || '',
    target_audience: campaign.product.target_audience || '',
    key_benefits: campaign.product.key_benefits || '',
    brand_voice: campaign.product.brand_voice || 'professional',
    price: campaign.product.price || '',
    category: campaign.product.category || '',
    age_range_min: campaign.product.age_range_min || 25,
    age_range_max: campaign.product.age_range_max || 55,
    gender: campaign.product.gender || 'All',
    geography: campaign.product.geography || '',
    occupation: campaign.product.occupation || '',
    industry_business_type: campaign.product.industry_business_type || '',
    budget_range: campaign.product.budget_range || '',
    interests_behaviors: campaign.product.interests_behaviors || '',
    persona_count: campaign.product.persona_count || 'ai_determined',
    enable_personas: campaign.product.enable_personas || false
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Product Name
          </label>
          <input
            type="text"
            name="product_name"
            value={formData.product_name}
            onChange={handleChange}
            required
            className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Category
          </label>
          <input
            type="text"
            name="category"
            value={formData.category}
            onChange={handleChange}
            className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Product Description
        </label>
        <textarea
          name="product_description"
          value={formData.product_description}
          onChange={handleChange}
          required
          rows={3}
          className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Key Benefits
        </label>
        <textarea
          name="key_benefits"
          value={formData.key_benefits}
          onChange={handleChange}
          required
          rows={3}
          className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
        />
      </div>

      {/* Persona Fields */}
      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Persona Settings</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Age Range (Min)
            </label>
            <input
              type="number"
              name="age_range_min"
              value={formData.age_range_min}
              onChange={handleChange}
              min="18"
              max="100"
              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Age Range (Max)
            </label>
            <input
              type="number"
              name="age_range_max"
              value={formData.age_range_max}
              onChange={handleChange}
              min="18"
              max="100"
              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            />
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Gender
            </label>
            <select
              name="gender"
              value={formData.gender}
              onChange={handleChange}
              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            >
              <option value="All">All</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Geography
            </label>
            <input
              type="text"
              name="geography"
              value={formData.geography}
              onChange={handleChange}
              placeholder="e.g., California, USA"
              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            />
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Industry/Business Type
            </label>
            <input
              type="text"
              name="industry_business_type"
              value={formData.industry_business_type}
              onChange={handleChange}
              placeholder="e.g., Technology, Healthcare"
              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Budget Range
            </label>
            <input
              type="text"
              name="budget_range"
              value={formData.budget_range}
              onChange={handleChange}
              placeholder="e.g., $1,000 - $5,000"
              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            />
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Key Benefits
        </label>
        <textarea
          name="key_benefits"
          value={formData.key_benefits}
          onChange={handleChange}
          required
          rows={2}
          className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Brand Voice
          </label>
          <select
            name="brand_voice"
            value={formData.brand_voice}
            onChange={handleChange}
            className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
          >
            <option value="professional">Professional</option>
            <option value="friendly">Friendly</option>
            <option value="casual">Casual</option>
            <option value="authoritative">Authoritative</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Price
          </label>
          <input
            type="text"
            name="price"
            value={formData.price}
            onChange={handleChange}
            className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Gender
          </label>
          <select
            name="gender"
            value={formData.gender}
            onChange={handleChange}
            className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
          >
            <option value="All">All</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
          </select>
        </div>
      </div>

      <div className="flex justify-end space-x-4">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg transition-colors"
        >
          Duplicate Campaign
        </button>
      </div>
    </form>
  );
};

// Landing Page Mockup Component
const LandingMockup = () => {
  const [selectedDesign, setSelectedDesign] = useState(1);

  return (
    <>
    <div className="min-h-screen bg-gray-50">
      {/* Design Switcher */}
      <div className="fixed top-4 right-4 z-50 bg-white rounded-lg shadow-xl p-4">
        <div className="text-sm font-semibold mb-2">Select Design:</div>
        <div className="space-y-2">
          <button 
            onClick={() => setSelectedDesign(1)} 
            className={`w-full px-4 py-2 rounded-lg text-sm ${selectedDesign === 1 ? (colorTheme === 'purple' ? 'bg-purple-600 text-white' : colorTheme === 'blue' ? 'bg-blue-600 text-white' : 'bg-green-600 text-white') : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
          >
            Option 1: Minimal & Bold
          </button>
          <button 
            onClick={() => setSelectedDesign(2)} 
            className={`w-full px-4 py-2 rounded-lg text-sm ${selectedDesign === 2 ? (colorTheme === 'purple' ? 'bg-purple-600 text-white' : colorTheme === 'blue' ? 'bg-blue-600 text-white' : 'bg-green-600 text-white') : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
          >
            Option 2: Gradient & Glass
          </button>
          <button 
            onClick={() => setSelectedDesign(3)} 
            className={`w-full px-4 py-2 rounded-lg text-sm ${selectedDesign === 3 ? (colorTheme === 'purple' ? 'bg-purple-600 text-white' : colorTheme === 'blue' ? 'bg-blue-600 text-white' : 'bg-green-600 text-white') : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
          >
            Option 3: Story-Driven
          </button>
        </div>
      </div>

      {/* Option 1: Minimal & Bold */}
      {selectedDesign === 1 && (
        <div className="animate-fadeIn">
          <header className="bg-white/80 backdrop-blur-md fixed w-full z-40 border-b border-gray-200">
            <div className="max-w-7xl mx-auto px-6 py-4">
              <div className="flex justify-between items-center">
                <div className="text-2xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">AdElevate</div>
                <nav className="hidden md:flex space-x-8 text-sm">
                  <a href="#" className="text-gray-600 hover:text-gray-900">Features</a>
                  <a href="#" className="text-gray-600 hover:text-gray-900">Pricing</a>
                  <a href="#" className="text-gray-600 hover:text-gray-900">Resources</a>
                </nav>
                <div className="flex space-x-4">
                  <button className="text-gray-600 hover:text-gray-900 text-sm font-medium">Sign In</button>
                  <button className="px-6 py-2 bg-black text-white rounded-full text-sm font-medium hover:bg-gray-800">Get Started</button>
                </div>
              </div>
            </div>
          </header>

          <section className="pt-32 pb-20 px-6">
            <div className="max-w-6xl mx-auto text-center">
              <h1 className="text-6xl md:text-8xl font-bold text-gray-900 mb-6">
                Marketing.<br/>
                <span className="bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">Simplified.</span>
              </h1>
              <p className="text-xl md:text-2xl text-gray-600 mb-12 max-w-3xl mx-auto">
                Create stunning marketing content in seconds with AI. No design skills needed.
              </p>
              <button className="px-12 py-4 bg-black text-white rounded-full text-lg font-medium hover:bg-gray-800 shadow-2xl">
                Try AdElevate Free →
              </button>
              
              <div className="mt-16">
                <div className="bg-gray-900 rounded-2xl shadow-2xl p-2 mx-auto max-w-5xl">
                  <div className="bg-gradient-to-br from-green-100 to-emerald-100 rounded-xl p-12 text-center">
                    <div className="text-4xl font-bold text-gray-900 mb-4">Dashboard Preview</div>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="bg-white rounded-lg p-4 shadow">
                        <div className="text-2xl mb-2">📊</div>
                        <div className="text-sm text-gray-600">Analytics</div>
                      </div>
                      <div className="bg-white rounded-lg p-4 shadow">
                        <div className="text-2xl mb-2">🎯</div>
                        <div className="text-sm text-gray-600">Campaigns</div>
                      </div>
                      <div className="bg-white rounded-lg p-4 shadow">
                        <div className="text-2xl mb-2">⚡</div>
                        <div className="text-sm text-gray-600">Quick Gen</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="py-20 px-6">
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="md:col-span-2 bg-gradient-to-br from-green-100 to-emerald-100 rounded-3xl p-12 hover:shadow-xl transition">
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">AI-Powered Content Generation</h3>
                  <p className="text-gray-600 text-lg mb-8">Create perfect marketing copy for any platform in seconds</p>
                  <div className="bg-white rounded-2xl p-6 shadow-lg">
                    <div className="space-y-3">
                      <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                      <div className="h-4 bg-gray-200 rounded w-full"></div>
                      <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-3xl p-8 hover:shadow-xl transition">
                  <div className="text-4xl mb-4">📱</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Multi-Platform</h3>
                  <p className="text-gray-600">One click, all channels</p>
                </div>

                <div className="bg-white rounded-3xl p-8 hover:shadow-xl transition">
                  <div className="text-4xl mb-4">⚡</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Lightning Fast</h3>
                  <p className="text-gray-600">Generate in seconds</p>
                </div>

                <div className="md:col-span-2 bg-gradient-to-br from-purple-100 to-pink-100 rounded-3xl p-12 hover:shadow-xl transition">
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">Video Creation Made Easy</h3>
                  <p className="text-gray-600 text-lg">AI-powered video generation with multiple models</p>
                </div>
              </div>
            </div>
          </section>

          <section className="py-32 px-6">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">Start creating today.</h2>
              <p className="text-xl text-gray-600 mb-12">No credit card required. Free trial included.</p>
              <button className="px-12 py-4 bg-black text-white rounded-full text-lg font-medium hover:bg-gray-800 shadow-2xl">
                Get Started Free
              </button>
            </div>
          </section>
        </div>
      )}

      {/* Option 2: Gradient & Glassmorphism */}
      {selectedDesign === 2 && (
        <div className="animate-fadeIn min-h-screen bg-gradient-to-br from-green-500 via-emerald-500 to-teal-500">
          <header className="bg-white/10 backdrop-blur-md fixed w-full z-40 border-b border-white/20">
            <div className="max-w-7xl mx-auto px-6 py-4">
              <div className="flex justify-between items-center">
                <div className="text-2xl font-bold text-white">AdElevate</div>
                <nav className="hidden md:flex space-x-8 text-sm">
                  <a href="#" className="text-white/80 hover:text-white">Features</a>
                  <a href="#" className="text-white/80 hover:text-white">Pricing</a>
                  <a href="#" className="text-white/80 hover:text-white">Resources</a>
                </nav>
                <button className="px-6 py-2 bg-white/10 backdrop-blur-md text-white rounded-full text-sm font-medium hover:bg-white/20 border border-white/20">Get Started</button>
              </div>
            </div>
          </header>

          <section className="pt-32 pb-20 px-6">
            <div className="max-w-6xl mx-auto text-center">
              <div className="bg-white/10 backdrop-blur-md inline-block px-6 py-2 rounded-full text-white text-sm mb-8 border border-white/20">
                ✨ New: AI Video Generation
              </div>
              <h1 className="text-6xl md:text-8xl font-bold text-white mb-6">
                Transform Your<br/>Marketing Game
              </h1>
              <p className="text-xl md:text-2xl text-white/90 mb-12 max-w-3xl mx-auto">
                AI-powered content creation platform trusted by 10,000+ marketers
              </p>
              <div className="flex justify-center gap-4 flex-wrap">
                <button className="px-8 py-4 bg-white text-green-600 rounded-full text-lg font-bold hover:shadow-2xl">
                  Start Free Trial
                </button>
                <button className="px-8 py-4 bg-white/10 backdrop-blur-md text-white rounded-full text-lg font-medium hover:bg-white/20 border border-white/20">
                  Watch Demo
                </button>
              </div>

              <div className="mt-20 relative">
                <div className="bg-white/10 backdrop-blur-md p-8 rounded-2xl max-w-4xl mx-auto border border-white/20">
                  <div className="bg-white/10 rounded-xl p-6">
                    <div className="grid grid-cols-3 gap-4 text-white">
                      <div>
                        <div className="text-4xl font-bold">5+</div>
                        <div className="text-sm opacity-80">Formats</div>
                      </div>
                      <div>
                        <div className="text-4xl font-bold">10k+</div>
                        <div className="text-sm opacity-80">Users</div>
                      </div>
                      <div>
                        <div className="text-4xl font-bold">99%</div>
                        <div className="text-sm opacity-80">Satisfaction</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="py-20 px-6">
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white/10 backdrop-blur-md p-8 rounded-3xl hover:scale-105 transition border border-white/20">
                  <div className="text-5xl mb-4">🎯</div>
                  <h3 className="text-2xl font-bold text-white mb-3">Smart AI</h3>
                  <p className="text-white/80">Advanced AI models for perfect content</p>
                </div>
                <div className="bg-white/10 backdrop-blur-md p-8 rounded-3xl hover:scale-105 transition border border-white/20">
                  <div className="text-5xl mb-4">🎬</div>
                  <h3 className="text-2xl font-bold text-white mb-3">Video Gen</h3>
                  <p className="text-white/80">Create videos with AI in minutes</p>
                </div>
                <div className="bg-white/10 backdrop-blur-md p-8 rounded-3xl hover:scale-105 transition border border-white/20">
                  <div className="text-5xl mb-4">📊</div>
                  <h3 className="text-2xl font-bold text-white mb-3">Analytics</h3>
                  <p className="text-white/80">Track performance in real-time</p>
                </div>
              </div>
            </div>
          </section>

          <section className="py-32 px-6">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-5xl md:text-6xl font-bold text-white mb-6">Ready to get started?</h2>
              <p className="text-xl text-white/90 mb-12">Join thousands of marketers already creating amazing content</p>
              <button className="px-12 py-4 bg-white text-green-600 rounded-full text-lg font-bold hover:shadow-2xl">
                Start Your Free Trial
              </button>
            </div>
          </section>
        </div>
      )}

      {/* Option 3: Story-Driven */}
      {selectedDesign === 3 && (
        <div className="animate-fadeIn">
          <header className="bg-white shadow-sm fixed w-full z-40">
            <div className="max-w-7xl mx-auto px-6 py-4">
              <div className="flex justify-between items-center">
                <div className="text-2xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">AdElevate</div>
                <button className="px-6 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg text-sm font-medium">Get Started Free</button>
              </div>
            </div>
          </header>

          <section className="pt-32 pb-20 px-6 bg-red-50">
            <div className="max-w-6xl mx-auto">
              <div className="grid md:grid-cols-2 gap-12 items-center">
                <div>
                  <div className="text-red-600 font-semibold mb-4">THE PROBLEM</div>
                  <h1 className="text-5xl font-bold text-gray-900 mb-6">Creating marketing content is too slow & expensive</h1>
                  <ul className="space-y-4 text-lg text-gray-700">
                    <li className="flex items-start">
                      <span className="text-red-500 mr-3">❌</span>
                      <span>Takes days to create a single campaign</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-red-500 mr-3">❌</span>
                      <span>Need expensive designers & copywriters</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-red-500 mr-3">❌</span>
                      <span>Inconsistent brand voice across channels</span>
                    </li>
                  </ul>
                </div>
                <div className="bg-white rounded-2xl p-8 shadow-xl">
                  <div className="bg-red-100 rounded-lg h-64 flex items-center justify-center">
                    <div className="text-center">
                      <div className="text-6xl mb-4">😤</div>
                      <p className="text-gray-700 font-semibold">Frustrated Marketer</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="py-20 px-6 bg-green-50">
            <div className="max-w-6xl mx-auto">
              <div className="grid md:grid-cols-2 gap-12 items-center">
                <div className="order-2 md:order-1">
                  <div className="bg-white rounded-2xl p-8 shadow-xl">
                    <div className="bg-green-100 rounded-lg h-64 flex items-center justify-center">
                      <div className="text-center">
                        <div className="text-6xl mb-4">🚀</div>
                        <p className="text-gray-700 font-semibold">AdElevate Dashboard</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="order-1 md:order-2">
                  <div className="text-green-600 font-semibold mb-4">THE SOLUTION</div>
                  <h2 className="text-5xl font-bold text-gray-900 mb-6">AdElevate creates everything in seconds</h2>
                  <ul className="space-y-4 text-lg text-gray-700">
                    <li className="flex items-start">
                      <span className="text-green-500 mr-3">✅</span>
                      <span>Generate campaigns in under 60 seconds</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-500 mr-3">✅</span>
                      <span>AI-powered, no team needed</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-500 mr-3">✅</span>
                      <span>Consistent brand voice automatically</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          <section className="py-20 px-6 bg-white">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-4xl font-bold text-center text-gray-900 mb-16">How It Works</h2>
              <div className="grid md:grid-cols-3 gap-8">
                <div className="text-center">
                  <div className={`w-16 h-16 ${colorTheme === 'purple' ? 'bg-purple-600' : colorTheme === 'blue' ? 'bg-blue-600' : 'bg-green-600'} text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4`}>1</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Describe Your Product</h3>
                  <p className="text-gray-600">Tell AdElevate about what you're selling</p>
                </div>
                <div className="text-center">
                  <div className={`w-16 h-16 ${colorTheme === 'purple' ? 'bg-purple-600' : colorTheme === 'blue' ? 'bg-blue-600' : 'bg-green-600'} text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4`}>2</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">AI Creates Content</h3>
                  <p className="text-gray-600">Watch as AdElevate generates everything</p>
                </div>
                <div className="text-center">
                  <div className={`w-16 h-16 ${colorTheme === 'purple' ? 'bg-purple-600' : colorTheme === 'blue' ? 'bg-blue-600' : 'bg-green-600'} text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4`}>3</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Publish Everywhere</h3>
                  <p className="text-gray-600">Deploy to all channels instantly</p>
                </div>
              </div>
            </div>
          </section>

          <section className="py-20 px-6 bg-gray-50">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-4xl font-bold text-center text-gray-900 mb-16">The AdElevate Difference</h2>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-white rounded-2xl p-8 border-2 border-gray-200">
                  <div className="text-red-600 font-semibold mb-4">WITHOUT AdElevate</div>
                  <ul className="space-y-3 text-gray-700">
                    <li>⏱️ 5-7 days per campaign</li>
                    <li>💰 $5,000+ per month in costs</li>
                    <li>😓 Constant revisions & back-and-forth</li>
                    <li>📉 Inconsistent results</li>
                  </ul>
                </div>
                <div className={`bg-gradient-to-br ${colorTheme === 'purple' ? 'from-purple-50 to-pink-50 border-purple-600' : colorTheme === 'blue' ? 'from-blue-50 to-sky-50 border-blue-600' : 'from-green-50 to-emerald-50 border-green-600'} rounded-2xl p-8 border-2`}>
                  <div className={`${colorTheme === 'purple' ? 'text-purple-600' : colorTheme === 'blue' ? 'text-blue-600' : 'text-green-600'} font-semibold mb-4`}>WITH AdElevate</div>
                  <ul className="space-y-3 text-gray-900">
                    <li>⚡ Under 60 seconds per campaign</li>
                    <li>💚 $49/month all-inclusive</li>
                    <li>😊 One-click generation</li>
                    <li>📈 Proven conversion rates</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          <section className="py-32 px-6 bg-gradient-to-r from-green-600 to-emerald-600">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-5xl md:text-6xl font-bold text-white mb-6">Ready to Transform Your Marketing?</h2>
              <p className="text-xl text-white/90 mb-12">Start your free trial today. No credit card required.</p>
              <button className="px-12 py-4 bg-white text-green-600 rounded-xl text-lg font-bold hover:shadow-2xl">
                Start Free Trial →
              </button>
              <p className="text-white/80 text-sm mt-6">✨ 50 free credits • No subscription required • Pay as you go</p>
            </div>
          </section>
        </div>
      )}
    </div>
    
    {/* Global Credit Cost Preview Modal - MOVED OUTSIDE dashboard container so it works everywhere */}
    {(() => {
      console.log('🔍 Modal Render Check:', { showCreditPreview, hasCreditPreviewData: !!creditPreviewData, creditPreviewData });
      return null;
    })()}
    {/* Debug log to check why modal doesn't render */}
    {console.log('🔍 RENDER CHECK:', { showCreditPreview, creditPreviewData: creditPreviewData ? 'exists' : 'null', willRender: showCreditPreview && creditPreviewData })}
    
    {/* Debug visual indicator */}
    {showCreditPreview && (
      <div style={{position: 'fixed', top: 0, left: 0, background: 'yellow', padding: '10px', zIndex: 9999}}>
        DEBUG: showCreditPreview is TRUE! creditPreviewData: {creditPreviewData ? 'EXISTS' : 'NULL'}
      </div>
    )}
    
    {showCreditPreview && creditPreviewData && (
      <CreditCostPreview
        costPreview={creditPreviewData}
        onProceed={handleCreditPreviewProceed}
        onCancel={handleCreditPreviewCancel}
        onPurchaseCredits={handlePurchaseCreditsFromPreview}
      />
    )}
    </>
  );
};

const MainApp = () => {
  // Check if we should show mockup
  const urlParams = new URLSearchParams(window.location.search);
  const showMockup = urlParams.get('mockup') === 'true';

  if (showMockup) {
    return <LandingMockup />;
  }

  return (
    <ErrorBoundary>
      <ColorThemeProvider>
        <ThemeProvider>
          <AuthProvider>
            <App />
          </AuthProvider>
        </ThemeProvider>
      </ColorThemeProvider>
    </ErrorBoundary>
  );
};

export default MainApp;
